<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, shallowRef, useSSRContext, watch } from "vue";
import type { Component } from "vue";
import { AlertCircle, ArrowDownRight, ArrowLeftRight, Bookmark, Check, CircleDot, Cog, Columns2, Download, FlipHorizontal2, FlipVertical2, FolderOpen, HelpCircle, Heart, Image as ImageIcon, Lock, Maximize2, Menu, Minimize2, Moon, Move, Palette, Play, Radius, RotateCcw, RotateCw, Save, Scissors, Settings2, Share2, SlidersHorizontal, SlidersVertical, SquareDashed, SquareRoundCorner, SquareUser, Stamp, Sun, Target, Trash2, Type, Undo2, Unlock, ZoomIn } from "@lucide/vue";
import { siFacebook, siInstagram, siPinterest, siSinaweibo, siTiktok, siWechat, siX, siXiaohongshu, siYoutube } from "simple-icons";
import cmpLogo from "./assets/cmp_logo.png";
import cmpLogoDark from "./assets/cmp_logo_d.png";
import dropArrow from "./assets/drop-arrow.svg";
import { editorMessage, type EditorMessageKey } from "./editorUiCopy";
import { helpSectionOrder, localizedHelpContent, localizedSeoCopy } from "./helpContent";
import { getHowItWorksSteps } from "./howItWorksContent";
import guideCrop from "./assets/how-it-works/01-crop.jpg";
import guideFilter from "./assets/how-it-works/02-filter.jpg";
import guideBorder from "./assets/how-it-works/03-border.jpg";
import guideShadow from "./assets/how-it-works/04-shadow.jpg";
import guideText from "./assets/how-it-works/05-text.jpg";
import guideExport from "./assets/how-it-works/06-export.jpg";

const guideSteps = computed(() => getHowItWorksSteps(language.value).map((step, index) => ({
  ...step,
  image: [guideCrop, guideFilter, guideBorder, guideShadow, guideText, guideExport][index],
})));

const howItWorksLabelByLanguage: Record<LanguageCode, string> = {
  en: "How It Works", es: "Cómo funciona", fr: "Comment ça marche", de: "So funktioniert es", ja: "使い方", zh: "使用方法", ko: "사용 방법", hi: "यह कैसे काम करता है", pt: "Como funciona", it: "Come funziona", ru: "Как это работает", ar: "كيف يعمل", nl: "Zo werkt het", pl: "Jak to działa", tr: "Nasıl çalışır", id: "Cara kerjanya", ms: "Cara Ia Berfungsi", vi: "Cách hoạt động", th: "วิธีใช้งาน",
};

const acceptedFormatsLabelByLanguage: Record<LanguageCode, string> = {
  en: "Accepted formats:", es: "Formatos aceptados:", fr: "Formats acceptés :", de: "Unterstützte Formate:", ja: "対応形式：", zh: "支持的格式：", ko: "지원 형식:", hi: "स्वीकृत फ़ॉर्मैट:", pt: "Formatos aceitos:", it: "Formati supportati:", ru: "Поддерживаемые форматы:", ar: "التنسيقات المدعومة:", nl: "Ondersteunde formaten:", pl: "Obsługiwane formaty:", tr: "Desteklenen formatlar:", id: "Format yang didukung:", ms: "Format yang disokong:", vi: "Định dạng được hỗ trợ:", th: "รูปแบบที่รองรับ:",
};

const howItWorksIntroByLanguage: Record<LanguageCode, string> = {
  en: "Learn how to crop, enhance, decorate, and export an image with Cut My Pic. Use simple controls for quick edits, or open a tool’s settings button for detailed control.",
  es: "Aprende a recortar, mejorar, decorar y exportar una imagen con Cut My Pic. Usa controles sencillos para ediciones rápidas o abre los ajustes de una herramienta para un control detallado.",
  fr: "Apprenez à recadrer, améliorer, décorer et exporter une image avec Cut My Pic. Utilisez les commandes simples pour des modifications rapides ou ouvrez les réglages détaillés.",
  de: "Erfahren Sie, wie Sie Bilder mit Cut My Pic zuschneiden, verbessern, gestalten und exportieren. Nutzen Sie einfache Steuerelemente oder öffnen Sie die detaillierten Einstellungen.",
  ja: "Cut My Picで画像を切り抜き、補正、装飾、書き出しする方法を紹介します。簡単な操作ですばやく編集したり、詳細設定を開いて細かく調整できます。",
  zh: "了解如何使用 Cut My Pic 裁剪、增强、装饰和导出图片。使用简单控件快速编辑，或打开工具设置进行详细调整。",
  ko: "Cut My Pic으로 이미지를 자르고 보정하고 꾸미고 내보내는 방법을 알아보세요. 간단한 도구로 빠르게 편집하거나 상세 설정을 열어 세밀하게 조정할 수 있습니다.",
  hi: "Cut My Pic से इमेज को क्रॉप, बेहतर, सजाने और निर्यात करने का तरीका जानें। तेज़ संपादन के लिए सरल नियंत्रण या विस्तृत समायोजन के लिए सेटिंग खोलें।",
  pt: "Aprenda a cortar, melhorar, decorar e exportar uma imagem com o Cut My Pic. Use controles simples para edições rápidas ou abra as configurações detalhadas.",
  it: "Scopri come ritagliare, migliorare, decorare ed esportare un’immagine con Cut My Pic. Usa i controlli semplici per modifiche rapide o apri le impostazioni dettagliate.",
  ru: "Узнайте, как обрезать, улучшать, оформлять и экспортировать изображения в Cut My Pic. Используйте простые инструменты или откройте подробные настройки.",
  ar: "تعلّم كيفية قص الصور وتحسينها وتزيينها وتصديرها باستخدام Cut My Pic. استخدم الأدوات البسيطة للتعديلات السريعة أو افتح الإعدادات التفصيلية.",
  nl: "Leer afbeeldingen bijsnijden, verbeteren, versieren en exporteren met Cut My Pic. Gebruik eenvoudige bediening voor snelle bewerkingen of open de uitgebreide instellingen.",
  pl: "Dowiedz się, jak przycinać, ulepszać, ozdabiać i eksportować obrazy w Cut My Pic. Używaj prostych elementów sterujących lub otwórz ustawienia szczegółowe.",
  tr: "Cut My Pic ile görselleri kırpmayı, geliştirmeyi, süslemeyi ve dışa aktarmayı öğrenin. Hızlı düzenlemeler için basit kontrolleri, ayrıntılı ayarlar için ayar düğmesini kullanın.",
  id: "Pelajari cara memotong, menyempurnakan, menghias, dan mengekspor gambar dengan Cut My Pic. Gunakan kontrol sederhana untuk edit cepat atau buka pengaturan terperinci.",
  ms: "Ketahui cara memangkas, menambah baik, menghias dan mengeksport imej dengan Cut My Pic. Gunakan kawalan ringkas untuk suntingan pantas atau buka tetapan terperinci.",
  vi: "Tìm hiểu cách cắt, cải thiện, trang trí và xuất ảnh bằng Cut My Pic. Dùng điều khiển đơn giản để chỉnh sửa nhanh hoặc mở cài đặt chi tiết.",
  th: "เรียนรู้วิธีครอป ปรับแต่ง ตกแต่ง และส่งออกภาพด้วย Cut My Pic ใช้การควบคุมแบบง่ายเพื่อแก้ไขอย่างรวดเร็ว หรือเปิดการตั้งค่าโดยละเอียด",
};

function localizedGuideStepTitle(index: number) {
  if (language.value === "ms") {
    return `${index + 1}. ${guideSteps.value[index]?.title ?? ""}`;
  }
  const titles = [
    `${t("crop")} & ${t("maskShort")}`,
    t("filters"),
    t("border"),
    t("shadow"),
    `${t("text")} & ${t("stampMode")}`,
    t("export"),
  ];
  return `${index + 1}. ${titles[index] ?? ""}`;
}

function formatGuideText(text: string) {
  const escaped = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return escaped
    .replace(/"([^\"]+)"/g, '<span class="guide-highlight">"$1"</span>')
    .replace(/“([^”]+)”/g, '<span class="guide-highlight">“$1”</span>')
    .replace(/‘([^’]+)’/g, '<span class="guide-highlight">‘$1’</span>')
    .replace(/「([^」]+)」/g, '<span class="guide-highlight">「$1」</span>');
}

const isBrowser = typeof window !== "undefined";
const prerenderPath = () => (globalThis as typeof globalThis & { __CUTMYPIC_PRERENDER_PATH__?: string }).__CUTMYPIC_PRERENDER_PATH__ ?? "/";
const currentPathname = () => (isBrowser ? window.location.pathname : prerenderPath());
const readLocalStorage = (key: string) => {
  if (!isBrowser) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const siteBaseUrl = (import.meta.env.VITE_SITE_URL || (isBrowser ? window.location.origin : "https://cutmypic.com")).replace(/\/+$/, "");

const socialBrandIcons = {
  facebook: siFacebook,
  instagram: siInstagram,
  pinterest: siPinterest,
  tiktok: siTiktok,
  wechat: siWechat,
  weibo: siSinaweibo,
  x: siX,
  xiaohongshu: siXiaohongshu,
  youtube: siYoutube,
} as const;

type AppPathState = {
  language: LanguageCode;
  isManual: boolean;
  isFaq: boolean;
  isGuide?: boolean;
};

type Crop = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type CommittedImageShape = "rect" | "circle" | "heart" | "star";

type ColorReplaceState = {
  targetColor: string;
  replacementColor: string;
  strength: number;
  smoothness: number;
  targetTransparent: boolean;
  replacementTransparent: boolean;
};

type DragMode =
  | "move"
  | "n"
  | "e"
  | "s"
  | "w"
  | "ne"
  | "nw"
  | "se"
  | "sw";

type ToolId = "image" | "corners" | "border" | "shadow" | "crop" | "applyCrop" | "resize" | "filters" | "adjustments" | "text" | "export";
type FilterId =
  | "none"
  | "colorreplace"
  | "bw"
  | "sepia"
  | "oldfashioned"
  | "vintage"
  | "cool"
  | "warm"
  | "cinematic"
  | "matte"
  | "cyanotype"
  | "glow"
  | "greenify"
  | "redify"
  | "bluify"
  | "purplify"
  | "brownify";
type LanguageCode =
  | "en"
  | "es"
  | "fr"
  | "de"
  | "ja"
  | "zh"
  | "ko"
  | "hi"
  | "pt"
  | "it"
  | "ru"
  | "ar"
  | "nl"
  | "pl"
  | "tr"
  | "id"
  | "ms"
  | "vi"
  | "th";
type TranslationKey =
  | "tools"
  | "image"
  | "corners"
  | "border"
  | "keyboardShortcuts"
  | "editHistory"
  | "returnToPoint"
  | "deleteThisStep"
  | "undo"
  | "redo"
  | "exportShortcut"
  | "historyShortcut"
  | "applyCurrentEdit"
  | "switchTools"
  | "closePanels"
  | "enterFullscreen"
  | "exitFullscreen"
  | "shadow"
  | "settings"
  | "manualButton"
  | "crop"
  | "cropShort"
  | "maskShort"
  | "resize"
  | "resizeOnly"
  | "rotate"
  | "rotateFlip"
  | "rotateClockwise"
  | "rotateCounterClockwise"
  | "flipVertical"
  | "flipHorizontal"
  | "flip"
  | "freeRotate"
  | "filters"
  | "adjustments"
  | "text"
  | "export"
  | "chooseImage"
  | "replaceImage"
  | "editor"
  | "theme"
  | "noImageLoaded"
  | "useLightMode"
  | "useDarkMode"
  | "formats"
  | "dropImage"
  | "tapToLoadImage"
  | "localBrowserPrefix"
  | "localBrowserYours"
  | "localBrowserSuffix"
  | "dropToBegin"
  | "freeSelect"
  | "squareSelect"
  | "widescreenSelect"
  | "circleSelect"
  | "constrainProportions"
  | "freeform"
  | "width"
  | "height"
  | "filterPreset"
  | "colorReplace"
  | "targetColor"
  | "replacementColor"
  | "strength"
  | "smoothness"
  | "transparent"
  | "transparency"
  | "includeBorder"
  | "viewBackground"
  | "checkerboard"
  | "none"
  | "oldFashioned"
  | "vintage"
  | "greenify"
  | "redify"
  | "bluify"
  | "purplify"
  | "brownify"
  | "coolTone"
  | "warmTone"
  | "cinematic"
  | "matte"
  | "cyanotype"
  | "warmGlow"
  | "contrast"
  | "brightness"
  | "colorBalance"
  | "saturation"
  | "sharpen"
  | "soften"
  | "size"
  | "topPosition"
  | "horizontal"
  | "textField"
  | "opacity"
  | "color"
  | "cropCommon"
  | "cropSocial"
  | "socialTemplateSizeWarning"
  | "freeCut"
  | "squareCut"
  | "widescreenCut"
  | "circleCut"
  | "heartCut"
  | "starCut"
  | "heartSelect"
  | "instagramPost"
  | "storyReel"
  | "tiktokDouyin"
  | "pinterestPin"
  | "youtubeThumbnail"
  | "facebookFeed"
  | "facebookCover"
  | "xPost"
  | "xHeader"
  | "linkedinCover"
  | "xiaohongshuNote"
  | "wechatCover"
  | "weiboFeed"
  | "freeSelectLabel"
  | "roundedSelect"
  | "maskType"
  | "intensity"
  | "dropShadowSize"
  | "dropShadowIntensity"
  | "dropShadowColor"
  | "positionText"
  | "place"
  | "animals"
  | "decorations"
  | "labels"
  | "apply"
  | "revert"
  | "resizeHelp"
  | "textPlaceholder"
  | "textMode"
  | "stampMode"
  | "live"
  | "ready"
  | "bw"
  | "sepia"
  | "seoTitle"
  | "seoDescription"
  | "tool"
  | "closePanel"
  | "noFileSelected"
  | "source"
  | "dimensions"
  | "uploadSize"
  | "currentSize"
  | "cornerRadius"
  | "max"
  | "lock"
  | "center"
  | "full"
  | "square"
  | "zoom"
  | "zoomLevel"
  | "pngExport"
  | "quality"
  | "transparentAreaColor"
  | "loadingImage"
  | "downloadPng"
  | "downloadNewImage"
  | "shareImage"
  | "copyImage"
  | "shareTo"
  | "openWeibo"
  | "openWeChat"
  | "openDouyin"
  | "openXiaohongshu"
  | "openQQ"
  | "shareCopiedTitle"
  | "shareCopiedMessage"
  | "openingNow"
  | "saveLastChanges"
  | "yes"
  | "no"
  | "donate"
  | "imageTrimmer"
  | "imageMasking"
  | "maskMode"
  | "blur"
  | "overlayColor"
  | "layer1"
  | "layer2"
  | "flipLayers"
  | "mergeLayers"
  | "uploadLayer"
  | "showLayer"
  | "hideLayer"
  | "topLayerSize"
  | "borderThickness"
  | "borderColor"
  | "gradientBorder"
  | "linearGradient"
  | "edgeGradient"
  | "gradientAngle"
  | "gradientStart"
  | "gradientEnd"
  | "swapGradientColors"
  | "shadowDarkness"
  | "shadowSize"
  | "shadowColor"
  | "shadowDirection"
  | "moveCrop"
  | "moveLayer"
  | "resizeTop"
  | "resizeRight"
  | "resizeBottom"
  | "resizeLeft"
  | "resizeTopRight"
  | "resizeTopLeft"
  | "resizeBottomRight"
  | "resizeBottomLeft"
  | "language"
  | "auto"
  | "manualOverview"
  | "tutorialQuickTip"
  | "tutorialLetsGetCutting"
  | "tutorialDragHandles"
  | "tutorialMakeCut"
  | "tutorialClickScissors"
  | "tutorialKeepCreating"
  | "tutorialTryTools"
  | "tutorialDontShowAgain"
  | "showTutorial"
  | "tutorialOk"
  | "unappliedChangesWarning";

const filterTranslationKeys: Record<FilterId, TranslationKey> = {
  none: "none", colorreplace: "colorReplace", bw: "bw", sepia: "sepia", oldfashioned: "oldFashioned", vintage: "vintage",
  cool: "coolTone", warm: "warmTone", cinematic: "cinematic", matte: "matte", cyanotype: "cyanotype",
  glow: "warmGlow", greenify: "greenify", redify: "redify", bluify: "bluify", purplify: "purplify", brownify: "brownify",
};

const translations = {
  en: {
    tools: "Tools",
    language: "Language",
    auto: "Auto",
    manualOverview: "Overview",
    tutorialQuickTip: "Quick tip",
    tutorialLetsGetCutting: "Let's get cutting!",
    tutorialDragHandles: "Drag these handles to select the area you would like to keep!",
    tutorialMakeCut: "Make the cut!",
    tutorialClickScissors: "Now click these scissors to crop your image!",
    tutorialKeepCreating: "Keep creating!",
    tutorialTryTools: "Now try some of these tools to spice up your image even more!",
    tutorialDontShowAgain: "Don't show again",
    showTutorial: "Show Tutorial",
    tutorialOk: "OK",
    unappliedChangesWarning: "You have unapplied changes to your image.",
    image: "Info",
    corners: "Round Corners",
    border: "Add Border",
    keyboardShortcuts: "Keyboard Shortcuts",
    undo: "Undo",
    redo: "Redo",
    exportShortcut: "Export",
    historyShortcut: "History",
    returnToPoint: "Return to this point in editing",
    deleteThisStep: "Delete this step",
    applyCurrentEdit: "Apply current edit",
    switchTools: "Switch tools",
    closePanels: "Close panels",
    editHistory: "Edit history",
    enterFullscreen: "Enter fullscreen",
    exitFullscreen: "Exit fullscreen",
    shadow: "Shadow",
    settings: "Settings",
    manualButton: "User Manual",
    crop: "Crop & Mask",
    cropShort: "Crop",
    maskShort: "Mask",
    cropCommon: "Common",
    cropSocial: "Social",
    socialTemplateSizeWarning: "The uploaded image is smaller than the recommended size for this template. Continuing may reduce image quality.",
    freeCut: "Free Cut",
    squareCut: "Square Cut",
    widescreenCut: "16:9 Cut",
    circleCut: "Circle Cut",
    heartCut: "Heart Cut",
    starCut: "Star Cut",
    freeSelectLabel: "Free Select",
    roundedSelect: "Rounded Select",
    maskType: "Mask Type",
    intensity: "Intensity",
    dropShadowSize: "Drop Shadow Size",
    dropShadowIntensity: "Drop Shadow Intensity",
    dropShadowColor: "Drop Shadow Color",
    positionText: "Position Text",
    place: "Place",
    animals: "Animals",
    labels: "Labels",
    resize: "Resize & Rotate",
    resizeOnly: "Resize",
    rotate: "Rotate 90°",
    rotateFlip: "Rotate & Flip",
    rotateClockwise: "90° Clockwise",
    rotateCounterClockwise: "90° Counter Clockwise",
    flipVertical: "Flip Vertical",
    flipHorizontal: "Flip Horizontal",
    flip: "Flip",
    freeRotate: "Free Rotate",
    export: "Export",
    chooseImage: "Choose Image",
    replaceImage: "Replace image",
    editor: "Editor",
    theme: "Theme",
    noImageLoaded: "No image loaded",
    useLightMode: "Use light mode",
    useDarkMode: "Use dark mode",
    formats: "PNG, JPG, WebP, or GIF",
    dropImage: "Drop Image Here To Begin!",
    tapToLoadImage: "Tap here to load image",
    localBrowserPrefix: "Your data is",
    localBrowserYours: "yours",
    localBrowserSuffix: " and will not be uploaded to any server, ensuring your privacy 100%.",
    dropToBegin: "Drop an image to begin",
    freeSelect: "Free Select",
    squareSelect: "Square Select",
    widescreenSelect: "Widescreen Select",
    circleSelect: "Circle Select",
    heartSelect: "Heart Select",
    constrainProportions: "Constrain Proportions",
    freeform: "Freeform",
    width: "Width",
    height: "Height",
    filterPreset: "Filter Preset",
    colorReplace: "Color Replace",
    targetColor: "Target Color",
    replacementColor: "Replacement Color",
    strength: "Strength",
    smoothness: "Smoothness",
    transparent: "Transparent",
    transparency: "Transparency",
    includeBorder: "Include Border",
    viewBackground: "Viewing Area Background",
    checkerboard: "Checkerboard",
    none: "None",
    oldFashioned: "Old Fashioned",
    vintage: "Vintage",
    greenify: "Greenify",
    redify: "Redify",
    bluify: "Bluify",
    purplify: "Purplify",
    brownify: "Brownify",
    coolTone: "Cool Tone",
    warmTone: "Warm Tone",
    cinematic: "Cinematic",
    matte: "Matte",
    cyanotype: "Cyanotype",
    warmGlow: "Warm Glow",
    contrast: "Contrast",
    brightness: "Brightness",
    colorBalance: "Color balance",
    saturation: "Saturation",
    sharpen: "Sharpen",
    soften: "Soften",
    size: "Size",
    topPosition: "Top Position",
    horizontal: "Horizontalidad",
    textField: "Text",
    opacity: "Transparency",
    textMode: "Text",
    stampMode: "Stamp",
    decorations: "Decorations",
    color: "Color",
    apply: "Apply",
    revert: "Revert",
    resizeHelp: "Freeform keeps each dimension independent. Constrain Proportions keeps them locked together.",
    textPlaceholder: "Your Text Here",
    live: "Live",
    ready: "Ready",
    bw: "B&W",
    sepia: "Sepia",
    seoTitle: "Cut My Pic - Client-side image editor",
    seoDescription: "A private client-side image editor for cropping, Rounded Corners, Borders, Shadows, Filters, Adjustments, Resize, Text, and Export.",
    tool: "Tool",
    closePanel: "Close panel",
    noFileSelected: "No file selected",
    source: "Source",
    dimensions: "Dimensions",
    uploadSize: "Upload Size",
    currentSize: "Current Size",
    cornerRadius: "Corner Radius",
    max: "Max",
    lock: "Lock",
    center: "Center",
    full: "Full",
    square: "Square",
    zoom: "Zoom",
    zoomLevel: "Zoom Level",
    pngExport: "PNG Export",
    quality: "Quality",
    transparentAreaColor: "Transparent Area Color",
    loadingImage: "Loading image",
    downloadPng: "Download",
    downloadNewImage: "Download New Image",
    imageTrimmer: "Image Trimmer",
    imageMasking: "Image Masking",
    maskMode: "Mask Mode",
    blur: "Blur",
    overlayColor: "Color overlay",
    shareImage: "Share image",
    copyImage: "Copy image",
    shareTo: "Share to",
    openWeibo: "Open Weibo",
    openWeChat: "Open WeChat",
    openDouyin: "Open Douyin",
    openXiaohongshu: "Open Xiaohongshu",
    openQQ: "Open QQ",
    openShareChat: "Open ShareChat",
    shareToX: "Share to X",
    shareToFacebook: "Share to Facebook",
    shareToLinkedIn: "Share to LinkedIn",
    shareToReddit: "Share to Reddit",
    shareCopiedTitle: "Image copied to clipboard",
    shareCopiedMessage: "Image copied to clipboard. You can now paste the image into your post.",
    openingNow: "Opening now",
    saveLastChanges: "Save last changes to image before downloading?",
    yes: "Yes",
    no: "No",
    donate: "Donate",
    layer1: "Layer 1",
    layer2: "Layer 2",
    flipLayers: "Flip layers",
    mergeLayers: "Merge layers",
    uploadLayer: "Upload layer",
    showLayer: "Show layer",
    hideLayer: "Hide layer",
    topLayerSize: "Top layer size",
    borderThickness: "Border Thickness",
    borderColor: "Border Color",
    gradientBorder: "Gradient Border",
    linearGradient: "Linear",
    edgeGradient: "Edge",
    gradientAngle: "Gradient Angle",
    gradientStart: "Gradient Start",
    gradientEnd: "Gradient End",
    swapGradientColors: "Swap gradient colors",
    shadowDarkness: "Shadow Intensity",
    shadowSize: "Shadow Size",
    shadowColor: "Shadow Color",
    shadowDirection: "Shadow Direction",
    moveCrop: "Move Crop",
    moveLayer: "Move Layer",
    resizeTop: "Resize Top",
    resizeRight: "Resize Right",
    resizeBottom: "Resize Bottom",
    resizeLeft: "Resize Left",
    resizeTopRight: "Resize Top Right",
    resizeTopLeft: "Resize Top Left",
    resizeBottomRight: "Resize Bottom Right",
    resizeBottomLeft: "Resize Bottom Left",
    filters: "Filters",
    adjustments: "Adjustments",
    text: "Text & Stamps",
  },
  es: {
    tools: "Herramientas",
    language: "Idioma",
    auto: "Automático",
    manualOverview: "Resumen",
    tutorialQuickTip: "Consejo rápido",
    tutorialLetsGetCutting: "¡Empecemos a recortar!",
    tutorialDragHandles: "Arrastra estos controles para seleccionar el área que quieres conservar.",
    tutorialMakeCut: "¡Haz el recorte!",
    tutorialClickScissors: "Ahora haz clic en estas tijeras para recortar tu imagen.",
    tutorialKeepCreating: "¡Sigue creando!",
    tutorialTryTools: "Prueba estas herramientas para darle aún más vida a tu imagen.",
    tutorialDontShowAgain: "No volver a mostrar",
    showTutorial: "Mostrar tutorial",
    tutorialOk: "Aceptar",
    unappliedChangesWarning: "Tienes cambios sin aplicar en tu imagen.",
    image: "Info",
    corners: "Redondear esquinas",
    border: "Agregar borde",
    keyboardShortcuts: "Atajos de teclado",
    undo: "Deshacer",
    redo: "Rehacer",
    exportShortcut: "Exportar",
    historyShortcut: "Historial",
    returnToPoint: "Volver a este punto de la edición",
    deleteThisStep: "Eliminar este paso",
    applyCurrentEdit: "Aplicar edición actual",
    switchTools: "Cambiar herramientas",
    closePanels: "Cerrar paneles",
    editHistory: "Editar historial",
    enterFullscreen: "Entrar en pantalla completa",
    exitFullscreen: "Salir de pantalla completa",
    settings: "Ajustes",
    manualButton: "Manual de usuario",
    crop: "Recortar y máscara",
    cropShort: "Recortar",
    maskShort: "Máscara",
    cropCommon: "Común",
    cropSocial: "Social",
    socialTemplateSizeWarning: "La imagen subida es más pequeña que el tamaño recomendado para esta plantilla. Continuar puede reducir la calidad de la imagen.",
    freeCut: "Corte libre",
    squareCut: "Corte cuadrado",
    widescreenCut: "Corte 16:9",
    circleCut: "Corte circular",
    heartCut: "Corte de corazón",
    starCut: "Corte de estrella",
    freeSelectLabel: "Selección libre",
    roundedSelect: "Selección redondeada",
    maskType: "Tipo de máscara",
    intensity: "Intensidad",
    dropShadowSize: "Tamaño de sombra",
    dropShadowIntensity: "Intensidad de sombra",
    dropShadowColor: "Color de sombra",
    positionText: "Posicionar texto",
    place: "Colocar",
    animals: "Animales",
    labels: "Etiquetas",
    resize: "Redimensionar y rotar",
    resizeOnly: "Redimensionar",
    rotate: "Rotar",
    rotateFlip: "Rotar y voltear",
    rotateClockwise: "90° en sentido horario",
    rotateCounterClockwise: "90° en sentido antihorario",
    flipVertical: "Voltear verticalmente",
    flipHorizontal: "Voltear horizontalmente",
    flip: "Voltear",
    freeRotate: "Rotación libre",
    export: "Exportar",
    chooseImage: "Elegir imagen",
    replaceImage: "Reemplazar imagen",
    editor: "Editor",
    theme: "Tema",
    noImageLoaded: "No hay imagen cargada",
    useLightMode: "Usar modo claro",
    useDarkMode: "Usar modo oscuro",
    formats: "PNG, JPG, WebP o GIF",
    dropImage: "Suelta una imagen para empezar",
    tapToLoadImage: "Toca aquí para cargar una imagen",
    localBrowserPrefix: "Tus datos son",
    localBrowserYours: "tuyos",
    localBrowserSuffix: "y no se subirán a ningún servidor, garantizando tu privacidad al 100%.",
    dropToBegin: "Suelta una imagen para comenzar",
    apply: "Aplicar",
    revert: "Restaurar",
    textPlaceholder: "Tu texto aquí",
    live: "En vivo",
    ready: "Listo",
    bw: "Blanco y negro",
    sepia: "Sepia",
    seoTitle: "Cut My Pic - editor de imágenes local",
    seoDescription: "Un editor de imágenes privado en el navegador para recortar, esquinas redondeadas, bordes, sombras, filtros, ajustes, cambio de tamaño, texto y exportación.",
    tool: "Herramienta",
    closePanel: "Cerrar panel",
    noFileSelected: "No hay archivo seleccionado",
    source: "Origen",
    dimensions: "Dimensiones",
    uploadSize: "Tamaño original",
    currentSize: "Tamaño actual",
    cornerRadius: "Radio de esquina",
    max: "Máx.",
    lock: "Bloquear",
    center: "Centrar",
    full: "Completo",
    square: "Cuadrado",
    zoom: "Zoom",
    zoomLevel: "Nivel de zoom",
    pngExport: "Exportar PNG",
    quality: "Calidad",
    transparentAreaColor: "Color del área transparente",
    loadingImage: "Cargando imagen",
    downloadPng: "Descargar",
    downloadNewImage: "Descargar imagen nueva",
    imageTrimmer: "Recorte de imagen",
    imageMasking: "Máscara de imagen",
    maskMode: "Modo de máscara",
    blur: "Desenfoque",
    overlayColor: "Superposición de color",
    shareImage: "Compartir imagen",
    copyImage: "Copiar imagen",
    shareTo: "Compartir en",
    openWeibo: "Abrir Weibo",
    openWeChat: "Abrir WeChat",
    openDouyin: "Abrir Douyin",
    openXiaohongshu: "Abrir Xiaohongshu",
    openQQ: "Abrir QQ",
    shareToX: "Compartir en X",
    shareToFacebook: "Compartir en Facebook",
    shareToLinkedIn: "Compartir en LinkedIn",
    shareToReddit: "Compartir en Reddit",
    shareCopiedTitle: "Imagen copiada al portapapeles",
    shareCopiedMessage: "Imagen copiada al portapapeles. Ahora puedes pegar la imagen en tu publicación.",
    openingNow: "Abriendo ahora",
    saveLastChanges: "¿Guardar los últimos cambios en la imagen antes de descargar?",
    yes: "Sí",
    no: "No",
    donate: "Donar",
    layer1: "Capa 1",
    layer2: "Capa 2",
    flipLayers: "Invertir capas",
    mergeLayers: "Fusionar capas",
    uploadLayer: "Subir capa",
    showLayer: "Mostrar capa",
    hideLayer: "Ocultar capa",
    topLayerSize: "Tamaño capa superior",
    borderThickness: "Grosor del borde",
    borderColor: "Color del borde",
    gradientBorder: "Borde degradado",
    linearGradient: "Lineal",
    edgeGradient: "Contorno",
    gradientAngle: "Ángulo del degradado",
    gradientStart: "Inicio del degradado",
    gradientEnd: "Fin del degradado",
    swapGradientColors: "Intercambiar colores del degradado",
    moveCrop: "Mover recorte",
    moveLayer: "Mover capa",
    resizeTop: "Redimensionar arriba",
    resizeRight: "Redimensionar derecha",
    resizeBottom: "Redimensionar abajo",
    resizeLeft: "Redimensionar izquierda",
    resizeTopRight: "Redimensionar arriba derecha",
    resizeTopLeft: "Redimensionar arriba izquierda",
    resizeBottomRight: "Redimensionar abajo derecha",
    resizeBottomLeft: "Redimensionar abajo izquierda",
    shadowDarkness: "Oscuridad",
    shadowSize: "Tamaño",
    shadowColor: "Color de la sombra",
    shadowDirection: "Dirección",
    freeSelect: "Selección libre",
    squareSelect: "Selección cuadrada",
    widescreenSelect: "Selección 16:9",
    circleSelect: "Selección circular",
    heartSelect: "Selección de corazón",
    constrainProportions: "Mantener proporciones",
    freeform: "Libre",
    width: "Ancho",
    height: "Alto",
    filterPreset: "Filtro",
    none: "Ninguno",
    oldFashioned: "Antiguo",
    vintage: "Vintage",
    greenify: "Verde",
    redify: "Rojo",
    bluify: "Azul",
    purplify: "Púrpura",
    brownify: "Marrón",
    coolTone: "Tono frío",
    warmTone: "Tono cálido",
    cinematic: "Cinematográfico",
    matte: "Mate",
    cyanotype: "Cianotipo",
    warmGlow: "Brillo cálido",
    contrast: "Contraste",
    brightness: "Brillo",
    colorBalance: "Balance de color",
    saturation: "Saturación",
    sharpen: "Enfocar",
    soften: "Suavizar",
    size: "Tamaño",
    topPosition: "Posición superior",
    horizontal: "Horizontal",
    textField: "Texto",
    opacity: "Opacidade",
    textMode: "Texto",
    stampMode: "Sello",
    decorations: "Decoraciones",
    color: "Color",
    resizeHelp: "El modo libre mantiene cada dimensión independiente. Mantener proporciones las bloquea entre sí.",
    filters: "Filtros",
    adjustments: "Ajustes",
    text: "Texto y sellos",
  },
  fr: {
    tools: "Outils",
    language: "Langue",
    auto: "Automatique",
    manualOverview: "Aperçu",
    tutorialQuickTip: "Astuce rapide",
    tutorialLetsGetCutting: "Commençons le découpage !",
    tutorialDragHandles: "Faites glisser ces poignées pour sélectionner la zone à conserver.",
    tutorialMakeCut: "Découpez l'image !",
    tutorialClickScissors: "Cliquez maintenant sur ces ciseaux pour recadrer votre image.",
    tutorialKeepCreating: "Continuez à créer !",
    tutorialTryTools: "Essayez maintenant ces outils pour embellir encore plus votre image.",
    tutorialDontShowAgain: "Ne plus afficher",
    showTutorial: "Afficher le tutoriel",
    tutorialOk: "OK",
    unappliedChangesWarning: "Votre image contient des modifications non appliquées.",
    image: "Info",
    corners: "Arrondir les coins",
    keyboardShortcuts: "Raccourcis clavier",
    undo: "Annuler",
    redo: "Rétablir",
    exportShortcut: "Exporter",
    historyShortcut: "Historique",
    returnToPoint: "Revenir à ce point de la modification",
    deleteThisStep: "Supprimer cette étape",
    applyCurrentEdit: "Appliquer la modification en cours",
    switchTools: "Changer d’outil",
    closePanels: "Fermer les panneaux",
    editHistory: "Historique des modifications",
    enterFullscreen: "Passer en plein écran",
    exitFullscreen: "Quitter le plein écran",
    border: "Ajouter une bordure",
    shadow: "Ombre",
    settings: "Réglages",
    manualButton: "Manuel d'utilisation",
    crop: "Rognage et masque",
    cropShort: "Rogner",
    maskShort: "Masque",
    cropCommon: "Commun",
    cropSocial: "Social",
    socialTemplateSizeWarning: "L’image importée est plus petite que la taille recommandée pour ce modèle. Continuer peut réduire la qualité de l’image.",
    freeCut: "Découpe libre",
    squareCut: "Découpe carrée",
    widescreenCut: "Découpe 16:9",
    circleCut: "Découpe circulaire",
    heartCut: "Découpe cœur",
    starCut: "Découpe étoile",
    freeSelectLabel: "Sélection libre",
    roundedSelect: "Sélection arrondie",
    maskType: "Type de masque",
    intensity: "Intensité",
    dropShadowSize: "Taille de l’ombre",
    dropShadowIntensity: "Intensité de l’ombre",
    dropShadowColor: "Couleur de l’ombre",
    positionText: "Positionner le texte",
    place: "Placer",
    animals: "Animaux",
    labels: "Étiquettes",
    resize: "Redimensionner et pivoter",
    resizeOnly: "Redimensionner",
    rotate: "Faire pivoter",
    rotateFlip: "Pivoter et retourner",
    rotateClockwise: "90° vers la droite",
    rotateCounterClockwise: "90° vers la gauche",
    flipVertical: "Retourner verticalement",
    flipHorizontal: "Retourner horizontalement",
    flip: "Retourner",
    freeRotate: "Rotation libre",
    export: "Exporter",
    chooseImage: "Choisir une image",
    replaceImage: "Remplacer l’image",
    editor: "Editeur",
    theme: "Thème",
    noImageLoaded: "Aucune image chargée",
    useLightMode: "Utiliser le mode clair",
    useDarkMode: "Utiliser le mode sombre",
    formats: "PNG, JPG, WebP ou GIF",
    dropImage: "Déposez une image pour commencer",
    tapToLoadImage: "Touchez ici pour charger une image",
    localBrowserPrefix: "Vos données sont",
    localBrowserYours: "les vôtres",
    localBrowserSuffix: "et ne seront jamais envoyées à un serveur, pour garantir votre confidentialité à 100 %.",
    dropToBegin: "Déposez une image pour commencer",
    apply: "Appliquer",
    revert: "Annuler",
    textPlaceholder: "Votre texte ici",
    live: "Actif",
    ready: "Prêt",
    bw: "N&B",
    sepia: "Sépia",
    freeSelect: "Sélection libre",
    squareSelect: "Sélection carrée",
    widescreenSelect: "Sélection 16:9",
    circleSelect: "Sélection cercle",
    heartSelect: "Sélection cœur",
    constrainProportions: "Conserver les proportions",
    freeform: "Libre",
    width: "Largeur",
    height: "Hauteur",
    filterPreset: "Filtre",
    none: "Aucun",
    oldFashioned: "Ancien",
    vintage: "Vintage",
    greenify: "Vert",
    redify: "Rouge",
    bluify: "Bleu",
    purplify: "Violet",
    brownify: "Brun",
    coolTone: "Ton froid",
    warmTone: "Ton chaud",
    cinematic: "Cinématographique",
    matte: "Mat",
    cyanotype: "Cyanotype",
    warmGlow: "Lueur chaude",
    contrast: "Contraste",
    brightness: "Luminosité",
    colorBalance: "Balance des couleurs",
    saturation: "Saturation",
    sharpen: "Netteté",
    soften: "Adoucir",
    size: "Taille",
    topPosition: "Position haute",
    horizontal: "Horizontal",
    textField: "Texte",
    opacity: "Opacité",
    textMode: "Texte",
    stampMode: "Tampon",
    decorations: "Décorations",
    color: "Couleur",
    resizeHelp: "Le mode libre conserve chaque dimension indépendamment. Conserver les proportions les verrouille ensemble.",
    seoTitle: "Cut My Pic - éditeur d'image local",
    seoDescription: "Un éditeur d'image privé dans le navigateur pour rogner, arrondir les coins, ajouter des bordures, des ombres, des filtres, des réglages, redimensionner, du texte et exporter.",
    tool: "Outil",
    closePanel: "Fermer le panneau",
    noFileSelected: "Aucun fichier selectionne",
    source: "Source",
    dimensions: "Dimensions",
    uploadSize: "Taille importee",
    currentSize: "Taille actuelle",
    cornerRadius: "Rayon des coins",
    max: "Max",
    lock: "Verrouiller",
    center: "Centrer",
    full: "Entier",
    square: "Carre",
    zoom: "Zoom",
    zoomLevel: "Niveau de zoom",
    pngExport: "Export PNG",
    quality: "Qualité",
    transparentAreaColor: "Couleur de la zone transparente",
    loadingImage: "Chargement de l’image",
    downloadPng: "Télécharger",
    downloadNewImage: "Télécharger la nouvelle image",
    imageTrimmer: "Découpe d'image",
    imageMasking: "Masquage d'image",
    maskMode: "Mode de masque",
    blur: "Flou",
    overlayColor: "Superposition de couleur",
    shareImage: "Partager l’image",
    copyImage: "Copier l’image",
    shareTo: "Partager vers",
    openWeibo: "Ouvrir Weibo",
    openWeChat: "Ouvrir WeChat",
    openDouyin: "Ouvrir Douyin",
    openXiaohongshu: "Ouvrir Xiaohongshu",
    openQQ: "Ouvrir QQ",
    shareToX: "Partager sur X",
    shareToFacebook: "Partager sur Facebook",
    shareToLinkedIn: "Partager sur LinkedIn",
    shareToReddit: "Partager sur Reddit",
    shareCopiedTitle: "Image copiée dans le presse-papiers",
    shareCopiedMessage: "Image copiée dans le presse-papiers. Vous pouvez maintenant coller l’image dans votre publication.",
    openingNow: "Ouverture en cours",
    saveLastChanges: "Enregistrer les dernières modifications sur l’image avant le téléchargement ?",
    yes: "Oui",
    no: "Non",
    donate: "Faire un don",
    layer1: "Calque 1",
    layer2: "Calque 2",
    flipLayers: "Inverser les calques",
    mergeLayers: "Fusionner les calques",
    uploadLayer: "Importer calque",
    showLayer: "Afficher calque",
    hideLayer: "Masquer calque",
    topLayerSize: "Taille du calque supérieur",
    borderThickness: "Épaisseur de la bordure",
    borderColor: "Couleur de la bordure",
    gradientBorder: "Bordure dégradée",
    linearGradient: "Linéaire",
    edgeGradient: "Contour",
    gradientAngle: "Angle du dégradé",
    gradientStart: "Début du dégradé",
    gradientEnd: "Fin du dégradé",
    swapGradientColors: "Inverser les couleurs du dégradé",
    moveCrop: "Déplacer le recadrage",
    moveLayer: "Deplacer le calque",
    resizeTop: "Redimensionner en haut",
    resizeRight: "Redimensionner à droite",
    resizeBottom: "Redimensionner en bas",
    resizeLeft: "Redimensionner à gauche",
    resizeTopRight: "Redimensionner en haut à droite",
    resizeTopLeft: "Redimensionner en haut à gauche",
    resizeBottomRight: "Redimensionner en bas à droite",
    resizeBottomLeft: "Redimensionner en bas à gauche",
    shadowDarkness: "Obscurité",
    shadowSize: "Taille",
    shadowColor: "Couleur de l'ombre",
    shadowDirection: "Direction",
    filters: "Filtres",
    adjustments: "Réglages",
    text: "Texte et tampons",
  },
  de: {
    tools: "Werkzeuge",
    language: "Sprache",
    auto: "Automatisch",
    manualOverview: "Übersicht",
    tutorialQuickTip: "Kurzer Tipp",
    tutorialLetsGetCutting: "Los geht's mit dem Zuschneiden!",
    tutorialDragHandles: "Ziehe diese Griffe, um den Bereich auszuwählen, den du behalten möchtest.",
    tutorialMakeCut: "Jetzt zuschneiden!",
    tutorialClickScissors: "Klicke jetzt auf diese Schere, um dein Bild zuzuschneiden.",
    tutorialKeepCreating: "Weiter gestalten!",
    tutorialTryTools: "Probiere jetzt diese Werkzeuge aus, um dein Bild noch interessanter zu gestalten.",
    tutorialDontShowAgain: "Nicht mehr anzeigen",
    showTutorial: "Tutorial anzeigen",
    tutorialOk: "OK",
    unappliedChangesWarning: "Dein Bild enthält noch nicht angewendete Änderungen.",
    image: "Info",
    corners: "Ecken abrunden",
    keyboardShortcuts: "Tastenkürzel",
    undo: "Rückgängig",
    redo: "Wiederholen",
    exportShortcut: "Exportieren",
    historyShortcut: "Verlauf",
    returnToPoint: "Zu diesem Bearbeitungspunkt zurückkehren",
    deleteThisStep: "Diesen Schritt löschen",
    applyCurrentEdit: "Aktuelle Änderung anwenden",
    switchTools: "Werkzeuge wechseln",
    closePanels: "Panels schließen",
    editHistory: "Verlauf bearbeiten",
    enterFullscreen: "Vollbild öffnen",
    exitFullscreen: "Vollbild beenden",
    border: "Rahmen hinzufügen",
    settings: "Einstellungen",
    manualButton: "Benutzerhandbuch",
    crop: "Zuschneiden & Maske",
    cropShort: "Zuschneiden",
    maskShort: "Maske",
    cropCommon: "Allgemein",
    cropSocial: "Sozial",
    socialTemplateSizeWarning: "Das hochgeladene Bild ist kleiner als die für diese Vorlage empfohlene Größe. Wenn du fortfährst, kann die Bildqualität abnehmen.",
    freeCut: "Freischnitt",
    squareCut: "Quadratischer Schnitt",
    widescreenCut: "16:9 Schnitt",
    circleCut: "Kreis-Schnitt",
    heartCut: "Herz-Schnitt",
    starCut: "Stern-Schnitt",
    freeSelectLabel: "Freie Auswahl",
    roundedSelect: "Abgerundete Auswahl",
    maskType: "Maskentyp",
    intensity: "Intensität",
    dropShadowSize: "Schattengröße",
    dropShadowIntensity: "Schattenintensität",
    dropShadowColor: "Schattenfarbe",
    positionText: "Text positionieren",
    place: "Platzieren",
    animals: "Tiere",
    labels: "Beschriftungen",
    resize: "Größe ändern und drehen",
    resizeOnly: "Größe ändern",
    rotate: "Drehen",
    rotateFlip: "Drehen & Spiegeln",
    rotateClockwise: "90° im Uhrzeigersinn",
    rotateCounterClockwise: "90° gegen den Uhrzeigersinn",
    flipVertical: "Vertikal spiegeln",
    flipHorizontal: "Horizontal spiegeln",
    flip: "Spiegeln",
    freeRotate: "Freie Drehung",
    export: "Export",
    chooseImage: "Bild wählen",
    replaceImage: "Bild ersetzen",
    editor: "Editor",
    theme: "Design",
    noImageLoaded: "Kein Bild geladen",
    useLightMode: "Hellen Modus verwenden",
    useDarkMode: "Dunklen Modus verwenden",
    formats: "PNG, JPG, WebP oder GIF",
    dropImage: "Bild ablegen, um zu starten",
    tapToLoadImage: "Hier tippen, um ein Bild zu laden",
    localBrowserPrefix: "Ihre Daten sind",
    localBrowserYours: "Ihre eigenen",
    localBrowserSuffix: "und werden nicht an einen Server hochgeladen, damit Ihre Privatsphäre zu 100 % gewahrt bleibt.",
    dropToBegin: "Bild ablegen, um zu beginnen",
    apply: "Anwenden",
    revert: "Zurücksetzen",
    textPlaceholder: "Ihr Text hier",
    live: "Live",
    ready: "Bereit",
    bw: "S/W",
    sepia: "Sepia",
    freeSelect: "Freie Auswahl",
    squareSelect: "Quadratische Auswahl",
    widescreenSelect: "16:9 Auswahl",
    circleSelect: "Kreis-Auswahl",
    heartSelect: "Herz-Auswahl",
    constrainProportions: "Proportionen beibehalten",
    freeform: "Freiform",
    width: "Breite",
    height: "Höhe",
    filterPreset: "Filter",
    none: "Keine",
    oldFashioned: "Altmodisch",
    vintage: "Vintage",
    greenify: "Grün",
    redify: "Rot",
    bluify: "Blau",
    purplify: "Lila",
    brownify: "Braun",
    coolTone: "Kalter Ton",
    warmTone: "Warmer Ton",
    cinematic: "Kinematografisch",
    matte: "Matt",
    cyanotype: "Zyanotypie",
    warmGlow: "Warmer Glanz",
    contrast: "Kontrast",
    brightness: "Helligkeit",
    colorBalance: "Farbbalance",
    saturation: "Sättigung",
    sharpen: "Schärfen",
    soften: "Weichzeichnen",
    size: "Größe",
    topPosition: "Oben position",
    horizontal: "Horizontal",
    textField: "Text",
    opacity: "Opacity",
    textMode: "Text",
    stampMode: "Stempel",
    decorations: "Dekorationen",
    color: "Farbe",
    resizeHelp: "Freiform hält jede Dimension unabhängig. Proportionen beibehalten verknüpft sie.",
    seoTitle: "Cut My Pic - lokaler Bildeditor",
    seoDescription: "Ein privater Bildeditor direkt im Browser zum Zuschneiden, runden Ecken, Rahmen, Schatten, Filtern, Anpassen, Skalieren, Text und Exportieren.",
    tool: "Werkzeug",
    closePanel: "Panel schliessen",
    noFileSelected: "Keine Datei ausgewaehlt",
    source: "Quelle",
    dimensions: "Abmessungen",
    uploadSize: "Upload-Groesse",
    currentSize: "Aktuelle Groesse",
    cornerRadius: "Eckenradius",
    max: "Max",
    lock: "Sperren",
    center: "Zentrieren",
    full: "Voll",
    square: "Quadrat",
    zoom: "Zoom",
    zoomLevel: "Zoomstufe",
    pngExport: "PNG-Export",
    quality: "Qualität",
    transparentAreaColor: "Farbe transparenter Bereiche",
    loadingImage: "Bild wird geladen",
    downloadPng: "Herunterladen",
    downloadNewImage: "Neues Bild herunterladen",
    imageTrimmer: "Bildzuschnitt",
    imageMasking: "Bildmaske",
    maskMode: "Maskenmodus",
    blur: "Weichzeichnen",
    overlayColor: "Farbüberlagerung",
    shareImage: "Bild teilen",
    copyImage: "Bild kopieren",
    shareTo: "Teilen auf",
    openWeibo: "Weibo öffnen",
    openWeChat: "WeChat öffnen",
    openDouyin: "Douyin öffnen",
    openXiaohongshu: "Xiaohongshu öffnen",
    openQQ: "QQ öffnen",
    shareToX: "Auf X teilen",
    shareToFacebook: "Auf Facebook teilen",
    shareToLinkedIn: "Auf LinkedIn teilen",
    shareToReddit: "Auf Reddit teilen",
    shareCopiedTitle: "Bild in die Zwischenablage kopiert",
    shareCopiedMessage: "Bild in die Zwischenablage kopiert. Du kannst das Bild jetzt in deinen Beitrag einfügen.",
    openingNow: "Wird jetzt geöffnet",
    saveLastChanges: "Letzte Änderungen am Bild vor dem Herunterladen speichern?",
    yes: "Ja",
    no: "Nein",
    donate: "Spenden",
    layer1: "Ebene 1",
    layer2: "Ebene 2",
    flipLayers: "Ebenen tauschen",
    mergeLayers: "Ebenen zusammenfuehren",
    uploadLayer: "Ebene hochladen",
    showLayer: "Ebene anzeigen",
    hideLayer: "Ebene ausblenden",
    topLayerSize: "Groesse der oberen Ebene",
    borderThickness: "Rahmenstärke",
    borderColor: "Rahmenfarbe",
    gradientBorder: "Verlaufrahmen",
    linearGradient: "Linear",
    edgeGradient: "Kante",
    gradientAngle: "Verlaufwinkel",
    gradientStart: "Verlauf Start",
    gradientEnd: "Verlauf Ende",
    swapGradientColors: "Verlaufsfarben tauschen",
    moveCrop: "Zuschnitt verschieben",
    moveLayer: "Ebene verschieben",
    resizeTop: "Oben anpassen",
    resizeRight: "Rechts anpassen",
    resizeBottom: "Unten anpassen",
    resizeLeft: "Links anpassen",
    resizeTopRight: "Oben rechts anpassen",
    resizeTopLeft: "Oben links anpassen",
    resizeBottomRight: "Unten rechts anpassen",
    resizeBottomLeft: "Unten links anpassen",
    shadowDarkness: "Dunkelheit",
    shadowSize: "Größe",
    shadowColor: "Schattenfarbe",
    shadowDirection: "Richtung",
    filters: "Filter",
    adjustments: "Anpassungen",
    text: "Text & Stempel",
  },
  ja: {
    tools: "ツール",
    language: "言語",
    auto: "自動",
    manualOverview: "ユーザーマニュアル",
    tutorialQuickTip: "簡単なヒント",
    tutorialLetsGetCutting: "切り抜きを始めましょう！",
    tutorialDragHandles: "ハンドルをドラッグして、残したい範囲を選択してください。",
    tutorialMakeCut: "切り抜きましょう！",
    tutorialClickScissors: "次に、このハサミをクリックして画像を切り抜きましょう！",
    tutorialKeepCreating: "さらに編集しましょう！",
    tutorialTryTools: "次は、これらのツールで画像をもっと魅力的にしてみましょう！",
    tutorialDontShowAgain: "今後表示しない",
    showTutorial: "チュートリアルを表示",
    tutorialOk: "OK",
    unappliedChangesWarning: "画像に未適用の変更があります。",
    image: "情報",
    corners: "角丸",
    keyboardShortcuts: "キーボードショートカット",
    undo: "元に戻す",
    redo: "やり直す",
    exportShortcut: "書き出し",
    historyShortcut: "履歴",
    returnToPoint: "この編集時点に戻る",
    deleteThisStep: "このステップを削除",
    applyCurrentEdit: "現在の編集を適用",
    switchTools: "ツールを切り替え",
    closePanels: "パネルを閉じる",
    editHistory: "編集履歴",
    enterFullscreen: "フルスクリーンに入る",
    exitFullscreen: "フルスクリーンを終了",
    border: "ボーダー",
    settings: "設定",
    manualButton: "ユーザーマニュアル",
    crop: "クロップとマスク",
    cropShort: "クロップ",
    maskShort: "マスク",
    cropCommon: "共通",
    cropSocial: "SNS",
    socialTemplateSizeWarning: "アップロードした画像は、このテンプレートの推奨サイズより小さいです。続行すると画質が低下する可能性があります。",
    freeCut: "フリーカット",
    squareCut: "正方形カット",
    widescreenCut: "16:9カット",
    circleCut: "円形カット",
    heartSelect: "ハート選択",
    heartCut: "ハート型カット",
    starCut: "星型カット",
    freeSelectLabel: "自由選択",
    roundedSelect: "角丸選択",
    maskType: "マスクの種類",
    intensity: "強さ",
    dropShadowSize: "影のサイズ",
    dropShadowIntensity: "影の強さ",
    dropShadowColor: "影の色",
    positionText: "テキストを配置",
    place: "配置",
    animals: "動物",
    labels: "ラベル",
    resize: "サイズ変更と回転",
    resizeOnly: "サイズ変更",
    rotate: "回転",
    rotateFlip: "回転と反転",
    rotateClockwise: "90° 右回転",
    rotateCounterClockwise: "90° 左回転",
    flipVertical: "上下反転",
    flipHorizontal: "左右反転",
    flip: "反転",
    freeRotate: "自由回転",
    export: "エクスポート",
    chooseImage: "画像を選択",
    replaceImage: "画像を置換",
    editor: "エディター",
    theme: "テーマ",
    noImageLoaded: "画像がありません",
    useLightMode: "ライトモードにする",
    useDarkMode: "ダークモードにする",
    formats: "PNG、JPG、WebP、GIF",
    dropImage: "画像をドロップして編集開始",
    tapToLoadImage: "ここをタップして画像を読み込む",
    localBrowserPrefix: "あなたのデータは",
    localBrowserYours: "あなたのもの",
    localBrowserSuffix: "であり、サーバーへアップロードされることはありません。<br />プライバシーを100%守ります。",
    dropToBegin: "画像をドロップして開始",
    constrainProportions: "比率を固定",
    resizeHelp: "自由形式では各寸法を個別に変更できます。比率を固定すると両方が連動します。",
    seoTitle: "Cut My Pic - ローカル画像エディタ",
    seoDescription: "切り抜き、角丸、枠線、影、フィルター、調整、サイズ変更、テキスト、書き出しに対応した、ブラウザ内で完結するプライベート画像エディタ。",
    tool: "ツール",
    closePanel: "パネルを閉じる",
    noFileSelected: "ファイル未選択",
    source: "ソース",
    dimensions: "寸法",
    uploadSize: "アップロードサイズ",
    currentSize: "現在のサイズ",
    cornerRadius: "角丸半径",
    max: "最大",
    lock: "固定",
    center: "中央",
    full: "全体",
    square: "正方形",
    zoom: "ズーム",
    zoomLevel: "ズームレベル",
    pngExport: "PNG書き出し",
    quality: "品質",
    transparentAreaColor: "透明部分の色",
    loadingImage: "画像を読み込んでいます",
    downloadPng: "ダウンロード",
    downloadNewImage: "新しい画像をダウンロード",
    imageTrimmer: "イメージトリマー",
    imageMasking: "イメージマスキング",
    maskMode: "マスクモード",
    blur: "ぼかし",
    overlayColor: "カラーオーバーレイ",
    shareImage: "画像を共有",
    copyImage: "画像をコピー",
    shareTo: "共有先",
    openWeibo: "Weiboを開く",
    openWeChat: "WeChatを開く",
    openDouyin: "Douyinを開く",
    openXiaohongshu: "Xiaohongshuを開く",
    openQQ: "QQを開く",
    shareToX: "Xに共有",
    shareToFacebook: "Facebookに共有",
    shareToLinkedIn: "LinkedInに共有",
    shareToReddit: "Redditに共有",
    shareCopiedTitle: "画像をクリップボードにコピーしました",
    shareCopiedMessage: "画像をクリップボードにコピーしました。今すぐ投稿に貼り付けられます。",
    openingNow: "開いています",
    saveLastChanges: "ダウンロードする前に最後の変更を画像に保存しますか？",
    yes: "はい",
    no: "いいえ",
    donate: "寄付",
    layer1: "レイヤー 1",
    layer2: "レイヤー 2",
    flipLayers: "レイヤー反転",
    mergeLayers: "レイヤーを結合",
    uploadLayer: "レイヤーを追加",
    showLayer: "レイヤーを表示",
    hideLayer: "レイヤーを非表示",
    topLayerSize: "上レイヤーサイズ",
    borderThickness: "ボーダーの太さ",
    borderColor: "ボーダーの色",
    gradientBorder: "グラデーションボーダー",
    linearGradient: "線形",
    edgeGradient: "エッジ",
    gradientAngle: "グラデーション角度",
    gradientStart: "開始色",
    gradientEnd: "終了色",
    swapGradientColors: "グラデーションの色を入れ替える",
    moveCrop: "クロップを移動",
    moveLayer: "レイヤーを移動",
    resizeTop: "上を調整",
    resizeRight: "右を調整",
    resizeBottom: "下を調整",
    resizeLeft: "左を調整",
    resizeTopRight: "右上を調整",
    resizeTopLeft: "左上を調整",
    resizeBottomRight: "右下を調整",
    resizeBottomLeft: "左下を調整",
    shadow: "ドロップシャドウ",
    filters: "カラーフィルター",
    adjustments: "画像調整",
    text: "テキストとスタンプ",
    greenify: "緑",
    redify: "赤",
    bluify: "青",
    purplify: "紫",
    brownify: "茶",
    contrast: "コントラスト",
    brightness: "明るさ",
    colorBalance: "カラーバランス",
    saturation: "彩度",
    sharpen: "シャープ",
    soften: "ソフト",
    width: "幅",
    height: "高さ",
    size: "サイズ",
    topPosition: "上部位置",
    horizontal: "水平方向",
    textField: "テキスト",
    opacity: "不透明度",
    textMode: "テキスト",
    stampMode: "スタンプ",
    decorations: "デコレーション",
    color: "色",
    apply: "適用",
    revert: "元に戻す",
  },
  zh: {
    tools: "工具",
    language: "语言",
    auto: "自动",
    manualOverview: "概览",
    tutorialQuickTip: "快速提示",
    tutorialLetsGetCutting: "开始裁剪吧！",
    tutorialDragHandles: "拖动这些手柄，选择你想保留的区域。",
    tutorialMakeCut: "开始裁剪！",
    tutorialClickScissors: "现在点击剪刀按钮裁剪图片。",
    tutorialKeepCreating: "继续创作！",
    tutorialTryTools: "现在试试这些工具，让你的图片更加出彩！",
    tutorialDontShowAgain: "不再显示",
    showTutorial: "显示教程",
    tutorialOk: "确定",
    unappliedChangesWarning: "你的图片有尚未应用的更改。",
    image: "信息",
    corners: "圆角",
    keyboardShortcuts: "键盘快捷键",
    undo: "撤销",
    redo: "重做",
    exportShortcut: "导出",
    historyShortcut: "历史记录",
    returnToPoint: "返回到此编辑节点",
    deleteThisStep: "删除此步骤",
    applyCurrentEdit: "应用当前编辑",
    switchTools: "切换工具",
    closePanels: "关闭面板",
    editHistory: "编辑历史",
    enterFullscreen: "进入全屏",
    exitFullscreen: "退出全屏",
    border: "添加边框",
    shadow: "阴影",
    settings: "设置",
    manualButton: "用户手册",
    crop: "裁剪与蒙版",
    cropShort: "裁剪",
    maskShort: "蒙版",
    cropCommon: "常规",
    cropSocial: "社交",
    socialTemplateSizeWarning: "上传的图片小于此模板的推荐尺寸。继续操作可能会降低图片质量。",
    freeCut: "自由裁剪",
    squareCut: "方形裁剪",
    widescreenCut: "16:9 裁剪",
    circleCut: "圆形裁剪",
    heartCut: "心形裁剪",
    starCut: "星形裁剪",
    freeSelectLabel: "自由选择",
    roundedSelect: "圆角选择",
    maskType: "蒙版类型",
    resize: "调整大小和旋转",
    resizeOnly: "调整大小",
    rotate: "旋转",
    rotateFlip: "旋转和翻转",
    rotateClockwise: "90° 顺时针",
    rotateCounterClockwise: "90° 逆时针",
    flipVertical: "垂直翻转",
    flipHorizontal: "水平翻转",
    flip: "翻转",
    freeRotate: "自由旋转",
    filters: "滤镜",
    adjustments: "调整",
    text: "文字和印章",
    export: "导出",
    chooseImage: "选择图片",
    replaceImage: "替换图片",
    editor: "编辑器",
    theme: "主题",
    noImageLoaded: "未加载图片",
    useLightMode: "使用浅色模式",
    useDarkMode: "使用深色模式",
    formats: "PNG、JPG、WebP 或 GIF",
    dropImage: "拖入图片开始编辑",
    tapToLoadImage: "点按此处加载图片",
    localBrowserPrefix: "您的数据",
    localBrowserYours: "完全属于您",
    localBrowserSuffix: "，不会上传到任何服务器，确保您的隐私 100% 安全。",
    dropToBegin: "拖入图片开始",
    seoTitle: "Cut My Pic - 本地图片编辑器",
    seoDescription: "一款在浏览器中本地运行的私密图片编辑器，支持裁剪、圆角、边框、阴影、滤镜、调整、缩放、文字和导出。",
    tool: "工具",
    closePanel: "关闭面板",
    noFileSelected: "未选择文件",
    source: "来源",
    dimensions: "尺寸",
    uploadSize: "上传尺寸",
    currentSize: "当前尺寸",
    cornerRadius: "圆角半径",
    max: "最大",
    lock: "锁定",
    center: "居中",
    full: "完整",
    square: "正方形",
    zoom: "缩放",
    zoomLevel: "缩放级别",
    pngExport: "导出 PNG",
    quality: "质量",
    transparentAreaColor: "透明区域颜色",
    loadingImage: "正在加载图片",
    downloadPng: "下载",
    downloadNewImage: "下载新图片",
    imageTrimmer: "图像裁切器",
    imageMasking: "图像遮罩",
    maskMode: "遮罩模式",
    blur: "模糊",
    overlayColor: "颜色叠加",
    shareImage: "分享图片",
    copyImage: "复制图片",
    shareTo: "分享到",
    openWeibo: "打开微博",
    openWeChat: "打开微信",
    openDouyin: "打开抖音",
    openXiaohongshu: "打开小红书",
    openQQ: "打开 QQ",
    shareToX: "分享到 X",
    shareToFacebook: "分享到 Facebook",
    shareToLinkedIn: "分享到 LinkedIn",
    shareToReddit: "分享到 Reddit",
    shareCopiedTitle: "图片已复制到剪贴板",
    shareCopiedMessage: "图片已复制到剪贴板。你现在可以将图片粘贴到你的帖子中。",
    openingNow: "正在打开",
    saveLastChanges: "在下载前保存对图片的最后更改吗？",
    yes: "是",
    no: "否",
    donate: "捐赠",
    layer1: "图层 1",
    layer2: "图层 2",
    flipLayers: "翻转图层",
    mergeLayers: "合并图层",
    uploadLayer: "上传图层",
    showLayer: "显示图层",
    hideLayer: "隐藏图层",
    topLayerSize: "顶层大小",
    borderThickness: "边框粗细",
    borderColor: "边框颜色",
    gradientBorder: "渐变边框",
    linearGradient: "线性",
    edgeGradient: "边缘",
    gradientAngle: "渐变角度",
    gradientStart: "渐变起点",
    gradientEnd: "渐变终点",
    swapGradientColors: "交换渐变颜色",
    moveCrop: "移动裁剪框",
    moveLayer: "移动图层",
    resizeTop: "调整上边",
    resizeRight: "调整右边",
    resizeBottom: "调整下边",
    resizeLeft: "调整左边",
    resizeTopRight: "调整右上角",
    resizeTopLeft: "调整左上角",
    resizeBottomRight: "调整右下角",
    resizeBottomLeft: "调整左下角",
    freeSelect: "自由选择",
    squareSelect: "方形选择",
    widescreenSelect: "16:9 选择",
    circleSelect: "圆形选择",
    heartSelect: "心形选择",
    constrainProportions: "保持比例",
    freeform: "自由形式",
    filterPreset: "滤镜预设",
    none: "无",
    bw: "黑白",
    sepia: "棕褐色",
    oldFashioned: "复古风",
    vintage: "复古",
    greenify: "绿色",
    redify: "红色",
    bluify: "蓝色",
    purplify: "紫色",
    brownify: "棕色",
    coolTone: "冷色调",
    warmTone: "暖色调",
    cinematic: "电影感",
    matte: "哑光",
    cyanotype: "青蓝印相",
    warmGlow: "温暖光晕",
    contrast: "对比度",
    brightness: "亮度",
    colorBalance: "色彩平衡",
    saturation: "饱和度",
    sharpen: "锐化",
    soften: "柔化",
    width: "宽度",
    height: "高度",
    size: "大小",
    topPosition: "顶部位置",
    horizontal: "水平",
    textField: "文本",
    opacity: "不透明度",
    textMode: "文本",
    stampMode: "印章",
    decorations: "装饰",
    color: "颜色",
    apply: "应用",
    revert: "还原",
    textPlaceholder: "在此输入文字",
    live: "预览中",
    ready: "就绪",
    shadowDarkness: "阴影深度",
    shadowSize: "阴影大小",
    shadowColor: "阴影颜色",
    shadowDirection: "阴影方向",
    resizeHelp: "自由形式会分别调整每个尺寸。保持比例会将它们锁定在一起。",
  },
  ko: {
    tools: "도구",
    language: "언어",
    auto: "자동",
    manualOverview: "개요",
    tutorialQuickTip: "간단한 팁",
    tutorialLetsGetCutting: "자르기를 시작해 볼까요!",
    tutorialDragHandles: "핸들을 드래그하여 남기고 싶은 영역을 선택하세요.",
    tutorialMakeCut: "이미지를 잘라 보세요!",
    tutorialClickScissors: "이제 가위 버튼을 클릭하여 이미지를 자르세요.",
    tutorialKeepCreating: "계속 꾸며 보세요!",
    tutorialTryTools: "이 도구들을 사용해 이미지를 더욱 멋지게 꾸며 보세요!",
    tutorialDontShowAgain: "다시 표시하지 않기",
    showTutorial: "튜토리얼 표시",
    tutorialOk: "확인",
    unappliedChangesWarning: "이미지에 적용되지 않은 변경 사항이 있습니다.",
    image: "정보",
    corners: "둥근 모서리",
    keyboardShortcuts: "키보드 단축키",
    undo: "실행 취소",
    redo: "다시 실행",
    exportShortcut: "내보내기",
    historyShortcut: "기록",
    returnToPoint: "이 편집 지점으로 돌아가기",
    deleteThisStep: "이 단계 삭제",
    applyCurrentEdit: "현재 편집 적용",
    switchTools: "도구 전환",
    closePanels: "패널 닫기",
    editHistory: "편집 기록",
    enterFullscreen: "전체 화면으로",
    exitFullscreen: "전체 화면 종료",
    border: "테두리 추가",
    shadow: "그림자",
    settings: "설정",
    manualButton: "사용자 설명서",
    crop: "자르기 및 마스크",
    cropShort: "자르기",
    maskShort: "마스크",
    cropCommon: "일반",
    cropSocial: "소셜",
    socialTemplateSizeWarning: "업로드한 이미지가 이 템플릿의 권장 크기보다 작습니다. 계속하면 이미지 품질이 저하될 수 있습니다.",
    freeCut: "자유 자르기",
    squareCut: "정사각형 자르기",
    widescreenCut: "16:9 자르기",
    circleCut: "원형 자르기",
    heartCut: "하트 자르기",
    starCut: "별 자르기",
    instagramPost: "인스타그램 게시물",
    storyReel: "스토리 / 릴",
    tiktokDouyin: "틱톡 / 더우인",
    pinterestPin: "핀터레스트 핀",
    youtubeThumbnail: "유튜브 썸네일",
    facebookFeed: "페이스북 피드",
    facebookCover: "페이스북 커버",
    xPost: "X 게시물",
    xHeader: "X 헤더",
    linkedinCover: "링크드인 커버",
    xiaohongshuNote: "샤오홍슈 노트",
    wechatCover: "위챗 커버",
    weiboFeed: "웨이보 피드",
    portraitFeedFormat: "세로 피드 형식",
    fullscreenVertical: "전체 화면 세로 형식",
    verticalVideoCover: "세로 동영상 커버",
    tallDiscoveryPin: "긴 탐색용 핀 형식",
    videoThumbnail: "동영상 썸네일",
    squarePostFormat: "정사각형 게시물 형식",
    coverLinkPreview: "커버 링크 미리보기",
    landscapePostImage: "가로 게시물 이미지",
    profileBanner: "프로필 배너",
    wideSocialPreview: "넓은 소셜 미리보기",
    redNotePortraitNote: "샤오홍슈 세로 형식",
    articleCoverStyle: "기사 커버 스타일",
    squareFeedImage: "정사각형 피드 이미지",
    freeSelectLabel: "자유 선택",
    roundedSelect: "둥근 선택",
    maskType: "마스크 유형",
    intensity: "강도",
    dropShadowSize: "그림자 크기",
    dropShadowIntensity: "그림자 강도",
    dropShadowColor: "그림자 색",
    positionText: "텍스트 배치",
    place: "배치",
    animals: "동물",
    labels: "레이블",
    resize: "크기 조정 및 회전",
    resizeOnly: "크기 조정",
    rotate: "회전",
    rotateFlip: "회전 및 뒤집기",
    rotateClockwise: "90° 시계 방향",
    rotateCounterClockwise: "90° 반시계 방향",
    flipVertical: "세로 뒤집기",
    flipHorizontal: "가로 뒤집기",
    flip: "뒤집기",
    freeRotate: "자유 회전",
    filters: "필터",
    adjustments: "조정",
    text: "텍스트 및 스탬프",
    export: "내보내기",
    chooseImage: "이미지 선택",
    replaceImage: "이미지 교체",
    editor: "편집기",
    theme: "테마",
    noImageLoaded: "이미지가 없습니다",
    useLightMode: "라이트 모드 사용",
    useDarkMode: "다크 모드 사용",
    formats: "PNG, JPG, WebP 또는 GIF",
    dropImage: "이미지를 놓아 편집 시작",
    tapToLoadImage: "여기를 탭하여 이미지를 불러오기",
    localBrowserPrefix: "사용자 데이터는",
    localBrowserYours: "사용자만의 것",
    localBrowserSuffix: "이며 어떤 서버에도 업로드되지 않아 개인정보를 100% 지켜드립니다.",
    dropToBegin: "이미지를 놓아 시작",
    seoTitle: "Cut My Pic - 로컬 이미지 편집기",
    seoDescription: "자르기, 둥근 모서리, 테두리, 그림자, 필터, 조정, 크기 변경, 텍스트, 내보내기를 지원하는 브라우저 내 개인용 이미지 편집기입니다.",
    tool: "도구",
    closePanel: "패널 닫기",
    noFileSelected: "선택된 파일 없음",
    source: "소스",
    dimensions: "크기",
    uploadSize: "업로드 크기",
    currentSize: "현재 크기",
    cornerRadius: "모서리 반경",
    max: "최대",
    lock: "잠금",
    center: "가운데",
    full: "전체",
    square: "정사각형",
    zoom: "확대/축소",
    zoomLevel: "확대/축소 수준",
    pngExport: "PNG 내보내기",
    quality: "품질",
    transparentAreaColor: "투명 영역 색상",
    loadingImage: "이미지 불러오는 중",
    downloadPng: "다운로드",
    downloadNewImage: "새 이미지 다운로드",
    imageTrimmer: "이미지 트리머",
    imageMasking: "이미지 마스킹",
    maskMode: "마스크 모드",
    blur: "흐림",
    overlayColor: "색상 오버레이",
    shareImage: "이미지 공유",
    copyImage: "이미지 복사",
    shareTo: "공유 대상",
    openWeibo: "웨이보 열기",
    openWeChat: "위챗 열기",
    openDouyin: "더우인 열기",
    openXiaohongshu: "샤오홍슈 열기",
    openQQ: "QQ 열기",
    shareToX: "X로 공유",
    shareToFacebook: "Facebook으로 공유",
    shareToLinkedIn: "LinkedIn으로 공유",
    shareToReddit: "Reddit으로 공유",
    shareCopiedTitle: "이미지가 클립보드에 복사되었습니다",
    shareCopiedMessage: "이미지가 클립보드에 복사되었습니다. 이제 게시물에 이미지를 붙여넣을 수 있습니다.",
    openingNow: "지금 열기",
    saveLastChanges: "다운로드하기 전에 마지막 변경 사항을 이미지에 저장할까요?",
    yes: "예",
    no: "아니요",
    donate: "기부",
    layer1: "레이어 1",
    layer2: "레이어 2",
    flipLayers: "레이어 전환",
    mergeLayers: "레이어 병합",
    uploadLayer: "레이어 업로드",
    showLayer: "레이어 표시",
    hideLayer: "레이어 숨기기",
    topLayerSize: "상단 레이어 크기",
    borderThickness: "테두리 두께",
    borderColor: "테두리 색상",
    gradientBorder: "그라데이션 테두리",
    linearGradient: "선형",
    edgeGradient: "가장자리",
    gradientAngle: "그라데이션 각도",
    gradientStart: "시작 색상",
    gradientEnd: "끝 색상",
    swapGradientColors: "그라데이션 색상 바꾸기",
    moveCrop: "자르기 영역 이동",
    moveLayer: "레이어 이동",
    resizeTop: "위쪽 조절",
    resizeRight: "오른쪽 조절",
    resizeBottom: "아래쪽 조절",
    resizeLeft: "왼쪽 조절",
    resizeTopRight: "오른쪽 위 조절",
    resizeTopLeft: "왼쪽 위 조절",
    resizeBottomRight: "오른쪽 아래 조절",
    resizeBottomLeft: "왼쪽 아래 조절",
    freeSelect: "자유 선택",
    squareSelect: "정사각형 선택",
    widescreenSelect: "16:9 선택",
    circleSelect: "원형 선택",
    heartSelect: "하트 선택",
    constrainProportions: "비율 유지",
    freeform: "자유형",
    filterPreset: "필터 프리셋",
    none: "없음",
    bw: "흑백",
    sepia: "세피아",
    oldFashioned: "올드 패션",
    vintage: "빈티지",
    greenify: "초록",
    redify: "빨강",
    bluify: "파랑",
    purplify: "보라",
    brownify: "갈색",
    coolTone: "차가운 톤",
    warmTone: "따뜻한 톤",
    cinematic: "시네마",
    matte: "매트",
    cyanotype: "시아노타입",
    warmGlow: "따뜻한 빛",
    contrast: "대비",
    brightness: "밝기",
    colorBalance: "색 균형",
    saturation: "채도",
    sharpen: "선명하게",
    soften: "부드럽게",
    width: "너비",
    height: "높이",
    size: "크기",
    topPosition: "상단 위치",
    horizontal: "가로",
    textField: "텍스트",
    opacity: "불투명도",
    textMode: "텍스트",
    stampMode: "스탬프",
    decorations: "장식",
    color: "색상",
    apply: "적용",
    revert: "되돌리기",
    textPlaceholder: "텍스트를 입력하세요",
    live: "실시간",
    ready: "준비됨",
    shadowDarkness: "그림자 농도",
    shadowSize: "그림자 크기",
    shadowColor: "그림자 색상",
    shadowDirection: "그림자 방향",
    resizeHelp: "자유형은 각 치수를 따로 조절합니다. 비율 유지는 가로와 세로를 함께 묶습니다.",
  },
  hi: {
    tools: "टूल",
    language: "भाषा",
    auto: "स्वतः",
    manualOverview: "अवलोकन",
    tutorialQuickTip: "त्वरित सुझाव",
    tutorialLetsGetCutting: "चलिए काटना शुरू करें!",
    tutorialDragHandles: "जिस हिस्से को रखना है उसे चुनने के लिए इन हैंडल को खींचें।",
    tutorialMakeCut: "अब क्रॉप करें!",
    tutorialClickScissors: "अब अपनी इमेज क्रॉप करने के लिए इस कैंची पर क्लिक करें।",
    tutorialKeepCreating: "बनाते रहें!",
    tutorialTryTools: "अपनी इमेज को और आकर्षक बनाने के लिए इन टूल्स को आज़माएँ।",
    tutorialDontShowAgain: "दोबारा न दिखाएँ",
    showTutorial: "ट्यूटोरियल दिखाएँ",
    tutorialOk: "ठीक है",
    unappliedChangesWarning: "आपकी इमेज में लागू न किए गए बदलाव हैं।",
    image: "जानकारी",
    corners: "गोल कोने",
    keyboardShortcuts: "कीबोर्ड शॉर्टकट",
    undo: "पूर्ववत करें",
    redo: "फिर से करें",
    exportShortcut: "एक्सपोर्ट",
    historyShortcut: "इतिहास",
    returnToPoint: "संपादन के इस बिंदु पर लौटें",
    deleteThisStep: "इस चरण को हटाएँ",
    applyCurrentEdit: "वर्तमान संपादन लागू करें",
    switchTools: "टूल बदलें",
    closePanels: "पैनल बंद करें",
    editHistory: "एडिट इतिहास",
    enterFullscreen: "पूर्ण स्क्रीन में जाएँ",
    exitFullscreen: "पूर्ण स्क्रीन से बाहर आएँ",
    border: "बॉर्डर जोड़ें",
    shadow: "छाया",
    settings: "सेटिंग्स",
    manualButton: "उपयोगकर्ता मार्गदर्शिका",
    crop: "क्रॉप और मास्क",
    cropShort: "क्रॉप",
    maskShort: "मास्क",
    cropCommon: "सामान्य",
    cropSocial: "सोशल",
    socialTemplateSizeWarning: "अपलोड की गई इमेज इस टेम्पलेट के सुझाए गए आकार से छोटी है। जारी रखने पर इमेज की गुणवत्ता कम हो सकती है।",
    freeCut: "मुक्त कट",
    squareCut: "वर्गाकार कट",
    widescreenCut: "16:9 कट",
    circleCut: "वृत्ताकार कट",
    heartCut: "दिल के आकार में काटें",
    starCut: "तारे के आकार में काटें",
    freeSelectLabel: "मुक्त चयन",
    roundedSelect: "गोल चयन",
    maskType: "मास्क प्रकार",
    intensity: "तीव्रता",
    dropShadowSize: "छाया आकार",
    dropShadowIntensity: "छाया तीव्रता",
    dropShadowColor: "छाया रंग",
    positionText: "पाठ रखें",
    place: "रखें",
    animals: "जानवर",
    labels: "लेबल",
    resize: "आकार बदलें और घुमाएँ",
    resizeOnly: "आकार बदलें",
    rotate: "घुमाएँ",
    rotateFlip: "घुमाएँ और पलटें",
    rotateClockwise: "90° दक्षिणावर्त",
    rotateCounterClockwise: "90° वामावर्त",
    flipVertical: "ऊर्ध्वाधर पलटें",
    flipHorizontal: "क्षैतिज पलटें",
    flip: "पलटें",
    freeRotate: "मुक्त घुमाव",
    filters: "फ़िल्टर",
    adjustments: "समायोजन",
    text: "टेक्स्ट और स्टैम्प",
    export: "निर्यात",
    chooseImage: "छवि चुनें",
    replaceImage: "छवि बदलें",
    editor: "एडिटर",
    theme: "थीम",
    noImageLoaded: "कोई छवि लोड नहीं",
    useLightMode: "लाइट मोड उपयोग करें",
    useDarkMode: "डार्क मोड उपयोग करें",
    formats: "PNG, JPG, WebP या GIF",
    dropImage: "संपादन शुरू करने के लिए छवि छोड़ें",
    tapToLoadImage: "छवि लोड करने के लिए यहां टैप करें",
    localBrowserPrefix: "आपका डेटा",
    localBrowserYours: "आपका ही है",
    localBrowserSuffix: "और किसी सर्वर पर अपलोड नहीं किया जाएगा, ताकि आपकी गोपनीयता 100% बनी रहे।",
    dropToBegin: "शुरू करने के लिए छवि छोड़ें",
    seoTitle: "Cut My Pic - लोकल इमेज एडिटर",
    seoDescription: "ब्राउज़र में चलने वाला निजी इमेज एडिटर, जिसमें क्रॉप, राउंडेड कॉर्नर, बॉर्डर, शैडो, फ़िल्टर, एडजस्टमेंट, रिसाइज़, टेक्स्ट और एक्सपोर्ट शामिल हैं।",
    tool: "टूल",
    closePanel: "पैनल बंद करें",
    noFileSelected: "कोई फ़ाइल चयनित नहीं",
    source: "स्रोत",
    dimensions: "आयाम",
    uploadSize: "अपलोड आकार",
    currentSize: "वर्तमान आकार",
    cornerRadius: "कोना त्रिज्या",
    max: "अधिकतम",
    lock: "लॉक",
    center: "केंद्र",
    full: "पूरा",
    square: "वर्ग",
    zoom: "ज़ूम",
    zoomLevel: "ज़ूम स्तर",
    pngExport: "PNG निर्यात",
    quality: "गुणवत्ता",
    transparentAreaColor: "पारदर्शी क्षेत्र का रंग",
    loadingImage: "छवि लोड हो रही है",
    downloadPng: "डाउनलोड",
    downloadNewImage: "नई छवि डाउनलोड करें",
    imageTrimmer: "इमेज ट्रिमर",
    imageMasking: "इमेज मास्किंग",
    maskMode: "मास्क मोड",
    blur: "ब्लर",
    overlayColor: "कलर ओवरले",
    shareImage: "छवि साझा करें",
    copyImage: "छवि कॉपी करें",
    shareTo: "साझा करें",
    openWeibo: "Weibo खोलें",
    openWeChat: "WeChat खोलें",
    openDouyin: "Douyin खोलें",
    openXiaohongshu: "Xiaohongshu खोलें",
    openQQ: "QQ खोलें",
    shareToX: "X पर साझा करें",
    shareToFacebook: "Facebook पर साझा करें",
    shareToLinkedIn: "LinkedIn पर साझा करें",
    shareToReddit: "Reddit पर साझा करें",
    shareCopiedTitle: "छवि क्लिपबोर्ड में कॉपी हो गई है",
    shareCopiedMessage: "छवि क्लिपबोर्ड में कॉपी हो गई है। अब आप अपनी पोस्ट में छवि पेस्ट कर सकते हैं।",
    openingNow: "अब खोल रहा है",
    saveLastChanges: "डाउनलोड करने से पहले अंतिम बदलाव छवि में सहेजें?",
    yes: "हाँ",
    no: "नहीं",
    donate: "दान करें",
    layer1: "लेयर 1",
    layer2: "लेयर 2",
    flipLayers: "लेयर पलटें",
    mergeLayers: "लेयर मिलाएं",
    uploadLayer: "लेयर अपलोड करें",
    showLayer: "लेयर दिखाएं",
    hideLayer: "लेयर छिपाएं",
    topLayerSize: "ऊपरी लेयर आकार",
    borderThickness: "बॉर्डर मोटाई",
    borderColor: "बॉर्डर रंग",
    gradientBorder: "ग्रेडिएंट बॉर्डर",
    linearGradient: "लीनियर",
    edgeGradient: "किनारा",
    gradientAngle: "ग्रेडिएंट कोण",
    gradientStart: "ग्रेडिएंट शुरू",
    gradientEnd: "ग्रेडिएंट अंत",
    swapGradientColors: "ग्रेडिएंट रंग बदलें",
    moveCrop: "क्रॉप क्षेत्र ले जाएं",
    moveLayer: "लेयर ले जाएं",
    resizeTop: "ऊपर बदलें",
    resizeRight: "दाईं ओर बदलें",
    resizeBottom: "नीचे बदलें",
    resizeLeft: "बाईं ओर बदलें",
    resizeTopRight: "ऊपर दाईं ओर बदलें",
    resizeTopLeft: "ऊपर बाईं ओर बदलें",
    resizeBottomRight: "नीचे दाईं ओर बदलें",
    resizeBottomLeft: "नीचे बाईं ओर बदलें",
    freeSelect: "मुक्त चयन",
    squareSelect: "वर्ग चयन",
    widescreenSelect: "16:9 चयन",
    circleSelect: "सर्कल चयन",
    heartSelect: "दिल चयन",
    constrainProportions: "अनुपात बनाए रखें",
    freeform: "मुक्त रूप",
    filterPreset: "फ़िल्टर प्रीसेट",
    none: "कोई नहीं",
    bw: "काला-सफ़ेद",
    sepia: "सेपिया",
    oldFashioned: "पुराना अंदाज़",
    vintage: "विंटेज",
    greenify: "हरा",
    redify: "लाल",
    bluify: "नीला",
    purplify: "बैंगनी",
    brownify: "भूरा",
    coolTone: "ठंडा टोन",
    warmTone: "गर्म टोन",
    cinematic: "सिनेमैटिक",
    matte: "मैट",
    cyanotype: "सायनोटाइप",
    warmGlow: "गर्म चमक",
    contrast: "कंट्रास्ट",
    brightness: "चमक",
    colorBalance: "रंग संतुलन",
    saturation: "संतृप्ति",
    sharpen: "तेज़ करें",
    soften: "मुलायम करें",
    width: "चौड़ाई",
    height: "ऊँचाई",
    size: "आकार",
    topPosition: "ऊपरी स्थिति",
    horizontal: "क्षैतिज",
    textField: "टेक्स्ट",
    opacity: "अपारदर्शिता",
    textMode: "टेक्स्ट",
    stampMode: "स्टैम्प",
    decorations: "सजावट",
    color: "रंग",
    apply: "लागू करें",
    revert: "वापस करें",
    textPlaceholder: "अपना टेक्स्ट यहाँ",
    live: "लाइव",
    ready: "तैयार",
    shadowDarkness: "छाया की गहराई",
    shadowSize: "छाया का आकार",
    shadowColor: "छाया का रंग",
    shadowDirection: "छाया की दिशा",
    resizeHelp: "मुक्त रूप में हर माप अलग-अलग बदलता है। अनुपात बनाए रखें दोनों मापों को साथ जोड़ता है।",
  },
  pt: {
    tools: "Ferramentas",
    language: "Idioma",
    auto: "Automático",
    manualOverview: "Resumen",
    tutorialQuickTip: "Dica rápida",
    tutorialLetsGetCutting: "Vamos começar a recortar!",
    tutorialDragHandles: "Arraste estas alças para selecionar a área que deseja manter.",
    tutorialMakeCut: "Faça o recorte!",
    tutorialClickScissors: "Agora clique nesta tesoura para recortar sua imagem.",
    tutorialKeepCreating: "Continue criando!",
    tutorialTryTools: "Experimente estas ferramentas para deixar sua imagem ainda mais interessante.",
    tutorialDontShowAgain: "Não mostrar novamente",
    showTutorial: "Mostrar tutorial",
    tutorialOk: "OK",
    unappliedChangesWarning: "Sua imagem tem alterações não aplicadas.",
    image: "Info",
    corners: "Arredondar cantos",
    keyboardShortcuts: "Atalhos de teclado",
    undo: "Desfazer",
    redo: "Refazer",
    exportShortcut: "Exportar",
    historyShortcut: "Histórico",
    returnToPoint: "Voltar a este ponto da edição",
    deleteThisStep: "Excluir esta etapa",
    applyCurrentEdit: "Aplicar edição atual",
    switchTools: "Trocar ferramentas",
    closePanels: "Fechar painéis",
    editHistory: "Histórico de edição",
    enterFullscreen: "Entrar em tela cheia",
    exitFullscreen: "Sair da tela cheia",
    border: "Adicionar borda",
    shadow: "Sombra",
    settings: "Configurações",
    manualButton: "Manual do usuário",
    crop: "Cortar e máscara",
    cropShort: "Cortar",
    maskShort: "Máscara",
    cropCommon: "Comum",
    cropSocial: "Social",
    socialTemplateSizeWarning: "A imagem enviada é menor que o tamanho recomendado para este modelo. Continuar pode reduzir a qualidade da imagem.",
    freeCut: "Corte livre",
    squareCut: "Corte quadrado",
    widescreenCut: "Corte 16:9",
    circleCut: "Corte circular",
    heartCut: "Corte de coração",
    starCut: "Corte de estrela",
    freeSelectLabel: "Seleção livre",
    roundedSelect: "Seleção arredondada",
    maskType: "Tipo de máscara",
    intensity: "Intensidade",
    dropShadowSize: "Tamanho da sombra",
    dropShadowIntensity: "Intensidade da sombra",
    dropShadowColor: "Cor da sombra",
    positionText: "Posicionar texto",
    place: "Posicionar",
    animals: "Animais",
    labels: "Etiquetas",
    resize: "Redimensionar e girar",
    resizeOnly: "Redimensionar",
    rotate: "Girar",
    rotateFlip: "Girar e inverter",
    rotateClockwise: "90° no sentido horário",
    rotateCounterClockwise: "90° no sentido anti-horário",
    flipVertical: "Inverter verticalmente",
    flipHorizontal: "Inverter horizontalmente",
    flip: "Inverter",
    freeRotate: "Rotação livre",
    filters: "Filtros",
    adjustments: "Ajustes",
    text: "Texto e carimbos",
    export: "Exportar",
    chooseImage: "Escolher imagem",
    replaceImage: "Substituir imagem",
    editor: "Editor",
    theme: "Tema",
    noImageLoaded: "Nenhuma imagem carregada",
    useLightMode: "Usar modo claro",
    useDarkMode: "Usar modo escuro",
    formats: "PNG, JPG, WebP ou GIF",
    dropImage: "Solte uma imagem para editar",
    tapToLoadImage: "Toque aqui para carregar a imagem",
    localBrowserPrefix: "Seus dados são",
    localBrowserYours: "seus",
    localBrowserSuffix: "e não serão enviados a nenhum servidor, garantindo sua privacidade em 100%.",
    dropToBegin: "Solte uma imagem para começar",
    seoTitle: "Cut My Pic - editor de imagens local",
    seoDescription: "Um editor de imagens privado no navegador para cortar, arredondar cantos, bordas, sombras, filtros, ajustes, redimensionar, texto e exportar.",
    tool: "Ferramenta",
    closePanel: "Fechar painel",
    noFileSelected: "Nenhum arquivo selecionado",
    source: "Origem",
    dimensions: "Dimensões",
    uploadSize: "Tamanho enviado",
    currentSize: "Tamanho atual",
    cornerRadius: "Raio dos cantos",
    max: "Máx.",
    lock: "Bloquear",
    center: "Centralizar",
    full: "Inteira",
    square: "Quadrado",
    zoom: "Zoom",
    zoomLevel: "Nível de zoom",
    pngExport: "Exportar PNG",
    quality: "Qualidade",
    transparentAreaColor: "Cor da área transparente",
    loadingImage: "Carregando imagem",
    downloadPng: "Baixar",
    downloadNewImage: "Baixar nova imagem",
    imageTrimmer: "Cortador de imagem",
    imageMasking: "Máscara de imagem",
    maskMode: "Modo de máscara",
    blur: "Desfoque",
    overlayColor: "Sobreposição de cor",
    shareImage: "Compartilhar imagem",
    shareCopiedTitle: "Imagem copiada para a área de transferência",
    shareCopiedMessage: "Imagem copiada para a área de transferência. Agora você pode colar a imagem na sua publicação.",
    openingNow: "Abrindo agora",
    saveLastChanges: "Salvar as últimas alterações na imagem antes de baixar?",
    yes: "Sim",
    no: "Não",
    donate: "Doar",
    layer1: "Camada 1",
    layer2: "Camada 2",
    flipLayers: "Inverter camadas",
    mergeLayers: "Mesclar camadas",
    uploadLayer: "Enviar camada",
    showLayer: "Mostrar camada",
    hideLayer: "Ocultar camada",
    topLayerSize: "Tamanho da camada superior",
    borderThickness: "Espessura da borda",
    borderColor: "Cor da borda",
    gradientBorder: "Borda em gradiente",
    linearGradient: "Linear",
    edgeGradient: "Contorno",
    gradientAngle: "Ângulo do gradiente",
    gradientStart: "Início do gradiente",
    gradientEnd: "Fim do gradiente",
    swapGradientColors: "Trocar cores do gradiente",
    moveCrop: "Mover corte",
    moveLayer: "Mover camada",
    resizeTop: "Ajustar topo",
    resizeRight: "Ajustar direita",
    resizeBottom: "Ajustar base",
    resizeLeft: "Ajustar esquerda",
    resizeTopRight: "Ajustar canto superior direito",
    resizeTopLeft: "Ajustar canto superior esquerdo",
    resizeBottomRight: "Ajustar canto inferior direito",
    resizeBottomLeft: "Ajustar canto inferior esquerdo",
    freeSelect: "Seleção livre",
    squareSelect: "Seleção quadrada",
    widescreenSelect: "Seleção 16:9",
    circleSelect: "Seleção circular",
    heartSelect: "Seleção de coração",
    constrainProportions: "Manter proporções",
    freeform: "Forma livre",
    filterPreset: "Predefinição de filtro",
    none: "Nenhum",
    bw: "P&B",
    sepia: "Sépia",
    oldFashioned: "Antigo",
    vintage: "Vintage",
    greenify: "Verde",
    redify: "Vermelho",
    bluify: "Azul",
    purplify: "Roxo",
    brownify: "Marrom",
    coolTone: "Tom frio",
    warmTone: "Tom quente",
    cinematic: "Cinematográfico",
    matte: "Matte",
    cyanotype: "Cianótipo",
    warmGlow: "Brilho quente",
    contrast: "Contraste",
    brightness: "Brilho",
    colorBalance: "Balanço de cor",
    saturation: "Saturação",
    sharpen: "Aguçar",
    soften: "Suavizar",
    width: "Largura",
    height: "Altura",
    size: "Tamanho",
    topPosition: "Posição superior",
    horizontal: "Horizontal",
    textField: "Texto",
    opacity: "Opacidade",
    textMode: "Texto",
    stampMode: "Carimbo",
    decorations: "Decorações",
    color: "Cor",
    apply: "Aplicar",
    revert: "Reverter",
    textPlaceholder: "Seu texto aqui",
    live: "Ao vivo",
    ready: "Pronto",
    shadowDarkness: "Escuridão",
    shadowSize: "Tamanho da sombra",
    shadowColor: "Cor da sombra",
    shadowDirection: "Direção da sombra",
    resizeHelp: "Forma livre altera cada dimensão separadamente. Manter proporções vincula largura e altura.",
  },
  it: {
    tools: "Strumenti",
    language: "Lingua",
    auto: "Auto",
    manualOverview: "Overview",
    tutorialQuickTip: "Suggerimento rapido",
    tutorialLetsGetCutting: "Iniziamo a ritagliare!",
    tutorialDragHandles: "Trascina queste maniglie per selezionare l'area che vuoi conservare.",
    tutorialMakeCut: "Ritaglia l'immagine!",
    tutorialClickScissors: "Ora fai clic su queste forbici per ritagliare l'immagine.",
    tutorialKeepCreating: "Continua a creare!",
    tutorialTryTools: "Prova questi strumenti per rendere la tua immagine ancora più interessante.",
    tutorialDontShowAgain: "Non mostrare più",
    showTutorial: "Mostra tutorial",
    tutorialOk: "OK",
    unappliedChangesWarning: "L'immagine contiene modifiche non applicate.",
    image: "Info",
    corners: "Angoli arrotondati",
    keyboardShortcuts: "Scorciatoie da tastiera",
    undo: "Annulla",
    redo: "Ripeti",
    exportShortcut: "Esporta",
    historyShortcut: "Cronologia",
    returnToPoint: "Torna a questo punto della modifica",
    deleteThisStep: "Elimina questo passaggio",
    applyCurrentEdit: "Applica modifica corrente",
    switchTools: "Cambia strumenti",
    closePanels: "Chiudi pannelli",
    editHistory: "Cronologia modifiche",
    enterFullscreen: "Entra a schermo intero",
    exitFullscreen: "Esci dallo schermo intero",
    border: "Aggiungi bordo",
    shadow: "Ombra",
    settings: "Impostazioni",
    manualButton: "Manuale utente",
    crop: "Ritaglia e maschera",
    cropShort: "Ritaglia",
    maskShort: "Maschera",
    cropCommon: "Comune",
    cropSocial: "Social",
    socialTemplateSizeWarning: "L’immagine caricata è più piccola delle dimensioni consigliate per questo modello. Continuando, la qualità dell’immagine potrebbe diminuire.",
    freeCut: "Ritaglio libero",
    squareCut: "Ritaglio quadrato",
    widescreenCut: "Ritaglio 16:9",
    circleCut: "Ritaglio circolare",
    heartCut: "Ritaglio a cuore",
    starCut: "Ritaglio a stella",
    freeSelectLabel: "Selezione libera",
    roundedSelect: "Selezione arrotondata",
    maskType: "Tipo di maschera",
    intensity: "Intensità",
    dropShadowSize: "Dimensione ombra",
    dropShadowIntensity: "Intensità ombra",
    dropShadowColor: "Colore ombra",
    positionText: "Posiziona testo",
    place: "Posiziona",
    animals: "Animali",
    labels: "Etichette",
    resize: "Ridimensiona e ruota",
    resizeOnly: "Ridimensiona",
    rotate: "Ruota",
    rotateFlip: "Ruota e capovolgi",
    rotateClockwise: "90° in senso orario",
    rotateCounterClockwise: "90° in senso antiorario",
    flipVertical: "Capovolgi verticalmente",
    flipHorizontal: "Capovolgi orizzontalmente",
    flip: "Capovolgi",
    freeRotate: "Rotazione libera",
    filters: "Filtri",
    adjustments: "Regolazioni",
    text: "Testo e timbri",
    export: "Esporta",
    chooseImage: "Scegli immagine",
    replaceImage: "Sostituisci immagine",
    editor: "Editor",
    theme: "Tema",
    noImageLoaded: "Nessuna immagine caricata",
    useLightMode: "Usa tema chiaro",
    useDarkMode: "Usa tema scuro",
    formats: "PNG, JPG, WebP o GIF",
    dropImage: "Trascina un'immagine per modificare",
    tapToLoadImage: "Tocca qui per caricare un'immagine",
    localBrowserPrefix: "I tuoi dati sono",
    localBrowserYours: "tuoi",
    localBrowserSuffix: "e non verranno caricati su alcun server, garantendo al 100% la tua privacy.",
    dropToBegin: "Trascina un'immagine per iniziare",
    seoTitle: "Cut My Pic - editor immagini locale",
    seoDescription: "Un editor di immagini privato nel browser per ritagliare, arrotondare angoli, bordi, ombre, filtri, regolazioni, ridimensionare, testo ed esportazione.",
    tool: "Strumento",
    closePanel: "Chiudi pannello",
    noFileSelected: "Nessun file selezionato",
    source: "Origine",
    dimensions: "Dimensioni",
    uploadSize: "Dimensione caricata",
    currentSize: "Dimensione attuale",
    cornerRadius: "Raggio angoli",
    max: "Max",
    lock: "Blocca",
    center: "Centra",
    full: "Intera",
    square: "Quadrata",
    zoom: "Zoom",
    zoomLevel: "Livello di zoom",
    pngExport: "Esporta PNG",
    quality: "Qualità",
    transparentAreaColor: "Colore area trasparente",
    loadingImage: "Caricamento immagine",
    downloadPng: "Scarica",
    downloadNewImage: "Scarica nuova immagine",
    imageTrimmer: "Ritaglio immagine",
    imageMasking: "Mascheratura immagine",
    maskMode: "Modalità maschera",
    blur: "Sfocatura",
    overlayColor: "Sovrapposizione colore",
    shareImage: "Condividi immagine",
    shareCopiedTitle: "Immagine copiata negli appunti",
    shareCopiedMessage: "Immagine copiata negli appunti. Ora puoi incollare l’immagine nel tuo post.",
    openingNow: "Apertura in corso",
    saveLastChanges: "Salvare le ultime modifiche all'immagine prima del download?",
    yes: "Sì",
    no: "No",
    donate: "Dona",
    layer1: "Livello 1",
    layer2: "Livello 2",
    flipLayers: "Inverti livelli",
    mergeLayers: "Unisci livelli",
    uploadLayer: "Carica livello",
    showLayer: "Mostra livello",
    hideLayer: "Nascondi livello",
    topLayerSize: "Dimensione livello superiore",
    borderThickness: "Spessore bordo",
    borderColor: "Colore bordo",
    gradientBorder: "Bordo sfumato",
    linearGradient: "Lineare",
    edgeGradient: "Contorno",
    gradientAngle: "Angolo sfumatura",
    gradientStart: "Inizio sfumatura",
    gradientEnd: "Fine sfumatura",
    swapGradientColors: "Scambia i colori della sfumatura",
    moveCrop: "Sposta ritaglio",
    moveLayer: "Sposta livello",
    resizeTop: "Ridimensiona sopra",
    resizeRight: "Ridimensiona destra",
    resizeBottom: "Ridimensiona sotto",
    resizeLeft: "Ridimensiona sinistra",
    resizeTopRight: "Ridimensiona in alto a destra",
    resizeTopLeft: "Ridimensiona in alto a sinistra",
    resizeBottomRight: "Ridimensiona in basso a destra",
    resizeBottomLeft: "Ridimensiona in basso a sinistra",
    freeSelect: "Selezione libera",
    squareSelect: "Selezione quadrata",
    widescreenSelect: "Selezione 16:9",
    circleSelect: "Selezione cerchio",
    heartSelect: "Selezione cuore",
    constrainProportions: "Mantieni proporzioni",
    freeform: "Forma libera",
    filterPreset: "Preimpostazione filtro",
    none: "Nessuno",
    bw: "Bianco e nero",
    sepia: "Seppia",
    oldFashioned: "Vecchio stile",
    vintage: "Vintage",
    greenify: "Verde",
    redify: "Rosso",
    bluify: "Blu",
    purplify: "Viola",
    brownify: "Marrone",
    coolTone: "Tono freddo",
    warmTone: "Tono caldo",
    cinematic: "Cinematografico",
    matte: "Opaco",
    cyanotype: "Cianotipo",
    warmGlow: "Bagliore caldo",
    contrast: "Contrasto",
    brightness: "Luminosità",
    colorBalance: "Bilanciamento colore",
    saturation: "Saturazione",
    sharpen: "Nitidezza",
    soften: "Smussa",
    width: "Larghezza",
    height: "Altezza",
    size: "Dimensione",
    topPosition: "Posizione superiore",
    horizontal: "Orizzontale",
    textField: "Testo",
    opacity: "Opacità",
    textMode: "Testo",
    stampMode: "Timbro",
    decorations: "Decorazioni",
    color: "Colore",
    apply: "Applica",
    revert: "Ripristina",
    textPlaceholder: "Il tuo testo qui",
    live: "Live",
    ready: "Pronto",
    shadowDarkness: "Oscurità",
    shadowSize: "Dimensione ombra",
    shadowColor: "Colore ombra",
    shadowDirection: "Direzione ombra",
    resizeHelp: "La forma libera mantiene ogni dimensione indipendente. Mantieni proporzioni le blocca insieme.",
  },
  ru: {
    tools: "Инструменты",
    language: "Язык",
    auto: "Авто",
    manualOverview: "Обзор",
    tutorialQuickTip: "Быстрый совет",
    tutorialLetsGetCutting: "Начнём обрезку!",
    tutorialDragHandles: "Перетащите маркеры, чтобы выбрать область, которую хотите оставить.",
    tutorialMakeCut: "Обрежьте изображение!",
    tutorialClickScissors: "Теперь нажмите на ножницы, чтобы обрезать изображение.",
    tutorialKeepCreating: "Продолжайте творить!",
    tutorialTryTools: "Попробуйте эти инструменты, чтобы сделать изображение ещё выразительнее.",
    tutorialDontShowAgain: "Больше не показывать",
    showTutorial: "Показывать обучение",
    tutorialOk: "ОК",
    unappliedChangesWarning: "В изображении есть неприменённые изменения.",
    image: "Инфо",
    corners: "Скруглить углы",
    keyboardShortcuts: "Сочетания клавиш",
    undo: "Отменить",
    redo: "Повторить",
    exportShortcut: "Экспорт",
    historyShortcut: "История",
    returnToPoint: "Вернуться к этому моменту редактирования",
    deleteThisStep: "Удалить этот шаг",
    applyCurrentEdit: "Применить текущее изменение",
    switchTools: "Переключить инструменты",
    closePanels: "Закрыть панели",
    editHistory: "История правок",
    enterFullscreen: "Войти в полноэкранный режим",
    exitFullscreen: "Выйти из полноэкранного режима",
    border: "Добавить рамку",
    shadow: "Тень",
    settings: "Настройки",
    manualButton: "Руководство пользователя",
    crop: "Обрезка и маска",
    cropShort: "Обрезка",
    maskShort: "Маска",
    cropCommon: "Общие",
    cropSocial: "Соцсети",
    socialTemplateSizeWarning: "Загруженное изображение меньше рекомендуемого размера для этого шаблона. Если продолжить, качество изображения может снизиться.",
    freeCut: "Свободный кадр",
    squareCut: "Квадратный кадр",
    widescreenCut: "Кадр 16:9",
    circleCut: "Круглый кадр",
    heartCut: "Кадр в форме сердца",
    starCut: "Кадр в форме звезды",
    freeSelectLabel: "Свободный выбор",
    roundedSelect: "Скругленный выбор",
    maskType: "Тип маски",
    intensity: "Интенсивность",
    dropShadowSize: "Размер тени",
    dropShadowIntensity: "Интенсивность тени",
    dropShadowColor: "Цвет тени",
    positionText: "Разместить текст",
    place: "Разместить",
    animals: "Животные",
    labels: "Метки",
    resize: "Изменить размер и повернуть",
    resizeOnly: "Изменить размер",
    rotate: "Повернуть",
    rotateFlip: "Поворот и отражение",
    rotateClockwise: "90° по часовой стрелке",
    rotateCounterClockwise: "90° против часовой стрелки",
    flipVertical: "Отразить по вертикали",
    flipHorizontal: "Отразить по горизонтали",
    flip: "Отразить",
    freeRotate: "Свободный поворот",
    filters: "Фильтры",
    adjustments: "Коррекции",
    text: "Текст и штампы",
    export: "Экспорт",
    chooseImage: "Выбрать изображение",
    replaceImage: "Заменить изображение",
    editor: "Редактор",
    theme: "Тема",
    noImageLoaded: "Изображение не загружено",
    useLightMode: "Включить светлый режим",
    useDarkMode: "Включить темный режим",
    formats: "PNG, JPG, WebP или GIF",
    dropImage: "Перетащите изображение для редактирования",
    tapToLoadImage: "Нажмите здесь, чтобы загрузить изображение",
    localBrowserPrefix: "Ваши данные",
    localBrowserYours: "принадлежат вам",
    localBrowserSuffix: "и не будут загружаться ни на какой сервер, обеспечивая вашу конфиденциальность на 100%.",
    dropToBegin: "Перетащите изображение, чтобы начать",
    seoTitle: "Cut My Pic - локальный редактор изображений",
    seoDescription: "Частный редактор изображений прямо в браузере для кадрирования, скругления углов, рамок, теней, фильтров, настроек, изменения размера, текста и экспорта.",
    tool: "Инструмент",
    closePanel: "Закрыть панель",
    noFileSelected: "Файл не выбран",
    source: "Источник",
    dimensions: "Размеры",
    uploadSize: "Размер загрузки",
    currentSize: "Текущий размер",
    cornerRadius: "Радиус углов",
    max: "Макс.",
    lock: "Блок.",
    center: "Центр",
    full: "Полное",
    square: "Квадрат",
    zoom: "Масштаб",
    zoomLevel: "Уровень масштаба",
    pngExport: "Экспорт PNG",
    quality: "Качество",
    transparentAreaColor: "Цвет прозрачной области",
    loadingImage: "Загрузка изображения",
    downloadPng: "Скачать",
    downloadNewImage: "Скачать новое изображение",
    imageTrimmer: "Триммер изображения",
    imageMasking: "Маска изображения",
    maskMode: "Режим маски",
    blur: "Размытие",
    overlayColor: "Цветовая заливка",
    shareImage: "Поделиться изображением",
    shareCopiedTitle: "Изображение скопировано в буфер обмена",
    shareCopiedMessage: "Изображение скопировано в буфер обмена. Теперь вы можете вставить его в публикацию.",
    openingNow: "Открывается",
    saveLastChanges: "Сохранить последние изменения изображения перед загрузкой?",
    yes: "Да",
    no: "Нет",
    donate: "Поддержать",
    layer1: "Слой 1",
    layer2: "Слой 2",
    flipLayers: "Поменять слои",
    mergeLayers: "Объединить слои",
    uploadLayer: "Загрузить слой",
    showLayer: "Показать слой",
    hideLayer: "Скрыть слой",
    topLayerSize: "Размер верхнего слоя",
    borderThickness: "Толщина рамки",
    borderColor: "Цвет рамки",
    gradientBorder: "Градиентная рамка",
    linearGradient: "Линейный",
    edgeGradient: "По краю",
    gradientAngle: "Угол градиента",
    gradientStart: "Начало градиента",
    gradientEnd: "Конец градиента",
    swapGradientColors: "Поменять цвета градиента",
    moveCrop: "Переместить обрезку",
    moveLayer: "Переместить слой",
    resizeTop: "Изменить верх",
    resizeRight: "Изменить справа",
    resizeBottom: "Изменить низ",
    resizeLeft: "Изменить слева",
    resizeTopRight: "Изменить верх справа",
    resizeTopLeft: "Изменить верх слева",
    resizeBottomRight: "Изменить низ справа",
    resizeBottomLeft: "Изменить низ слева",
    freeSelect: "Свободный выбор",
    squareSelect: "Квадратный выбор",
    widescreenSelect: "Выбор 16:9",
    circleSelect: "Круговое выделение",
    heartSelect: "Выделение сердца",
    constrainProportions: "Сохранять пропорции",
    freeform: "Свободный режим",
    filterPreset: "Пресет фильтра",
    none: "Нет",
    bw: "Ч/Б",
    sepia: "Сепия",
    oldFashioned: "Старый стиль",
    vintage: "Винтаж",
    greenify: "Зелёный",
    redify: "Красный",
    bluify: "Синий",
    purplify: "Фиолетовый",
    brownify: "Коричневый",
    coolTone: "Холодный тон",
    warmTone: "Теплый тон",
    cinematic: "Киношный",
    matte: "Матовый",
    cyanotype: "Цианотипия",
    warmGlow: "Теплое свечение",
    contrast: "Контраст",
    brightness: "Яркость",
    colorBalance: "Баланс цвета",
    saturation: "Насыщенность",
    sharpen: "Резкость",
    soften: "Смягчение",
    width: "Ширина",
    height: "Высота",
    size: "Размер",
    topPosition: "Верхняя позиция",
    horizontal: "Горизонтально",
    textField: "Текст",
    opacity: "Непрозрачность",
    textMode: "Текст",
    stampMode: "Штамп",
    decorations: "Украшения",
    color: "Цвет",
    apply: "Применить",
    revert: "Вернуть",
    textPlaceholder: "Ваш текст здесь",
    live: "Живой",
    ready: "Готово",
    shadowDarkness: "Темнота тени",
    shadowSize: "Размер тени",
    shadowColor: "Цвет тени",
    shadowDirection: "Направление тени",
    resizeHelp: "Свободный режим изменяет каждую величину отдельно. Сохранение пропорций связывает их вместе.",
  },
  ar: {
    tools: "الأدوات",
    language: "اللغة",
    auto: "تلقائي",
    manualOverview: "نظرة عامة",
    tutorialQuickTip: "تلميح سريع",
    tutorialLetsGetCutting: "لنبدأ القص!",
    tutorialDragHandles: "اسحب هذه المقابض لتحديد المنطقة التي تريد الاحتفاظ بها.",
    tutorialMakeCut: "نفّذ القص!",
    tutorialClickScissors: "انقر الآن على المقص لقص صورتك.",
    tutorialKeepCreating: "واصل الإبداع!",
    tutorialTryTools: "جرّب هذه الأدوات لإضفاء مزيد من التميز على صورتك.",
    tutorialDontShowAgain: "عدم الإظهار مجددًا",
    showTutorial: "إظهار البرنامج التعليمي",
    tutorialOk: "حسنًا",
    unappliedChangesWarning: "توجد تغييرات غير مطبّقة على صورتك.",
    image: "معلومات",
    corners: "تدوير الزوايا",
    keyboardShortcuts: "اختصارات لوحة المفاتيح",
    undo: "تراجع",
    redo: "إعادة",
    exportShortcut: "تصدير",
    historyShortcut: "السجل",
    returnToPoint: "العودة إلى هذه النقطة في التعديل",
    deleteThisStep: "حذف هذه الخطوة",
    applyCurrentEdit: "تطبيق التعديل الحالي",
    switchTools: "تبديل الأدوات",
    closePanels: "إغلاق اللوحات",
    editHistory: "سجل التعديلات",
    enterFullscreen: "الدخول إلى ملء الشاشة",
    exitFullscreen: "الخروج من ملء الشاشة",
    border: "إضافة حد",
    settings: "الإعدادات",
    manualButton: "دليل المستخدم",
    crop: "القص والقناع",
    cropShort: "قص",
    maskShort: "قناع",
    cropCommon: "عام",
    cropSocial: "اجتماعي",
    socialTemplateSizeWarning: "الصورة التي تم تحميلها أصغر من الحجم الموصى به لهذا القالب. قد يؤدي المتابعة إلى انخفاض جودة الصورة.",
    freeCut: "قص حر",
    squareCut: "قص مربع",
    widescreenCut: "قص 16:9",
    circleCut: "قص دائري",
    heartCut: "قص على شكل قلب",
    starCut: "قص على شكل نجمة",
    freeSelectLabel: "تحديد حر",
    roundedSelect: "تحديد مستدير",
    maskType: "نوع القناع",
    resize: "تغيير الحجم والتدوير",
    resizeOnly: "تغيير الحجم",
    rotate: "تدوير",
    rotateFlip: "تدوير وعكس",
    rotateClockwise: "90° مع عقارب الساعة",
    rotateCounterClockwise: "90° عكس عقارب الساعة",
    flipVertical: "عكس رأسي",
    flipHorizontal: "عكس أفقي",
    flip: "عكس",
    freeRotate: "تدوير حر",
    export: "تصدير",
    chooseImage: "اختر صورة",
    replaceImage: "استبدال الصورة",
    editor: "المحرر",
    theme: "السمة",
    noImageLoaded: "لم يتم تحميل صورة",
    useLightMode: "استخدم الوضع الفاتح",
    useDarkMode: "استخدم الوضع الداكن",
    formats: "PNG أو JPG أو WebP أو GIF",
    dropImage: "أفلت صورة لبدء التحرير",
    seoTitle: "Cut My Pic - محرر صور محلي",
    seoDescription: "محرر صور خاص يعمل داخل المتصفح للقص، تدوير الزوايا، الإطارات، الظلال، الفلاتر، التعديلات، تغيير الحجم، النص والتصدير.",
    localBrowserPrefix: "بياناتك",
    localBrowserYours: "لك",
    localBrowserSuffix: "ولن يتم رفعها إلى أي خادم، مما يضمن خصوصيتك بنسبة 100%.",
    dropToBegin: "أفلت صورة للبدء",
    freeSelect: "تحديد حر",
    squareSelect: "تحديد مربع",
    widescreenSelect: "تحديد 16:9",
    circleSelect: "تحديد دائري",
    heartSelect: "تحديد قلب",
    constrainProportions: "تقييد النسب",
    freeform: "حر",
    width: "العرض",
    height: "الارتفاع",
    filterPreset: "إعداد المرشح",
    none: "لا شيء",
    oldFashioned: "قديم الطراز",
    vintage: "فيتاج",
    greenify: "أخضر",
    redify: "أحمر",
    bluify: "أزرق",
    purplify: "بنفسجي",
    brownify: "بني",
    coolTone: "نغمة باردة",
    warmTone: "نغمة دافئة",
    cinematic: "سينمائي",
    matte: "مطفي",
    cyanotype: "سيانوتايب",
    warmGlow: "توهج دافئ",
    contrast: "التباين",
    brightness: "السطوع",
    colorBalance: "توازن اللون",
    saturation: "التشبع",
    sharpen: "زيادة الحدة",
    soften: "تنعيم",
    size: "الحجم",
    topPosition: "الموضع العلوي",
    horizontal: "أفقي",
    textField: "النص",
    opacity: "العتامة",
    textMode: "نص",
    stampMode: "ختم",
    decorations: "زخارف",
    color: "اللون",
    apply: "تطبيق",
    revert: "استعادة",
    resizeHelp: "يحتفظ الوضع الحر بكل بُعد بشكل مستقل. يربط تقييد النسب بينهما.",
    textPlaceholder: "اكتب النص هنا",
    tool: "الأداة",
    closePanel: "إغلاق اللوحة",
    noFileSelected: "لم يتم اختيار ملف",
    source: "المصدر",
    dimensions: "الأبعاد",
    uploadSize: "حجم الرفع",
    currentSize: "الحجم الحالي",
    cornerRadius: "نصف قطر الزوايا",
    max: "أقصى",
    lock: "قفل",
    center: "توسيط",
    full: "كامل",
    square: "مربع",
    zoom: "تكبير",
    zoomLevel: "مستوى التكبير",
    pngExport: "تصدير PNG",
    quality: "الجودة",
    transparentAreaColor: "لون المنطقة الشفافة",
    loadingImage: "جارٍ تحميل الصورة",
    downloadPng: "تنزيل",
    downloadNewImage: "تنزيل صورة جديدة",
    imageTrimmer: "مُقصّ الصورة",
    imageMasking: "قناع الصورة",
    maskMode: "وضع القناع",
    blur: "تمويه",
    overlayColor: "تراكب اللون",
    shareImage: "مشاركة الصورة",
    shareCopiedTitle: "تم نسخ الصورة إلى الحافظة",
    shareCopiedMessage: "تم نسخ الصورة إلى الحافظة. يمكنك الآن لصق الصورة في منشورك.",
    openingNow: "جارٍ الفتح الآن",
    saveLastChanges: "حفظ آخر التغييرات على الصورة قبل التنزيل؟",
    yes: "نعم",
    no: "لا",
    donate: "تبرع",
    layer1: "الطبقة 1",
    layer2: "الطبقة 2",
    flipLayers: "عكس الطبقات",
    mergeLayers: "دمج الطبقات",
    uploadLayer: "تحميل طبقة",
    showLayer: "إظهار الطبقة",
    hideLayer: "إخفاء الطبقة",
    topLayerSize: "حجم الطبقة العليا",
    borderThickness: "سماكة الحد",
    borderColor: "لون الحد",
    gradientBorder: "حد متدرج",
    linearGradient: "خطي",
    edgeGradient: "الحافة",
    gradientAngle: "زاوية التدرج",
    gradientStart: "بداية التدرج",
    gradientEnd: "نهاية التدرج",
    swapGradientColors: "تبديل ألوان التدرج",
    moveCrop: "تحريك القص",
    moveLayer: "تحريك الطبقة",
    resizeTop: "تغيير الأعلى",
    resizeRight: "تغيير اليمين",
    resizeBottom: "تغيير الأسفل",
    resizeLeft: "تغيير اليسار",
    resizeTopRight: "تغيير أعلى اليمين",
    resizeTopLeft: "تغيير أعلى اليسار",
    resizeBottomRight: "تغيير أسفل اليمين",
    resizeBottomLeft: "تغيير أسفل اليسار",
    shadow: "ظل منسدل",
    filters: "مرشحات الألوان",
    adjustments: "تعديلات الصورة",
    text: "النص والأختام",
  },
  nl: {
    tools: "Tools",
    language: "Taal",
    auto: "Automatisch",
    manualOverview: "Übersicht",
    tutorialQuickTip: "Snelle tip",
    tutorialLetsGetCutting: "Laten we gaan bijsnijden!",
    tutorialDragHandles: "Sleep deze handgrepen om het gebied te selecteren dat je wilt behouden.",
    tutorialMakeCut: "Snijd de afbeelding bij!",
    tutorialClickScissors: "Klik nu op deze schaar om je afbeelding bij te snijden.",
    tutorialKeepCreating: "Blijf creëren!",
    tutorialTryTools: "Probeer deze tools om je afbeelding nog aantrekkelijker te maken.",
    tutorialDontShowAgain: "Niet meer tonen",
    showTutorial: "Tutorial tonen",
    tutorialOk: "OK",
    unappliedChangesWarning: "Je afbeelding bevat niet-toegepaste wijzigingen.",
    image: "Info",
    corners: "Hoeken afronden",
    keyboardShortcuts: "Sneltoetsen",
    undo: "Ongedaan maken",
    redo: "Opnieuw",
    exportShortcut: "Exporteren",
    historyShortcut: "Geschiedenis",
    returnToPoint: "Ga terug naar dit bewerkingspunt",
    deleteThisStep: "Deze stap verwijderen",
    applyCurrentEdit: "Huidige wijziging toepassen",
    switchTools: "Van gereedschap wisselen",
    closePanels: "Panelen sluiten",
    editHistory: "Bewerkgeschiedenis",
    enterFullscreen: "Volledig scherm openen",
    exitFullscreen: "Volledig scherm verlaten",
    border: "Rand toevoegen",
    shadow: "Schaduw",
    settings: "Instellingen",
    manualButton: "Gebruikershandleiding",
    crop: "Bijsnijden en masker",
    cropShort: "Bijsnijden",
    maskShort: "Masker",
    cropCommon: "Algemeen",
    cropSocial: "Sociaal",
    socialTemplateSizeWarning: "De geüploade afbeelding is kleiner dan het aanbevolen formaat voor deze sjabloon. Doorgaan kan de beeldkwaliteit verminderen.",
    freeCut: "Vrije uitsnede",
    squareCut: "Vierkante uitsnede",
    widescreenCut: "16:9 uitsnede",
    circleCut: "Cirkeluitsnede",
    heartCut: "Hartuitsnede",
    starCut: "Steruitsnede",
    freeSelectLabel: "Vrije selectie",
    roundedSelect: "Afgeronde selectie",
    maskType: "Maskertype",
    intensity: "Intensiteit",
    dropShadowSize: "Schaduwgrootte",
    dropShadowIntensity: "Schaduwintensiteit",
    dropShadowColor: "Schaduwkleur",
    positionText: "Tekst plaatsen",
    place: "Plaatsen",
    animals: "Dieren",
    labels: "Labels",
    resize: "Formaat wijzigen en roteren",
    resizeOnly: "Formaat wijzigen",
    rotate: "Draaien",
    rotateFlip: "Draaien en spiegelen",
    rotateClockwise: "90° met de klok mee",
    rotateCounterClockwise: "90° tegen de klok in",
    flipVertical: "Verticaal spiegelen",
    flipHorizontal: "Horizontaal spiegelen",
    flip: "Spiegelen",
    freeRotate: "Vrij roteren",
    filters: "Filters",
    adjustments: "Aanpassingen",
    text: "Tekst en stempels",
    export: "Export",
    chooseImage: "Afbeelding kiezen",
    replaceImage: "Afbeelding vervangen",
    editor: "Editor",
    theme: "Thema",
    noImageLoaded: "Geen afbeelding geladen",
    useLightMode: "Lichte modus gebruiken",
    useDarkMode: "Donkere modus gebruiken",
    formats: "PNG, JPG, WebP of GIF",
    dropImage: "Sleep een afbeelding om te bewerken",
    tapToLoadImage: "Tik hier om een afbeelding te laden",
    localBrowserPrefix: "Je gegevens zijn",
    localBrowserYours: "van jou",
    localBrowserSuffix: "en worden niet geüpload naar een server, zodat je privacy 100% behouden blijft.",
    dropToBegin: "Sleep een afbeelding om te beginnen",
    seoTitle: "Cut My Pic - lokale afbeeldingseditor",
    seoDescription: "Een private afbeeldingseditor in de browser voor bijsnijden, afgeronde hoeken, randen, schaduwen, filters, aanpassingen, schalen, tekst en export.",
    tool: "Tool",
    closePanel: "Paneel sluiten",
    noFileSelected: "Geen bestand geselecteerd",
    source: "Bron",
    dimensions: "Afmetingen",
    uploadSize: "Uploadgrootte",
    currentSize: "Huidige grootte",
    cornerRadius: "Hoekradius",
    max: "Max",
    lock: "Vergrendel",
    center: "Centreren",
    full: "Volledig",
    square: "Vierkant",
    zoom: "Zoom",
    zoomLevel: "Zoomniveau",
    pngExport: "PNG export",
    quality: "Kwaliteit",
    transparentAreaColor: "Kleur transparant gebied",
    loadingImage: "Afbeelding laden",
    downloadPng: "Downloaden",
    downloadNewImage: "Nieuwe afbeelding downloaden",
    imageTrimmer: "Afbeelding bijsnijder",
    imageMasking: "Afbeeldingsmasker",
    maskMode: "Maskermodus",
    blur: "Vervagen",
    overlayColor: "Kleuroverlay",
    shareImage: "Afbeelding delen",
    shareCopiedTitle: "Afbeelding gekopieerd naar klembord",
    shareCopiedMessage: "Afbeelding gekopieerd naar klembord. Je kunt de afbeelding nu in je bericht plakken.",
    openingNow: "Nu openen",
    saveLastChanges: "De laatste wijzigingen in de afbeelding opslaan voor het downloaden?",
    yes: "Ja",
    no: "Nee",
    donate: "Doneren",
    layer1: "Laag 1",
    layer2: "Laag 2",
    flipLayers: "Lagen wisselen",
    mergeLayers: "Lagen samenvoegen",
    uploadLayer: "Laag uploaden",
    showLayer: "Laag tonen",
    hideLayer: "Laag verbergen",
    topLayerSize: "Grootte bovenste laag",
    borderThickness: "Randdikte",
    borderColor: "Randkleur",
    gradientBorder: "Verlooprand",
    linearGradient: "Lineair",
    edgeGradient: "Rand",
    gradientAngle: "Verloophoek",
    gradientStart: "Begin verloop",
    gradientEnd: "Einde verloop",
    swapGradientColors: "Verloopkleuren omwisselen",
    moveCrop: "Bijsnede verplaatsen",
    moveLayer: "Laag verplaatsen",
    resizeTop: "Bovenkant aanpassen",
    resizeRight: "Rechts aanpassen",
    resizeBottom: "Onderkant aanpassen",
    resizeLeft: "Links aanpassen",
    resizeTopRight: "Rechtsboven aanpassen",
    resizeTopLeft: "Linksboven aanpassen",
    resizeBottomRight: "Rechtsonder aanpassen",
    resizeBottomLeft: "Linksonder aanpassen",
    freeSelect: "Vrij selecteren",
    squareSelect: "Vierkant selecteren",
    widescreenSelect: "16:9 selecteren",
    circleSelect: "Cirkel selecteren",
    heartSelect: "Hart selecteren",
    constrainProportions: "Proporties behouden",
    freeform: "Vrije vorm",
    filterPreset: "Filtervoorinstelling",
    none: "Geen",
    bw: "Zwart-wit",
    sepia: "Sepia",
    oldFashioned: "Ouderwets",
    vintage: "Vintage",
    greenify: "Groen",
    redify: "Rood",
    bluify: "Blauw",
    purplify: "Paars",
    brownify: "Bruin",
    coolTone: "Koele toon",
    warmTone: "Warme toon",
    cinematic: "Cinematisch",
    matte: "Mat",
    cyanotype: "Cyanotype",
    warmGlow: "Warme gloed",
    contrast: "Kleurencontrast",
    brightness: "Helderheid",
    colorBalance: "Kleurbalans",
    saturation: "Verzadiging",
    sharpen: "Verscherpen",
    soften: "Verzachten",
    width: "Breedte",
    height: "Hoogte",
    size: "Grootte",
    topPosition: "Bovenpositie",
    horizontal: "Horizontaal",
    textField: "Tekst",
    opacity: "Nieprzezroczystość",
    textMode: "Tekst",
    stampMode: "Stempel",
    decorations: "Decoraties",
    color: "Kleur",
    apply: "Toepassen",
    revert: "Herstellen",
    textPlaceholder: "Uw tekst hier",
    live: "Live",
    ready: "Klaar",
    shadowDarkness: "Schaduwdonker",
    shadowSize: "Schaduwgrootte",
    shadowColor: "Schaduwkleur",
    shadowDirection: "Schaduwrichting",
    resizeHelp: "Vrije vorm wijzigt elke maat apart. Proporties behouden koppelt ze aan elkaar.",
  },
  pl: {
    tools: "Narzędzia",
    language: "Język",
    auto: "Automatycznie",
    manualOverview: "Przegląd",
    tutorialQuickTip: "Szybka wskazówka",
    tutorialLetsGetCutting: "Zacznijmy przycinanie!",
    tutorialDragHandles: "Przeciągnij te uchwyty, aby wybrać obszar, który chcesz zachować.",
    tutorialMakeCut: "Przytnij obraz!",
    tutorialClickScissors: "Kliknij teraz nożyczki, aby przyciąć obraz.",
    tutorialKeepCreating: "Twórz dalej!",
    tutorialTryTools: "Wypróbuj te narzędzia, aby jeszcze bardziej uatrakcyjnić obraz.",
    tutorialDontShowAgain: "Nie pokazuj ponownie",
    showTutorial: "Pokaż samouczek",
    tutorialOk: "OK",
    unappliedChangesWarning: "Obraz zawiera niezastosowane zmiany.",
    image: "Info",
    corners: "Zaokrąglij rogi",
    keyboardShortcuts: "Skróty klawiaturowe",
    undo: "Cofnij",
    redo: "Ponów",
    exportShortcut: "Eksport",
    historyShortcut: "Historia",
    returnToPoint: "Wróć do tego etapu edycji",
    deleteThisStep: "Usuń ten krok",
    applyCurrentEdit: "Zastosuj bieżącą zmianę",
    switchTools: "Przełącz narzędzia",
    closePanels: "Zamknij panele",
    editHistory: "Historia edycji",
    enterFullscreen: "Wejdź na pełny ekran",
    exitFullscreen: "Opuść pełny ekran",
    border: "Dodaj obramowanie",
    shadow: "Cień",
    settings: "Ustawienia",
    manualButton: "Instrukcja obsługi",
    crop: "Kadruj i maska",
    cropShort: "Kadruj",
    maskShort: "Maska",
    cropCommon: "Ogólne",
    cropSocial: "Społecznościowe",
    socialTemplateSizeWarning: "Przesłany obraz jest mniejszy niż rozmiar zalecany dla tego szablonu. Kontynuowanie może obniżyć jakość obrazu.",
    freeCut: "Swobodny kadr",
    squareCut: "Kadr kwadratowy",
    widescreenCut: "Kadr 16:9",
    circleCut: "Kadr kołowy",
    heartCut: "Kadr w kształcie serca",
    starCut: "Kadr w kształcie gwiazdy",
    freeSelectLabel: "Swobodny wybór",
    roundedSelect: "Zaokrąglony wybór",
    maskType: "Typ maski",
    intensity: "Natężenie",
    dropShadowSize: "Rozmiar cienia",
    dropShadowIntensity: "Natężenie cienia",
    dropShadowColor: "Kolor cienia",
    positionText: "Umieść tekst",
    place: "Umieść",
    animals: "Zwierzęta",
    labels: "Etykiety",
    resize: "Zmień rozmiar i obróć",
    resizeOnly: "Zmień rozmiar",
    rotate: "Obróć",
    rotateFlip: "Obróć i odbij",
    rotateClockwise: "90° zgodnie z ruchem wskazówek zegara",
    rotateCounterClockwise: "90° przeciwnie do ruchu wskazówek zegara",
    flipVertical: "Odbij pionowo",
    flipHorizontal: "Odbij poziomo",
    flip: "Odbij",
    freeRotate: "Swobodny obrót",
    filters: "Filtry",
    adjustments: "Regulacje",
    text: "Tekst i pieczątki",
    export: "Eksport",
    chooseImage: "Wybierz obraz",
    replaceImage: "Zastąp obraz",
    editor: "Edytor",
    theme: "Motyw",
    noImageLoaded: "Nie wczytano obrazu",
    useLightMode: "Użyj trybu jasnego",
    useDarkMode: "Użyj trybu ciemnego",
    formats: "PNG, JPG, WebP lub GIF",
    dropImage: "Upuść obraz, aby rozpocząć edycję",
    tapToLoadImage: "Stuknij tutaj, aby wczytać obraz",
    localBrowserPrefix: "Twoje dane są",
    localBrowserYours: "twoje",
    localBrowserSuffix: "i nie będą wysyłane na żaden serwer, co zapewnia Ci 100% prywatności.",
    dropToBegin: "Upuść obraz, aby zacząć",
    seoTitle: "Cut My Pic - lokalny edytor obrazów",
    seoDescription: "Prywatny edytor obrazów działający w przeglądarce do kadrowania, zaokrąglania rogów, ramek, cieni, filtrów, regulacji, zmiany rozmiaru, tekstu i eksportu.",
    tool: "Narzędzie",
    closePanel: "Zamknij panel",
    noFileSelected: "Nie wybrano pliku",
    source: "Źródło",
    dimensions: "Wymiary",
    uploadSize: "Rozmiar przeslany",
    currentSize: "Biezacy rozmiar",
    cornerRadius: "Promień rogów",
    max: "Maks.",
    lock: "Zablokuj",
    center: "Środek",
    full: "Całość",
    square: "Kwadrat",
    zoom: "Zoom",
    zoomLevel: "Poziom powiększenia",
    pngExport: "Eksport PNG",
    quality: "Jakość",
    transparentAreaColor: "Kolor przezroczystego obszaru",
    loadingImage: "Ładowanie obrazu",
    downloadPng: "Pobierz",
    downloadNewImage: "Pobierz nowy obraz",
    imageTrimmer: "Przycinanie obrazu",
    imageMasking: "Maska obrazu",
    maskMode: "Tryb maski",
    blur: "Rozmycie",
    overlayColor: "Nakładka koloru",
    shareImage: "Udostępnij obraz",
    shareCopiedTitle: "Obraz skopiowany do schowka",
    shareCopiedMessage: "Obraz skopiowany do schowka. Teraz możesz wkleić obraz do swojego posta.",
    openingNow: "Otwieranie",
    saveLastChanges: "Zapisać ostatnie zmiany obrazu przed pobraniem?",
    yes: "Tak",
    no: "Nie",
    donate: "Wesprzyj",
    layer1: "Warstwa 1",
    layer2: "Warstwa 2",
    flipLayers: "Zamień warstwy",
    mergeLayers: "Scal warstwy",
    uploadLayer: "Wgraj warstwę",
    showLayer: "Pokaż warstwę",
    hideLayer: "Ukryj warstwę",
    topLayerSize: "Rozmiar górnej warstwy",
    borderThickness: "Grubość obramowania",
    borderColor: "Kolor obramowania",
    gradientBorder: "Gradient obramowania",
    linearGradient: "Liniowy",
    edgeGradient: "Krawędziowy",
    gradientAngle: "Kąt gradientu",
    gradientStart: "Początek gradientu",
    gradientEnd: "Koniec gradientu",
    swapGradientColors: "Zamień kolory gradientu",
    moveCrop: "Przenieś kadr",
    moveLayer: "Przenieś warstwę",
    resizeTop: "Zmień górę",
    resizeRight: "Zmień prawą",
    resizeBottom: "Zmień dół",
    resizeLeft: "Zmień lewą",
    resizeTopRight: "Zmień prawy górny",
    resizeTopLeft: "Zmień lewy górny",
    resizeBottomRight: "Zmień prawy dolny",
    resizeBottomLeft: "Zmień lewy dolny",
    freeSelect: "Swobodny wybór",
    squareSelect: "Wybór kwadratowy",
    widescreenSelect: "Wybór 16:9",
    circleSelect: "Zaznaczenie koła",
    heartSelect: "Zaznaczenie serca",
    constrainProportions: "Zachowaj proporcje",
    freeform: "Swobodny",
    filterPreset: "Ustawienie filtra",
    none: "Brak",
    bw: "Czarno-biały",
    sepia: "Sepia",
    oldFashioned: "Stary styl",
    vintage: "Vintage",
    greenify: "Zielony",
    redify: "Czerwony",
    bluify: "Niebieski",
    purplify: "Fioletowy",
    brownify: "Brązowy",
    coolTone: "Chłodny ton",
    warmTone: "Ciepły ton",
    cinematic: "Filmowy",
    matte: "Matowy",
    cyanotype: "Cjanotypia",
    warmGlow: "Ciepła poświata",
    contrast: "Kontrast",
    brightness: "Jasność",
    colorBalance: "Balans kolorów",
    saturation: "Nasycenie",
    sharpen: "Wyostrzenie",
    soften: "Zmiękczenie",
    width: "Szerokość",
    height: "Wysokość",
    size: "Rozmiar",
    topPosition: "Pozycja górna",
    horizontal: "Poziomo",
    textField: "Tekst",
    opacity: "Nieprzezroczystość",
    textMode: "Tekst",
    stampMode: "Pieczątka",
    decorations: "Dekoracje",
    color: "Kolor",
    apply: "Zastosuj",
    revert: "Przywróć",
    textPlaceholder: "Twój tekst tutaj",
    live: "Podgląd",
    ready: "Gotowe",
    shadowDarkness: "Ciemność cienia",
    shadowSize: "Rozmiar cienia",
    shadowColor: "Kolor cienia",
    shadowDirection: "Kierunek cienia",
    resizeHelp: "Tryb swobodny zmienia każdy wymiar osobno. Zachowanie proporcji łączy je ze sobą.",
  },
  tr: {
    tools: "Araçlar",
    language: "Dil",
    auto: "Otomatik",
    manualOverview: "Genel Bakış",
    tutorialQuickTip: "Hızlı ipucu",
    tutorialLetsGetCutting: "Kırpmaya başlayalım!",
    tutorialDragHandles: "Saklamak istediğiniz alanı seçmek için bu tutamaçları sürükleyin.",
    tutorialMakeCut: "Kırpmayı yapın!",
    tutorialClickScissors: "Şimdi görüntünüzü kırpmak için makasa tıklayın.",
    tutorialKeepCreating: "Üretmeye devam edin!",
    tutorialTryTools: "Görüntünüzü daha da güzelleştirmek için bu araçları deneyin.",
    tutorialDontShowAgain: "Bir daha gösterme",
    showTutorial: "Öğreticiyi göster",
    tutorialOk: "Tamam",
    unappliedChangesWarning: "Görüntünüzde uygulanmamış değişiklikler var.",
    image: "Bilgi",
    corners: "Köşeleri yuvarla",
    keyboardShortcuts: "Klavye kısayolları",
    undo: "Geri al",
    redo: "İleri al",
    exportShortcut: "Dışa aktar",
    historyShortcut: "Geçmiş",
    returnToPoint: "Düzenlemede bu noktaya dön",
    deleteThisStep: "Bu adımı sil",
    applyCurrentEdit: "Geçerli düzenlemeyi uygula",
    switchTools: "Araçları değiştir",
    closePanels: "Panelleri kapat",
    editHistory: "Düzenleme geçmişi",
    enterFullscreen: "Tam ekrana gir",
    exitFullscreen: "Tam ekrandan çık",
    border: "Kenarlık ekle",
    shadow: "Gölge",
    settings: "Ayarlar",
    manualButton: "Kullanıcı Kılavuzu",
    crop: "Kırp ve maskele",
    cropShort: "Kırp",
    maskShort: "Maske",
    cropCommon: "Genel",
    cropSocial: "Sosyal",
    socialTemplateSizeWarning: "Yüklenen görsel bu şablon için önerilen boyuttan daha küçük. Devam etmek görsel kalitesini düşürebilir.",
    freeCut: "Serbest kesim",
    squareCut: "Kare kesim",
    widescreenCut: "16:9 kesim",
    circleCut: "Dairesel kesim",
    heartCut: "Kalp kesimi",
    starCut: "Yıldız kesimi",
    freeSelectLabel: "Serbest seçim",
    roundedSelect: "Yuvarlatılmış seçim",
    maskType: "Maske türü",
    intensity: "Yoğunluk",
    dropShadowSize: "Gölge boyutu",
    dropShadowIntensity: "Gölge yoğunluğu",
    dropShadowColor: "Gölge rengi",
    positionText: "Metni konumlandır",
    place: "Yerleştir",
    animals: "Hayvanlar",
    labels: "Etiketler",
    resize: "Boyutlandır ve döndür",
    resizeOnly: "Boyutlandır",
    rotate: "Döndür",
    rotateFlip: "Döndür ve çevir",
    rotateClockwise: "90° saat yönünde",
    rotateCounterClockwise: "90° saat yönünün tersine",
    flipVertical: "Dikey çevir",
    flipHorizontal: "Yatay çevir",
    flip: "Çevir",
    freeRotate: "Serbest döndürme",
    filters: "Filtreler",
    adjustments: "Ayarlamalar",
    text: "Metin ve damgalar",
    export: "Dışa aktar",
    chooseImage: "Görsel seç",
    replaceImage: "Görseli değiştir",
    editor: "Düzenleyici",
    theme: "Tema",
    noImageLoaded: "Görsel yüklenmedi",
    useLightMode: "Açık modu kullan",
    useDarkMode: "Koyu modu kullan",
    formats: "PNG, JPG, WebP veya GIF",
    dropImage: "Düzenlemek için görsel bırakın",
    tapToLoadImage: "Görüntü yüklemek için buraya dokunun",
    localBrowserPrefix: "Verileriniz",
    localBrowserYours: "size aittir",
    localBrowserSuffix: "ve hiçbir sunucuya yüklenmez; gizliliğiniz %100 korunur.",
    dropToBegin: "Başlamak için görsel bırakın",
    seoTitle: "Cut My Pic - yerel görsel düzenleyici",
    seoDescription: "Kırpma, yuvarlatılmış köşeler, kenarlıklar, gölgeler, filtreler, ayarlamalar, yeniden boyutlandırma, metin ve dışa aktarma için tarayıcı içinde özel bir görsel düzenleyici.",
    tool: "Araç",
    closePanel: "Paneli kapat",
    noFileSelected: "Dosya seçilmedi",
    source: "Kaynak",
    dimensions: "Boyutlar",
    uploadSize: "Yukleme boyutu",
    currentSize: "Guncel boyut",
    cornerRadius: "Köşe yarıçapı",
    max: "Maks.",
    lock: "Kilitle",
    center: "Ortala",
    full: "Tam",
    square: "Kare",
    zoom: "Yakınlaştır",
    zoomLevel: "Yakınlaştırma düzeyi",
    pngExport: "PNG dışa aktar",
    quality: "Kalite",
    transparentAreaColor: "Şeffaf alan rengi",
    loadingImage: "Görsel yükleniyor",
    downloadPng: "İndir",
    downloadNewImage: "Yeni resmi indir",
    imageTrimmer: "Görsel kırpıcı",
    imageMasking: "Görsel maskesi",
    maskMode: "Maske modu",
    blur: "Bulanıklaştırma",
    overlayColor: "Renk kaplama",
    shareImage: "Görseli paylaş",
    shareCopiedTitle: "Görsel panoya kopyalandı",
    shareCopiedMessage: "Görsel panoya kopyalandı. Artık görseli gönderinize yapıştırabilirsiniz.",
    openingNow: "Şimdi açılıyor",
    saveLastChanges: "İndirmeden önce son değişiklikleri resme kaydet?",
    yes: "Evet",
    no: "Hayır",
    donate: "Bağış yap",
    layer1: "Katman 1",
    layer2: "Katman 2",
    flipLayers: "Katmanları çevir",
    mergeLayers: "Katmanları birleştir",
    uploadLayer: "Katman yükle",
    showLayer: "Katmanı göster",
    hideLayer: "Katmanı gizle",
    topLayerSize: "Üst katman boyutu",
    borderThickness: "Kenarlık kalınlığı",
    borderColor: "Kenarlık rengi",
    gradientBorder: "Gradyan kenarlık",
    linearGradient: "Doğrusal",
    edgeGradient: "Kenar",
    gradientAngle: "Gradyan açısı",
    gradientStart: "Gradyan başlangıcı",
    gradientEnd: "Gradyan sonu",
    swapGradientColors: "Gradyan renklerini değiştir",
    moveCrop: "Kırpmayı taşı",
    moveLayer: "Katmanı taşı",
    resizeTop: "Üstü ayarla",
    resizeRight: "Sağı ayarla",
    resizeBottom: "Altı ayarla",
    resizeLeft: "Solu ayarla",
    resizeTopRight: "Sağ üstü ayarla",
    resizeTopLeft: "Sol üstü ayarla",
    resizeBottomRight: "Sağ altı ayarla",
    resizeBottomLeft: "Sol altı ayarla",
    freeSelect: "Serbest seçim",
    squareSelect: "Kare seçim",
    widescreenSelect: "16:9 seçim",
    circleSelect: "Daire seçimi",
    heartSelect: "Kalp seçimi",
    constrainProportions: "Oranları koru",
    freeform: "Serbest biçim",
    filterPreset: "Filtre ön ayarı",
    none: "Yok",
    bw: "Siyah-beyaz",
    sepia: "Sepya",
    oldFashioned: "Eski tarz",
    vintage: "Vintage",
    greenify: "Yeşil",
    redify: "Kırmızı",
    bluify: "Mavi",
    purplify: "Mor",
    brownify: "Kahverengi",
    coolTone: "Soğuk ton",
    warmTone: "Sıcak ton",
    cinematic: "Sinematik",
    matte: "Mat",
    cyanotype: "Siyanotip",
    warmGlow: "Sıcak parlama",
    contrast: "Kontrast",
    brightness: "Parlaklık",
    colorBalance: "Renk dengesi",
    saturation: "Doygunluk",
    sharpen: "Keskinleştir",
    soften: "Yumuşat",
    width: "Genişlik",
    height: "Yükseklik",
    size: "Boyut",
    topPosition: "Üst konum",
    horizontal: "Yatay",
    textField: "Metin",
    opacity: "Opaklık",
    textMode: "Metin",
    stampMode: "Damga",
    decorations: "Süslemeler",
    color: "Renk",
    apply: "Uygula",
    revert: "Geri al",
    textPlaceholder: "Metninizi buraya yazın",
    live: "Canlı",
    ready: "Hazır",
    shadowDarkness: "Gölge koyuluğu",
    shadowSize: "Gölge boyutu",
    shadowColor: "Gölge rengi",
    shadowDirection: "Gölge yönü",
    resizeHelp: "Serbest biçim her boyutu ayrı değiştirir. Oranları koru onları birlikte kilitler.",
  },
  id: {
    tools: "Alat",
    language: "Bahasa",
    auto: "Otomatis",
    manualOverview: "Ikhtisar",
    tutorialQuickTip: "Tips singkat",
    tutorialLetsGetCutting: "Mari mulai memotong!",
    tutorialDragHandles: "Seret gagang ini untuk memilih area yang ingin dipertahankan.",
    tutorialMakeCut: "Potong gambarnya!",
    tutorialClickScissors: "Sekarang klik gunting ini untuk memotong gambar Anda.",
    tutorialKeepCreating: "Terus berkarya!",
    tutorialTryTools: "Coba alat-alat ini untuk membuat gambar Anda semakin menarik.",
    tutorialDontShowAgain: "Jangan tampilkan lagi",
    showTutorial: "Tampilkan tutorial",
    tutorialOk: "OK",
    unappliedChangesWarning: "Gambar Anda memiliki perubahan yang belum diterapkan.",
    image: "Info",
    corners: "Sudut membulat",
    keyboardShortcuts: "Pintasan keyboard",
    undo: "Urungkan",
    redo: "Ulangi",
    exportShortcut: "Ekspor",
    historyShortcut: "Riwayat",
    returnToPoint: "Kembali ke titik pengeditan ini",
    deleteThisStep: "Hapus langkah ini",
    applyCurrentEdit: "Terapkan edit saat ini",
    switchTools: "Ganti alat",
    closePanels: "Tutup panel",
    editHistory: "Riwayat edit",
    enterFullscreen: "Masuk layar penuh",
    exitFullscreen: "Keluar layar penuh",
    border: "Tambah border",
    shadow: "Bayangan",
    settings: "Pengaturan",
    manualButton: "Panduan pengguna",
    crop: "Pangkas dan topeng",
    cropShort: "Pangkas",
    maskShort: "Topeng",
    cropCommon: "Umum",
    cropSocial: "Sosial",
    socialTemplateSizeWarning: "Gambar yang diunggah lebih kecil daripada ukuran yang disarankan untuk templat ini. Melanjutkan dapat menurunkan kualitas gambar.",
    freeCut: "Pangkas bebas",
    squareCut: "Pangkas persegi",
    widescreenCut: "Pangkas 16:9",
    circleCut: "Pangkas lingkaran",
    heartCut: "Pangkas bentuk hati",
    starCut: "Pangkas bentuk bintang",
    freeSelectLabel: "Pilihan bebas",
    roundedSelect: "Pilihan membulat",
    maskType: "Jenis masker",
    intensity: "Intensitas",
    dropShadowSize: "Ukuran bayangan",
    dropShadowIntensity: "Intensitas bayangan",
    dropShadowColor: "Warna bayangan",
    positionText: "Tempatkan teks",
    place: "Tempatkan",
    animals: "Hewan",
    labels: "Label",
    resize: "Ubah ukuran dan putar",
    resizeOnly: "Ubah ukuran",
    rotate: "Putar",
    rotateFlip: "Putar dan balik",
    rotateClockwise: "90° searah jarum jam",
    rotateCounterClockwise: "90° berlawanan arah jarum jam",
    flipVertical: "Balik vertikal",
    flipHorizontal: "Balik horizontal",
    flip: "Balik",
    freeRotate: "Putar bebas",
    filters: "Filter",
    adjustments: "Penyesuaian",
    text: "Teks dan stempel",
    export: "Ekspor",
    chooseImage: "Pilih gambar",
    replaceImage: "Ganti gambar",
    editor: "Editor",
    theme: "Tema",
    noImageLoaded: "Belum ada gambar",
    useLightMode: "Gunakan mode terang",
    useDarkMode: "Gunakan mode gelap",
    formats: "PNG, JPG, WebP, atau GIF",
    dropImage: "Lepas gambar untuk mulai mengedit",
    tapToLoadImage: "Ketuk di sini untuk memuat gambar",
    localBrowserPrefix: "Data Anda adalah",
    localBrowserYours: "milik Anda",
    localBrowserSuffix: "dan tidak akan diunggah ke server mana pun, sehingga privasi Anda 100% terjaga.",
    dropToBegin: "Lepas gambar untuk mulai",
    seoTitle: "Cut My Pic - editor gambar lokal",
    seoDescription: "Editor gambar pribadi di dalam browser untuk memotong, sudut membulat, bingkai, bayangan, filter, penyesuaian, ubah ukuran, teks, dan ekspor.",
    tool: "Alat",
    closePanel: "Tutup panel",
    noFileSelected: "Belum ada file dipilih",
    source: "Sumber",
    dimensions: "Dimensi",
    uploadSize: "Ukuran unggahan",
    currentSize: "Ukuran saat ini",
    cornerRadius: "Radius sudut",
    max: "Maks",
    lock: "Kunci",
    center: "Tengah",
    full: "Penuh",
    square: "Persegi",
    zoom: "Zoom",
    zoomLevel: "Tingkat zoom",
    pngExport: "Ekspor PNG",
    quality: "Kualitas",
    transparentAreaColor: "Warna area transparan",
    loadingImage: "Memuat gambar",
    downloadPng: "Unduh",
    downloadNewImage: "Unduh gambar baru",
    imageTrimmer: "Pemotong gambar",
    imageMasking: "Masker gambar",
    maskMode: "Mode masker",
    blur: "Buram",
    overlayColor: "Lapisan warna",
    shareImage: "Bagikan gambar",
    shareCopiedTitle: "Gambar disalin ke papan klip",
    shareCopiedMessage: "Gambar disalin ke papan klip. Sekarang Anda dapat menempelkan gambar ke posting Anda.",
    openingNow: "Sedang membuka",
    saveLastChanges: "Simpan perubahan terakhir ke gambar sebelum mengunduh?",
    yes: "Ya",
    no: "Tidak",
    donate: "Donasi",
    layer1: "Layer 1",
    layer2: "Layer 2",
    flipLayers: "Balik layer",
    mergeLayers: "Gabungkan layer",
    uploadLayer: "Unggah layer",
    showLayer: "Tampilkan layer",
    hideLayer: "Sembunyikan layer",
    topLayerSize: "Ukuran layer atas",
    borderThickness: "Ketebalan tepi",
    borderColor: "Warna tepi",
    gradientBorder: "Tepi gradasi",
    linearGradient: "Linear",
    edgeGradient: "Tepi",
    gradientAngle: "Sudut gradasi",
    gradientStart: "Awal gradasi",
    gradientEnd: "Akhir gradasi",
    swapGradientColors: "Tukar warna gradasi",
    moveCrop: "Pindahkan pangkasan",
    moveLayer: "Pindahkan layer",
    resizeTop: "Ubah sisi atas",
    resizeRight: "Ubah sisi kanan",
    resizeBottom: "Ubah sisi bawah",
    resizeLeft: "Ubah sisi kiri",
    resizeTopRight: "Ubah kanan atas",
    resizeTopLeft: "Ubah kiri atas",
    resizeBottomRight: "Ubah kanan bawah",
    resizeBottomLeft: "Ubah kiri bawah",
    freeSelect: "Pilihan bebas",
    squareSelect: "Pilihan kotak",
    widescreenSelect: "Pilihan 16:9",
    circleSelect: "Pilih lingkaran",
    heartSelect: "Pilih hati",
    constrainProportions: "Pertahankan proporsi",
    freeform: "Bentuk bebas",
    filterPreset: "Preset filter",
    none: "Tidak ada",
    bw: "Hitam putih",
    sepia: "Sepia",
    oldFashioned: "Gaya lama",
    vintage: "Vintage",
    greenify: "Hijau",
    redify: "Merah",
    bluify: "Biru",
    purplify: "Ungu",
    brownify: "Cokelat",
    coolTone: "Nada dingin",
    warmTone: "Nada hangat",
    cinematic: "Sinematik",
    matte: "Matte",
    cyanotype: "Sianotipe",
    warmGlow: "Kilau hangat",
    contrast: "Kontras",
    brightness: "Kecerahan",
    colorBalance: "Keseimbangan warna",
    saturation: "Saturasi",
    sharpen: "Tajamkan",
    soften: "Lunakkan",
    width: "Lebar",
    height: "Tinggi",
    size: "Ukuran",
    topPosition: "Posisi atas",
    horizontal: "Horizontal",
    textField: "Teks",
    opacity: "Opasitas",
    textMode: "Teks",
    stampMode: "Stempel",
    decorations: "Dekorasi",
    color: "Warna",
    apply: "Terapkan",
    revert: "Kembalikan",
    textPlaceholder: "Teks Anda di sini",
    live: "Langsung",
    ready: "Siap",
    shadowDarkness: "Kegelapan bayangan",
    shadowSize: "Ukuran bayangan",
    shadowColor: "Warna bayangan",
    shadowDirection: "Arah bayangan",
    resizeHelp: "Mode bebas mengubah setiap dimensi secara terpisah. Pertahankan proporsi mengunci keduanya bersama.",
  },
  ms: {},
  vi: {
    tools: "Công cụ",
    language: "Ngôn ngữ",
    auto: "Tự động",
    manualOverview: "Tổng quan",
    tutorialQuickTip: "Mẹo nhanh",
    tutorialLetsGetCutting: "Bắt đầu cắt ảnh nhé!",
    tutorialDragHandles: "Kéo các tay cầm này để chọn vùng bạn muốn giữ lại.",
    tutorialMakeCut: "Cắt ảnh thôi!",
    tutorialClickScissors: "Bây giờ hãy nhấp vào biểu tượng kéo để cắt ảnh.",
    tutorialKeepCreating: "Tiếp tục sáng tạo!",
    tutorialTryTools: "Hãy thử các công cụ này để làm cho ảnh của bạn nổi bật hơn nữa.",
    tutorialDontShowAgain: "Không hiển thị lại",
    showTutorial: "Hiển thị hướng dẫn",
    tutorialOk: "OK",
    unappliedChangesWarning: "Ảnh của bạn có các thay đổi chưa được áp dụng.",
    image: "Thông tin",
    corners: "Bo góc",
    keyboardShortcuts: "Phím tắt bàn phím",
    undo: "Hoàn tác",
    redo: "Làm lại",
    exportShortcut: "Xuất",
    historyShortcut: "Lịch sử",
    returnToPoint: "Quay lại điểm chỉnh sửa này",
    deleteThisStep: "Xóa bước này",
    applyCurrentEdit: "Áp dụng chỉnh sửa hiện tại",
    switchTools: "Chuyển công cụ",
    closePanels: "Đóng bảng",
    editHistory: "Lịch sử chỉnh sửa",
    enterFullscreen: "Vào toàn màn hình",
    exitFullscreen: "Thoát toàn màn hình",
    border: "Thêm viền",
    shadow: "Đổ bóng",
    settings: "Cài đặt",
    manualButton: "Sổ tay người dùng",
    crop: "Cắt và mặt nạ",
    cropShort: "Cắt",
    maskShort: "Mặt nạ",
    cropCommon: "Chung",
    cropSocial: "Mạng xã hội",
    socialTemplateSizeWarning: "Ảnh đã tải lên nhỏ hơn kích thước được đề xuất cho mẫu này. Tiếp tục có thể làm giảm chất lượng ảnh.",
    freeCut: "Cắt tự do",
    squareCut: "Cắt vuông",
    widescreenCut: "Cắt 16:9",
    circleCut: "Cắt hình tròn",
    heartCut: "Cắt hình trái tim",
    starCut: "Cắt hình ngôi sao",
    freeSelectLabel: "Chọn tự do",
    roundedSelect: "Chọn bo tròn",
    maskType: "Loại mặt nạ",
    intensity: "Cường độ",
    dropShadowSize: "Kích thước bóng",
    dropShadowIntensity: "Cường độ bóng",
    dropShadowColor: "Màu bóng",
    positionText: "Đặt văn bản",
    place: "Đặt",
    animals: "Động vật",
    labels: "Nhãn",
    resize: "Đổi kích thước và xoay",
    resizeOnly: "Đổi kích thước",
    rotate: "Xoay",
    rotateFlip: "Xoay và lật",
    rotateClockwise: "90° theo chiều kim đồng hồ",
    rotateCounterClockwise: "90° ngược chiều kim đồng hồ",
    flipVertical: "Lật dọc",
    flipHorizontal: "Lật ngang",
    flip: "Lật",
    freeRotate: "Xoay tự do",
    filters: "Bộ lọc",
    adjustments: "Tinh chỉnh",
    text: "Văn bản và tem",
    export: "Xuất",
    chooseImage: "Chọn ảnh",
    replaceImage: "Thay ảnh",
    editor: "Trình sửa",
    theme: "Giao diện",
    noImageLoaded: "Chưa tải ảnh",
    useLightMode: "Dùng chế độ sáng",
    useDarkMode: "Dùng chế độ tối",
    formats: "PNG, JPG, WebP hoặc GIF",
    dropImage: "Thả ảnh để bắt đầu sửa",
    tapToLoadImage: "Chạm vào đây để tải ảnh",
    localBrowserPrefix: "Dữ liệu của bạn là",
    localBrowserYours: "của bạn",
    localBrowserSuffix: "và sẽ không được tải lên bất kỳ máy chủ nào, đảm bảo quyền riêng tư 100%.",
    dropToBegin: "Thả ảnh để bắt đầu",
    seoTitle: "Cut My Pic - trình chỉnh sửa ảnh cục bộ",
    seoDescription: "Trình chỉnh sửa ảnh riêng tư trong trình duyệt để cắt, bo góc, thêm viền, đổ bóng, bộ lọc, tinh chỉnh, thay đổi kích thước, văn bản và xuất ảnh.",
    tool: "Công cụ",
    closePanel: "Đóng bảng",
    noFileSelected: "Chưa chọn tệp",
    source: "Nguồn",
    dimensions: "Kích thước",
    uploadSize: "Kich thuoc tai len",
    currentSize: "Kich thuoc hien tai",
    cornerRadius: "Bán kính góc",
    max: "Tối đa",
    lock: "Khóa",
    center: "Giữa",
    full: "Đầy đủ",
    square: "Vuông",
    zoom: "Thu phóng",
    zoomLevel: "Mức thu phóng",
    pngExport: "Xuất PNG",
    quality: "Chất lượng",
    transparentAreaColor: "Màu vùng trong suốt",
    loadingImage: "Đang tải ảnh",
    downloadPng: "Tải xuống",
    downloadNewImage: "Tải ảnh mới",
    imageTrimmer: "Cắt ảnh",
    imageMasking: "Mặt nạ ảnh",
    maskMode: "Chế độ mặt nạ",
    blur: "Làm mờ",
    overlayColor: "Phủ màu",
    shareImage: "Chia sẻ ảnh",
    shareCopiedTitle: "Ảnh đã được sao chép vào bộ nhớ tạm",
    shareCopiedMessage: "Ảnh đã được sao chép vào bộ nhớ tạm. Bây giờ bạn có thể dán ảnh vào bài đăng của mình.",
    openingNow: "Đang mở",
    saveLastChanges: "Lưu các thay đổi cuối cùng vào ảnh trước khi tải xuống?",
    yes: "Có",
    no: "Không",
    donate: "Ủng hộ",
    layer1: "Lớp 1",
    layer2: "Lớp 2",
    flipLayers: "Đổi lớp",
    mergeLayers: "Gộp lớp",
    uploadLayer: "Tải lớp lên",
    showLayer: "Hiện lớp",
    hideLayer: "Ẩn lớp",
    topLayerSize: "Kích thước lớp trên",
    borderThickness: "Độ dày viền",
    borderColor: "Màu viền",
    gradientBorder: "Viền chuyển màu",
    linearGradient: "Tuyến tính",
    edgeGradient: "Theo viền",
    gradientAngle: "Góc chuyển màu",
    gradientStart: "Màu bắt đầu",
    gradientEnd: "Màu kết thúc",
    swapGradientColors: "Đổi thứ tự màu chuyển",
    moveCrop: "Di chuyển vùng cắt",
    moveLayer: "Di chuyển lớp",
    resizeTop: "Đổi cạnh trên",
    resizeRight: "Đổi cạnh phải",
    resizeBottom: "Đổi cạnh dưới",
    resizeLeft: "Đổi cạnh trái",
    resizeTopRight: "Đổi góc trên phải",
    resizeTopLeft: "Đổi góc trên trái",
    resizeBottomRight: "Đổi góc dưới phải",
    resizeBottomLeft: "Đổi góc dưới trái",
    freeSelect: "Chọn tự do",
    squareSelect: "Chọn vuông",
    widescreenSelect: "Chọn 16:9",
    circleSelect: "Chọn hình tròn",
    heartSelect: "Chọn hình trái tim",
    constrainProportions: "Giữ tỷ lệ",
    freeform: "Tự do",
    filterPreset: "Cài sẵn bộ lọc",
    none: "Không",
    bw: "Trắng đen",
    sepia: "Sepia",
    oldFashioned: "Kiểu cũ",
    vintage: "Vintage",
    greenify: "Xanh lá",
    redify: "Đỏ",
    bluify: "Xanh dương",
    purplify: "Tím",
    brownify: "Nâu",
    coolTone: "Tông lạnh",
    warmTone: "Tông ấm",
    cinematic: "Điện ảnh",
    matte: "Mờ",
    cyanotype: "Chụp cyanotype",
    warmGlow: "Ánh ấm",
    contrast: "Tương phản",
    brightness: "Độ sáng",
    colorBalance: "Cân bằng màu",
    saturation: "Độ bão hòa",
    sharpen: "Làm nét",
    soften: "Làm mềm",
    width: "Chiều rộng",
    height: "Chiều cao",
    size: "Kích cỡ",
    topPosition: "Vị trí trên",
    horizontal: "Ngang",
    textField: "Văn bản",
    opacity: "Độ mờ",
    textMode: "Văn bản",
    stampMode: "Tem",
    decorations: "Trang trí",
    color: "Màu",
    apply: "Áp dụng",
    revert: "Hoàn tác",
    textPlaceholder: "Văn bản của bạn ở đây",
    live: "Trực tiếp",
    ready: "Sẵn sàng",
    shadowDarkness: "Độ đậm bóng",
    shadowSize: "Kích thước bóng",
    shadowColor: "Màu bóng",
    shadowDirection: "Hướng bóng",
    resizeHelp: "Chế độ tự do thay đổi từng kích thước riêng. Giữ tỷ lệ khóa chúng lại với nhau.",
  },
  th: {
    tools: "เครื่องมือ",
    language: "ภาษา",
    auto: "อัตโนมัติ",
    manualOverview: "ภาพรวม",
    tutorialQuickTip: "เคล็ดลับสั้น ๆ",
    tutorialLetsGetCutting: "มาเริ่มตัดภาพกัน!",
    tutorialDragHandles: "ลากจุดจับเหล่านี้เพื่อเลือกพื้นที่ที่คุณต้องการเก็บไว้",
    tutorialMakeCut: "ตัดภาพเลย!",
    tutorialClickScissors: "ตอนนี้คลิกกรรไกรเพื่อตัดภาพของคุณ",
    tutorialKeepCreating: "สร้างสรรค์ต่อไป!",
    tutorialTryTools: "ลองใช้เครื่องมือเหล่านี้เพื่อทำให้ภาพของคุณโดดเด่นยิ่งขึ้น",
    tutorialDontShowAgain: "ไม่ต้องแสดงอีก",
    showTutorial: "แสดงบทแนะนำ",
    tutorialOk: "ตกลง",
    unappliedChangesWarning: "รูปภาพของคุณมีการเปลี่ยนแปลงที่ยังไม่ได้ใช้",
    image: "ข้อมูล",
    corners: "มุมโค้ง",
    keyboardShortcuts: "แป้นพิมพ์ลัด",
    undo: "เลิกทำ",
    redo: "ทำซ้ำ",
    exportShortcut: "ส่งออก",
    historyShortcut: "ประวัติ",
    returnToPoint: "กลับไปยังจุดแก้ไขนี้",
    deleteThisStep: "ลบขั้นตอนนี้",
    applyCurrentEdit: "ใช้การแก้ไขปัจจุบัน",
    switchTools: "สลับเครื่องมือ",
    closePanels: "ปิดแผง",
    editHistory: "ประวัติการแก้ไข",
    enterFullscreen: "เข้าสู่โหมดเต็มหน้าจอ",
    exitFullscreen: "ออกจากโหมดเต็มหน้าจอ",
    border: "เพิ่มเส้นขอบ",
    shadow: "เงา",
    settings: "การตั้งค่า",
    manualButton: "คู่มือผู้ใช้",
    crop: "ครอบตัดและมาสก์",
    cropShort: "ครอบตัด",
    maskShort: "มาสก์",
    cropCommon: "ทั่วไป",
    cropSocial: "โซเชียล",
    socialTemplateSizeWarning: "รูปภาพที่อัปโหลดมีขนาดเล็กกว่าขนาดที่แนะนำสำหรับเทมเพลตนี้ การดำเนินการต่ออาจทำให้คุณภาพของรูปภาพลดลง",
    freeCut: "ตัดอิสระ",
    squareCut: "ตัดสี่เหลี่ยม",
    widescreenCut: "ตัด 16:9",
    circleCut: "ตัดวงกลม",
    heartCut: "ตัดรูปหัวใจ",
    starCut: "ตัดรูปดาว",
    freeSelectLabel: "เลือกอิสระ",
    roundedSelect: "เลือกมุมโค้ง",
    maskType: "ชนิดมาสก์",
    intensity: "ความเข้ม",
    dropShadowSize: "ขนาดเงา",
    dropShadowIntensity: "ความเข้มเงา",
    dropShadowColor: "สีเงา",
    positionText: "วางข้อความ",
    place: "วาง",
    animals: "สัตว์",
    labels: "ป้ายกำกับ",
    resize: "ปรับขนาดและหมุน",
    resizeOnly: "ปรับขนาด",
    rotate: "หมุน",
    rotateFlip: "หมุนและพลิก",
    rotateClockwise: "90° ตามเข็มนาฬิกา",
    rotateCounterClockwise: "90° ทวนเข็มนาฬิกา",
    flipVertical: "พลิกแนวตั้ง",
    flipHorizontal: "พลิกแนวนอน",
    flip: "พลิก",
    freeRotate: "หมุนอิสระ",
    filters: "ฟิลเตอร์",
    adjustments: "ปรับแต่ง",
    text: "ข้อความและตราประทับ",
    export: "ส่งออก",
    chooseImage: "เลือกรูปภาพ",
    replaceImage: "เปลี่ยนรูปภาพ",
    editor: "ตัวแก้ไข",
    theme: "ธีม",
    noImageLoaded: "ยังไม่มีรูปภาพ",
    useLightMode: "ใช้โหมดสว่าง",
    useDarkMode: "ใช้โหมดมืด",
    formats: "PNG, JPG, WebP หรือ GIF",
    dropImage: "วางรูปภาพเพื่อเริ่มแก้ไข",
    tapToLoadImage: "แตะที่นี่เพื่อโหลดรูปภาพ",
    localBrowserPrefix: "ข้อมูลของคุณเป็น",
    localBrowserYours: "ของคุณ",
    localBrowserSuffix: "และจะไม่ถูกอัปโหลดไปยังเซิร์ฟเวอร์ใด ๆ เพื่อให้ความเป็นส่วนตัวของคุณปลอดภัย 100%",
    dropToBegin: "วางรูปภาพเพื่อเริ่ม",
    seoTitle: "Cut My Pic - เครื่องมือแต่งภาพในเบราว์เซอร์",
    seoDescription: "โปรแกรมแต่งภาพแบบส่วนตัวที่ทำงานในเบราว์เซอร์สำหรับครอป มุมโค้ง เส้นขอบ เงา ฟิลเตอร์ ปรับแต่ง ปรับขนาด ข้อความ และส่งออก",
    tool: "เครื่องมือ",
    closePanel: "ปิดแผง",
    noFileSelected: "ยังไม่ได้เลือกไฟล์",
    source: "แหล่งที่มา",
    dimensions: "ขนาด",
    uploadSize: "ขนาดที่อัปโหลด",
    currentSize: "ขนาดปัจจุบัน",
    cornerRadius: "รัศมีมุม",
    max: "สูงสุด",
    lock: "ล็อก",
    center: "กึ่งกลาง",
    full: "เต็ม",
    square: "สี่เหลี่ยม",
    zoom: "ซูม",
    zoomLevel: "ระดับการซูม",
    pngExport: "ส่งออก PNG",
    quality: "คุณภาพ",
    transparentAreaColor: "สีพื้นที่โปร่งใส",
    loadingImage: "กำลังโหลดรูปภาพ",
    downloadPng: "ดาวน์โหลด",
    downloadNewImage: "ดาวน์โหลดรูปภาพใหม่",
    imageTrimmer: "ตัวตัดภาพ",
    imageMasking: "มาสก์ภาพ",
    maskMode: "โหมดมาสก์",
    blur: "เบลอ",
    overlayColor: "การซ้อนสี",
    shareImage: "แชร์รูปภาพ",
    shareCopiedTitle: "คัดลอกรูปภาพไปยังคลิปบอร์ดแล้ว",
    shareCopiedMessage: "คัดลอกรูปภาพไปยังคลิปบอร์ดแล้ว ตอนนี้คุณสามารถวางรูปภาพลงในโพสต์ของคุณได้",
    openingNow: "กำลังเปิด",
    saveLastChanges: "บันทึกการเปลี่ยนแปลงล่าสุดลงในรูปก่อนดาวน์โหลดหรือไม่",
    yes: "ใช่",
    no: "ไม่",
    donate: "บริจาค",
    layer1: "เลเยอร์ 1",
    layer2: "เลเยอร์ 2",
    flipLayers: "สลับเลเยอร์",
    mergeLayers: "รวมเลเยอร์",
    uploadLayer: "อัปโหลดเลเยอร์",
    showLayer: "แสดงเลเยอร์",
    hideLayer: "ซ่อนเลเยอร์",
    topLayerSize: "ขนาดเลเยอร์บน",
    borderThickness: "ความหนาขอบ",
    borderColor: "สีขอบ",
    gradientBorder: "ขอบไล่สี",
    linearGradient: "เชิงเส้น",
    edgeGradient: "ตามขอบ",
    gradientAngle: "มุมไล่สี",
    gradientStart: "สีเริ่มต้น",
    gradientEnd: "สีสิ้นสุด",
    swapGradientColors: "สลับสีไล่ระดับ",
    moveCrop: "ย้ายพื้นที่ครอบตัด",
    moveLayer: "ย้ายเลเยอร์",
    resizeTop: "ปรับด้านบน",
    resizeRight: "ปรับด้านขวา",
    resizeBottom: "ปรับด้านล่าง",
    resizeLeft: "ปรับด้านซ้าย",
    resizeTopRight: "ปรับมุมขวาบน",
    resizeTopLeft: "ปรับมุมซ้ายบน",
    resizeBottomRight: "ปรับมุมขวาล่าง",
    resizeBottomLeft: "ปรับมุมซ้ายล่าง",
    freeSelect: "เลือกอิสระ",
    squareSelect: "เลือกสี่เหลี่ยม",
    widescreenSelect: "เลือก 16:9",
    circleSelect: "เลือกวงกลม",
    heartSelect: "เลือกรูปหัวใจ",
    constrainProportions: "คงสัดส่วน",
    freeform: "อิสระ",
    filterPreset: "ค่าล่วงหน้าฟิลเตอร์",
    none: "ไม่มี",
    bw: "ขาวดำ",
    sepia: "เซเปีย",
    oldFashioned: "สไตล์เก่า",
    vintage: "วินเทจ",
    greenify: "สีเขียว",
    redify: "สีแดง",
    bluify: "สีน้ำเงิน",
    purplify: "สีม่วง",
    brownify: "สีน้ำตาล",
    coolTone: "โทนเย็น",
    warmTone: "โทนอุ่น",
    cinematic: "ภาพยนตร์",
    matte: "แมตต์",
    cyanotype: "ไซยาโนไทป์",
    warmGlow: "แสงอบอุ่น",
    contrast: "คอนทราสต์",
    brightness: "ความสว่าง",
    colorBalance: "สมดุลสี",
    saturation: "ความอิ่มสี",
    sharpen: "เพิ่มความคม",
    soften: "ทำให้นุ่มลง",
    width: "ความกว้าง",
    height: "ความสูง",
    size: "ขนาด",
    topPosition: "ตำแหน่งบน",
    horizontal: "แนวนอน",
    textField: "ข้อความ",
    opacity: "ความทึบ",
    textMode: "ข้อความ",
    stampMode: "ตราประทับ",
    decorations: "ของตกแต่ง",
    color: "สี",
    apply: "ใช้",
    revert: "คืนค่า",
    textPlaceholder: "พิมพ์ข้อความที่นี่",
    live: "สด",
    ready: "พร้อม",
    shadowDarkness: "ความเข้มเงา",
    shadowSize: "ขนาดเงา",
    shadowColor: "สีเงา",
    shadowDirection: "ทิศทางเงา",
    resizeHelp: "โหมดอิสระจะปรับแต่ละมิติแยกกัน คงสัดส่วนจะล็อกให้สัมพันธ์กัน",
  },
} as unknown as Record<LanguageCode, Record<TranslationKey, string>>;

const translationFallbacks: Partial<Record<LanguageCode, LanguageCode>> = {
  ms: "id",
};

const socialPresetLabels = {
  en: ["Instagram Post", "Story / Reel", "TikTok / Douyin", "Pinterest Pin", "YouTube Thumbnail", "Facebook Feed", "Facebook Cover", "X Post", "X Header", "LinkedIn Cover", "Xiaohongshu Note", "WeChat Cover", "Weibo Feed"],
  es: ["Publicación de Instagram", "Historia / Reel", "TikTok / Douyin", "Pin de Pinterest", "Miniatura de YouTube", "Feed de Facebook", "Portada de Facebook", "Publicación en X", "Encabezado de X", "Portada de LinkedIn", "Nota de Xiaohongshu", "Portada de WeChat", "Feed de Weibo"],
  fr: ["Publication Instagram", "Story / Reel", "TikTok / Douyin", "Épingle Pinterest", "Miniature YouTube", "Fil Facebook", "Couverture Facebook", "Publication X", "En-tête X", "Couverture LinkedIn", "Note Xiaohongshu", "Couverture WeChat", "Fil Weibo"],
  de: ["Instagram-Beitrag", "Story / Reel", "TikTok / Douyin", "Pinterest-Pin", "YouTube-Vorschaubild", "Facebook-Feed", "Facebook-Titelbild", "X-Beitrag", "X-Kopfzeile", "LinkedIn-Titelbild", "Xiaohongshu-Notiz", "WeChat-Titelbild", "Weibo-Feed"],
  ja: ["Instagram投稿", "ストーリー / リール", "TikTok / Douyin", "Pinterestピン", "YouTubeサムネイル", "Facebookフィード", "Facebookカバー", "X投稿", "Xヘッダー", "LinkedInカバー", "小紅書ノート", "WeChatカバー", "Weiboフィード"],
  zh: ["Instagram 帖子", "快拍 / Reel", "TikTok / 抖音", "Pinterest Pin", "YouTube 缩略图", "Facebook 动态", "Facebook 封面", "X 帖子", "X 页眉", "LinkedIn 封面", "小红书笔记", "微信封面", "微博动态"],
  ko: ["인스타그램 게시물", "스토리 / 릴", "틱톡 / 더우인", "핀터레스트 핀", "유튜브 썸네일", "페이스북 피드", "페이스북 커버", "X 게시물", "X 헤더", "링크드인 커버", "샤오홍슈 노트", "위챗 커버", "웨이보 피드"],
  hi: ["Instagram पोस्ट", "स्टोरी / रील", "TikTok / Douyin", "Pinterest पिन", "YouTube थंबनेल", "Facebook फ़ीड", "Facebook कवर", "X पोस्ट", "X हेडर", "LinkedIn कवर", "Xiaohongshu नोट", "WeChat कवर", "Weibo फ़ीड"],
  pt: ["Publicação do Instagram", "Story / Reel", "TikTok / Douyin", "Pin do Pinterest", "Miniatura do YouTube", "Feed do Facebook", "Capa do Facebook", "Publicação no X", "Cabeçalho do X", "Capa do LinkedIn", "Nota do Xiaohongshu", "Capa do WeChat", "Feed do Weibo"],
  it: ["Post Instagram", "Storia / Reel", "TikTok / Douyin", "Pin Pinterest", "Miniatura YouTube", "Feed Facebook", "Copertina Facebook", "Post X", "Intestazione X", "Copertina LinkedIn", "Nota Xiaohongshu", "Copertina WeChat", "Feed Weibo"],
  ru: ["Публикация Instagram", "История / Reel", "TikTok / Douyin", "Пин Pinterest", "Миниатюра YouTube", "Лента Facebook", "Обложка Facebook", "Публикация X", "Шапка X", "Обложка LinkedIn", "Заметка Xiaohongshu", "Обложка WeChat", "Лента Weibo"],
  ar: ["منشور Instagram", "قصة / Reel", "TikTok / Douyin", "دبوس Pinterest", "صورة YouTube المصغرة", "موجز Facebook", "غلاف Facebook", "منشور X", "رأس X", "غلاف LinkedIn", "ملاحظة Xiaohongshu", "غلاف WeChat", "موجز Weibo"],
  nl: ["Instagram-bericht", "Story / Reel", "TikTok / Douyin", "Pinterest-pin", "YouTube-miniatuur", "Facebook-feed", "Facebook-omslag", "X-bericht", "X-kop", "LinkedIn-omslag", "Xiaohongshu-notitie", "WeChat-omslag", "Weibo-feed"],
  pl: ["Post na Instagramie", "Relacja / Reel", "TikTok / Douyin", "Pin Pinterest", "Miniatura YouTube", "Aktualności Facebook", "Okładka Facebook", "Post X", "Nagłówek X", "Okładka LinkedIn", "Notatka Xiaohongshu", "Okładka WeChat", "Aktualności Weibo"],
  tr: ["Instagram gönderisi", "Hikâye / Reel", "TikTok / Douyin", "Pinterest pini", "YouTube küçük resmi", "Facebook akışı", "Facebook kapağı", "X gönderisi", "X başlığı", "LinkedIn kapağı", "Xiaohongshu notu", "WeChat kapağı", "Weibo akışı"],
  id: ["Postingan Instagram", "Cerita / Reel", "TikTok / Douyin", "Pin Pinterest", "Thumbnail YouTube", "Umpan Facebook", "Sampul Facebook", "Postingan X", "Header X", "Sampul LinkedIn", "Catatan Xiaohongshu", "Sampul WeChat", "Umpan Weibo"],
  ms: ["Siaran Instagram", "Cerita / Reel", "TikTok / Douyin", "Pin Pinterest", "Imej kecil YouTube", "Suapan Facebook", "Kulit Facebook", "Siaran X", "Pengepala X", "Kulit LinkedIn", "Nota Xiaohongshu", "Kulit WeChat", "Suapan Weibo"],
  vi: ["Bài đăng Instagram", "Tin / Reel", "TikTok / Douyin", "Ghim Pinterest", "Hình thu nhỏ YouTube", "Bảng tin Facebook", "Ảnh bìa Facebook", "Bài đăng X", "Ảnh đầu trang X", "Ảnh bìa LinkedIn", "Ghi chú Xiaohongshu", "Ảnh bìa WeChat", "Bảng tin Weibo"],
  th: ["โพสต์ Instagram", "สตอรี่ / Reel", "TikTok / Douyin", "พิน Pinterest", "ภาพขนาดย่อ YouTube", "ฟีด Facebook", "ปก Facebook", "โพสต์ X", "ส่วนหัว X", "ปก LinkedIn", "โน้ต Xiaohongshu", "ปก WeChat", "ฟีด Weibo"],
} satisfies Record<LanguageCode, readonly [string, string, string, string, string, string, string, string, string, string, string, string, string]>;

const socialPresetLabelKeys = ["instagramPost", "storyReel", "tiktokDouyin", "pinterestPin", "youtubeThumbnail", "facebookFeed", "facebookCover", "xPost", "xHeader", "linkedinCover", "xiaohongshuNote", "wechatCover", "weiboFeed"] as const;

const translationFixes: Partial<Record<LanguageCode, Partial<Record<TranslationKey, string>>>> = {
  es: { shadow: "Sombra", colorReplace: "Reemplazar color", targetColor: "Color objetivo", replacementColor: "Color de reemplazo", strength: "Intensidad", smoothness: "Suavizado", transparent: "Transparente", transparency: "Transparencia", includeBorder: "Incluir borde", viewBackground: "Fondo del área de visualización", checkerboard: "Tablero" },
  fr: { colorReplace: "Remplacement de couleur", targetColor: "Couleur cible", replacementColor: "Couleur de remplacement", strength: "Intensité", smoothness: "Lissage", transparent: "Transparent", transparency: "Transparence", includeBorder: "Inclure la bordure", viewBackground: "Arrière-plan de la zone d’affichage", checkerboard: "Damier" },
  de: { shadow: "Schatten", colorReplace: "Farbe ersetzen", targetColor: "Zielfarbe", replacementColor: "Ersatzfarbe", strength: "Stärke", smoothness: "Glättung", transparent: "Transparent", transparency: "Transparenz", includeBorder: "Rahmen einbeziehen", viewBackground: "Hintergrund des Anzeigebereichs", checkerboard: "Schachbrett" },
  ja: { freeSelect: "自由選択", squareSelect: "正方形選択", widescreenSelect: "ワイド選択", circleSelect: "円形選択", freeform: "自由形式", filterPreset: "フィルタープリセット", none: "なし", oldFashioned: "オールドファッション", vintage: "ビンテージ", coolTone: "クールトーン", warmTone: "ウォームトーン", cinematic: "シネマティック", matte: "マット", cyanotype: "青写真", warmGlow: "暖かい輝き", textPlaceholder: "ここにテキストを入力", live: "ライブ", ready: "準備完了", bw: "白黒", sepia: "セピア", shadowDarkness: "影の濃さ", shadowSize: "影のサイズ", shadowColor: "影の色", shadowDirection: "影の方向", colorReplace: "色の置換", targetColor: "対象色", replacementColor: "置換色", strength: "強度", smoothness: "滑らかさ", transparent: "透明", transparency: "透明度", includeBorder: "枠線を含める", viewBackground: "表示エリアの背景", checkerboard: "市松模様" },
  zh: { intensity: "强度", dropShadowSize: "投影大小", dropShadowIntensity: "投影强度", dropShadowColor: "投影颜色", positionText: "放置文字", place: "放置", animals: "动物", labels: "标签", colorReplace: "颜色替换", targetColor: "目标颜色", replacementColor: "替换颜色", strength: "强度", smoothness: "平滑度", transparent: "透明", transparency: "透明度", includeBorder: "包含边框", viewBackground: "查看区域背景", checkerboard: "棋盘格" },
  ko: { colorReplace: "색상 바꾸기", targetColor: "대상 색상", replacementColor: "대체 색상", strength: "강도", smoothness: "부드러움", transparent: "투명", transparency: "투명도", includeBorder: "테두리 포함", viewBackground: "보기 영역 배경", checkerboard: "바둑판" },
  hi: { colorReplace: "रंग बदलें", targetColor: "लक्ष्य रंग", replacementColor: "प्रतिस्थापन रंग", strength: "तीव्रता", smoothness: "चिकनापन", transparent: "पारदर्शी", transparency: "पारदर्शिता", includeBorder: "बॉर्डर शामिल करें", viewBackground: "देखने के क्षेत्र की पृष्ठभूमि", checkerboard: "चेकरबोर्ड" },
  pt: { copyImage: "Copiar imagem", shareTo: "Compartilhar em", openWeibo: "Abrir Weibo", openWeChat: "Abrir WeChat", openDouyin: "Abrir Douyin", openXiaohongshu: "Abrir Xiaohongshu", openQQ: "Abrir QQ", colorReplace: "Substituir cor", targetColor: "Cor alvo", replacementColor: "Cor de substituição", strength: "Intensidade", smoothness: "Suavização", transparent: "Transparente", transparency: "Transparência", includeBorder: "Incluir borda", viewBackground: "Fundo da área de visualização", checkerboard: "Xadrez" },
  it: { copyImage: "Copia immagine", shareTo: "Condividi su", openWeibo: "Apri Weibo", openWeChat: "Apri WeChat", openDouyin: "Apri Douyin", openXiaohongshu: "Apri Xiaohongshu", openQQ: "Apri QQ", colorReplace: "Sostituisci colore", targetColor: "Colore di destinazione", replacementColor: "Colore sostitutivo", strength: "Intensità", smoothness: "Levigatura", transparent: "Trasparente", transparency: "Trasparenza", includeBorder: "Includi bordo", viewBackground: "Sfondo dell'area di visualizzazione", checkerboard: "Scacchiera" },
  ru: { copyImage: "Копировать изображение", shareTo: "Поделиться в", openWeibo: "Открыть Weibo", openWeChat: "Открыть WeChat", openDouyin: "Открыть Douyin", openXiaohongshu: "Открыть Xiaohongshu", openQQ: "Открыть QQ", colorReplace: "Замена цвета", targetColor: "Целевой цвет", replacementColor: "Цвет замены", strength: "Интенсивность", smoothness: "Сглаживание", transparent: "Прозрачный", transparency: "Прозрачность", includeBorder: "Включить рамку", viewBackground: "Фон области просмотра", checkerboard: "Шахматная сетка" },
  ar: { tapToLoadImage: "انقر هنا لتحميل صورة", intensity: "الشدة", dropShadowSize: "حجم ظل الإسقاط", dropShadowIntensity: "شدة ظل الإسقاط", dropShadowColor: "لون ظل الإسقاط", positionText: "وضع النص", place: "وضع", animals: "حيوانات", labels: "ملصقات", live: "مباشر", ready: "جاهز", bw: "أبيض وأسود", sepia: "بني داكن", copyImage: "نسخ الصورة", shareTo: "مشاركة عبر", openWeibo: "فتح Weibo", openWeChat: "فتح WeChat", openDouyin: "فتح Douyin", openXiaohongshu: "فتح Xiaohongshu", openQQ: "فتح QQ", shadowDarkness: "شدة الظل", shadowSize: "حجم الظل", shadowColor: "لون الظل", shadowDirection: "اتجاه الظل", colorReplace: "استبدال اللون", targetColor: "اللون المستهدف", replacementColor: "اللون البديل", strength: "القوة", smoothness: "التنعيم", transparent: "شفاف", transparency: "الشفافية", includeBorder: "تضمين الإطار", viewBackground: "خلفية منطقة العرض", checkerboard: "رقعة شطرنج" },
  nl: { copyImage: "Afbeelding kopiëren", shareTo: "Delen via", openWeibo: "Weibo openen", openWeChat: "WeChat openen", openDouyin: "Douyin openen", openXiaohongshu: "Xiaohongshu openen", openQQ: "QQ openen", colorReplace: "Kleur vervangen", targetColor: "Doelkleur", replacementColor: "Vervangingskleur", strength: "Sterkte", smoothness: "Verzachting", transparent: "Transparant", transparency: "Transparantie", includeBorder: "Rand meenemen", viewBackground: "Achtergrond weergavegebied", checkerboard: "Dambord" },
  pl: { copyImage: "Kopiuj obraz", shareTo: "Udostępnij w", openWeibo: "Otwórz Weibo", openWeChat: "Otwórz WeChat", openDouyin: "Otwórz Douyin", openXiaohongshu: "Otwórz Xiaohongshu", openQQ: "Otwórz QQ", colorReplace: "Zamiana koloru", targetColor: "Kolor docelowy", replacementColor: "Kolor zastępczy", strength: "Siła", smoothness: "Wygładzanie", transparent: "Przezroczysty", transparency: "Przezroczystość", includeBorder: "Uwzględnij obramowanie", viewBackground: "Tło obszaru podglądu", checkerboard: "Szachownica" },
  tr: { copyImage: "Görseli kopyala", shareTo: "Şurada paylaş", openWeibo: "Weibo'yu aç", openWeChat: "WeChat'i aç", openDouyin: "Douyin'i aç", openXiaohongshu: "Xiaohongshu'yu aç", openQQ: "QQ'yu aç", colorReplace: "Renk Değiştir", targetColor: "Hedef renk", replacementColor: "Yeni renk", strength: "Güç", smoothness: "Yumuşatma", transparent: "Şeffaf", transparency: "Saydamlık", includeBorder: "Kenarlığı dahil et", viewBackground: "Görüntüleme alanı arka planı", checkerboard: "Dama tahtası" },
  id: { copyImage: "Salin gambar", shareTo: "Bagikan ke", openWeibo: "Buka Weibo", openWeChat: "Buka WeChat", openDouyin: "Buka Douyin", openXiaohongshu: "Buka Xiaohongshu", openQQ: "Buka QQ", colorReplace: "Ganti Warna", targetColor: "Warna target", replacementColor: "Warna pengganti", strength: "Kekuatan", smoothness: "Penghalusan", transparent: "Transparan", transparency: "Transparansi", includeBorder: "Sertakan bingkai", viewBackground: "Latar area tampilan", checkerboard: "Papan catur" },
  ms: {
    quality: "Kualiti",
    transparentAreaColor: "Warna kawasan lutsinar",
    loadingImage: "Memuatkan imej",
    auto: "Automatik", manualOverview: "Gambaran keseluruhan", tutorialQuickTip: "Petua ringkas", tutorialLetsGetCutting: "Mari mula memangkas!", tutorialDragHandles: "Seret pemegang ini untuk memilih kawasan yang mahu dikekalkan.", tutorialMakeCut: "Pangkas imej!", tutorialClickScissors: "Sekarang klik gunting ini untuk memangkas imej anda.", tutorialKeepCreating: "Teruskan berkarya!", tutorialTryTools: "Cuba alat ini untuk menjadikan imej anda lebih menarik.", tutorialDontShowAgain: "Jangan tunjukkan lagi", showTutorial: "Tunjukkan tutorial", unappliedChangesWarning: "Imej anda mempunyai perubahan yang belum digunakan.", image: "Maklumat", corners: "Sudut bulat", keyboardShortcuts: "Pintasan papan kekunci", undo: "Buat asal", redo: "Buat semula", historyShortcut: "Sejarah", returnToPoint: "Kembali ke tahap suntingan ini", deleteThisStep: "Padam langkah ini", applyCurrentEdit: "Gunakan suntingan semasa", switchTools: "Tukar alat", closePanels: "Tutup panel", editHistory: "Sejarah suntingan", enterFullscreen: "Masuk skrin penuh", exitFullscreen: "Keluar skrin penuh", border: "Tambah bingkai", settings: "Tetapan", manualButton: "Panduan pengguna", crop: "Pangkas & topeng", cropShort: "Pangkas", maskShort: "Topeng", cropCommon: "Umum", cropSocial: "Sosial", socialTemplateSizeWarning: "Imej yang dimuat naik lebih kecil daripada saiz yang disyorkan untuk templat ini. Meneruskan boleh mengurangkan kualiti imej.", freeCut: "Pangkasan bebas", squareCut: "Pangkasan segi empat", widescreenCut: "Pangkasan 16:9", circleCut: "Pangkasan bulat", heartCut: "Pangkasan hati", starCut: "Pangkasan bintang", freeSelectLabel: "Pilihan bebas", roundedSelect: "Pilihan bulat", maskType: "Jenis topeng", dropShadowSize: "Saiz bayang", dropShadowIntensity: "Keamatan bayang", dropShadowColor: "Warna bayang", positionText: "Letakkan teks", place: "Letakkan", animals: "Haiwan", resize: "Ubah saiz & putar", resizeOnly: "Ubah saiz", rotate: "Putar", rotateFlip: "Putar & balik", rotateClockwise: "90° mengikut arah jam", rotateCounterClockwise: "90° melawan arah jam", flipVertical: "Balik menegak", flipHorizontal: "Balik mendatar", freeRotate: "Putaran bebas", filters: "Penapis", adjustments: "Pelarasan", text: "Teks & cap", export: "Eksport", chooseImage: "Pilih imej", replaceImage: "Ganti imej", editor: "Penyunting", noImageLoaded: "Tiada imej dimuatkan", useLightMode: "Gunakan mod cerah", useDarkMode: "Gunakan mod gelap", formats: "PNG, JPG, WebP atau GIF", dropImage: "Lepaskan imej untuk mula menyunting", tapToLoadImage: "Ketik di sini untuk memuatkan imej", localBrowserPrefix: "Data anda ialah", localBrowserYours: "milik anda", localBrowserSuffix: "dan tidak akan dimuat naik ke mana-mana pelayan, jadi privasi anda dilindungi 100%.", dropToBegin: "Lepaskan imej untuk bermula", seoTitle: "Cut My Pic - penyunting imej setempat", seoDescription: "Penyunting imej peribadi dalam pelayar untuk memangkas, membulatkan sudut, menambah bingkai, bayang, penapis, pelarasan, mengubah saiz, teks dan mengeksport.", closePanel: "Tutup panel", noFileSelected: "Tiada fail dipilih", source: "Sumber", dimensions: "Dimensi", uploadSize: "Saiz muat naik", currentSize: "Saiz semasa", cornerRadius: "Jejari sudut", center: "Tengah", full: "Penuh", square: "Segi empat", zoomLevel: "Tahap zum", pngExport: "Eksport PNG", downloadPng: "Muat turun", downloadNewImage: "Muat turun imej baharu", imageTrimmer: "Pemangkas imej", imageMasking: "Penopengan imej", maskMode: "Mod topeng", blur: "Kabur", overlayColor: "Warna tindihan", shareImage: "Kongsi imej", copyImage: "Salin imej", shareTo: "Kongsi ke", shareCopiedTitle: "Imej disalin ke papan keratan", shareCopiedMessage: "Imej telah disalin ke papan keratan. Kini anda boleh menampalnya ke dalam siaran anda.", openingNow: "Sedang membuka", saveLastChanges: "Simpan perubahan terakhir pada imej sebelum memuat turun?", yes: "Ya", no: "Tidak", donate: "Derma", layer1: "Lapisan 1", layer2: "Lapisan 2", flipLayers: "Balik lapisan", mergeLayers: "Gabungkan lapisan", uploadLayer: "Muat naik lapisan", showLayer: "Tunjukkan lapisan", hideLayer: "Sembunyikan lapisan", topLayerSize: "Saiz lapisan atas", borderThickness: "Ketebalan bingkai", borderColor: "Warna bingkai", gradientBorder: "Bingkai kecerunan", gradientAngle: "Sudut kecerunan", gradientStart: "Permulaan kecerunan", gradientEnd: "Penghujung kecerunan", swapGradientColors: "Tukar warna kecerunan", moveCrop: "Alihkan pangkasan", moveLayer: "Alihkan lapisan", resizeTop: "Ubah saiz bahagian atas", resizeRight: "Ubah saiz bahagian kanan", resizeBottom: "Ubah saiz bahagian bawah", resizeLeft: "Ubah saiz bahagian kiri", resizeTopRight: "Ubah saiz kanan atas", resizeTopLeft: "Ubah saiz kiri atas", resizeBottomRight: "Ubah saiz kanan bawah", resizeBottomLeft: "Ubah saiz kiri bawah", freeSelect: "Pilihan bebas", squareSelect: "Pilihan segi empat", widescreenSelect: "Pilihan 16:9", circleSelect: "Pilihan bulat", heartSelect: "Pilihan hati", constrainProportions: "Kekalkan nisbah", freeform: "Bentuk bebas", filterPreset: "Pratetap penapis", none: "Tiada", bw: "Hitam putih", oldFashioned: "Gaya lama", greenify: "Hijau", redify: "Merah", bluify: "Biru", purplify: "Ungu", brownify: "Coklat", coolTone: "Nada dingin", warmTone: "Nada hangat", warmGlow: "Cahaya hangat", brightness: "Kecerahan", colorBalance: "Keseimbangan warna", saturation: "Ketepuan", sharpen: "Tajamkan", soften: "Lembutkan", width: "Lebar", height: "Tinggi", size: "Saiz", topPosition: "Kedudukan atas", horizontal: "Mendatar", opacity: "Kelegapan", stampMode: "Cap", decorations: "Hiasan", apply: "Gunakan", revert: "Kembalikan", textPlaceholder: "Teks anda di sini", live: "Langsung", ready: "Sedia", shadowDarkness: "Kegelapan bayang", shadowSize: "Saiz bayang", shadowColor: "Warna bayang", shadowDirection: "Arah bayang", resizeHelp: "Mod bebas mengubah setiap dimensi secara berasingan. Kekalkan nisbah mengunci kedua-duanya bersama.", colorReplace: "Ganti warna", targetColor: "Warna sasaran", replacementColor: "Warna gantian", strength: "Kekuatan", smoothness: "Pelicinan", transparent: "Lutsinar", transparency: "Ketelusan", includeBorder: "Sertakan bingkai", viewBackground: "Latar kawasan paparan", checkerboard: "Papan dam", openWeibo: "Buka Weibo", openWeChat: "Buka WeChat", openDouyin: "Buka Douyin", openXiaohongshu: "Buka Xiaohongshu", openQQ: "Buka QQ", shadow: "Bayang", zoom: "Zum", tools: "Alat"
  },
  vi: { copyImage: "Sao chép ảnh", shareTo: "Chia sẻ lên", openWeibo: "Mở Weibo", openWeChat: "Mở WeChat", openDouyin: "Mở Douyin", openXiaohongshu: "Mở Xiaohongshu", openQQ: "Mở QQ", colorReplace: "Thay màu", targetColor: "Màu mục tiêu", replacementColor: "Màu thay thế", strength: "Cường độ", smoothness: "Làm mịn", transparent: "Trong suốt", transparency: "Độ trong suốt", includeBorder: "Bao gồm viền", viewBackground: "Nền vùng xem", checkerboard: "Bàn cờ" },
  th: { copyImage: "คัดลอกรูปภาพ", shareTo: "แชร์ไปยัง", openWeibo: "เปิด Weibo", openWeChat: "เปิด WeChat", openDouyin: "เปิด Douyin", openXiaohongshu: "เปิด Xiaohongshu", openQQ: "เปิด QQ", colorReplace: "แทนที่สี", targetColor: "สีเป้าหมาย", replacementColor: "สีแทนที่", strength: "ความแรง", smoothness: "ความเรียบเนียน", transparent: "โปร่งใส", transparency: "ความโปร่งใส", includeBorder: "รวมเส้นขอบ", viewBackground: "พื้นหลังพื้นที่แสดงผล", checkerboard: "ลายตารางหมากรุก" },
};

const unsupportedFileTypeCopy: Record<LanguageCode, string> = {
  en: "Unsupported file type. Please use PNG, JPG, WebP, or GIF.",
  es: "Tipo de archivo no compatible. Usa PNG, JPG, WebP o GIF.",
  fr: "Type de fichier non pris en charge. Utilisez PNG, JPG, WebP ou GIF.",
  de: "Nicht unterstützter Dateityp. Bitte PNG, JPG, WebP oder GIF verwenden.",
  ja: "サポートされていないファイル形式です。PNG、JPG、WebP、GIFを使用してください。",
  zh: "不支持此文件类型。请使用 PNG、JPG、WebP 或 GIF。",
  ko: "지원되지 않는 파일 형식입니다. PNG, JPG, WebP 또는 GIF를 사용하세요.",
  hi: "असमर्थित फ़ाइल प्रकार। कृपया PNG, JPG, WebP या GIF का उपयोग करें।",
  pt: "Tipo de arquivo não compatível. Use PNG, JPG, WebP ou GIF.",
  it: "Tipo di file non supportato. Usa PNG, JPG, WebP o GIF.",
  ru: "Неподдерживаемый тип файла. Используйте PNG, JPG, WebP или GIF.",
  ar: "نوع الملف غير مدعوم. استخدم PNG أو JPG أو WebP أو GIF.",
  nl: "Niet-ondersteund bestandstype. Gebruik PNG, JPG, WebP of GIF.",
  pl: "Nieobsługiwany typ pliku. Użyj PNG, JPG, WebP lub GIF.",
  tr: "Desteklenmeyen dosya türü. PNG, JPG, WebP veya GIF kullanın.",
  id: "Jenis file tidak didukung. Gunakan PNG, JPG, WebP, atau GIF.",
  ms: "Jenis fail tidak disokong. Sila gunakan PNG, JPG, WebP atau GIF.",
  vi: "Loại tệp không được hỗ trợ. Hãy dùng PNG, JPG, WebP hoặc GIF.",
  th: "ไม่รองรับประเภทไฟล์นี้ โปรดใช้ PNG, JPG, WebP หรือ GIF",
};

const clearImageCopy: Record<LanguageCode, string> = {
  en: "Clear image and history",
  es: "Borrar imagen e historial",
  fr: "Effacer l’image et l’historique",
  de: "Bild und Verlauf löschen",
  ja: "画像と履歴を消去",
  zh: "清除图片和历史记录",
  ko: "이미지 및 기록 지우기",
  hi: "छवि और इतिहास साफ़ करें",
  pt: "Limpar imagem e histórico",
  it: "Cancella immagine e cronologia",
  ru: "Очистить изображение и историю",
  ar: "مسح الصورة والسجل",
  nl: "Afbeelding en geschiedenis wissen",
  pl: "Wyczyść obraz i historię",
  tr: "Görseli ve geçmişi temizle",
  id: "Hapus gambar dan riwayat",
  ms: "Kosongkan imej dan sejarah",
  vi: "Xóa ảnh và lịch sử",
  th: "ล้างรูปภาพและประวัติ",
};

type SaveToolCopy = {
  save: string;
  install: string;
  title: string;
  intro: string;
  desktop: string;
  iosSafari: string;
  iosOther: string;
  android: string;
  postTitle: string;
  postBody: string;
  notNow: string;
};

const saveToolCopy: Record<LanguageCode, SaveToolCopy> = {
  en: { save: "Save CutMyPic", install: "Install CutMyPic", title: "Save CutMyPic for later", intro: "Keep CutMyPic within easy reach for your next image.", desktop: "Press {shortcut} to bookmark this page, or use the bookmark menu in Safari, Chrome, or Firefox.", iosSafari: "In Safari, tap Share, then Add to Home Screen.", iosOther: "Open this page in Safari to add it to your Home Screen.", android: "Open your browser menu and choose Install app or Add to Home screen.", postTitle: "Use CutMyPic again?", postBody: "Save this tool for quick access the next time you edit an image.", notNow: "Not now" },
  es: { save: "Guardar CutMyPic", install: "Instalar CutMyPic", title: "Guarda CutMyPic para después", intro: "Ten CutMyPic a mano para tu próxima imagen.", desktop: "Pulsa {shortcut} para guardar esta página en marcadores o usa el menú de marcadores de Safari, Chrome o Firefox.", iosSafari: "En Safari, toca Compartir y luego Añadir a pantalla de inicio.", iosOther: "Abre esta página en Safari para añadirla a la pantalla de inicio.", android: "Abre el menú del navegador y elige Instalar aplicación o Añadir a pantalla de inicio.", postTitle: "¿Volverás a usar CutMyPic?", postBody: "Guarda esta herramienta para acceder rápidamente la próxima vez que edites una imagen.", notNow: "Ahora no" },
  fr: { save: "Enregistrer CutMyPic", install: "Installer CutMyPic", title: "Gardez CutMyPic pour plus tard", intro: "Gardez CutMyPic à portée de main pour votre prochaine image.", desktop: "Appuyez sur {shortcut} pour ajouter cette page aux favoris, ou utilisez le menu des favoris de Safari, Chrome ou Firefox.", iosSafari: "Dans Safari, touchez Partager, puis Sur l’écran d’accueil.", iosOther: "Ouvrez cette page dans Safari pour l’ajouter à l’écran d’accueil.", android: "Ouvrez le menu du navigateur et choisissez Installer l’application ou Ajouter à l’écran d’accueil.", postTitle: "Réutiliser CutMyPic ?", postBody: "Enregistrez cet outil pour y accéder rapidement lors de votre prochaine retouche.", notNow: "Pas maintenant" },
  de: { save: "CutMyPic speichern", install: "CutMyPic installieren", title: "CutMyPic für später speichern", intro: "Halten Sie CutMyPic für Ihr nächstes Bild griffbereit.", desktop: "Drücken Sie {shortcut}, um diese Seite als Lesezeichen zu speichern, oder verwenden Sie das Lesezeichenmenü in Safari, Chrome oder Firefox.", iosSafari: "Tippen Sie in Safari auf Teilen und dann auf Zum Home-Bildschirm.", iosOther: "Öffnen Sie diese Seite in Safari, um sie zum Home-Bildschirm hinzuzufügen.", android: "Öffnen Sie das Browsermenü und wählen Sie App installieren oder Zum Startbildschirm hinzufügen.", postTitle: "CutMyPic erneut verwenden?", postBody: "Speichern Sie dieses Tool für schnellen Zugriff bei Ihrer nächsten Bildbearbeitung.", notNow: "Nicht jetzt" },
  ja: { save: "CutMyPicを保存", install: "CutMyPicをインストール", title: "CutMyPicを保存して後で使う", intro: "次の画像編集ですぐ使えるようにCutMyPicを保存できます。", desktop: "{shortcut}を押してこのページをブックマークするか、Safari、Chrome、Firefoxのブックマークメニューを使用してください。", iosSafari: "Safariで共有をタップし、「ホーム画面に追加」を選びます。", iosOther: "このページをSafariで開くとホーム画面に追加できます。", android: "ブラウザメニューを開き、「アプリをインストール」または「ホーム画面に追加」を選びます。", postTitle: "CutMyPicをまた使いますか？", postBody: "次回すぐに画像を編集できるよう、このツールを保存しましょう。", notNow: "今はしない" },
  zh: { save: "保存 CutMyPic", install: "安装 CutMyPic", title: "保存 CutMyPic 以便以后使用", intro: "保存 CutMyPic，方便下次编辑图片时快速打开。", desktop: "按 {shortcut} 将此页面加入书签，或使用 Safari、Chrome 或 Firefox 的书签菜单。", iosSafari: "在 Safari 中轻点“共享”，然后选择“添加到主屏幕”。", iosOther: "请在 Safari 中打开此页面，将其添加到主屏幕。", android: "打开浏览器菜单，然后选择“安装应用”或“添加到主屏幕”。", postTitle: "还会使用 CutMyPic？", postBody: "保存此工具，以便下次编辑图片时快速访问。", notNow: "暂不" },
  ko: { save: "CutMyPic 저장", install: "CutMyPic 설치", title: "나중을 위해 CutMyPic 저장", intro: "다음 이미지 편집 때 바로 열 수 있도록 CutMyPic을 저장하세요.", desktop: "{shortcut}을 눌러 이 페이지를 북마크하거나 Safari, Chrome 또는 Firefox의 북마크 메뉴를 사용하세요.", iosSafari: "Safari에서 공유를 탭한 다음 홈 화면에 추가를 선택하세요.", iosOther: "홈 화면에 추가하려면 이 페이지를 Safari에서 여세요.", android: "브라우저 메뉴를 열고 앱 설치 또는 홈 화면에 추가를 선택하세요.", postTitle: "CutMyPic을 다시 사용하시겠어요?", postBody: "다음 이미지 편집 때 빠르게 이용할 수 있도록 이 도구를 저장하세요.", notNow: "나중에" },
  hi: { save: "CutMyPic सहेजें", install: "CutMyPic इंस्टॉल करें", title: "CutMyPic को बाद के लिए सहेजें", intro: "अपनी अगली छवि के लिए CutMyPic को आसानी से उपलब्ध रखें।", desktop: "इस पेज को बुकमार्क करने के लिए {shortcut} दबाएँ, या Safari, Chrome अथवा Firefox का बुकमार्क मेनू उपयोग करें।", iosSafari: "Safari में शेयर टैप करें, फिर होम स्क्रीन पर जोड़ें चुनें।", iosOther: "इसे होम स्क्रीन पर जोड़ने के लिए यह पेज Safari में खोलें।", android: "ब्राउज़र मेनू खोलें और ऐप इंस्टॉल करें या होम स्क्रीन पर जोड़ें चुनें।", postTitle: "CutMyPic फिर इस्तेमाल करेंगे?", postBody: "अगली बार छवि संपादित करते समय तेज़ पहुँच के लिए इस टूल को सहेजें।", notNow: "अभी नहीं" },
  pt: { save: "Salvar CutMyPic", install: "Instalar CutMyPic", title: "Salve o CutMyPic para depois", intro: "Mantenha o CutMyPic por perto para editar sua próxima imagem.", desktop: "Pressione {shortcut} para adicionar esta página aos favoritos ou use o menu de favoritos do Safari, Chrome ou Firefox.", iosSafari: "No Safari, toque em Compartilhar e depois em Adicionar à Tela de Início.", iosOther: "Abra esta página no Safari para adicioná-la à Tela de Início.", android: "Abra o menu do navegador e escolha Instalar app ou Adicionar à tela inicial.", postTitle: "Vai usar o CutMyPic novamente?", postBody: "Salve esta ferramenta para acesso rápido na próxima edição de imagem.", notNow: "Agora não" },
  it: { save: "Salva CutMyPic", install: "Installa CutMyPic", title: "Salva CutMyPic per dopo", intro: "Tieni CutMyPic a portata di mano per la prossima immagine.", desktop: "Premi {shortcut} per aggiungere questa pagina ai preferiti oppure usa il menu dei preferiti di Safari, Chrome o Firefox.", iosSafari: "In Safari, tocca Condividi e poi Aggiungi alla schermata Home.", iosOther: "Apri questa pagina in Safari per aggiungerla alla schermata Home.", android: "Apri il menu del browser e scegli Installa app o Aggiungi a schermata Home.", postTitle: "Userai di nuovo CutMyPic?", postBody: "Salva questo strumento per accedervi rapidamente alla prossima modifica.", notNow: "Non ora" },
  ru: { save: "Сохранить CutMyPic", install: "Установить CutMyPic", title: "Сохраните CutMyPic на будущее", intro: "Держите CutMyPic под рукой для следующего изображения.", desktop: "Нажмите {shortcut}, чтобы добавить страницу в закладки, или используйте меню закладок Safari, Chrome или Firefox.", iosSafari: "В Safari нажмите «Поделиться», затем «На экран Домой».", iosOther: "Откройте эту страницу в Safari, чтобы добавить её на экран Домой.", android: "Откройте меню браузера и выберите «Установить приложение» или «Добавить на главный экран».", postTitle: "Будете использовать CutMyPic снова?", postBody: "Сохраните этот инструмент для быстрого доступа при следующем редактировании.", notNow: "Не сейчас" },
  ar: { save: "حفظ CutMyPic", install: "تثبيت CutMyPic", title: "احفظ CutMyPic لاستخدامه لاحقًا", intro: "احتفظ بـ CutMyPic في متناولك لصورتك التالية.", desktop: "اضغط {shortcut} لإضافة هذه الصفحة إلى الإشارات المرجعية، أو استخدم قائمة الإشارات في Safari أو Chrome أو Firefox.", iosSafari: "في Safari، اضغط مشاركة ثم إضافة إلى الشاشة الرئيسية.", iosOther: "افتح هذه الصفحة في Safari لإضافتها إلى الشاشة الرئيسية.", android: "افتح قائمة المتصفح واختر تثبيت التطبيق أو الإضافة إلى الشاشة الرئيسية.", postTitle: "هل ستستخدم CutMyPic مرة أخرى؟", postBody: "احفظ هذه الأداة للوصول السريع عند تعديل صورتك التالية.", notNow: "ليس الآن" },
  nl: { save: "CutMyPic bewaren", install: "CutMyPic installeren", title: "Bewaar CutMyPic voor later", intro: "Houd CutMyPic binnen handbereik voor je volgende afbeelding.", desktop: "Druk op {shortcut} om deze pagina als bladwijzer op te slaan, of gebruik het bladwijzermenu van Safari, Chrome of Firefox.", iosSafari: "Tik in Safari op Deel en daarna op Zet op beginscherm.", iosOther: "Open deze pagina in Safari om haar aan het beginscherm toe te voegen.", android: "Open het browsermenu en kies App installeren of Toevoegen aan startscherm.", postTitle: "CutMyPic opnieuw gebruiken?", postBody: "Bewaar deze tool voor snelle toegang bij je volgende beeldbewerking.", notNow: "Niet nu" },
  pl: { save: "Zapisz CutMyPic", install: "Zainstaluj CutMyPic", title: "Zapisz CutMyPic na później", intro: "Miej CutMyPic pod ręką podczas edycji kolejnego obrazu.", desktop: "Naciśnij {shortcut}, aby dodać tę stronę do zakładek, lub użyj menu zakładek w Safari, Chrome albo Firefox.", iosSafari: "W Safari stuknij Udostępnij, a następnie Do ekranu początkowego.", iosOther: "Otwórz tę stronę w Safari, aby dodać ją do ekranu początkowego.", android: "Otwórz menu przeglądarki i wybierz Zainstaluj aplikację lub Dodaj do ekranu głównego.", postTitle: "Użyjesz CutMyPic ponownie?", postBody: "Zapisz to narzędzie, aby szybko wrócić do niego przy kolejnej edycji.", notNow: "Nie teraz" },
  tr: { save: "CutMyPic’i kaydet", install: "CutMyPic’i yükle", title: "CutMyPic’i daha sonrası için kaydet", intro: "Bir sonraki görseliniz için CutMyPic elinizin altında olsun.", desktop: "Bu sayfayı yer imlerine eklemek için {shortcut} tuşlarına basın veya Safari, Chrome ya da Firefox’un yer imi menüsünü kullanın.", iosSafari: "Safari’de Paylaş’a, ardından Ana Ekrana Ekle’ye dokunun.", iosOther: "Ana ekranınıza eklemek için bu sayfayı Safari’de açın.", android: "Tarayıcı menüsünü açıp Uygulamayı yükle veya Ana ekrana ekle seçeneğini seçin.", postTitle: "CutMyPic’i tekrar kullanacak mısınız?", postBody: "Bir sonraki görsel düzenlemenizde hızlı erişim için bu aracı kaydedin.", notNow: "Şimdi değil" },
  id: { save: "Simpan CutMyPic", install: "Instal CutMyPic", title: "Simpan CutMyPic untuk nanti", intro: "Simpan CutMyPic agar mudah dijangkau untuk gambar berikutnya.", desktop: "Tekan {shortcut} untuk memarkahi halaman ini, atau gunakan menu markah di Safari, Chrome, atau Firefox.", iosSafari: "Di Safari, ketuk Bagikan, lalu Tambahkan ke Layar Utama.", iosOther: "Buka halaman ini di Safari untuk menambahkannya ke Layar Utama.", android: "Buka menu browser lalu pilih Instal aplikasi atau Tambahkan ke layar utama.", postTitle: "Akan memakai CutMyPic lagi?", postBody: "Simpan alat ini agar mudah dibuka saat Anda mengedit gambar berikutnya.", notNow: "Nanti saja" },
  ms: { save: "Simpan CutMyPic", install: "Pasang CutMyPic", title: "Simpan CutMyPic untuk kemudian", intro: "Pastikan CutMyPic mudah dicapai untuk imej anda yang seterusnya.", desktop: "Tekan {shortcut} untuk menanda halaman ini, atau gunakan menu penanda halaman Safari, Chrome atau Firefox.", iosSafari: "Dalam Safari, ketik Kongsi, kemudian Tambah ke Skrin Utama.", iosOther: "Buka halaman ini dalam Safari untuk menambahkannya ke Skrin Utama.", android: "Buka menu pelayar dan pilih Pasang aplikasi atau Tambah ke skrin utama.", postTitle: "Akan gunakan CutMyPic lagi?", postBody: "Simpan alat ini untuk akses pantas apabila anda menyunting imej seterusnya.", notNow: "Bukan sekarang" },
  vi: { save: "Lưu CutMyPic", install: "Cài đặt CutMyPic", title: "Lưu CutMyPic để dùng sau", intro: "Giữ CutMyPic trong tầm tay cho ảnh tiếp theo của bạn.", desktop: "Nhấn {shortcut} để đánh dấu trang này hoặc dùng menu dấu trang trong Safari, Chrome hay Firefox.", iosSafari: "Trong Safari, chạm Chia sẻ rồi chọn Thêm vào Màn hình chính.", iosOther: "Mở trang này trong Safari để thêm vào Màn hình chính.", android: "Mở menu trình duyệt rồi chọn Cài đặt ứng dụng hoặc Thêm vào màn hình chính.", postTitle: "Bạn sẽ dùng lại CutMyPic?", postBody: "Lưu công cụ này để truy cập nhanh khi chỉnh sửa ảnh lần tới.", notNow: "Để sau" },
  th: { save: "บันทึก CutMyPic", install: "ติดตั้ง CutMyPic", title: "บันทึก CutMyPic ไว้ใช้ภายหลัง", intro: "เก็บ CutMyPic ไว้ใกล้มือสำหรับรูปภาพถัดไปของคุณ", desktop: "กด {shortcut} เพื่อบุ๊กมาร์กหน้านี้ หรือใช้เมนูบุ๊กมาร์กใน Safari, Chrome หรือ Firefox", iosSafari: "ใน Safari ให้แตะแชร์ แล้วเลือกเพิ่มไปยังหน้าจอโฮม", iosOther: "เปิดหน้านี้ใน Safari เพื่อเพิ่มไปยังหน้าจอโฮม", android: "เปิดเมนูเบราว์เซอร์ แล้วเลือกติดตั้งแอปหรือเพิ่มไปยังหน้าจอหลัก", postTitle: "จะใช้ CutMyPic อีกไหม?", postBody: "บันทึกเครื่องมือนี้เพื่อเข้าถึงได้อย่างรวดเร็วเมื่อแก้ไขรูปภาพครั้งถัดไป", notNow: "ไว้ทีหลัง" },
};

const workspaceHeaderCopy: Record<LanguageCode, { currentSize: string; selectedSize: string; zoomLevel: string }> = {
  en: { currentSize: "Current Size", selectedSize: "Selected Size", zoomLevel: "Zoom Level" },
  es: { currentSize: "Tamaño actual", selectedSize: "Tamaño seleccionado", zoomLevel: "Nivel de zoom" },
  fr: { currentSize: "Taille actuelle", selectedSize: "Taille sélectionnée", zoomLevel: "Niveau de zoom" },
  de: { currentSize: "Aktuelle Größe", selectedSize: "Ausgewählte Größe", zoomLevel: "Zoomstufe" },
  ja: { currentSize: "現在のサイズ", selectedSize: "選択サイズ", zoomLevel: "ズームレベル" },
  zh: { currentSize: "当前尺寸", selectedSize: "所选尺寸", zoomLevel: "缩放级别" },
  ko: { currentSize: "현재 크기", selectedSize: "선택 크기", zoomLevel: "확대/축소 수준" },
  hi: { currentSize: "वर्तमान आकार", selectedSize: "चयनित आकार", zoomLevel: "ज़ूम स्तर" },
  pt: { currentSize: "Tamanho atual", selectedSize: "Tamanho selecionado", zoomLevel: "Nível de zoom" },
  it: { currentSize: "Dimensione attuale", selectedSize: "Dimensione selezionata", zoomLevel: "Livello di zoom" },
  ru: { currentSize: "Текущий размер", selectedSize: "Выбранный размер", zoomLevel: "Уровень масштаба" },
  ar: { currentSize: "الحجم الحالي", selectedSize: "الحجم المحدد", zoomLevel: "مستوى التكبير" },
  nl: { currentSize: "Huidige grootte", selectedSize: "Geselecteerde grootte", zoomLevel: "Zoomniveau" },
  pl: { currentSize: "Bieżący rozmiar", selectedSize: "Wybrany rozmiar", zoomLevel: "Poziom powiększenia" },
  tr: { currentSize: "Geçerli boyut", selectedSize: "Seçili boyut", zoomLevel: "Yakınlaştırma düzeyi" },
  id: { currentSize: "Ukuran saat ini", selectedSize: "Ukuran pilihan", zoomLevel: "Tingkat zoom" },
  ms: { currentSize: "Saiz semasa", selectedSize: "Saiz dipilih", zoomLevel: "Tahap zum" },
  vi: { currentSize: "Kích thước hiện tại", selectedSize: "Kích thước đã chọn", zoomLevel: "Mức thu phóng" },
  th: { currentSize: "ขนาดปัจจุบัน", selectedSize: "ขนาดที่เลือก", zoomLevel: "ระดับการซูม" },
};

type ShareSiteCopy = { label: string; message: string; copied: string };

const shareSiteCopy: Record<LanguageCode, ShareSiteCopy> = {
  en: { label: "Share CutMyPic", message: "Try CutMyPic, a private image editor that works in your browser.", copied: "CutMyPic link copied" },
  es: { label: "Compartir CutMyPic", message: "Prueba CutMyPic, un editor de imágenes privado que funciona en tu navegador.", copied: "Enlace de CutMyPic copiado" },
  fr: { label: "Partager CutMyPic", message: "Essayez CutMyPic, un éditeur d’images privé qui fonctionne dans votre navigateur.", copied: "Lien CutMyPic copié" },
  de: { label: "CutMyPic teilen", message: "Probiere CutMyPic aus, einen privaten Bildeditor, der direkt im Browser funktioniert.", copied: "CutMyPic-Link kopiert" },
  ja: { label: "CutMyPicを共有", message: "ブラウザで使えるプライベート画像編集ツール、CutMyPicをお試しください。", copied: "CutMyPicのリンクをコピーしました" },
  zh: { label: "分享 CutMyPic", message: "试试 CutMyPic，一款可在浏览器中使用的私密图片编辑器。", copied: "已复制 CutMyPic 链接" },
  ko: { label: "CutMyPic 공유", message: "브라우저에서 작동하는 개인정보 보호 이미지 편집기 CutMyPic을 사용해 보세요.", copied: "CutMyPic 링크를 복사했습니다" },
  hi: { label: "CutMyPic साझा करें", message: "CutMyPic आज़माएँ—एक निजी छवि संपादक जो आपके ब्राउज़र में चलता है।", copied: "CutMyPic लिंक कॉपी हो गया" },
  pt: { label: "Compartilhar CutMyPic", message: "Experimente o CutMyPic, um editor de imagens privado que funciona no navegador.", copied: "Link do CutMyPic copiado" },
  it: { label: "Condividi CutMyPic", message: "Prova CutMyPic, un editor di immagini privato che funziona nel browser.", copied: "Link di CutMyPic copiato" },
  ru: { label: "Поделиться CutMyPic", message: "Попробуйте CutMyPic — приватный редактор изображений, работающий в браузере.", copied: "Ссылка на CutMyPic скопирована" },
  ar: { label: "مشاركة CutMyPic", message: "جرّب CutMyPic، محرر صور خاص يعمل في متصفحك.", copied: "تم نسخ رابط CutMyPic" },
  nl: { label: "CutMyPic delen", message: "Probeer CutMyPic, een privé-afbeeldingseditor die in je browser werkt.", copied: "CutMyPic-link gekopieerd" },
  pl: { label: "Udostępnij CutMyPic", message: "Wypróbuj CutMyPic — prywatny edytor obrazów działający w przeglądarce.", copied: "Skopiowano link do CutMyPic" },
  tr: { label: "CutMyPic’i paylaş", message: "Tarayıcınızda çalışan özel görsel düzenleyici CutMyPic’i deneyin.", copied: "CutMyPic bağlantısı kopyalandı" },
  id: { label: "Bagikan CutMyPic", message: "Coba CutMyPic, editor gambar privat yang bekerja di browser Anda.", copied: "Tautan CutMyPic disalin" },
  ms: { label: "Kongsi CutMyPic", message: "Cuba CutMyPic, penyunting imej peribadi yang berfungsi dalam pelayar anda.", copied: "Pautan CutMyPic disalin" },
  vi: { label: "Chia sẻ CutMyPic", message: "Hãy thử CutMyPic, trình chỉnh sửa ảnh riêng tư hoạt động trong trình duyệt.", copied: "Đã sao chép liên kết CutMyPic" },
  th: { label: "แชร์ CutMyPic", message: "ลองใช้ CutMyPic โปรแกรมแก้ไขรูปภาพแบบส่วนตัวที่ทำงานในเบราว์เซอร์", copied: "คัดลอกลิงก์ CutMyPic แล้ว" },
};

const supportShareLabelByLanguage: Record<LanguageCode, string> = {
  en: "Support & Share",
  es: "Apoyar y compartir",
  fr: "Soutenir et partager",
  de: "Unterstützen & teilen",
  ja: "サポートと共有",
  zh: "支持与分享",
  ko: "후원 및 공유",
  hi: "सहयोग और साझा करें",
  pt: "Apoie e compartilhe",
  it: "Sostieni e condividi",
  ru: "Поддержка и обмен",
  ar: "الدعم والمشاركة",
  nl: "Steunen en delen",
  pl: "Wsparcie i udostępnianie",
  tr: "Destekle ve paylaş",
  id: "Dukung & bagikan",
  ms: "Sokong & kongsi",
  vi: "Hỗ trợ & chia sẻ",
  th: "สนับสนุนและแชร์",
};

const workspacePositionCopy: Record<LanguageCode, { autoCenterOn: string; autoCenterOff: string; moveImage: string }> = {
  en: { autoCenterOn: "Auto-center after edits: On", autoCenterOff: "Auto-center after edits: Off", moveImage: "Move image" },
  es: { autoCenterOn: "Centrar automáticamente tras editar: Activado", autoCenterOff: "Centrar automáticamente tras editar: Desactivado", moveImage: "Mover imagen" },
  fr: { autoCenterOn: "Recentrage automatique après modification : activé", autoCenterOff: "Recentrage automatique après modification : désactivé", moveImage: "Déplacer l’image" },
  de: { autoCenterOn: "Nach Bearbeitungen automatisch zentrieren: Ein", autoCenterOff: "Nach Bearbeitungen automatisch zentrieren: Aus", moveImage: "Bild verschieben" },
  ja: { autoCenterOn: "編集後の自動中央揃え：オン", autoCenterOff: "編集後の自動中央揃え：オフ", moveImage: "画像を移動" },
  zh: { autoCenterOn: "编辑后自动居中：开启", autoCenterOff: "编辑后自动居中：关闭", moveImage: "移动图片" },
  ko: { autoCenterOn: "편집 후 자동 가운데 정렬: 켬", autoCenterOff: "편집 후 자동 가운데 정렬: 끔", moveImage: "이미지 이동" },
  hi: { autoCenterOn: "संपादन के बाद अपने-आप केंद्रित करें: चालू", autoCenterOff: "संपादन के बाद अपने-आप केंद्रित करें: बंद", moveImage: "छवि खिसकाएँ" },
  pt: { autoCenterOn: "Centralizar automaticamente após editar: Ativado", autoCenterOff: "Centralizar automaticamente após editar: Desativado", moveImage: "Mover imagem" },
  it: { autoCenterOn: "Centratura automatica dopo le modifiche: attiva", autoCenterOff: "Centratura automatica dopo le modifiche: disattiva", moveImage: "Sposta immagine" },
  ru: { autoCenterOn: "Автоцентрирование после правок: вкл.", autoCenterOff: "Автоцентрирование после правок: выкл.", moveImage: "Переместить изображение" },
  ar: { autoCenterOn: "التوسيط التلقائي بعد التعديلات: مفعّل", autoCenterOff: "التوسيط التلقائي بعد التعديلات: متوقف", moveImage: "تحريك الصورة" },
  nl: { autoCenterOn: "Automatisch centreren na bewerkingen: Aan", autoCenterOff: "Automatisch centreren na bewerkingen: Uit", moveImage: "Afbeelding verplaatsen" },
  pl: { autoCenterOn: "Automatyczne centrowanie po edycji: Włączone", autoCenterOff: "Automatyczne centrowanie po edycji: Wyłączone", moveImage: "Przenieś obraz" },
  tr: { autoCenterOn: "Düzenlemelerden sonra otomatik ortala: Açık", autoCenterOff: "Düzenlemelerden sonra otomatik ortala: Kapalı", moveImage: "Görseli taşı" },
  id: { autoCenterOn: "Pusatkan otomatis setelah edit: Aktif", autoCenterOff: "Pusatkan otomatis setelah edit: Nonaktif", moveImage: "Pindahkan gambar" },
  ms: { autoCenterOn: "Pusat automatik selepas suntingan: Hidup", autoCenterOff: "Pusat automatik selepas suntingan: Mati", moveImage: "Alihkan imej" },
  vi: { autoCenterOn: "Tự động căn giữa sau khi chỉnh sửa: Bật", autoCenterOff: "Tự động căn giữa sau khi chỉnh sửa: Tắt", moveImage: "Di chuyển ảnh" },
  th: { autoCenterOn: "จัดกึ่งกลางอัตโนมัติหลังแก้ไข: เปิด", autoCenterOff: "จัดกึ่งกลางอัตโนมัติหลังแก้ไข: ปิด", moveImage: "ย้ายรูปภาพ" },
};

const dragItemCopy: Record<LanguageCode, string> = {
  en: "Drag {item}",
  es: "Arrastrar {item}",
  fr: "Faire glisser {item}",
  de: "{item} ziehen",
  ja: "{item}をドラッグ",
  zh: "拖动{item}",
  ko: "{item} 드래그",
  hi: "{item} को खींचें",
  pt: "Arrastar {item}",
  it: "Trascina {item}",
  ru: "Перетащить: {item}",
  ar: "اسحب {item}",
  nl: "{item} slepen",
  pl: "Przeciągnij: {item}",
  tr: "{item} öğesini sürükle",
  id: "Seret {item}",
  ms: "Seret {item}",
  vi: "Kéo {item}",
  th: "ลาก {item}",
};

function dragItemLabel(item: string) {
  return dragItemCopy[language.value].replace("{item}", item);
}

const toolLabelKeys: Record<ToolId, TranslationKey> = {
  image: "image",
  corners: "corners",
  border: "border",
  shadow: "shadow",
  crop: "crop",
  applyCrop: "crop",
  resize: "resize",
  filters: "filters",
  adjustments: "adjustments",
  text: "text",
  export: "export",
};

const tools: Array<{ id: ToolId; labelKey: TranslationKey; icon: Component }> = [
  { id: "crop", labelKey: "crop", icon: SquareDashed },
  { id: "corners", labelKey: "corners", icon: SquareRoundCorner },
  { id: "resize", labelKey: "resize", icon: Maximize2 },
  { id: "filters", labelKey: "filters", icon: Palette },
  { id: "text", labelKey: "text", icon: Type },
  { id: "border", labelKey: "border", icon: SquareUser },
  { id: "adjustments", labelKey: "adjustments", icon: SlidersVertical },
  { id: "shadow", labelKey: "shadow", icon: Moon },
  { id: "export", labelKey: "export", icon: Download },
];
const shareBrandColors: Record<string, { bg: string; fg: string; border: string }> = {
  share: { bg: "#eef5ff", fg: "#2563eb", border: "#bfdbfe" },
  x: { bg: "#edf6ff", fg: "#0f172a", border: "#cbd5e1" },
  facebook: { bg: "#eaf2ff", fg: "#1877f2", border: "#bfdbfe" },
  line: { bg: "#eefaf3", fg: "#06c755", border: "#bbf7d0" },
  whatsapp: { bg: "#eefaf3", fg: "#25d366", border: "#bbf7d0" },
  linkedin: { bg: "#e8f3ff", fg: "#0a66c2", border: "#bfdbfe" },
  weibo: { bg: "#fff0f2", fg: "#e6162d", border: "#fecdd3" },
  wechat: { bg: "#eefaf3", fg: "#07c160", border: "#bbf7d0" },
  douyin: { bg: "#fef2f6", fg: "#fe2c55", border: "#fecdd3" },
  xiaohongshu: { bg: "#fff0f2", fg: "#ff2442", border: "#fecdd3" },
  qq: { bg: "#e6f7ff", fg: "#12b7f5", border: "#bae6fd" },
  sharechat: { bg: "#fff2e8", fg: "#ff7b00", border: "#ffd8b5" },
};
const canvasRef = ref<HTMLCanvasElement | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);
const stageRef = ref<HTMLElement | null>(null);
const cropTutorialHandleRef = ref<HTMLElement | null>(null);
const cropTutorialPanelCommitRef = ref<HTMLElement | null>(null);
const image = ref<HTMLImageElement | null>(null);
const imagePreviewSource = shallowRef<HTMLCanvasElement | null>(null);
let protectedOverlayMask: HTMLCanvasElement | null = null;
const imageUrl = ref("");
const imageName = ref("");
const imageSize = reactive({ width: 0, height: 0 });
const isDraggingFile = ref(false);
const imageLoading = ref(false);
const dropFlashVisible = ref(false);
const unsupportedFileNoticeVisible = ref(false);
const cropTutorialStorageKey = "cutmypic-hide-crop-tutorial";
const cropTutorialDismissed = ref(readLocalStorage(cropTutorialStorageKey) === "1");
const cropTutorialStep = ref<"hidden" | "handles" | "dragging" | "scissors" | "tools">("hidden");
const cropTutorialCardStyle = ref<Record<string, string>>({});
const cropTutorialPointerStyle = ref<Record<string, string>>({});
const cropTutorialPointerFacesLeft = ref(false);
let cropTutorialStarted = false;
let cropTutorialHandleDragStarted = false;
let imageLoadRequestId = 0;
let cropTutorialFinalDismissArmed = false;
const storedTheme = readLocalStorage("cutmypic-theme");
const theme = ref<"dark" | "light">(storedTheme === "light" ? "light" : "dark");
const storedViewBackgroundMode = readLocalStorage("cutmypic-view-background-mode");
const storedViewBackgroundColor = readLocalStorage("cutmypic-view-background-color");
const viewBackgroundMode = ref<"checkerboard" | "color">(storedViewBackgroundMode === "color" ? "color" : "checkerboard");
const viewBackgroundColor = ref(/^#[0-9a-f]{6}$/i.test(storedViewBackgroundColor ?? "") ? storedViewBackgroundColor! : "#ffffff");
const activeTool = ref<ToolId | null>(null);
const canvasFilterSupported = ref(true);
const activePanelTool = ref<"image" | "corners" | "border" | "shadow" | "crop" | "resize" | "filters" | "adjustments" | "text" | null>(null);
const fullscreenMode = ref(false);
const resizeMode = ref<"constrain" | "freeform">("constrain");
const resizeConstrainedAspect = ref(1);
const resizePanelMode = ref<"resize" | "rotate">("resize");
const resizeRotateDegrees = ref(0);
const committedResizeRotateDegrees = ref(0);
const resizeFlipHorizontal = ref(false);
const committedResizeFlipHorizontal = ref(false);
const resizeFlipVertical = ref(false);
const committedResizeFlipVertical = ref(false);
const cropMode = ref<"free" | "square" | "widescreen" | "portrait" | "story" | "pin" | "cinema" | "classic" | "wide" | "header" | "circle" | "heart" | "star">("free");
const committedImageShape = ref<CommittedImageShape>("rect");
const cropFunctionMode = ref<"trimmer" | "masking">("trimmer");
const cropTrimmerTab = ref<"cropCommon" | "cropSocial">("cropCommon");
const selectedCropPresetId = ref("common-free");
const freeCropAspectLocked = ref(false);
const freeCropLockedAspect = ref(1);
const cropCircleAspectLocked = ref(true);
const cropMaskShape = ref<"free" | "circle">("free");
const maskRoundedAspectLocked = ref(true);
const cropMaskMode = ref<"blur" | "color">("blur");
const cropMaskBlurIntensity = ref(12);
const committedCropMaskBlurIntensity = ref(12);
const cropMaskTransparency = ref(45);
const committedCropMaskTransparency = ref(45);
const cropMaskColor = ref("#64b5f6");
const committedCropMaskColor = ref("#64b5f6");
type ExportFormat = "png" | "webp" | "jpg";
const exportFormat = ref<ExportFormat>("png");
const exportFormatOptions = [
  { id: "png", label: "PNG", mimeType: "image/png", extension: "png" },
  { id: "webp", label: "WebP", mimeType: "image/webp", extension: "webp" },
  { id: "jpg", label: "JPG", mimeType: "image/jpeg", extension: "jpg" },
] as const;
const exportQuality = ref(92);
const exportJpegBackgroundColor = ref("#ffffff");
const exportDialogOpen = ref(false);
const exportSavePromptOpen = ref(false);
const shortcutHelpOpen = ref(false);
type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};
const deferredInstallPrompt = shallowRef<InstallPromptEvent | null>(null);
const saveToolDialogOpen = ref(false);
const saveToolOfferVisible = ref(false);
const appInstalled = ref(false);
const saveToolOfferStorageKey = "cutmypic-save-tool-offer-hidden";
const exportPreviewUrl = ref("");
const shareClipboardNoticeOpen = ref(false);
const shareOpeningNowOpen = ref(false);
const shareSiteStatusMessage = ref("");
const borderShadowWarningOpen = ref(false);
const stageCursor = ref<string | null>(null);
const imagePan = reactive({ x: 0, y: 0 });
const imagePanActive = ref(false);
const imagePanMode = ref(false);
const autoCenterEnabled = ref(true);
const comparisonMode = ref(false);
const comparisonLabelsVisible = ref(false);
const comparisonPosition = ref(50);
const comparisonBeforeImage = shallowRef<HTMLImageElement | null>(null);
let comparisonBeforeDataUrl = "";
let suspendedToolForComparison: ToolId | null = null;
let suspendedPanelForComparison: typeof activePanelTool.value = null;
let comparisonPointer: { pointerId: number; captureTarget: HTMLElement } | null = null;
let comparisonLabelFrame: number | null = null;
const fullscreenRailPosition = reactive({ x: 12, y: 96.5 });
const hoverHint = ref("");
const hoverHintVisible = ref(false);
const hoverHintVariant = ref<"default" | "warning">("default");
const hoverHintStyle = ref<Record<string, string>>({});
const previewBaseScale = ref(1);
let shareOpenTimer: number | null = null;
let clipboardNoticeTimer: number | null = null;
let shareSiteStatusTimer: number | null = null;
let saveToolOfferTimer: number | null = null;
let hoverHintTimer: number | null = null;
let fullscreenRailPointer:
  | {
      pointerId: number;
      startX: number;
      startY: number;
      railX: number;
      railY: number;
    }
  | null = null;

const crop = reactive<Crop>({
  x: 0,
  y: 0,
  width: 0,
  height: 0,
});
const committedCrop = reactive<Crop>({
  x: 0,
  y: 0,
  width: 0,
  height: 0,
});

const resizeBox = reactive<Crop>({
  x: 0,
  y: 0,
  width: 0,
  height: 0,
});
const committedResizeBox = reactive<Crop>({
  x: 0,
  y: 0,
  width: 0,
  height: 0,
});
function getResizeTransformBounds(width: number, height: number, angleDegrees: number) {
  const normalized = ((angleDegrees % 360) + 360) % 360;
  const radians = (normalized * Math.PI) / 180;
  const sin = Math.abs(Math.sin(radians));
  const cos = Math.abs(Math.cos(radians));
  return {
    width: Math.max(1, Math.ceil(width * cos + height * sin)),
    height: Math.max(1, Math.ceil(width * sin + height * cos)),
  };
}

function drawTransformedImage(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  drawWidth: number,
  drawHeight: number,
  angleDegrees: number,
  flipHorizontal: boolean,
  flipVertical: boolean,
) {
  const bounds = getResizeTransformBounds(drawWidth, drawHeight, angleDegrees);
  const radians = ((angleDegrees % 360) * Math.PI) / 180;
  ctx.save();
  ctx.translate(bounds.width / 2, bounds.height / 2);
  ctx.rotate(radians);
  ctx.scale(flipHorizontal ? -1 : 1, flipVertical ? -1 : 1);
  ctx.drawImage(source, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
  ctx.restore();
}
const committedCornerRadius = ref(0);
const committedCornerRadiusX = ref(0);
const committedCornerRadiusY = ref(0);
const committedFilter = ref<FilterId>("none");
const committedColorReplace = reactive<ColorReplaceState>({
  targetColor: "#ffffff",
  replacementColor: "#000000",
  strength: 20,
  smoothness: 20,
  targetTransparent: false,
  replacementTransparent: false,
});
const committedAdjustments = reactive({
  contrast: 100,
  brightness: 100,
  saturation: 100,
  colorBalance: 0,
  sharpen: 0,
  soften: 0,
  transparency: 0,
  transparencyIncludeBorder: false,
});
const committedText = reactive({
  content: "",
  color: "#e8f1ff",
  size: 36,
  rotation: 0,
  x: 50,
  y: 4,
  shadowSize: 8,
  shadowDarkness: 35,
  shadowColor: "#000000",
});
const committedBorderState = reactive({
  borderThickness: 0,
  borderColor: "#64b5f6",
  borderUseGradient: false,
  borderGradientType: "linear" as "linear" | "edge",
  borderGradientAngle: 270,
  borderGradientStart: "#64b5f6",
  borderGradientEnd: "#8bc9fb",
});
const committedBorderLayers = reactive<BorderLayer[]>([]);
const committedShadowState = reactive({
  shadowDarkness: 0,
  shadowSize: 0,
  shadowColor: "#000000",
  shadowDirection: 0,
  contentWidth: 0,
  contentHeight: 0,
  contentX: 0,
  contentY: 0,
});
const textState = reactive({
  content: "",
  color: "#e8f1ff",
  size: 36,
  rotation: 0,
  x: 50,
  y: 4,
  shadowSize: 8,
  shadowDarkness: 35,
  shadowColor: "#000000",
});

function textSizeMaximumForDimensions(width: number, height: number) {
  const shorterDimension = Math.max(1, Math.min(width, height));
  return Math.min(2000, Math.max(160, Math.round(shorterDimension * 0.4)));
}

function defaultTextSizeForDimensions(width: number, height: number) {
  if (width <= 0 || height <= 0) return 36;
  const shorterDimension = Math.min(width, height);
  return Math.min(
    textSizeMaximumForDimensions(width, height),
    Math.max(10, Math.round(shorterDimension * 0.04)),
  );
}

const textSizeMaximum = computed(() => textSizeMaximumForDimensions(imageSize.width, imageSize.height));
const textPanelMode = ref<"text" | "stamp">("text");
const textPositionMode = ref(false);
const textDragActive = ref(false);
const textDragOffset = reactive({ x: 0, y: 0 });
let mobileTextPositionScrollY = 0;
let mobileTextPositionBodyTop = "";
const stampPositionMode = ref(false);
const stampDragActive = ref(false);
const stampDragOffset = reactive({ x: 0, y: 0 });
const stampPreviewRef = ref<HTMLElement | null>(null);
let pendingStampPlacementMetrics:
  | { width?: number; height?: number; fontSize?: number; lineWidth?: number; centerX: number; centerY: number }
  | null = null;
const canEditTextPosition = computed(() => hasImage.value && textPanelMode.value === "text" && !!textState.content.trim());

function setMobileTextPositionScreenLock(locked: boolean) {
  if (!isBrowser) return;
  const root = document.documentElement;
  if (locked && !root.classList.contains("is-mobile-text-position-locked")) {
    mobileTextPositionScrollY = window.scrollY;
    mobileTextPositionBodyTop = document.body.style.top;
    document.body.style.top = `-${mobileTextPositionScrollY}px`;
    root.classList.add("is-mobile-text-position-locked");
    return;
  }
  if (!locked && root.classList.contains("is-mobile-text-position-locked")) {
    root.classList.remove("is-mobile-text-position-locked");
    document.body.style.top = mobileTextPositionBodyTop;
    window.scrollTo(0, mobileTextPositionScrollY);
  }
}

watch(textPositionMode, (isPositioning) => {
  setMobileTextPositionScreenLock(isPositioning && window.innerWidth <= 767);
});
const stampPositionLabel = computed(() => {
  if (selectedStampPreset.value) return selectedStampPreset.value.label;
  const name = textState.content.trim();
  return name || "Stamp";
});
const canEditStampPosition = computed(() => hasImage.value && textPanelMode.value === "stamp" && !!selectedStampPreset.value);
const selectedStampPreset = computed(() => stampPresets.find((preset) => preset.label === selectedStampLabel.value) ?? null);
const placementFontSize = computed(() => Math.max(10, Math.round(textState.size * canvasMetrics.scale)));
const placementMargin = computed(() => Math.max(12, Math.round(placementFontSize.value * 0.45)));
const textPreviewOffsetY = computed(() => Math.round(measureTextMetrics(placementFontSize.value).ascent / 2));
const textPreviewStyle = computed(() => ({
  color: textState.color,
  fontSize: `${placementFontSize.value}px`,
  transform: `translateY(${-textPreviewOffsetY.value}px) rotate(${textState.rotation}deg)`,
  textShadow: textState.shadowSize > 0 && textState.shadowDarkness > 0
    ? `0 2px ${Math.round(textState.shadowSize * canvasMetrics.scale)}px ${hexToRgba(textState.shadowColor, textState.shadowDarkness / 100)}`
    : "none",
}));
function measureTextMetrics(fontSize: number, fontWeight = "400") {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return {
      ascent: Math.round(fontSize * 0.8),
      descent: Math.round(fontSize * 0.2),
    };
  }
  ctx.font = `${fontWeight} ${fontSize}px Inter, ui-sans-serif, system-ui, sans-serif`;
  const metrics = ctx.measureText("Hg");
  return {
    ascent: metrics.actualBoundingBoxAscent || Math.round(fontSize * 0.8),
    descent: metrics.actualBoundingBoxDescent || Math.round(fontSize * 0.2),
  };
}

function measureTextBox(fontSize: number, text: string, maxWidth: number, fontWeight = "400") {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    const lineHeight = Math.round(fontSize * 1.2);
    const lines = text.split(/\s+/).filter(Boolean);
    return {
      lines,
      lineHeight,
      width: maxWidth,
      height: Math.max(lineHeight, lines.length * lineHeight),
      ascent: Math.round(fontSize * 0.8),
      descent: Math.round(fontSize * 0.2),
    };
  }
  ctx.font = `${fontWeight} ${fontSize}px Inter, ui-sans-serif, system-ui, sans-serif`;
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = words[0] ?? "";
  for (let i = 1; i < words.length; i += 1) {
    const testLine = `${current} ${words[i]}`;
    if (ctx.measureText(testLine).width <= maxWidth) current = testLine;
    else {
      lines.push(current);
      current = words[i];
    }
  }
  if (current) lines.push(current);
  const metrics = ctx.measureText("Hg");
  const ascent = metrics.actualBoundingBoxAscent || Math.round(fontSize * 0.8);
  const descent = metrics.actualBoundingBoxDescent || Math.round(fontSize * 0.2);
  const lineHeight = Math.round(fontSize * 1.2);
  return {
    lines,
    lineHeight,
    width: Math.max(...lines.map((line) => ctx.measureText(line).width), 0),
    height: ascent + descent + Math.max(0, lines.length - 1) * lineHeight,
    ascent,
    descent,
  };
}

function textCommitOffsetX() {
  const displayScale = canvasMetrics.scale || 1;
  return Math.round(1 / displayScale);
}
const stampPlacementWidth = computed(() => {
  if (selectedStampPreset.value?.kind === "price-tag") {
    return Math.max(84, Math.round(118 * (textState.size / Math.max(1, selectedStampPreset.value.size))));
  }
  return Math.max(84, Math.round(textState.size * canvasMetrics.scale * 0.85));
});
const textPlacementWidth = computed(() => Math.max(120, Math.round(textState.size * canvasMetrics.scale * 3.2)));
const textPlacementHeight = computed(() => Math.max(18, Math.round(textState.size * canvasMetrics.scale * 0.6)));
const textPlacementBox = computed(() => measureTextBox(
  placementFontSize.value,
  textState.content.trim(),
  Math.max(40, canvasMetrics.imageWidth - placementMargin.value * 2),
));
const stampPositionPoint = computed(() => ({
  left: canvasMetrics.imageX + clamp((textState.x / 100) * canvasMetrics.imageWidth, placementMargin.value, canvasMetrics.imageWidth - placementMargin.value),
  top: canvasMetrics.imageY + clamp((textState.y / 100) * canvasMetrics.imageHeight, placementMargin.value, canvasMetrics.imageHeight - placementMargin.value),
}));
const stampPlaceButtonStyle = computed(() => ({
  left: `${stampPositionPoint.value.left + stampPlacementWidth.value / 2 + 14}px`,
  top: `${stampPositionPoint.value.top}px`,
}));
const stampCancelButtonStyle = computed(() => ({
  left: `${stampPositionPoint.value.left + stampPlacementWidth.value / 2 + 134}px`,
  top: `${stampPositionPoint.value.top}px`,
}));
const textPositionPoint = computed(() => {
  const minimumTop = Math.max(0, textPlacementBox.value.height - textPlacementBox.value.ascent);
  const maximumTop = Math.max(minimumTop, canvasMetrics.imageHeight - textPlacementBox.value.ascent);
  return {
    left: canvasMetrics.imageX + clamp((textState.x / 100) * canvasMetrics.imageWidth, placementMargin.value, canvasMetrics.imageWidth - placementMargin.value),
    top: canvasMetrics.imageY + clamp((textState.y / 100) * canvasMetrics.imageHeight, minimumTop, maximumTop),
  };
});
const mobileTextControlsPoint = computed(() => {
  const controlsWidth = 162;
  const minimumLeft = canvasMetrics.imageX + 8;
  const maximumLeft = Math.max(minimumLeft, canvasMetrics.imageX + canvasMetrics.imageWidth - controlsWidth - 8);
  const desiredTop = textPositionPoint.value.top + textPlacementHeight.value / 2 + 31;
  const minimumTop = 29;
  const maximumTop = Math.max(minimumTop, canvasMetrics.cssHeight - 29);
  return {
    left: clamp(textPositionPoint.value.left - controlsWidth / 2, minimumLeft, maximumLeft),
    top: clamp(desiredTop, minimumTop, maximumTop),
  };
});
const textPlaceButtonStyle = computed(() => ({
  left: `${isMobileViewport.value ? mobileTextControlsPoint.value.left : textPositionPoint.value.left + textPlacementWidth.value / 2 + 14}px`,
  top: `${isMobileViewport.value ? mobileTextControlsPoint.value.top : textPositionPoint.value.top - 12}px`,
}));
const textCancelButtonStyle = computed(() => ({
  left: `${isMobileViewport.value ? mobileTextControlsPoint.value.left + 120 : textPositionPoint.value.left + textPlacementWidth.value / 2 + 134}px`,
  top: `${isMobileViewport.value ? mobileTextControlsPoint.value.top : textPositionPoint.value.top - 12}px`,
}));

type StampPreset = {
  label: string;
  content: string;
  category: "animals" | "decorations" | "labels";
  kind: "emoji" | "price-tag";
  color: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  size: number;
  x: number;
  y: number;
};

const selectedStampLabel = ref("");
const animalStampPresets: StampPreset[] = [
  { label: "Dog", content: "🐶", category: "animals", kind: "emoji", color: "#f97316", size: 68, x: 50, y: 50 },
  { label: "Cat", content: "🐱", category: "animals", kind: "emoji", color: "#38bdf8", size: 68, x: 50, y: 50 },
  { label: "Mouse", content: "🐭", category: "animals", kind: "emoji", color: "#a78bfa", size: 68, x: 50, y: 50 },
  { label: "Lion", content: "🦁", category: "animals", kind: "emoji", color: "#f59e0b", size: 68, x: 50, y: 50 },
  { label: "Rabbit", content: "🐰", category: "animals", kind: "emoji", color: "#f472b6", size: 68, x: 50, y: 50 },
  { label: "Panda", content: "🐼", category: "animals", kind: "emoji", color: "#94a3b8", size: 68, x: 50, y: 50 },
  { label: "Fox", content: "🦊", category: "animals", kind: "emoji", color: "#fb923c", size: 68, x: 50, y: 50 },
  { label: "Bear", content: "🐻", category: "animals", kind: "emoji", color: "#d97706", size: 68, x: 50, y: 50 },
  { label: "Frog", content: "🐸", category: "animals", kind: "emoji", color: "#22c55e", size: 68, x: 50, y: 50 },
  { label: "Koala", content: "🐨", category: "animals", kind: "emoji", color: "#cbd5e1", size: 68, x: 50, y: 50 },
  { label: "Tiger", content: "🐯", category: "animals", kind: "emoji", color: "#facc15", size: 68, x: 50, y: 50 },
  { label: "Penguin", content: "🐧", category: "animals", kind: "emoji", color: "#60a5fa", size: 68, x: 50, y: 50 },
  { label: "Whale", content: "🐋", category: "animals", kind: "emoji", color: "#0ea5e9", size: 68, x: 50, y: 50 },
  { label: "Elephant", content: "🐘", category: "animals", kind: "emoji", color: "#64748b", size: 68, x: 50, y: 50 },
  { label: "Horse", content: "🐴", category: "animals", kind: "emoji", color: "#a16207", size: 68, x: 50, y: 50 },
  { label: "Bird", content: "🐦", category: "animals", kind: "emoji", color: "#7c3aed", size: 68, x: 50, y: 50 },
  { label: "Cow", content: "🐮", category: "animals", kind: "emoji", color: "#f8fafc", size: 68, x: 50, y: 50 },
  { label: "Monkey", content: "🐵", category: "animals", kind: "emoji", color: "#92400e", size: 68, x: 50, y: 50 },
  { label: "Pig", content: "🐷", category: "animals", kind: "emoji", color: "#fb7185", size: 68, x: 50, y: 50 },
  { label: "Unicorn", content: "🦄", category: "animals", kind: "emoji", color: "#c084fc", size: 68, x: 50, y: 50 },
];
const decorationStampPresets: StampPreset[] = [
  { label: "Heart", content: "❤️", category: "decorations", kind: "emoji", color: "#ef4444", size: 68, x: 50, y: 50 },
  { label: "Sparkling Heart", content: "💖", category: "decorations", kind: "emoji", color: "#ec4899", size: 68, x: 50, y: 50 },
  { label: "Pink Flower", content: "🌸", category: "decorations", kind: "emoji", color: "#f9a8d4", size: 68, x: 50, y: 50 },
  { label: "Rose", content: "🌹", category: "decorations", kind: "emoji", color: "#dc2626", size: 68, x: 50, y: 50 },
  { label: "Sunflower", content: "🌻", category: "decorations", kind: "emoji", color: "#facc15", size: 68, x: 50, y: 50 },
  { label: "Tulip", content: "🌷", category: "decorations", kind: "emoji", color: "#fb7185", size: 68, x: 50, y: 50 },
  { label: "Blossom", content: "🌼", category: "decorations", kind: "emoji", color: "#fde047", size: 68, x: 50, y: 50 },
  { label: "Star", content: "⭐", category: "decorations", kind: "emoji", color: "#fbbf24", size: 68, x: 50, y: 50 },
  { label: "Sparkles", content: "✨", category: "decorations", kind: "emoji", color: "#fde68a", size: 68, x: 50, y: 50 },
  { label: "Rainbow", content: "🌈", category: "decorations", kind: "emoji", color: "#60a5fa", size: 68, x: 50, y: 50 },
  { label: "Butterfly", content: "🦋", category: "decorations", kind: "emoji", color: "#8b5cf6", size: 68, x: 50, y: 50 },
  { label: "Ribbon", content: "🎀", category: "decorations", kind: "emoji", color: "#f472b6", size: 68, x: 50, y: 50 },
  { label: "Balloon", content: "🎈", category: "decorations", kind: "emoji", color: "#ef4444", size: 68, x: 50, y: 50 },
  { label: "Celebration", content: "🎉", category: "decorations", kind: "emoji", color: "#f59e0b", size: 68, x: 50, y: 50 },
  { label: "Crown", content: "👑", category: "decorations", kind: "emoji", color: "#facc15", size: 68, x: 50, y: 50 },
  { label: "Gem", content: "💎", category: "decorations", kind: "emoji", color: "#22d3ee", size: 68, x: 50, y: 50 },
  { label: "Fire", content: "🔥", category: "decorations", kind: "emoji", color: "#f97316", size: 68, x: 50, y: 50 },
  { label: "Snowflake", content: "❄️", category: "decorations", kind: "emoji", color: "#7dd3fc", size: 68, x: 50, y: 50 },
  { label: "Leaf", content: "🍃", category: "decorations", kind: "emoji", color: "#22c55e", size: 68, x: 50, y: 50 },
  { label: "Lucky Clover", content: "🍀", category: "decorations", kind: "emoji", color: "#16a34a", size: 68, x: 50, y: 50 },
  { label: "Crescent Moon", content: "🌙", category: "decorations", kind: "emoji", color: "#fcd34d", size: 68, x: 50, y: 50 },
  { label: "Shooting Star", content: "🌠", category: "decorations", kind: "emoji", color: "#60a5fa", size: 68, x: 50, y: 50 },
  { label: "Wrapped Gift", content: "🎁", category: "decorations", kind: "emoji", color: "#ef4444", size: 68, x: 50, y: 50 },
  { label: "Musical Notes", content: "🎶", category: "decorations", kind: "emoji", color: "#a78bfa", size: 68, x: 50, y: 50 },
];
const labelStampPresets: StampPreset[] = [
  { label: "Sale", content: "SALE", category: "labels", kind: "price-tag", color: "#ef4444", backgroundColor: "#ef4444", textColor: "#ffffff", size: 54, x: 50, y: 50 },
  { label: "New", content: "NEW", category: "labels", kind: "price-tag", color: "#22c55e", backgroundColor: "#22c55e", textColor: "#052e16", size: 54, x: 50, y: 50 },
  { label: "Hot", content: "HOT", category: "labels", kind: "price-tag", color: "#f97316", backgroundColor: "#f97316", textColor: "#fff7ed", size: 54, x: 50, y: 50 },
  { label: "10% Off", content: "10% OFF", category: "labels", kind: "price-tag", color: "#f97316", backgroundColor: "#f97316", textColor: "#fff7ed", size: 54, x: 50, y: 50 },
  { label: "20% Off", content: "20% OFF", category: "labels", kind: "price-tag", color: "#ef4444", backgroundColor: "#ef4444", textColor: "#ffffff", size: 54, x: 50, y: 50 },
  { label: "30% Off", content: "30% OFF", category: "labels", kind: "price-tag", color: "#22c55e", backgroundColor: "#22c55e", textColor: "#052e16", size: 54, x: 50, y: 50 },
  { label: "50% Off", content: "50% OFF", category: "labels", kind: "price-tag", color: "#facc15", backgroundColor: "#facc15", textColor: "#422006", size: 54, x: 50, y: 50 },
  { label: "Best", content: "BEST", category: "labels", kind: "price-tag", color: "#60a5fa", backgroundColor: "#60a5fa", textColor: "#0f172a", size: 54, x: 50, y: 50 },
  { label: "Sold", content: "SOLD", category: "labels", kind: "price-tag", color: "#94a3b8", backgroundColor: "#94a3b8", textColor: "#111827", size: 54, x: 50, y: 50 },
  { label: "Blank Tag 1", content: "", category: "labels", kind: "price-tag", color: "#ef4444", backgroundColor: "#ef4444", textColor: "#ffffff", size: 54, x: 50, y: 50 },
  { label: "Blank Tag 2", content: "", category: "labels", kind: "price-tag", color: "#60a5fa", backgroundColor: "#60a5fa", textColor: "#0f172a", size: 54, x: 50, y: 50 },
  { label: "Blank Tag 3", content: "", category: "labels", kind: "price-tag", color: "#ffffff", backgroundColor: "#ffffff", textColor: "#111827", borderColor: "#111827", size: 54, x: 50, y: 50 },
];
const stampPresets = [...decorationStampPresets, ...animalStampPresets, ...labelStampPresets];

const settings = reactive({
  radius: 48,
  zoom: 1,
  filter: "none" as FilterId,
  colorReplaceTarget: "#ffffff",
  colorReplaceReplacement: "#000000",
  colorReplaceStrength: 20,
  colorReplaceSmoothness: 20,
  colorReplaceTargetTransparent: false,
  colorReplaceReplacementTransparent: false,
  contrast: 100,
  brightness: 100,
  saturation: 100,
  colorBalance: 0,
  sharpen: 0,
  soften: 0,
  transparency: 0,
  transparencyIncludeBorder: false,
  borderThickness: 0,
  borderColor: "#64b5f6",
  borderUseGradient: false,
  borderGradientType: "linear" as "linear" | "edge",
  borderGradientAngle: 270,
  borderGradientStart: "#64b5f6",
  borderGradientEnd: "#8bc9fb",
  shadowDarkness: 0,
  shadowSize: 0,
  shadowColor: "#000000",
  shadowDirection: 45,
});

type PreviewSnapshot = {
  settings: {
    radius: number;
    zoom: number;
    filter: FilterId;
    colorReplaceTarget?: string;
    colorReplaceReplacement?: string;
    colorReplaceStrength?: number;
    colorReplaceSmoothness?: number;
    colorReplaceTargetTransparent?: boolean;
    colorReplaceReplacementTransparent?: boolean;
    contrast: number;
    brightness: number;
    saturation: number;
    colorBalance: number;
    sharpen: number;
    soften: number;
    transparency?: number;
    transparencyIncludeBorder?: boolean;
    borderThickness: number;
    borderColor: string;
    borderUseGradient: boolean;
    borderGradientType: "linear" | "edge";
    borderGradientAngle: number;
    borderGradientStart: string;
    borderGradientEnd: string;
    shadowDarkness: number;
    shadowSize: number;
    shadowColor: string;
  shadowDirection: number;
  };
  crop: Crop;
  committedCrop: Crop;
  resizeBox: Crop;
  resizePanelMode: "resize" | "rotate";
  resizeRotateDegrees: number;
  resizeFlipHorizontal: boolean;
  resizeFlipVertical: boolean;
  previewBaseScale: number;
  cropFunctionMode: "trimmer" | "masking";
  committedImageShape?: CommittedImageShape;
  selectedCropPresetId?: string;
  freeCropAspectLocked?: boolean;
  freeCropLockedAspect?: number;
  cropCircleAspectLocked?: boolean;
  cropMaskShape: "free" | "circle";
  maskRoundedAspectLocked?: boolean;
  cropMaskMode: "blur" | "color";
  cropMaskBlurIntensity: number;
  committedCropMaskBlurIntensity: number;
  cropMaskTransparency: number;
  committedCropMaskTransparency: number;
  cropMaskColor: string;
  committedCropMaskColor: string;
  committedResizeRotateDegrees?: number;
  committedResizeFlipHorizontal?: boolean;
  committedResizeFlipVertical?: boolean;
  textState: {
    content: string;
    color: string;
    size: number;
    rotation?: number;
    x: number;
    y: number;
    shadowSize: number;
    shadowDarkness: number;
    shadowColor: string;
  };
  cropMode?: typeof cropMode.value;
  committedResizeBox?: Crop;
  resizeMode?: typeof resizeMode.value;
  resizeConstrainedAspect?: number;
  textPanelMode?: typeof textPanelMode.value;
  selectedStampLabel?: string;
  activePanelTool?: typeof activePanelTool.value;
  activeTool: ToolId | null;
};

type WorkflowStep =
  | {
      kind: "crop";
      mode: typeof cropMode.value;
      selectedPresetId: string;
      aspect: number;
    }
  | { kind: "corners"; radiusRatio: number }
  | {
      kind: "border";
      thicknessRatio: number;
      state: {
        borderColor: string;
        borderUseGradient: boolean;
        borderGradientType: "linear" | "edge";
        borderGradientAngle: number;
        borderGradientStart: string;
        borderGradientEnd: string;
      };
    }
  | { kind: "shadow"; darkness: number; sizeRatio: number; color: string; direction: number }
  | { kind: "filter"; filter: FilterId; colorReplace?: ColorReplaceState }
  | {
      kind: "adjustments";
      contrast: number;
      brightness: number;
      saturation: number;
      colorBalance: number;
      sharpen: number;
      soften: number;
      transparency?: number;
      transparencyIncludeBorder?: boolean;
    }
  | {
      kind: "resize";
      width: number;
      height: number;
      rotation: number;
      flipHorizontal: boolean;
      flipVertical: boolean;
    }
  | {
      kind: "text";
      panelMode: "text" | "stamp";
      stampLabel: string;
      content: string;
      color: string;
      sizeRatio: number;
      rotation: number;
      x: number;
      y: number;
      shadowSizeRatio: number;
      shadowDarkness: number;
      shadowColor: string;
    };

type SavedWorkflow = {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  steps: WorkflowStep[];
};

type WorkflowResizeWarning = {
  sourceWidth: number;
  sourceHeight: number;
  targetWidth: number;
  targetHeight: number;
};

type ImageHistoryEntry = {
  kind: HistoryStepKind;
  label?: string;
  dataUrl: string;
  name: string;
  width: number;
  height: number;
  zoom: number;
  previewSnapshot: PreviewSnapshot;
  committedCornerRadius: number;
  committedCornerRadiusX?: number;
  committedCornerRadiusY?: number;
  committedFilter: FilterId;
  committedColorReplace?: ColorReplaceState;
  committedAdjustments: {
    contrast: number;
    brightness: number;
    saturation: number;
    colorBalance: number;
    sharpen: number;
    soften: number;
    transparency?: number;
    transparencyIncludeBorder?: boolean;
  };
  committedText: {
    content: string;
    color: string;
    size: number;
    rotation: number;
    x: number;
    y: number;
    shadowSize: number;
    shadowDarkness: number;
    shadowColor: string;
  };
  committedBorderState: {
    borderThickness: number;
    borderColor: string;
    borderUseGradient: boolean;
    borderGradientType: "linear" | "edge";
    borderGradientAngle: number;
    borderGradientStart: string;
    borderGradientEnd: string;
  };
  committedBorderLayers: BorderLayer[];
  committedShadowState: {
    shadowDarkness: number;
    shadowSize: number;
    shadowColor: string;
    shadowDirection: number;
    contentWidth: number;
    contentHeight: number;
    contentX: number;
    contentY: number;
  };
  committedCropMaskColor?: string;
  committedResizeRotateDegrees?: number;
  committedResizeFlipHorizontal?: boolean;
  committedResizeFlipVertical?: boolean;
  protectedOverlayMaskDataUrl?: string;
  workflowStep?: WorkflowStep;
  isMaskEdit?: boolean;
};

type BorderLayer = {
  borderThickness: number;
  borderColor: string;
  borderUseGradient: boolean;
  borderGradientType: "linear" | "edge";
  borderGradientAngle: number;
  borderGradientStart: string;
  borderGradientEnd: string;
};

type HistoryStepKind = "original" | "crop" | "corners" | "border" | "shadow" | "filter" | "text" | "adjustments" | "resize";

type StoredImageHistoryEntry = Omit<ImageHistoryEntry, "dataUrl" | "protectedOverlayMaskDataUrl"> & {
  imageBlob: Blob;
  protectedOverlayMaskBlob?: Blob;
};

type AutosavedProject = {
  version: 1;
  savedAt: number;
  previewHistory: StoredImageHistoryEntry[];
  redoHistory: StoredImageHistoryEntry[];
  workingSnapshot: PreviewSnapshot;
};

function normalizeBorderGradientType(value: unknown): "linear" | "edge" {
  return value === "edge" || value === "radial" ? "edge" : "linear";
}

const previewHistory = ref<ImageHistoryEntry[]>([]);
const redoHistory = ref<ImageHistoryEntry[]>([]);
const historyPanelOpen = ref(false);
const savedWorkflows = ref<SavedWorkflow[]>([]);
const workflowLibraryOpen = ref(false);
const workflowSaveDialogOpen = ref(false);
const workflowName = ref("");
const workflowApplying = ref(false);
const workflowAwaitingCrop = ref(false);
const workflowCropLockedAspect = ref<number | null>(null);
const activeWorkflowName = ref("");
const pendingWorkflowSteps = ref<WorkflowStep[]>([]);
const workflowStatusMessage = ref("");
const workflowResizeWarning = ref<WorkflowResizeWarning | null>(null);
const hoveredHistoryIndex = ref<number | null>(null);
const rebuildingHistory = ref(false);
const historyButtonRef = ref<HTMLElement | null>(null);
const mobileHistoryButtonRef = ref<HTMLElement | null>(null);
const autosaveRecoveryNoticeOpen = ref(false);
let isRestoringPreview = false;
let suppressFilterHistory = false;
let workflowStatusTimer: number | null = null;
let workflowResizeWarningResolver: ((proceed: boolean) => void) | null = null;

const historyStepLabelKeys: Record<HistoryStepKind, TranslationKey> = {
  original: "source",
  crop: "crop",
  corners: "corners",
  border: "border",
  shadow: "shadow",
  filter: "filters",
  text: "text",
  adjustments: "adjustments",
  resize: "resize",
};

function capturePreviewSnapshot(): PreviewSnapshot {
  return {
    settings: {
      radius: settings.radius,
      zoom: settings.zoom,
      filter: settings.filter,
      colorReplaceTarget: settings.colorReplaceTarget,
      colorReplaceReplacement: settings.colorReplaceReplacement,
      colorReplaceStrength: settings.colorReplaceStrength,
      colorReplaceSmoothness: settings.colorReplaceSmoothness,
      colorReplaceTargetTransparent: settings.colorReplaceTargetTransparent,
      colorReplaceReplacementTransparent: settings.colorReplaceReplacementTransparent,
      contrast: settings.contrast,
      brightness: settings.brightness,
      saturation: settings.saturation,
      colorBalance: settings.colorBalance,
      sharpen: settings.sharpen,
      soften: settings.soften,
      transparency: settings.transparency,
      transparencyIncludeBorder: settings.transparencyIncludeBorder,
      borderThickness: settings.borderThickness,
      borderColor: settings.borderColor,
      borderUseGradient: settings.borderUseGradient,
      borderGradientType: settings.borderGradientType,
      borderGradientAngle: settings.borderGradientAngle,
      borderGradientStart: settings.borderGradientStart,
      borderGradientEnd: settings.borderGradientEnd,
      shadowDarkness: settings.shadowDarkness,
      shadowSize: settings.shadowSize,
      shadowColor: settings.shadowColor,
      shadowDirection: settings.shadowDirection,
    },
    crop: { ...crop },
    committedCrop: { ...committedCrop },
    resizeBox: { ...resizeBox },
    committedResizeBox: { ...committedResizeBox },
    cropMode: cropMode.value,
    resizePanelMode: resizePanelMode.value,
    resizeMode: resizeMode.value,
    resizeConstrainedAspect: resizeConstrainedAspect.value,
    resizeRotateDegrees: resizeRotateDegrees.value,
    resizeFlipHorizontal: resizeFlipHorizontal.value,
    resizeFlipVertical: resizeFlipVertical.value,
    previewBaseScale: previewBaseScale.value,
    cropFunctionMode: cropFunctionMode.value,
    committedImageShape: committedImageShape.value,
    selectedCropPresetId: selectedCropPresetId.value,
    freeCropAspectLocked: freeCropAspectLocked.value,
    freeCropLockedAspect: freeCropLockedAspect.value,
    cropCircleAspectLocked: cropCircleAspectLocked.value,
    cropMaskShape: cropMaskShape.value,
    maskRoundedAspectLocked: maskRoundedAspectLocked.value,
    cropMaskMode: cropMaskMode.value,
    cropMaskBlurIntensity: cropMaskBlurIntensity.value,
    committedCropMaskBlurIntensity: committedCropMaskBlurIntensity.value,
    cropMaskTransparency: cropMaskTransparency.value,
    committedCropMaskTransparency: committedCropMaskTransparency.value,
    cropMaskColor: cropMaskColor.value,
    committedCropMaskColor: committedCropMaskColor.value,
    committedResizeRotateDegrees: committedResizeRotateDegrees.value,
    committedResizeFlipHorizontal: committedResizeFlipHorizontal.value,
    committedResizeFlipVertical: committedResizeFlipVertical.value,
    textState: { ...textState },
    textPanelMode: textPanelMode.value,
    selectedStampLabel: selectedStampLabel.value,
    activePanelTool: activePanelTool.value,
    activeTool: activeTool.value,
  };
}

function restorePreviewSnapshot(snapshot: PreviewSnapshot) {
  isRestoringPreview = true;
  settings.radius = snapshot.settings.radius;
  settings.zoom = snapshot.settings.zoom ?? 1;
  settings.filter = snapshot.settings.filter;
  settings.colorReplaceTarget = snapshot.settings.colorReplaceTarget ?? "#ffffff";
  settings.colorReplaceReplacement = snapshot.settings.colorReplaceReplacement ?? "#000000";
  settings.colorReplaceStrength = snapshot.settings.colorReplaceStrength ?? 20;
  settings.colorReplaceSmoothness = snapshot.settings.colorReplaceSmoothness ?? 0;
  settings.colorReplaceTargetTransparent = snapshot.settings.colorReplaceTargetTransparent ?? false;
  settings.colorReplaceReplacementTransparent = snapshot.settings.colorReplaceReplacementTransparent ?? false;
  settings.contrast = snapshot.settings.contrast;
  settings.brightness = snapshot.settings.brightness;
  settings.saturation = snapshot.settings.saturation;
  settings.colorBalance = snapshot.settings.colorBalance;
  settings.sharpen = snapshot.settings.sharpen;
  settings.soften = snapshot.settings.soften;
  settings.transparency = snapshot.settings.transparency ?? 0;
  settings.transparencyIncludeBorder = snapshot.settings.transparencyIncludeBorder ?? false;
  settings.borderThickness = snapshot.settings.borderThickness;
  settings.borderColor = snapshot.settings.borderColor;
  settings.borderUseGradient = snapshot.settings.borderUseGradient;
  settings.borderGradientType = normalizeBorderGradientType(snapshot.settings.borderGradientType);
  settings.borderGradientAngle = snapshot.settings.borderGradientAngle;
  settings.borderGradientStart = snapshot.settings.borderGradientStart;
  settings.borderGradientEnd = snapshot.settings.borderGradientEnd;
  settings.shadowDarkness = snapshot.settings.shadowDarkness;
  settings.shadowSize = snapshot.settings.shadowSize;
  settings.shadowColor = snapshot.settings.shadowColor;
  settings.shadowDirection = snapshot.settings.shadowDirection;
  crop.x = snapshot.crop.x;
  crop.y = snapshot.crop.y;
  crop.width = snapshot.crop.width;
  crop.height = snapshot.crop.height;
  committedCrop.x = snapshot.committedCrop.x;
  committedCrop.y = snapshot.committedCrop.y;
  committedCrop.width = snapshot.committedCrop.width;
  committedCrop.height = snapshot.committedCrop.height;
  resizeBox.x = snapshot.resizeBox.x;
  resizeBox.y = snapshot.resizeBox.y;
  resizeBox.width = snapshot.resizeBox.width;
  resizeBox.height = snapshot.resizeBox.height;
  committedResizeBox.x = snapshot.committedResizeBox?.x ?? snapshot.resizeBox.x;
  committedResizeBox.y = snapshot.committedResizeBox?.y ?? snapshot.resizeBox.y;
  committedResizeBox.width = snapshot.committedResizeBox?.width ?? snapshot.resizeBox.width;
  committedResizeBox.height = snapshot.committedResizeBox?.height ?? snapshot.resizeBox.height;
  cropMode.value = snapshot.cropMode ?? cropMode.value;
  resizePanelMode.value = snapshot.resizePanelMode ?? "resize";
  resizeMode.value = snapshot.resizeMode ?? "constrain";
  resizeConstrainedAspect.value = snapshot.resizeConstrainedAspect ?? snapshot.resizeBox.width / Math.max(1, snapshot.resizeBox.height);
  resizeRotateDegrees.value = snapshot.resizeRotateDegrees ?? 0;
  resizeFlipHorizontal.value = snapshot.resizeFlipHorizontal ?? false;
  resizeFlipVertical.value = snapshot.resizeFlipVertical ?? false;
  previewBaseScale.value = snapshot.previewBaseScale;
  cropFunctionMode.value = snapshot.cropFunctionMode;
  committedImageShape.value = snapshot.committedImageShape ?? "rect";
  selectedCropPresetId.value = snapshot.selectedCropPresetId ?? cropMode.value;
  freeCropAspectLocked.value = snapshot.freeCropAspectLocked ?? false;
  freeCropLockedAspect.value = snapshot.freeCropLockedAspect ?? snapshot.crop.width / Math.max(1, snapshot.crop.height);
  cropCircleAspectLocked.value = snapshot.cropCircleAspectLocked ?? true;
  cropMaskShape.value = snapshot.cropMaskShape ?? "free";
  maskRoundedAspectLocked.value = snapshot.maskRoundedAspectLocked ?? true;
  cropMaskMode.value = snapshot.cropMaskMode;
  cropMaskBlurIntensity.value = snapshot.cropMaskBlurIntensity;
  committedCropMaskBlurIntensity.value = snapshot.committedCropMaskBlurIntensity ?? snapshot.cropMaskBlurIntensity;
  cropMaskTransparency.value = snapshot.cropMaskTransparency;
  committedCropMaskTransparency.value = snapshot.committedCropMaskTransparency ?? snapshot.cropMaskTransparency;
  cropMaskColor.value = snapshot.cropMaskColor;
  committedCropMaskColor.value = snapshot.committedCropMaskColor ?? snapshot.cropMaskColor;
  committedResizeRotateDegrees.value = snapshot.committedResizeRotateDegrees ?? snapshot.resizeRotateDegrees ?? 0;
  committedResizeFlipHorizontal.value = snapshot.committedResizeFlipHorizontal ?? snapshot.resizeFlipHorizontal ?? false;
  committedResizeFlipVertical.value = snapshot.committedResizeFlipVertical ?? snapshot.resizeFlipVertical ?? false;
  textState.content = snapshot.textState.content;
  textState.color = snapshot.textState.color;
  textState.size = snapshot.textState.size;
  textState.rotation = snapshot.textState.rotation ?? 0;
  textState.x = snapshot.textState.x;
  textState.y = snapshot.textState.y;
  textState.shadowSize = snapshot.textState.shadowSize ?? 8;
  textState.shadowDarkness = snapshot.textState.shadowDarkness ?? 35;
  textState.shadowColor = snapshot.textState.shadowColor ?? "#000000";
  textPanelMode.value = snapshot.textPanelMode ?? "text";
  selectedStampLabel.value = snapshot.selectedStampLabel ?? "";
  activePanelTool.value = snapshot.activePanelTool ?? null;
  activeTool.value = snapshot.activeTool;
  isRestoringPreview = false;
}

function captureImageHistoryEntry(kind: HistoryStepKind = "original"): ImageHistoryEntry | null {
  if (!image.value) return null;
  const canvas = document.createElement("canvas");
  canvas.width = imageSize.width;
  canvas.height = imageSize.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(image.value, 0, 0, canvas.width, canvas.height);
  return {
    kind,
    label: kind === "filter" ? `${t("filters")} - ${t(filterTranslationKeys[settings.filter])}` : undefined,
    dataUrl: canvas.toDataURL("image/png"),
    name: imageName.value,
    width: imageSize.width,
    height: imageSize.height,
    zoom: settings.zoom,
    previewSnapshot: capturePreviewSnapshot(),
    committedCornerRadius: committedCornerRadius.value,
    committedCornerRadiusX: committedCornerRadiusX.value,
    committedCornerRadiusY: committedCornerRadiusY.value,
    committedFilter: committedFilter.value,
    committedColorReplace: { ...committedColorReplace },
    committedAdjustments: { ...committedAdjustments },
    committedText: { ...committedText },
    committedBorderState: { ...committedBorderState },
    committedBorderLayers: committedBorderLayers.map((layer) => ({ ...layer })),
    committedShadowState: { ...committedShadowState },
    committedResizeRotateDegrees: committedResizeRotateDegrees.value,
    committedResizeFlipHorizontal: committedResizeFlipHorizontal.value,
    committedResizeFlipVertical: committedResizeFlipVertical.value,
  };
}

function pushHistoryEntry(entry: ImageHistoryEntry) {
  if (rebuildingHistory.value) return;
  entry.protectedOverlayMaskDataUrl = protectedOverlayMask?.toDataURL("image/png");
  previewHistory.value.push(entry);
  redoHistory.value = [];
}

async function restoreImageHistoryEntry(entry: ImageHistoryEntry) {
  const nextImage = new Image();
  nextImage.decoding = "async";
  nextImage.src = entry.dataUrl;
  await nextImage.decode();
  await restoreProtectedOverlayMask(entry.protectedOverlayMaskDataUrl);
  setCurrentImage(nextImage, entry.dataUrl, entry.name, { preserveZoom: true, preservePreviewScale: true, resetCornerRadius: false });
  restorePreviewSnapshot(entry.previewSnapshot);
  committedCornerRadius.value = entry.committedCornerRadius;
  committedCornerRadiusX.value = entry.committedCornerRadiusX ?? entry.committedCornerRadius;
  committedCornerRadiusY.value = entry.committedCornerRadiusY ?? entry.committedCornerRadius;
  committedFilter.value = entry.committedFilter;
  const restoredColorReplace = entry.committedColorReplace;
  Object.assign(committedColorReplace, {
    targetColor: restoredColorReplace?.targetColor ?? entry.previewSnapshot.settings.colorReplaceTarget ?? "#ffffff",
    replacementColor: restoredColorReplace?.replacementColor ?? entry.previewSnapshot.settings.colorReplaceReplacement ?? "#000000",
    strength: restoredColorReplace?.strength ?? entry.previewSnapshot.settings.colorReplaceStrength ?? 20,
    smoothness: restoredColorReplace?.smoothness ?? entry.previewSnapshot.settings.colorReplaceSmoothness ?? 0,
    targetTransparent: restoredColorReplace?.targetTransparent ?? entry.previewSnapshot.settings.colorReplaceTargetTransparent ?? false,
    replacementTransparent: restoredColorReplace?.replacementTransparent ?? entry.previewSnapshot.settings.colorReplaceReplacementTransparent ?? false,
  });
  committedAdjustments.contrast = entry.committedAdjustments.contrast;
  committedAdjustments.brightness = entry.committedAdjustments.brightness;
  committedAdjustments.saturation = entry.committedAdjustments.saturation;
  committedAdjustments.colorBalance = entry.committedAdjustments.colorBalance;
  committedAdjustments.sharpen = entry.committedAdjustments.sharpen ?? 0;
  committedAdjustments.soften = entry.committedAdjustments.soften ?? 0;
  committedAdjustments.transparency = entry.committedAdjustments.transparency ?? 0;
  committedAdjustments.transparencyIncludeBorder = entry.committedAdjustments.transparencyIncludeBorder ?? false;
  committedText.content = entry.committedText.content;
  committedText.color = entry.committedText.color;
  committedText.size = entry.committedText.size;
  committedText.rotation = entry.committedText.rotation ?? 0;
  committedText.x = entry.committedText.x;
  committedText.y = entry.committedText.y;
  committedText.shadowSize = entry.committedText.shadowSize ?? 8;
  committedText.shadowDarkness = entry.committedText.shadowDarkness ?? 35;
  committedText.shadowColor = entry.committedText.shadowColor ?? "#000000";
  committedBorderState.borderThickness = entry.committedBorderState.borderThickness;
  committedBorderState.borderColor = entry.committedBorderState.borderColor;
  committedBorderState.borderUseGradient = entry.committedBorderState.borderUseGradient;
  committedBorderState.borderGradientType = normalizeBorderGradientType(entry.committedBorderState.borderGradientType);
  committedBorderState.borderGradientAngle = entry.committedBorderState.borderGradientAngle;
  committedBorderState.borderGradientStart = entry.committedBorderState.borderGradientStart;
  committedBorderState.borderGradientEnd = entry.committedBorderState.borderGradientEnd;
  committedBorderLayers.splice(0, committedBorderLayers.length, ...entry.committedBorderLayers.map((layer) => ({ ...layer })));
  committedShadowState.shadowDarkness = entry.committedShadowState.shadowDarkness;
  committedShadowState.shadowSize = entry.committedShadowState.shadowSize;
  committedShadowState.shadowColor = entry.committedShadowState.shadowColor;
  committedShadowState.shadowDirection = entry.committedShadowState.shadowDirection;
  committedShadowState.contentWidth = entry.committedShadowState.contentWidth ?? 0;
  committedShadowState.contentHeight = entry.committedShadowState.contentHeight ?? 0;
  committedShadowState.contentX = entry.committedShadowState.contentX ?? 0;
  committedShadowState.contentY = entry.committedShadowState.contentY ?? 0;
  committedResizeRotateDegrees.value = entry.committedResizeRotateDegrees ?? entry.previewSnapshot.committedResizeRotateDegrees ?? 0;
  committedResizeFlipHorizontal.value = entry.committedResizeFlipHorizontal ?? entry.previewSnapshot.committedResizeFlipHorizontal ?? false;
  committedResizeFlipVertical.value = entry.committedResizeFlipVertical ?? entry.previewSnapshot.committedResizeFlipVertical ?? false;
  imageSize.width = entry.width;
  imageSize.height = entry.height;
  crop.x = 0;
  crop.y = 0;
  crop.width = entry.width;
  crop.height = entry.height;
  committedCrop.x = 0;
  committedCrop.y = 0;
  committedCrop.width = entry.width;
  committedCrop.height = entry.height;
  resizeBox.x = 0;
  resizeBox.y = 0;
  resizeBox.width = entry.width;
  resizeBox.height = entry.height;
  render();
}

const autosaveDatabaseName = "cutmypic-projects";
const autosaveStoreName = "autosaves";
const autosaveProjectKey = "current-project";
const savedWorkflowsKey = "saved-workflows";
const autosavePreviewHistoryLimit = 10;
const autosaveRedoHistoryLimit = 4;
let autosaveDatabasePromise: Promise<IDBDatabase> | null = null;
let autosaveEntryCache = new WeakMap<ImageHistoryEntry, Promise<StoredImageHistoryEntry>>();
let autosaveTimer: number | null = null;
let autosaveNoticeTimer: number | null = null;
let autosaveReady = false;
let autosaveRestoring = false;
let autosaveWriteInProgress = false;
let autosaveWriteQueued = false;

const autosaveRecoveredMessages: Record<LanguageCode, string> = {
  en: "Recovered your autosaved project.",
  es: "Se recuperó tu proyecto guardado automáticamente.",
  fr: "Votre projet enregistré automatiquement a été récupéré.",
  de: "Ihr automatisch gespeichertes Projekt wurde wiederhergestellt.",
  ja: "自動保存されたプロジェクトを復元しました。",
  zh: "已恢复自动保存的项目。",
  ko: "자동 저장된 프로젝트를 복구했습니다.",
  hi: "आपका अपने-आप सहेजा गया प्रोजेक्ट पुनर्प्राप्त कर लिया गया है।",
  pt: "Seu projeto salvo automaticamente foi recuperado.",
  it: "Il progetto salvato automaticamente è stato ripristinato.",
  ru: "Автоматически сохраненный проект восстановлен.",
  ar: "تمت استعادة مشروعك المحفوظ تلقائيًا.",
  nl: "Uw automatisch opgeslagen project is hersteld.",
  pl: "Przywrócono automatycznie zapisany projekt.",
  tr: "Otomatik kaydedilen projeniz geri yüklendi.",
  id: "Proyek yang disimpan otomatis telah dipulihkan.",
  ms: "Projek yang disimpan secara automatik telah dipulihkan.",
  vi: "Đã khôi phục dự án được tự động lưu.",
  th: "กู้คืนโปรเจกต์ที่บันทึกอัตโนมัติแล้ว",
};

const comparisonMessages: Record<LanguageCode, { compare: string; before: string; after: string }> = {
  en: { compare: "Compare original and edited image", before: "Before", after: "After" },
  es: { compare: "Comparar la imagen original y la editada", before: "Antes", after: "Después" },
  fr: { compare: "Comparer l’image originale et l’image modifiée", before: "Avant", after: "Après" },
  de: { compare: "Original und bearbeitetes Bild vergleichen", before: "Vorher", after: "Nachher" },
  ja: { compare: "元画像と編集後の画像を比較", before: "編集前", after: "編集後" },
  zh: { compare: "比较原图和编辑后的图像", before: "编辑前", after: "编辑后" },
  ko: { compare: "원본과 편집한 이미지 비교", before: "이전", after: "이후" },
  hi: { compare: "मूल और संपादित छवि की तुलना करें", before: "पहले", after: "बाद में" },
  pt: { compare: "Comparar a imagem original e a editada", before: "Antes", after: "Depois" },
  it: { compare: "Confronta l’immagine originale e quella modificata", before: "Prima", after: "Dopo" },
  ru: { compare: "Сравнить оригинал и отредактированное изображение", before: "До", after: "После" },
  ar: { compare: "مقارنة الصورة الأصلية بالصورة المعدلة", before: "قبل", after: "بعد" },
  nl: { compare: "Originele en bewerkte afbeelding vergelijken", before: "Voor", after: "Na" },
  pl: { compare: "Porównaj obraz oryginalny i edytowany", before: "Przed", after: "Po" },
  tr: { compare: "Orijinal ve düzenlenmiş görüntüyü karşılaştır", before: "Önce", after: "Sonra" },
  id: { compare: "Bandingkan gambar asli dan hasil edit", before: "Sebelum", after: "Sesudah" },
  ms: { compare: "Bandingkan imej asal dan imej yang disunting", before: "Sebelum", after: "Selepas" },
  vi: { compare: "So sánh ảnh gốc và ảnh đã chỉnh sửa", before: "Trước", after: "Sau" },
  th: { compare: "เปรียบเทียบภาพต้นฉบับและภาพที่แก้ไข", before: "ก่อน", after: "หลัง" },
};

const autosaveRecoveredMessage = computed(() => autosaveRecoveredMessages[language.value]);
const comparisonCopy = computed(() => comparisonMessages[language.value]);

function openAutosaveDatabase() {
  if (autosaveDatabasePromise) return autosaveDatabasePromise;
  autosaveDatabasePromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = window.indexedDB.open(autosaveDatabaseName, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(autosaveStoreName)) {
        request.result.createObjectStore(autosaveStoreName);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Unable to open autosave storage."));
  });
  return autosaveDatabasePromise;
}

async function putAutosavedProject(project: AutosavedProject) {
  const database = await openAutosaveDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(autosaveStoreName, "readwrite");
    transaction.objectStore(autosaveStoreName).put(project, autosaveProjectKey);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("Unable to save project."));
    transaction.onabort = () => reject(transaction.error ?? new Error("Project autosave was aborted."));
  });
}

async function readAutosavedProject() {
  const database = await openAutosaveDatabase();
  return new Promise<AutosavedProject | undefined>((resolve, reject) => {
    const transaction = database.transaction(autosaveStoreName, "readonly");
    const request = transaction.objectStore(autosaveStoreName).get(autosaveProjectKey);
    request.onsuccess = () => resolve(request.result as AutosavedProject | undefined);
    request.onerror = () => reject(request.error ?? new Error("Unable to read autosaved project."));
  });
}

async function clearAutosavedProject() {
  if (autosaveTimer != null) {
    window.clearTimeout(autosaveTimer);
    autosaveTimer = null;
  }
  const database = await openAutosaveDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(autosaveStoreName, "readwrite");
    transaction.objectStore(autosaveStoreName).delete(autosaveProjectKey);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("Unable to clear autosaved project."));
  });
}

async function readSavedWorkflows() {
  const database = await openAutosaveDatabase();
  return new Promise<SavedWorkflow[]>((resolve, reject) => {
    const transaction = database.transaction(autosaveStoreName, "readonly");
    const request = transaction.objectStore(autosaveStoreName).get(savedWorkflowsKey);
    request.onsuccess = () => resolve(Array.isArray(request.result) ? request.result as SavedWorkflow[] : []);
    request.onerror = () => reject(request.error ?? new Error("Unable to read saved workflows."));
  });
}

async function persistSavedWorkflows() {
  const database = await openAutosaveDatabase();
  const storedWorkflows = JSON.parse(JSON.stringify(savedWorkflows.value)) as SavedWorkflow[];
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(autosaveStoreName, "readwrite");
    transaction.objectStore(autosaveStoreName).put(storedWorkflows, savedWorkflowsKey);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("Unable to save workflows."));
    transaction.onabort = () => reject(transaction.error ?? new Error("Saving workflows was aborted."));
  });
}

async function loadSavedWorkflows() {
  try {
    savedWorkflows.value = (await readSavedWorkflows()).sort((a, b) => b.updatedAt - a.updatedAt);
  } catch (error) {
    console.warn("Cut My Pic could not load saved workflows.", error);
  }
}

async function serializeHistoryEntry(entry: ImageHistoryEntry) {
  const cached = autosaveEntryCache.get(entry);
  if (cached) return cached;
  const serialized = (async (): Promise<StoredImageHistoryEntry> => {
    const { dataUrl, protectedOverlayMaskDataUrl, ...metadata } = entry;
    const imageBlob = await (await fetch(dataUrl)).blob();
    const protectedOverlayMaskBlob = protectedOverlayMaskDataUrl
      ? await (await fetch(protectedOverlayMaskDataUrl)).blob()
      : undefined;
    return {
      ...JSON.parse(JSON.stringify(metadata)),
      imageBlob,
      protectedOverlayMaskBlob,
    } as StoredImageHistoryEntry;
  })();
  autosaveEntryCache.set(entry, serialized);
  return serialized;
}

function blobToDataUrl(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("Unable to restore saved image data."));
    reader.readAsDataURL(blob);
  });
}

async function deserializeHistoryEntry(entry: StoredImageHistoryEntry): Promise<ImageHistoryEntry> {
  const { imageBlob, protectedOverlayMaskBlob, ...metadata } = entry;
  return {
    ...metadata,
    dataUrl: await blobToDataUrl(imageBlob),
    protectedOverlayMaskDataUrl: protectedOverlayMaskBlob ? await blobToDataUrl(protectedOverlayMaskBlob) : undefined,
  };
}

function boundedPreviewHistory(entries: ImageHistoryEntry[]) {
  if (entries.length <= autosavePreviewHistoryLimit) return entries;
  return [entries[0], ...entries.slice(-(autosavePreviewHistoryLimit - 1))];
}

async function buildAutosavedProject(latestOnly = false): Promise<AutosavedProject | null> {
  const latestEntry = previewHistory.value[previewHistory.value.length - 1];
  if (!latestEntry) return null;
  const previewEntries = latestOnly ? [latestEntry] : boundedPreviewHistory(previewHistory.value);
  const redoEntries = latestOnly ? [] : redoHistory.value.slice(0, autosaveRedoHistoryLimit);
  const storedPreviewHistory: StoredImageHistoryEntry[] = [];
  const storedRedoHistory: StoredImageHistoryEntry[] = [];
  for (const entry of previewEntries) storedPreviewHistory.push(await serializeHistoryEntry(entry));
  for (const entry of redoEntries) storedRedoHistory.push(await serializeHistoryEntry(entry));
  const workingSnapshot = capturePreviewSnapshot();
  if (comparisonMode.value) {
    workingSnapshot.activeTool = suspendedToolForComparison;
    workingSnapshot.activePanelTool = suspendedPanelForComparison;
  }
  return {
    version: 1,
    savedAt: Date.now(),
    previewHistory: storedPreviewHistory,
    redoHistory: storedRedoHistory,
    workingSnapshot,
  };
}

async function persistAutosavedProject() {
  if (!autosaveReady || autosaveRestoring || !previewHistory.value.length) return;
  if (autosaveWriteInProgress) {
    autosaveWriteQueued = true;
    return;
  }
  autosaveWriteInProgress = true;
  try {
    const project = await buildAutosavedProject();
    if (project) await putAutosavedProject(project);
  } catch (error) {
    if (error instanceof DOMException && (error.name === "QuotaExceededError" || error.name === "UnknownError")) {
      try {
        await clearAutosavedProject();
        const fallbackProject = await buildAutosavedProject(true);
        if (fallbackProject) await putAutosavedProject(fallbackProject);
      } catch (fallbackError) {
        console.warn("Cut My Pic could not autosave this project.", fallbackError);
      }
    } else {
      console.warn("Cut My Pic could not autosave this project.", error);
    }
  } finally {
    autosaveWriteInProgress = false;
    if (autosaveWriteQueued) {
      autosaveWriteQueued = false;
      void persistAutosavedProject();
    }
  }
}

function scheduleAutosave(delay = 700) {
  if (!autosaveReady || autosaveRestoring || !previewHistory.value.length) return;
  if (autosaveTimer != null) window.clearTimeout(autosaveTimer);
  autosaveTimer = window.setTimeout(() => {
    autosaveTimer = null;
    void persistAutosavedProject();
  }, delay);
}

async function restoreAutosavedProject() {
  try {
    const savedProject = await readAutosavedProject();
    if (!savedProject || savedProject.version !== 1 || !savedProject.previewHistory.length) return false;
    autosaveRestoring = true;
    const restoredPreviewHistory: ImageHistoryEntry[] = [];
    const restoredRedoHistory: ImageHistoryEntry[] = [];
    for (const entry of savedProject.previewHistory) restoredPreviewHistory.push(await deserializeHistoryEntry(entry));
    for (const entry of savedProject.redoHistory) restoredRedoHistory.push(await deserializeHistoryEntry(entry));
    const latestEntry = restoredPreviewHistory[restoredPreviewHistory.length - 1];
    if (!latestEntry) return false;
    previewHistory.value = restoredPreviewHistory;
    redoHistory.value = restoredRedoHistory;
    await restoreImageHistoryEntry(latestEntry);
    restorePreviewSnapshot(savedProject.workingSnapshot ?? latestEntry.previewSnapshot);
    await nextTick();
    render();
    autosaveRecoveryNoticeOpen.value = true;
    autosaveNoticeTimer = window.setTimeout(() => {
      autosaveRecoveryNoticeOpen.value = false;
      autosaveNoticeTimer = null;
    }, 5000);
    return true;
  } catch (error) {
    console.warn("Cut My Pic could not restore the autosaved project.", error);
    try {
      await clearAutosavedProject();
    } catch {
      // Keep startup usable even if browser storage is unavailable.
    }
    return false;
  } finally {
    autosaveRestoring = false;
  }
}

function flushAutosave() {
  if (!autosaveReady || autosaveRestoring || !previewHistory.value.length) return;
  if (autosaveTimer != null) {
    window.clearTimeout(autosaveTimer);
    autosaveTimer = null;
  }
  void persistAutosavedProject();
}

function localizedHistoryEntryLabel(entry: ImageHistoryEntry) {
  if (entry.kind === "crop") {
    if (entry.isMaskEdit) {
      const maskMode = entry.previewSnapshot.cropMaskMode;
      return maskMode === "color"
        ? `${t("overlayColor")} - ${t("imageMasking")}`
        : `${t("blur")} - ${t("imageMasking")}`;
    }
    const cropStep = entry.workflowStep?.kind === "crop" ? entry.workflowStep : null;
    const mode = cropStep?.mode ?? entry.previewSnapshot.cropMode;
    if (mode === "circle") return t("circleCut");
    if (mode === "heart") return t("heartCut");
    if (mode === "star") return t("starCut");
    return t("crop");
  }
  if (entry.kind === "filter") {
    const filterStep = entry.workflowStep?.kind === "filter" ? entry.workflowStep : null;
    const filter = filterStep?.filter ?? entry.committedFilter ?? entry.previewSnapshot.settings.filter;
    return `${t("filters")} - ${t(filterTranslationKeys[filter])}`;
  }
  if (entry.kind === "adjustments") {
    const adjustmentStep = entry.workflowStep?.kind === "adjustments" ? entry.workflowStep : null;
    if (adjustmentStep) {
      return adjustmentHistoryLabel({
        ...adjustmentStep,
        transparency: adjustmentStep.transparency ?? 0,
        transparencyIncludeBorder: adjustmentStep.transparencyIncludeBorder ?? false,
      });
    }
  }
  if (entry.kind === "text") {
    const textStep = entry.workflowStep?.kind === "text" ? entry.workflowStep : null;
    if (textStep?.panelMode === "stamp") return `${t("stampMode")} - ${textStep.stampLabel || t("stampMode")}`;
    if (textStep) return t("text");
  }
  return entry.label ?? t(historyStepLabelKeys[entry.kind]);
}

const historyEntries = computed(() => {
  const makeSwatchColor = (entry: ImageHistoryEntry) =>
    entry.kind === "crop" && (entry.isMaskEdit || entry.label === "Color mask") && entry.committedCropMaskColor
      ? entry.committedCropMaskColor
      : null;
  const previewEntries = previewHistory.value.map((entry, index) => ({
    index,
    label: localizedHistoryEntryLabel(entry),
    kind: entry.kind,
    swatchColor: makeSwatchColor(entry),
    isOriginal: index === 0,
    isHovered: hoveredHistoryIndex.value === index,
    isAfterHovered: hoveredHistoryIndex.value !== null && index > hoveredHistoryIndex.value,
    isRedo: false,
  }));
  const redoEntries = redoHistory.value.map((entry, redoIndex) => {
    const index = previewHistory.value.length + redoIndex;
    return {
      index,
      label: localizedHistoryEntryLabel(entry),
      kind: entry.kind,
      swatchColor: makeSwatchColor(entry),
      isOriginal: false,
      isHovered: false,
      isAfterHovered: false,
      isRedo: true,
    };
  });
  return [...previewEntries, ...redoEntries];
});

const historyStepCount = computed(() => Math.max(0, previewHistory.value.length - 1));

function cloneWorkflowStep(step: WorkflowStep) {
  return JSON.parse(JSON.stringify(step)) as WorkflowStep;
}

function deriveWorkflowStep(entry: ImageHistoryEntry, previousEntry?: ImageHistoryEntry): WorkflowStep | null {
  if (entry.workflowStep) return cloneWorkflowStep(entry.workflowStep);
  const previousMinDimension = Math.max(1, Math.min(previousEntry?.width ?? entry.width, previousEntry?.height ?? entry.height));
  const entryMinDimension = Math.max(1, Math.min(entry.width, entry.height));
  if (entry.kind === "crop") {
    if (entry.isMaskEdit || entry.label === "Blur mask" || entry.label === "Color mask") return null;
    return {
      kind: "crop",
      mode: entry.previewSnapshot.cropMode ?? "free",
      selectedPresetId: entry.previewSnapshot.selectedCropPresetId ?? "common-free",
      aspect: entry.width / Math.max(1, entry.height),
    };
  }
  if (entry.kind === "corners") {
    return { kind: "corners", radiusRatio: entry.committedCornerRadius / entryMinDimension };
  }
  if (entry.kind === "border") {
    const layer = entry.committedBorderLayers[entry.committedBorderLayers.length - 1] ?? entry.committedBorderState;
    return {
      kind: "border",
      thicknessRatio: layer.borderThickness / previousMinDimension,
      state: {
        borderColor: layer.borderColor,
        borderUseGradient: layer.borderUseGradient,
        borderGradientType: normalizeBorderGradientType(layer.borderGradientType),
        borderGradientAngle: layer.borderGradientAngle,
        borderGradientStart: layer.borderGradientStart,
        borderGradientEnd: layer.borderGradientEnd,
      },
    };
  }
  if (entry.kind === "shadow") {
    return {
      kind: "shadow",
      darkness: entry.committedShadowState.shadowDarkness,
      sizeRatio: entry.committedShadowState.shadowSize / previousMinDimension,
      color: entry.committedShadowState.shadowColor,
      direction: entry.committedShadowState.shadowDirection,
    };
  }
  if (entry.kind === "filter" && entry.committedFilter !== "none") {
    return {
      kind: "filter",
      filter: entry.committedFilter,
      colorReplace: entry.committedFilter === "colorreplace"
        ? { ...(entry.committedColorReplace ?? committedColorReplace) }
        : undefined,
    };
  }
  if (entry.kind === "text") {
    const placedText = entry.committedText;
    const panelMode = entry.label?.startsWith("Stamp") ? "stamp" : entry.previewSnapshot.textPanelMode ?? "text";
    return {
      kind: "text",
      panelMode,
      stampLabel: entry.previewSnapshot.selectedStampLabel ?? "",
      content: placedText.content,
      color: placedText.color,
      sizeRatio: placedText.size / entryMinDimension,
      rotation: placedText.rotation,
      x: placedText.x,
      y: placedText.y,
      shadowSizeRatio: placedText.shadowSize / entryMinDimension,
      shadowDarkness: placedText.shadowDarkness,
      shadowColor: placedText.shadowColor,
    };
  }
  if (entry.kind === "resize") {
    return {
      kind: "resize",
      width: entry.width,
      height: entry.height,
      rotation: entry.committedResizeRotateDegrees ?? 0,
      flipHorizontal: entry.committedResizeFlipHorizontal ?? false,
      flipVertical: entry.committedResizeFlipVertical ?? false,
    };
  }
  return null;
}

const reusableWorkflowSteps = computed(() => {
  const steps = previewHistory.value.slice(1).map((entry, index) => deriveWorkflowStep(entry, previewHistory.value[index])).filter((step): step is WorkflowStep => step !== null);
  const cropStep = steps.find((step) => step.kind === "crop");
  return cropStep ? [cropStep, ...steps.filter((step) => step.kind !== "crop")] : steps;
});

function workflowStepLabel(step: WorkflowStep) {
  if (step.kind === "crop") return t("crop");
  if (step.kind === "corners") return t("corners");
  if (step.kind === "border") return t("border");
  if (step.kind === "shadow") return t("shadow");
  if (step.kind === "filter") return t(filterTranslationKeys[step.filter]);
  if (step.kind === "adjustments") return t("adjustments");
  if (step.kind === "resize") return `${t("resizeOnly")} ${step.width} × ${step.height}`;
  return step.panelMode === "stamp" ? step.stampLabel || t("stampMode") : t("text");
}

function workflowSummary(workflow: SavedWorkflow) {
  return workflow.steps.map(workflowStepLabel).join(" · ");
}

function openWorkflowSaveDialog() {
  if (!reusableWorkflowSteps.value.length) return;
  workflowName.value = `Workflow ${savedWorkflows.value.length + 1}`;
  workflowSaveDialogOpen.value = true;
}

async function saveCurrentWorkflow() {
  const name = workflowName.value.trim();
  if (!name || !reusableWorkflowSteps.value.length) return;
  const now = Date.now();
  const workflow: SavedWorkflow = {
    id: window.crypto.randomUUID?.() ?? `workflow-${now}-${Math.random().toString(36).slice(2)}`,
    name,
    createdAt: now,
    updatedAt: now,
    steps: reusableWorkflowSteps.value.map(cloneWorkflowStep),
  };
  savedWorkflows.value = [workflow, ...savedWorkflows.value];
  await persistSavedWorkflows();
  workflowSaveDialogOpen.value = false;
  workflowLibraryOpen.value = true;
}

async function deleteSavedWorkflow(workflowId: string) {
  savedWorkflows.value = savedWorkflows.value.filter((workflow) => workflow.id !== workflowId);
  await persistSavedWorkflows();
}

function openHistoryPanel() {
  if (!hasImage.value) return;
  if (isMobileViewport.value) {
    showTopSettings.value = false;
    showLanguageMenu.value = false;
    activePanelTool.value = null;
    mobileToolMenuOpen.value = false;
    mobileQuickActionsOpen.value = false;
    mobileZoomOpen.value = false;
    mobileViewBackgroundOpen.value = false;
  }
  positionHistoryPanel(true);
  historyPanelOpen.value = true;
}

function closeHistoryPanel() {
  historyPanelOpen.value = false;
  hoveredHistoryIndex.value = null;
}

function toggleMobileHistoryPanel() {
  if (historyPanelOpen.value) {
    closeHistoryPanel();
    return;
  }
  openHistoryPanel();
}

function setHistoryHover(index: number | null) {
  hoveredHistoryIndex.value = index;
}

async function rebuildImageWithoutHistoryStep(baseEntry: ImageHistoryEntry, laterEntries: ImageHistoryEntry[]) {
  const steps = laterEntries
    .map((entry, stepIndex) => deriveWorkflowStep(entry, stepIndex === 0 ? baseEntry : laterEntries[stepIndex - 1]))
    .filter((step): step is WorkflowStep => step !== null);

  rebuildingHistory.value = true;
  workflowApplying.value = true;
  try {
    await restoreImageHistoryEntry(baseEntry);
    const cropStep = steps.find((step): step is Extract<WorkflowStep, { kind: "crop" }> => step.kind === "crop");
    if (cropStep) {
      activeTool.value = "crop";
      positionWorkflowCrop(cropStep);
      await applyCropToImage();
    }
    for (const step of steps) {
      if (step.kind !== "crop") await applyWorkflowStep(step, true);
    }
  } finally {
    workflowApplying.value = false;
    rebuildingHistory.value = false;
    activeTool.value = null;
    activePanelTool.value = null;
    await nextTick();
    render();
  }
}

function deleteHistoryEntry(index: number, isRedo: boolean) {
  if (isRedo) {
    const redoIndex = index - previewHistory.value.length;
    if (redoIndex >= 0 && redoIndex < redoHistory.value.length) redoHistory.value.splice(redoIndex, 1);
  } else {
    // Keep the original image as the stable root of the history. Removing a
    // later entry only removes that record; subsequent entries and the
    // later records remain intact. Move the canvas to the point immediately
    // before the removed edit so the deletion is visible right away.
    if (index <= 0 || index >= previewHistory.value.length) return;
    const baseEntry = previewHistory.value[index - 1];
    const laterEntries = previewHistory.value.slice(index + 1);
    const remainingEntries = previewHistory.value.filter((_, entryIndex) => entryIndex !== index);
    previewHistory.value = remainingEntries;
    if (baseEntry) void rebuildImageWithoutHistoryStep(baseEntry, laterEntries);
  }
  hoveredHistoryIndex.value = null;
  scheduleAutosave();
}

const canvasMetrics = reactive({
  cssWidth: 0,
  cssHeight: 0,
  imageX: 0,
  imageY: 0,
  imageWidth: 0,
  imageHeight: 0,
  scale: 1,
});

const floatingPanel = reactive({
  x: 0,
  y: 0,
});

const historyPanelPosition = reactive({
  x: 0,
  y: 0,
});

const floatingPanelBounds = reactive({
  maxHeight: 0,
});

let resizeObserver: ResizeObserver | null = null;
let activePointer:
  | {
      pointerId: number;
      captureTarget: HTMLElement;
      mode: DragMode;
      startX: number;
      startY: number;
      startCrop: Crop;
    }
  | null = null;
let resizePointer:
  | {
      pointerId: number;
      captureTarget: HTMLElement;
      mode: DragMode;
      startX: number;
      startY: number;
      startBox: Crop;
    }
  | null = null;
let floatingPointer:
  | {
      pointerId: number;
      startX: number;
      startY: number;
      panelX: number;
      panelY: number;
    }
  | null = null;
let historyPointer:
  | {
      pointerId: number;
      startX: number;
      startY: number;
      panelX: number;
      panelY: number;
    }
  | null = null;
let imagePanPointer:
  | {
      pointerId: number;
      captureTarget: HTMLElement;
      startX: number;
      startY: number;
      panX: number;
      panY: number;
    }
  | null = null;
let suspendedToolForPan: ToolId | null = null;
let suspendedPanelForPan: typeof activePanelTool.value = null;
let dropFlashTimer: number | null = null;
let unsupportedFileNoticeTimer: number | null = null;

function onWindowResize() {
  updateViewportFlags();
  render();
  updateFloatingPanelBounds();
  positionFloatingPanel();
  positionHistoryPanel();
  positionCropTutorial();
}

function isVisibleTutorialTarget(target: HTMLElement | null | undefined): target is HTMLElement {
  if (!target) return false;
  const rect = target.getBoundingClientRect();
  const style = window.getComputedStyle(target);
  return rect.width > 0 && rect.height > 0 && style.display !== "none" && style.visibility !== "hidden";
}

function getCropTutorialRailCommitTarget() {
  return document.querySelector<HTMLElement>("[data-crop-tutorial-commit='rail']");
}

function getCropTutorialToolsTarget() {
  return Array.from(document.querySelectorAll<HTMLElement>("[data-crop-tutorial-filter-selector]")).find(isVisibleTutorialTarget) ?? null;
}

function positionCropTutorial() {
  if (cropTutorialStep.value !== "handles" && cropTutorialStep.value !== "scissors" && cropTutorialStep.value !== "tools") return;

  const target = cropTutorialStep.value === "handles"
    ? cropTutorialHandleRef.value
    : cropTutorialStep.value === "scissors"
      ? [getCropTutorialRailCommitTarget(), cropTutorialPanelCommitRef.value].find(isVisibleTutorialTarget) ?? null
      : getCropTutorialToolsTarget();
  if (!isVisibleTutorialTarget(target)) return;

  const rect = target.getBoundingClientRect();
  const targetX = rect.left + rect.width / 2;
  const targetY = rect.top + rect.height / 2;
  const viewportPadding = 12;
  const cardWidth = Math.min(320, window.innerWidth - viewportPadding * 2);
  const estimatedCardHeight = cropTutorialStep.value === "handles" ? 172 : cropTutorialStep.value === "tools" ? 174 : 152;
  const targetIsOnLeft = targetX < Math.max(150, window.innerWidth * 0.24);
  let cardLeft = targetIsOnLeft ? targetX + 64 : targetX - cardWidth - 64;
  let cardTop = targetY - estimatedCardHeight / 2;
  if (cropTutorialStep.value === "tools") cardLeft += 36;

  cardLeft = clamp(cardLeft, viewportPadding, window.innerWidth - cardWidth - viewportPadding);
  cardTop = clamp(cardTop, viewportPadding, window.innerHeight - estimatedCardHeight - viewportPadding);
  cropTutorialCardStyle.value = {
    left: `${Math.round(cardLeft)}px`,
    top: `${Math.round(cardTop)}px`,
    width: `${Math.round(cardWidth)}px`,
  };
  cropTutorialPointerFacesLeft.value = targetIsOnLeft;
  const arrowGap = cropTutorialStep.value === "scissors" ? 17 : cropTutorialStep.value === "tools" ? 13 : 9;
  cropTutorialPointerStyle.value = {
    // Stop the arrowhead just short of the control so it does not cover the target.
    left: `${Math.round(targetX + (targetIsOnLeft ? -12 + arrowGap : -36 - arrowGap))}px`,
    top: `${Math.round(targetY - 36 - arrowGap)}px`,
  };
}

function closeCropTutorial() {
  if (cropTutorialFinalDismissArmed) {
    window.removeEventListener("pointerdown", closeFinalCropTutorialOnInteraction, true);
    cropTutorialFinalDismissArmed = false;
  }
  cropTutorialStep.value = "hidden";
  cropTutorialHandleDragStarted = false;
}

function closeFinalCropTutorialOnInteraction() {
  if (cropTutorialStep.value === "tools") closeCropTutorial();
}

function dismissCropTutorialForever() {
  window.localStorage.setItem(cropTutorialStorageKey, "1");
  cropTutorialDismissed.value = true;
  closeCropTutorial();
}

async function startCropTutorial() {
  if (cropTutorialDismissed.value || isMobileViewport.value) return;
  if (cropTutorialStarted) {
    if (cropTutorialStep.value === "handles") {
      await nextTick();
      positionCropTutorial();
    }
    return;
  }
  cropTutorialStarted = true;
  cropTutorialStep.value = "handles";
  await nextTick();
  positionCropTutorial();
}

async function showCropTutorialScissors() {
  cropTutorialStep.value = "scissors";
  await nextTick();
  if (!isVisibleTutorialTarget(getCropTutorialRailCommitTarget()) && isMobileViewport.value) {
    activePanelTool.value = "crop";
    await nextTick();
    positionFloatingPanel();
  }
  await nextTick();
  positionCropTutorial();
}

async function showCropTutorialTools() {
  cropTutorialStep.value = "tools";
  activePanelTool.value = null;
  mobileToolMenuOpen.value = false;
  showTool("filters");
  await nextTick();
  if (activePanelTool.value === "filters") positionFloatingPanel();
  await nextTick();
  positionCropTutorial();
  window.requestAnimationFrame(() => {
    if (cropTutorialStep.value !== "tools" || cropTutorialFinalDismissArmed) return;
    cropTutorialFinalDismissArmed = true;
    window.addEventListener("pointerdown", closeFinalCropTutorialOnInteraction, { capture: true, once: true });
  });
}

function applyCurrentToolAction() {
  if (!hasImage.value) return false;
  if (isMobileViewport.value && mobileBackMode.value && !mobileApplyAvailable.value) {
    mobileBackMode.value = false;
    stepBackPreview();
    return true;
  }
  if (activeTool.value === "crop" && cropPending.value) {
    if (isMobileViewport.value) mobileBackMode.value = true;
    void applyCropToImage();
    return true;
  }
  if (activeTool.value === "corners" && cornerPending.value) {
    if (isMobileViewport.value) mobileBackMode.value = true;
    void applyRoundedCornersToImage();
    return true;
  }
  if (activeTool.value === "border" && borderPending.value) {
    if (isMobileViewport.value) mobileBackMode.value = true;
    void applyBorderToImage();
    return true;
  }
  if (activeTool.value === "shadow" && shadowPending.value) {
    if (isMobileViewport.value) mobileBackMode.value = true;
    void applyShadowToImage();
    return true;
  }
  if (activeTool.value === "resize" && resizePending.value) {
    if (isMobileViewport.value) mobileBackMode.value = true;
    void applyResizeToImage();
    return true;
  }
  if (activeTool.value === "filters" && filterPending.value) {
    if (isMobileViewport.value) mobileBackMode.value = true;
    void applyFilterToImage();
    return true;
  }
  if (activeTool.value === "adjustments" && adjustmentsPending.value) {
    if (isMobileViewport.value) mobileBackMode.value = true;
    void applyAdjustmentsToImage();
    return true;
  }
  if (activeTool.value === "text" && textPending.value) {
    if (isMobileViewport.value) mobileBackMode.value = true;
    void applyTextToImage();
    return true;
  }
  return false;
}

function onDocumentKeydown(event: KeyboardEvent) {
  const modifier = event.metaKey || event.ctrlKey;
  const shift = event.shiftKey;
  const target = event.target as HTMLElement | null;
  const tagName = target?.tagName?.toLowerCase();
  const isTypingField = tagName === "input" || tagName === "textarea" || target?.isContentEditable;
  const isComposingText = event.isComposing || event.key === "Process" || event.keyCode === 229;

  if (isComposingText) return;

  if (event.key === "Escape" && saveToolDialogOpen.value) {
    event.preventDefault();
    saveToolDialogOpen.value = false;
    return;
  }
  if (event.key === "Escape" && saveToolOfferVisible.value) {
    event.preventDefault();
    dismissSaveToolOffer();
    return;
  }
  if (event.key === "Escape" && historyPanelOpen.value) {
    event.preventDefault();
    closeHistoryPanel();
    return;
  }
  if (event.key === "Escape" && stampPositionMode.value) {
    event.preventDefault();
    cancelStampPosition();
    return;
  }
  if (event.key === "Escape" && textPositionMode.value) {
    event.preventDefault();
    cancelTextPosition();
    return;
  }
  if (event.key === "Escape" && exportSavePromptOpen.value) {
    event.preventDefault();
    exportSavePromptOpen.value = false;
    exportDialogOpen.value = true;
    return;
  }
  if (event.key === "Escape" && exportDialogOpen.value) {
    event.preventDefault();
    exportDialogOpen.value = false;
    return;
  }
  if (event.key === "Escape" && comparisonMode.value) {
    event.preventDefault();
    exitComparisonMode();
    return;
  }
  if (event.key === "Escape" && fullscreenMode.value) {
    event.preventDefault();
    void toggleFullscreenMode();
    return;
  }
  if (modifier && shift && event.key.toLowerCase() === "z") {
    if (!hasImage.value || !redoHistory.value.length) return;
    event.preventDefault();
    stepForwardPreview();
    return;
  }
  if (modifier && event.key.toLowerCase() === "s") {
    if (!hasImage.value) return;
    event.preventDefault();
    exportPng();
    return;
  }
  if (modifier && shift && event.key.toLowerCase() === "h") {
    if (!hasImage.value) return;
    event.preventDefault();
    openHistoryPanel();
    return;
  }
  if (modifier && event.key.toLowerCase() === "z") {
    if (!hasImage.value || previewHistory.value.length < 2) return;
    event.preventDefault();
    stepBackPreview();
    return;
  }
  if (event.key === "Enter" && !modifier && !isTypingField && applyCurrentToolAction()) {
    event.preventDefault();
    return;
  }
  if (modifier && event.key.toLowerCase() === "enter" && applyCurrentToolAction()) {
    event.preventDefault();
    return;
  }
  if (isTypingField) return;
  if (modifier) return;
  if (event.key >= "1" && event.key <= "8") {
    if (!hasImage.value) return;
    event.preventDefault();
    const shortcutTool = ["crop", "corners", "shadow", "resize", "filters", "adjustments", "text", "border"][Number(event.key) - 1] as ToolId | undefined;
    if (shortcutTool) showTool(shortcutTool);
  }
}

function preloadLogos() {
  [cmpLogo, cmpLogoDark].forEach((src) => {
    const image = new Image();
    image.src = src;
  });
}

function triggerDropFlash() {
  dropFlashVisible.value = false;
  if (dropFlashTimer !== null) window.clearTimeout(dropFlashTimer);
  void nextTick(() => {
    dropFlashVisible.value = true;
    dropFlashTimer = window.setTimeout(() => {
      dropFlashVisible.value = false;
      dropFlashTimer = null;
    }, 1000);
  });
}

function detectLanguage(): LanguageCode {
  const pathLanguage = detectPathState().language;
  if (pathLanguage) return pathLanguage;
  const storedLanguage = readLocalStorage("cutmypic-language")?.toLowerCase();
  if (storedLanguage === "zh") return "zh";
  if (storedLanguage && storedLanguage in translations) return storedLanguage as LanguageCode;
  return detectBrowserLanguage();
}

function detectPathState(pathname = currentPathname()): AppPathState {
  const segments = pathname.split("/").filter(Boolean).map((segment) => segment.toLowerCase());
  if (segments[0] === "guide") return { language: "en", isManual: false, isFaq: false, isGuide: true };
  if (segments[0] === "manual") return { language: "en", isManual: true, isFaq: false };
  const pathLanguage = segments[0];
  if (pathLanguage === "zh") {
    return { language: "zh", isManual: segments[1] === "manual", isFaq: segments[1] === "faq", isGuide: segments[1] === "guide" };
  }
  if (pathLanguage === "faq") return { language: "en", isManual: false, isFaq: true };
  if (pathLanguage && pathLanguage in translations) {
    return { language: pathLanguage as LanguageCode, isManual: segments[1] === "manual", isFaq: segments[1] === "faq", isGuide: segments[1] === "guide" };
  }
  return { language: "en", isManual: segments[0] === "manual", isFaq: segments[0] === "faq", isGuide: segments[0] === "guide" };
}

function detectBrowserLanguage(): LanguageCode {
  if (!isBrowser) return "en";
  const browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const language of browserLanguages) {
    const normalized = language.toLowerCase();
    const baseLanguage = normalized.split("-")[0];
    if (baseLanguage === "zh") return "zh";
    if (baseLanguage in translations) return baseLanguage as LanguageCode;
  }
  return "en";
}

function applyDocumentLanguage(nextLanguage: LanguageCode) {
  document.documentElement.lang = nextLanguage === "zh" ? "zh-Hans" : nextLanguage;
  document.documentElement.dir = nextLanguage === "ar" ? "rtl" : "ltr";
}

function getLocalizedUrl(nextLanguage: LanguageCode) {
  return `${siteBaseUrl}${nextLanguage === "en" ? "/" : `/${nextLanguage}/`}`;
}

function getLocalizedManualUrl(nextLanguage: LanguageCode) {
  return `${siteBaseUrl}${nextLanguage === "en" ? "/manual/" : `/${nextLanguage}/manual/`}`;
}

function getLocalizedFaqUrl(nextLanguage: LanguageCode) {
  return `${siteBaseUrl}${nextLanguage === "en" ? "/faq/" : `/${nextLanguage}/faq/`}`;
}

function getLocalizedGuideUrl(nextLanguage: LanguageCode) {
  return `${siteBaseUrl}${nextLanguage === "en" ? "/guide/" : `/${nextLanguage}/guide/`}`;
}

function getCurrentLocalizedPageUrl(nextLanguage: LanguageCode) {
  if (isManualPage.value) return getLocalizedManualUrl(nextLanguage);
  if (isFaqPage.value) return getLocalizedFaqUrl(nextLanguage);
  if (isGuidePage.value) return getLocalizedGuideUrl(nextLanguage);
  return getLocalizedUrl(nextLanguage);
}

function setLanguageOverride(nextLanguage: LanguageCode | "auto") {
  if (nextLanguage === "auto") {
    window.localStorage.removeItem("cutmypic-language");
    languageSelection.value = "auto";
    language.value = detectBrowserLanguage();
    const nextPath = isManualPage.value
      ? getLocalizedManualUrl(language.value)
      : isFaqPage.value
        ? getLocalizedFaqUrl(language.value)
        : isGuidePage.value
          ? getLocalizedGuideUrl(language.value)
          : getLocalizedUrl(language.value);
    window.history.replaceState({}, "", nextPath);
    routePath.value = window.location.pathname;
    showTopSettings.value = false;
    showLanguageMenu.value = false;
    return;
  }
  window.localStorage.setItem("cutmypic-language", nextLanguage);
  languageSelection.value = nextLanguage;
  language.value = nextLanguage;
  const nextPath = isManualPage.value
    ? getLocalizedManualUrl(nextLanguage)
    : isFaqPage.value
      ? getLocalizedFaqUrl(nextLanguage)
      : isGuidePage.value
        ? getLocalizedGuideUrl(nextLanguage)
        : getLocalizedUrl(nextLanguage);
  window.history.replaceState({}, "", nextPath);
  routePath.value = window.location.pathname;
  showTopSettings.value = false;
  showLanguageMenu.value = false;
}

const showTopSettings = ref(false);
const showLanguageMenu = ref(false);
const routePath = ref(currentPathname());
const manualJumpSelection = ref("");
const isManualPage = computed(() => detectPathState(routePath.value).isManual);
const isFaqPage = computed(() => detectPathState(routePath.value).isFaq);
const isGuidePage = computed(() => detectPathState(routePath.value).isGuide ?? false);
const manualSections = [
  { id: "intro", labelKey: "manualOverview", icon: Target, hideTitle: true },
  { id: "crop", labelKey: "crop", icon: Scissors },
  { id: "corners", labelKey: "corners", icon: Radius },
  { id: "resize", labelKey: "resize", icon: Maximize2 },
  { id: "filters", labelKey: "filters", icon: Palette },
  { id: "adjustments", labelKey: "adjustments", icon: SlidersVertical },
  { id: "text", labelKey: "text", icon: Type },
  { id: "border", labelKey: "border", icon: SquareUser },
  { id: "shadow", labelKey: "shadow", icon: Moon },
  { id: "zoom", labelKey: "zoom", icon: SlidersHorizontal },
  { id: "compare", icon: Columns2 },
  { id: "center", labelKey: "center", icon: Target },
  { id: "manual-move", icon: Move },
  { id: "fullscreen", icon: Maximize2 },
  { id: "undo", labelKey: "undo", icon: Undo2 },
  { id: "history", labelKey: "editHistory", icon: FolderOpen },
  { id: "delete-image", icon: Trash2 },
  { id: "workflows", icon: Save },
  { id: "shortcuts", labelKey: "keyboardShortcuts", icon: Menu },
  { id: "export", labelKey: "export", icon: Download },
] as const;

type ManualContentSection = {
  id: string;
  titleKey?: keyof typeof translations.en | "manualOverview";
  title?: string;
  hideTitle?: boolean;
  body: string[];
};

const workspaceManualControlCopy: Record<LanguageCode, {
  center: { title: string; body: string[] };
  move: { title: string; body: string[] };
  deleteImage: { title: string; body: string[] };
}> = {
  en: {
    center: { title: "Auto-Center", body: ["Auto-Center determines whether applied edits return the image to the center. It is on by default; when off, the target icon turns red and the manual screen position is retained. This changes only the editor view."] },
    move: { title: "Manual Move", body: ["Turn on the four-arrow Move button, then drag with a mouse, trackpad, or touch gesture. Move turns Auto-Center off; click Move again, select a tool, or re-enable Auto-Center to leave this mode. The exported image is unchanged."] },
    deleteImage: { title: "Delete Image", body: ["The trash-can button beside Edit History removes the current image, its edit history, and its locally saved recovery copy. This cannot be undone through Edit History, so download anything you want to keep first."] },
  },
  es: {
    center: { title: "Centrado automático", body: ["El centrado automático decide si la imagen vuelve al centro después de aplicar una edición. Está activado de forma predeterminada; al desactivarlo, el icono se vuelve rojo y se conserva la posición manual. Solo afecta a la vista del editor."] },
    move: { title: "Movimiento manual", body: ["Activa el botón Mover de cuatro flechas y arrastra con el ratón, el panel táctil o un gesto táctil. Mover desactiva el centrado automático; vuelve a pulsarlo, elige una herramienta o reactiva el centrado para salir. La imagen exportada no cambia."] },
    deleteImage: { title: "Eliminar imagen", body: ["El botón de papelera junto al Historial elimina la imagen actual, su historial y la copia de recuperación guardada localmente. No se puede deshacer desde el Historial; descarga antes todo lo que quieras conservar."] },
  },
  fr: {
    center: { title: "Centrage automatique", body: ["Le centrage automatique détermine si l’image revient au centre après l’application d’une modification. Il est activé par défaut ; lorsqu’il est désactivé, l’icône devient rouge et la position manuelle est conservée. Seule la vue de l’éditeur est affectée."] },
    move: { title: "Déplacement manuel", body: ["Activez le bouton Déplacer à quatre flèches, puis faites glisser l’image avec la souris, le pavé tactile ou un geste tactile. Ce mode désactive le centrage automatique ; cliquez à nouveau, choisissez un outil ou réactivez le centrage pour le quitter. L’image exportée reste inchangée."] },
    deleteImage: { title: "Supprimer l’image", body: ["Le bouton Corbeille à côté de l’Historique supprime l’image actuelle, son historique et sa copie de récupération locale. Cette action ne peut pas être annulée dans l’Historique ; téléchargez d’abord tout élément à conserver."] },
  },
  de: {
    center: { title: "Automatisch zentrieren", body: ["Automatisch zentrieren legt fest, ob das Bild nach einer angewendeten Bearbeitung in die Mitte zurückkehrt. Die Funktion ist standardmäßig aktiv; ausgeschaltet wird das Symbol rot und die manuelle Position bleibt erhalten. Dies betrifft nur die Editoransicht."] },
    move: { title: "Manuell verschieben", body: ["Aktiviere die Schaltfläche mit den vier Pfeilen und ziehe das Bild mit Maus, Trackpad oder Touch-Geste. Dadurch wird die automatische Zentrierung ausgeschaltet. Klicke erneut, wähle ein Werkzeug oder aktiviere die Zentrierung, um den Modus zu verlassen. Der Export bleibt unverändert."] },
    deleteImage: { title: "Bild löschen", body: ["Die Papierkorb-Schaltfläche neben dem Bearbeitungsverlauf entfernt das aktuelle Bild, seinen Verlauf und die lokal gespeicherte Wiederherstellungskopie. Dies kann nicht über den Verlauf rückgängig gemacht werden; lade vorher alles herunter, was du behalten möchtest."] },
  },
  ja: {
    center: { title: "自動中央揃え", body: ["自動中央揃えは、編集を適用した後に画像を中央へ戻すかを設定します。初期状態ではオンです。オフにするとアイコンが赤くなり、手動の表示位置が維持されます。書き出し画像には影響しません。"] },
    move: { title: "手動移動", body: ["4方向矢印の移動ボタンをオンにし、マウス、トラックパッド、またはタッチ操作で画像をドラッグします。移動を有効にすると自動中央揃えはオフになります。もう一度移動を押す、編集ツールを選ぶ、または自動中央揃えをオンにすると終了します。"] },
    deleteImage: { title: "画像を削除", body: ["編集履歴の横にあるゴミ箱ボタンは、現在の画像、編集履歴、およびローカル保存された復元用コピーを削除します。編集履歴から元に戻せないため、必要な画像は先にダウンロードしてください。"] },
  },
  zh: {
    center: { title: "自动居中", body: ["自动居中决定应用编辑后图片是否回到中央。默认开启；关闭后图标会变红，并保留手动设置的显示位置。它只影响编辑器视图，不影响导出文件。"] },
    move: { title: "手动移动", body: ["开启四向箭头移动按钮，然后使用鼠标、触控板或触摸手势拖动图片。移动模式会关闭自动居中；再次点击移动、选择编辑工具或重新开启自动居中即可退出。导出图片不会改变。"] },
    deleteImage: { title: "删除图片", body: ["编辑历史旁的垃圾桶按钮会删除当前图片、编辑历史以及本地保存的恢复副本。此操作无法通过编辑历史撤销，请先下载需要保留的内容。"] },
  },
  ko: {
    center: { title: "자동 가운데 정렬", body: ["자동 가운데 정렬은 편집을 적용한 뒤 이미지를 중앙으로 되돌릴지 설정합니다. 기본값은 켬이며, 끄면 아이콘이 빨간색으로 바뀌고 수동 화면 위치가 유지됩니다. 내보낸 파일에는 영향을 주지 않습니다."] },
    move: { title: "수동 이동", body: ["네 방향 화살표 이동 버튼을 켠 다음 마우스, 트랙패드 또는 터치 동작으로 이미지를 드래그하세요. 이동을 켜면 자동 가운데 정렬이 꺼집니다. 이동을 다시 누르거나 편집 도구를 선택하거나 자동 가운데 정렬을 켜면 종료됩니다."] },
    deleteImage: { title: "이미지 삭제", body: ["편집 기록 옆의 휴지통 버튼은 현재 이미지, 편집 기록 및 로컬로 저장된 복구 사본을 삭제합니다. 편집 기록으로 되돌릴 수 없으므로 보관할 이미지는 먼저 다운로드하세요."] },
  },
  hi: {
    center: { title: "स्वतः केंद्रित करें", body: ["स्वतः केंद्रित करें यह तय करता है कि संपादन लागू होने के बाद छवि केंद्र में लौटेगी या नहीं। यह डिफ़ॉल्ट रूप से चालू है; बंद होने पर आइकन लाल हो जाता है और मैन्युअल स्थिति बनी रहती है। इससे केवल संपादक दृश्य बदलता है।"] },
    move: { title: "मैन्युअल मूव", body: ["चार तीर वाला मूव बटन चालू करें, फिर माउस, ट्रैकपैड या स्पर्श से छवि खींचें। मूव स्वतः केंद्रित करना बंद करता है; बाहर निकलने के लिए मूव फिर दबाएँ, कोई टूल चुनें या स्वतः केंद्रित करना चालू करें। निर्यात नहीं बदलता।"] },
    deleteImage: { title: "छवि हटाएँ", body: ["संपादन इतिहास के पास कूड़ेदान बटन वर्तमान छवि, उसका इतिहास और स्थानीय रिकवरी कॉपी हटाता है। इसे इतिहास से पूर्ववत नहीं किया जा सकता, इसलिए जो रखना हो उसे पहले डाउनलोड करें।"] },
  },
  pt: {
    center: { title: "Centralização automática", body: ["A centralização automática define se a imagem volta ao centro após aplicar uma edição. Ela vem ativada; quando desativada, o ícone fica vermelho e a posição manual é mantida. Isso altera apenas a visualização do editor."] },
    move: { title: "Movimento manual", body: ["Ative o botão Mover com quatro setas e arraste usando mouse, trackpad ou toque. Mover desativa a centralização automática; clique novamente, escolha uma ferramenta ou reative a centralização para sair. A imagem exportada não muda."] },
    deleteImage: { title: "Excluir imagem", body: ["O botão de lixeira ao lado do Histórico remove a imagem atual, seu histórico e a cópia de recuperação salva localmente. Isso não pode ser desfeito pelo Histórico; baixe antes tudo o que deseja manter."] },
  },
  it: {
    center: { title: "Centratura automatica", body: ["La centratura automatica stabilisce se l’immagine torna al centro dopo aver applicato una modifica. È attiva per impostazione predefinita; quando è disattivata, l’icona diventa rossa e la posizione manuale viene mantenuta. Cambia solo la vista dell’editor."] },
    move: { title: "Spostamento manuale", body: ["Attiva il pulsante Sposta con quattro frecce e trascina con mouse, trackpad o tocco. Sposta disattiva la centratura automatica; fai di nuovo clic, scegli uno strumento o riattiva la centratura per uscire. L’immagine esportata non cambia."] },
    deleteImage: { title: "Elimina immagine", body: ["Il pulsante del cestino accanto alla Cronologia elimina l’immagine corrente, la cronologia e la copia di recupero salvata localmente. Non può essere annullato dalla Cronologia; scarica prima ciò che vuoi conservare."] },
  },
  ru: {
    center: { title: "Автоцентрирование", body: ["Автоцентрирование определяет, возвращается ли изображение в центр после применения правки. По умолчанию оно включено; при отключении значок становится красным, а ручное положение сохраняется. Меняется только вид в редакторе."] },
    move: { title: "Ручное перемещение", body: ["Включите кнопку с четырьмя стрелками и перетаскивайте изображение мышью, трекпадом или касанием. Перемещение отключает автоцентрирование; нажмите кнопку снова, выберите инструмент или включите автоцентрирование, чтобы выйти. Экспорт не меняется."] },
    deleteImage: { title: "Удалить изображение", body: ["Кнопка корзины рядом с Историей удаляет текущее изображение, историю и локальную резервную копию. Это нельзя отменить через Историю, поэтому сначала скачайте всё, что хотите сохранить."] },
  },
  ar: {
    center: { title: "التوسيط التلقائي", body: ["يحدد التوسيط التلقائي ما إذا كانت الصورة ستعود إلى الوسط بعد تطبيق تعديل. يكون مفعّلًا افتراضيًا؛ وعند إيقافه يصبح الرمز أحمر ويُحتفظ بالموضع اليدوي. يؤثر ذلك في عرض المحرر فقط."] },
    move: { title: "التحريك اليدوي", body: ["فعّل زر التحريك ذي الأسهم الأربعة ثم اسحب الصورة بالماوس أو لوحة اللمس أو اللمس المباشر. يؤدي ذلك إلى إيقاف التوسيط التلقائي؛ اضغط الزر مجددًا أو اختر أداة أو أعد تشغيل التوسيط للخروج. لا تتغير الصورة المصدّرة."] },
    deleteImage: { title: "حذف الصورة", body: ["يحذف زر سلة المهملات بجوار سجل التعديلات الصورة الحالية وسجلها ونسخة الاسترداد المحفوظة محليًا. لا يمكن التراجع عن ذلك من السجل، لذا نزّل أولًا ما تريد الاحتفاظ به."] },
  },
  nl: {
    center: { title: "Automatisch centreren", body: ["Automatisch centreren bepaalt of de afbeelding na een toegepaste bewerking naar het midden terugkeert. Het staat standaard aan; wanneer het uitstaat wordt het pictogram rood en blijft de handmatige positie behouden. Alleen de editorweergave verandert."] },
    move: { title: "Handmatig verplaatsen", body: ["Schakel de knop met vier pijlen in en sleep met muis, trackpad of aanraking. Verplaatsen schakelt automatisch centreren uit; klik opnieuw, kies een gereedschap of zet centreren weer aan om af te sluiten. De export verandert niet."] },
    deleteImage: { title: "Afbeelding verwijderen", body: ["De prullenbakknop naast Bewerkingsgeschiedenis verwijdert de huidige afbeelding, de geschiedenis en de lokaal opgeslagen herstelkopie. Dit kan niet via de geschiedenis ongedaan worden gemaakt; download eerst wat je wilt bewaren."] },
  },
  pl: {
    center: { title: "Automatyczne centrowanie", body: ["Automatyczne centrowanie określa, czy obraz wraca na środek po zastosowaniu edycji. Domyślnie jest włączone; po wyłączeniu ikona staje się czerwona, a ręczne położenie zostaje zachowane. Zmienia się tylko widok edytora."] },
    move: { title: "Ręczne przesuwanie", body: ["Włącz przycisk z czterema strzałkami i przeciągnij obraz myszą, gładzikiem lub dotykiem. Przesuwanie wyłącza automatyczne centrowanie; kliknij ponownie, wybierz narzędzie lub włącz centrowanie, aby wyjść. Eksport pozostaje bez zmian."] },
    deleteImage: { title: "Usuń obraz", body: ["Przycisk kosza obok Historii usuwa bieżący obraz, jego historię i lokalnie zapisaną kopię odzyskiwania. Nie można tego cofnąć przez Historię, dlatego najpierw pobierz wszystko, co chcesz zachować."] },
  },
  tr: {
    center: { title: "Otomatik ortalama", body: ["Otomatik ortalama, bir düzenleme uygulandıktan sonra görselin merkeze dönüp dönmeyeceğini belirler. Varsayılan olarak açıktır; kapatıldığında simge kırmızı olur ve elle belirlenen konum korunur. Yalnızca editör görünümü değişir."] },
    move: { title: "Elle taşıma", body: ["Dört oklu Taşı düğmesini açın ve fare, izleme dörtgeni veya dokunmayla görseli sürükleyin. Taşı otomatik ortalamayı kapatır; çıkmak için tekrar tıklayın, bir araç seçin veya ortalamayı açın. Dışa aktarılan görsel değişmez."] },
    deleteImage: { title: "Görseli sil", body: ["Düzenleme Geçmişi yanındaki çöp kutusu düğmesi geçerli görseli, geçmişini ve yerel kurtarma kopyasını siler. Bu işlem Geçmişten geri alınamaz; saklamak istediklerinizi önce indirin."] },
  },
  id: {
    center: { title: "Pusat otomatis", body: ["Pusat otomatis menentukan apakah gambar kembali ke tengah setelah edit diterapkan. Fitur ini aktif secara default; saat dimatikan, ikon menjadi merah dan posisi manual dipertahankan. Hanya tampilan editor yang berubah."] },
    move: { title: "Pindah manual", body: ["Aktifkan tombol Pindah berikon empat panah, lalu seret dengan mouse, trackpad, atau sentuhan. Pindah mematikan pusat otomatis; klik lagi, pilih alat, atau aktifkan kembali pusat otomatis untuk keluar. Hasil ekspor tidak berubah."] },
    deleteImage: { title: "Hapus gambar", body: ["Tombol tempat sampah di sebelah Riwayat menghapus gambar saat ini, riwayatnya, dan salinan pemulihan lokal. Tindakan ini tidak dapat dibatalkan melalui Riwayat; unduh dahulu semua yang ingin disimpan."] },
  },
  ms: {
    center: { title: "Pusat automatik", body: ["Pusat automatik menentukan sama ada imej kembali ke tengah selepas suntingan digunakan. Ia hidup secara lalai; apabila dimatikan, ikon menjadi merah dan kedudukan manual dikekalkan. Hanya paparan penyunting berubah."] },
    move: { title: "Alih manual", body: ["Hidupkan butang Alih berikon empat anak panah, kemudian seret dengan tetikus, pad jejak atau sentuhan. Alih mematikan pusat automatik; klik sekali lagi, pilih alat atau hidupkan semula pusat automatik untuk keluar. Imej eksport tidak berubah."] },
    deleteImage: { title: "Padam imej", body: ["Butang tong sampah di sebelah Sejarah Suntingan memadam imej semasa, sejarahnya dan salinan pemulihan setempat. Tindakan ini tidak boleh dibuat asal melalui sejarah; muat turun dahulu semua yang mahu disimpan."] },
  },
  vi: {
    center: { title: "Tự động căn giữa", body: ["Tự động căn giữa quyết định ảnh có trở về giữa sau khi áp dụng chỉnh sửa hay không. Tính năng này bật mặc định; khi tắt, biểu tượng chuyển đỏ và vị trí thủ công được giữ lại. Chỉ khung xem của trình chỉnh sửa thay đổi."] },
    move: { title: "Di chuyển thủ công", body: ["Bật nút Di chuyển có bốn mũi tên rồi kéo ảnh bằng chuột, bàn di chuột hoặc thao tác chạm. Di chuyển sẽ tắt tự động căn giữa; nhấn lại, chọn công cụ hoặc bật lại căn giữa để thoát. Ảnh xuất không thay đổi."] },
    deleteImage: { title: "Xóa ảnh", body: ["Nút thùng rác cạnh Lịch sử chỉnh sửa sẽ xóa ảnh hiện tại, lịch sử và bản khôi phục lưu cục bộ. Không thể hoàn tác qua Lịch sử, vì vậy hãy tải xuống mọi thứ cần giữ trước."] },
  },
  th: {
    center: { title: "จัดกึ่งกลางอัตโนมัติ", body: ["การจัดกึ่งกลางอัตโนมัติกำหนดว่ารูปจะกลับมาตรงกลางหลังใช้การแก้ไขหรือไม่ โดยเปิดไว้เป็นค่าเริ่มต้น เมื่อปิด ไอคอนจะเป็นสีแดงและคงตำแหน่งที่ย้ายไว้ มีผลเฉพาะมุมมองในตัวแก้ไขเท่านั้น"] },
    move: { title: "ย้ายด้วยตนเอง", body: ["เปิดปุ่มย้ายรูปไอคอนลูกศรสี่ทิศ แล้วลากด้วยเมาส์ แทร็กแพด หรือการสัมผัส การย้ายจะปิดการจัดกึ่งกลางอัตโนมัติ กดอีกครั้ง เลือกเครื่องมือ หรือเปิดการจัดกึ่งกลางเพื่อออก ภาพที่ส่งออกไม่เปลี่ยนแปลง"] },
    deleteImage: { title: "ลบรูปภาพ", body: ["ปุ่มถังขยะข้างประวัติการแก้ไขจะลบรูปปัจจุบัน ประวัติ และสำเนากู้คืนที่บันทึกไว้ในเครื่อง ไม่สามารถเลิกทำผ่านประวัติได้ โปรดดาวน์โหลดสิ่งที่ต้องการเก็บไว้ก่อน"] },
  },
};

const manualContentByLanguage: Partial<Record<
  LanguageCode,
  ManualContentSection[]
>> = {
  en: [
  {
    id: "intro",
    titleKey: "manualOverview",
    hideTitle: true,
    body: [
      "Cut My Pic is a fast, private image editor that runs entirely in your browser. It is built for quick edits that still need to look polished: crop and mask, rounded corners, borders, shadows, resize, color tweaks, sharpen, soften, text overlays, stamps, filters, and export.",
      "Because everything happens locally, you can drag in an image, fine-tune it, review your edit history, switch to fullscreen when needed, and download the result without sending your file to a server. That keeps the workflow simple, fast, and 100% private.",
    ],
  },
  {
    id: "crop",
    titleKey: "crop",
    body: [
      "The Crop tool lets you choose the part of the image you want to keep. The side rail offers free select, square, or 16:9 crop modes.",
      "The settings panel gives you two behaviors: Image Trimmer removes everything outside the selected area, while Image Masking keeps the full image and fills the selected region with blur or a solid color.",
      "Use the Apply button when you want to commit the crop result. If you switch tools without applying, the crop preview resets so it is always clear what is committed and what is still temporary.",
    ],
  },
  {
    id: "corners",
    titleKey: "corners",
    body: [
      "Round Corners softens the outer corners of the entire image. The quick presets are shortcuts for common values, and the slider lets you fine-tune the radius.",
      "This tool is useful for profile images, cards, thumbnails, and anywhere you want a softer finish. Apply it when the shape looks right; otherwise, reset it to return to a square image.",
    ],
  },
  {
    id: "border",
    titleKey: "border",
    body: [
      "Add Border places a border around the image. You can set the thickness, choose a border color, and optionally turn on a gradient border.",
      "When the gradient border is enabled, the angle controls the direction of the gradient and the start and end colors can be adjusted separately. Apply the border when the preview looks right so it becomes part of the exported image.",
    ],
  },
  {
    id: "shadow",
    titleKey: "shadow",
    body: [
      "Shadow adds a drop shadow to separate the image from the background. More darkness creates more contrast; the size control determines how soft and spread out the shadow appears.",
      "The color picker changes the shadow tone, and the direction controls where the shadow falls. It is useful for mockups, product images, and social media graphics.",
    ],
  },
  {
    id: "zoom",
    titleKey: "zoom",
    body: [
      "The Zoom slider changes the magnification of the workspace preview without changing the image's pixel dimensions or exported size. Move it right to inspect details and left to see more of the canvas.",
      "The percentage readout shows the current zoom level. Zoom and manual positioning affect only the editor view, not the downloaded image.",
    ],
  },
  {
    id: "compare",
    title: "Compare",
    body: [
      "Compare becomes available after you apply at least one edit. It opens a split view with the original image on the Before side and the current applied result on the After side.",
      "Drag the divider to reveal more of either version. You can also focus the divider and use the arrow keys, Home, or End for precise movement. Turning Compare off restores the tool you were using before the comparison.",
    ],
  },
  {
    id: "center",
    title: "Auto-Center",
    body: [
      "Auto-Center controls whether the image returns to the center of the workspace after an edit is applied. It is turned on by default.",
      "Click the target-shaped button to turn Auto-Center off. The icon and border turn red while it is off, and applied edits retain the image's manual screen position. Click it again to turn Auto-Center on and immediately return the image to its default centered position.",
      "Auto-Center changes only the workspace view. It does not move content within the image or alter the exported file.",
    ],
  },
  {
    id: "manual-move",
    title: "Manual Move",
    body: [
      "Manual Move lets you reposition the image anywhere in the workspace with a mouse, trackpad, or touch gesture. Click the four-arrow Move button to activate it, then drag the image to the position you want.",
      "Activating Manual Move turns Auto-Center off so the position is retained when edits are applied. Click Move again, select an editing tool, or turn Auto-Center back on to leave Manual Move mode.",
      "Manual positioning affects only the workspace view. It does not change the image pixels or the exported result.",
    ],
  },
  {
    id: "fullscreen",
    title: "Fullscreen",
    body: [
      "Fullscreen expands the editing workspace so more of the image and canvas are visible. The compact tool rail and floating settings panels remain available, and the tool rail can be repositioned by dragging its handle.",
      "Your image, applied edits, history, zoom, and open tool stay with you when entering or leaving Fullscreen. Use the Fullscreen button again, or press Escape, to return to the standard layout.",
    ],
  },
  {
    id: "undo",
    titleKey: "undo",
    body: [
      "Undo moves back by one applied edit. Use the Undo button or Cmd/Ctrl+Z; use Cmd/Ctrl+Shift+Z to redo an edit that you stepped back from.",
      "Temporary previews are not history states until you press Apply. If you undo and then apply a different edit, that new result becomes the next history state instead of the previously available redo path.",
    ],
  },
  {
    id: "history",
    titleKey: "editHistory",
    body: [
      "Edit History is available as soon as an image is loaded. State #0 is the original image, and every numbered state after it represents one applied edit. The step counter reports applied edits and does not count the original image.",
      "Select any state to restore the image to that point. Later states remain in the panel as redo states, so you can move forward again until you apply a new edit. Each state restores the image pixels and the settings needed to continue from that result.",
      "The footer also shows the Reusable Step Counter. This can be lower than the history count because it includes only the steps that can be saved as part of a reusable workflow.",
    ],
  },
  {
    id: "delete-image",
    title: "Delete Image",
    body: [
      "The trash-can button beside Edit History completely removes the current image from Cut My Pic. It also clears the image's edit history and the locally saved recovery copy from this browser.",
      "Use this when you are finished with an image or want to begin again with an empty editor. This action cannot be undone through Edit History, so download anything you want to keep before deleting it.",
    ],
  },
  {
    id: "workflows",
    title: "Reusable Workflows",
    body: [
      "A workflow saves reusable instructions from your applied edit history so you can repeat a look on another image. Open Edit History, choose Save Workflow, review the reusable-step count, give the workflow a name, and save it in your browser.",
      "Choose Load Workflow to view, apply, or delete saved workflows. Workflows are stored locally in this browser and are not uploaded with your images. Temporary previews and edits that are not included in the Reusable Step Counter are not saved in the workflow.",
      "Crop placement is never guessed on a new image. When a workflow includes a crop, the editor opens the crop tool and asks you to position the selection. If the workflow also includes a resize, the selector locks to the saved target ratio; after you apply the crop, the remaining steps run and the image is resized to the saved pixel dimensions.",
      "If the selected area is smaller than the workflow's resize target, the editor warns you before enlarging it because upscaling can reduce quality. When the workflow finishes, the preview changes to a 1:1 pixel view so you can inspect the result accurately.",
    ],
  },
  {
    id: "resize",
    titleKey: "resize",
    body: [
      "Resize & Rotate changes the image’s pixel dimensions and orientation. Use Constrain Proportions to keep the same aspect ratio, or Freeform if you need a custom stretch.",
      "The width and height fields work with the on-canvas resize handles, while the Rotate & Flip controls let you turn the image in 90° steps, rotate freely, or flip it vertically and horizontally before applying the result.",
    ],
  },
  {
    id: "filters",
    titleKey: "filters",
    body: [
      "Filters offers quick styles like black and white, sepia, vintage, warm, cool, and more.",
      "Use the preview to compare looks before applying. Once applied, the filter becomes part of the image and can be combined with your other edits.",
    ],
  },
  {
    id: "adjustments",
    titleKey: "adjustments",
    body: [
      "Adjustments lets you fine-tune contrast, brightness, color balance, saturation, sharpen, and soften. These controls are ideal when the image is already close but needs a little more punch, balance, or edge definition.",
      "Sharpen and soften preview live as you move the slider, so you can see the result before you apply it. Small changes can make a big difference, especially when you are preparing an image to share or publish.",
    ],
  },
  {
    id: "text",
    titleKey: "text",
    body: [
      "Text lets you place a title or label over the image. Start by typing the text and choosing a color, then use the floating panel to adjust the size and position.",
      "It also includes Stamp mode for preset graphics and labels, which is useful for titles, callouts, stickers, and quick social graphics.",
    ],
  },
  {
    id: "shortcuts",
    titleKey: "keyboardShortcuts",
    body: [
      "Cmd/Ctrl+Z steps back one preview edit from the current image history, and Cmd/Ctrl+Shift+Z steps forward again.",
      "Cmd/Ctrl+S opens export, Cmd/Ctrl+Shift+H opens history, and Cmd/Ctrl+Enter applies the current edit when a tool has an Apply button.",
      "1 through 8 switch quickly between Crop, Round Corners, Shadow, Resize, Filters, Adjustments, Text, and Add Border.",
      "Fullscreen mode keeps the tool rail and controls compact so you can work with more of the image visible at once.",
      "Escape closes the history panel, export dialogs, or fullscreen mode.",
    ],
  },
  {
    id: "export",
    titleKey: "export",
    body: [
      "Export creates your final PNG, JPG, or WebP and opens a download and sharing panel. PNG and WebP can preserve transparency; JPG fills transparent areas with your selected background color.",
      "If you still have unapplied changes in the current tool, the app will ask whether you want to save them before exporting. That way the final file matches what you actually intended.",
    ],
  },
  ],
  ja: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic は、ブラウザ内だけで動作する高速でプライベートな画像エディタです。トリミング、角丸、枠線、影、サイズ変更、色調整、テキスト、フィルター、書き出しなど、素早いけれど仕上がりもきれいに整えたい編集に向いています。",
        "すべての処理がローカルで行われるため、画像をドラッグして調整し、ファイルをサーバーへ送らずに結果をダウンロードできます。作業はシンプルで高速、そして 100% プライベートです。",
      ],
    },
    {
      id: "crop",
      titleKey: "crop",
      body: [
        "Crop ツールでは、残したい画像の部分を選べます。サイドバーでは、自由選択、正方形、16:9 のクロップを使えます。",
        "設定パネルでは 2 つの動作を切り替えられます。Image Trimmer は選択範囲の外側を削除し、Image Masking は画像全体を保持したまま、選択範囲をぼかしまたは単色で埋めます。",
        "結果を確定したいときは Apply を押してください。別のツールへ移動しても、未適用のままならプレビューはリセットされます。",
      ],
    },
    {
      id: "corners",
      titleKey: "corners",
      body: [
        "Round Corners は画像全体の外側の角をやわらかくします。クイック設定はよく使う値へのショートカットで、スライダーで半径を細かく調整できます。",
        "プロフィール画像、カード、サムネイルなど、やわらかい印象にしたい場面で便利です。形がよければ適用し、違和感があれば元に戻して四角い画像に戻せます。",
      ],
    },
    {
      id: "border",
      titleKey: "border",
      body: [
        "Add Border は画像のまわりに枠線を追加します。太さを設定し、枠線の色を選び、必要ならグラデーション枠線も使えます。",
        "グラデーションを有効にすると、角度が方向を決め、開始色と終了色を別々に調整できます。プレビューがよければ Apply して、書き出し画像に反映してください。",
      ],
    },
    {
      id: "shadow",
      titleKey: "shadow",
      body: [
        "Shadow は画像の背後にドロップシャドウを追加して、背景から浮かせて見せます。Darkness が高いほどコントラストが強くなり、Size で影の広がりと柔らかさを調整できます。",
        "Color で影の色味を変え、Direction で影の落ちる向きを決めます。モックアップ、商品画像、SNS 用の画像で特に便利です。",
      ],
    },
    {
      id: "resize",
      titleKey: "resize",
      body: [
        "Resize は画像のピクセルサイズを変更します。Constrain Proportions を使うと縦横比を保ち、Freeform を使うと自由に伸縮できます。",
        "幅と高さの入力欄、そしてキャンバス上の操作は連動しています。書き出し前に画像を拡大・縮小したい場合に使うツールです。",
      ],
    },
    {
      id: "filters",
      titleKey: "filters",
      body: [
        "Filters では、白黒、セピア、ビンテージ、暖色、寒色などのクイックスタイルを選べます。",
        "適用する前にプレビューで見比べられます。適用するとフィルターは画像の一部になり、他の編集と組み合わせられます。",
      ],
    },
    {
      id: "adjustments",
      titleKey: "adjustments",
      body: [
        "Adjustments では、コントラスト、明るさ、カラーバランス、彩度を細かく調整できます。すでにかなり良い画像に、もう少しだけ力強さやバランスを足したいときに便利です。",
        "小さな調整でも、共有や公開の前には大きな差になります。",
      ],
    },
    {
      id: "text",
      titleKey: "text",
      body: [
        "Text では、画像の上にタイトルやラベルを配置できます。まず文字を入力して色を選び、その後フローティングパネルでサイズと位置を調整します。",
        "タイトル、注釈、ラベル、SNS 用の簡単な画像作成に役立ちます。",
      ],
    },
    {
      id: "export",
      titleKey: "export",
      body: [
        "書き出しでは最終画像を PNG、JPG、WebP から選び、ダウンロードと共有のパネルを開きます。PNG と WebP は透明部分を保持でき、JPG は選択した背景色で透明部分を埋めます。",
        "現在のツールに未適用の変更が残っている場合は、書き出す前に保存するかどうかを確認します。これにより、最終ファイルが本当に意図した内容と一致します。",
      ],
    },
  ],
  es: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic es un editor de imágenes rápido y privado que funciona por completo en tu navegador. Está pensado para ediciones rápidas que aun así deben verse pulidas: recorte, esquinas redondeadas, bordes, sombras, cambio de tamaño, ajustes de color, texto, filtros y exportación.",
        "Como todo ocurre de forma local, puedes arrastrar una imagen, ajustarla y descargar el resultado sin enviar tu archivo a un servidor. Eso mantiene el flujo de trabajo simple, rápido y 100 % privado.",
      ],
    },
    {
      id: "crop",
      titleKey: "crop",
      body: [
        "La herramienta Recortar sirve para seleccionar la parte de la imagen que quieres conservar. La barra lateral te permite elegir un recorte libre, cuadrado o 16:9.",
        "El panel de ajustes ofrece dos comportamientos: Recorte de imagen elimina todo lo que queda fuera del área seleccionada, mientras que Máscara de imagen conserva la imagen completa y rellena la región seleccionada con desenfoque o un color sólido.",
        "Usa el botón Aplicar cuando quieras confirmar el resultado del recorte. Si cambias a otra herramienta sin aplicar, la vista previa del recorte se restablece para que siempre sepas qué está confirmado y qué sigue siendo temporal.",
      ],
    },
    {
      id: "corners",
      titleKey: "corners",
      body: [
        "Redondear esquinas te permite suavizar las esquinas exteriores de toda la imagen. Los ajustes rápidos son un atajo para valores comunes, y el control deslizante te permite afinar el radio.",
        "Esta herramienta es útil para imágenes de perfil, tarjetas, miniaturas y cualquier caso en el que quieras un acabado más suave. Aplícala cuando la forma se vea bien; si no, vuelve atrás para recuperar una imagen cuadrada.",
      ],
    },
    {
      id: "border",
      titleKey: "border",
      body: [
        "Agregar borde añade un borde alrededor de la imagen. Puedes ajustar el grosor, elegir un color de borde y, si quieres, activar un borde degradado.",
        "Cuando el borde degradado está activado, el ángulo controla la dirección del degradado y los colores de inicio y final pueden ajustarse por separado. Aplica el borde cuando la vista previa se vea bien para que forme parte de la imagen exportada.",
      ],
    },
    {
      id: "shadow",
      titleKey: "shadow",
      body: [
        "Sombra añade una sombra proyectada a la imagen para separarla del fondo. Cuanta más oscuridad, más contraste; el control de tamaño determina lo suave y extendida que se ve la sombra.",
        "El selector de color cambia el tono de la sombra, y la dirección controla hacia dónde cae. Es útil para maquetas, imágenes de producto y piezas para redes sociales.",
      ],
    },
    {
      id: "resize",
      titleKey: "resize",
      body: [
        "Redimensionar cambia las dimensiones en píxeles de la imagen. Usa Mantener proporciones para conservar la misma relación de aspecto, o Libre si necesitas una deformación personalizada.",
        "Los campos de ancho y alto y los controles de ajuste trabajan juntos. Si quieres escalar la imagen antes de exportarla, Redimensionar es la herramienta adecuada.",
      ],
    },
    {
      id: "filters",
      titleKey: "filters",
      body: [
        "Filtros ofrece estilos rápidos como blanco y negro, sepia, vintage, cálido, frío y más.",
        "Usa la vista previa para comparar estilos antes de aplicar. Una vez aplicado, el filtro pasa a formar parte de la imagen y puede combinarse con el resto de tus ediciones.",
      ],
    },
    {
      id: "adjustments",
      titleKey: "adjustments",
      body: [
        "Ajustes te permite ajustar contraste, brillo, balance de color y saturación. Estos controles son ideales cuando la imagen ya está bien pero necesita un poco más de fuerza o equilibrio.",
        "Los pequeños cambios pueden marcar una gran diferencia, especialmente cuando preparas una imagen para compartir o publicar.",
      ],
    },
    {
      id: "text",
      titleKey: "text",
      body: [
        "Texto te permite colocar un título o una etiqueta sobre la imagen. Empieza escribiendo el texto y eligiendo un color; después usa el panel flotante para ajustar el tamaño y la posición.",
        "Es útil para títulos, llamadas, etiquetas y gráficos rápidos para redes sociales.",
      ],
    },
    {
      id: "export",
      titleKey: "export",
      body: [
        "Exportar crea la imagen final en PNG, JPG o WebP y abre el panel de descarga y uso compartido. PNG y WebP pueden conservar la transparencia; JPG rellena las zonas transparentes con el color de fondo elegido.",
        "Si todavía tienes cambios sin aplicar en la herramienta actual, la app te preguntará si quieres guardarlos antes de exportar. Así el archivo final coincide con lo que realmente querías.",
      ],
    },
  ],
  fr: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic est un éditeur d’images rapide et privé qui fonctionne entièrement dans votre navigateur. Il est conçu pour des retouches rapides qui restent soignées : rognage, coins arrondis, bordures, ombres, redimensionnement, réglages de couleur, texte, filtres et export.",
        "Comme tout se fait en local, vous pouvez déposer une image, l’ajuster puis télécharger le résultat sans envoyer votre fichier à un serveur. Le flux reste simple, rapide et 100 % privé.",
      ],
    },
    {
      id: "crop",
      titleKey: "crop",
      body: [
        "Le mode Recadrer sert à sélectionner la partie de l’image que vous souhaitez conserver. La barre latérale vous permet de choisir un recadrage libre, carré ou 16:9.",
        "Le panneau de réglages offre deux comportements : Rognage d’image supprime tout ce qui se trouve hors de la zone sélectionnée, tandis que Masque d’image conserve l’image entière et remplit la région sélectionnée avec un flou ou une couleur unie.",
        "Utilisez le bouton Appliquer lorsque vous voulez valider le résultat du recadrage. Si vous passez à un autre outil sans appliquer, l’aperçu du recadrage est réinitialisé pour que vous sachiez toujours ce qui est validé et ce qui reste temporaire.",
      ],
    },
    {
      id: "corners",
      titleKey: "corners",
      body: [
        "Les coins arrondis vous permettent d’adoucir les coins extérieurs de toute l’image. Les préréglages rapides servent de raccourcis pour les valeurs courantes, et le curseur vous permet d’ajuster le rayon avec précision.",
        "Cet outil est utile pour les portraits, cartes, vignettes et tout ce pour quoi vous souhaitez une finition plus douce. Appliquez-le lorsque la forme vous convient ; sinon, revenez en arrière pour retrouver une image carrée.",
      ],
    },
    {
      id: "border",
      titleKey: "border",
      body: [
        "Ajouter une bordure permet d’ajouter une bordure autour de l’image. Vous pouvez définir l’épaisseur, choisir une couleur de bordure et activer éventuellement une bordure en dégradé.",
        "Lorsque la bordure en dégradé est activée, l’angle contrôle la direction du dégradé et les couleurs de début et de fin peuvent être modifiées indépendamment. Appliquez la bordure lorsque l’aperçu vous convient pour qu’elle fasse partie de l’image exportée.",
      ],
    },
    {
      id: "shadow",
      titleKey: "shadow",
      body: [
        "L’outil Ombre ajoute une ombre portée à l’image pour la détacher du fond. Plus l’ombre est sombre, plus le contraste augmente ; le curseur de taille contrôle l’étendue et la douceur de l’ombre.",
        "Le sélecteur de couleur modifie la teinte de l’ombre, et la direction détermine où elle se projette. C’est pratique pour les maquettes, les images produit et les visuels sociaux.",
      ],
    },
    {
      id: "resize",
      titleKey: "resize",
      body: [
        "Redimensionner modifie les dimensions en pixels de l’image elle-même. Utilisez Conserver les proportions pour conserver le même ratio, ou Libre si vous avez besoin d’un étirement personnalisé.",
        "Les champs de largeur et de hauteur ainsi que les poignées fonctionnent ensemble. Si vous souhaitez mettre l’image à l’échelle avant l’export, Redimensionner est le bon outil.",
      ],
    },
    {
      id: "filters",
      titleKey: "filters",
      body: [
        "Filtres propose des styles rapides comme noir et blanc, sépia, vintage, chaud, froid et plus encore.",
        "Utilisez l’aperçu pour comparer les rendus avant d’appliquer. Une fois appliqué, le filtre fait partie de l’image et peut être combiné avec le reste de vos retouches.",
      ],
    },
    {
      id: "adjustments",
      titleKey: "adjustments",
      body: [
        "Réglages permet d’ajuster le contraste, la luminosité, la balance des couleurs et la saturation. Ces réglages sont utiles lorsque l’image est correcte mais mérite un peu plus de présence ou d’équilibre.",
        "De petites modifications peuvent faire une grande différence, surtout lorsque vous préparez une image pour le partage ou la publication.",
      ],
    },
    {
      id: "text",
      titleKey: "text",
      body: [
        "Texte vous permet d’ajouter une légende ou une étiquette sur l’image. Commencez par saisir le texte et choisir une couleur, puis utilisez le panneau flottant pour ajuster la taille et la position.",
        "C’est utile pour les titres, les appels, les étiquettes et les visuels rapides pour les réseaux sociaux.",
      ],
    },
    {
      id: "export",
      titleKey: "export",
      body: [
        "Exporter crée l’image finale au format PNG, JPG ou WebP et ouvre le panneau de téléchargement et de partage. PNG et WebP peuvent conserver la transparence ; JPG remplit les zones transparentes avec la couleur d’arrière-plan choisie.",
        "Si vous avez encore des modifications non appliquées dans l’outil courant, l’application vous demandera si vous souhaitez les enregistrer avant l’export. Le fichier final correspond ainsi à ce que vous vouliez vraiment.",
      ],
    },
  ],
  de: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic ist ein schneller, privater Bildeditor, der vollständig im Browser läuft. Er ist für schnelle Bearbeitungen gedacht, die trotzdem sauber aussehen sollen: Zuschneiden, runde Ecken, Rahmen, Schatten, Größenänderung, Farbanpassungen, Text, Filter und Export.",
        "Da alles lokal passiert, kannst du ein Bild hineinziehen, fein abstimmen und das Ergebnis herunterladen, ohne deine Datei an einen Server zu senden. So bleibt der Ablauf einfach, schnell und 100 % privat.",
      ],
    },
    {
      id: "crop",
      titleKey: "crop",
      body: [
        "Der Zuschneiden-Modus dient dazu, den Teil des Bildes auszuwählen, den du behalten möchtest. Die Seitenleiste erlaubt dir freien Zuschnitt, quadratischen Zuschnitt oder 16:9-Zuschnitt.",
        "Das Einstellungsfeld bietet zwei Verhaltensweisen: Bild zuschneiden entfernt alles außerhalb des ausgewählten Bereichs, während Bildmaske das komplette Bild erhält und den gewählten Bereich mit Unschärfe oder einer Vollfarbe füllt.",
        "Verwende die Schaltfläche Anwenden, wenn du das Zuschneiden bestätigen möchtest. Wenn du ohne Anwenden zu einem anderen Werkzeug wechselst, wird die Vorschau zurückgesetzt, damit du immer weißt, was bestätigt und was noch vorläufig ist.",
      ],
    },
    {
      id: "corners",
      titleKey: "corners",
      body: [
        "Ecken abrunden macht die äußeren Ecken des gesamten Bildes weicher. Die Schnellwerte sind ein Shortcut für häufige Einstellungen, und der Regler erlaubt dir eine präzise Feinabstimmung des Radius.",
        "Dieses Werkzeug eignet sich für Profilbilder, Karten, Vorschaubilder und überall dort, wo ein weicherer Abschluss gewünscht ist. Wende es an, wenn die Form passt; andernfalls setze es zurück, um wieder ein quadratisches Bild zu erhalten.",
      ],
    },
    {
      id: "border",
      titleKey: "border",
      body: [
        "Rahmen hinzufügen fügt dem Bild einen Rahmen hinzu. Du kannst die Stärke festlegen, eine Rahmenfarbe wählen und optional einen Verlauf aktivieren.",
        "Wenn der Verlauf aktiv ist, steuert der Winkel die Richtung des Verlaufs, und Start- und Endfarben lassen sich unabhängig anpassen. Wende den Rahmen an, wenn die Vorschau passt, damit er Teil des exportierten Bildes wird.",
      ],
    },
    {
      id: "shadow",
      titleKey: "shadow",
      body: [
        "Schatten fügt dem Bild einen Schlagschatten hinzu, damit es sich vom Hintergrund abhebt. Dunklere Schatten erhöhen den Kontrast; der Größenregler bestimmt, wie weich und verteilt der Schatten wirkt.",
        "Der Farbwähler ändert die Schattenfarbe, und die Richtung bestimmt, wohin der Schatten fällt. Das ist praktisch für Mockups, Produktbilder und Social-Media-Grafiken.",
      ],
    },
    {
      id: "resize",
      titleKey: "resize",
      body: [
        "Größe ändern ändert die Pixelabmessungen des Bildes selbst. Verwende Proportionen beibehalten, um das Seitenverhältnis zu halten, oder Frei, wenn du eine eigene Streckung brauchst.",
        "Die Breiten- und Höhenfelder sowie die Griffe arbeiten zusammen. Wenn du das Bild vor dem Export vergrößern oder verkleinern möchtest, ist Größe ändern das richtige Werkzeug.",
      ],
    },
    {
      id: "filters",
      titleKey: "filters",
      body: [
        "Filter bietet schnelle Farblooks wie Schwarzweiß, Sepia, Vintage, warm, kühl und mehr.",
        "Nutze die Vorschau, um Looks vor dem Anwenden zu vergleichen. Sobald ein Filter angewendet wird, ist er Teil des Bildes und kann mit den restlichen Bearbeitungen kombiniert werden.",
      ],
    },
    {
      id: "adjustments",
      titleKey: "adjustments",
      body: [
        "Anpassungen ermöglicht dir, Kontrast, Helligkeit, Farbbalance und Sättigung zu verfeinern. Diese Regler sind nützlich, wenn das Bild technisch stimmt, aber etwas mehr Wirkung oder Balance braucht.",
        "Kleine Änderungen können einen großen Unterschied machen, besonders wenn du ein Bild für das Teilen oder Veröffentlichen vorbereitest.",
      ],
    },
    {
      id: "text",
      titleKey: "text",
      body: [
        "Text erlaubt dir, eine Beschriftung oder ein Label über dem Bild zu platzieren. Gib zuerst den Text ein und wähle eine Farbe; anschließend kannst du im schwebenden Bedienfeld Größe und Position anpassen.",
        "Das ist nützlich für Titel, Callouts, Labels und schnelle Social-Media-Grafiken.",
      ],
    },
    {
      id: "export",
      titleKey: "export",
      body: [
        "Export erstellt das fertige Bild als PNG, JPG oder WebP und öffnet das Download- und Freigabefenster. PNG und WebP können Transparenz erhalten; JPG füllt transparente Bereiche mit der gewählten Hintergrundfarbe.",
        "Wenn im aktuellen Werkzeug noch nicht angewendete Änderungen vorhanden sind, fragt die App vor dem Export, ob du sie speichern möchtest. So entspricht die Datei genau deiner Absicht.",
      ],
    },
  ],
  zh: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic 是一款快速、私密、完全在浏览器中运行的图片编辑器。它适合需要快速处理、但仍要保持精致效果的编辑：裁剪、圆角、边框、阴影、尺寸调整、颜色微调、文字、滤镜和导出。",
        "由于所有处理都在本地完成，你可以拖入图片、进行微调，并直接下载结果，而无需把文件发送到服务器。整个流程简单、快速，并且 100% 私密。",
      ],
    },
    { id: "crop", titleKey: "crop", body: ["裁剪 用于选择你想保留的图片部分。侧边栏可选择自由裁剪、正方形或 16:9 裁剪。", "设置面板提供两种行为：图像裁切器 会移除所选区域外的所有内容，而 图像遮罩 会保留整张图片，并用模糊或纯色填充所选区域。", "当你想确认裁剪结果时，点击 应用。若切换到其他工具而未应用，裁剪预览会重置。"] },
    { id: "corners", titleKey: "corners", body: ["圆角 可让整张图片的外角更柔和。快速预设适合常用数值，滑杆可精细调整半径。", "它适合头像、卡片、缩略图以及任何你想要更柔和外观的场景。"] },
    { id: "border", titleKey: "border", body: ["添加边框 会在图片周围添加边框。你可以设置粗细、选择边框颜色，并可启用渐变边框。", "启用渐变后，角度会控制渐变方向，起始色和结束色可以分别调整。"] },
    { id: "shadow", titleKey: "shadow", body: ["阴影 会为图片添加投影，让图片从背景中更突出。越深的阴影对比越强；大小滑杆决定阴影有多柔和、扩散多远。", "颜色选择器可更改阴影颜色，方向则决定阴影落向哪里。"] },
    { id: "resize", titleKey: "resize", body: ["调整大小 会修改图片本身的像素尺寸。使用 保持比例 可保持原始比例，或在需要自由拉伸时使用 自由形式。", "宽度和高度输入会一起工作。如果你想在导出前放大或缩小图片，调整大小 就是正确工具。"] },
    { id: "filters", titleKey: "filters", body: ["滤镜 提供黑白、棕褐、复古、暖色、冷色等快速风格。", "你可以先用预览比较效果，再决定是否应用。"] },
    { id: "adjustments", titleKey: "adjustments", body: ["调整 可调整对比度、亮度、色彩平衡和饱和度。", "在导出或发布前，细微调整通常能带来明显提升。"] },
    { id: "text", titleKey: "text", body: ["文字 可以在图片上方放置标题或标签。先输入文字并选择颜色，再用浮动面板调整大小和位置。", "适合标题、标注、标签和快速社交图。"] },
    { id: "export", titleKey: "export", body: ["导出可生成 PNG、JPG 或 WebP，并打开下载/分享面板。PNG 和 WebP 可保留透明度；JPG 会用所选背景色填充透明区域。", "如果当前工具还有未应用的更改，应用会在导出前询问是否保存。"] },
  ],
  ko: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic은 브라우저 안에서만 실행되는 빠르고 개인적인 이미지 편집기입니다. 자르기, 둥근 모서리, 테두리, 그림자, 크기 조정, 색상 보정, 텍스트, 필터, 내보내기처럼 빠르지만 깔끔하게 마무리해야 하는 편집에 맞춰 제작되었습니다.",
        "모든 작업이 로컬에서 이루어지므로 이미지를 끌어다 놓고 조정한 뒤 파일을 서버로 보내지 않고 바로 내려받을 수 있습니다. 그래서 작업 흐름이 단순하고 빠르며 100% 비공개입니다.",
      ],
    },
    { id: "crop", titleKey: "crop", body: ["자르기은 유지할 이미지 부분을 선택하는 기능입니다. 사이드바에서 자유 자르기, 정사각형, 16:9 자르기를 선택할 수 있습니다.", "설정 패널에는 두 가지 동작이 있습니다. 이미지 트리머는 선택 영역 밖의 모든 내용을 제거하고, 이미지 마스킹은 전체 이미지를 유지한 채 선택 영역을 흐림 효과 또는 단색으로 채웁니다.", "결과를 확정하려면 적용를 누르세요. 적용하지 않은 채 다른 도구로 이동하면 미리보기는 초기화됩니다."] },
    { id: "corners", titleKey: "corners", body: ["둥근 모서리는 이미지 전체의 바깥쪽 모서리를 부드럽게 만듭니다. 빠른 프리셋은 자주 쓰는 값에 대한 바로 가기이며, 슬라이더로 반지름을 세밀하게 조정할 수 있습니다.", "프로필 이미지, 카드, 썸네일처럼 더 부드러운 마감이 필요한 곳에 유용합니다."] },
    { id: "border", titleKey: "border", body: ["테두리 추가는 이미지 주변에 테두리를 추가합니다. 두께를 지정하고, 테두리 색을 고르며, 필요하면 그라데이션 테두리를 사용할 수 있습니다.", "그라데이션이 켜지면 각도는 방향을 정하고 시작색과 끝색은 각각 따로 조정할 수 있습니다."] },
    { id: "shadow", titleKey: "shadow", body: ["그림자는 이미지를 배경에서 분리해 보이도록 그림자를 추가합니다. 더 어두울수록 대비가 강해지고, 크기 슬라이더는 그림자의 부드러움과 퍼짐을 조절합니다.", "색상 선택기로 그림자 색을 바꾸고, 방향으로 그림자가 떨어지는 위치를 정할 수 있습니다."] },
    { id: "resize", titleKey: "resize", body: ["크기 조정은 이미지 자체의 픽셀 크기를 변경합니다. 비율 유지를 사용하면 원본 비율을 유지하고, 자유형은 자유롭게 늘리거나 줄일 때 사용합니다.", "너비와 높이 입력은 함께 동작합니다. 내보내기 전에 이미지를 키우거나 줄이고 싶다면 크기 조정가 맞는 도구입니다."] },
    { id: "filters", titleKey: "filters", body: ["필터는 흑백, 세피아, 빈티지, 따뜻한 느낌, 차가운 느낌 등 빠른 스타일을 제공합니다.", "적용하기 전에 미리보기로 비교해 볼 수 있습니다."] },
    { id: "adjustments", titleKey: "adjustments", body: ["조정은 대비, 밝기, 색상 균형, 채도를 조정할 수 있습니다.", "출력이나 게시 전에 작은 조정만으로도 큰 차이를 만들 수 있습니다."] },
    { id: "text", titleKey: "text", body: ["텍스트는 이미지 위에 제목이나 라벨을 올릴 수 있습니다. 먼저 텍스트를 입력하고 색을 고른 다음, 떠 있는 패널에서 크기와 위치를 조정하세요.", "제목, 설명, 라벨, 빠른 소셜 그래픽에 유용합니다."] },
    { id: "export", titleKey: "export", body: ["내보내기는 최종 이미지를 PNG, JPG 또는 WebP로 만들고 다운로드/공유 패널을 엽니다. PNG와 WebP는 투명도를 유지할 수 있고, JPG는 선택한 배경색으로 투명 영역을 채웁니다.", "현재 도구에 아직 적용하지 않은 변경 사항이 있으면, 내보내기 전에 저장할지 묻습니다."] },
  ],
  hi: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic एक तेज़, निजी इमेज एडिटर है जो पूरी तरह आपके ब्राउज़र में चलता है। यह उन त्वरित संपादनों के लिए बनाया गया है जिन्हें फिर भी साफ़ और पेशेवर दिखना चाहिए: क्रॉप, राउंडेड कॉर्नर, बॉर्डर, शैडो, आकार बदलना, रंग सुधार, टेक्स्ट, फ़िल्टर और एक्सपोर्ट।",
        "क्योंकि सब कुछ स्थानीय रूप से होता है, आप इमेज को ड्रैग करके ठीक कर सकते हैं और फ़ाइल को सर्वर पर भेजे बिना परिणाम डाउनलोड कर सकते हैं। इससे काम आसान, तेज़ और 100% निजी रहता है।",
      ],
    },
    { id: "crop", titleKey: "crop", body: ["क्रॉप का उपयोग उस हिस्से को चुनने के लिए करें जिसे आप रखना चाहते हैं। साइडबार में आप free crop, square crop, या 16:9 crop चुन सकते हैं।", "सेटिंग्स पैनल में दो विकल्प हैं: इमेज ट्रिमर चयनित क्षेत्र के बाहर सब कुछ हटा देता है, जबकि इमेज मास्किंग पूरी इमेज रखता है और चयनित क्षेत्र को blur या solid color से भरता है।", "जब आप क्रॉप परिणाम को पक्का करना चाहें, तब लागू करें दबाएँ। अगर आप बिना apply किए किसी और tool पर जाएँगे, तो preview reset हो जाएगा।"] },
    { id: "corners", titleKey: "corners", body: ["गोल कोने पूरी इमेज के बाहरी कोनों को नरम बनाता है। Quick presets सामान्य values के shortcuts हैं, और slider से radius को बारीकी से बदला जा सकता है।", "यह profile images, cards, thumbnails और उन सभी जगहों के लिए उपयोगी है जहाँ softer finish चाहिए।"] },
    { id: "border", titleKey: "border", body: ["बॉर्डर जोड़ें इमेज के चारों ओर border जोड़ता है। आप thickness सेट कर सकते हैं, border color चुन सकते हैं, और चाहें तो gradient border भी चालू कर सकते हैं।", "Gradient चालू होने पर angle उसकी direction तय करता है, और start/end colors को अलग-अलग बदला जा सकता है।"] },
    { id: "shadow", titleKey: "shadow", body: ["छाया इमेज में drop shadow जोड़ता है ताकि वह background से अलग दिखे। Darker shadow से contrast बढ़ता है; size slider shadow की softness और spread नियंत्रित करता है।", "Color picker shadow का रंग बदलता है, और direction shadow के गिरने की दिशा तय करता है।"] },
    { id: "resize", titleKey: "resize", body: ["आकार बदलें इमेज के pixel dimensions बदलता है। समान aspect ratio बनाए रखने के लिए अनुपात बनाए रखें का उपयोग करें, या free stretch के लिए मुक्त रूप चुनें।", "Width और height inputs साथ काम करते हैं। निर्यात से पहले इमेज को बड़ा या छोटा करना हो तो आकार बदलें सही tool है।"] },
    { id: "filters", titleKey: "filters", body: ["फ़िल्टर black and white, sepia, vintage, warm, cool और अन्य quick styles देता है।", "लागू करें करने से पहले preview में look compare करें।"] },
    { id: "adjustments", titleKey: "adjustments", body: ["समायोजन से contrast, brightness, color balance और saturation को refine किया जा सकता है।", "निर्यात या publish करने से पहले छोटे changes भी बड़ा फर्क ला सकते हैं।"] },
    { id: "text", titleKey: "text", body: ["टेक्स्ट आपको इमेज पर caption या label रखने देता है। पहले text टाइप करें और color चुनें, फिर floating panel में size और position ठीक करें।", "यह titles, callouts, labels और quick social graphics के लिए उपयोगी है।"] },
    { id: "export", titleKey: "export", body: ["निर्यात अंतिम इमेज को PNG, JPG या WebP में बनाकर डाउनलोड/शेयर पैनल खोलता है। PNG और WebP पारदर्शिता रख सकते हैं; JPG पारदर्शी हिस्सों को चुने हुए बैकग्राउंड रंग से भरता है।", "यदि current tool में कोई unapplied changes हैं, तो app export से पहले उन्हें save करने के बारे में पूछेगा।"] },
  ],
  pt: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic é um editor de imagens rápido e privado que funciona totalmente no navegador. Ele foi feito para edições rápidas que ainda precisam ficar com aparência caprichada: corte, cantos arredondados, bordas, sombras, redimensionamento, ajustes de cor, texto, filtros e exportação.",
        "Como tudo acontece localmente, você pode arrastar uma imagem, refinar os detalhes e baixar o resultado sem enviar o arquivo para um servidor. Isso mantém o fluxo simples, rápido e 100% privado.",
      ],
    },
    { id: "crop", titleKey: "crop", body: ["O modo Cortar serve para escolher a parte da imagem que você quer manter. A barra lateral permite recorte livre, quadrado ou 16:9.", "O painel de configurações oferece dois comportamentos: Cortador de imagem remove tudo fora da área selecionada, enquanto Máscara de imagem mantém a imagem inteira e preenche a região selecionada com desfoque ou uma cor sólida.", "Use o botão Aplicar quando quiser confirmar o resultado. Se mudar para outra ferramenta sem aplicar, a prévia é redefinida."] },
    { id: "corners", titleKey: "corners", body: ["Arredondar cantos suaviza os cantos externos de toda a imagem. Os presets rápidos ajudam em valores comuns, e o controle deslizante ajusta o raio com precisão.", "É útil para perfis, cartões, miniaturas e qualquer situação em que você queira um acabamento mais suave."] },
    { id: "border", titleKey: "border", body: ["Adicionar borda coloca uma borda ao redor da imagem. Você pode definir espessura, escolher uma cor e ativar um gradiente.", "Quando o gradiente está ligado, o ângulo controla a direção e as cores inicial/final podem ser ajustadas separadamente."] },
    { id: "shadow", titleKey: "shadow", body: ["Sombra adiciona uma sombra projetada para destacar a imagem do fundo. Mais escuro aumenta o contraste; o tamanho controla a suavidade.", "A cor muda o tom da sombra, e a direção controla para onde ela cai."] },
    { id: "resize", titleKey: "resize", body: ["Redimensionar altera as dimensões em pixels da imagem. Use Manter proporções para manter o mesmo aspecto ou Forma livre para esticar manualmente.", "Os campos de largura/altura trabalham juntos. Use isso para aumentar ou reduzir antes de exportar."] },
    { id: "filters", titleKey: "filters", body: ["Filtros oferece estilos rápidos como preto e branco, sépia, vintage, quente, frio e mais.", "A prévia ajuda a comparar antes de aplicar. Depois de aplicado, o filtro passa a fazer parte da imagem."] },
    { id: "adjustments", titleKey: "adjustments", body: ["Ajustes permite refinar contraste, brilho, balanço de cor e saturação.", "Pequenos ajustes podem fazer uma grande diferença, especialmente para compartilhar ou publicar."] },
    { id: "text", titleKey: "text", body: ["Texto coloca uma legenda ou rótulo sobre a imagem. Digite o texto, escolha uma cor e use o painel flutuante para ajustar tamanho e posição.", "É útil para títulos, chamadas, rótulos e gráficos rápidos."] },
    { id: "export", titleKey: "export", body: ["Exportar cria a imagem final em PNG, JPG ou WebP e abre o painel de download/compartilhamento. PNG e WebP podem manter a transparência; JPG preenche áreas transparentes com a cor de fundo escolhida.", "Se ainda houver mudanças não aplicadas, o app pergunta se você quer salvá-las antes de exportar."] },
  ],
  it: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic è un editor di immagini veloce e privato che funziona interamente nel browser. È pensato per modifiche rapide ma curate: ritaglio, angoli arrotondati, bordi, ombre, ridimensionamento, regolazioni colore, testo, filtri ed esportazione.",
        "Poiché tutto avviene in locale, puoi trascinare un’immagine, rifinirla e scaricare il risultato senza inviare il file a un server. Il flusso resta semplice, veloce e 100% privato.",
      ],
    },
    { id: "crop", titleKey: "crop", body: ["Ritaglia serve per selezionare la parte dell’immagine da mantenere. La barra laterale permette ritaglio libero, quadrato o 16:9.", "Il pannello impostazioni offre due comportamenti: Ritaglio immagine elimina tutto fuori dall’area selezionata, mentre Mascheratura immagine mantiene l’immagine intera e riempie l’area con sfocatura o un colore pieno.", "Usa Applica per confermare il risultato. Se cambi strumento senza applicare, l’anteprima si resetta."] },
    { id: "corners", titleKey: "corners", body: ["Angoli arrotondati ammorbidisce gli angoli esterni dell’intera immagine. I preset rapidi sono scorciatoie per valori comuni, mentre il cursore regola con precisione il raggio.", "È utile per profili, card, miniature e ovunque tu voglia un risultato più morbido."] },
    { id: "border", titleKey: "border", body: ["Aggiungi bordo aggiunge un bordo intorno all’immagine. Puoi impostare lo spessore, scegliere un colore e attivare un bordo sfumato.", "Quando il bordo sfumato è attivo, l’angolo controlla la direzione e i colori iniziale/finale si possono regolare separatamente."] },
    { id: "shadow", titleKey: "shadow", body: ["Ombra aggiunge un’ombra proiettata per staccare l’immagine dallo sfondo. Più scura = più contrasto; la dimensione controlla la morbidezza.", "Il colore cambia il tono dell’ombra e la direzione ne controlla la posizione."] },
    { id: "resize", titleKey: "resize", body: ["Ridimensiona cambia le dimensioni in pixel dell’immagine. Usa Mantieni proporzioni per mantenere il rapporto o Forma libera per deformare manualmente.", "I campi larghezza/altezza lavorano insieme: usa questo strumento per ridimensionare prima dell’esportazione."] },
    { id: "filters", titleKey: "filters", body: ["Filtri offre stili rapidi come bianco e nero, seppia, vintage, caldo, freddo e altro.", "L’anteprima aiuta a confrontare i risultati prima di applicare il filtro."] },
    { id: "adjustments", titleKey: "adjustments", body: ["Regolazioni consente di regolare contrasto, luminosità, bilanciamento colore e saturazione.", "Piccoli cambiamenti possono fare una grande differenza, soprattutto per condividere o pubblicare."] },
    { id: "text", titleKey: "text", body: ["Testo consente di inserire una didascalia o un’etichetta sopra l’immagine. Digita il testo, scegli un colore e usa il pannello flottante per regolare dimensione e posizione.", "È utile per titoli, richiami, etichette e grafiche rapide."] },
    { id: "export", titleKey: "export", body: ["Esporta crea l’immagine finale in PNG, JPG o WebP e apre il pannello di download/condivisione. PNG e WebP possono mantenere la trasparenza; JPG riempie le aree trasparenti con il colore di sfondo scelto.", "Se ci sono modifiche non applicate, l’app chiede se vuoi salvarle prima di esportare."] },
  ],
  ru: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic — это быстрый и приватный редактор изображений, который работает полностью в браузере. Он создан для быстрых правок, которые при этом выглядят аккуратно: кадрирование, скругление углов, рамки, тени, изменение размера, цветокоррекция, текст, фильтры и экспорт.",
        "Поскольку всё происходит локально, вы можете перетащить изображение, настроить его и скачать результат, не отправляя файл на сервер. Это делает работу простой, быстрой и на 100% приватной.",
      ],
    },
    { id: "crop", titleKey: "crop", body: ["Обрезка предназначен для выбора части изображения, которую вы хотите оставить. Боковая панель позволяет выбрать свободное, квадратное или 16:9 кадрирование.", "Панель настроек предлагает два режима: Триммер изображения удаляет всё вне выбранной области, а Маска изображения сохраняет всё изображение и заполняет выбранную область размытием или сплошным цветом.", "Нажмите Применить, чтобы применить результат. Если перейти к другому инструменту без применения, предпросмотр сбрасывается."] },
    { id: "corners", titleKey: "corners", body: ["Скруглить углы смягчает внешние углы всего изображения. Быстрые пресеты помогают выбрать типовые значения, а ползунок точно настраивает радиус.", "Полезно для аватаров, карточек, миниатюр и любых изображений с более мягким завершением."] },
    { id: "border", titleKey: "border", body: ["Добавить рамку добавляет рамку вокруг изображения. Можно задать толщину, выбрать цвет и при желании включить градиент.", "Когда градиент включён, угол задаёт направление, а начальный и конечный цвета можно менять отдельно."] },
    { id: "shadow", titleKey: "shadow", body: ["Тень добавляет тень, чтобы изображение лучше отделялось от фона. Более тёмная тень усиливает контраст; размер задаёт мягкость.", "Выбор цвета меняет оттенок тени, а направление задаёт её падение."] },
    { id: "resize", titleKey: "resize", body: ["Изменить размер меняет размер изображения в пикселях. Используйте Сохранять пропорции, чтобы сохранить пропорции, или Свободный режим для произвольного растяжения.", "Поля ширины и высоты работают вместе и позволяют изменить размер перед экспортом."] },
    { id: "filters", titleKey: "filters", body: ["Фильтры предлагает быстрые стили, например чёрно-белый, сепию, винтаж, тёплый, холодный и другие.", "Предпросмотр помогает сравнить варианты перед применением."] },
    { id: "adjustments", titleKey: "adjustments", body: ["Коррекции позволяет настроить контраст, яркость, баланс цвета и насыщенность.", "Небольшие изменения могут сильно улучшить результат, особенно перед публикацией."] },
    { id: "text", titleKey: "text", body: ["Текст позволяет разместить подпись или метку поверх изображения. Сначала введите текст, выберите цвет, затем настройте размер и позицию в плавающей панели.", "Полезно для заголовков, пометок и быстрых график."] },
    { id: "export", titleKey: "export", body: ["Экспорт создаёт итоговое изображение в PNG, JPG или WebP и открывает панель скачивания и публикации. PNG и WebP могут сохранять прозрачность; JPG заполняет прозрачные области выбранным цветом фона.", "Если есть неприменённые изменения, приложение спросит, нужно ли сохранить их перед экспортом."] },
  ],
  ar: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic هو محرر صور سريع وخاص يعمل بالكامل داخل المتصفح. صُمم للتعديلات السريعة التي ما زالت تحتاج إلى مظهر أنيق: القص، تدوير الزوايا، الإطارات، الظلال، تغيير الحجم، ضبط الألوان، النصوص، الفلاتر والتصدير.",
        "وبما أن كل شيء يتم محليًا، يمكنك سحب صورة وتعديلها ثم تنزيل النتيجة دون إرسال الملف إلى خادم. هذا يجعل سير العمل بسيطًا وسريعًا وخاصة بنسبة 100%.",
      ],
    },
    { id: "crop", titleKey: "crop", body: ["وضع قص مخصص لاختيار الجزء الذي تريد الاحتفاظ به. يتيح الشريط الجانبي قصًا حرًا أو مربعًا أو بنسبة 16:9.", "لوحة الإعدادات توفر سلوكين: مُقصّ الصورة يحذف كل ما هو خارج المنطقة المحددة، بينما قناع الصورة يبقي الصورة كاملة ويملأ المنطقة المحددة بضبابية أو لون ثابت.", "استخدم زر تطبيق عندما تريد تثبيت النتيجة. إذا انتقلت إلى أداة أخرى دون تطبيق، تتم إعادة ضبط المعاينة."] },
    { id: "corners", titleKey: "corners", body: ["تدوير الزوايا يخفف الزوايا الخارجية للصورة بالكامل. الإعدادات السريعة مناسبة للقيم الشائعة، ويتيح المنزلق ضبط نصف القطر بدقة.", "مفيد لصور الملفات الشخصية والبطاقات والصور المصغرة وأي مكان تريد فيه مظهرًا أنعم."] },
    { id: "border", titleKey: "border", body: ["إضافة حد يضيف إطارًا حول الصورة. يمكنك تحديد السمك واختيار اللون، أو تشغيل حد متدرج.", "عند تفعيل الحد المتدرج، يتحكم الزاوية باتجاه التدرج ويمكن تعديل لون البداية والنهاية بشكل مستقل."] },
    { id: "shadow", titleKey: "shadow", body: ["ظل منسدل يضيف ظلًا لإبراز الصورة عن الخلفية. كلما زادت العتمة زاد التباين؛ الحجم يحدد مدى نعومة الظل.", "أداة اختيار اللون تغيّر لون الظل، والاتجاه يحدد أين يسقط الظل."] },
    { id: "resize", titleKey: "resize", body: ["تغيير الحجم يغيّر أبعاد الصورة بالبكسل. استخدم تقييد النسب للحفاظ على النسبة، أو حر للتمدد الحر.", "حقلا العرض والارتفاع يعملان معًا لتغيير الحجم قبل التصدير."] },
    { id: "filters", titleKey: "filters", body: ["مرشحات الألوان يوفر أنماطًا سريعة مثل الأبيض والأسود والسبيا والفينتج والدافئ والبارد وغيرها.", "استخدم المعاينة لمقارنة المظهر قبل التطبيق."] },
    { id: "adjustments", titleKey: "adjustments", body: ["تعديلات الصورة يتيح ضبط التباين والسطوع وتوازن الألوان والتشبع.", "التغييرات الصغيرة قد تصنع فرقًا كبيرًا قبل المشاركة أو النشر."] },
    { id: "text", titleKey: "text", body: ["نص يتيح وضع عنوان أو تسمية فوق الصورة. اكتب النص واختر اللون ثم عدّل الحجم والموقع من اللوحة العائمة.", "مفيد للعناوين والتنبيهات والملصقات السريعة."] },
    { id: "export", titleKey: "export", body: ["ينشئ التصدير الصورة النهائية بصيغة PNG أو JPG أو WebP ويفتح لوحة التنزيل والمشاركة. يمكن لـ PNG وWebP الحفاظ على الشفافية، بينما يملأ JPG المناطق الشفافة بلون الخلفية المختار.", "إذا كانت هناك تغييرات غير مطبقة، ستسأل الأداة إن كنت تريد حفظها قبل التصدير."] },
  ],
  nl: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic is een snelle, privé afbeeldingseditor die volledig in je browser draait. Hij is gemaakt voor snelle bewerkingen die er toch netjes uit moeten zien: bijsnijden, afgeronde hoeken, randen, schaduwen, formaat wijzigen, kleuraanpassingen, tekst, filters en exporteren.",
        "Omdat alles lokaal gebeurt, kun je een afbeelding slepen, verfijnen en het resultaat downloaden zonder je bestand naar een server te sturen. Dat houdt de workflow simpel, snel en 100% privé.",
      ],
    },
    { id: "crop", titleKey: "crop", body: ["Bijsnijden is bedoeld om het deel van de afbeelding te kiezen dat je wilt behouden. In de zijbalk kun je vrij, vierkant of 16:9 snijden.", "Het instellingenpaneel heeft twee gedragingen: Afbeelding bijsnijden verwijdert alles buiten het geselecteerde gebied, terwijl Afbeeldingsmasker de volledige afbeelding behoudt en het geselecteerde gebied vult met vervaging of een effen kleur.", "Gebruik Toepassen om het resultaat vast te leggen. Als je naar een andere tool gaat zonder toe te passen, wordt de preview teruggezet."] },
    { id: "corners", titleKey: "corners", body: ["Hoeken afronden verzacht de buitenste hoeken van de hele afbeelding. De presets zijn snelle keuzes voor veelgebruikte waarden, en de slider verfijnt de radius.", "Handig voor profielfoto’s, kaarten, thumbnails en overal waar je een zachtere afwerking wilt."] },
    { id: "border", titleKey: "border", body: ["Rand toevoegen plaatst een rand rond de afbeelding. Je kunt de dikte instellen, een kleur kiezen en eventueel een verloop activeren.", "Wanneer het verloop aanstaat, bepaalt de hoek de richting en kun je begin- en eindkleuren apart aanpassen."] },
    { id: "shadow", titleKey: "shadow", body: ["Schaduw voegt een slagschaduw toe zodat de afbeelding loskomt van de achtergrond. Donkerder vergroot het contrast; grootte bepaalt hoe zacht de schaduw is.", "De kleurkiezer verandert de schaduwkleur en de richting bepaalt waar hij valt."] },
    { id: "resize", titleKey: "resize", body: ["Formaat wijzigen past de pixelafmetingen van de afbeelding aan. Gebruik Proporties behouden om de verhouding te behouden, of Vrij om vrij te stretchen.", "Breedte en hoogte werken samen om de afbeelding voor export te schalen."] },
    { id: "filters", titleKey: "filters", body: ["Filters biedt snelle stijlen zoals zwart-wit, sepia, vintage, warm, koel en meer.", "Gebruik de preview om looks te vergelijken voor je toepast."] },
    { id: "adjustments", titleKey: "adjustments", body: ["Aanpassingen laat je contrast, helderheid, kleurbalans en verzadiging finetunen.", "Kleine aanpassingen kunnen veel verschil maken, vooral voor delen of publiceren."] },
    { id: "text", titleKey: "text", body: ["Tekst laat je een onderschrift of label boven op de afbeelding plaatsen. Typ de tekst, kies een kleur en stel grootte en positie af in het zwevende paneel.", "Handig voor titels, callouts, labels en snelle social graphics."] },
    { id: "export", titleKey: "export", body: ["Export maakt de uiteindelijke afbeelding als PNG, JPG of WebP en opent het download- en deelvenster. PNG en WebP kunnen transparantie behouden; JPG vult transparante gebieden met de gekozen achtergrondkleur.", "Als er nog niet-toegepaste wijzigingen zijn, vraagt de app of je ze eerst wilt bewaren."] },
  ],
  pl: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic to szybki i prywatny edytor obrazów działający całkowicie w przeglądarce. Został stworzony do szybkich poprawek, które nadal mają wyglądać dopracowanie: przycinanie, zaokrąglone rogi, ramki, cienie, zmiana rozmiaru, korekta kolorów, tekst, filtry i eksport.",
        "Ponieważ wszystko dzieje się lokalnie, możesz przeciągnąć obraz, dopracować go i pobrać wynik bez wysyłania pliku na serwer. Dzięki temu praca pozostaje prosta, szybka i w 100% prywatna.",
      ],
    },
    {
      id: "crop",
      titleKey: "crop",
      body: [
        "Narzędzie Przycinanie pozwala wybrać część obrazu, którą chcesz zachować. Pasek boczny oferuje tryb swobodny, kwadratowy lub 16:9.",
        "Panel ustawień daje dwa zachowania: Przycinacz obrazu usuwa wszystko poza zaznaczonym obszarem, a Maska obrazu zachowuje cały obraz i wypełnia zaznaczony obszar rozmyciem lub jednolitym kolorem.",
        "Użyj przycisku Zastosuj, gdy chcesz zatwierdzić wynik. Jeśli przejdziesz do innego narzędzia bez zastosowania, podgląd zostanie zresetowany.",
      ],
    },
    {
      id: "corners",
      titleKey: "corners",
      body: [
        "Zaokrąglanie rogów zmiękcza zewnętrzne rogi całego obrazu. Szybkie presety to skróty do popularnych wartości, a suwak pozwala dokładnie ustawić promień.",
        "To narzędzie przydaje się do zdjęć profilowych, kart, miniatur i wszędzie tam, gdzie chcesz łagodniejszego wykończenia.",
      ],
    },
    {
      id: "border",
      titleKey: "border",
      body: [
        "Dodaj obramowanie dodaje ramkę wokół obrazu. Możesz ustawić grubość, wybrać kolor i opcjonalnie włączyć obramowanie gradientowe.",
        "Gdy gradient jest włączony, kąt określa kierunek przejścia, a kolor początkowy i końcowy można dopasować osobno. Zastosuj efekt, gdy podgląd wygląda dobrze.",
      ],
    },
    {
      id: "shadow",
      titleKey: "shadow",
      body: [
        "Cień dodaje cień pod obrazem, aby odseparować go od tła. Większa ciemność zwiększa kontrast; rozmiar określa, jak miękki i rozproszony jest cień.",
        "Wybór koloru zmienia ton cienia, a kierunek określa, gdzie cień pada. Przydaje się w makietach, zdjęciach produktów i grafikach społecznościowych.",
      ],
    },
    {
      id: "resize",
      titleKey: "resize",
      body: [
        "Zmiana rozmiaru zmienia wymiary obrazu w pikselach. Użyj Zachowaj proporcje, aby utrzymać ten sam stosunek boków, albo Swobodnie, jeśli potrzebujesz własnego rozciągnięcia.",
        "Pola szerokości i wysokości oraz kontrolki na podglądzie działają razem. To właściwe narzędzie, jeśli chcesz przeskalować obraz przed eksportem.",
      ],
    },
    {
      id: "filters",
      titleKey: "filters",
      body: [
        "Filtry oferują szybkie style, takie jak czarno-biały, sepia, vintage, ciepły, chłodny i inne.",
        "Użyj podglądu, aby porównać wyglądy przed zastosowaniem. Po zastosowaniu filtr staje się częścią obrazu i można go łączyć z innymi zmianami.",
      ],
    },
    {
      id: "adjustments",
      titleKey: "adjustments",
      body: [
        "Dopasowania pozwalają precyzyjnie ustawić kontrast, jasność, balans kolorów i nasycenie. Te kontrolki są idealne, gdy obraz jest już blisko ideału, ale potrzebuje odrobiny więcej energii lub balansu.",
        "Małe zmiany mogą zrobić dużą różnicę, zwłaszcza gdy przygotowujesz obraz do udostępnienia lub publikacji.",
      ],
    },
    {
      id: "text",
      titleKey: "text",
      body: [
        "Tekst pozwala umieścić tytuł lub etykietę na obrazie. Najpierw wpisz tekst i wybierz kolor, a potem użyj pływającego panelu, aby ustawić rozmiar i pozycję.",
        "To przydatne do tytułów, podpisów, etykiet i szybkich grafik społecznościowych.",
      ],
    },
    {
      id: "export",
      titleKey: "export",
      body: [
        "Eksport tworzy końcowy obraz jako PNG, JPG lub WebP i otwiera panel pobierania oraz udostępniania. PNG i WebP mogą zachować przezroczystość; JPG wypełnia przezroczyste obszary wybranym kolorem tła.",
        "Jeśli w bieżącym narzędziu są jeszcze niezastosowane zmiany, aplikacja zapyta, czy chcesz je zapisać przed eksportem. Dzięki temu finalny plik odpowiada Twojemu zamiarowi.",
      ],
    },
  ],
  tr: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic, tamamen tarayıcıda çalışan hızlı ve gizli bir görsel düzenleyicidir. Kırpma, yuvarlatılmış köşeler, çerçeveler, gölgeler, boyut değiştirme, renk ayarları, metin, filtreler ve dışa aktarma gibi hızlı ama düzenli görünmesi gereken işler için tasarlanmıştır.",
        "Her şey yerelde işlendiği için bir görseli sürükleyip ince ayar yapabilir ve dosyanızı bir sunucuya göndermeden sonucu indirebilirsiniz. Bu da süreci basit, hızlı ve %100 gizli tutar.",
      ],
    },
    { id: "crop", titleKey: "crop", body: ["Kırpma aracı, tutmak istediğiniz görüntü bölümünü seçmenizi sağlar. Yan menüde serbest seçim, kare ya da 16:9 kırpma kullanabilirsiniz.", "Ayarlar paneli iki davranış sunar: Görsel Kesici seçili alanın dışındaki her şeyi kaldırır, Görsel Maskeleme ise tüm görüntüyü korur ve seçili alanı bulanıklık ya da düz renk ile doldurur.", "Sonucu onaylamak istediğinizde Uygula düğmesine basın. Başka bir araca geçip uygulamazsanız önizleme sıfırlanır."] },
    { id: "corners", titleKey: "corners", body: ["Köşeleri Yuvarla, tüm görüntünün dış köşelerini yumuşatır. Hızlı ön ayarlar yaygın değerler için kısayoldur, kaydırıcı ise yarıçapı ince ayarlamanızı sağlar.", "Profil görselleri, kartlar, küçük resimler ve daha yumuşak bir bitiş istediğiniz her yerde kullanışlıdır."] },
    { id: "border", titleKey: "border", body: ["Kenarlık Ekle, görüntünün etrafına bir çerçeve ekler. Kalınlığı ayarlayabilir, bir renk seçebilir ve isterseniz degrade kenarlık açabilirsiniz.", "Degrade etkinleştiğinde açı yönü belirler ve başlangıç/bitiş renkleri ayrı ayrı ayarlanabilir."] },
    { id: "shadow", titleKey: "shadow", body: ["Gölge, görüntüyü arka plandan ayırmak için bir düşen gölge ekler. Koyu değer arttıkça kontrast güçlenir; boyut, gölgenin ne kadar yumuşak ve yayılmış görüneceğini belirler.", "Renk seçici gölge tonunu değiştirir, yön ise gölgenin nereye düşeceğini belirler."] },
    { id: "resize", titleKey: "resize", body: ["Boyut Değiştir, görüntünün piksel boyutlarını değiştirir. Aynı oranı korumak için Oranı Koru seçeneğini kullanın, özel bir esnetme gerekiyorsa Serbest Mod kullanın.", "Genişlik ve yükseklik alanları birlikte çalışır. Dışa aktarmadan önce görseli büyütmek veya küçültmek istiyorsanız doğru araç budur."] },
    { id: "filters", titleKey: "filters", body: ["Filtreler, siyah beyaz, sepya, vintage, sıcak, soğuk ve daha fazlası gibi hızlı stiller sunar.", "Uygulamadan önce önizleme ile görünümü karşılaştırabilirsiniz."] },
    { id: "adjustments", titleKey: "adjustments", body: ["Düzeltmeler, kontrast, parlaklık, renk dengesi ve doygunluğu ince ayarlamanızı sağlar.", "Küçük değişiklikler, özellikle paylaşım veya yayın hazırlığında büyük fark yaratabilir."] },
    { id: "text", titleKey: "text", body: ["Metin, görüntünün üstüne bir başlık veya etiket yerleştirmenizi sağlar. Önce metni yazıp bir renk seçin, ardından kayan panelden boyut ve konumu ayarlayın.", "Başlıklar, açıklamalar, etiketler ve hızlı sosyal medya görselleri için kullanışlıdır."] },
    { id: "export", titleKey: "export", body: ["Dışa Aktar, son görseli PNG, JPG veya WebP olarak oluşturur ve indirme/paylaşma panelini açar. PNG ve WebP saydamlığı koruyabilir; JPG saydam alanları seçilen arka plan rengiyle doldurur.", "Geçerli araçta henüz uygulanmamış değişiklikler varsa uygulama, dışa aktarmadan önce bunları kaydetmek isteyip istemediğinizi sorar."] },
  ],
  id: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic adalah editor gambar cepat dan pribadi yang berjalan sepenuhnya di browser. Aplikasi ini dibuat untuk pengeditan cepat yang tetap harus terlihat rapi: memotong, sudut membulat, bingkai, bayangan, ubah ukuran, penyesuaian warna, teks, filter, dan ekspor.",
        "Karena semuanya diproses secara lokal, Anda dapat menyeret gambar, menyempurnakannya, dan mengunduh hasilnya tanpa mengirim file ke server. Alurnya jadi sederhana, cepat, dan 100% pribadi.",
      ],
    },
    { id: "crop", titleKey: "crop", body: ["Alat Crop memungkinkan Anda memilih bagian gambar yang ingin disimpan. Bilah samping menyediakan mode seleksi bebas, persegi, atau 16:9.", "Panel pengaturan memberi dua perilaku: Pemotong Gambar menghapus semua yang berada di luar area terpilih, sementara Masking Gambar mempertahankan seluruh gambar dan mengisi area terpilih dengan blur atau warna solid.", "Gunakan tombol Apply saat Anda ingin mengunci hasilnya. Jika berpindah alat tanpa menerapkan, pratinjau akan direset."] },
    { id: "corners", titleKey: "corners", body: ["Sudut Membulat menghaluskan sudut luar seluruh gambar. Preset cepat adalah pintasan untuk nilai umum, dan slider membantu menyetel radius secara presisi.", "Cocok untuk foto profil, kartu, thumbnail, dan di mana pun Anda ingin hasil yang lebih lembut."] },
    { id: "border", titleKey: "border", body: ["Tambah Bingkai menambahkan bingkai di sekitar gambar. Anda bisa mengatur ketebalan, memilih warna, dan menyalakan border gradien jika perlu.", "Saat gradien aktif, angle mengatur arah gradien dan warna awal/akhir dapat disesuaikan secara terpisah."] },
    { id: "shadow", titleKey: "shadow", body: ["Bayangan menambahkan bayangan jatuh agar gambar lebih terpisah dari latar. Darkness yang lebih tinggi meningkatkan kontras; size mengatur seberapa lembut dan menyebar bayangan.", "Pemilih warna mengubah tone bayangan, dan direction menentukan ke arah mana bayangan jatuh."] },
    { id: "resize", titleKey: "resize", body: ["Ubah Ukuran mengubah dimensi piksel gambar. Gunakan Pertahankan Proporsi untuk menjaga rasio aspek, atau Bebas jika ingin peregangan bebas.", "Kolom lebar dan tinggi bekerja bersama. Ini adalah alat yang tepat jika Anda ingin memperbesar atau memperkecil gambar sebelum diekspor."] },
    { id: "filters", titleKey: "filters", body: ["Filter menawarkan gaya cepat seperti hitam putih, sepia, vintage, hangat, dingin, dan lainnya.", "Gunakan pratinjau untuk membandingkan tampilan sebelum menerapkan."] },
    { id: "adjustments", titleKey: "adjustments", body: ["Penyesuaian memungkinkan Anda menyempurnakan contrast, brightness, color balance, dan saturation.", "Perubahan kecil bisa memberi dampak besar, terutama saat menyiapkan gambar untuk dibagikan atau dipublikasikan."] },
    { id: "text", titleKey: "text", body: ["Teks memungkinkan Anda menaruh judul atau label di atas gambar. Mulailah dengan mengetik teks dan memilih warna, lalu gunakan panel melayang untuk mengatur ukuran dan posisi.", "Ini berguna untuk judul, penanda, label, dan grafik sosial cepat."] },
    { id: "export", titleKey: "export", body: ["Ekspor membuat gambar akhir sebagai PNG, JPG, atau WebP dan membuka panel unduh/bagikan. PNG dan WebP dapat mempertahankan transparansi; JPG mengisi area transparan dengan warna latar yang dipilih.", "Jika masih ada perubahan yang belum diterapkan pada alat saat ini, aplikasi akan bertanya apakah Anda ingin menyimpannya sebelum mengekspor."] },
  ],
  ms: [
    { id: "intro", titleKey: "manualOverview", hideTitle: true, body: ["Cut My Pic ialah penyunting imej yang pantas dan peribadi serta berfungsi sepenuhnya dalam pelayar. Ia sesuai untuk memangkas, membulatkan sudut, menambah bingkai, bayang, teks dan penapis, mengubah saiz serta mengeksport.", "Semua pemprosesan berlaku secara setempat. Imej anda kekal pada peranti dan tidak dihantar ke pelayan."] },
    { id: "crop", titleKey: "crop", body: ["Alat Pangkas membolehkan anda memilih bahagian imej yang mahu dikekalkan. Bar sisi menyediakan pilihan bebas, segi empat sama dan 16:9.", "Pemangkas Imej membuang kawasan di luar pilihan, manakala Penopengan Imej mengekalkan imej dan mengaburkan atau mewarnakan kawasan yang dipilih.", "Pilih Gunakan untuk mengesahkan hasil dan menambahnya pada sejarah."] },
    { id: "corners", titleKey: "corners", body: ["Sudut Bulat melembutkan sudut luar imej. Gunakan pratetap untuk nilai lazim atau gelangsar untuk melaras jejari dengan tepat.", "Ia sesuai untuk foto profil, kad dan imej kecil."] },
    { id: "border", titleKey: "border", body: ["Tambah Bingkai menghasilkan garis luar di sekeliling imej. Anda boleh menetapkan ketebalan, memilih warna atau mengaktifkan bingkai kecerunan.", "Apabila kecerunan aktif, laraskan arah serta warna mula dan akhir secara berasingan."] },
    { id: "shadow", titleKey: "shadow", body: ["Bayang menambah bayang jatuh supaya imej lebih menonjol daripada latar. Keamatan mengawal kegelapan dan saiz mengawal kelembutan serta sebarannya.", "Pemilih warna dan kawalan arah menentukan rupa akhir bayang."] },
    { id: "resize", titleKey: "resize", body: ["Ubah Saiz & Putar mengubah dimensi piksel atau orientasi imej. Gunakan Kekalkan Nisbah untuk mengekalkan nisbah aspek, atau Bentuk Bebas untuk saiz tersuai.", "Medan lebar dan tinggi membantu menyediakan imej pada saiz eksport yang diperlukan."] },
    { id: "filters", titleKey: "filters", body: ["Penapis menyediakan gaya pantas seperti hitam putih, sepia, vintaj, hangat dan sejuk.", "Semak pratonton pada kanvas sebelum memilih Gunakan."] },
    { id: "adjustments", titleKey: "adjustments", body: ["Pelarasan membolehkan anda memperhalus kontras, kecerahan, keseimbangan warna, ketepuan dan kelutsinaran.", "Perubahan kecil sering memberikan hasil yang lebih semula jadi."] },
    { id: "text", titleKey: "text", body: ["Teks membolehkan anda meletakkan tajuk atau label pada imej. Masukkan teks, pilih warna, kemudian laraskan saiz dan kedudukan.", "Anda juga boleh bertukar kepada mod Cap untuk grafik hiasan sedia ada."] },
    { id: "export", titleKey: "export", body: ["Eksport menghasilkan imej akhir sebagai PNG, JPG atau WebP dan membuka pilihan muat turun, salin dan kongsi. PNG dan WebP boleh mengekalkan kelutsinaran; JPG mengisi kawasan lutsinar dengan warna latar yang dipilih.", "Jika alat semasa mempunyai perubahan yang belum digunakan, aplikasi akan meminta anda mengesahkannya sebelum mengeksport."] },
  ],
  vi: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic là một trình chỉnh sửa ảnh nhanh và riêng tư, chạy hoàn toàn trong trình duyệt. Ứng dụng được tạo cho các chỉnh sửa nhanh nhưng vẫn cần trông gọn gàng: cắt, bo góc, thêm viền, đổ bóng, đổi kích thước, chỉnh màu, văn bản, bộ lọc và xuất ảnh.",
        "Vì mọi thứ đều xử lý cục bộ, bạn có thể kéo ảnh vào, tinh chỉnh rồi tải kết quả xuống mà không cần gửi tệp lên máy chủ. Nhờ vậy quy trình đơn giản, nhanh và 100% riêng tư.",
      ],
    },
    { id: "crop", titleKey: "crop", body: ["Công cụ Crop cho phép bạn chọn phần ảnh muốn giữ lại. Thanh bên hỗ trợ chế độ chọn tự do, hình vuông hoặc 16:9.", "Bảng cài đặt có hai kiểu hoạt động: Cắt ảnh sẽ xóa mọi thứ nằm ngoài vùng đã chọn, còn Che ảnh sẽ giữ toàn bộ ảnh và lấp vùng đã chọn bằng làm mờ hoặc màu phẳng.", "Hãy nhấn Apply khi bạn muốn xác nhận kết quả. Nếu chuyển sang công cụ khác mà chưa áp dụng, phần xem trước sẽ được đặt lại."] },
    { id: "corners", titleKey: "corners", body: ["Bo góc làm mềm các góc ngoài của toàn bộ ảnh. Các preset nhanh là lối tắt cho những giá trị thường dùng, còn thanh trượt cho phép bạn tinh chỉnh bán kính.", "Rất hữu ích cho ảnh đại diện, thẻ, thumbnail và bất kỳ nơi nào bạn muốn kết thúc mềm hơn."] },
    { id: "border", titleKey: "border", body: ["Thêm viền thêm đường viền quanh ảnh. Bạn có thể đặt độ dày, chọn màu viền và bật viền chuyển sắc nếu muốn.", "Khi gradient được bật, góc sẽ điều khiển hướng chuyển sắc và màu đầu/cuối có thể chỉnh riêng."] },
    { id: "shadow", titleKey: "shadow", body: ["Đổ bóng thêm bóng đổ để ảnh tách khỏi nền. Darkness cao hơn tạo tương phản mạnh hơn; size điều khiển độ mềm và độ lan của bóng.", "Bộ chọn màu thay đổi tông bóng, còn direction quyết định bóng đổ về hướng nào."] },
    { id: "resize", titleKey: "resize", body: ["Đổi kích thước thay đổi kích thước pixel của ảnh. Dùng Giữ tỉ lệ để giữ đúng tỉ lệ, hoặc Tự do nếu bạn muốn kéo giãn tự do.", "Các ô chiều rộng và chiều cao làm việc cùng nhau. Đây là công cụ phù hợp nếu bạn muốn phóng to hoặc thu nhỏ ảnh trước khi xuất."] },
    { id: "filters", titleKey: "filters", body: ["Bộ lọc cung cấp các kiểu nhanh như đen trắng, sepia, vintage, ấm, lạnh và nhiều hơn nữa.", "Hãy dùng xem trước để so sánh giao diện trước khi áp dụng."] },
    { id: "adjustments", titleKey: "adjustments", body: ["Điều chỉnh cho phép tinh chỉnh contrast, brightness, color balance và saturation.", "Những thay đổi nhỏ có thể tạo ra khác biệt lớn, nhất là khi chuẩn bị ảnh để chia sẻ hoặc đăng tải."] },
    { id: "text", titleKey: "text", body: ["Văn bản cho phép bạn đặt tiêu đề hoặc nhãn lên ảnh. Bắt đầu bằng cách nhập chữ và chọn màu, sau đó dùng bảng nổi để điều chỉnh kích thước và vị trí.", "Rất hữu ích cho tiêu đề, chú thích, nhãn và đồ họa mạng xã hội nhanh."] },
    { id: "export", titleKey: "export", body: ["Xuất tạo ảnh cuối cùng dưới dạng PNG, JPG hoặc WebP và mở bảng tải xuống/chia sẻ. PNG và WebP có thể giữ độ trong suốt; JPG lấp vùng trong suốt bằng màu nền đã chọn.", "Nếu còn thay đổi chưa được áp dụng ở công cụ hiện tại, ứng dụng sẽ hỏi bạn có muốn lưu chúng trước khi xuất hay không."] },
  ],
  th: [
    {
      id: "intro",
      titleKey: "manualOverview",
      hideTitle: true,
      body: [
        "Cut My Pic คือโปรแกรมแต่งภาพที่รวดเร็วและเป็นส่วนตัวซึ่งทำงานทั้งหมดในเบราว์เซอร์ ออกแบบมาสำหรับการแก้ไขแบบรวดเร็วแต่ยังคงดูเรียบร้อย: ครอป มุมโค้ง เส้นขอบ เงา ปรับขนาด ปรับสี ข้อความ ฟิลเตอร์ และการส่งออก",
        "เพราะทุกอย่างเกิดขึ้นในเครื่อง คุณจึงลากภาพเข้ามา ปรับแต่ง และดาวน์โหลดผลลัพธ์ได้โดยไม่ต้องส่งไฟล์ไปยังเซิร์ฟเวอร์ ทำให้ขั้นตอนการทำงานเรียบง่าย รวดเร็ว และเป็นส่วนตัว 100%",
      ],
    },
    { id: "crop", titleKey: "crop", body: ["เครื่องมือ Crop ช่วยให้คุณเลือกส่วนของภาพที่ต้องการเก็บไว้ แถบด้านข้างมีโหมดเลือกอิสระ สี่เหลี่ยมจัตุรัส หรือ 16:9", "แผงตั้งค่ามีสองแบบการทำงาน: เครื่องมือตัดภาพ จะลบทุกอย่างที่อยู่นอกพื้นที่ที่เลือก ส่วน การปิดบังภาพ จะเก็บภาพทั้งหมดไว้และเติมพื้นที่ที่เลือกด้วยเบลอหรือสีทึบ", "กด Apply เมื่อคุณต้องการยืนยันผลลัพธ์ หากเปลี่ยนไปใช้เครื่องมืออื่นโดยไม่กดใช้ ภาพตัวอย่างจะถูกรีเซ็ต"] },
    { id: "corners", titleKey: "corners", body: ["มุมโค้ง ทำให้มุมด้านนอกของภาพทั้งหมดนุ่มขึ้น พรีเซ็ตแบบเร็วเป็นทางลัดสำหรับค่าที่ใช้บ่อย และแถบเลื่อนช่วยปรับรัศมีได้ละเอียด", "เหมาะสำหรับรูปโปรไฟล์ การ์ด ภาพย่อ และทุกกรณีที่คุณต้องการความนุ่มนวลมากขึ้น"] },
    { id: "border", titleKey: "border", body: ["เพิ่มเส้นขอบ เพิ่มเส้นขอบรอบภาพ คุณสามารถกำหนดความหนา เลือกสี และเปิดใช้ขอบแบบไล่สีได้", "เมื่อเปิดใช้ gradient มุมจะควบคุมทิศทางของการไล่สี และสามารถปรับสีเริ่มต้น/สิ้นสุดแยกกันได้"] },
    { id: "shadow", titleKey: "shadow", body: ["เงา เพิ่มเงาตกกระทบเพื่อให้ภาพแยกจากพื้นหลังมากขึ้น ยิ่ง Darkness สูง เงายิ่งเข้ม และ Size จะควบคุมความนุ่มและการกระจายของเงา", "ตัวเลือกสีเปลี่ยนโทนของเงา และ direction จะกำหนดว่าเงาจะตกไปทางไหน"] },
    { id: "resize", titleKey: "resize", body: ["ปรับขนาด เปลี่ยนขนาดพิกเซลของภาพ ใช้ คงอัตราส่วน เพื่อคงอัตราส่วนเดิม หรือใช้ อิสระ หากต้องการยืดแบบอิสระ", "ช่องกว้างและสูงทำงานร่วมกัน เครื่องมือนี้เหมาะถ้าคุณต้องการขยายหรือย่อภาพก่อนส่งออก"] },
    { id: "filters", titleKey: "filters", body: ["ฟิลเตอร์ มีสไตล์สำเร็จรูปเช่น ขาวดำ ซีเปีย วินเทจ โทนอุ่น โทนเย็น และอื่นๆ", "ใช้ตัวอย่างภาพเพื่อเปรียบเทียบก่อนกดใช้"] },
    { id: "adjustments", titleKey: "adjustments", body: ["การปรับแต่ง ให้คุณปรับ contrast, brightness, color balance และ saturation อย่างละเอียด", "การเปลี่ยนแปลงเล็กๆ สามารถสร้างความแตกต่างได้มาก โดยเฉพาะก่อนแชร์หรือเผยแพร่"] },
    { id: "text", titleKey: "text", body: ["ข้อความ ให้คุณวางหัวข้อหรือป้ายกำกับบนภาพ เริ่มจากพิมพ์ข้อความและเลือกสี จากนั้นใช้แผงลอยเพื่อปรับขนาดและตำแหน่ง", "เหมาะสำหรับหัวข้อ คำอธิบาย ป้ายกำกับ และกราฟิกโซเชียลแบบรวดเร็ว"] },
    { id: "export", titleKey: "export", body: ["ส่งออก จะสร้างภาพสุดท้ายเป็น PNG, JPG หรือ WebP และเปิดแผงดาวน์โหลด/แชร์ PNG และ WebP สามารถคงความโปร่งใสได้ ส่วน JPG จะเติมพื้นที่โปร่งใสด้วยสีพื้นหลังที่เลือก", "หากยังมีการเปลี่ยนแปลงที่ไม่ได้ใช้ในเครื่องมือปัจจุบัน แอปจะถามว่าต้องการบันทึกก่อนส่งออกหรือไม่"] },
  ],
} as const;
const manualHighlightTermsByLanguage: Partial<Record<LanguageCode, readonly string[]>> = {
  en: [
    "Cut My Pic",
    "Image Trimmer",
    "Image Masking",
    "Round Corners",
    "Add Border",
    "Constrain Proportions",
    "Freeform",
    "Crop",
    "Shadow",
    "Resize & Rotate",
    "Filters",
    "Adjustments",
    "Text",
    "Export",
  ],
  es: [
    "Cut My Pic",
    "Recorte de imagen",
    "Máscara de imagen",
    "Esquinas redondeadas",
    "Agregar borde",
    "Mantener proporciones",
    "Libre",
    "Recortar",
    "Sombra",
    "Redimensionar",
    "Filtros",
    "Ajustes",
    "Texto",
    "Exportar",
  ],
  fr: [
    "Cut My Pic",
    "Rognage d’image",
    "Masque d’image",
    "Coins arrondis",
    "Ajouter une bordure",
    "Conserver les proportions",
    "Libre",
    "Recadrer",
    "Ombre",
    "Redimensionner",
    "Filtres",
    "Réglages",
    "Texte",
    "Export",
  ],
  de: [
    "Cut My Pic",
    "Bild zuschneiden",
    "Bildmaske",
    "Abgerundete Ecken",
    "Rahmen hinzufügen",
    "Proportionen beibehalten",
    "Freiform",
    "Zuschneiden",
    "Schatten",
    "Größe ändern",
    "Filter",
    "Anpassungen",
    "Text",
    "Export",
  ],
  ja: [
    "Cut My Pic",
    "画像トリマー",
    "画像マスキング",
    "角丸",
    "枠線を追加",
    "比率を維持",
    "自由形式",
    "Crop",
    "Shadow",
    "Resize",
    "Filters",
    "Adjustments",
    "Text",
    "Export",
  ],
  zh: [
    "Cut My Pic",
    "图像裁切器",
    "图像遮罩",
    "圆角",
    "添加边框",
    "保持比例",
    "自由形式",
    "裁剪",
    "阴影",
    "调整大小",
    "滤镜",
    "调整",
    "文字",
    "导出",
  ],
  ko: [
    "Cut My Pic",
    "이미지 트리머",
    "이미지 마스킹",
    "둥근 모서리",
    "테두리 추가",
    "비율 유지",
    "자유형",
    "자르기",
    "그림자",
    "크기 조정",
    "필터",
    "조정",
    "텍스트",
    "내보내기",
  ],
  hi: [
    "Cut My Pic",
    "इमेज ट्रिमर",
    "इमेज मास्किंग",
    "गोल कोने",
    "बॉर्डर जोड़ें",
    "अनुपात बनाए रखें",
    "मुक्त रूप",
    "क्रॉप",
    "छाया",
    "आकार बदलें",
    "फ़िल्टर",
    "समायोजन",
    "टेक्स्ट",
    "निर्यात",
  ],
  pl: [
    "Cut My Pic",
    "Przycinanie obrazu",
    "Maska obrazu",
    "Zaokrąglone rogi",
    "Dodaj obramowanie",
    "Zachowaj proporcje",
    "Swobodnie",
    "Przycinanie",
    "Cień",
    "Zmiana rozmiaru",
    "Filtry",
    "Dopasowania",
    "Tekst",
    "Eksport",
  ],
  tr: [
    "Cut My Pic",
    "Görsel Kırpıcı",
    "Görsel Maskeleme",
    "Yuvarlatılmış Köşeler",
    "Kenarlık Ekle",
    "Oranı Koru",
    "Serbest Mod",
    "Kırpma",
    "Gölge",
    "Boyut Değiştir",
    "Filtreler",
    "Düzeltmeler",
    "Metin",
    "Dışa Aktar",
  ],
  id: [
    "Cut My Pic",
    "Pemotong Gambar",
    "Masking Gambar",
    "Sudut Membulat",
    "Tambah Bingkai",
    "Pertahankan Proporsi",
    "Bebas",
    "Crop",
    "Shadow",
    "Resize",
    "Filters",
    "Adjustments",
    "Text",
    "Export",
  ],
  ms: [
    "Cut My Pic", "Pemangkas Imej", "Penopengan Imej", "Sudut Bulat", "Tambah Bingkai", "Kekalkan Nisbah", "Bentuk Bebas", "Pangkas", "Bayang", "Ubah Saiz & Putar", "Penapis", "Pelarasan", "Teks", "Eksport",
  ],
  vi: [
    "Cut My Pic",
    "Cắt ảnh",
    "Che ảnh",
    "Bo góc",
    "Thêm viền",
    "Giữ tỉ lệ",
    "Tự do",
    "Cắt",
    "Đổ bóng",
    "Đổi kích thước",
    "Bộ lọc",
    "Điều chỉnh",
    "Văn bản",
    "Xuất",
  ],
  th: [
    "Cut My Pic",
    "เครื่องมือตัดภาพ",
    "การปิดบังภาพ",
    "มุมโค้ง",
    "เพิ่มเส้นขอบ",
    "คงอัตราส่วน",
    "อิสระ",
    "ครอป",
    "เงา",
    "ปรับขนาด",
    "ฟิลเตอร์",
    "การปรับแต่ง",
    "ข้อความ",
    "ส่งออก",
  ],
} as const;
const manualTermTranslationsByLanguage: Partial<Record<LanguageCode, Record<string, string>>> = {
  es: {
    "Image Trimmer": "Recorte de imagen",
    "Image Masking": "Máscara de imagen",
    "Round Corners": "Esquinas redondeadas",
    "Add Border": "Agregar borde",
    "Constrain Proportions": "Mantener proporciones",
    Freeform: "Libre",
    Crop: "Recortar",
    Shadow: "Sombra",
    Resize: "Redimensionar y rotar",
    Filters: "Filtros",
    Adjustments: "Ajustes",
    Text: "Texto",
    Export: "Exportar",
    Download: "Descarga",
  },
  fr: {
    "Image Trimmer": "Rognage d’image",
    "Image Masking": "Masque d’image",
    "Round Corners": "Coins arrondis",
    "Add Border": "Ajouter une bordure",
    "Constrain Proportions": "Conserver les proportions",
    Freeform: "Libre",
    Crop: "Recadrer",
    Shadow: "Ombre",
    Resize: "Redimensionner et pivoter",
    Filters: "Filtres",
    Adjustments: "Réglages",
    Text: "Texte",
    Export: "Exporter",
    Download: "Télécharger",
  },
  de: {
    "Image Trimmer": "Bild zuschneiden",
    "Image Masking": "Bildmaske",
    "Round Corners": "Ecken abrunden",
    "Add Border": "Rahmen hinzufügen",
    "Constrain Proportions": "Proportionen beibehalten",
    Freeform: "Freiform",
    Crop: "Zuschneiden",
    Shadow: "Schatten",
    Resize: "Größe ändern und drehen",
    Filters: "Filter",
    Adjustments: "Anpassungen",
    Text: "Text",
    Export: "Export",
    Download: "Herunterladen",
  },
  ja: {
    "Image Trimmer": "画像トリマー",
    "Image Masking": "画像マスキング",
    "Round Corners": "角丸",
    "Add Border": "枠線を追加",
    "Constrain Proportions": "比率を維持",
    Freeform: "自由形式",
    Crop: "トリミング",
    Shadow: "影",
    Resize: "サイズ変更と回転",
    Filters: "フィルター",
    Adjustments: "調整",
    Text: "テキスト",
    Export: "書き出し",
    Download: "ダウンロード",
  },
  zh: {
    Crop: "裁剪",
    "Round Corners": "圆角",
    "Add Border": "添加边框",
    Shadow: "阴影",
    Resize: "调整大小和旋转",
    Filters: "滤镜",
    Adjustments: "调整",
    Text: "文字",
    Export: "导出",
    Download: "下载",
  },
  ko: {
    Crop: "자르기",
    "Round Corners": "둥근 모서리",
    "Add Border": "테두리 추가",
    Shadow: "그림자",
    Resize: "크기 조정 및 회전",
    Filters: "필터",
    Adjustments: "조정",
    Text: "텍스트",
    Export: "내보내기",
    Download: "다운로드",
    Freeform: "자유형",
    "Constrain Proportions": "비율 유지",
  },
  hi: {
    Crop: "क्रॉप",
    "Round Corners": "राउंडेड कॉर्नर",
    "Add Border": "बॉर्डर जोड़ें",
    Shadow: "शैडो",
    Resize: "आकार बदलें और घुमाएँ",
    Filters: "फ़िल्टर",
    Adjustments: "समायोजन",
    Text: "टेक्स्ट",
    Export: "निर्यात",
    Download: "डाउनलोड",
    Freeform: "मुक्त रूप",
    "Constrain Proportions": "अनुपात बनाए रखें",
  },
  pt: {
    Crop: "Recortar",
    "Round Corners": "Cantos arredondados",
    "Add Border": "Adicionar borda",
    "Image Trimmer": "Cortador de imagem",
    "Image Masking": "Máscara de imagem",
    "Constrain Proportions": "Manter proporções",
    Freeform: "Forma livre",
    Shadow: "Sombra",
    Resize: "Redimensionar e girar",
    Filters: "Filtros",
    Adjustments: "Ajustes",
    Text: "Texto",
    Export: "Exportar",
    Download: "Baixar",
  },
  nl: {
    Crop: "Bijsnijden",
    "Round Corners": "Afgeronde hoeken",
    "Add Border": "Rand toevoegen",
    "Image Trimmer": "Afbeelding bijsnijden",
    "Image Masking": "Afbeeldingsmasker",
    "Constrain Proportions": "Proporties behouden",
    Freeform: "Vrij",
    Shadow: "Schaduw",
    Resize: "Formaat wijzigen en roteren",
    Filters: "Filters",
    Adjustments: "Aanpassingen",
    Text: "Tekst",
    Export: "Exporteren",
    Download: "Downloaden",
  },
  pl: {
    Crop: "Przycinanie",
    "Round Corners": "Zaokrąglone rogi",
    "Add Border": "Dodaj obramowanie",
    "Image Trimmer": "Przycinacz obrazu",
    "Image Masking": "Maska obrazu",
    "Constrain Proportions": "Zachowaj proporcje",
    Freeform: "Swobodnie",
    Shadow: "Cień",
    Resize: "Zmiana rozmiaru i obrót",
    Filters: "Filtry",
    Adjustments: "Dopasowania",
    Text: "Tekst",
    Export: "Eksport",
    Download: "Pobierz",
  },
  tr: {
    Crop: "Kırpma",
    "Round Corners": "Yuvarlatılmış köşeler",
    "Add Border": "Kenarlık ekle",
    "Image Trimmer": "Görsel Kesici",
    "Image Masking": "Görsel Maskeleme",
    "Constrain Proportions": "Oranı koru",
    Freeform: "Serbest mod",
    Shadow: "Gölge",
    Resize: "Boyut değiştir ve döndür",
    Filters: "Filtreler",
    Adjustments: "Düzeltmeler",
    Text: "Metin",
    Export: "Dışa aktar",
    Download: "İndir",
  },
  id: {
    Crop: "Pangkas",
    "Round Corners": "Sudut Membulat",
    "Add Border": "Tambah Bingkai",
    "Image Trimmer": "Pemotong Gambar",
    "Image Masking": "Masking Gambar",
    "Constrain Proportions": "Pertahankan Proporsi",
    Freeform: "Bebas",
    Shadow: "Bayangan",
    Resize: "Ubah Ukuran dan Putar",
    Filters: "Filter",
    Adjustments: "Penyesuaian",
    Text: "Teks",
    Export: "Ekspor",
    Download: "Unduh",
  },
  ms: {
    Crop: "Pangkas", "Round Corners": "Sudut Bulat", "Add Border": "Tambah Bingkai", "Image Trimmer": "Pemangkas Imej", "Image Masking": "Penopengan Imej", "Constrain Proportions": "Kekalkan Nisbah", Freeform: "Bentuk Bebas", Shadow: "Bayang", Resize: "Ubah Saiz & Putar", Filters: "Penapis", Adjustments: "Pelarasan", Text: "Teks", Export: "Eksport", Download: "Muat turun",
  },
  vi: {
    Crop: "Cắt",
    "Round Corners": "Bo góc",
    "Add Border": "Thêm viền",
    "Image Trimmer": "Cắt ảnh",
    "Image Masking": "Che ảnh",
    "Constrain Proportions": "Giữ tỉ lệ",
    Freeform: "Tự do",
    Shadow: "Đổ bóng",
    Resize: "Đổi kích thước và xoay",
    Filters: "Bộ lọc",
    Adjustments: "Điều chỉnh",
    Text: "Văn bản",
    Export: "Xuất",
    Download: "Tải xuống",
  },
  th: {
    Crop: "ครอป",
    "Round Corners": "มุมโค้ง",
    "Add Border": "เพิ่มเส้นขอบ",
    "Image Trimmer": "เครื่องมือตัดภาพ",
    "Image Masking": "การปิดบังภาพ",
    "Constrain Proportions": "คงอัตราส่วน",
    Freeform: "อิสระ",
    Shadow: "เงา",
    Resize: "ปรับขนาดและหมุน",
    Filters: "ฟิลเตอร์",
    Adjustments: "การปรับแต่ง",
    Text: "ข้อความ",
    Export: "ส่งออก",
    Download: "ดาวน์โหลด",
  },
} as const;
const manualPageCopy: Record<LanguageCode, { title: string; intro: string; jump: string; back: string }> = {
  en: {
    title: "User Manual",
    intro:
      "Cut My Pic is a fast, private image editor that runs entirely in your browser. It is built for quick edits that still need to look polished: trimming, rounded corners, borders, shadows, resize and rotate, color tweaks, text overlays, filters, and export. Because everything happens locally, you can drag in an image, fine-tune it, and download the result without sending your file to a server. That keeps the workflow simple, fast, and 100% private.",
    jump: "Jump to a section",
    back: "Back to editor",
  },
  es: {
    title: "Manual de usuario",
    intro:
      "Cut My Pic es un editor de imágenes rápido y privado que se ejecuta por completo en tu navegador. Está pensado para retoques rápidos que sigan viéndose bien: recorte, esquinas redondeadas, bordes, sombras, redimensionado, ajustes de color, texto, filtros y exportación. Como todo ocurre en local, puedes arrastrar una imagen, ajustarla y descargar el resultado sin enviar tu archivo a un servidor. Eso hace que el flujo sea simple, rápido y 100 % privado.",
    jump: "Ir a una sección",
    back: "Volver al editor",
  },
  fr: {
    title: "Manuel d’utilisation",
    intro:
      "Cut My Pic est un éditeur d’images rapide et privé qui s’exécute entièrement dans votre navigateur. Il est conçu pour des retouches rapides qui restent soignées : rognage, coins arrondis, bordures, ombres, redimensionnement, réglages de couleur, texte, filtres et export. Comme tout se fait en local, vous pouvez déposer une image, la régler puis télécharger le résultat sans envoyer votre fichier à un serveur. Le flux reste simple, rapide et 100 % privé.",
    jump: "Aller à une section",
    back: "Retour à l’éditeur",
  },
  de: {
    title: "Benutzerhandbuch",
    intro:
      "Cut My Pic ist ein schneller, privater Bildeditor, der vollständig im Browser läuft. Er ist für schnelle Bearbeitungen gedacht, die trotzdem sauber aussehen sollen: Zuschneiden, runde Ecken, Rahmen, Schatten, Größenänderung, Farbanpassungen, Text, Filter und Export. Da alles lokal passiert, kannst du ein Bild hineinziehen, fein abstimmen und das Ergebnis herunterladen, ohne deine Datei an einen Server zu senden. So bleibt der Ablauf einfach, schnell und 100 % privat.",
    jump: "Zu einem Abschnitt springen",
    back: "Zurück zum Editor",
  },
  ja: {
    title: "ユーザーマニュアル",
    intro:
      "Cut My Pic は、ブラウザ内だけで動作する高速でプライベートな画像エディタです。トリミング、角丸、枠線、影、サイズ変更、色調整、テキスト、フィルター、書き出しなど、仕上がりをきれいに整えたい素早い編集向けに作られています。すべてローカルで処理されるため、画像をドラッグして調整し、ファイルをサーバーへ送らずに結果をダウンロードできます。作業はシンプルで高速、そして100%プライベートです。",
    jump: "セクションへ移動",
    back: "エディタに戻る",
  },
  zh: {
    title: "用户手册",
    intro:
      "Cut My Pic 是一款快速、私密、完全在浏览器中运行的图片编辑器。它适合需要快速处理、但仍要保持精致效果的编辑：裁剪、圆角、边框、阴影、尺寸调整、颜色微调、文字、滤镜和导出。由于所有处理都在本地完成，你可以拖入图片、进行微调，并直接下载结果，而无需把文件发送到服务器。整个流程简单、快速，并且 100% 私密。",
    jump: "跳转到章节",
    back: "返回编辑器",
  },
  ko: {
    title: "사용자 설명서",
    intro:
      "Cut My Pic은 브라우저 안에서만 실행되는 빠르고 개인적인 이미지 편집기입니다. 자르기, 둥근 모서리, 테두리, 그림자, 크기 조정, 색상 보정, 텍스트, 필터, 내보내기처럼 빠르지만 깔끔하게 마무리해야 하는 편집에 맞춰 제작되었습니다. 모든 작업이 로컬에서 이루어지므로 이미지를 끌어다 놓고 조정한 뒤 파일을 서버로 보내지 않고 바로 내려받을 수 있습니다. 그래서 작업 흐름이 단순하고 빠르며 100% 비공개입니다.",
    jump: "섹션으로 이동",
    back: "편집기로 돌아가기",
  },
  hi: {
    title: "उपयोगकर्ता मार्गदर्शिका",
    intro:
      "Cut My Pic एक तेज़, निजी इमेज एडिटर है जो पूरी तरह आपके ब्राउज़र में चलता है। यह उन त्वरित संपादनों के लिए बनाया गया है जिन्हें फिर भी साफ़ और पेशेवर दिखना चाहिए: क्रॉप, राउंडेड कॉर्नर, बॉर्डर, शैडो, आकार बदलना, रंग सुधार, टेक्स्ट, फ़िल्टर और एक्सपोर्ट। क्योंकि सब कुछ स्थानीय रूप से होता है, आप इमेज को ड्रैग करके ठीक कर सकते हैं और फ़ाइल को सर्वर पर भेजे बिना परिणाम डाउनलोड कर सकते हैं। इससे काम आसान, तेज़ और 100% निजी रहता है।",
    jump: "किसी अनुभाग पर जाएँ",
    back: "एडिटर पर वापस जाएँ",
  },
  pt: {
    title: "Manual do usuário",
    intro:
      "Cut My Pic é um editor de imagens rápido e privado que funciona totalmente no seu navegador. Ele foi feito para edições rápidas que ainda precisam ficar bem acabadas: recorte, cantos arredondados, bordas, sombras, redimensionamento, ajustes de cor, texto, filtros e exportação. Como tudo acontece localmente, você pode arrastar uma imagem, ajustar os detalhes e baixar o resultado sem enviar seu arquivo para um servidor. Isso mantém o fluxo simples, rápido e 100% privado.",
    jump: "Ir para uma seção",
    back: "Voltar ao editor",
  },
  it: {
    title: "Manuale utente",
    intro:
      "Cut My Pic è un editor di immagini veloce e privato che funziona interamente nel browser. È pensato per modifiche rapide ma curate: ritaglio, angoli arrotondati, bordi, ombre, ridimensionamento, regolazioni colore, testo, filtri ed esportazione. Poiché tutto avviene in locale, puoi trascinare un’immagine, rifinirla e scaricare il risultato senza inviare il file a un server. Il flusso resta semplice, veloce e 100% privato.",
    jump: "Vai a una sezione",
    back: "Torna all’editor",
  },
  ru: {
    title: "Руководство пользователя",
    intro:
      "Cut My Pic — это быстрый и приватный редактор изображений, который работает полностью в браузере. Он создан для быстрых правок, которые при этом выглядят аккуратно: кадрирование, скругление углов, рамки, тени, изменение размера, цветокоррекция, текст, фильтры и экспорт. Поскольку всё происходит локально, вы можете перетащить изображение, настроить его и скачать результат, не отправляя файл на сервер. Это делает работу простой, быстрой и на 100% приватной.",
    jump: "Перейти к разделу",
    back: "Вернуться в редактор",
  },
  ar: {
    title: "دليل المستخدم",
    intro:
      "Cut My Pic هو محرر صور سريع وخاص يعمل بالكامل داخل المتصفح. صُمم للتعديلات السريعة التي ما زالت تحتاج إلى مظهر أنيق: القص، تدوير الزوايا، الإطارات، الظلال، تغيير الحجم، ضبط الألوان، النصوص، الفلاتر والتصدير. وبما أن كل شيء يتم محليًا، يمكنك سحب صورة وتعديلها ثم تنزيل النتيجة دون إرسال الملف إلى خادم. هذا يجعل سير العمل بسيطًا وسريعًا وخاصة بنسبة 100%.",
    jump: "الانتقال إلى قسم",
    back: "العودة إلى المحرر",
  },
  nl: {
    title: "Gebruikershandleiding",
    intro:
      "Cut My Pic is een snelle, privé afbeeldingseditor die volledig in je browser draait. Hij is gemaakt voor snelle bewerkingen die er toch netjes uit moeten zien: bijsnijden, afgeronde hoeken, randen, schaduwen, formaat wijzigen, kleuraanpassingen, tekst, filters en exporteren. Omdat alles lokaal gebeurt, kun je een afbeelding slepen, verfijnen en het resultaat downloaden zonder je bestand naar een server te sturen. Dat houdt de workflow simpel, snel en 100% privé.",
    jump: "Ga naar een sectie",
    back: "Terug naar de editor",
  },
  pl: {
    title: "Instrukcja obsługi",
    intro:
      "Cut My Pic to szybki, prywatny edytor obrazów działający w całości w przeglądarce. Został stworzony do szybkich poprawek, które nadal mają wyglądać dopracowanie: kadrowanie, zaokrąglone rogi, ramki, cienie, zmiana rozmiaru, korekta kolorów, tekst, filtry i eksport. Ponieważ wszystko dzieje się lokalnie, możesz przeciągnąć obraz, dopracować go i pobrać wynik bez wysyłania pliku na serwer. Dzięki temu praca pozostaje prosta, szybka i w 100% prywatna.",
    jump: "Przejdź do sekcji",
    back: "Wróć do edytora",
  },
  tr: {
    title: "Kullanım kılavuzu",
    intro:
      "Cut My Pic, tamamen tarayıcınızda çalışan hızlı ve gizli bir görsel düzenleyicidir. Kırpma, yuvarlatılmış köşeler, çerçeveler, gölgeler, boyut değiştirme, renk ayarları, metin, filtreler ve dışa aktarma gibi hızlı ama özenli görünmesi gereken düzenlemeler için tasarlanmıştır. Her şey yerel olarak işlendiği için bir görseli sürükleyip ince ayar yapabilir ve dosyanızı bir sunucuya göndermeden sonucu indirebilirsiniz. Bu da süreci basit, hızlı ve %100 gizli tutar.",
    jump: "Bir bölüme git",
    back: "Düzenleyiciye dön",
  },
  id: {
    title: "Panduan pengguna",
    intro:
      "Cut My Pic adalah editor gambar cepat dan pribadi yang berjalan sepenuhnya di browser Anda. Aplikasi ini dibuat untuk pengeditan cepat yang tetap harus terlihat rapi: memotong, sudut membulat, bingkai, bayangan, ubah ukuran, penyesuaian warna, teks, filter, dan ekspor. Karena semuanya diproses secara lokal, Anda dapat menyeret gambar, menyempurnakannya, dan mengunduh hasilnya tanpa mengirim file ke server. Alurnya jadi sederhana, cepat, dan 100% pribadi.",
    jump: "Buka bagian",
    back: "Kembali ke editor",
  },
  ms: {
    title: "Panduan Pengguna",
    intro:
      "Cut My Pic ialah penyunting imej yang pantas dan peribadi serta berfungsi sepenuhnya dalam pelayar anda. Ia dibina untuk suntingan pantas yang masih kelihatan kemas: memangkas, membulatkan sudut, menambah bingkai dan bayang, mengubah saiz, melaras warna, menambah teks dan penapis, serta mengeksport. Oleh sebab semuanya diproses secara setempat, anda boleh menyeret masuk imej, memperhalusnya dan memuat turun hasil tanpa menghantar fail ke pelayan. Aliran kerja kekal ringkas, pantas dan 100% peribadi.",
    jump: "Lompat ke bahagian",
    back: "Kembali ke penyunting",
  },
  vi: {
    title: "Hướng dẫn sử dụng",
    intro:
      "Cut My Pic là một trình chỉnh sửa ảnh nhanh và riêng tư, chạy hoàn toàn trong trình duyệt. Ứng dụng được tạo cho các chỉnh sửa nhanh nhưng vẫn cần trông gọn gàng: cắt, bo góc, thêm viền, đổ bóng, đổi kích thước, chỉnh màu, văn bản, bộ lọc và xuất ảnh. Vì mọi thứ đều xử lý cục bộ, bạn có thể kéo ảnh vào, tinh chỉnh rồi tải kết quả xuống mà không cần gửi tệp lên máy chủ. Nhờ vậy quy trình đơn giản, nhanh và 100% riêng tư.",
    jump: "Đi đến mục",
    back: "Quay lại trình chỉnh sửa",
  },
  th: {
    title: "คู่มือผู้ใช้",
    intro:
      "Cut My Pic คือโปรแกรมแต่งภาพที่รวดเร็วและเป็นส่วนตัวซึ่งทำงานทั้งหมดในเบราว์เซอร์ ออกแบบมาสำหรับการแก้ไขแบบรวดเร็วแต่ยังคงดูเรียบร้อย: ครอป มุมโค้ง เส้นขอบ เงา ปรับขนาด ปรับสี ข้อความ ฟิลเตอร์ และการส่งออก เพราะทุกอย่างเกิดขึ้นในเครื่อง คุณจึงลากภาพเข้ามา ปรับแต่ง และดาวน์โหลดผลลัพธ์ได้โดยไม่ต้องส่งไฟล์ไปยังเซิร์ฟเวอร์ ทำให้ขั้นตอนการทำงานเรียบง่าย รวดเร็ว และเป็นส่วนตัว 100%",
    jump: "ไปยังส่วน",
    back: "กลับไปยังตัวแก้ไข",
  },
};

const faqPageCopyByLanguage: Record<LanguageCode, { title: string; intro: string; back: string; general: string; social: string }> = {
  en: {
    title: "FAQ",
    intro: "Answers to common questions about Cut My Pic, the browser-based editor, and how the local workflow works.",
    back: "Back to editor",
    general: "General",
    social: "Social Networks",
  },
  es: {
    title: "Preguntas frecuentes",
    intro: "Respuestas a preguntas comunes sobre Cut My Pic, el editor basado en el navegador y cómo funciona el flujo local.",
    back: "Volver al editor",
    general: "General",
    social: "Redes sociales",
  },
  fr: {
    title: "FAQ",
    intro: "Réponses aux questions fréquentes sur Cut My Pic, l’éditeur dans le navigateur, et le fonctionnement local.",
    back: "Retour à l’éditeur",
    general: "Général",
    social: "Réseaux sociaux",
  },
  de: {
    title: "FAQ",
    intro: "Antworten auf häufige Fragen zu Cut My Pic, dem Browser-Editor und dem lokalen Workflow.",
    back: "Zurück zum Editor",
    general: "Allgemein",
    social: "Soziale Netzwerke",
  },
  ja: {
    title: "よくある質問",
    intro: "Cut My Pic、ブラウザベースのエディタ、そしてローカルでの作業の流れについてのよくある質問への回答です。",
    back: "エディタに戻る",
    general: "一般",
    social: "SNS",
  },
  zh: {
    title: "常见问题",
    intro: "有关 Cut My Pic、浏览器编辑器以及本地工作流程的常见问题解答。",
    back: "返回编辑器",
    general: "常规",
    social: "社交网络",
  },
  ko: {
    title: "자주 묻는 질문",
    intro: "Cut My Pic, 브라우저 기반 편집기, 그리고 로컬 작업 방식에 대한 일반적인 질문과 답변입니다.",
    back: "편집기로 돌아가기",
    general: "일반",
    social: "소셜 네트워크",
  },
  hi: {
    title: "अक्सर पूछे जाने वाले प्रश्न",
    intro: "Cut My Pic, ब्राउज़र-आधारित एडिटर, और लोकल वर्कफ़्लो के बारे में सामान्य सवालों के जवाब।",
    back: "एडिटर पर वापस जाएँ",
    general: "सामान्य",
    social: "सोशल नेटवर्क",
  },
  pt: {
    title: "Perguntas frequentes",
    intro: "Respostas para perguntas comuns sobre o Cut My Pic, o editor no navegador e como o fluxo local funciona.",
    back: "Voltar ao editor",
    general: "Geral",
    social: "Redes sociais",
  },
  it: {
    title: "Domande frequenti",
    intro: "Risposte alle domande comuni su Cut My Pic, l’editor nel browser e il flusso di lavoro locale.",
    back: "Torna all’editor",
    general: "Generale",
    social: "Social network",
  },
  ru: {
    title: "Часто задаваемые вопросы",
    intro: "Ответы на частые вопросы о Cut My Pic, браузерном редакторе и локальном рабочем процессе.",
    back: "Вернуться к редактору",
    general: "Общее",
    social: "Социальные сети",
  },
  ar: {
    title: "الأسئلة الشائعة",
    intro: "إجابات على الأسئلة الشائعة حول Cut My Pic، ومحرر المتصفح، وكيفية عمل التحرير المحلي.",
    back: "العودة إلى المحرر",
    general: "عام",
    social: "الشبكات الاجتماعية",
  },
  nl: {
    title: "Veelgestelde vragen",
    intro: "Antwoorden op veelgestelde vragen over Cut My Pic, de browser-editor en hoe de lokale workflow werkt.",
    back: "Terug naar de editor",
    general: "Algemeen",
    social: "Sociale netwerken",
  },
  pl: {
    title: "FAQ",
    intro: "Odpowiedzi na najczęstsze pytania o Cut My Pic, edytor w przeglądarce i lokalny sposób pracy.",
    back: "Wróć do edytora",
    general: "Ogólne",
    social: "Sieci społecznościowe",
  },
  tr: {
    title: "Sık Sorulan Sorular",
    intro: "Cut My Pic, tarayıcı tabanlı düzenleyici ve yerel çalışma akışı hakkında sık sorulan soruların yanıtları.",
    back: "Düzenleyiciye dön",
    general: "Genel",
    social: "Sosyal ağlar",
  },
  id: {
    title: "FAQ",
    intro: "Jawaban atas pertanyaan umum tentang Cut My Pic, editor berbasis browser, dan cara kerja alur lokal.",
    back: "Kembali ke editor",
    general: "Umum",
    social: "Jaringan sosial",
  },
  ms: {
    title: "Soalan Lazim",
    intro: "Jawapan kepada soalan umum tentang Cut My Pic, penyunting berasaskan pelayar dan cara aliran kerja setempat berfungsi.",
    back: "Kembali ke penyunting",
    general: "Umum",
    social: "Rangkaian sosial",
  },
  vi: {
    title: "Câu hỏi thường gặp",
    intro: "Câu trả lời cho các câu hỏi thường gặp về Cut My Pic, trình chỉnh sửa trên trình duyệt và quy trình làm việc cục bộ.",
    back: "Quay lại trình chỉnh sửa",
    general: "Chung",
    social: "Mạng xã hội",
  },
  th: {
    title: "คำถามที่พบบ่อย",
    intro: "คำตอบสำหรับคำถามที่พบบ่อยเกี่ยวกับ Cut My Pic, โปรแกรมแก้ไขบนเบราว์เซอร์ และวิธีการทำงานแบบใช้เครื่องของคุณเอง",
    back: "กลับไปที่ตัวแก้ไข",
    general: "ทั่วไป",
    social: "โซเชียลเน็ตเวิร์ก",
  },
};

type FaqEntry = {
  question: string;
  answer: string;
  answerHtml?: string;
};

type FaqSection = {
  title: string;
  entries: FaqEntry[];
};

const faqSections: FaqSection[] = [
  {
    title: "General",
    entries: [
      {
        question: "What is Cut My Pic?",
        answer: "Originally conceived by Sean Crownover (maker of Jishin.Watch) in 2009, this new 2026 version of Cut My Pic is a private, browser-based image editor for cropping and masking, rounded corners, borders, shadows, resize, filters, adjustments, sharpen and soften, text, stamps, history, fullscreen editing, and export.",
        answerHtml: 'Originally conceived by Sean Crownover (<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">maker of Jishin.Watch</a>) in 2009, this new 2026 version of Cut My Pic is a private, browser-based image editor for cropping and masking, rounded corners, borders, shadows, resize, filters, adjustments, sharpen and soften, text, stamps, history, fullscreen editing, and export.',
      },
      {
        question: "Does Cut My Pic upload my images?",
        answer: "No. Editing runs locally in your browser, so your images stay on your device.",
      },
      {
        question: "What formats can I upload?",
        answer: "You can upload common image formats such as PNG, JPG, WebP, and GIF.",
      },
      {
        question: "What format does export use?",
        answer: "Export supports PNG, JPG, and WebP. PNG and WebP can preserve transparency; JPG uses your selected background color.",
      },
      {
        question: "Why does shadow make the image area look different?",
        answer: "Shadow expands the canvas so the shadow itself is not clipped. The photo stays the same visual scale after the shadow is applied.",
      },
      {
        question: "What do sharpen and soften do?",
        answer: "Sharpen makes edges look crisper and more defined, while soften does the opposite by gently blurring the image. Both preview live so you can fine-tune the amount before applying.",
      },
      {
        question: "Can I add multiple borders?",
        answer: "Yes. Each time you apply a border, it becomes part of the image so later crop or corner edits can reapply the full border stack.",
      },
      {
        question: "Can I use text and stamps together?",
        answer: "Yes. Text mode lets you add custom text, while Stamp mode gives you preset graphics and labels. You can switch between them from the same text panel.",
      },
      {
        question: "What is the best workflow?",
        answer: "A simple order works best: crop first, then rounded corners or border, then filters and adjustments, then text or stamps, and export at the end.",
      },
      {
        question: "Can I undo more than one step?",
        answer: "Yes. Open the history window to jump back to an earlier edit point, including the original image if needed.",
      },
      {
        question: "How do Undo and Edit History work together?",
        answer: "Undo moves back one applied edit at a time. Edit History shows the complete sequence, with #0 as the original image and each later number representing one applied edit. Select a state to jump directly to it; any later states remain available as redo states until you apply a different edit.",
      },
      {
        question: "What does the history step counter show?",
        answer: "The history step counter shows how many edits have been applied after the original image. Temporary previews do not become history states until you press Apply. The Reusable Step Counter at the bottom may be lower because it counts only the steps that can be saved in a workflow.",
      },
      {
        question: "What is a reusable workflow?",
        answer: "A reusable workflow is a saved sequence of supported editing instructions from your applied history. Use Save Workflow in the Edit History footer, give it a name, and use Load Workflow to apply it to another image. Saved workflows stay in this browser and can be deleted from the workflow library.",
      },
      {
        question: "How do workflows handle crop and resize steps?",
        answer: "When a workflow contains a crop, you position the crop on the new image instead of reusing old coordinates. If the workflow also has a resize target, the crop selector locks to that aspect ratio and the final workflow step resizes the result to the saved pixel dimensions. The editor warns before enlarging a crop that is smaller than the target because upscaling can reduce quality.",
      },
      {
        question: "How does fullscreen editing help?",
        answer: "Fullscreen mode gives you more room to work, and the tool rail stays available so you can keep editing while seeing more of the image.",
      },
      {
        question: "What’s the difference between crop, cut, and mask?",
        answer: "Crop removes part of the image, cut modes define the shape you want to keep, and mask modes let you blur or color the selected area instead of trimming it away.",
      },
      {
        question: "Does Cut My Pic work offline?",
        answer: "Yes, the editor runs in your browser, so it can keep working even when you are not connected, as long as the page is already loaded.",
      },
      {
        question: "Why do some tools have Apply buttons?",
        answer: "Apply buttons lock in an edit so you can see it become part of the history and continue building on that result with other tools.",
      },
    ],
  },
  {
    title: "Social Networks",
    entries: [
      {
        question: "Instagram",
        answer:
          "Best Cut My Pic modes: 4:5 Cut for feed posts, 1:1 Cut for square posts, and 9:16 Cut for Stories and Reels. Keep text large, centered, and away from the edges. Crop first, then use Rounded Corners or Border only if the design needs them. For profile-style graphics, Circle Cut works well. If you want a white or colored frame around the image, apply the border after you finish cropping.",
      },
      {
        question: "Facebook",
        answer:
          "Best Cut My Pic modes: 1:1 Cut or 4:5 Cut for feed posts, 1.91:1 Cut for link previews, and 9:16 Cut for Stories. Make the main subject clear at smaller sizes and avoid tiny text. Square Cut is a safe default for feed posts, while 16:9 is useful when you want a wider link-share graphic. Borders help if you need contrast against the feed background.",
      },
      {
        question: "X",
        answer:
          "Best Cut My Pic modes: 16:9 Cut for most posts, 1.91:1 Cut for wide link-style visuals, and 3:1 Cut for banner-style headers. Use bold, simple compositions and avoid cramming too much text into the image. Keep the focal point centered. A subtle border can help the image stand out in a fast-scrolling timeline.",
      },
      {
        question: "LinkedIn",
        answer:
          "Best Cut My Pic modes: 1.91:1 Cut for link-preview style graphics and 1:1 Cut for clean square posts. Keep the headline short and the design more businesslike than decorative. Use borders sparingly and keep contrast strong so the graphic still reads on desktop and mobile.",
      },
      {
        question: "TikTok and Douyin",
        answer:
          "Best Cut My Pic preset: TikTok/Douyin at 9:16, with a recommended size of 1080 × 1920 px. Both platforms favor vertical content, so keep important elements in the center where interface controls are less likely to cover them. Leave extra breathing room around text and faces.",
      },
      {
        question: "Pinterest",
        answer:
          "Best Cut My Pic mode: 2:3 Cut. Pinterest performs well with tall vertical images, and 2:3 is the safest default. A strong subject plus a readable title block usually works better than a busy collage. Crop vertically and keep the headline high contrast. If you want a polished pin, add a border only after the crop is final so the frame follows the final shape.",
      },
      {
        question: "YouTube",
        answer:
          "Best Cut My Pic mode: 16:9 Cut. YouTube thumbnails should be wide, bold, and large enough to stay sharp in search and recommendations. Use big text, bold contrast, and a clear focal point. Keep the layout simple, and use borders only if they improve separation from the background. For thumbnail designs, readability matters more than decorative detail.",
      },
      {
        question: "Xiaohongshu",
        answer:
          "Best Cut My Pic preset: Xiaohongshu Note at 3:4, with a recommended size of 1080 × 1440 px. This portrait format gives product photos, travel images, tutorials, and text-led notes enough vertical room. Keep the main subject and important text away from the outer edges so the post remains readable in the feed.",
      },
      {
        question: "WeChat",
        answer:
          "Best Cut My Pic preset: WeChat Cover at 2.35:1, with a recommended size of 900 × 383 px. It is a wide article-cover format, so use a strong horizontal composition, keep the focal point clear, and avoid small text near the edges.",
      },
      {
        question: "Weibo",
        answer:
          "Best Cut My Pic preset: Weibo Feed at 1:1, with a recommended size of 1080 × 1080 px. Square images are dependable in the feed and work well for photos, announcements, and graphics. Keep important content centered and apply decorative borders only after the crop is final.",
      },
      {
        question: "Which social cut sizes are available?",
        answer:
          "The current crop presets are: Instagram Post 4:5 (1080 × 1350), Story/Reel 9:16 (1080 × 1920), TikTok/Douyin 9:16 (1080 × 1920), Pinterest Pin 2:3 (1000 × 1500), YouTube Thumbnail 16:9 (1280 × 720), Facebook Feed 1:1 (1080 × 1080), Facebook Cover 1.91:1 (1200 × 628), X Post 16:9 (1600 × 900), X Header 3:1 (1500 × 500), LinkedIn Cover 1.91:1 (1200 × 628), Xiaohongshu Note 3:4 (1080 × 1440), WeChat Cover 2.35:1 (900 × 383), and Weibo Feed 1:1 (1080 × 1080).",
      },
    ],
  },
];

const faqEntries = faqSections.flatMap((section) => section.entries);
const faqSectionsWithEntries = faqSections;

const localizedFaqPageCopy = computed(() => faqPageCopyByLanguage[language.value] ?? faqPageCopyByLanguage.en);
const localizedFaqSectionsWithEntries = computed<FaqSection[]>(() => {
  const localizedFaqSectionsByLanguage: Partial<Record<LanguageCode, FaqSection[]>> = {
    en: faqSections,
    es: [
      {
        title: "General",
        entries: [
          { question: "¿Qué es Cut My Pic?", answer: "Concebido originalmente por Sean Crownover (creador de Jishin.Watch) en 2009, esta nueva versión de Cut My Pic de 2026 es un editor de imágenes privado en el navegador para recortar, enmascarar, redondear esquinas, añadir bordes, sombras, cambiar el tamaño, aplicar filtros, hacer ajustes, enfocar y suavizar, añadir texto, sellos, ver el historial, editar a pantalla completa y exportar.", answerHtml: 'Concebido originalmente por Sean Crownover (<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">creador de Jishin.Watch</a>) en 2009, esta nueva versión de Cut My Pic de 2026 es un editor de imágenes privado en el navegador para recortar, enmascarar, redondear esquinas, añadir bordes, sombras, cambiar el tamaño, aplicar filtros, hacer ajustes, enfocar y suavizar, añadir texto, sellos, ver el historial, editar a pantalla completa y exportar.' },
          { question: "¿Cut My Pic sube mis imágenes?", answer: "No. La edición se realiza localmente en tu navegador, así que tus imágenes permanecen en tu dispositivo." },
          { question: "¿Qué formatos puedo subir?", answer: "Puedes subir formatos comunes como PNG, JPG, WebP y GIF." },
          { question: "¿Qué formato usa la exportación?", answer: "La exportación admite PNG, JPG y WebP. PNG y WebP pueden conservar la transparencia; JPG usa el color de fondo elegido." },
          { question: "¿Por qué la sombra hace que el área de la imagen se vea distinta?", answer: "La sombra amplía el lienzo para que no se recorte. La foto conserva el mismo tamaño visual después de aplicar la sombra." },
          { question: "¿Qué hacen enfocar y suavizar?", answer: "Enfocar hace los bordes más nítidos y definidos, mientras que suavizar hace lo contrario difuminando la imagen de forma suave. Ambos tienen vista previa en vivo para ajustar la intensidad antes de aplicar." },
          { question: "¿Puedo añadir varios bordes?", answer: "Sí. Cada vez que aplicas un borde, pasa a formar parte de la imagen, así que los recortes o cambios de esquinas posteriores pueden reaplicar toda la pila de bordes." },
          { question: "¿Puedo usar texto y sellos juntos?", answer: "Sí. El modo Texto permite añadir texto personalizado y el modo Sello ofrece gráficos y etiquetas predefinidos. Puedes cambiar entre ambos desde el mismo panel." },
          { question: "¿Cuál es el mejor flujo de trabajo?", answer: "Suele funcionar mejor este orden: recortar primero, luego esquinas redondeadas o borde, después filtros y ajustes, luego texto o sellos, y exportar al final." },
          { question: "¿Puedo deshacer más de un paso?", answer: "Sí. Abre la ventana de historial para volver a un punto anterior, incluso a la imagen original si hace falta." },
          { question: "¿Cómo ayuda la edición a pantalla completa?", answer: "El modo pantalla completa te da más espacio de trabajo y mantiene disponible la barra de herramientas para seguir editando mientras ves más de la imagen." },
          { question: "¿Cuál es la diferencia entre recortar, cortar y enmascarar?", answer: "Recortar elimina parte de la imagen, los modos de corte definen la forma que quieres conservar y los modos de máscara permiten difuminar o colorear el área seleccionada en lugar de eliminarla." },
          { question: "¿Cut My Pic funciona sin conexión?", answer: "Sí. Como el editor funciona en tu navegador, puede seguir funcionando aunque no estés conectado, siempre que la página ya esté cargada." },
          { question: "¿Por qué algunas herramientas tienen botones Aplicar?", answer: "Los botones Aplicar confirman una edición para que pase al historial y puedas seguir trabajando a partir de ese resultado con otras herramientas." },
        ],
      },
      {
        title: "Redes sociales",
        entries: [
          { question: "Instagram", answer: "Modos recomendados: recorte 4:5 para publicaciones del feed, 1:1 para publicaciones cuadradas y 9:16 para Stories y Reels. Mantén el texto grande, centrado y lejos de los bordes. Recorta primero y añade esquinas redondeadas o borde solo si el diseño lo necesita." },
          { question: "Facebook", answer: "Modos recomendados: 1:1 o 4:5 para publicaciones del feed, 1.91:1 para vistas previas de enlaces y 9:16 para Stories. Haz que el sujeto principal se vea bien en tamaños pequeños y evita el texto diminuto." },
          { question: "X", answer: "Modos recomendados: 16:9 para la mayoría de publicaciones, 1.91:1 para visuales anchos de estilo enlace y 3:1 para cabeceras tipo banner. Mantén la composición simple y el foco centrado." },
          { question: "LinkedIn", answer: "Modos recomendados: 1.91:1 para gráficos de vista previa de enlace y 1:1 para publicaciones cuadradas limpias. Mantén el título corto y un estilo más profesional que decorativo." },
          { question: "TikTok", answer: "Modo recomendado: 9:16. TikTok está pensado para contenido vertical, así que coloca los elementos importantes en el centro porque la interfaz de la app cubre la parte superior e inferior." },
          { question: "Pinterest", answer: "Modo recomendado: 2:3. Pinterest funciona muy bien con imágenes verticales y 2:3 es el valor predeterminado más seguro. Un sujeto fuerte y un bloque de título legible suelen funcionar mejor que un collage recargado." },
          { question: "YouTube", answer: "Modo recomendado: 16:9. Las miniaturas de YouTube deben ser anchas, llamativas y lo bastante grandes para verse nítidas en búsquedas y recomendaciones." },
          { question: "¿Qué tamaños de corte para redes sociales hay disponibles?", answer: "Los ajustes actuales son: Instagram 4:5, 1:1 y 9:16; Facebook 1:1, 4:5, 1.91:1 y 9:16; X 16:9, 1.91:1 y 3:1; LinkedIn 1.91:1 y 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; y Weibo 1:1." },
        ],
      },
    ],
    fr: [
      {
        title: "Général",
        entries: [
          { question: "Qu’est-ce que Cut My Pic ?", answer: "Conçu à l’origine par Sean Crownover (créateur de Jishin.Watch) en 2009, cette nouvelle version 2026 de Cut My Pic est un éditeur d’images privé dans le navigateur pour rogner, masquer, arrondir les coins, ajouter des bordures, des ombres, redimensionner, appliquer des filtres, faire des réglages, accentuer et adoucir, ajouter du texte, des tampons, consulter l’historique, éditer en plein écran et exporter.", answerHtml: 'Conçu à l’origine par Sean Crownover (<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">créateur de Jishin.Watch</a>) en 2009, cette nouvelle version 2026 de Cut My Pic est un éditeur d’images privé dans le navigateur pour rogner, masquer, arrondir les coins, ajouter des bordures, des ombres, redimensionner, appliquer des filtres, faire des réglages, accentuer et adoucir, ajouter du texte, des tampons, consulter l’historique, éditer en plein écran et exporter.' },
          { question: "Cut My Pic envoie-t-il mes images ?", answer: "Non. La retouche se fait localement dans votre navigateur, donc vos images restent sur votre appareil." },
          { question: "Quels formats puis-je importer ?", answer: "Vous pouvez importer les formats courants comme PNG, JPG, WebP et GIF." },
          { question: "Quel format utilise l’export ?", answer: "L’export prend en charge PNG, JPG et WebP. PNG et WebP peuvent conserver la transparence ; JPG utilise la couleur d’arrière-plan choisie." },
          { question: "Pourquoi l’ombre modifie-t-elle l’apparence de la zone d’image ?", answer: "L’ombre agrandit le canevas pour éviter qu’elle soit coupée. La photo conserve la même taille visuelle après application de l’ombre." },
          { question: "Que font accentuer et adoucir ?", answer: "Accentuer rend les contours plus nets et plus définis, tandis qu’adoucir fait l’inverse en floutant légèrement l’image. Les deux offrent un aperçu en direct pour régler l’intensité avant application." },
          { question: "Puis-je ajouter plusieurs bordures ?", answer: "Oui. Chaque bordure appliquée devient partie intégrante de l’image, ce qui permet aux recadrages ou ajustements de coins ultérieurs de réappliquer toute la pile de bordures." },
          { question: "Puis-je utiliser le texte et les tampons ensemble ?", answer: "Oui. Le mode Texte permet d’ajouter du texte personnalisé, et le mode Tampon fournit des graphiques et libellés prédéfinis. Vous pouvez passer de l’un à l’autre depuis le même panneau." },
          { question: "Quel est le meilleur flux de travail ?", answer: "L’ordre le plus simple fonctionne bien : rogner d’abord, puis coins arrondis ou bordure, ensuite filtres et réglages, puis texte ou tampons, et export à la fin." },
          { question: "Puis-je annuler plus d’une étape ?", answer: "Oui. Ouvrez l’historique pour revenir à un point d’édition précédent, y compris l’image d’origine si nécessaire." },
          { question: "Comment l’édition plein écran aide-t-elle ?", answer: "Le mode plein écran vous donne plus d’espace de travail et conserve la barre d’outils disponible pour continuer à éditer tout en voyant davantage l’image." },
          { question: "Quelle est la différence entre rogner, couper et masquer ?", answer: "Rogner supprime une partie de l’image, les modes de coupe définissent la forme que vous souhaitez conserver, et les modes de masque permettent de flouter ou colorer la zone sélectionnée au lieu de la supprimer." },
          { question: "Cut My Pic fonctionne-t-il hors ligne ?", answer: "Oui. Comme l’éditeur fonctionne dans votre navigateur, il peut continuer à fonctionner même sans connexion, tant que la page a déjà été chargée." },
          { question: "Pourquoi certains outils ont-ils des boutons Appliquer ?", answer: "Les boutons Appliquer valident une modification pour l’ajouter à l’historique et continuer à travailler à partir de ce résultat avec d’autres outils." },
        ],
      },
      {
        title: "Réseaux sociaux",
        entries: [
          { question: "Instagram", answer: "Modes recommandés : 4:5 pour le fil, 1:1 pour les publications carrées et 9:16 pour Stories et Reels. Gardez le texte grand, centré et éloigné des bords. Rognez d’abord, puis n’ajoutez des coins arrondis ou une bordure que si le design en a besoin." },
          { question: "Facebook", answer: "Modes recommandés : 1:1 ou 4:5 pour le fil, 1.91:1 pour les aperçus de liens et 9:16 pour les Stories. Veillez à ce que le sujet principal reste lisible en petit format et évitez les textes minuscules." },
          { question: "X", answer: "Modes recommandés : 16:9 pour la plupart des publications, 1.91:1 pour les visuels larges de type lien et 3:1 pour les en-têtes de type bannière. Gardez une composition simple et le point focal centré." },
          { question: "LinkedIn", answer: "Modes recommandés : 1.91:1 pour les visuels de prévisualisation de lien et 1:1 pour des publications carrées propres. Gardez un titre court et un style plus professionnel que décoratif." },
          { question: "TikTok", answer: "Mode recommandé : 9:16. TikTok est conçu pour le contenu vertical, donc placez les éléments importants au centre car l’interface de l’application couvre le haut et le bas." },
          { question: "Pinterest", answer: "Mode recommandé : 2:3. Pinterest fonctionne très bien avec les images verticales et 2:3 est le réglage par défaut le plus sûr. Un sujet fort et un bloc de titre lisible fonctionnent mieux qu’un collage chargé." },
          { question: "YouTube", answer: "Mode recommandé : 16:9. Les miniatures YouTube doivent être larges, percutantes et assez grandes pour rester nettes dans la recherche et les recommandations." },
          { question: "Quels formats de recadrage pour les réseaux sociaux sont disponibles ?", answer: "Les préréglages actuels sont : Instagram 4:5, 1:1 et 9:16 ; Facebook 1:1, 4:5, 1.91:1 et 9:16 ; X 16:9, 1.91:1 et 3:1 ; LinkedIn 1.91:1 et 1:1 ; TikTok 9:16 ; Pinterest 2:3 ; YouTube 16:9 ; Xiaohongshu 3:4 ; WeChat 2.35:1 ; et Weibo 1:1." },
        ],
      },
    ],
    de: [
      {
        title: "Allgemein",
        entries: [
          { question: "Was ist Cut My Pic?", answer: "Ursprünglich 2009 von Sean Crownover (dem Entwickler von Jishin.Watch) konzipiert, ist diese neue Version von Cut My Pic aus dem Jahr 2026 ein privater Bildeditor im Browser zum Zuschneiden, Maskieren, für runde Ecken, Rahmen, Schatten, Größenänderung, Filter, Anpassungen, Schärfen und Weichzeichnen, Text, Stempel, Verlauf, Vollbildbearbeitung und Export.", answerHtml: 'Ursprünglich 2009 von Sean Crownover (<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">dem Entwickler von Jishin.Watch</a>) konzipiert, ist diese neue Version von Cut My Pic aus dem Jahr 2026 ein privater Bildeditor im Browser zum Zuschneiden, Maskieren, für runde Ecken, Rahmen, Schatten, Größenänderung, Filter, Anpassungen, Schärfen und Weichzeichnen, Text, Stempel, Verlauf, Vollbildbearbeitung und Export.' },
          { question: "Lädt Cut My Pic meine Bilder hoch?", answer: "Nein. Die Bearbeitung läuft lokal im Browser, daher bleiben deine Bilder auf deinem Gerät." },
          { question: "Welche Formate kann ich hochladen?", answer: "Du kannst gängige Bildformate wie PNG, JPG, WebP und GIF hochladen." },
          { question: "Welches Format nutzt der Export?", answer: "Der Export unterstützt PNG, JPG und WebP. PNG und WebP können Transparenz erhalten; JPG verwendet die gewählte Hintergrundfarbe." },
          { question: "Warum verändert Schatten den Bildbereich optisch?", answer: "Schatten erweitert die Leinwand, damit er nicht abgeschnitten wird. Das Foto behält nach dem Anwenden dieselbe visuelle Größe." },
          { question: "Was machen Schärfen und Weichzeichnen?", answer: "Schärfen macht Kanten klarer und definierter, während Weichzeichnen das Gegenteil tut und das Bild sanft unscharf macht. Beide zeigen eine Live-Vorschau, damit du die Stärke vor dem Anwenden fein abstimmen kannst." },
          { question: "Kann ich mehrere Rahmen hinzufügen?", answer: "Ja. Jedes Mal, wenn du einen Rahmen anwendest, wird er Teil des Bildes, sodass spätere Zuschnitte oder Eckenbearbeitungen den kompletten Rahmenstapel erneut anwenden können." },
          { question: "Kann ich Text und Stempel zusammen verwenden?", answer: "Ja. Der Textmodus erlaubt benutzerdefinierten Text, und der Stempelmodus liefert vordefinierte Grafiken und Beschriftungen. Du kannst zwischen beiden im selben Textbereich wechseln." },
          { question: "Was ist der beste Arbeitsablauf?", answer: "Am besten funktioniert diese Reihenfolge: zuerst zuschneiden, dann runde Ecken oder Rahmen, danach Filter und Anpassungen, dann Text oder Stempel und am Ende exportieren." },
          { question: "Kann ich mehr als einen Schritt rückgängig machen?", answer: "Ja. Öffne das Verlaufsfenster, um zu einem früheren Bearbeitungspunkt zurückzukehren, bei Bedarf sogar zum Originalbild." },
          { question: "Wie hilft die Vollbildbearbeitung?", answer: "Der Vollbildmodus bietet mehr Arbeitsfläche und lässt die Werkzeugleiste verfügbar, damit du weiter bearbeiten kannst, während du mehr vom Bild siehst." },
          { question: "Was ist der Unterschied zwischen Zuschneiden, Schneiden und Maskieren?", answer: "Zuschneiden entfernt einen Teil des Bildes, Schnittmodi definieren die Form, die erhalten bleiben soll, und Maskenmodi lassen dich den ausgewählten Bereich weichzeichnen oder einfärben, statt ihn zu entfernen." },
          { question: "Funktioniert Cut My Pic offline?", answer: "Ja. Da der Editor im Browser läuft, kann er auch ohne Verbindung weiterarbeiten, solange die Seite bereits geladen ist." },
          { question: "Warum haben manche Werkzeuge eine Anwenden-Schaltfläche?", answer: "Die Anwenden-Schaltfläche bestätigt eine Bearbeitung, damit sie Teil des Verlaufs wird und du mit anderen Werkzeugen auf diesem Ergebnis weiterarbeiten kannst." },
        ],
      },
      {
        title: "Soziale Netzwerke",
        entries: [
          { question: "Instagram", answer: "Empfohlene Modi: 4:5 für Feed-Posts, 1:1 für quadratische Beiträge und 9:16 für Stories und Reels. Halte den Text groß, zentriert und weg von den Rändern. Zuerst zuschneiden und runde Ecken oder einen Rahmen nur dann hinzufügen, wenn das Design sie braucht." },
          { question: "Facebook", answer: "Empfohlene Modi: 1:1 oder 4:5 für Feed-Posts, 1.91:1 für Link-Vorschauen und 9:16 für Stories. Achte darauf, dass das Hauptmotiv auch in kleiner Größe klar bleibt und vermeide winzigen Text." },
          { question: "X", answer: "Empfohlene Modi: 16:9 für die meisten Beiträge, 1.91:1 für breite Link-Visuals und 3:1 für bannerartige Header. Halte die Komposition einfach und den Fokus zentriert." },
          { question: "LinkedIn", answer: "Empfohlene Modi: 1.91:1 für Link-Vorschau-Grafiken und 1:1 für saubere quadratische Beiträge. Halte die Überschrift kurz und das Design eher geschäftlich als dekorativ." },
          { question: "TikTok", answer: "Empfohlener Modus: 9:16. TikTok ist für vertikale Inhalte ausgelegt, also platziere wichtige Elemente in der Mitte, weil die App-Oberfläche oben und unten etwas verdeckt." },
          { question: "Pinterest", answer: "Empfohlener Modus: 2:3. Pinterest funktioniert sehr gut mit hohen vertikalen Bildern, und 2:3 ist der sicherste Standardwert. Ein starkes Motiv und ein gut lesbarer Titelblock funktionieren meist besser als eine überladene Collage." },
          { question: "YouTube", answer: "Empfohlener Modus: 16:9. YouTube-Thumbnails sollten breit, auffällig und groß genug sein, damit sie in Suche und Empfehlungen scharf bleiben." },
          { question: "Welche Social-Media-Zuschnittgrößen sind verfügbar?", answer: "Die aktuellen Vorgaben sind: Instagram 4:5, 1:1 und 9:16; Facebook 1:1, 4:5, 1.91:1 und 9:16; X 16:9, 1.91:1 und 3:1; LinkedIn 1.91:1 und 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; und Weibo 1:1." },
        ],
      },
    ],
    ja: [
      {
        title: "一般",
        entries: [
          { question: "Cut My Pic とは何ですか？", answer: "Cut My Pic は、2009年に Sean Crownover（Jishin.Watch の制作者）によって考案されました。2026年に新しく生まれ変わったこのバージョンは、トリミング、マスク、角丸、枠線、影、サイズ変更、フィルター、調整、シャープ化とソフト化、テキスト、スタンプ、履歴、フルスクリーン編集、書き出しに対応した、ブラウザ内で動作するプライベートな画像エディタです。", answerHtml: 'Cut My Pic は、2009年に Sean Crownover（<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">Jishin.Watch の制作者</a>）によって考案されました。2026年に新しく生まれ変わったこのバージョンは、トリミング、マスク、角丸、枠線、影、サイズ変更、フィルター、調整、シャープ化とソフト化、テキスト、スタンプ、履歴、フルスクリーン編集、書き出しに対応した、ブラウザ内で動作するプライベートな画像エディタです。' },
          { question: "画像はアップロードされますか？", answer: "いいえ。編集はブラウザ内でローカルに行われるため、画像は端末に残ります。" },
          { question: "どの形式をアップロードできますか？", answer: "PNG、JPG、WebP、GIF などの一般的な画像形式をアップロードできます。" },
          { question: "書き出しの形式は何ですか？", answer: "PNG、JPG、WebP に対応しています。PNG と WebP は透明部分を保持でき、JPG は選択した背景色を使用します。" },
          { question: "影を付けると画像領域が違って見えるのはなぜですか？", answer: "影が切れないようにキャンバスを広げるためです。影を適用した後も写真の見た目の大きさは変わりません。" },
          { question: "シャープ化とソフト化は何をしますか？", answer: "シャープ化は輪郭をよりくっきり見せ、ソフト化はその逆でやさしくぼかします。どちらもライブプレビューがあるので、適用前に量を細かく調整できます。" },
          { question: "枠線は複数追加できますか？", answer: "はい。枠線を適用するたびに画像の一部になるので、その後のトリミングや角の編集でも枠線全体を再適用できます。" },
          { question: "テキストとスタンプを一緒に使えますか？", answer: "はい。テキストモードではカスタム文字を追加でき、スタンプモードでは用意されたグラフィックとラベルを使えます。同じテキストパネル内で切り替えられます。" },
          { question: "一番良い作業手順は何ですか？", answer: "一般的には、まずトリミング、次に角丸や枠線、その後にフィルターと調整、最後にテキストやスタンプ、そして書き出しの順番が最適です。" },
          { question: "複数のステップを元に戻せますか？", answer: "はい。履歴ウィンドウを開けば、必要に応じて元画像まで含めて以前の編集地点に戻れます。" },
          { question: "フルスクリーン編集の利点は何ですか？", answer: "フルスクリーンモードでは作業スペースが広くなり、ツールバーはそのまま使えるので、より多くの画像を見ながら編集できます。" },
          { question: "トリミング、カット、マスクの違いは何ですか？", answer: "トリミングは画像の一部を削除し、カットモードは残す形を定義し、マスクモードでは削除の代わりに選択範囲をぼかしたり色を付けたりします。" },
          { question: "Cut My Pic はオフラインでも動きますか？", answer: "はい。ブラウザ内で動作するため、ページがすでに読み込まれていれば、接続がなくても使い続けられます。" },
          { question: "一部のツールに適用ボタンがあるのはなぜですか？", answer: "適用ボタンは編集を確定して履歴に残し、その結果を基に他のツールで作業を続けられるようにします。" },
        ],
      },
      {
        title: "SNS",
        entries: [
          { question: "Instagram", answer: "おすすめのモード: フィード投稿は 4:5、正方形投稿は 1:1、Stories と Reels は 9:16。テキストは大きく中央寄せにして端から離してください。まずトリミングを行い、デザイン上必要な場合だけ角丸や枠線を追加します。" },
          { question: "Facebook", answer: "おすすめのモード: フィード投稿は 1:1 または 4:5、リンクプレビューは 1.91:1、Stories は 9:16。小さい表示でも主役がはっきり見えるようにし、細かすぎる文字は避けてください。" },
          { question: "X", answer: "おすすめのモード: ほとんどの投稿は 16:9、横長のリンク風ビジュアルは 1.91:1、バナー風ヘッダーは 3:1。構図はシンプルに保ち、焦点は中央に置いてください。" },
          { question: "LinkedIn", answer: "おすすめのモード: リンクプレビュー風グラフィックは 1.91:1、きれいな正方形投稿は 1:1。見出しは短く、装飾よりビジネス感を重視してください。" },
          { question: "TikTok", answer: "おすすめのモード: 9:16。TikTok は縦型コンテンツ向けなので、アプリ UI が上下を覆うため重要な要素は中央に置いてください。" },
          { question: "Pinterest", answer: "おすすめのモード: 2:3。Pinterest は縦長画像と相性がよく、2:3 が最も安全な基本値です。強い主役と読みやすいタイトルブロックが効果的です。" },
          { question: "YouTube", answer: "おすすめのモード: 16:9。YouTube のサムネイルは横長で、検索やおすすめでも鮮明に見えるだけの大きさが必要です。" },
          { question: "利用できるSNS用の切り抜きサイズは何ですか？", answer: "現在のプリセットは、Instagram 4:5 / 1:1 / 9:16、Facebook 1:1 / 4:5 / 1.91:1 / 9:16、X 16:9 / 1.91:1 / 3:1、LinkedIn 1.91:1 / 1:1、TikTok 9:16、Pinterest 2:3、YouTube 16:9、Xiaohongshu 3:4、WeChat 2.35:1、Weibo 1:1 です。" },
        ],
      },
    ],
    zh: [
      {
        title: "常规",
        entries: [
          { question: "Cut My Pic 是什么？", answer: "Cut My Pic 最初由 Sean Crownover（Jishin.Watch 的创作者）于 2009 年构思。全新的 2026 年版本是一款私密的浏览器图像编辑器，支持裁剪、遮罩、圆角、边框、阴影、调整大小、滤镜、调整、锐化与柔化、文本、印章、历史记录、全屏编辑和导出。", answerHtml: 'Cut My Pic 最初由 Sean Crownover（<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">Jishin.Watch 的创作者</a>）于 2009 年构思。全新的 2026 年版本是一款私密的浏览器图像编辑器，支持裁剪、遮罩、圆角、边框、阴影、调整大小、滤镜、调整、锐化与柔化、文本、印章、历史记录、全屏编辑和导出。' },
          { question: "Cut My Pic 会上传我的图片吗？", answer: "不会。编辑全部在浏览器本地进行，所以你的图片会保留在设备上。" },
          { question: "我可以上传哪些格式？", answer: "你可以上传 PNG、JPG、WebP 和 GIF 等常见图像格式。" },
          { question: "导出使用什么格式？", answer: "导出支持 PNG、JPG 和 WebP。PNG 和 WebP 可保留透明度；JPG 使用所选背景色。" },
          { question: "为什么阴影会让图像区域看起来不同？", answer: "阴影会扩展画布，避免阴影被裁掉。应用阴影后，图片的视觉尺寸保持不变。" },
          { question: "锐化和柔化有什么作用？", answer: "锐化会让边缘更清晰、更明确，而柔化则相反，会让图像轻微模糊。两者都提供实时预览，方便你在应用前微调强度。" },
          { question: "我可以添加多个边框吗？", answer: "可以。每次应用边框后，它都会成为图像的一部分，因此后续的裁剪或角部编辑仍可重新应用整套边框效果。" },
          { question: "文本和印章可以一起使用吗？", answer: "可以。文本模式可添加自定义文字，而印章模式提供预设图形和标签。你可以在同一文本面板中切换它们。" },
          { question: "最佳工作流程是什么？", answer: "通常最好的顺序是：先裁剪，再圆角或边框，然后滤镜和调整，最后添加文本或印章，结束时再导出。" },
          { question: "我可以撤销多步吗？", answer: "可以。打开历史窗口就能回到之前的编辑点，必要时甚至可以回到原图。" },
          { question: "全屏编辑有什么帮助？", answer: "全屏模式能给你更多工作空间，同时保留工具栏，让你在看到更多图像的同时继续编辑。" },
          { question: "裁剪、切割和遮罩有什么区别？", answer: "裁剪会删除图像的一部分，切割模式定义你要保留的形状，而遮罩模式则允许你对选区进行模糊或着色，而不是直接删除它。" },
          { question: "Cut My Pic 可以离线使用吗？", answer: "可以。编辑器在浏览器中运行，只要页面已经加载，就算没有网络也能继续工作。" },
          { question: "为什么有些工具有“应用”按钮？", answer: "“应用”按钮会确认编辑，让它进入历史记录，并让你基于该结果继续使用其他工具。" },
        ],
      },
      {
        title: "社交网络",
        entries: [
          { question: "Instagram", answer: "推荐模式：信息流帖子用 4:5 裁切，方形帖子用 1:1 裁切，Stories 和 Reels 用 9:16 裁切。保持文字大、居中并远离边缘。先裁切，只有在设计需要时才加圆角或边框。" },
          { question: "Facebook", answer: "推荐模式：信息流帖子用 1:1 或 4:5 裁切，链接预览用 1.91:1 裁切，Stories 用 9:16 裁切。确保主体在较小尺寸下依然清晰可见，避免使用过小文字。" },
          { question: "X", answer: "推荐模式：大多数帖子用 16:9 裁切，宽幅链接式视觉用 1.91:1 裁切，横幅式标题用 3:1 裁切。保持构图简洁，并让焦点居中。" },
          { question: "LinkedIn", answer: "推荐模式：链接预览风格图用 1.91:1 裁切，干净的方形帖子用 1:1 裁切。标题保持简短，风格更偏商务而不是装饰性。" },
          { question: "TikTok", answer: "推荐模式：9:16 裁切。TikTok 以竖屏内容为主，因此重要元素应放在中央，因为应用界面会遮住上下边缘。" },
          { question: "Pinterest", answer: "推荐模式：2:3 裁切。Pinterest 很适合竖向图片，2:3 是最稳妥的默认值。突出主体并保持标题清晰通常比复杂拼贴更有效。" },
          { question: "YouTube", answer: "推荐模式：16:9 裁切。YouTube 缩略图应该宽大、醒目，并且足够大，确保在搜索和推荐中依然清晰。" },
          { question: "有哪些社交平台裁切尺寸？", answer: "当前预设包括：Instagram 4:5、1:1 和 9:16；Facebook 1:1、4:5、1.91:1 和 9:16；X 16:9、1.91:1 和 3:1；LinkedIn 1.91:1 和 1:1；TikTok 9:16；Pinterest 2:3；YouTube 16:9；小红书 3:4；微信 2.35:1；微博 1:1。" },
        ],
      },
    ],
    ko: [
      {
        title: "일반",
        entries: [
          { question: "Cut My Pic은 무엇인가요?", answer: "Cut My Pic은 2009년 Sean Crownover(Jishin.Watch 제작자)가 처음 구상했습니다. 새롭게 선보이는 2026년 버전은 자르기, 마스킹, 둥근 모서리, 테두리, 그림자, 크기 변경, 필터, 조정, 선명하게 및 부드럽게, 텍스트, 스탬프, 기록, 전체 화면 편집과 내보내기를 지원하는 브라우저 기반 개인 이미지 편집기입니다.", answerHtml: 'Cut My Pic은 2009년 Sean Crownover(<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">Jishin.Watch 제작자</a>)가 처음 구상했습니다. 새롭게 선보이는 2026년 버전은 자르기, 마스킹, 둥근 모서리, 테두리, 그림자, 크기 변경, 필터, 조정, 선명하게 및 부드럽게, 텍스트, 스탬프, 기록, 전체 화면 편집과 내보내기를 지원하는 브라우저 기반 개인 이미지 편집기입니다.' },
          { question: "Cut My Pic이 이미지를 업로드하나요?", answer: "아니요. 편집은 브라우저에서 로컬로 실행되므로 이미지가 기기에 그대로 남습니다." },
          { question: "어떤 형식을 업로드할 수 있나요?", answer: "PNG, JPG, WebP, GIF 같은 일반적인 이미지 형식을 업로드할 수 있습니다." },
          { question: "내보내기는 어떤 형식을 사용하나요?", answer: "PNG, JPG, WebP를 지원합니다. PNG와 WebP는 투명도를 유지할 수 있고, JPG는 선택한 배경색을 사용합니다." },
          { question: "그림자를 적용하면 이미지 영역이 달라 보이는 이유는 무엇인가요?", answer: "그림자가 잘리지 않도록 캔버스가 확장됩니다. 그림자를 적용한 뒤에도 사진의 시각적 크기는 그대로입니다." },
          { question: "선명하게와 부드럽게는 무엇을 하나요?", answer: "선명하게는 가장자리를 또렷하게 만들고, 부드럽게는 이미지를 살짝 흐리게 합니다. 둘 다 적용 전에 강도를 조절할 수 있는 실시간 미리보기를 제공합니다." },
          { question: "테두리를 여러 번 추가할 수 있나요?", answer: "네. 적용한 테두리는 이미지의 일부가 되므로 이후 자르기나 모서리 편집에서도 전체 테두리 묶음을 다시 적용할 수 있습니다." },
          { question: "텍스트와 스탬프를 함께 사용할 수 있나요?", answer: "네. 텍스트 모드에서는 사용자 텍스트를 추가하고 스탬프 모드에서는 미리 준비된 그래픽과 레이블을 사용할 수 있습니다." },
          { question: "가장 좋은 편집 순서는 무엇인가요?", answer: "먼저 자르고, 둥근 모서리나 테두리, 필터와 조정, 텍스트나 스탬프를 적용한 뒤 마지막에 내보내는 순서가 좋습니다." },
          { question: "두 단계 이상 실행 취소할 수 있나요?", answer: "네. 기록 창에서 이전 편집 지점이나 원본 이미지로 바로 돌아갈 수 있습니다." },
          { question: "전체 화면 편집은 어떻게 도움이 되나요?", answer: "전체 화면은 더 넓은 작업 공간을 제공하면서 도구 모음을 유지해 이미지의 더 많은 부분을 보며 편집할 수 있게 합니다." },
          { question: "자르기, 컷, 마스크의 차이는 무엇인가요?", answer: "자르기는 이미지 일부를 제거하고, 컷 모드는 유지할 모양을 정하며, 마스크 모드는 선택 영역을 제거하는 대신 흐리게 하거나 색칠합니다." },
          { question: "Cut My Pic은 오프라인에서도 작동하나요?", answer: "네. 페이지가 이미 로드되어 있다면 브라우저에서 실행되므로 연결이 없어도 계속 사용할 수 있습니다." },
          { question: "일부 도구에 적용 버튼이 있는 이유는 무엇인가요?", answer: "적용 버튼은 편집을 확정해 기록에 추가하고, 그 결과를 바탕으로 다른 도구를 계속 사용할 수 있게 합니다." },
        ],
      },
      {
        title: "소셜 네트워크",
        entries: [
          { question: "Instagram", answer: "피드 게시물은 4:5, 정사각형 게시물은 1:1, 스토리와 릴은 9:16을 권장합니다. 글자를 크고 중앙에 두고 가장자리에서 떨어뜨리세요." },
          { question: "Facebook", answer: "피드 게시물은 1:1 또는 4:5, 링크 미리보기는 1.91:1, 스토리는 9:16을 권장합니다. 작은 화면에서도 주요 피사체가 분명하게 보이게 하세요." },
          { question: "X", answer: "일반 게시물은 16:9, 넓은 링크형 이미지는 1.91:1, 배너형 헤더는 3:1을 권장합니다. 구도를 단순하게 하고 초점을 중앙에 두세요." },
          { question: "LinkedIn", answer: "링크 미리보기 그래픽은 1.91:1, 깔끔한 정사각형 게시물은 1:1을 권장합니다. 제목은 짧게 유지하고 전문적인 구성을 사용하세요." },
          { question: "TikTok", answer: "권장 모드는 9:16입니다. 앱 인터페이스가 위아래를 가릴 수 있으므로 중요한 요소는 중앙에 배치하세요." },
          { question: "Pinterest", answer: "권장 모드는 2:3입니다. 세로 이미지에 적합하며 강한 피사체와 읽기 쉬운 제목이 복잡한 콜라주보다 효과적입니다." },
          { question: "YouTube", answer: "권장 모드는 16:9입니다. 썸네일은 넓고 눈에 띄며 검색과 추천 화면에서도 선명하게 보일 만큼 커야 합니다." },
          { question: "어떤 소셜 자르기 크기를 사용할 수 있나요?", answer: "Instagram, Facebook, X, LinkedIn, TikTok, Pinterest, YouTube, Xiaohongshu, WeChat과 Weibo용 프리셋을 사용할 수 있습니다." },
        ],
      },
    ],
    hi: [
      {
        title: "सामान्य",
        entries: [
          { question: "Cut My Pic क्या है?", answer: "Cut My Pic की मूल कल्पना Sean Crownover (Jishin.Watch के निर्माता) ने 2009 में की थी। इसका नया 2026 संस्करण एक निजी ब्राउज़र-आधारित इमेज एडिटर है जो क्रॉप, मास्क, राउंडेड कॉर्नर, बॉर्डर, शैडो, रीसाइज़, फ़िल्टर, एडजस्टमेंट, शार्पन और सॉफ्टन, टेक्स्ट, स्टैम्प, हिस्ट्री, फ़ुलस्क्रीन एडिटिंग और एक्सपोर्ट सपोर्ट करता है।", answerHtml: 'Cut My Pic की मूल कल्पना Sean Crownover (<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">Jishin.Watch के निर्माता</a>) ने 2009 में की थी। इसका नया 2026 संस्करण एक निजी ब्राउज़र-आधारित इमेज एडिटर है जो क्रॉप, मास्क, राउंडेड कॉर्नर, बॉर्डर, शैडो, रीसाइज़, फ़िल्टर, एडजस्टमेंट, शार्पन और सॉफ्टन, टेक्स्ट, स्टैम्प, हिस्ट्री, फ़ुलस्क्रीन एडिटिंग और एक्सपोर्ट सपोर्ट करता है।' },
          { question: "क्या Cut My Pic मेरी इमेज को अपलोड करता है?", answer: "नहीं। एडिटिंग आपके ब्राउज़र में लोकल रूप से होती है, इसलिए आपकी इमेज आपके डिवाइस पर रहती हैं।" },
          { question: "मैं कौन-से फ़ॉर्मेट अपलोड कर सकता हूँ?", answer: "आप PNG, JPG, WebP और GIF जैसे सामान्य इमेज फ़ॉर्मेट अपलोड कर सकते हैं।" },
          { question: "एक्सपोर्ट किस फ़ॉर्मेट का उपयोग करता है?", answer: "एक्सपोर्ट PNG, JPG और WebP को सपोर्ट करता है। PNG और WebP पारदर्शिता रख सकते हैं; JPG चुने हुए बैकग्राउंड रंग का उपयोग करता है।" },
          { question: "शैडो लगाने पर इमेज क्षेत्र अलग क्यों दिखता है?", answer: "शैडो कैनवास को बढ़ा देता है ताकि शैडो कट न जाए। शैडो लागू करने के बाद फोटो का विज़ुअल आकार वही रहता है।" },
          { question: "शार्पन और सॉफ्टन क्या करते हैं?", answer: "शार्पन किनारों को अधिक स्पष्ट बनाता है, जबकि सॉफ्टन इसका उल्टा करता है और इमेज को हल्का धुंधला करता है। दोनों में लाइव प्रीव्यू है ताकि आप लागू करने से पहले मात्रा को ठीक से समायोजित कर सकें।" },
          { question: "क्या मैं कई बॉर्डर जोड़ सकता हूँ?", answer: "हाँ। हर बार बॉर्डर लगाने पर वह इमेज का हिस्सा बन जाता है, इसलिए बाद में क्रॉप या कॉर्नर एडिटिंग के दौरान पूरे बॉर्डर स्टैक को फिर से लागू किया जा सकता है।" },
          { question: "क्या मैं टेक्स्ट और स्टैम्प को साथ उपयोग कर सकता हूँ?", answer: "हाँ। टेक्स्ट मोड में कस्टम टेक्स्ट जोड़ा जा सकता है, जबकि स्टैम्प मोड में पहले से बने ग्राफ़िक्स और लेबल मिलते हैं। आप एक ही टेक्स्ट पैनल से इनके बीच बदल सकते हैं।" },
          { question: "सबसे अच्छा वर्कफ़्लो क्या है?", answer: "आमतौर पर सबसे अच्छा क्रम है: पहले क्रॉप, फिर राउंडेड कॉर्नर या बॉर्डर, उसके बाद फ़िल्टर और एडजस्टमेंट, फिर टेक्स्ट या स्टैम्प, और अंत में एक्सपोर्ट।" },
          { question: "क्या मैं एक से अधिक स्टेप अनडू कर सकता हूँ?", answer: "हाँ। हिस्ट्री विंडो खोलकर आप पहले के एडिट पॉइंट पर लौट सकते हैं, ज़रूरत होने पर मूल इमेज तक भी।" },
          { question: "फ़ुलस्क्रीन एडिटिंग कैसे मदद करती है?", answer: "फ़ुलस्क्रीन मोड आपको ज़्यादा कार्यक्षेत्र देता है और टूलबार उपलब्ध रखता है, ताकि आप इमेज का अधिक हिस्सा देखते हुए एडिट कर सकें।" },
          { question: "क्रॉप, कट और मास्क में क्या अंतर है?", answer: "क्रॉप इमेज का हिस्सा हटाता है, कट मोड वह आकार तय करते हैं जिसे आप रखना चाहते हैं, और मास्क मोड चयनित क्षेत्र को हटाने के बजाय उसे ब्लर या रंगीन कर सकता है।" },
          { question: "क्या Cut My Pic ऑफ़लाइन काम करता है?", answer: "हाँ। क्योंकि एडिटर आपके ब्राउज़र में चलता है, पेज लोड होने के बाद बिना इंटरनेट के भी काम जारी रख सकता है।" },
          { question: "कुछ टूल्स में Apply बटन क्यों होते हैं?", answer: "Apply बटन एडिट को पक्का करते हैं ताकि वह हिस्ट्री में शामिल हो जाए और आप उसी परिणाम पर अन्य टूल्स से काम जारी रख सकें।" },
        ],
      },
      {
        title: "सोशल नेटवर्क",
        entries: [
          { question: "Instagram", answer: "सुझाए गए मोड: फ़ीड पोस्ट के लिए 4:5 क्रॉप, स्क्वायर पोस्ट के लिए 1:1 क्रॉप, और Stories व Reels के लिए 9:16 क्रॉप। टेक्स्ट को बड़ा, बीच में, और किनारों से दूर रखें। पहले क्रॉप करें और डिज़ाइन की ज़रूरत होने पर ही राउंडेड कॉर्नर या बॉर्डर जोड़ें।" },
          { question: "Facebook", answer: "सुझाए गए मोड: फ़ीड पोस्ट के लिए 1:1 या 4:5 क्रॉप, लिंक प्रीव्यू के लिए 1.91:1 क्रॉप, और Stories के लिए 9:16 क्रॉप। मुख्य विषय को छोटे आकार में भी स्पष्ट रखें और बहुत छोटे टेक्स्ट से बचें।" },
          { question: "X", answer: "सुझाए गए मोड: अधिकतर पोस्ट के लिए 16:9 क्रॉप, चौड़े लिंक-स्टाइल विज़ुअल के लिए 1.91:1 क्रॉप, और बैनर-स्टाइल हेडर के लिए 3:1 क्रॉप। लेआउट सरल रखें और फोकस बीच में रखें।" },
          { question: "LinkedIn", answer: "सुझाए गए मोड: लिंक प्रीव्यू-स्टाइल ग्राफ़िक्स के लिए 1.91:1 क्रॉप और साफ़-सुथरी स्क्वायर पोस्ट के लिए 1:1 क्रॉप। शीर्षक छोटा रखें और डिज़ाइन को सजावटी की बजाय व्यावसायिक रखें।" },
          { question: "TikTok", answer: "सुझाया गया मोड: 9:16 क्रॉप। TikTok वर्टिकल कंटेंट के लिए बना है, इसलिए महत्वपूर्ण तत्वों को बीच में रखें क्योंकि ऐप UI ऊपर और नीचे को ढकता है।" },
          { question: "Pinterest", answer: "सुझाया गया मोड: 2:3 क्रॉप। Pinterest ऊँची वर्टिकल इमेज के साथ अच्छा काम करता है और 2:3 सबसे सुरक्षित डिफ़ॉल्ट है। मज़बूत विषय और पढ़ने योग्य शीर्षक ब्लॉक अक्सर भरे-भरे कोलाज से बेहतर काम करते हैं।" },
          { question: "YouTube", answer: "सुझाया गया मोड: 16:9 क्रॉप। YouTube थंबनेल चौड़े, बोल्ड और इतने बड़े होने चाहिए कि सर्च और रिकमेंडेशन में भी साफ़ दिखें।" },
          { question: "सोशल मीडिया के लिए कौन-कौन से cut sizes उपलब्ध हैं?", answer: "वर्तमान presets हैं: Instagram 4:5, 1:1, 9:16; Facebook 1:1, 4:5, 1.91:1, 9:16; X 16:9, 1.91:1, 3:1; LinkedIn 1.91:1, 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; और Weibo 1:1।" },
        ],
      },
    ],
    pt: [
      {
        title: "Geral",
        entries: [
          { question: "O que é o Cut My Pic?", answer: "Concebido originalmente por Sean Crownover (criador do Jishin.Watch) em 2009, esta nova versão 2026 do Cut My Pic é um editor de imagens privado no navegador que oferece recorte, máscara, cantos arredondados, bordas, sombras, redimensionamento, filtros, ajustes, nitidez e suavização, texto, carimbos, histórico, edição em tela cheia e exportação.", answerHtml: 'Concebido originalmente por Sean Crownover (<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">criador do Jishin.Watch</a>) em 2009, esta nova versão 2026 do Cut My Pic é um editor de imagens privado no navegador que oferece recorte, máscara, cantos arredondados, bordas, sombras, redimensionamento, filtros, ajustes, nitidez e suavização, texto, carimbos, histórico, edição em tela cheia e exportação.' },
          { question: "O Cut My Pic envia minhas imagens para um servidor?", answer: "Não. A edição acontece localmente no seu navegador, então suas imagens permanecem no seu dispositivo." },
          { question: "Quais formatos posso enviar?", answer: "Você pode enviar formatos comuns como PNG, JPG, WebP e GIF." },
          { question: "Qual formato a exportação usa?", answer: "A exportação aceita PNG, JPG e WebP. PNG e WebP podem manter a transparência; JPG usa a cor de fundo escolhida." },
          { question: "Por que a sombra faz a área da imagem parecer diferente?", answer: "A sombra amplia a tela para que ela não seja cortada. A foto mantém o mesmo tamanho visual depois que a sombra é aplicada." },
          { question: "O que nitidez e suavização fazem?", answer: "A nitidez deixa as bordas mais nítidas e definidas, enquanto a suavização faz o oposto, desfocando suavemente a imagem. Ambas têm pré-visualização ao vivo para ajustar a intensidade antes de aplicar." },
          { question: "Posso adicionar várias bordas?", answer: "Sim. Cada vez que você aplica uma borda, ela passa a fazer parte da imagem, então cortes ou ajustes posteriores de canto podem reaplicar toda a pilha de bordas." },
          { question: "Posso usar texto e carimbos juntos?", answer: "Sim. O modo Texto permite adicionar texto personalizado, enquanto o modo Carimbo oferece gráficos e rótulos predefinidos. Você pode alternar entre eles no mesmo painel de texto." },
          { question: "Qual é o melhor fluxo de trabalho?", answer: "Uma ordem simples costuma funcionar melhor: recorte primeiro, depois cantos arredondados ou borda, em seguida filtros e ajustes, depois texto ou carimbos, e exporte no final." },
          { question: "Posso desfazer mais de um passo?", answer: "Sim. Abra a janela de histórico para voltar a um ponto anterior de edição, inclusive à imagem original se necessário." },
          { question: "Como a edição em tela cheia ajuda?", answer: "O modo tela cheia oferece mais espaço de trabalho e mantém a barra de ferramentas disponível para continuar editando enquanto você vê mais da imagem." },
          { question: "Qual é a diferença entre recorte, corte e máscara?", answer: "Recorte remove parte da imagem, modos de corte definem a forma que você quer manter, e modos de máscara permitem desfocar ou colorir a área selecionada em vez de removê-la." },
          { question: "O Cut My Pic funciona offline?", answer: "Sim. Como o editor roda no navegador, ele pode continuar funcionando mesmo sem conexão, desde que a página já tenha carregado." },
          { question: "Por que algumas ferramentas têm botões Aplicar?", answer: "Os botões Aplicar confirmam uma edição para que ela entre no histórico e você possa continuar trabalhando a partir desse resultado com outras ferramentas." },
        ],
      },
      {
        title: "Redes sociais",
        entries: [
          { question: "Instagram", answer: "Modos recomendados: recorte 4:5 para posts do feed, 1:1 para posts quadrados e 9:16 para Stories e Reels. Mantenha o texto grande, centralizado e longe das bordas. Recorte primeiro e só adicione cantos arredondados ou borda se o design precisar." },
          { question: "Facebook", answer: "Modos recomendados: 1:1 ou 4:5 para posts do feed, 1.91:1 para prévias de link e 9:16 para Stories. Faça o assunto principal continuar claro em tamanhos menores e evite texto minúsculo." },
          { question: "X", answer: "Modos recomendados: 16:9 para a maioria dos posts, 1.91:1 para visuais amplos no estilo de link e 3:1 para cabeçalhos em formato de banner. Mantenha a composição simples e o foco centralizado." },
          { question: "LinkedIn", answer: "Modos recomendados: 1.91:1 para gráficos de prévia de link e 1:1 para posts quadrados limpos. Mantenha o título curto e um visual mais profissional do que decorativo." },
          { question: "TikTok", answer: "Modo recomendado: 9:16. O TikTok foi feito para conteúdo vertical, então coloque os elementos importantes no centro porque a interface do app cobre a parte superior e inferior." },
          { question: "Pinterest", answer: "Modo recomendado: 2:3. O Pinterest funciona muito bem com imagens verticais e 2:3 é o padrão mais seguro. Um assunto forte e um bloco de título legível geralmente funcionam melhor do que uma colagem muito cheia." },
          { question: "YouTube", answer: "Modo recomendado: 16:9. Miniaturas do YouTube devem ser largas, chamativas e grandes o bastante para continuarem nítidas em busca e recomendações." },
          { question: "Quais tamanhos de corte para redes sociais estão disponíveis?", answer: "Os presets atuais são: Instagram 4:5, 1:1 e 9:16; Facebook 1:1, 4:5, 1.91:1 e 9:16; X 16:9, 1.91:1 e 3:1; LinkedIn 1.91:1 e 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; e Weibo 1:1." },
        ],
      },
    ],
    it: [
      {
        title: "Generale",
        entries: [
          { question: "Che cos’è Cut My Pic?", answer: "Ideato originariamente da Sean Crownover (creatore di Jishin.Watch) nel 2009, questa nuova versione 2026 di Cut My Pic è un editor di immagini privato nel browser che supporta ritaglio, maschera, angoli arrotondati, bordi, ombre, ridimensionamento, filtri, regolazioni, accentua e sfuma, testo, timbri, cronologia, modifica a schermo intero ed esportazione.", answerHtml: 'Ideato originariamente da Sean Crownover (<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">creatore di Jishin.Watch</a>) nel 2009, questa nuova versione 2026 di Cut My Pic è un editor di immagini privato nel browser che supporta ritaglio, maschera, angoli arrotondati, bordi, ombre, ridimensionamento, filtri, regolazioni, accentua e sfuma, testo, timbri, cronologia, modifica a schermo intero ed esportazione.' },
          { question: "Cut My Pic carica le mie immagini?", answer: "No. La modifica avviene localmente nel browser, quindi le tue immagini restano sul dispositivo." },
          { question: "Quali formati posso caricare?", answer: "Puoi caricare formati comuni come PNG, JPG, WebP e GIF." },
          { question: "Quale formato usa l’esportazione?", answer: "L’esportazione supporta PNG, JPG e WebP. PNG e WebP possono mantenere la trasparenza; JPG usa il colore di sfondo scelto." },
          { question: "Perché l’ombra fa sembrare diversa l’area dell’immagine?", answer: "L’ombra espande la tela per evitare che venga tagliata. La foto mantiene la stessa dimensione visiva dopo l’applicazione dell’ombra." },
          { question: "Cosa fanno accentua e sfuma?", answer: "Accentua rende i bordi più nitidi e definiti, mentre sfuma fa l’opposto rendendo l’immagine leggermente più morbida. Entrambi hanno l’anteprima live, così puoi regolare bene l’intensità prima di applicarli." },
          { question: "Posso aggiungere più bordi?", answer: "Sì. Ogni volta che applichi un bordo, questo diventa parte dell’immagine, così i ritagli o gli aggiustamenti agli angoli successivi possono riapplicare l’intera pila di bordi." },
          { question: "Posso usare testo e timbri insieme?", answer: "Sì. La modalità Testo permette di aggiungere testo personalizzato, mentre la modalità Timbro offre grafica ed etichette predefinite. Puoi passare da una all’altra nello stesso pannello." },
          { question: "Qual è il flusso di lavoro migliore?", answer: "Di solito funziona meglio questo ordine: ritaglia prima, poi angoli arrotondati o bordo, quindi filtri e regolazioni, poi testo o timbri, e infine esporta." },
          { question: "Posso annullare più di un passaggio?", answer: "Sì. Apri la finestra della cronologia per tornare a un punto di modifica precedente, anche all’immagine originale se serve." },
          { question: "In che modo la modifica a schermo intero aiuta?", answer: "La modalità schermo intero ti offre più spazio di lavoro e mantiene disponibile la barra degli strumenti, così puoi continuare a modificare mentre vedi più immagine." },
          { question: "Qual è la differenza tra ritaglio, taglio e maschera?", answer: "Il ritaglio rimuove una parte dell’immagine, le modalità di taglio definiscono la forma che vuoi mantenere e le modalità maschera permettono di sfocare o colorare l’area selezionata invece di eliminarla." },
          { question: "Cut My Pic funziona offline?", answer: "Sì. Poiché l’editor funziona nel browser, può continuare a funzionare anche senza connessione, purché la pagina sia già caricata." },
          { question: "Perché alcuni strumenti hanno il pulsante Applica?", answer: "Il pulsante Applica conferma una modifica in modo che entri nella cronologia e tu possa continuare a lavorare da quel risultato con altri strumenti." },
        ],
      },
      {
        title: "Reti sociali",
        entries: [
          { question: "Instagram", answer: "Modalità consigliate: ritaglio 4:5 per i post del feed, 1:1 per i post quadrati e 9:16 per Stories e Reels. Mantieni il testo grande, centrato e lontano dai bordi. Ritaglia prima e aggiungi angoli arrotondati o bordo solo se il design lo richiede." },
          { question: "Facebook", answer: "Modalità consigliate: 1:1 o 4:5 per i post del feed, 1.91:1 per le anteprime link e 9:16 per le Stories. Fai in modo che il soggetto principale resti chiaro anche nelle dimensioni ridotte e evita il testo troppo piccolo." },
          { question: "X", answer: "Modalità consigliate: 16:9 per la maggior parte dei post, 1.91:1 per visual ampi in stile link e 3:1 per intestazioni in stile banner. Mantieni la composizione semplice e il punto focale al centro." },
          { question: "LinkedIn", answer: "Modalità consigliate: 1.91:1 per grafica di anteprima link e 1:1 per post quadrati puliti. Mantieni il titolo breve e uno stile più professionale che decorativo." },
          { question: "TikTok", answer: "Modalità consigliata: 9:16. TikTok è pensato per contenuti verticali, quindi posiziona gli elementi importanti al centro perché l’interfaccia dell’app copre la parte superiore e inferiore." },
          { question: "Pinterest", answer: "Modalità consigliata: 2:3. Pinterest funziona molto bene con immagini verticali e 2:3 è il valore predefinito più sicuro. Un soggetto forte e un blocco titolo leggibile funzionano spesso meglio di un collage troppo affollato." },
          { question: "YouTube", answer: "Modalità consigliata: 16:9. Le miniature di YouTube dovrebbero essere larghe, d’impatto e abbastanza grandi da restare nitide nella ricerca e nei suggerimenti." },
          { question: "Quali dimensioni di taglio social sono disponibili?", answer: "I preset attuali sono: Instagram 4:5, 1:1 e 9:16; Facebook 1:1, 4:5, 1.91:1 e 9:16; X 16:9, 1.91:1 e 3:1; LinkedIn 1.91:1 e 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; e Weibo 1:1." },
        ],
      },
    ],
    ru: [
      {
        title: "Общее",
        entries: [
          { question: "Что такое Cut My Pic?", answer: "Изначально Cut My Pic был задуман Шоном Кроуновером (создателем Jishin.Watch) в 2009 году. Новая версия 2026 года — это приватный браузерный редактор изображений с поддержкой кадрирования, маски, скругления углов, рамок, теней, изменения размера, фильтров, настроек, повышения и смягчения резкости, текста, штампов, истории, полноэкранного редактирования и экспорта.", answerHtml: 'Изначально Cut My Pic был задуман Шоном Кроуновером (<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">создателем Jishin.Watch</a>) в 2009 году. Новая версия 2026 года — это приватный браузерный редактор изображений с поддержкой кадрирования, маски, скругления углов, рамок, теней, изменения размера, фильтров, настроек, повышения и смягчения резкости, текста, штампов, истории, полноэкранного редактирования и экспорта.' },
          { question: "Cut My Pic загружает мои изображения?", answer: "Нет. Редактирование происходит локально в браузере, поэтому изображения остаются на вашем устройстве." },
          { question: "Какие форматы можно загружать?", answer: "Можно загружать распространённые форматы, такие как PNG, JPG, WebP и GIF." },
          { question: "В каком формате выполняется экспорт?", answer: "Экспорт поддерживает PNG, JPG и WebP. PNG и WebP могут сохранять прозрачность; JPG использует выбранный цвет фона." },
          { question: "Почему тень делает область изображения визуально другой?", answer: "Тень расширяет холст, чтобы её не обрезало. После применения тени фотография сохраняет тот же визуальный размер." },
          { question: "Что делают повышение и смягчение резкости?", answer: "Повышение резкости делает края чётче и выразительнее, а смягчение делает обратное, слегка размывая изображение. Оба режима показывают живой предпросмотр, чтобы вы могли точно настроить силу до применения." },
          { question: "Можно ли добавить несколько рамок?", answer: "Да. Каждый раз при применении рамки она становится частью изображения, поэтому последующие обрезки или корректировки углов могут повторно применить всю цепочку рамок." },
          { question: "Можно ли использовать текст и штампы вместе?", answer: "Да. Режим текста позволяет добавлять собственный текст, а режим штампа предлагает готовую графику и подписи. Между ними можно переключаться в одной и той же панели." },
          { question: "Какой рабочий порядок лучше всего?", answer: "Обычно лучше всего такой порядок: сначала кадрирование, затем скругление углов или рамка, потом фильтры и настройки, затем текст или штампы, и в конце экспорт." },
          { question: "Можно ли отменить больше одного шага?", answer: "Да. Откройте окно истории, чтобы вернуться к более ранней точке редактирования, при необходимости даже к исходному изображению." },
          { question: "Чем помогает полноэкранное редактирование?", answer: "Полноэкранный режим даёт больше рабочего пространства и сохраняет доступ к панели инструментов, чтобы вы могли продолжать редактирование, видя больше изображения." },
          { question: "В чём разница между кадрированием, вырезанием и маской?", answer: "Кадрирование удаляет часть изображения, режимы вырезания определяют форму, которую вы хотите сохранить, а режимы маски позволяют размывать или окрашивать выбранную область вместо удаления." },
          { question: "Работает ли Cut My Pic офлайн?", answer: "Да. Поскольку редактор работает в браузере, он может продолжать работу даже без подключения, если страница уже загружена." },
          { question: "Почему у некоторых инструментов есть кнопка «Применить»?", answer: "Кнопка «Применить» подтверждает изменение, чтобы оно попало в историю, и вы могли продолжить работу с другими инструментами, опираясь на этот результат." },
        ],
      },
      {
        title: "Соцсети",
        entries: [
          { question: "Instagram", answer: "Рекомендуемые режимы: 4:5 для постов ленты, 1:1 для квадратных публикаций и 9:16 для Stories и Reels. Делайте текст крупным, по центру и подальше от краёв. Сначала кадрируйте, а затем добавляйте скругление или рамку только если это нужно дизайну." },
          { question: "Facebook", answer: "Рекомендуемые режимы: 1:1 или 4:5 для постов ленты, 1.91:1 для превью ссылок и 9:16 для Stories. Следите, чтобы основной объект был хорошо виден в маленьком размере, и избегайте слишком мелкого текста." },
          { question: "X", answer: "Рекомендуемые режимы: 16:9 для большинства постов, 1.91:1 для широких визуалов в стиле ссылки и 3:1 для заголовков в стиле баннера. Держите композицию простой, а фокус — в центре." },
          { question: "LinkedIn", answer: "Рекомендуемые режимы: 1.91:1 для графики предпросмотра ссылки и 1:1 для чистых квадратных постов. Делайте заголовок коротким и стиль более деловым, чем декоративным." },
          { question: "TikTok", answer: "Рекомендуемый режим: 9:16. TikTok ориентирован на вертикальный контент, поэтому важные элементы лучше располагать по центру, так как интерфейс приложения закрывает верх и низ." },
          { question: "Pinterest", answer: "Рекомендуемый режим: 2:3. Pinterest отлично работает с вертикальными изображениями, и 2:3 — самый безопасный вариант по умолчанию. Сильный объект и читаемый заголовок обычно работают лучше, чем перегруженный коллаж." },
          { question: "YouTube", answer: "Рекомендуемый режим: 16:9. Миниатюры YouTube должны быть широкими, выразительными и достаточно крупными, чтобы оставаться чёткими в поиске и рекомендациях." },
          { question: "Какие размеры обрезки для соцсетей доступны?", answer: "Текущие пресеты: Instagram 4:5, 1:1 и 9:16; Facebook 1:1, 4:5, 1.91:1 и 9:16; X 16:9, 1.91:1 и 3:1; LinkedIn 1.91:1 и 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; и Weibo 1:1." },
        ],
      },
    ],
    ar: [
      {
        title: "عام",
        entries: [
          { question: "ما هو Cut My Pic؟", answer: "ابتكر Sean Crownover (صانع Jishin.Watch) فكرة Cut My Pic لأول مرة عام 2009. أما إصدار 2026 الجديد فهو محرر صور خاص يعمل داخل المتصفح ويدعم القص، والقناع، والزوايا المستديرة، والإطارات، والظلال، وتغيير الحجم، والفلاتر، والتعديلات، والتوضيح والتنعيم، والنص، والأختام، والسجل، والتحرير بملء الشاشة، والتصدير.", answerHtml: 'ابتكر Sean Crownover (<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">صانع Jishin.Watch</a>) فكرة Cut My Pic لأول مرة عام 2009. أما إصدار 2026 الجديد فهو محرر صور خاص يعمل داخل المتصفح ويدعم القص، والقناع، والزوايا المستديرة، والإطارات، والظلال، وتغيير الحجم، والفلاتر، والتعديلات، والتوضيح والتنعيم، والنص، والأختام، والسجل، والتحرير بملء الشاشة، والتصدير.' },
          { question: "هل يرفع Cut My Pic صوري؟", answer: "لا. تتم عملية التحرير محليًا داخل المتصفح، لذلك تبقى صورك على جهازك." },
          { question: "ما الصيغ التي يمكنني رفعها؟", answer: "يمكنك رفع الصيغ الشائعة مثل PNG وJPG وWebP وGIF." },
          { question: "ما صيغة التصدير؟", answer: "يدعم التصدير PNG وJPG وWebP. يمكن لـ PNG وWebP الحفاظ على الشفافية، بينما يستخدم JPG لون الخلفية المختار." },
          { question: "لماذا يجعل الظل منطقة الصورة تبدو مختلفة؟", answer: "الظل يوسع مساحة العمل حتى لا يتم قصه. تبقى الصورة بنفس الحجم البصري بعد تطبيق الظل." },
          { question: "ماذا يفعل التوضيح والتنعيم؟", answer: "التوضيح يجعل الحواف أكثر وضوحًا وحدّة، بينما يفعل التنعيم العكس عبر تمويه الصورة بشكل ناعم. كلاهما يعرض معاينة مباشرة لتتمكن من ضبط القوة قبل التطبيق." },
          { question: "هل يمكنني إضافة عدة إطارات؟", answer: "نعم. كل مرة تطبق فيها إطارًا يصبح جزءًا من الصورة، لذلك يمكن لعمليات القص أو تعديل الزوايا لاحقًا إعادة تطبيق مجموعة الإطارات كاملة." },
          { question: "هل يمكن استخدام النص والأختام معًا؟", answer: "نعم. يتيح وضع النص إضافة نص مخصص، بينما يوفر وضع الختم رسومات وتسميات جاهزة. يمكنك التبديل بينهما من نفس اللوحة." },
          { question: "ما أفضل سير للعمل؟", answer: "عادةً ما ينجح هذا الترتيب: القص أولًا، ثم الزوايا المستديرة أو الإطار، ثم الفلاتر والتعديلات، ثم النص أو الأختام، ثم التصدير في النهاية." },
          { question: "هل يمكنني التراجع عن أكثر من خطوة؟", answer: "نعم. افتح نافذة السجل للعودة إلى نقطة تحرير سابقة، وحتى إلى الصورة الأصلية إذا لزم الأمر." },
          { question: "كيف يساعد التحرير بملء الشاشة؟", answer: "يوفر وضع ملء الشاشة مساحة عمل أكبر مع بقاء شريط الأدوات متاحًا، حتى تتمكن من التحرير أثناء رؤية جزء أكبر من الصورة." },
          { question: "ما الفرق بين القص والقطع والقناع؟", answer: "القص يزيل جزءًا من الصورة، بينما تحدد أوضاع القطع الشكل الذي تريد الاحتفاظ به، وتسمح أوضاع القناع بتمويه أو تلوين المنطقة المحددة بدلًا من حذفها." },
          { question: "هل يعمل Cut My Pic دون اتصال؟", answer: "نعم. بما أن المحرر يعمل داخل المتصفح، يمكنه الاستمرار في العمل حتى بدون اتصال طالما أن الصفحة تم تحميلها مسبقًا." },
          { question: "لماذا تحتوي بعض الأدوات على زر تطبيق؟", answer: "زر تطبيق يؤكد التعديل حتى ينتقل إلى السجل، ويمكنك بعد ذلك مواصلة العمل من النتيجة نفسها باستخدام أدوات أخرى." },
        ],
      },
      {
        title: "الشبكات الاجتماعية",
        entries: [
          { question: "Instagram", answer: "الأوضاع الموصى بها: قص 4:5 لمنشورات الخلاصة، و1:1 للمنشورات المربعة، و9:16 للقصص وReels. اجعل النص كبيرًا ومتمركزًا وبعيدًا عن الحواف. ابدأ بالقص ثم أضف الزوايا المستديرة أو الإطار فقط إذا احتاج التصميم ذلك." },
          { question: "Facebook", answer: "الأوضاع الموصى بها: 1:1 أو 4:5 لمنشورات الخلاصة، و1.91:1 لمعاينات الروابط، و9:16 للقصص. اجعل العنصر الرئيسي واضحًا في الأحجام الصغيرة وتجنب النص الصغير جدًا." },
          { question: "X", answer: "الأوضاع الموصى بها: 16:9 لمعظم المنشورات، و1.91:1 للصور العريضة ذات نمط الروابط، و3:1 للعناوين الشبيهة باللافتات. حافظ على التكوين بسيطًا واجعل التركيز في الوسط." },
          { question: "LinkedIn", answer: "الأوضاع الموصى بها: 1.91:1 لرسومات معاينة الروابط و1:1 للمنشورات المربعة النظيفة. اجعل العنوان قصيرًا وأكثر طابعًا مهنيًا من الزخرفي." },
          { question: "TikTok", answer: "الوضع الموصى به: 9:16. TikTok مخصص للمحتوى العمودي، لذا ضع العناصر المهمة في الوسط لأن واجهة التطبيق تغطي الأعلى والأسفل." },
          { question: "Pinterest", answer: "الوضع الموصى به: 2:3. يعمل Pinterest جيدًا مع الصور العمودية، ويُعد 2:3 الخيار الافتراضي الأكثر أمانًا. عادةً ما يكون العنصر القوي والعنوان المقروء أفضل من الكولاج المزدحم." },
          { question: "YouTube", answer: "الوضع الموصى به: 16:9. يجب أن تكون صور YouTube المصغرة عريضة وجريئة وكبيرة بما يكفي لتبقى واضحة في البحث والتوصيات." },
          { question: "ما مقاسات القص المتاحة لوسائل التواصل؟", answer: "الإعدادات الحالية هي: Instagram ‏4:5 و1:1 و9:16؛ Facebook ‏1:1 و4:5 و1.91:1 و9:16؛ X ‏16:9 و1.91:1 و3:1؛ LinkedIn ‏1.91:1 و1:1؛ TikTok ‏9:16؛ Pinterest ‏2:3؛ YouTube ‏16:9؛ Xiaohongshu ‏3:4؛ WeChat ‏2.35:1؛ وWeibo ‏1:1." },
        ],
      },
    ],
    nl: [
      {
        title: "Algemeen",
        entries: [
          { question: "Wat is Cut My Pic?", answer: "Cut My Pic werd oorspronkelijk in 2009 bedacht door Sean Crownover (maker van Jishin.Watch). Deze nieuwe versie uit 2026 is een privé afbeeldingseditor in de browser met ondersteuning voor bijsnijden, maskeren, afgeronde hoeken, randen, schaduwen, formaat wijzigen, filters, aanpassingen, verscherpen en verzachten, tekst, stempels, geschiedenis, fullscreen bewerken en exporteren.", answerHtml: 'Cut My Pic werd oorspronkelijk in 2009 bedacht door Sean Crownover (<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">maker van Jishin.Watch</a>). Deze nieuwe versie uit 2026 is een privé afbeeldingseditor in de browser met ondersteuning voor bijsnijden, maskeren, afgeronde hoeken, randen, schaduwen, formaat wijzigen, filters, aanpassingen, verscherpen en verzachten, tekst, stempels, geschiedenis, fullscreen bewerken en exporteren.' },
          { question: "Uploadt Cut My Pic mijn afbeeldingen?", answer: "Nee. Bewerken gebeurt lokaal in je browser, dus je afbeeldingen blijven op je apparaat." },
          { question: "Welke formaten kan ik uploaden?", answer: "Je kunt gangbare afbeeldingsformaten uploaden zoals PNG, JPG, WebP en GIF." },
          { question: "Welk formaat gebruikt export?", answer: "Export ondersteunt PNG, JPG en WebP. PNG en WebP kunnen transparantie behouden; JPG gebruikt de gekozen achtergrondkleur." },
          { question: "Waarom laat schaduw het afbeeldingsgebied er anders uitzien?", answer: "Schaduw vergroot het canvas zodat de schaduw niet wordt afgesneden. De foto behoudt na toepassing dezelfde visuele grootte." },
          { question: "Wat doen verscherpen en verzachten?", answer: "Verscherpen maakt randen duidelijker en scherper, terwijl verzachten het tegenovergestelde doet en de afbeelding subtiel vervaagt. Beide hebben live preview zodat je de sterkte kunt finetunen voordat je toepast." },
          { question: "Kan ik meerdere randen toevoegen?", answer: "Ja. Elke keer dat je een rand toepast, wordt die onderdeel van de afbeelding, zodat latere uitsnedes of hoekaanpassingen de volledige randstapel opnieuw kunnen toepassen." },
          { question: "Kan ik tekst en stempels samen gebruiken?", answer: "Ja. De tekstmodus laat je eigen tekst toevoegen en de stempelmodus biedt vooraf ingestelde grafieken en labels. Je kunt tussen beide schakelen in hetzelfde tekstpaneel." },
          { question: "Wat is de beste werkvolgorde?", answer: "Een simpele volgorde werkt meestal het best: eerst bijsnijden, daarna afgeronde hoeken of rand, vervolgens filters en aanpassingen, daarna tekst of stempels, en aan het einde exporteren." },
          { question: "Kan ik meer dan één stap ongedaan maken?", answer: "Ja. Open het geschiedenisvenster om terug te gaan naar een eerder bewerkingspunt, indien nodig zelfs naar de originele afbeelding." },
          { question: "Hoe helpt fullscreen bewerken?", answer: "Fullscreen geeft je meer werkruimte en houdt de werkbalk beschikbaar, zodat je verder kunt bewerken terwijl je meer van de afbeelding ziet." },
          { question: "Wat is het verschil tussen bijsnijden, uitsnijden en maskeren?", answer: "Bijsnijden verwijdert een deel van de afbeelding, uitsnijmodi bepalen de vorm die je wilt behouden, en maskermodi laten je het geselecteerde gebied vervagen of inkleuren in plaats van het te verwijderen." },
          { question: "Werkt Cut My Pic offline?", answer: "Ja. Omdat de editor in je browser draait, kan hij blijven werken zonder verbinding zolang de pagina al is geladen." },
          { question: "Waarom hebben sommige hulpmiddelen een knop Toepassen?", answer: "De knop Toepassen bevestigt een bewerking zodat die in de geschiedenis komt en je op dat resultaat verder kunt werken met andere hulpmiddelen." },
        ],
      },
      {
        title: "Sociale netwerken",
        entries: [
          { question: "Instagram", answer: "Aanbevolen modi: 4:5 uitsnede voor feed-posts, 1:1 voor vierkante posts en 9:16 voor Stories en Reels. Houd tekst groot, gecentreerd en weg van de randen. Snijd eerst bij en voeg alleen afgeronde hoeken of een rand toe als het ontwerp dat nodig heeft." },
          { question: "Facebook", answer: "Aanbevolen modi: 1:1 of 4:5 voor feed-posts, 1.91:1 voor linkvoorbeelden en 9:16 voor Stories. Zorg dat het hoofdonderwerp ook op kleinere formaten duidelijk blijft en vermijd piepkleine tekst." },
          { question: "X", answer: "Aanbevolen modi: 16:9 voor de meeste posts, 1.91:1 voor brede linkachtige visuals en 3:1 voor bannerachtige headers. Houd de compositie eenvoudig en het focuspunt gecentreerd." },
          { question: "LinkedIn", answer: "Aanbevolen modi: 1.91:1 voor linkvoorvertoningen en 1:1 voor nette vierkante posts. Houd de kop kort en maak het ontwerp meer zakelijk dan decoratief." },
          { question: "TikTok", answer: "Aanbevolen modus: 9:16. TikTok is gemaakt voor verticale content, dus plaats belangrijke elementen in het midden omdat de app-interface de boven- en onderkant bedekt." },
          { question: "Pinterest", answer: "Aanbevolen modus: 2:3. Pinterest werkt goed met verticale afbeeldingen en 2:3 is de veiligste standaard. Een sterk onderwerp en een goed leesbaar titelblok werken meestal beter dan een druk collage." },
          { question: "YouTube", answer: "Aanbevolen modus: 16:9. YouTube-thumbnails moeten breed, opvallend en groot genoeg zijn om scherp te blijven in zoeken en aanbevelingen." },
          { question: "Welke sociale uitsnijformaten zijn beschikbaar?", answer: "De huidige presets zijn: Instagram 4:5, 1:1 en 9:16; Facebook 1:1, 4:5, 1.91:1 en 9:16; X 16:9, 1.91:1 en 3:1; LinkedIn 1.91:1 en 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; en Weibo 1:1." },
        ],
      },
    ],
    pl: [
      {
        title: "Ogólne",
        entries: [
          { question: "Czym jest Cut My Pic?", answer: "Cut My Pic został pierwotnie wymyślony przez Seana Crownovera (twórcę Jishin.Watch) w 2009 roku. Nowa wersja z 2026 roku to prywatny, przeglądarkowy edytor obrazów, który obsługuje kadrowanie, maskę, zaokrąglone rogi, obramowania, cienie, zmianę rozmiaru, filtry, regulacje, wyostrzanie i zmiękczanie, tekst, stemple, historię, edycję pełnoekranową i eksport.", answerHtml: 'Cut My Pic został pierwotnie wymyślony przez Seana Crownovera (<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">twórcę Jishin.Watch</a>) w 2009 roku. Nowa wersja z 2026 roku to prywatny, przeglądarkowy edytor obrazów, który obsługuje kadrowanie, maskę, zaokrąglone rogi, obramowania, cienie, zmianę rozmiaru, filtry, regulacje, wyostrzanie i zmiękczanie, tekst, stemple, historię, edycję pełnoekranową i eksport.' },
          { question: "Czy Cut My Pic przesyła moje obrazy?", answer: "Nie. Edycja odbywa się lokalnie w przeglądarce, więc Twoje obrazy zostają na urządzeniu." },
          { question: "Jakie formaty mogę przesłać?", answer: "Możesz przesłać popularne formaty obrazu, takie jak PNG, JPG, WebP i GIF." },
          { question: "Jakiego formatu używa eksport?", answer: "Eksport obsługuje PNG, JPG i WebP. PNG i WebP mogą zachować przezroczystość; JPG używa wybranego koloru tła." },
          { question: "Dlaczego cień sprawia, że obszar obrazu wygląda inaczej?", answer: "Cień rozszerza obszar roboczy, aby nie został obcięty. Zdjęcie zachowuje ten sam wizualny rozmiar po zastosowaniu cienia." },
          { question: "Co robią wyostrzanie i zmiękczanie?", answer: "Wyostrzanie sprawia, że krawędzie są bardziej wyraźne, a zmiękczanie robi odwrotnie, delikatnie rozmazując obraz. Oba mają podgląd na żywo, więc możesz precyzyjnie dobrać intensywność przed zastosowaniem." },
          { question: "Czy mogę dodać kilka obramowań?", answer: "Tak. Za każdym razem, gdy zastosujesz obramowanie, staje się ono częścią obrazu, więc późniejsze kadrowanie lub korekty rogów mogą ponownie zastosować cały zestaw obramowań." },
          { question: "Czy mogę używać tekstu i stempli razem?", answer: "Tak. Tryb Tekst umożliwia dodawanie własnego tekstu, a tryb Stempel oferuje gotowe grafiki i etykiety. Możesz przełączać się między nimi w tym samym panelu tekstu." },
          { question: "Jaki jest najlepszy przepływ pracy?", answer: "Najlepiej działa kolejność: najpierw kadrowanie, potem zaokrąglone rogi lub obramowanie, następnie filtry i regulacje, potem tekst lub stemple, a na końcu eksport." },
          { question: "Czy mogę cofnąć więcej niż jeden krok?", answer: "Tak. Otwórz okno historii, aby wrócić do wcześniejszego punktu edycji, nawet do oryginalnego obrazu, jeśli trzeba." },
          { question: "Jak pomaga edycja pełnoekranowa?", answer: "Tryb pełnoekranowy daje więcej miejsca do pracy i utrzymuje pasek narzędzi dostępny, dzięki czemu możesz edytować, widząc większą część obrazu." },
          { question: "Jaka jest różnica między kadrowaniem, cięciem i maską?", answer: "Kadrowanie usuwa część obrazu, tryby cięcia definiują kształt, który chcesz zachować, a tryby maski pozwalają rozmyć lub pokolorować zaznaczony obszar zamiast go usuwać." },
          { question: "Czy Cut My Pic działa offline?", answer: "Tak. Ponieważ edytor działa w przeglądarce, może nadal działać bez połączenia, o ile strona została już załadowana." },
          { question: "Dlaczego niektóre narzędzia mają przycisk Zastosuj?", answer: "Przycisk Zastosuj zatwierdza zmianę, aby trafiła do historii, a Ty możesz potem kontynuować pracę z innymi narzędziami na podstawie tego efektu." },
        ],
      },
      {
        title: "Sieci społecznościowe",
        entries: [
          { question: "Instagram", answer: "Polecane tryby: 4:5 do postów w feedzie, 1:1 do kwadratowych postów i 9:16 do Stories oraz Reels. Zachowaj duży, wyśrodkowany tekst z dala od krawędzi. Najpierw kadruj, a zaokrąglone rogi lub obramowanie dodawaj tylko wtedy, gdy projekt tego potrzebuje." },
          { question: "Facebook", answer: "Polecane tryby: 1:1 lub 4:5 do postów w feedzie, 1.91:1 do podglądów linków i 9:16 do Stories. Upewnij się, że główny obiekt jest czytelny nawet w małym rozmiarze i unikaj drobnego tekstu." },
          { question: "X", answer: "Polecane tryby: 16:9 do większości postów, 1.91:1 do szerokich wizualizacji typu link i 3:1 do nagłówków w stylu banera. Utrzymuj prostą kompozycję i centralny punkt uwagi." },
          { question: "LinkedIn", answer: "Polecane tryby: 1.91:1 do grafik podglądu linków i 1:1 do czystych kwadratowych postów. Trzymaj tytuł krótki, a styl bardziej biznesowy niż dekoracyjny." },
          { question: "TikTok", answer: "Polecany tryb: 9:16. TikTok jest tworzony z myślą o treściach pionowych, więc ważne elementy umieszczaj na środku, ponieważ interfejs aplikacji zakrywa górę i dół." },
          { question: "Pinterest", answer: "Polecany tryb: 2:3. Pinterest dobrze działa z pionowymi obrazami, a 2:3 to najbezpieczniejsza wartość domyślna. Mocny obiekt i czytelny blok tytułu zwykle działają lepiej niż przeładowany kolaż." },
          { question: "YouTube", answer: "Polecany tryb: 16:9. Miniatury YouTube powinny być szerokie, wyraziste i na tyle duże, by pozostały ostre w wyszukiwarce i rekomendacjach." },
          { question: "Jakie rozmiary cięcia dla mediów społecznościowych są dostępne?", answer: "Obecne presety to: Instagram 4:5, 1:1 i 9:16; Facebook 1:1, 4:5, 1.91:1 i 9:16; X 16:9, 1.91:1 i 3:1; LinkedIn 1.91:1 i 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; i Weibo 1:1." },
        ],
      },
    ],
    tr: [
      {
        title: "Genel",
        entries: [
          { question: "Cut My Pic nedir?", answer: "Cut My Pic ilk olarak 2009 yılında Sean Crownover (Jishin.Watch'in yapımcısı) tarafından tasarlandı. Yeni 2026 sürümü; kırpma, maske, yuvarlatılmış köşeler, kenarlıklar, gölgeler, yeniden boyutlandırma, filtreler, ayarlamalar, keskinleştirme ve yumuşatma, metin, damgalar, geçmiş, tam ekran düzenleme ve dışa aktarmayı destekleyen özel bir tarayıcı içi görsel düzenleyicidir.", answerHtml: 'Cut My Pic ilk olarak 2009 yılında Sean Crownover (<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">Jishin.Watch\'in yapımcısı</a>) tarafından tasarlandı. Yeni 2026 sürümü; kırpma, maske, yuvarlatılmış köşeler, kenarlıklar, gölgeler, yeniden boyutlandırma, filtreler, ayarlamalar, keskinleştirme ve yumuşatma, metin, damgalar, geçmiş, tam ekran düzenleme ve dışa aktarmayı destekleyen özel bir tarayıcı içi görsel düzenleyicidir.' },
          { question: "Cut My Pic görsellerimi yükler mi?", answer: "Hayır. Düzenleme tarayıcınızda yerel olarak yapılır, bu yüzden görselleriniz cihazınızda kalır." },
          { question: "Hangi dosya formatlarını yükleyebilirim?", answer: "PNG, JPG, WebP ve GIF gibi yaygın görsel formatlarını yükleyebilirsiniz." },
          { question: "Dışa aktarma hangi formatı kullanır?", answer: "Dışa aktarma PNG, JPG ve WebP’yi destekler. PNG ve WebP saydamlığı koruyabilir; JPG seçilen arka plan rengini kullanır." },
          { question: "Gölgeler neden görüntü alanını farklı gösterir?", answer: "Gölge kırpılmaması için tuvali genişletir. Fotoğraf, gölge uygulandıktan sonra aynı görsel boyutta kalır." },
          { question: "Keskinleştirme ve yumuşatma ne yapar?", answer: "Keskinleştirme kenarları daha net ve belirgin yapar, yumuşatma ise bunun tersini yaparak görseli hafifçe bulanıklaştırır. İkisi de canlı önizleme sunar, böylece uygulamadan önce yoğunluğu ince ayarlayabilirsiniz." },
          { question: "Birden fazla kenarlık ekleyebilir miyim?", answer: "Evet. Her kenarlık uyguladığınızda görüntünün bir parçası olur, böylece sonraki kırpma veya köşe düzenlemeleri tüm kenarlık katmanını yeniden uygulayabilir." },
          { question: "Metin ve damgaları birlikte kullanabilir miyim?", answer: "Evet. Metin modu özel metin eklemenizi sağlar, Damga modu ise hazır grafikler ve etiketler sunar. İkisinin arasında aynı metin panelinden geçiş yapabilirsiniz." },
          { question: "En iyi iş akışı nedir?", answer: "Genelde en iyi sıra şudur: önce kırpma, sonra yuvarlatılmış köşeler veya kenarlık, ardından filtreler ve ayarlamalar, sonra metin veya damgalar ve en son dışa aktarma." },
          { question: "Birden fazla adımı geri alabilir miyim?", answer: "Evet. Geçmiş penceresini açarak önceki bir düzenleme noktasına, gerekirse orijinal görsele bile dönebilirsiniz." },
          { question: "Tam ekran düzenleme nasıl yardımcı olur?", answer: "Tam ekran modu daha fazla çalışma alanı sağlar ve araç çubuğunu kullanılabilir tutar, böylece görselin daha fazlasını görerek düzenlemeye devam edebilirsiniz." },
          { question: "Kırpma, kesme ve maskeleme arasındaki fark nedir?", answer: "Kırpma görüntünün bir kısmını kaldırır, kesme modları tutmak istediğiniz şekli tanımlar ve maske modları seçilen alanı silmek yerine bulanıklaştırmanıza veya renklendirmenize izin verir." },
          { question: "Cut My Pic çevrimdışı çalışır mı?", answer: "Evet. Düzenleyici tarayıcıda çalıştığı için, sayfa zaten yüklenmişse bağlantı olmadan da çalışmaya devam edebilir." },
          { question: "Bazı araçlarda neden Uygula düğmesi var?", answer: "Uygula düğmesi bir düzenlemeyi onaylar, böylece geçmişe eklenir ve bu sonuç üzerinden diğer araçlarla çalışmaya devam edebilirsiniz." },
        ],
      },
      {
        title: "Sosyal Ağlar",
        entries: [
          { question: "Instagram", answer: "Önerilen modlar: akış gönderileri için 4:5 kırpma, kare gönderiler için 1:1 kırpma ve Stories ile Reels için 9:16 kırpma. Metni büyük, ortalı ve kenarlardan uzak tutun. Önce kırpın, yuvarlatılmış köşe veya kenarlık yalnızca tasarım gerekiyorsa ekleyin." },
          { question: "Facebook", answer: "Önerilen modlar: akış gönderileri için 1:1 veya 4:5 kırpma, bağlantı önizlemeleri için 1.91:1 kırpma ve Stories için 9:16 kırpma. Ana konunun küçük boyutta da net görünmesini sağlayın ve çok küçük metinden kaçının." },
          { question: "X", answer: "Önerilen modlar: çoğu gönderi için 16:9 kırpma, geniş bağlantı tarzı görseller için 1.91:1 kırpma ve afiş tarzı başlıklar için 3:1 kırpma. Kompozisyonu basit tutun ve odağı ortada bırakın." },
          { question: "LinkedIn", answer: "Önerilen modlar: bağlantı önizleme tarzı grafikler için 1.91:1 kırpma ve temiz kare gönderiler için 1:1 kırpma. Başlığı kısa tutun ve tasarımı dekoratiften çok profesyonel yapın." },
          { question: "TikTok", answer: "Önerilen mod: 9:16. TikTok dikey içerik içindir, bu yüzden önemli öğeleri ortaya yerleştirin çünkü uygulama arayüzü üst ve alt kısmı kaplar." },
          { question: "Pinterest", answer: "Önerilen mod: 2:3. Pinterest uzun dikey görsellerle iyi çalışır ve 2:3 en güvenli varsayılandır. Güçlü bir konu ve okunabilir başlık bloğu genellikle kalabalık kolajlardan daha iyi sonuç verir." },
          { question: "YouTube", answer: "Önerilen mod: 16:9. YouTube küçük resimleri geniş, dikkat çekici ve arama ile önerilerde net kalacak kadar büyük olmalıdır." },
          { question: "Hangi sosyal kırpma boyutları mevcut?", answer: "Mevcut ön ayarlar: Instagram 4:5, 1:1, 9:16; Facebook 1:1, 4:5, 1.91:1, 9:16; X 16:9, 1.91:1, 3:1; LinkedIn 1.91:1, 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; ve Weibo 1:1." },
        ],
      },
    ],
    id: [
      {
        title: "Umum",
        entries: [
          { question: "Apa itu Cut My Pic?", answer: "Cut My Pic pertama kali digagas oleh Sean Crownover (pembuat Jishin.Watch) pada 2009. Versi baru 2026 ini adalah editor gambar pribadi berbasis browser yang mendukung crop, mask, sudut membulat, border, shadow, resize, filter, adjustments, sharpen dan soften, teks, stempel, riwayat, edit layar penuh, dan ekspor.", answerHtml: 'Cut My Pic pertama kali digagas oleh Sean Crownover (<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">pembuat Jishin.Watch</a>) pada 2009. Versi baru 2026 ini adalah editor gambar pribadi berbasis browser yang mendukung crop, mask, sudut membulat, border, shadow, resize, filter, adjustments, sharpen dan soften, teks, stempel, riwayat, edit layar penuh, dan ekspor.' },
          { question: "Apakah Cut My Pic mengunggah gambar saya?", answer: "Tidak. Pengeditan dilakukan secara lokal di browser Anda, jadi gambar tetap di perangkat Anda." },
          { question: "Format apa yang bisa saya unggah?", answer: "Anda bisa mengunggah format gambar umum seperti PNG, JPG, WebP, dan GIF." },
          { question: "Format apa yang dipakai ekspor?", answer: "Ekspor mendukung PNG, JPG, dan WebP. PNG dan WebP dapat mempertahankan transparansi; JPG memakai warna latar yang dipilih." },
          { question: "Mengapa shadow membuat area gambar terlihat berbeda?", answer: "Shadow memperluas kanvas agar shadow tidak terpotong. Foto tetap memiliki ukuran visual yang sama setelah shadow diterapkan." },
          { question: "Apa fungsi sharpen dan soften?", answer: "Sharpen membuat tepi terlihat lebih tajam dan jelas, sedangkan soften melakukan kebalikannya dengan memburamkan gambar secara halus. Keduanya memiliki pratinjau langsung sehingga Anda bisa menyesuaikan intensitas sebelum menerapkannya." },
          { question: "Bisakah saya menambahkan beberapa border?", answer: "Bisa. Setiap kali Anda menerapkan border, border tersebut menjadi bagian dari gambar, sehingga crop atau penyesuaian sudut berikutnya bisa menerapkan kembali seluruh tumpukan border." },
          { question: "Bisakah saya memakai teks dan stempel bersama?", answer: "Bisa. Mode Teks memungkinkan Anda menambahkan teks kustom, sementara Mode Stempel menyediakan grafik dan label siap pakai. Anda bisa berpindah di antara keduanya dari panel teks yang sama." },
          { question: "Apa alur kerja terbaik?", answer: "Urutan yang biasanya paling baik adalah: crop dulu, lalu sudut membulat atau border, kemudian filter dan adjustments, setelah itu teks atau stempel, dan ekspor di akhir." },
          { question: "Bisakah saya membatalkan lebih dari satu langkah?", answer: "Bisa. Buka jendela riwayat untuk kembali ke titik edit sebelumnya, bahkan ke gambar asli jika diperlukan." },
          { question: "Bagaimana edit layar penuh membantu?", answer: "Mode layar penuh memberi Anda ruang kerja lebih luas dan tetap mempertahankan toolbar agar Anda bisa terus mengedit sambil melihat lebih banyak gambar." },
          { question: "Apa bedanya crop, cut, dan mask?", answer: "Crop menghapus sebagian gambar, mode cut menentukan bentuk yang ingin dipertahankan, dan mode mask memungkinkan Anda memburamkan atau mewarnai area yang dipilih alih-alih menghapusnya." },
          { question: "Apakah Cut My Pic bisa dipakai offline?", answer: "Ya. Karena editor berjalan di browser, ia tetap bisa bekerja tanpa koneksi selama halaman sudah dimuat." },
          { question: "Mengapa beberapa alat punya tombol Apply?", answer: "Tombol Apply mengonfirmasi edit agar masuk ke riwayat, sehingga Anda bisa melanjutkan bekerja dari hasil itu dengan alat lain." },
        ],
      },
      {
        title: "Jejaring Sosial",
        entries: [
          { question: "Instagram", answer: "Mode yang disarankan: crop 4:5 untuk posting feed, 1:1 untuk posting persegi, dan 9:16 untuk Stories serta Reels. Jaga teks tetap besar, di tengah, dan jauh dari tepi. Crop dulu, lalu tambahkan sudut membulat atau border hanya jika desain membutuhkannya." },
          { question: "Facebook", answer: "Mode yang disarankan: 1:1 atau 4:5 untuk posting feed, 1.91:1 untuk pratinjau tautan, dan 9:16 untuk Stories. Pastikan subjek utama tetap jelas di ukuran kecil dan hindari teks yang terlalu kecil." },
          { question: "X", answer: "Mode yang disarankan: 16:9 untuk sebagian besar posting, 1.91:1 untuk visual lebar bergaya tautan, dan 3:1 untuk header bergaya banner. Jaga komposisi tetap sederhana dan fokus tetap di tengah." },
          { question: "LinkedIn", answer: "Mode yang disarankan: 1.91:1 untuk grafik pratinjau tautan dan 1:1 untuk posting persegi yang bersih. Jaga judul tetap singkat dan buat tampilannya lebih profesional daripada dekoratif." },
          { question: "TikTok", answer: "Mode yang disarankan: 9:16. TikTok dibuat untuk konten vertikal, jadi tempatkan elemen penting di tengah karena antarmuka aplikasi menutupi bagian atas dan bawah." },
          { question: "Pinterest", answer: "Mode yang disarankan: 2:3. Pinterest sangat cocok dengan gambar vertikal dan 2:3 adalah nilai default paling aman. Subjek yang kuat dan blok judul yang mudah dibaca biasanya lebih efektif daripada kolase yang terlalu ramai." },
          { question: "YouTube", answer: "Mode yang disarankan: 16:9. Thumbnail YouTube harus lebar, mencolok, dan cukup besar agar tetap tajam di pencarian dan rekomendasi." },
          { question: "Ukuran potong sosial apa saja yang tersedia?", answer: "Preset saat ini adalah: Instagram 4:5, 1:1, dan 9:16; Facebook 1:1, 4:5, 1.91:1, dan 9:16; X 16:9, 1.91:1, dan 3:1; LinkedIn 1.91:1 dan 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; dan Weibo 1:1." },
        ],
      },
    ],
    ms: [
      {
        title: "Umum",
        entries: [
          { question: "Apakah Cut My Pic?", answer: "Cut My Pic pada asalnya dicetuskan oleh Sean Crownover (pencipta Jishin.Watch) pada tahun 2009. Versi baharu 2026 ini ialah penyunting imej peribadi dalam pelayar yang menyokong pangkasan, topeng, sudut bulat, bingkai, bayang, ubah saiz, penapis, pelarasan, teks, cap, sejarah, skrin penuh dan eksport.", answerHtml: 'Cut My Pic pada asalnya dicetuskan oleh Sean Crownover (<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">pencipta Jishin.Watch</a>) pada tahun 2009. Versi baharu 2026 ini ialah penyunting imej peribadi dalam pelayar yang menyokong pangkasan, topeng, sudut bulat, bingkai, bayang, ubah saiz, penapis, pelarasan, teks, cap, sejarah, skrin penuh dan eksport.' },
          { question: "Adakah Cut My Pic memuat naik imej saya?", answer: "Tidak. Penyuntingan berlaku secara setempat dalam pelayar, jadi imej kekal pada peranti anda." },
          { question: "Apakah format yang boleh saya muat naik?", answer: "Anda boleh memuat naik format imej lazim seperti PNG, JPG, WebP dan GIF." },
          { question: "Apakah format eksport?", answer: "Eksport menyokong PNG, JPG dan WebP. PNG dan WebP boleh mengekalkan kelutsinaran; JPG menggunakan warna latar yang dipilih." },
          { question: "Bolehkah saya membuat asal lebih daripada satu langkah?", answer: "Ya. Buka Sejarah suntingan untuk kembali ke mana-mana langkah terdahulu atau imej asal." },
          { question: "Bolehkah saya menggunakan teks dan cap bersama-sama?", answer: "Ya. Tambah teks tersuai dalam mod Teks dan pilih grafik sedia ada dalam mod Cap. Anda boleh bertukar antara kedua-duanya dalam panel yang sama." },
          { question: "Apakah urutan kerja yang disyorkan?", answer: "Biasanya paling mudah untuk memangkas dahulu, kemudian menambah sudut atau bingkai, menggunakan penapis dan pelarasan, menambah teks atau cap, lalu mengeksport pada akhir." },
          { question: "Adakah Cut My Pic berfungsi di luar talian?", answer: "Ya. Selepas halaman dimuatkan, penyunting boleh terus berfungsi tanpa sambungan kerana pemprosesan dilakukan dalam pelayar." },
        ],
      },
      {
        title: "Rangkaian sosial",
        entries: [
          { question: "Instagram", answer: "Gunakan 4:5 untuk siaran suapan, 1:1 untuk siaran segi empat sama dan 9:16 untuk Stories atau Reels. Pastikan teks besar, di tengah dan jauh daripada tepi." },
          { question: "Facebook", answer: "Gunakan 1:1 atau 4:5 untuk suapan, 1.91:1 untuk pratonton pautan dan 9:16 untuk Stories." },
          { question: "X", answer: "Gunakan 16:9 untuk kebanyakan siaran, 1.91:1 untuk visual lebar dan 3:1 untuk pengepala berbentuk sepanduk." },
          { question: "LinkedIn", answer: "Gunakan 1.91:1 untuk pratonton pautan dan 1:1 untuk siaran segi empat sama yang kemas." },
          { question: "TikTok", answer: "Gunakan 9:16 dan letakkan unsur penting di tengah kerana antara muka aplikasi meliputi bahagian atas dan bawah." },
          { question: "Pinterest", answer: "Gunakan 2:3. Imej menegak dengan subjek yang jelas dan tajuk mudah dibaca biasanya memberikan hasil terbaik." },
          { question: "YouTube", answer: "Gunakan 16:9. Imej kecil perlu lebar, berani dan cukup jelas untuk paparan bersaiz kecil." },
          { question: "Apakah saiz pangkasan sosial yang tersedia?", answer: "Pratetap termasuk Instagram, Facebook, X, LinkedIn, TikTok, Pinterest, YouTube, Xiaohongshu, WeChat dan Weibo pada nisbah yang sesuai untuk setiap platform." },
        ],
      },
    ],
    vi: [
      {
        title: "Chung",
        entries: [
          { question: "Cut My Pic là gì?", answer: "Cut My Pic được Sean Crownover (người tạo ra Jishin.Watch) lên ý tưởng lần đầu vào năm 2009. Phiên bản 2026 mới này là trình chỉnh sửa ảnh riêng tư trên trình duyệt, hỗ trợ cắt, mặt nạ, bo góc, viền, bóng đổ, đổi kích thước, bộ lọc, điều chỉnh, làm nét và làm mềm, văn bản, tem, lịch sử, chỉnh sửa toàn màn hình và xuất ảnh.", answerHtml: 'Cut My Pic được Sean Crownover (<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">người tạo ra Jishin.Watch</a>) lên ý tưởng lần đầu vào năm 2009. Phiên bản 2026 mới này là trình chỉnh sửa ảnh riêng tư trên trình duyệt, hỗ trợ cắt, mặt nạ, bo góc, viền, bóng đổ, đổi kích thước, bộ lọc, điều chỉnh, làm nét và làm mềm, văn bản, tem, lịch sử, chỉnh sửa toàn màn hình và xuất ảnh.' },
          { question: "Cut My Pic có tải ảnh của tôi lên không?", answer: "Không. Việc chỉnh sửa diễn ra cục bộ trong trình duyệt, nên ảnh của bạn vẫn ở trên thiết bị." },
          { question: "Tôi có thể tải lên những định dạng nào?", answer: "Bạn có thể tải lên các định dạng ảnh phổ biến như PNG, JPG, WebP và GIF." },
          { question: "Xuất ảnh dùng định dạng gì?", answer: "Xuất ảnh hỗ trợ PNG, JPG và WebP. PNG và WebP có thể giữ độ trong suốt; JPG dùng màu nền đã chọn." },
          { question: "Tại sao bóng đổ làm vùng ảnh trông khác đi?", answer: "Bóng đổ mở rộng khung vẽ để không bị cắt mất. Ảnh giữ nguyên kích thước hiển thị sau khi áp dụng bóng đổ." },
          { question: "Làm nét và làm mềm có tác dụng gì?", answer: "Làm nét khiến các cạnh rõ và sắc hơn, còn làm mềm thì ngược lại bằng cách làm mờ ảnh nhẹ nhàng. Cả hai đều có xem trước trực tiếp để bạn tinh chỉnh cường độ trước khi áp dụng." },
          { question: "Tôi có thể thêm nhiều viền không?", answer: "Có. Mỗi lần áp dụng viền, nó trở thành một phần của ảnh, nên các thao tác cắt hoặc chỉnh góc sau đó có thể áp dụng lại toàn bộ chuỗi viền." },
          { question: "Tôi có thể dùng văn bản và tem cùng lúc không?", answer: "Có. Chế độ Văn bản cho phép thêm chữ tuỳ chỉnh, còn Chế độ Tem cung cấp đồ họa và nhãn có sẵn. Bạn có thể chuyển đổi giữa chúng trong cùng một bảng." },
          { question: "Quy trình làm việc tốt nhất là gì?", answer: "Thường hiệu quả nhất là: cắt trước, sau đó bo góc hoặc thêm viền, tiếp theo là bộ lọc và điều chỉnh, rồi thêm văn bản hoặc tem, và cuối cùng là xuất ảnh." },
          { question: "Tôi có thể hoàn tác nhiều bước không?", answer: "Có. Mở cửa sổ lịch sử để quay lại một điểm chỉnh sửa trước đó, thậm chí là ảnh gốc nếu cần." },
          { question: "Chỉnh sửa toàn màn hình giúp gì?", answer: "Chế độ toàn màn hình cho bạn nhiều không gian làm việc hơn và vẫn giữ thanh công cụ sẵn sàng để tiếp tục chỉnh sửa trong khi xem được nhiều ảnh hơn." },
          { question: "Khác nhau giữa cắt, crop và mặt nạ là gì?", answer: "Crop xóa một phần ảnh, các chế độ cut xác định hình dạng bạn muốn giữ, còn chế độ mặt nạ cho phép làm mờ hoặc tô màu vùng đã chọn thay vì xóa nó." },
          { question: "Cut My Pic có hoạt động offline không?", answer: "Có. Vì trình chỉnh sửa chạy trong trình duyệt nên nó vẫn có thể hoạt động không cần kết nối miễn là trang đã được tải." },
          { question: "Tại sao một số công cụ có nút Áp dụng?", answer: "Nút Áp dụng xác nhận thay đổi để nó đi vào lịch sử, giúp bạn tiếp tục làm việc từ kết quả đó với các công cụ khác." },
        ],
      },
      {
        title: "Mạng xã hội",
        entries: [
          { question: "Instagram", answer: "Chế độ khuyên dùng: crop 4:5 cho bài feed, 1:1 cho bài vuông, và 9:16 cho Stories và Reels. Giữ chữ lớn, ở giữa và cách xa mép. Hãy crop trước, rồi chỉ thêm bo góc hoặc viền nếu thiết kế cần." },
          { question: "Facebook", answer: "Chế độ khuyên dùng: 1:1 hoặc 4:5 cho bài feed, 1.91:1 cho xem trước liên kết, và 9:16 cho Stories. Hãy đảm bảo chủ thể chính vẫn rõ khi thu nhỏ và tránh chữ quá nhỏ." },
          { question: "X", answer: "Chế độ khuyên dùng: 16:9 cho hầu hết bài đăng, 1.91:1 cho hình rộng kiểu liên kết, và 3:1 cho tiêu đề kiểu banner. Giữ bố cục đơn giản và điểm nhấn ở giữa." },
          { question: "LinkedIn", answer: "Chế độ khuyên dùng: 1.91:1 cho đồ họa xem trước liên kết và 1:1 cho bài vuông gọn gàng. Giữ tiêu đề ngắn và phong cách chuyên nghiệp hơn là trang trí." },
          { question: "TikTok", answer: "Chế độ khuyên dùng: 9:16. TikTok dành cho nội dung dọc, nên hãy đặt các yếu tố quan trọng ở giữa vì giao diện ứng dụng che phần trên và dưới." },
          { question: "Pinterest", answer: "Chế độ khuyên dùng: 2:3. Pinterest hoạt động rất tốt với ảnh dọc và 2:3 là giá trị mặc định an toàn nhất. Một chủ thể mạnh và khối tiêu đề dễ đọc thường hiệu quả hơn một ảnh ghép quá nhiều chi tiết." },
          { question: "YouTube", answer: "Chế độ khuyên dùng: 16:9. Thumbnail YouTube nên rộng, nổi bật và đủ lớn để vẫn sắc nét trong tìm kiếm và đề xuất." },
          { question: "Có những kích thước cắt cho mạng xã hội nào?", answer: "Các preset hiện tại gồm: Instagram 4:5, 1:1 và 9:16; Facebook 1:1, 4:5, 1.91:1 và 9:16; X 16:9, 1.91:1 và 3:1; LinkedIn 1.91:1 và 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; và Weibo 1:1." },
        ],
      },
    ],
    th: [
      {
        title: "ทั่วไป",
        entries: [
          { question: "Cut My Pic คืออะไร?", answer: "Cut My Pic ได้รับการคิดค้นครั้งแรกโดย Sean Crownover (ผู้สร้าง Jishin.Watch) ในปี 2009 เวอร์ชันใหม่ปี 2026 นี้คือโปรแกรมแก้ไขภาพแบบส่วนตัวที่ทำงานในเบราว์เซอร์ รองรับการครอป มาสก์ มุมโค้ง เส้นขอบ เงา การปรับขนาด ฟิลเตอร์ การปรับแต่ง การทำให้คมและนุ่ม ข้อความ แสตมป์ ประวัติ การแก้ไขเต็มหน้าจอ และการส่งออก", answerHtml: 'Cut My Pic ได้รับการคิดค้นครั้งแรกโดย Sean Crownover (<a class="manual-jishin-link" href="https://jishin.watch" target="_blank" rel="noreferrer noopener">ผู้สร้าง Jishin.Watch</a>) ในปี 2009 เวอร์ชันใหม่ปี 2026 นี้คือโปรแกรมแก้ไขภาพแบบส่วนตัวที่ทำงานในเบราว์เซอร์ รองรับการครอป มาสก์ มุมโค้ง เส้นขอบ เงา การปรับขนาด ฟิลเตอร์ การปรับแต่ง การทำให้คมและนุ่ม ข้อความ แสตมป์ ประวัติ การแก้ไขเต็มหน้าจอ และการส่งออก' },
          { question: "Cut My Pic อัปโหลดรูปของฉันไหม?", answer: "ไม่ การแก้ไขเกิดขึ้นภายในเบราว์เซอร์แบบโลคัล ดังนั้นรูปของคุณจะยังอยู่ในอุปกรณ์" },
          { question: "ฉันอัปโหลดไฟล์แบบไหนได้บ้าง?", answer: "คุณสามารถอัปโหลดไฟล์ภาพทั่วไป เช่น PNG, JPG, WebP และ GIF" },
          { question: "การส่งออกใช้รูปแบบอะไร?", answer: "การส่งออกรองรับ PNG, JPG และ WebP โดย PNG และ WebP สามารถคงความโปร่งใสได้ ส่วน JPG ใช้สีพื้นหลังที่เลือก" },
          { question: "ทำไมเงาถึงทำให้พื้นที่ภาพดูต่างออกไป?", answer: "เงาจะขยายพื้นที่วาดเพื่อไม่ให้ถูกตัดออก ภาพจะคงขนาดการแสดงผลเดิมหลังใช้เงา" },
          { question: "การทำให้คมและการทำให้นุ่มทำอะไร?", answer: "การทำให้คมจะทำให้ขอบชัดและเด่นขึ้น ส่วนการทำให้นุ่มจะทำตรงข้ามด้วยการเบลอภาพอย่างอ่อนโยน ทั้งสองมีตัวอย่างสดให้ปรับความแรงก่อนใช้จริง" },
          { question: "ฉันเพิ่มขอบหลายชั้นได้ไหม?", answer: "ได้ ทุกครั้งที่ใช้ขอบ มันจะกลายเป็นส่วนหนึ่งของภาพ ดังนั้นการครอปหรือปรับมุมในภายหลังยังสามารถนำขอบทั้งหมดกลับมาใช้ได้" },
          { question: "ใช้ข้อความและสแตมป์ร่วมกันได้ไหม?", answer: "ได้ โหมดข้อความให้เพิ่มข้อความเองได้ ส่วนโหมดสแตมป์มีกราฟิกและป้ายสำเร็จรูป คุณสลับไปมาระหว่างกันได้ในแผงเดียวกัน" },
          { question: "เวิร์กโฟลว์ที่ดีที่สุดคืออะไร?", answer: "ลำดับที่มักได้ผลดีที่สุดคือ ครอปก่อน จากนั้นมุมโค้งหรือขอบ แล้วจึงฟิลเตอร์และการปรับแต่ง ตามด้วยข้อความหรือสแตมป์ และส่งออกเป็นขั้นตอนสุดท้าย" },
          { question: "ฉันสามารถย้อนหลายขั้นตอนได้ไหม?", answer: "ได้ เปิดหน้าต่างประวัติเพื่อกลับไปยังจุดแก้ไขก่อนหน้า หรือแม้แต่รูปต้นฉบับถ้าจำเป็น" },
          { question: "การแก้ไขเต็มหน้าจอช่วยอย่างไร?", answer: "โหมดเต็มหน้าจอช่วยให้มีพื้นที่ทำงานมากขึ้น และยังคงแถบเครื่องมือไว้ให้ใช้งาน เพื่อให้คุณแก้ไขต่อไปพร้อมมองเห็นภาพได้มากขึ้น" },
          { question: "ครอป ตัด และมาสก์ต่างกันอย่างไร?", answer: "การครอปจะลบส่วนของภาพออก โหมดตัดจะกำหนดรูปทรงที่ต้องการเก็บไว้ และโหมดมาสก์จะให้คุณเบลอหรือใส่สีในพื้นที่ที่เลือกแทนการลบออก" },
          { question: "Cut My Pic ใช้งานออฟไลน์ได้ไหม?", answer: "ได้ เพราะโปรแกรมทำงานในเบราว์เซอร์ จึงยังทำงานต่อได้แม้ไม่มีการเชื่อมต่อ ตราบใดที่หน้าเว็บถูกโหลดไว้แล้ว" },
          { question: "ทำไมบางเครื่องมือมีปุ่ม Apply?", answer: "ปุ่ม Apply ใช้ยืนยันการแก้ไขเพื่อให้เข้าสู่ประวัติ และคุณจะสามารถทำงานต่อจากผลลัพธ์นั้นด้วยเครื่องมืออื่นได้" },
        ],
      },
      {
        title: "โซเชียลมีเดีย",
        entries: [
          { question: "Instagram", answer: "โหมดที่แนะนำ: ครอป 4:5 สำหรับโพสต์ฟีด, 1:1 สำหรับโพสต์สี่เหลี่ยม และ 9:16 สำหรับ Stories และ Reels ควรวางข้อความให้ใหญ่ อยู่กลาง และห่างจากขอบ เริ่มจากการครอปก่อน แล้วค่อยเพิ่มมุมโค้งหรือขอบเมื่อดีไซน์ต้องการ" },
          { question: "Facebook", answer: "โหมดที่แนะนำ: 1:1 หรือ 4:5 สำหรับโพสต์ฟีด, 1.91:1 สำหรับพรีวิวลิงก์ และ 9:16 สำหรับ Stories ควรให้หัวเรื่องหลักยังชัดเจนแม้ในขนาดเล็ก และหลีกเลี่ยงข้อความที่เล็กเกินไป" },
          { question: "X", answer: "โหมดที่แนะนำ: 16:9 สำหรับโพสต์ส่วนใหญ่, 1.91:1 สำหรับภาพลักษณะลิงก์แบบกว้าง และ 3:1 สำหรับหัวข้อแบบแบนเนอร์ ควรรักษาองค์ประกอบให้เรียบง่ายและเน้นจุดสนใจไว้ตรงกลาง" },
          { question: "LinkedIn", answer: "โหมดที่แนะนำ: 1.91:1 สำหรับกราฟิกพรีวิวลิงก์ และ 1:1 สำหรับโพสต์สี่เหลี่ยมที่สะอาดตา ควรให้หัวเรื่องสั้น และภาพรวมดูเป็นมืออาชีพมากกว่าตกแต่ง" },
          { question: "TikTok", answer: "โหมดที่แนะนำ: 9:16 TikTok ออกแบบมาสำหรับคอนเทนต์แนวตั้ง ดังนั้นควรวางสิ่งสำคัญไว้ตรงกลาง เพราะส่วนบนและล่างจะถูก UI ของแอปบัง" },
          { question: "Pinterest", answer: "โหมดที่แนะนำ: 2:3 Pinterest เหมาะกับภาพแนวตั้งมาก และ 2:3 เป็นค่าเริ่มต้นที่ปลอดภัยที่สุด ภาพหลักที่เด่นและหัวข้อที่อ่านง่ายมักได้ผลดีกว่าคอลลาจที่แน่นเกินไป" },
          { question: "YouTube", answer: "โหมดที่แนะนำ: 16:9 ภาพขนาดย่อของ YouTube ควรกว้าง เด่น และใหญ่พอที่จะยังคมชัดในผลการค้นหาและคำแนะนำ" },
          { question: "มีขนาดครอปสำหรับโซเชียลอะไรบ้าง?", answer: "พรีเซ็ตปัจจุบันคือ: Instagram 4:5, 1:1 และ 9:16; Facebook 1:1, 4:5, 1.91:1 และ 9:16; X 16:9, 1.91:1 และ 3:1; LinkedIn 1.91:1 และ 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; และ Weibo 1:1" },
        ],
      },
    ],
  };
  const baseSections = localizedFaqSectionsByLanguage[language.value] ?? [
    { title: localizedFaqPageCopy.value.general, entries: faqSections[0]?.entries ?? [] },
    { title: localizedFaqPageCopy.value.social, entries: faqSections[1]?.entries ?? [] },
  ];
  const help = localizedHelpContent[language.value];
  const generalAdditions: FaqEntry[] = language.value === "en" ? [] : [
    { question: help.questions.historyAndUndo, answer: `${help.sections.undo.join(" ")} ${help.sections.history[0] ?? ""}`.trim() },
    { question: help.questions.historyCounter, answer: help.sections.history[1] ?? "" },
    { question: help.questions.workflow, answer: help.sections.workflows[0] ?? "" },
    { question: help.questions.workflowCropResize, answer: help.sections.workflows[1] ?? "" },
  ];
  const socialAdditions: FaqEntry[] = language.value === "en" ? [] : [
    { question: "Xiaohongshu", answer: help.social.xiaohongshu },
    { question: "WeChat", answer: help.social.wechat },
    { question: "Weibo", answer: help.social.weibo },
  ];
  return baseSections.map((section, index) => {
    if (index === 0) return { ...section, entries: [...section.entries, ...generalAdditions] };
    if (index !== 1) return section;
    const entries = [...section.entries];
    const summaryEntry = entries.pop();
    const localizedSummary = summaryEntry ? { ...summaryEntry, answer: help.social.summary } : null;
    return { ...section, entries: [...entries, ...socialAdditions, ...(localizedSummary ? [localizedSummary] : [])] };
  });
});

const manualFaqByLanguage: Record<
  LanguageCode,
  Array<{ question: string; answer: string }>
> = {
  en: [
    { question: "What is Cut My Pic?", answer: "Originally conceived by Sean Crownover (maker of Jishin.Watch) in 2009, this new 2026 version of Cut My Pic is a private, browser-based image editor for cropping and masking, rounded corners, borders, shadows, resize, filters, adjustments, sharpen and soften, text, stamps, history, fullscreen editing, and export." },
    { question: "Does it upload my images to a server?", answer: "No. Editing runs locally in your browser, so your images stay on your device." },
    { question: "What format does export use?", answer: "Export supports PNG, JPG, and WebP. PNG and WebP can preserve transparency; JPG uses your selected background color." },
    { question: "What new features are supported?", answer: "Recent additions include crop masking, sharpen and soften, text stamp mode, edit history, and fullscreen editing." },
    { question: "How does Edit History work?", answer: "State #0 is the original image and each later state is one applied edit. You can jump to any state, move through redo states, and see how many steps can be reused in a saved workflow." },
    { question: "How do I save and load a workflow?", answer: "Open Edit History, choose Save Workflow, and name the reusable sequence. Use Load Workflow to apply it to another image; crop placement is chosen for the new image and saved resize targets are applied at the end." },
    { question: "Which social cut sizes are available?", answer: "The current crop presets include Instagram Post 4:5, Story/Reel 9:16, TikTok/Douyin 9:16, Pinterest Pin 2:3, YouTube Thumbnail 16:9, Facebook Feed 1:1, Facebook Cover 1.91:1, X Post 16:9, X Header 3:1, LinkedIn Cover 1.91:1, Xiaohongshu Note 3:4, WeChat Cover 2.35:1, and Weibo Feed 1:1." },
  ],
  es: [
    { question: "¿Qué es Cut My Pic?", answer: "Cut My Pic es un editor de imágenes privado que funciona en el navegador para recortar, redondear esquinas, añadir bordes, sombras, filtros, ajustes, texto, redimensionar y exportar." },
    { question: "¿Sube mis imágenes a un servidor?", answer: "No. La edición se realiza de forma local en tu navegador, así que tus imágenes se quedan en tu dispositivo." },
    { question: "¿Qué formato usa la exportación?", answer: "La exportación admite PNG, JPG y WebP. PNG y WebP pueden conservar la transparencia; JPG usa el color de fondo elegido." },
    { question: "¿Qué funciones nuevas tiene?", answer: "Las novedades incluyen máscara de recorte, enfocar y suavizar, modo sello para texto, historial de edición y edición a pantalla completa." },
    { question: "¿Qué tamaños de corte social hay disponibles?", answer: "Los ajustes actuales son: Instagram 4:5, 1:1 y 9:16; Facebook 1:1, 4:5, 1.91:1 y 9:16; X 16:9, 1.91:1 y 3:1; LinkedIn 1.91:1 y 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; y Weibo 1:1." },
  ],
  fr: [
    { question: "Qu’est-ce que Cut My Pic ?", answer: "Cut My Pic est un éditeur d’images privé dans le navigateur pour rogner, arrondir les coins, ajouter des bordures, des ombres, des filtres, des réglages, du texte, redimensionner et exporter." },
    { question: "Mes images sont-elles envoyées sur un serveur ?", answer: "Non. La retouche se fait localement dans votre navigateur, donc vos images restent sur votre appareil." },
    { question: "Quel format utilise l’export ?", answer: "L’export prend en charge PNG, JPG et WebP. PNG et WebP peuvent conserver la transparence ; JPG utilise la couleur d’arrière-plan choisie." },
    { question: "Quelles sont les nouvelles fonctions ?", answer: "Les ajouts récents incluent le masque de recadrage, accentuer et adoucir, le mode tampon pour le texte, l’historique des modifications et l’édition en plein écran." },
    { question: "Quels formats de découpe pour les réseaux sociaux sont disponibles ?", answer: "Les préréglages actuels sont : Instagram 4:5, 1:1 et 9:16 ; Facebook 1:1, 4:5, 1.91:1 et 9:16 ; X 16:9, 1.91:1 et 3:1 ; LinkedIn 1.91:1 et 1:1 ; TikTok 9:16 ; Pinterest 2:3 ; YouTube 16:9 ; Xiaohongshu 3:4 ; WeChat 2.35:1 ; et Weibo 1:1." },
  ],
  de: [
    { question: "Was ist Cut My Pic?", answer: "Cut My Pic ist ein privater Bildeditor im Browser zum Zuschneiden, für runde Ecken, Rahmen, Schatten, Filter, Anpassungen, Text, Größenänderung und Export." },
    { question: "Werden meine Bilder auf einen Server hochgeladen?", answer: "Nein. Die Bearbeitung läuft lokal im Browser, daher bleiben deine Bilder auf deinem Gerät." },
    { question: "Welches Format nutzt der Export?", answer: "Der Export unterstützt PNG, JPG und WebP. PNG und WebP können Transparenz erhalten; JPG verwendet die gewählte Hintergrundfarbe." },
    { question: "Welche neuen Funktionen gibt es?", answer: "Zu den jüngsten Ergänzungen gehören Zuschnitt-Maske, Schärfen und Weichzeichnen, Text-Stempelmodus, Bearbeitungsverlauf und Vollbildbearbeitung." },
    { question: "Welche Social-Media-Zuschnittgrößen sind verfügbar?", answer: "Die aktuellen Vorgaben sind: Instagram 4:5, 1:1 und 9:16; Facebook 1:1, 4:5, 1.91:1 und 9:16; X 16:9, 1.91:1 und 3:1; LinkedIn 1.91:1 und 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; und Weibo 1:1." },
  ],
  ja: [
    { question: "Cut My Pic とは何ですか？", answer: "Cut My Pic は、2009年に Sean Crownover によって考案されました。2026年に新しく生まれ変わったこのバージョンは、トリミング、マスク、角丸、枠線、影、サイズ変更、フィルター、調整、シャープ化とソフト化、テキスト、スタンプ、履歴、フルスクリーン編集、書き出しに対応した、ブラウザ内で動作するプライベートな画像エディタです。" },
    { question: "画像はサーバーにアップロードされますか？", answer: "いいえ。編集はブラウザ内でローカルに行われるため、画像は端末上に残ります。" },
    { question: "書き出しの形式は何ですか？", answer: "PNG、JPG、WebP に対応しています。PNG と WebP は透明部分を保持でき、JPG は選択した背景色を使用します。" },
    { question: "新しく追加された機能は何ですか？", answer: "最近追加された機能には、クロップマスク、シャープ化とソフト化、テキストのスタンプモード、編集履歴、フルスクリーン編集があります。" },
    { question: "利用できるSNS用の切り抜きサイズは何ですか？", answer: "現在のプリセットは、Instagram 4:5 / 1:1 / 9:16、Facebook 1:1 / 4:5 / 1.91:1 / 9:16、X 16:9 / 1.91:1 / 3:1、LinkedIn 1.91:1 / 1:1、TikTok 9:16、Pinterest 2:3、YouTube 16:9、Xiaohongshu 3:4、WeChat 2.35:1、Weibo 1:1 です。" },
  ],
  zh: [
    { question: "Cut My Pic 是什么？", answer: "Cut My Pic 是一款在浏览器中运行的私密图片编辑器，支持裁剪、圆角、边框、阴影、滤镜、调整、文字、调整大小和导出。" },
    { question: "我的图片会上传到服务器吗？", answer: "不会。编辑全部在浏览器本地完成，你的图片会留在设备上。" },
    { question: "导出使用什么格式？", answer: "导出支持 PNG、JPG 和 WebP。PNG 和 WebP 可保留透明度；JPG 使用所选背景色。" },
    { question: "有哪些新功能？", answer: "最近新增了裁剪遮罩、锐化与柔化、文字印章模式、编辑历史和全屏编辑。" },
    { question: "有哪些社交平台裁切尺寸？", answer: "当前预设包括：Instagram 4:5、1:1 和 9:16；Facebook 1:1、4:5、1.91:1 和 9:16；X 16:9、1.91:1 和 3:1；LinkedIn 1.91:1 和 1:1；TikTok 9:16；Pinterest 2:3；YouTube 16:9；小红书 3:4；微信 2.35:1；微博 1:1。" },
  ],
  ko: [
    { question: "Cut My Pic은 무엇인가요?", answer: "Cut My Pic은 브라우저에서 실행되는 개인용 이미지 편집기로, 자르기, 둥근 모서리, 테두리, 그림자, 필터, 조정, 텍스트, 크기 조정, 내보내기를 지원합니다." },
    { question: "이미지가 서버로 업로드되나요?", answer: "아니요. 편집은 브라우저에서 로컬로 이루어지므로 이미지가 기기에 남아 있습니다." },
    { question: "내보내기 형식은 무엇인가요?", answer: "PNG, JPG, WebP를 지원합니다. PNG와 WebP는 투명도를 유지할 수 있고, JPG는 선택한 배경색을 사용합니다." },
    { question: "새로 추가된 기능은 무엇인가요?", answer: "최근 추가된 기능에는 크롭 마스크, 선명하게/부드럽게, 텍스트 스탬프 모드, 편집 기록, 전체 화면 편집이 포함됩니다." },
    { question: "사용 가능한 소셜 미디어 자르기 크기는 무엇인가요?", answer: "현재 프리셋은 Instagram 4:5, 1:1, 9:16; Facebook 1:1, 4:5, 1.91:1, 9:16; X 16:9, 1.91:1, 3:1; LinkedIn 1.91:1, 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; Weibo 1:1 입니다." },
  ],
  hi: [
    { question: "Cut My Pic क्या है?", answer: "Cut My Pic एक निजी ब्राउज़र-आधारित इमेज एडिटर है जो क्रॉप, राउंडेड कॉर्नर, बॉर्डर, शैडो, फ़िल्टर, समायोजन, टेक्स्ट, आकार बदलना और एक्सपोर्ट सपोर्ट करता है।" },
    { question: "क्या यह मेरी इमेज सर्वर पर अपलोड करता है?", answer: "नहीं। एडिटिंग आपके ब्राउज़र में स्थानीय रूप से होती है, इसलिए आपकी इमेज आपके डिवाइस पर रहती है।" },
    { question: "एक्सपोर्ट किस फ़ॉर्मेट में होता है?", answer: "एक्सपोर्ट PNG, JPG और WebP को सपोर्ट करता है। PNG और WebP पारदर्शिता रख सकते हैं; JPG चुने हुए बैकग्राउंड रंग का उपयोग करता है।" },
    { question: "नई सुविधाएँ कौन सी हैं?", answer: "हाल की नई सुविधाओं में crop masking, sharpen और soften, text stamp mode, edit history और fullscreen editing शामिल हैं।" },
    { question: "सोशल मीडिया के लिए कौन-कौन से cut sizes उपलब्ध हैं?", answer: "वर्तमान presets: Instagram 4:5, 1:1, 9:16; Facebook 1:1, 4:5, 1.91:1, 9:16; X 16:9, 1.91:1, 3:1; LinkedIn 1.91:1, 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; और Weibo 1:1।" },
  ],
  pt: [
    { question: "O que é Cut My Pic?", answer: "Cut My Pic é um editor de imagens privado no navegador para cortar, arredondar cantos, adicionar bordas, sombras, filtros, ajustes, texto, redimensionar e exportar." },
    { question: "Minhas imagens são enviadas para um servidor?", answer: "Não. A edição acontece localmente no navegador, então suas imagens ficam no seu dispositivo." },
    { question: "Qual formato a exportação usa?", answer: "A exportação aceita PNG, JPG e WebP. PNG e WebP podem manter a transparência; JPG usa a cor de fundo escolhida." },
    { question: "Quais funções novas estão disponíveis?", answer: "As adições recentes incluem máscara de corte, sharpen e soften, modo stamp para texto, histórico de edição e edição em tela cheia." },
    { question: "Quais tamanhos de corte para redes sociais estão disponíveis?", answer: "Os presets atuais são: Instagram 4:5, 1:1 e 9:16; Facebook 1:1, 4:5, 1.91:1 e 9:16; X 16:9, 1.91:1 e 3:1; LinkedIn 1.91:1 e 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; e Weibo 1:1." },
  ],
  it: [
    { question: "Che cos’è Cut My Pic?", answer: "Cut My Pic è un editor di immagini privato nel browser per ritagliare, arrotondare angoli, aggiungere bordi, ombre, filtri, regolazioni, testo, ridimensionare ed esportare." },
    { question: "Le mie immagini vengono caricate su un server?", answer: "No. La modifica avviene localmente nel browser, quindi le immagini restano sul tuo dispositivo." },
    { question: "Quale formato usa l’esportazione?", answer: "L’esportazione supporta PNG, JPG e WebP. PNG e WebP possono mantenere la trasparenza; JPG usa il colore di sfondo scelto." },
    { question: "Quali nuove funzioni sono disponibili?", answer: "Le aggiunte recenti includono maschera di ritaglio, accentua e sfuma, modalità timbro per il testo, cronologia delle modifiche e modifica a schermo intero." },
    { question: "Quali dimensioni di taglio social sono disponibili?", answer: "I preset attuali sono: Instagram 4:5, 1:1 e 9:16; Facebook 1:1, 4:5, 1.91:1 e 9:16; X 16:9, 1.91:1 e 3:1; LinkedIn 1.91:1 e 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; e Weibo 1:1." },
  ],
  ru: [
    { question: "Что такое Cut My Pic?", answer: "Cut My Pic — это приватный браузерный редактор изображений для кадрирования, скругления углов, рамок, теней, фильтров, коррекции, текста, изменения размера и экспорта." },
    { question: "Мои изображения отправляются на сервер?", answer: "Нет. Редактирование происходит локально в браузере, поэтому изображения остаются на вашем устройстве." },
    { question: "В каком формате выполняется экспорт?", answer: "Экспорт поддерживает PNG, JPG и WebP. PNG и WebP могут сохранять прозрачность; JPG использует выбранный цвет фона." },
    { question: "Какие новые функции появились?", answer: "Среди недавних добавлений есть маска кадрирования, усиление и смягчение резкости, режим штампа для текста, история изменений и полноэкранное редактирование." },
    { question: "Какие размеры обрезки для соцсетей доступны?", answer: "Текущие пресеты: Instagram 4:5, 1:1 и 9:16; Facebook 1:1, 4:5, 1.91:1 и 9:16; X 16:9, 1.91:1 и 3:1; LinkedIn 1.91:1 и 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; и Weibo 1:1." },
  ],
  ar: [
    { question: "ما هو Cut My Pic؟", answer: "Cut My Pic هو محرر صور خاص يعمل داخل المتصفح للقص وتدوير الزوايا وإضافة الإطارات والظلال والفلاتر والتعديلات والنص وتغيير الحجم والتصدير." },
    { question: "هل يتم رفع صوري إلى خادم؟", answer: "لا. تتم عملية التحرير محليًا داخل المتصفح، لذلك تبقى صورك على جهازك." },
    { question: "ما صيغة التصدير؟", answer: "يدعم التصدير PNG وJPG وWebP. يمكن لـ PNG وWebP الحفاظ على الشفافية، بينما يستخدم JPG لون الخلفية المختار." },
    { question: "ما هي الميزات الجديدة؟", answer: "تشمل الإضافات الحديثة قناع القص، والتوضيح والتنعيم، ووضع ختم النص، وسجل التعديلات، والتحرير بملء الشاشة." },
    { question: "ما مقاسات القص المتاحة لوسائل التواصل؟", answer: "الإعدادات الحالية هي: Instagram ‏4:5 و1:1 و9:16؛ Facebook ‏1:1 و4:5 و1.91:1 و9:16؛ X ‏16:9 و1.91:1 و3:1؛ LinkedIn ‏1.91:1 و1:1؛ TikTok ‏9:16؛ Pinterest ‏2:3؛ YouTube ‏16:9؛ Xiaohongshu ‏3:4؛ WeChat ‏2.35:1؛ وWeibo ‏1:1." },
  ],
  nl: [
    { question: "Wat is Cut My Pic?", answer: "Cut My Pic is een privé afbeeldingseditor in de browser voor bijsnijden, afgeronde hoeken, randen, schaduwen, filters, aanpassingen, tekst, formaat wijzigen en exporteren." },
    { question: "Worden mijn afbeeldingen naar een server geüpload?", answer: "Nee. Bewerken gebeurt lokaal in je browser, dus je afbeeldingen blijven op je apparaat." },
    { question: "Welk formaat gebruikt export?", answer: "Export ondersteunt PNG, JPG en WebP. PNG en WebP kunnen transparantie behouden; JPG gebruikt de gekozen achtergrondkleur." },
    { question: "Welke nieuwe functies zijn er?", answer: "Recente toevoegingen zijn cropmasker, verscherpen en verzachten, tekst-stempelmodus, geschiedenis en fullscreen bewerken." },
    { question: "Welke sociale uitsnijformaten zijn beschikbaar?", answer: "De huidige presets zijn: Instagram 4:5, 1:1 en 9:16; Facebook 1:1, 4:5, 1.91:1 en 9:16; X 16:9, 1.91:1 en 3:1; LinkedIn 1.91:1 en 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; en Weibo 1:1." },
  ],
  pl: [
    { question: "Czym jest Cut My Pic?", answer: "Cut My Pic to prywatny edytor obrazów w przeglądarce do kadrowania, zaokrąglania rogów, dodawania ramek, cieni, filtrów, regulacji, tekstu, zmiany rozmiaru i eksportu." },
    { question: "Czy moje obrazy są wysyłane na serwer?", answer: "Nie. Edycja odbywa się lokalnie w przeglądarce, więc obrazy zostają na Twoim urządzeniu." },
    { question: "W jakim formacie działa eksport?", answer: "Eksport obsługuje PNG, JPG i WebP. PNG i WebP mogą zachować przezroczystość; JPG używa wybranego koloru tła." },
    { question: "Jakie nowe funkcje są dostępne?", answer: "Najnowsze dodatki obejmują maskę kadrowania, wyostrzanie i zmiękczanie, tryb stempla tekstowego, historię edycji i edycję pełnoekranową." },
    { question: "Jakie rozmiary cięcia dla mediów społecznościowych są dostępne?", answer: "Obecne presety to: Instagram 4:5, 1:1 i 9:16; Facebook 1:1, 4:5, 1.91:1 i 9:16; X 16:9, 1.91:1 i 3:1; LinkedIn 1.91:1 i 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; i Weibo 1:1." },
  ],
  tr: [
    { question: "Cut My Pic nedir?", answer: "Cut My Pic, kırpma, yuvarlatılmış köşeler, çerçeveler, gölgeler, filtreler, ayarlamalar, metin, yeniden boyutlandırma ve dışa aktarma için özel bir tarayıcı içi görsel düzenleyicidir." },
    { question: "Görsellerim bir sunucuya yükleniyor mu?", answer: "Hayır. Düzenleme tarayıcınızda yerel olarak yapılır, bu yüzden görseller cihazınızda kalır." },
    { question: "Dışa aktarma hangi formatta?", answer: "Dışa aktarma PNG, JPG ve WebP’yi destekler. PNG ve WebP saydamlığı koruyabilir; JPG seçilen arka plan rengini kullanır." },
    { question: "Yeni hangi özellikler var?", answer: "Son eklenenler arasında kırpma maskesi, keskinleştirme ve yumuşatma, metin damga modu, geçmiş ve tam ekran düzenleme yer alıyor." },
    { question: "Hangi sosyal medya kırpma boyutları mevcut?", answer: "Mevcut ön ayarlar: Instagram 4:5, 1:1 ve 9:16; Facebook 1:1, 4:5, 1.91:1 ve 9:16; X 16:9, 1.91:1 ve 3:1; LinkedIn 1.91:1 ve 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; ve Weibo 1:1." },
  ],
  id: [
    { question: "Apa itu Cut My Pic?", answer: "Cut My Pic adalah editor gambar pribadi di browser untuk memotong, sudut membulat, bingkai, bayangan, filter, penyesuaian, teks, ubah ukuran, dan ekspor." },
    { question: "Apakah gambar saya diunggah ke server?", answer: "Tidak. Pengeditan dilakukan secara lokal di browser Anda, jadi gambar tetap di perangkat Anda." },
    { question: "Format ekspornya apa?", answer: "Ekspor mendukung PNG, JPG, dan WebP. PNG dan WebP dapat mempertahankan transparansi; JPG memakai warna latar yang dipilih." },
    { question: "Fitur baru apa saja yang tersedia?", answer: "Tambahan terbaru mencakup crop masking, sharpen dan soften, mode stempel teks, riwayat edit, dan edit layar penuh." },
    { question: "Ukuran potong sosial apa saja yang tersedia?", answer: "Preset saat ini: Instagram 4:5, 1:1, dan 9:16; Facebook 1:1, 4:5, 1.91:1, dan 9:16; X 16:9, 1.91:1, dan 3:1; LinkedIn 1.91:1 dan 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; dan Weibo 1:1." },
  ],
  ms: [
    { question: "Apakah Cut My Pic?", answer: "Cut My Pic ialah penyunting imej peribadi dalam pelayar untuk memangkas, membulatkan sudut, menambah bingkai, bayang, penapis, pelarasan dan teks, mengubah saiz serta mengeksport." },
    { question: "Adakah imej saya dimuat naik ke pelayan?", answer: "Tidak. Penyuntingan dilakukan secara setempat dalam pelayar anda, jadi imej kekal pada peranti anda." },
    { question: "Apakah format eksport?", answer: "Eksport menyokong PNG, JPG dan WebP. PNG dan WebP boleh mengekalkan kelutsinaran; JPG menggunakan warna latar yang dipilih." },
    { question: "Apakah ciri baharu yang tersedia?", answer: "Penambahan terkini termasuk topeng pangkasan, tajam dan lembut, mod cap teks, sejarah suntingan dan penyuntingan skrin penuh." },
    { question: "Apakah saiz pangkasan media sosial yang tersedia?", answer: "Pratetap semasa termasuk Instagram 4:5, 1:1 dan 9:16; Facebook 1:1, 4:5, 1.91:1 dan 9:16; X 16:9, 1.91:1 dan 3:1; LinkedIn 1.91:1 dan 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; dan Weibo 1:1." },
  ],
  vi: [
    { question: "Cut My Pic là gì?", answer: "Cut My Pic là trình chỉnh sửa ảnh riêng tư chạy trong trình duyệt để cắt, bo góc, thêm viền, đổ bóng, bộ lọc, chỉnh sửa, văn bản, đổi kích thước và xuất ảnh." },
    { question: "Ảnh của tôi có được tải lên máy chủ không?", answer: "Không. Việc chỉnh sửa diễn ra cục bộ trong trình duyệt, nên ảnh vẫn ở trên thiết bị của bạn." },
    { question: "Xuất ảnh dùng định dạng nào?", answer: "Xuất ảnh hỗ trợ PNG, JPG và WebP. PNG và WebP có thể giữ độ trong suốt; JPG dùng màu nền đã chọn." },
    { question: "Có những tính năng mới nào?", answer: "Các bổ sung gần đây gồm mặt nạ cắt, làm sắc nét và làm mềm, chế độ tem văn bản, lịch sử chỉnh sửa và chỉnh sửa toàn màn hình." },
    { question: "Có những kích thước cắt cho mạng xã hội nào?", answer: "Các preset hiện tại gồm: Instagram 4:5, 1:1 và 9:16; Facebook 1:1, 4:5, 1.91:1 và 9:16; X 16:9, 1.91:1 và 3:1; LinkedIn 1.91:1 và 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; và Weibo 1:1." },
  ],
  th: [
    { question: "Cut My Pic คืออะไร?", answer: "Cut My Pic คือโปรแกรมแต่งภาพแบบส่วนตัวในเบราว์เซอร์สำหรับครอป มุมโค้ง เส้นขอบ เงา ฟิลเตอร์ การปรับแต่ง ข้อความ การปรับขนาด และการส่งออก" },
    { question: "รูปภาพของฉันถูกอัปโหลดไปยังเซิร์ฟเวอร์หรือไม่?", answer: "ไม่ การแก้ไขเกิดขึ้นในเบราว์เซอร์แบบโลคัล ดังนั้นรูปภาพจะอยู่บนอุปกรณ์ของคุณ" },
    { question: "การส่งออกใช้ไฟล์รูปแบบใด?", answer: "การส่งออกรองรับ PNG, JPG และ WebP โดย PNG และ WebP สามารถคงความโปร่งใสได้ ส่วน JPG ใช้สีพื้นหลังที่เลือก" },
    { question: "มีฟีเจอร์ใหม่อะไรบ้าง?", answer: "ฟีเจอร์ใหม่ล่าสุดได้แก่ crop masking, sharpen และ soften, โหมด stamp สำหรับข้อความ, ประวัติการแก้ไข และการแก้ไขแบบเต็มหน้าจอ" },
    { question: "มีขนาดครอปสำหรับโซเชียลอะไรบ้าง?", answer: "พรีเซ็ตปัจจุบันคือ: Instagram 4:5, 1:1 และ 9:16; Facebook 1:1, 4:5, 1.91:1 และ 9:16; X 16:9, 1.91:1 และ 3:1; LinkedIn 1.91:1 และ 1:1; TikTok 9:16; Pinterest 2:3; YouTube 16:9; Xiaohongshu 3:4; WeChat 2.35:1; และ Weibo 1:1" },
  ],
};

function getManualFaqEntries(lang: LanguageCode) {
  const entries = manualFaqByLanguage[lang] ?? manualFaqByLanguage.en;
  if (lang === "en") return entries;
  const help = localizedHelpContent[lang];
  return [
    ...entries,
    { question: help.questions.historyAndUndo, answer: `${help.sections.undo.join(" ")} ${help.sections.history.join(" ")}` },
    { question: help.questions.workflow, answer: help.sections.workflows.join(" ") },
  ];
}

function getManualCopy(lang: LanguageCode) {
  return manualPageCopy[lang] ?? manualPageCopy.en;
}

function getManualContent(lang: LanguageCode) {
  const baseContent = (manualContentByLanguage[lang] ?? manualContentByLanguage.en ?? []) as ManualContentSection[];
  const controlCopy = workspaceManualControlCopy[lang];
  const localizedAdditions = lang === "en"
    ? []
    : [
        ...helpSectionOrder
          .filter((id) => id !== "center")
          .map((id) => ({ id, title: localizedHelpContent[lang].titles[id], body: localizedHelpContent[lang].sections[id] })),
        { id: "center", title: controlCopy.center.title, body: controlCopy.center.body },
        { id: "manual-move", title: controlCopy.move.title, body: controlCopy.move.body },
        { id: "delete-image", title: controlCopy.deleteImage.title, body: controlCopy.deleteImage.body },
      ];
  const content = [...baseContent, ...localizedAdditions];
  const order = Object.fromEntries(manualSections.map((section, index) => [section.id, index])) as Record<string, number>;
  return [...content].sort((left, right) => (order[left.id] ?? Number.MAX_SAFE_INTEGER) - (order[right.id] ?? Number.MAX_SAFE_INTEGER));
}

function getManualSectionTitle(section: ManualContentSection) {
  if (section.title) return section.title;
  return section.titleKey ? t(section.titleKey) : section.id;
}
const mobileToolMenuOpen = ref(false);
const mobileQuickActionsOpen = ref(false);
const mobileZoomOpen = ref(false);
const zoomTooltipVisible = ref(false);
const mobileViewBackgroundOpen = ref(false);
const mobileBackMode = ref(false);
const isMobileViewport = ref(false);

function toggleTopSettings() {
  const nextOpen = !showTopSettings.value;
  showTopSettings.value = nextOpen;
  if (nextOpen && isMobileViewport.value) {
    closeHistoryPanel();
    activePanelTool.value = null;
    mobileToolMenuOpen.value = false;
    mobileQuickActionsOpen.value = false;
    mobileZoomOpen.value = false;
    mobileViewBackgroundOpen.value = false;
  }
  if (!showTopSettings.value) showLanguageMenu.value = false;
}

function closeHelpOverlays() {
  shortcutHelpOpen.value = false;
}

function openShortcutHelp() {
  showTopSettings.value = false;
  showLanguageMenu.value = false;
  closeHelpOverlays();
  if (isManualPage.value || isFaqPage.value) {
    openEditorPage();
  }
  shortcutHelpOpen.value = true;
}

function scrollToManualSection(sectionId: string) {
  manualJumpSelection.value = sectionId;
  const element = document.getElementById(sectionId);
  if (!element) return;
  element.scrollIntoView({ behavior: "smooth", block: "start" });
}

function localizeManualText(text: string, lang: LanguageCode = language.value) {
  const replacements = manualTermTranslationsByLanguage[lang] ?? {};
  const escaped = text
    .split("&").join("&amp;")
    .split("<").join("&lt;")
    .split(">").join("&gt;");
  const terms = Object.keys(replacements).sort((a, b) => b.length - a.length);
  return terms.reduce((result, term) => {
    const replacement = replacements[term];
    const pattern = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g");
    return result.replace(pattern, replacement);
  }, escaped);
}

function formatManualParagraph(text: string, lang: LanguageCode = language.value) {
  const escapedText = localizeManualText(text, lang);
  const highlightTerms = manualHighlightTermsByLanguage[lang] ?? manualHighlightTermsByLanguage.en ?? [];
  const sortedTerms = [...highlightTerms].sort((a, b) => b.length - a.length);
  const escapedTerms = sortedTerms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const pattern = new RegExp(`(${escapedTerms.join("|")})`, "g");
  return escapedText.replace(pattern, '<span class="manual-highlight">$1</span>');
}

function openManualPage() {
  showTopSettings.value = false;
  showLanguageMenu.value = false;
  closeHelpOverlays();
  triggerDropFlash();
  const nextPath = getLocalizedManualUrl(language.value);
  window.history.pushState({ cutmypicRoute: "manual" }, "", nextPath);
  routePath.value = window.location.pathname;
}

async function openManualSection(sectionId: string) {
  showTopSettings.value = false;
  showLanguageMenu.value = false;
  closeHelpOverlays();
  const nextPath = `${getLocalizedManualUrl(language.value)}#${sectionId}`;
  window.history.pushState({ cutmypicRoute: "manual", sectionId }, "", nextPath);
  routePath.value = window.location.pathname;
  await nextTick();
  manualJumpSelection.value = sectionId;
  document.getElementById(sectionId)?.scrollIntoView({ behavior: "auto", block: "start" });
}

function openGuidePage() {
  showTopSettings.value = false;
  showLanguageMenu.value = false;
  closeHelpOverlays();
  window.history.pushState({ cutmypicRoute: "guide" }, "", getLocalizedGuideUrl(language.value));
  routePath.value = window.location.pathname;
}

function openFaqPage() {
  showTopSettings.value = false;
  showLanguageMenu.value = false;
  closeHelpOverlays();
  triggerDropFlash();
  const nextPath = getLocalizedFaqUrl(language.value);
  window.history.pushState({ cutmypicRoute: "faq" }, "", nextPath);
  routePath.value = window.location.pathname;
}

function openEditorPage() {
  const nextPath = getLocalizedUrl(language.value);
  window.history.replaceState({}, "", nextPath);
  routePath.value = window.location.pathname;
}

function trackPageView() {
  const analytics = window as Window & {
    gtag?: (...args: unknown[]) => void;
  };
  analytics.gtag?.("event", "page_view", {
    page_location: window.location.href,
    page_path: window.location.pathname + window.location.search + window.location.hash,
    page_title: document.title,
  });
}

function toggleMobileToolMenu() {
  mobileToolMenuOpen.value = !mobileToolMenuOpen.value;
  if (mobileToolMenuOpen.value) {
    activePanelTool.value = null;
    mobileViewBackgroundOpen.value = false;
  }
}

function toggleMobileQuickActions() {
  mobileQuickActionsOpen.value = !mobileQuickActionsOpen.value;
  if (mobileQuickActionsOpen.value) {
    mobileToolMenuOpen.value = false;
    activePanelTool.value = null;
    mobileViewBackgroundOpen.value = false;
  }
}

function chooseMobileQuickAction(action: "modes" | "zoom" | "download" | "background") {
  mobileQuickActionsOpen.value = false;
  if (action !== "zoom") mobileZoomOpen.value = false;
  if (action === "modes") {
    toggleMobileToolMenu();
  } else if (action === "zoom") {
    mobileZoomOpen.value = !mobileZoomOpen.value;
  } else if (action === "background") {
    mobileToolMenuOpen.value = false;
    activePanelTool.value = null;
    mobileViewBackgroundOpen.value = true;
  } else if (action === "download") {
    void downloadExportedImage();
  }
}

function updateViewportFlags() {
  isMobileViewport.value = window.innerWidth <= 767;
  if (!isMobileViewport.value) mobileViewBackgroundOpen.value = false;
}

function syncSeoLinks() {
  const canonicalUrl = getCurrentLocalizedPageUrl(language.value);

  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", canonicalUrl);

  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((node) => node.remove());
  const supportedLanguages = Object.keys(translations) as LanguageCode[];
  for (const code of supportedLanguages) {
    const alternate = document.createElement("link");
    alternate.setAttribute("rel", "alternate");
    alternate.setAttribute("hreflang", code === "zh" ? "zh-Hans" : code);
    alternate.setAttribute(
      "href",
      getCurrentLocalizedPageUrl(code),
    );
    document.head.appendChild(alternate);
  }
  const xDefault = document.createElement("link");
  xDefault.setAttribute("rel", "alternate");
  xDefault.setAttribute("hreflang", "x-default");
  xDefault.setAttribute("href", getCurrentLocalizedPageUrl("en"));
  document.head.appendChild(xDefault);

  let robots = document.head.querySelector('meta[name="robots"]');
  if (!robots) {
    robots = document.createElement("meta");
    robots.setAttribute("name", "robots");
    document.head.appendChild(robots);
  }
  robots.setAttribute("content", "index,follow");
}

function setSeoMetaContent(attribute: "name" | "property", key: string, content: string) {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attribute, key);
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
}

function buildSeoEntityGraph(title: string, description: string, canonicalUrl: string) {
  const languageTag = language.value === "zh" ? "zh-Hans" : language.value;
  const homeUrl = getLocalizedUrl(language.value);
  const manualUrl = getLocalizedManualUrl(language.value);
  const websiteId = `${siteBaseUrl}/#website`;
  const organizationId = `${siteBaseUrl}/#organization`;
  const applicationId = `${siteBaseUrl}/#software-application`;
  const webpageId = `${canonicalUrl}#webpage`;
  const breadcrumbId = `${canonicalUrl}#breadcrumb`;
  const pageType = isManualPage.value ? "manual" : isGuidePage.value ? "guide" : isFaqPage.value ? "faq" : "editor";
  const graph: Array<Record<string, unknown>> = [
    {
      "@type": "Organization",
      "@id": organizationId,
      name: "Cut My Pic",
      url: `${siteBaseUrl}/`,
      logo: {
        "@type": "ImageObject",
        url: `${siteBaseUrl}/icon-512.png`,
        width: 512,
        height: 512,
      },
    },
    {
      "@type": "WebSite",
      "@id": websiteId,
      url: `${siteBaseUrl}/`,
      name: "Cut My Pic",
      publisher: { "@id": organizationId },
      inLanguage: (Object.keys(translations) as LanguageCode[]).map((code) => code === "zh" ? "zh-Hans" : code),
    },
    {
      "@type": "SoftwareApplication",
      "@id": applicationId,
      name: "Cut My Pic",
      applicationCategory: "MultimediaApplication",
      applicationSubCategory: "Online Image Editor",
      operatingSystem: "Web Browser",
      browserRequirements: "JavaScript enabled",
      url: homeUrl,
      description,
      inLanguage: languageTag,
      isAccessibleForFree: true,
      publisher: { "@id": organizationId },
      isPartOf: { "@id": websiteId },
      keywords: "online image editor, crop photo, resize image, add border, photo filters, PNG JPG WebP export, private photo editing",
      screenshot: `${siteBaseUrl}/social-preview.png`,
      softwareHelp: { "@id": `${manualUrl}#webpage` },
      featureList: [
        "Crop photos and cut images into custom shapes",
        "Mask images with circle, heart, and star selections",
        "Resize, rotate, and flip images in the browser",
        "Add adjustable borders and drop shadows",
        "Apply photo filters and color adjustments",
        "Add positioned text and decorative stamps",
        "Save edit history and reusable local workflows",
        "Export images as PNG, JPG, or WebP files with format-specific controls",
      ],
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    },
    {
      "@type": "WebPage",
      "@id": webpageId,
      url: canonicalUrl,
      name: title,
      description,
      inLanguage: languageTag,
      isPartOf: { "@id": websiteId },
      about: { "@id": applicationId },
      ...(pageType === "editor" ? { mainEntity: { "@id": applicationId } } : { breadcrumb: { "@id": breadcrumbId } }),
    },
  ];
  if (pageType !== "editor") {
    graph.push({
      "@type": "BreadcrumbList",
      "@id": breadcrumbId,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Cut My Pic", item: homeUrl },
        { "@type": "ListItem", position: 2, name: title, item: canonicalUrl },
      ],
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

function updateSeoMetadata() {
  const manualCopy = getManualCopy(language.value);
  const seoCopy = localizedSeoCopy[language.value];
  const title = isManualPage.value ? manualCopy.title : isGuidePage.value ? howItWorksLabelByLanguage[language.value] : isFaqPage.value ? localizedFaqPageCopy.value.title : seoCopy.editorTitle;
  const description = isManualPage.value ? seoCopy.manualDescription : isGuidePage.value ? howItWorksIntroByLanguage[language.value] : isFaqPage.value ? seoCopy.faqDescription : seoCopy.editorDescription;
  document.title = title;
  let meta = document.head.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "description");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", description);
  let ogTitle = document.head.querySelector('meta[property="og:title"]');
  if (!ogTitle) {
    ogTitle = document.createElement("meta");
    ogTitle.setAttribute("property", "og:title");
    document.head.appendChild(ogTitle);
  }
  ogTitle.setAttribute("content", title);
  let ogDescription = document.head.querySelector('meta[property="og:description"]');
  if (!ogDescription) {
    ogDescription = document.createElement("meta");
    ogDescription.setAttribute("property", "og:description");
    document.head.appendChild(ogDescription);
  }
  ogDescription.setAttribute("content", description);
  setSeoMetaContent("property", "og:locale", seoCopy.openGraphLocale);

  let ogType = document.head.querySelector('meta[property="og:type"]');
  if (!ogType) {
    ogType = document.createElement("meta");
    ogType.setAttribute("property", "og:type");
    document.head.appendChild(ogType);
  }
  ogType.setAttribute("content", "website");

  let ogUrl = document.head.querySelector('meta[property="og:url"]');
  if (!ogUrl) {
    ogUrl = document.createElement("meta");
    ogUrl.setAttribute("property", "og:url");
    document.head.appendChild(ogUrl);
  }
  ogUrl.setAttribute("content", getCurrentLocalizedPageUrl(language.value));

  let ogImage = document.head.querySelector('meta[property="og:image"]');
  if (!ogImage) {
    ogImage = document.createElement("meta");
    ogImage.setAttribute("property", "og:image");
    document.head.appendChild(ogImage);
  }
  ogImage.setAttribute("content", `${siteBaseUrl}/social-preview.png`);
  setSeoMetaContent("property", "og:image:alt", seoCopy.imageAlt);
  setSeoMetaContent("property", "og:image:width", "1200");
  setSeoMetaContent("property", "og:image:height", "630");

  let ogSiteName = document.head.querySelector('meta[property="og:site_name"]');
  if (!ogSiteName) {
    ogSiteName = document.createElement("meta");
    ogSiteName.setAttribute("property", "og:site_name");
    document.head.appendChild(ogSiteName);
  }
  ogSiteName.setAttribute("content", "Cut My Pic");

  let twitterCard = document.head.querySelector('meta[name="twitter:card"]');
  if (!twitterCard) {
    twitterCard = document.createElement("meta");
    twitterCard.setAttribute("name", "twitter:card");
    document.head.appendChild(twitterCard);
  }
  twitterCard.setAttribute("content", "summary_large_image");

  let twitterTitle = document.head.querySelector('meta[name="twitter:title"]');
  if (!twitterTitle) {
    twitterTitle = document.createElement("meta");
    twitterTitle.setAttribute("name", "twitter:title");
    document.head.appendChild(twitterTitle);
  }
  twitterTitle.setAttribute("content", title);

  let twitterDescription = document.head.querySelector('meta[name="twitter:description"]');
  if (!twitterDescription) {
    twitterDescription = document.createElement("meta");
    twitterDescription.setAttribute("name", "twitter:description");
    document.head.appendChild(twitterDescription);
  }
  twitterDescription.setAttribute("content", description);

  let twitterImage = document.head.querySelector('meta[name="twitter:image"]');
  if (!twitterImage) {
    twitterImage = document.createElement("meta");
    twitterImage.setAttribute("name", "twitter:image");
    document.head.appendChild(twitterImage);
  }
  twitterImage.setAttribute("content", `${siteBaseUrl}/social-preview.png`);
  setSeoMetaContent("name", "twitter:image:alt", seoCopy.imageAlt);

  let structuredData = document.head.querySelector('script[type="application/ld+json"][data-cutmypic="true"]');
  if (!structuredData) {
    structuredData = document.createElement("script");
    structuredData.setAttribute("type", "application/ld+json");
    structuredData.setAttribute("data-cutmypic", "true");
    document.head.appendChild(structuredData);
  }
  const canonicalUrl = getCurrentLocalizedPageUrl(language.value);
  structuredData.textContent = JSON.stringify(buildSeoEntityGraph(title, description, canonicalUrl));

  document.head.querySelectorAll('script[type="application/ld+json"][id="cutmypic-manual-faq-schema"]').forEach((node) => node.remove());
  if (isManualPage.value || isFaqPage.value) {
    const faqScriptId = "cutmypic-page-faq-schema";
    let faqStructuredData = document.head.querySelector<HTMLScriptElement>(`script[type="application/ld+json"][id="${faqScriptId}"]`);
    if (!faqStructuredData) {
      faqStructuredData = document.createElement("script");
      faqStructuredData.setAttribute("type", "application/ld+json");
      faqStructuredData.setAttribute("id", faqScriptId);
      document.head.appendChild(faqStructuredData);
    }
    const faqs = isManualPage.value
      ? getManualFaqEntries(language.value)
      : localizedFaqSectionsWithEntries.value.flatMap((section) => section.entries);
    faqStructuredData.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${canonicalUrl}#faq`,
      url: canonicalUrl,
      name: title,
      inLanguage: language.value === "zh" ? "zh-Hans" : language.value,
      isPartOf: { "@id": `${siteBaseUrl}/#website` },
      mainEntity: faqs.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    });
  } else {
    document.head.querySelectorAll('script[type="application/ld+json"][id="cutmypic-page-faq-schema"]').forEach((node) => node.remove());
  }
}


function lightenHexColor(hex: string, amount = 0.28) {
  const normalized = hex.replace("#", "");
  if (normalized.length !== 6) return hex;
  const value = Number.parseInt(normalized, 16);
  if (Number.isNaN(value)) return hex;
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  const lighten = (channel: number) => Math.round(channel + (255 - channel) * amount);
  return `#${[lighten(r), lighten(g), lighten(b)].map((part) => part.toString(16).padStart(2, "0")).join("")}`;
}

function syncBorderGradientDefaults() {
  settings.borderGradientStart = settings.borderColor;
  settings.borderGradientEnd = lightenHexColor(settings.borderColor);
}

function swapBorderGradientColors() {
  const startColor = settings.borderGradientStart;
  settings.borderGradientStart = settings.borderGradientEnd;
  settings.borderGradientEnd = startColor;
}

function setCornerRadiusPreset(value: number) {
  if (!hasImage.value) return;
  settings.radius = clamp(value, 0, maxRadius.value || value);
}

function setDefaultCropGeometry() {
  const side = Math.min(imageSize.width, imageSize.height);
  const size = Math.max(1, Math.round(side * 0.75));
  crop.x = Math.round((imageSize.width - size) / 2);
  crop.y = Math.round((imageSize.height - size) / 2);
  crop.width = size;
  crop.height = size;
}

function fitCropRectToImage(rect: Crop, lockedAspect: number | null = null): Crop {
  const minSize = Math.min(10, imageSize.width, imageSize.height);
  let x = Math.round(rect.x);
  let y = Math.round(rect.y);
  let width = Math.max(minSize, Math.round(rect.width));
  let height = Math.max(minSize, Math.round(rect.height));

  if (lockedAspect) {
    const availableWidth = Math.max(minSize, imageSize.width - x);
    const availableHeight = Math.max(minSize, imageSize.height - y);
    width = Math.min(width, availableWidth);
    height = Math.min(height, availableHeight);

    if (width / lockedAspect > availableHeight) {
      height = availableHeight;
      width = Math.max(minSize, Math.round(height * lockedAspect));
    } else {
      width = Math.max(minSize, Math.min(width, availableWidth));
      height = Math.max(minSize, Math.round(width / lockedAspect));
      if (height > availableHeight) {
        height = availableHeight;
        width = Math.max(minSize, Math.round(height * lockedAspect));
      }
    }

    if (width > imageSize.width) width = imageSize.width;
    if (height > imageSize.height) height = imageSize.height;

    x = clamp(x, 0, Math.max(0, imageSize.width - width));
    y = clamp(y, 0, Math.max(0, imageSize.height - height));
  } else {
    x = clamp(x, 0, Math.max(0, imageSize.width - width));
    y = clamp(y, 0, Math.max(0, imageSize.height - height));
    width = Math.min(width, imageSize.width - x);
    height = Math.min(height, imageSize.height - y);
  }

  return {
    x,
    y,
    width: Math.max(minSize, width),
    height: Math.max(minSize, height),
  };
}

function resetToolStateForNewImage(width: number, height: number, options?: { preserveZoom?: boolean; preserveText?: boolean }) {
  const keepZoom = options?.preserveZoom === true ? settings.zoom : 1;
  committedCrop.x = 0;
  committedCrop.y = 0;
  committedCrop.width = width;
  committedCrop.height = height;
  crop.x = 0;
  crop.y = 0;
  crop.width = width;
  crop.height = height;
  cropMode.value = "free";
  committedImageShape.value = "rect";
  cropFunctionMode.value = "trimmer";
  selectedCropPresetId.value = "common-free";
  freeCropAspectLocked.value = false;
  freeCropLockedAspect.value = width / Math.max(1, height);
  cropCircleAspectLocked.value = true;
  maskRoundedAspectLocked.value = true;

  settings.radius = 0;
  settings.zoom = keepZoom;
  settings.filter = "none";
  settings.colorReplaceTarget = "#ffffff";
  settings.colorReplaceReplacement = "#000000";
  settings.colorReplaceStrength = 20;
  settings.colorReplaceSmoothness = 20;
  settings.colorReplaceTargetTransparent = false;
  settings.colorReplaceReplacementTransparent = false;
  settings.contrast = 100;
  settings.brightness = 100;
  settings.saturation = 100;
  settings.colorBalance = 0;
  settings.sharpen = 0;
  settings.soften = 0;
  settings.transparency = 0;
  settings.transparencyIncludeBorder = false;
  settings.borderThickness = 0;
  settings.borderColor = "#64b5f6";
  settings.borderUseGradient = false;
  settings.borderGradientType = "linear";
  settings.borderGradientAngle = 270;
  settings.borderGradientStart = "#64b5f6";
  settings.borderGradientEnd = "#8bc9fb";
  cropMaskShape.value = "free";

  resizeBox.x = 0;
  resizeBox.y = 0;
  resizeBox.width = width;
  resizeBox.height = height;
  committedResizeBox.x = 0;
  committedResizeBox.y = 0;
  committedResizeBox.width = width;
  committedResizeBox.height = height;
  resizePanelMode.value = "resize";
  resizeMode.value = "constrain";
  resizeConstrainedAspect.value = width / Math.max(1, height);
  resizeRotateDegrees.value = 0;
  resizeFlipHorizontal.value = false;
  resizeFlipVertical.value = false;
  committedResizeRotateDegrees.value = 0;
  committedResizeFlipHorizontal.value = false;
  committedResizeFlipVertical.value = false;
  committedCornerRadius.value = 0;
  committedCornerRadiusX.value = 0;
  committedCornerRadiusY.value = 0;
  committedFilter.value = "none";
  Object.assign(committedColorReplace, {
    targetColor: "#ffffff",
    replacementColor: "#000000",
    strength: 20,
    smoothness: 20,
    targetTransparent: false,
    replacementTransparent: false,
  });
  committedAdjustments.contrast = 100;
  committedAdjustments.brightness = 100;
  committedAdjustments.saturation = 100;
  committedAdjustments.colorBalance = 0;
  committedAdjustments.sharpen = 0;
  committedAdjustments.soften = 0;
  committedAdjustments.transparency = 0;
  committedAdjustments.transparencyIncludeBorder = false;
  committedBorderState.borderThickness = 0;
  committedBorderState.borderColor = "#64b5f6";
  committedBorderState.borderUseGradient = false;
  committedBorderState.borderGradientType = "linear";
  committedBorderState.borderGradientAngle = 270;
  committedBorderState.borderGradientStart = "#64b5f6";
  committedBorderState.borderGradientEnd = "#8bc9fb";
  committedBorderLayers.splice(0, committedBorderLayers.length);
  committedShadowState.shadowDarkness = 0;
  committedShadowState.shadowSize = 0;
  committedShadowState.shadowColor = "#000000";
  committedShadowState.shadowDirection = 0;
  if (!options?.preserveText) {
    textState.content = committedText.content;
    textState.color = committedText.color;
    textState.size = committedText.size;
    textState.rotation = committedText.rotation;
    textState.x = committedText.x;
    textState.y = committedText.y;
    textState.shadowSize = committedText.shadowSize;
    textState.shadowDarkness = committedText.shadowDarkness;
    textState.shadowColor = committedText.shadowColor;
  }
  settings.shadowDarkness = 0;
  settings.shadowSize = 0;
  settings.shadowColor = "#000000";
  settings.shadowDirection = 0;
  cropFunctionMode.value = "trimmer";
  cropMaskMode.value = "blur";
  cropMaskColor.value = "#64b5f6";
  committedCropMaskBlurIntensity.value = cropMaskBlurIntensity.value;
  committedCropMaskTransparency.value = cropMaskTransparency.value;
  committedCropMaskColor.value = cropMaskColor.value;
}

const language = ref<LanguageCode>(detectLanguage());
const languageSelection = ref<LanguageCode | "auto">(
  detectPathState().isManual ? "auto" : ((readLocalStorage("cutmypic-language") as LanguageCode | null) ?? "auto"),
);
const languageOptions: Array<{ code: LanguageCode | "auto"; label: string }> = [
  { code: "auto", label: "Auto" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "ja", label: "日本語" },
  { code: "zh", label: "中文" },
  { code: "ko", label: "한국어" },
  { code: "hi", label: "हिन्दी" },
  { code: "pt", label: "Português" },
  { code: "it", label: "Italiano" },
  { code: "ru", label: "Русский" },
  { code: "ar", label: "العربية" },
  { code: "nl", label: "Nederlands" },
  { code: "pl", label: "Polski" },
  { code: "tr", label: "Türkçe" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "ms", label: "Bahasa Melayu" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "th", label: "ไทย" },
];

const languageFlags: Record<LanguageCode, string> = {
  en: "🇺🇸",
  es: "🇪🇸",
  fr: "🇫🇷",
  de: "🇩🇪",
  ja: "🇯🇵",
  zh: "🇨🇳",
  ko: "🇰🇷",
  hi: "🇮🇳",
  pt: "🇵🇹",
  it: "🇮🇹",
  ru: "🇷🇺",
  ar: "🇸🇦",
  nl: "🇳🇱",
  pl: "🇵🇱",
  tr: "🇹🇷",
  id: "🇮🇩",
  ms: "🇲🇾",
  vi: "🇻🇳",
  th: "🇹🇭",
};

const activeLanguageFlag = computed(() => languageFlags[language.value] ?? "🌐");
const t = (key: TranslationKey) => {
  const fixedValue = translationFixes[language.value]?.[key];
  if (fixedValue) return fixedValue;
  const translatedValue = translations[language.value][key];
  if (translatedValue) return translatedValue;
  const fallbackLanguage = translationFallbacks[language.value];
  const fallbackValue = fallbackLanguage ? translationFixes[fallbackLanguage]?.[key] ?? translations[fallbackLanguage]?.[key] : undefined;
  if (fallbackValue) return fallbackValue;
  const socialIndex = socialPresetLabelKeys.indexOf(key as (typeof socialPresetLabelKeys)[number]);
  if (socialIndex >= 0) return socialPresetLabels[language.value][socialIndex];
  return translations.en[key] ?? key;
};
const ui = (key: EditorMessageKey, values?: Record<string, string | number>) => editorMessage(language.value, key, values);
const languageOptionLabel = (option: (typeof languageOptions)[number]) => option.code === "auto" ? t("auto") : option.label;
const currentSaveToolCopy = computed(() => saveToolCopy[language.value]);
const bookmarkShortcut = computed(() => isBrowser && /Mac|iPhone|iPad|iPod/i.test(navigator.platform) ? "⌘D" : "Ctrl+D");
const saveToolActionLabel = computed(() => deferredInstallPrompt.value ? currentSaveToolCopy.value.install : currentSaveToolCopy.value.save);
const saveToolInstructions = computed(() => {
  if (!isBrowser) return [currentSaveToolCopy.value.desktop.replace("{shortcut}", bookmarkShortcut.value)];
  if (isAppleMobileDevice()) {
    const safari = /Safari/i.test(navigator.userAgent) && !/(CriOS|FxiOS|EdgiOS|OPiOS)/i.test(navigator.userAgent);
    return safari
      ? [currentSaveToolCopy.value.iosSafari]
      : [currentSaveToolCopy.value.iosOther, currentSaveToolCopy.value.iosSafari];
  }
  if (/Android/i.test(navigator.userAgent) || isMobileViewport.value) return [currentSaveToolCopy.value.android];
  return [currentSaveToolCopy.value.desktop.replace("{shortcut}", bookmarkShortcut.value)];
});

function detectInstalledApp() {
  if (!isBrowser) return false;
  const iosNavigator = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia("(display-mode: standalone)").matches || iosNavigator.standalone === true;
}

function markSaveToolOfferHidden() {
  if (isBrowser) window.localStorage.setItem(saveToolOfferStorageKey, "1");
  saveToolOfferVisible.value = false;
}

function dismissSaveToolOffer() {
  markSaveToolOfferHidden();
}

function scheduleSaveToolOffer() {
  if (!isBrowser || appInstalled.value || readLocalStorage(saveToolOfferStorageKey) === "1") return;
  if (saveToolOfferTimer != null) window.clearTimeout(saveToolOfferTimer);
  saveToolOfferTimer = window.setTimeout(() => {
    if (!saveToolDialogOpen.value) saveToolOfferVisible.value = true;
    saveToolOfferTimer = null;
  }, 900);
}

async function openSaveTool() {
  showTopSettings.value = false;
  showLanguageMenu.value = false;
  markSaveToolOfferHidden();
  const promptEvent = deferredInstallPrompt.value;
  if (promptEvent) {
    deferredInstallPrompt.value = null;
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    if (choice.outcome === "accepted") {
      appInstalled.value = true;
      markSaveToolOfferHidden();
      return;
    }
  }
  saveToolDialogOpen.value = true;
}

function handleBeforeInstallPrompt(event: Event) {
  event.preventDefault();
  deferredInstallPrompt.value = event as InstallPromptEvent;
}

function handleAppInstalled() {
  appInstalled.value = true;
  deferredInstallPrompt.value = null;
  saveToolDialogOpen.value = false;
  markSaveToolOfferHidden();
}
type PrerenderSeoContext = {
  cutMyPicSeo?: {
    title: string;
    description: string;
    imageAlt: string;
    openGraphLocale: string;
    language: LanguageCode;
    pageType: "editor" | "manual" | "guide" | "faq";
    faqItems: Array<{ question: string; answer: string }>;
  };
};

if (import.meta.env.SSR) {
  const ssrContext = useSSRContext() as PrerenderSeoContext | undefined;
  if (ssrContext) {
    const manualCopy = getManualCopy(language.value);
    const seoCopy = localizedSeoCopy[language.value];
    const pageType = isManualPage.value ? "manual" : isGuidePage.value ? "guide" : isFaqPage.value ? "faq" : "editor";
    ssrContext.cutMyPicSeo = {
      title: isManualPage.value ? manualCopy.title : isGuidePage.value ? "How It Works" : isFaqPage.value ? localizedFaqPageCopy.value.title : seoCopy.editorTitle,
      description: isManualPage.value ? seoCopy.manualDescription : isGuidePage.value ? "Learn how to crop, edit, customize, and export photos online with Cut My Pic using simple and detailed browser-based tools." : isFaqPage.value ? seoCopy.faqDescription : seoCopy.editorDescription,
      imageAlt: seoCopy.imageAlt,
      openGraphLocale: seoCopy.openGraphLocale,
      language: language.value,
      pageType,
      faqItems: isManualPage.value
        ? getManualFaqEntries(language.value)
        : isFaqPage.value
          ? localizedFaqSectionsWithEntries.value.flatMap((section) => section.entries)
          : [],
    };
  }
}
const hasImage = computed(() => Boolean(image.value));
const cropTutorialEnabled = computed({
  get: () => !cropTutorialDismissed.value,
  set: (enabled: boolean) => {
    if (!enabled) {
      dismissCropTutorialForever();
      return;
    }
    window.localStorage.removeItem(cropTutorialStorageKey);
    cropTutorialDismissed.value = false;
    cropTutorialStarted = false;
    showTopSettings.value = false;
    if (hasImage.value) {
      activeTool.value = "crop";
      activePanelTool.value = null;
      void nextTick(startCropTutorial);
    }
  },
});
const activeToolMeta = computed(() => tools.find((tool) => tool.id === activeTool.value));
const activeToolLabel = computed(() => (activePanelTool.value ? t(toolLabelKeys[activePanelTool.value as ToolId]) : ""));
const logoSrc = computed(() => (theme.value === "light" ? cmpLogoDark : cmpLogo));
const currentColorReplaceState = computed<ColorReplaceState>(() => ({
  targetColor: settings.colorReplaceTarget,
  replacementColor: settings.colorReplaceReplacement,
  strength: settings.colorReplaceStrength,
  smoothness: settings.colorReplaceSmoothness,
  targetTransparent: settings.colorReplaceTargetTransparent,
  replacementTransparent: settings.colorReplaceReplacementTransparent,
}));
const maxRadius = computed(() => Math.min(crop.width, crop.height) / 2 || 0);
const radius = computed(() => Math.min(settings.radius, maxRadius.value));
const renderedCornerRadius = computed(() =>
  activeTool.value === "corners" ? Math.max(committedCornerRadius.value, radius.value) : committedCornerRadius.value,
);
const renderedCornerRadii = computed(() =>
  activeTool.value === "corners"
    ? { x: Math.max(committedCornerRadiusX.value, radius.value), y: Math.max(committedCornerRadiusY.value, radius.value) }
    : { x: committedCornerRadiusX.value, y: committedCornerRadiusY.value },
);
const activeFilter = computed(() => (activeTool.value === "filters" && filterPending.value ? settings.filter : "none"));
const activeAdjustments = computed(() =>
  activeTool.value === "adjustments"
    ? settings
    : {
        contrast: committedAdjustments.contrast,
        brightness: committedAdjustments.brightness,
        saturation: committedAdjustments.saturation,
        colorBalance: committedAdjustments.colorBalance,
      },
);
const cornerRadiusPresets = computed(() => {
  return [20, 40, 60, maxRadius.value || 0];
});
const outputSize = computed(() => (hasImage.value ? `${Math.round(crop.width)} x ${Math.round(crop.height)} PNG` : t("noImageLoaded")));
const uploadSize = computed(() => (hasImage.value ? `${imageSize.width} x ${imageSize.height} px` : t("noImageLoaded")));
const currentSize = computed(() => {
  if (!hasImage.value) return t("noImageLoaded");
  const resizeIsDifferent =
    resizeBox.width > 0 &&
    resizeBox.height > 0 &&
    (resizeBox.x !== 0 || resizeBox.y !== 0 || resizeBox.width !== imageSize.width || resizeBox.height !== imageSize.height);
  if (activeTool.value === "resize" || resizeIsDifferent) {
    return `${Math.round(resizeBox.width)} x ${Math.round(resizeBox.height)} px`;
  }
  return `${Math.round(crop.width)} x ${Math.round(crop.height)} px`;
});
const zoomLevelReadout = computed(() => (hasImage.value ? `${Math.round(settings.zoom * 100)}%` : "—"));
const imageDimensions = computed(() => (hasImage.value ? `${imageSize.width} x ${imageSize.height} px` : t("noImageLoaded")));
const pendingCropShape = computed<CommittedImageShape | null>(() =>
  cropMode.value === "circle" || cropMode.value === "heart" || cropMode.value === "star" ? cropMode.value : null,
);
const cropPending = computed(() =>
  hasImage.value &&
  (activeTool.value === "crop" && cropFunctionMode.value === "masking" ||
    (pendingCropShape.value !== null && pendingCropShape.value !== committedImageShape.value) ||
    crop.x !== committedCrop.x ||
    crop.y !== committedCrop.y ||
    crop.width !== committedCrop.width ||
    crop.height !== committedCrop.height),
);
const cornerPending = computed(() => hasImage.value && activeTool.value === "corners" && Math.round(radius.value) !== Math.round(committedCornerRadius.value));
const resizePending = computed(() =>
  hasImage.value &&
  (resizeBox.x !== committedResizeBox.x ||
    resizeBox.y !== committedResizeBox.y ||
    resizeBox.width !== committedResizeBox.width ||
    resizeBox.height !== committedResizeBox.height ||
    resizeRotateDegrees.value !== committedResizeRotateDegrees.value ||
    resizeFlipHorizontal.value !== committedResizeFlipHorizontal.value ||
    resizeFlipVertical.value !== committedResizeFlipVertical.value),
);
watch([resizePanelMode, resizeRotateDegrees, resizeFlipHorizontal, resizeFlipVertical], () => {
  if (activeTool.value === "resize" || activePanelTool.value === "resize") render();
});
watch(resizeMode, (nextMode, previousMode) => {
  if (nextMode === "constrain" && previousMode !== "constrain") {
    resizeConstrainedAspect.value = resizeBox.width / Math.max(1, resizeBox.height);
  }
});
const colorReplacePending = computed(() =>
  settings.colorReplaceTarget !== committedColorReplace.targetColor ||
  settings.colorReplaceReplacement !== committedColorReplace.replacementColor ||
  settings.colorReplaceStrength !== committedColorReplace.strength ||
  settings.colorReplaceSmoothness !== committedColorReplace.smoothness ||
  settings.colorReplaceTargetTransparent !== committedColorReplace.targetTransparent ||
  settings.colorReplaceReplacementTransparent !== committedColorReplace.replacementTransparent,
);
const filterPending = computed(() =>
  hasImage.value &&
  settings.filter !== "none" &&
  (settings.filter !== committedFilter.value || (settings.filter === "colorreplace" && colorReplacePending.value)),
);
const adjustmentsPending = computed(
  () =>
    hasImage.value &&
    (settings.contrast !== committedAdjustments.contrast ||
      settings.brightness !== committedAdjustments.brightness ||
      settings.saturation !== committedAdjustments.saturation ||
      settings.colorBalance !== committedAdjustments.colorBalance ||
      settings.sharpen !== committedAdjustments.sharpen ||
      settings.soften !== committedAdjustments.soften ||
      settings.transparency !== committedAdjustments.transparency ||
      settings.transparencyIncludeBorder !== committedAdjustments.transparencyIncludeBorder),
);
const textPending = computed(
  () =>
    hasImage.value &&
    (textState.content !== committedText.content ||
      textState.color !== committedText.color ||
      textState.size !== committedText.size ||
      textState.rotation !== committedText.rotation ||
      textState.x !== committedText.x ||
      textState.y !== committedText.y ||
      textState.shadowSize !== committedText.shadowSize ||
      textState.shadowDarkness !== committedText.shadowDarkness ||
      textState.shadowColor !== committedText.shadowColor),
);
const borderPending = computed(
  () =>
    hasImage.value &&
    (settings.borderThickness !== committedBorderState.borderThickness ||
      settings.borderColor !== committedBorderState.borderColor ||
      settings.borderUseGradient !== committedBorderState.borderUseGradient ||
      settings.borderGradientType !== committedBorderState.borderGradientType ||
      settings.borderGradientAngle !== committedBorderState.borderGradientAngle ||
      settings.borderGradientStart !== committedBorderState.borderGradientStart ||
      settings.borderGradientEnd !== committedBorderState.borderGradientEnd),
);
const shadowPending = computed(
  () =>
    hasImage.value &&
    (settings.shadowDarkness !== committedShadowState.shadowDarkness ||
      settings.shadowSize !== committedShadowState.shadowSize ||
      settings.shadowColor !== committedShadowState.shadowColor ||
      settings.shadowDirection !== committedShadowState.shadowDirection),
);
const mobileApplyAvailable = computed(() => {
  if (!isMobileViewport.value || !hasImage.value || !activeTool.value) return false;
  return activeTool.value === "crop" ? cropPending.value
    : activeTool.value === "corners" ? cornerPending.value
      : activeTool.value === "border" ? borderPending.value
        : activeTool.value === "shadow" ? shadowPending.value
          : activeTool.value === "resize" ? resizePending.value
            : activeTool.value === "filters" ? filterPending.value
              : activeTool.value === "adjustments" ? adjustmentsPending.value
                : activeTool.value === "text" ? textPending.value
                  : false;
});
const mobileBackAvailable = computed(() =>
  isMobileViewport.value && hasImage.value && Boolean(activeTool.value) && mobileBackMode.value && !mobileApplyAvailable.value,
);
watch(mobileApplyAvailable, (isPending) => {
  if (isPending && isMobileViewport.value) mobileBackMode.value = false;
});
const exportHasUnappliedChanges = computed(() =>
  cropPending.value ||
  cornerPending.value ||
  resizePending.value ||
  filterPending.value ||
  adjustmentsPending.value ||
  textPending.value ||
  borderPending.value ||
  shadowPending.value,
);
const hasPreviewChanges = computed(() => previewHistory.value.length > 1);
const canCompareImages = computed(() => hasImage.value && previewHistory.value.length > 1);
const shareSupported = computed(() => isBrowser && typeof navigator.share === "function" && typeof navigator.canShare === "function");
const shortcutDisplayMap = computed(() => ({
  history: "Cmd+Shift+H",
  redo: "Cmd+Shift+Z",
  export: "Cmd+S",
  apply: "Cmd+Enter",
  undo: "Cmd+Z",
}));

const cropStyle = computed(() => ({
  left: `${canvasMetrics.imageX + crop.x * canvasMetrics.scale}px`,
  top: `${canvasMetrics.imageY + crop.y * canvasMetrics.scale}px`,
  width: `${crop.width * canvasMetrics.scale}px`,
  height: `${crop.height * canvasMetrics.scale}px`,
  borderRadius: (cropFunctionMode.value === "masking" ? cropMaskShape.value === "circle" : cropMode.value === "circle")
    ? "50%"
    : `${(activeTool.value === "crop" ? 0 : radius.value) * canvasMetrics.scale}px`,
}));

const cropControlsVisible = computed(() =>
  hasImage.value &&
  activeTool.value === "crop" &&
  (!isMobileViewport.value || activePanelTool.value === "crop"),
);
const cornerControlsVisible = computed(() =>
  hasImage.value &&
  activeTool.value === "corners" &&
  (!isMobileViewport.value || activePanelTool.value === "corners"),
);
const circleAspectLockVisible = computed(() =>
  cropControlsVisible.value &&
  (cropFunctionMode.value === "masking"
    ? cropMaskShape.value === "circle"
    : cropMode.value === "free" || cropMode.value === "circle" || cropMode.value === "heart" || cropMode.value === "star"),
);
const circleAspectLocked = computed(() => {
  if (cropFunctionMode.value === "masking") return maskRoundedAspectLocked.value;
  return cropMode.value === "free" ? freeCropAspectLocked.value : cropCircleAspectLocked.value;
});
const circleAspectLockStyle = computed(() => ({
  left: `${Math.round(canvasMetrics.imageX + 12)}px`,
  top: `${Math.round(canvasMetrics.imageY + 12)}px`,
}));

const resizeStyle = computed(() => {
  return {
    left: `${canvasMetrics.imageX + resizeBox.x * canvasMetrics.scale}px`,
    top: `${canvasMetrics.imageY + resizeBox.y * canvasMetrics.scale}px`,
    width: `${resizeBox.width * canvasMetrics.scale}px`,
    height: `${resizeBox.height * canvasMetrics.scale}px`,
  };
});

const cropReadout = computed(() => {
  if (!hasImage.value) return t("dropToBegin");
  return `${Math.round(crop.width)} x ${Math.round(crop.height)} at ${Math.round(crop.x)}, ${Math.round(crop.y)}`;
});

const floatingStyle = computed(() => ({
  left: `${floatingPanel.x}px`,
  top: `${floatingPanel.y}px`,
  "--floating-panel-top": `${floatingPanel.y}px`,
  "--floating-panel-max-height": floatingPanelBounds.maxHeight ? `${floatingPanelBounds.maxHeight}px` : undefined,
}));

const historyPanelStyle = computed(() => ({
  left: `${historyPanelPosition.x}px`,
  top: `${historyPanelPosition.y}px`,
  "--floating-panel-max-height": floatingPanelBounds.maxHeight ? `${floatingPanelBounds.maxHeight}px` : undefined,
}));

const canPanImage = computed(() => hasImage.value);
const oneToOneZoomLevel = computed(() => 1 / Math.max(Number.EPSILON, previewBaseScale.value));
const zoomSliderMin = computed(() => Math.min(0.5, oneToOneZoomLevel.value));
const zoomSliderMax = computed(() => Math.max(3, oneToOneZoomLevel.value));
const zoomSliderProgress = computed(() => {
  const range = zoomSliderMax.value - zoomSliderMin.value;
  if (range <= 0) return 0;
  return Math.min(1, Math.max(0, (settings.zoom - zoomSliderMin.value) / range));
});
const zoomTooltipStyle = computed(() => ({
  "--zoom-tooltip-position": `${zoomSliderProgress.value * 100}%`,
}));

function showZoomTooltip() {
  if (hasImage.value) zoomTooltipVisible.value = true;
}

function hideZoomTooltip() {
  zoomTooltipVisible.value = false;
}

watch(canPanImage, (isAvailable) => {
  if (!isAvailable && imagePanMode.value) toggleImagePanMode();
});
watch(canCompareImages, (isAvailable) => {
  if (!isAvailable && comparisonMode.value) exitComparisonMode();
});
const stageCursorStyle = computed(() => ({
  cursor: imagePanActive.value
    ? "grabbing"
    : imagePanMode.value && canPanImage.value
      ? "grab"
      : stageCursor.value ?? undefined,
}));
const comparisonDividerStyle = computed(() => ({ left: `${comparisonPosition.value}%` }));

function setCurrentImage(
  nextImage: HTMLImageElement,
  url: string,
  name: string,
  options?: {
    resetCornerRadius?: boolean;
    preserveZoom?: boolean;
    preserveText?: boolean;
    preserveCropMode?: boolean;
    preservePreviewScale?: boolean;
    preserveCircleAspectLock?: boolean;
    preserveMaskAspectLock?: boolean;
    activateCropOnMobile?: boolean;
    viewportAnchor?: { x: number; y: number };
  },
) {
  const keepViewportAnchor = !autoCenterEnabled.value && hasImage.value
    ? options?.viewportAnchor ?? {
        x: canvasMetrics.imageX + canvasMetrics.imageWidth / 2,
        y: canvasMetrics.imageY + canvasMetrics.imageHeight / 2,
      }
    : null;
  if (imageUrl.value.startsWith("blob:")) URL.revokeObjectURL(imageUrl.value);
  const keepCornerRadius = options?.resetCornerRadius === false ? committedCornerRadius.value : 0;
  const keepCornerRadiusX = options?.resetCornerRadius === false ? committedCornerRadiusX.value : 0;
  const keepCornerRadiusY = options?.resetCornerRadius === false ? committedCornerRadiusY.value : 0;
  const keepCommittedImageShape = options?.resetCornerRadius === false ? committedImageShape.value : "rect";
  const keepZoom = options?.preserveZoom === true ? settings.zoom : 1;
  const keepPreviewBaseScale = options?.preservePreviewScale === true
    ? previewBaseScale.value
    : getPreviewBaseScale(nextImage.naturalWidth, nextImage.naturalHeight);
  const keepCropMode = cropMode.value;
  const keepCropFunctionMode = cropFunctionMode.value;
  const keepSelectedCropPresetId = selectedCropPresetId.value;
  const keepFreeCropAspectLocked = freeCropAspectLocked.value;
  const keepFreeCropLockedAspect = freeCropLockedAspect.value;
  const keepCropCircleAspectLocked = cropCircleAspectLocked.value;
  const keepMaskRoundedAspectLocked = maskRoundedAspectLocked.value;
  image.value = nextImage;
  imageUrl.value = url;
  imageName.value = name;
  imageSize.width = nextImage.naturalWidth;
  imageSize.height = nextImage.naturalHeight;
  imagePreviewSource.value = createImagePreviewSource(nextImage, nextImage.naturalWidth, nextImage.naturalHeight);
  resetToolStateForNewImage(nextImage.naturalWidth, nextImage.naturalHeight, {
    preserveZoom: options?.preserveZoom,
    preserveText: options?.preserveText,
  });
  if (options?.preserveCropMode) {
    cropMode.value = keepCropMode;
    cropFunctionMode.value = keepCropFunctionMode;
    selectedCropPresetId.value = keepSelectedCropPresetId;
    freeCropAspectLocked.value = keepFreeCropAspectLocked;
    freeCropLockedAspect.value = keepFreeCropLockedAspect;
    cropCircleAspectLocked.value = keepCropCircleAspectLocked;
    maskRoundedAspectLocked.value = keepMaskRoundedAspectLocked;
  }
  committedCornerRadius.value = keepCornerRadius;
  committedCornerRadiusX.value = keepCornerRadiusX;
  committedCornerRadiusY.value = keepCornerRadiusY;
  committedImageShape.value = keepCommittedImageShape;
  settings.zoom = keepZoom;
  previewBaseScale.value = keepPreviewBaseScale;
  if (keepViewportAnchor) {
    const previewInset = 6;
    const scale = Math.max(0.03, keepPreviewBaseScale * keepZoom);
    const drawWidth = nextImage.naturalWidth * scale;
    const drawHeight = nextImage.naturalHeight * scale;
    const cssWidth = canvasMetrics.cssWidth;
    const cssHeight = canvasMetrics.cssHeight;
    const defaultX = (cssWidth - drawWidth) / 2;
    const availableHeight = Math.max(0, cssHeight - previewInset * 2);
    const defaultY = isMobileViewport.value
      ? Math.max(8, (cssHeight - drawHeight) * 0.12)
      : previewInset + (availableHeight - drawHeight) / 2;
    imagePan.x = keepViewportAnchor.x - (defaultX + drawWidth / 2);
    imagePan.y = keepViewportAnchor.y - (defaultY + drawHeight / 2);
  }
  committedFilter.value = "none";
  committedAdjustments.contrast = 100;
  committedAdjustments.brightness = 100;
  committedAdjustments.saturation = 100;
  committedAdjustments.colorBalance = 0;
  committedAdjustments.sharpen = 0;
  committedAdjustments.soften = 0;
  committedAdjustments.transparency = 0;
  committedAdjustments.transparencyIncludeBorder = false;
  if (!options?.preserveText) {
    const defaultTextSize = defaultTextSizeForDimensions(imageSize.width, imageSize.height);
    committedText.content = "";
    committedText.color = "#e8f1ff";
    committedText.size = defaultTextSize;
    committedText.rotation = 0;
    committedText.x = 50;
    committedText.y = 4;
    committedText.shadowSize = 8;
    committedText.shadowDarkness = 35;
    committedText.shadowColor = "#000000";
    textState.content = "";
    textState.color = "#e8f1ff";
    textState.size = defaultTextSize;
    textState.rotation = 0;
    textState.x = 50;
    textState.y = 4;
    textState.shadowSize = 8;
    textState.shadowDarkness = 35;
    textState.shadowColor = "#000000";
  } else {
    committedText.content = textState.content;
    committedText.color = textState.color;
    committedText.size = textState.size;
    committedText.rotation = textState.rotation;
    committedText.x = textState.x;
    committedText.y = textState.y;
    committedText.shadowSize = textState.shadowSize;
    committedText.shadowDarkness = textState.shadowDarkness;
    committedText.shadowColor = textState.shadowColor;
  }
  if (isMobileViewport.value && options?.activateCropOnMobile) {
    activeTool.value = "crop";
    activePanelTool.value = "crop";
  }
}

function createImagePreviewSource(source: CanvasImageSource, width: number, height: number) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  canvas.getContext("2d")?.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function replaceCurrentImagePixels(nextImage: HTMLImageElement, url: string, name: string, previewSource?: HTMLCanvasElement) {
  if (imageUrl.value.startsWith("blob:")) URL.revokeObjectURL(imageUrl.value);
  image.value = nextImage;
  imagePreviewSource.value = previewSource ?? createImagePreviewSource(nextImage, nextImage.naturalWidth, nextImage.naturalHeight);
  imageUrl.value = url;
  imageName.value = name;
  imageSize.width = nextImage.naturalWidth;
  imageSize.height = nextImage.naturalHeight;
}

function openPicker() {
  fileInputRef.value?.click();
}

function restoreOriginalPreview() {
  if (!hasImage.value || !previewHistory.value.length) return;
  if (comparisonMode.value) exitComparisonMode(false);
  void restoreImageHistoryEntry(previewHistory.value[0]);
}

function restoreHistoryStep(index: number) {
  if (!hasImage.value || index < 0 || index >= previewHistory.value.length) return;
  if (comparisonMode.value) exitComparisonMode(false);
  redoHistory.value = previewHistory.value.slice(index + 1);
  previewHistory.value = previewHistory.value.slice(0, index + 1);
  const snapshot = previewHistory.value[index];
  if (!snapshot) return;
  void restoreImageHistoryEntry(snapshot);
}

function redoToHistoryStep(index: number) {
  if (!hasImage.value || index < previewHistory.value.length) return;
  if (comparisonMode.value) exitComparisonMode(false);
  const redoIndex = index - previewHistory.value.length;
  if (redoIndex < 0 || redoIndex >= redoHistory.value.length) return;
  const stepsToRestore = redoHistory.value.slice(0, redoIndex + 1);
  const snapshot = stepsToRestore[stepsToRestore.length - 1];
  if (!snapshot) return;
  previewHistory.value.push(...stepsToRestore);
  redoHistory.value = redoHistory.value.slice(redoIndex + 1);
  void restoreImageHistoryEntry(snapshot);
}

function stepBackPreview() {
  if (!hasImage.value || previewHistory.value.length < 2) return;
  if (comparisonMode.value) exitComparisonMode(false);
  const current = previewHistory.value.pop();
  if (current) redoHistory.value.unshift(current);
  const snapshot = previewHistory.value[previewHistory.value.length - 1];
  if (!snapshot) return;
  void restoreImageHistoryEntry(snapshot);
}

function stepForwardPreview() {
  if (!hasImage.value || !redoHistory.value.length) return;
  if (comparisonMode.value) exitComparisonMode(false);
  const next = redoHistory.value.shift();
  if (!next) return;
  previewHistory.value.push(next);
  void restoreImageHistoryEntry(next);
}

async function resetApp() {
  autosaveReady = false;
  try {
    await clearAutosavedProject();
  } catch (error) {
    console.warn("Cut My Pic could not clear the autosaved project.", error);
  }
  window.location.reload();
}

function panelToolFor(tool: ToolId | null) {
  if (!tool || tool === "export" || tool === "applyCrop") return null;
  return tool as Exclude<typeof activePanelTool.value, null>;
}

function setFullscreenMode(nextValue: boolean) {
  fullscreenMode.value = nextValue;
  if (nextValue && activeTool.value && activePanelTool.value == null) {
    activePanelTool.value = panelToolFor(activeTool.value);
    if (activePanelTool.value) positionFloatingPanel(true);
  }
  if (!nextValue) {
    resetFullscreenRailPosition();
    positionFloatingPanel(true);
    positionHistoryPanel(true);
  }
  void nextTick(render);
}

function browserFullscreenElement() {
  return document.fullscreenElement ?? (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement ?? null;
}

async function requestBrowserFullscreen() {
  if (browserFullscreenElement()) return;
  const root = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
  const request = root.requestFullscreen?.bind(root) ?? root.webkitRequestFullscreen?.bind(root);
  if (!request) return;
  try {
    await request();
  } catch (error) {
    console.warn("Cut My Pic could not enter browser fullscreen mode.", error);
  }
}

async function exitBrowserFullscreen() {
  if (!browserFullscreenElement()) return;
  const fullscreenDocument = document as Document & { webkitExitFullscreen?: () => Promise<void> | void };
  const exit = document.exitFullscreen?.bind(document) ?? fullscreenDocument.webkitExitFullscreen?.bind(document);
  if (!exit) return;
  try {
    await exit();
  } catch (error) {
    console.warn("Cut My Pic could not exit browser fullscreen mode.", error);
  }
}

function onBrowserFullscreenChange() {
  if (!browserFullscreenElement() && fullscreenMode.value) setFullscreenMode(false);
}

async function toggleFullscreenMode() {
  const enteringFullscreen = !fullscreenMode.value;
  setFullscreenMode(enteringFullscreen);
  if (enteringFullscreen) {
    await requestBrowserFullscreen();
  } else {
    await exitBrowserFullscreen();
  }
}

async function onToolClick(tool: ToolId) {
  if (comparisonMode.value) exitComparisonMode();
  mobileViewBackgroundOpen.value = false;
  if (tool === "applyCrop") {
    void applyCropToImage();
    mobileToolMenuOpen.value = false;
    return;
  }
  if (tool === "export") {
    exportPng();
    mobileToolMenuOpen.value = false;
    return;
  }
  showTool(tool);
  mobileToolMenuOpen.value = false;
}

function openToolPanel(tool: Exclude<typeof activePanelTool.value, null>) {
  if (comparisonMode.value) exitComparisonMode();
  activePanelTool.value = activePanelTool.value === tool ? null : tool;
  if (tool === "resize") activeTool.value = "resize";
  if (tool === "border" && activePanelTool.value === "border" && hasCommittedShadowEffect()) {
    borderShadowWarningOpen.value = true;
  }
  if (activePanelTool.value) positionFloatingPanel(false);
}

function setStageCursor(cursor: string | null) {
  stageCursor.value = cursor;
}

function resetCropPreview() {
  applyCropMode();
}

function applyCropMode() {
  if (cropMode.value === "square" || (cropMode.value === "circle" && cropCircleAspectLocked.value)) {
    setSquareCropGeometry();
    return;
  }
  if (cropMode.value === "widescreen") {
    setWidescreenCrop();
    return;
  }
  fitCropToImage();
  if (cropMode.value === "free" && freeCropAspectLocked.value) {
    freeCropLockedAspect.value = crop.width / Math.max(1, crop.height);
  }
}

function resetBorderPreview() {
  settings.borderThickness = committedBorderState.borderThickness;
  settings.borderColor = committedBorderState.borderColor;
  settings.borderUseGradient = committedBorderState.borderUseGradient;
  settings.borderGradientType = committedBorderState.borderGradientType;
  settings.borderGradientAngle = committedBorderState.borderGradientAngle;
  settings.borderGradientStart = committedBorderState.borderGradientStart;
  settings.borderGradientEnd = committedBorderState.borderGradientEnd;
}

function resetShadowPreview() {
  settings.shadowDarkness = committedShadowState.shadowDarkness;
  settings.shadowSize = committedShadowState.shadowSize;
  settings.shadowColor = committedShadowState.shadowColor;
  settings.shadowDirection = committedShadowState.shadowDirection;
}

function resetFilterPreview() {
  settings.filter = committedFilter.value;
  settings.colorReplaceTarget = committedColorReplace.targetColor;
  settings.colorReplaceReplacement = committedColorReplace.replacementColor;
  settings.colorReplaceStrength = committedColorReplace.strength;
  settings.colorReplaceSmoothness = committedColorReplace.smoothness;
  settings.colorReplaceTargetTransparent = committedColorReplace.targetTransparent;
  settings.colorReplaceReplacementTransparent = committedColorReplace.replacementTransparent;
}

function resetAdjustmentsPreview() {
  settings.contrast = committedAdjustments.contrast;
  settings.brightness = committedAdjustments.brightness;
  settings.saturation = committedAdjustments.saturation;
  settings.colorBalance = committedAdjustments.colorBalance;
  settings.sharpen = committedAdjustments.sharpen;
  settings.soften = committedAdjustments.soften;
  settings.transparency = committedAdjustments.transparency;
  settings.transparencyIncludeBorder = committedAdjustments.transparencyIncludeBorder;
}

function resetTextPreview() {
  textState.content = committedText.content;
  textState.color = committedText.color;
  textState.size = committedText.size;
  textState.rotation = committedText.rotation;
  textState.x = committedText.x;
  textState.y = committedText.y;
  textState.shadowSize = committedText.shadowSize;
  textState.shadowDarkness = committedText.shadowDarkness;
  textState.shadowColor = committedText.shadowColor;
}

function clearTextInputs() {
  textState.content = "";
}

function applyStampPreset(preset: StampPreset) {
  textPanelMode.value = "stamp";
  selectedStampLabel.value = preset.label;
  textState.content = preset.content;
  textState.color = preset.color;
  textState.size = preset.size;
  textState.rotation = 0;
  textState.x = preset.x;
  textState.y = preset.y;
  stampPositionMode.value = true;
  activePanelTool.value = null;
  void nextTick(render);
}

function clearStampSelection() {
  selectedStampLabel.value = "";
  textState.content = "";
}

function activateTextPanelMode(mode: "text" | "stamp") {
  textPanelMode.value = mode;
  if (mode === "text") {
    stampPositionMode.value = false;
  } else {
    textPositionMode.value = false;
  }
}

function setTextPositionFromPointer(clientX: number, clientY: number) {
  const { imageX, imageY, imageWidth, imageHeight } = canvasMetrics;
  if (!hasImage.value || !imageWidth || !imageHeight) return;
  const x = clamp(((clientX - imageX - textDragOffset.x) / imageWidth) * 100, 0, 100);
  const y = clamp(((clientY - imageY - textDragOffset.y) / imageHeight) * 100, 0, 100);
  textState.x = x;
  textState.y = y;
}

function beginTextPositionDrag(event: PointerEvent) {
  if (!canEditTextPosition.value || !textPositionMode.value) return;
  const { imageX, imageY, imageWidth, imageHeight } = canvasMetrics;
  if (!imageWidth || !imageHeight) return;
  const currentTextX = imageX + (textState.x / 100) * imageWidth;
  const currentTextY = imageY + (textState.y / 100) * imageHeight;
  textDragOffset.x = event.clientX - currentTextX;
  textDragOffset.y = event.clientY - currentTextY;
  textDragActive.value = true;
  setTextPositionFromPointer(event.clientX, event.clientY);
  (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
}

function onTextPositionDrag(event: PointerEvent) {
  if (!textDragActive.value) return;
  setTextPositionFromPointer(event.clientX, event.clientY);
}

function endTextPositionDrag(event?: PointerEvent) {
  if (!textDragActive.value) return;
  textDragActive.value = false;
  if (event?.currentTarget instanceof HTMLElement) {
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // ignore release errors
    }
  }
}

async function beginTextPosition() {
  if (!canEditTextPosition.value) return;
  if (isMobileViewport.value && document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
    await nextTick();
    await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
  }
  textPositionMode.value = true;
  activePanelTool.value = null;
  await nextTick();
  render();
}

function setStampPositionFromPointer(clientX: number, clientY: number) {
  const { imageX, imageY, imageWidth, imageHeight } = canvasMetrics;
  if (!hasImage.value || !imageWidth || !imageHeight) return;
  const x = clamp(((clientX - imageX - stampDragOffset.x) / imageWidth) * 100, 0, 100);
  const y = clamp(((clientY - imageY - stampDragOffset.y) / imageHeight) * 100, 0, 100);
  textState.x = x;
  textState.y = y;
}

function beginStampPositionDrag(event: PointerEvent) {
  if (!hasImage.value || textPanelMode.value !== "stamp" || !stampPositionMode.value) return;
  const { imageX, imageY, imageWidth, imageHeight } = canvasMetrics;
  if (!imageWidth || !imageHeight) return;
  const currentStampX = imageX + (textState.x / 100) * imageWidth;
  const currentStampY = imageY + (textState.y / 100) * imageHeight;
  stampDragOffset.x = event.clientX - currentStampX;
  stampDragOffset.y = event.clientY - currentStampY;
  stampDragActive.value = true;
  setStampPositionFromPointer(event.clientX, event.clientY);
  (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
}

function onStampPositionDrag(event: PointerEvent) {
  if (!stampDragActive.value) return;
  setStampPositionFromPointer(event.clientX, event.clientY);
}

function endStampPositionDrag(event?: PointerEvent) {
  if (!stampDragActive.value) return;
  stampDragActive.value = false;
  if (event?.currentTarget instanceof HTMLElement) {
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // ignore release errors
    }
  }
}

function textPanelAfterPlacement() {
  return fullscreenMode.value || isMobileViewport.value ? "text" : null;
}

async function confirmStampPosition() {
  if (!canEditStampPosition.value) return;
  const preview = stampPreviewRef.value;
  const displayScaleX = Math.max(0.03, canvasMetrics.imageWidth / Math.max(1, imageSize.width));
  const displayScaleY = Math.max(0.03, canvasMetrics.imageHeight / Math.max(1, imageSize.height));
  const centerX = (stampPositionPoint.value.left - canvasMetrics.imageX) / displayScaleX;
  const centerY = (stampPositionPoint.value.top - canvasMetrics.imageY) / displayScaleY;
  if (preview && selectedStampPreset.value?.kind === "price-tag") {
    const fontDisplayScale = Math.min(displayScaleX, displayScaleY);
    const computedStyle = window.getComputedStyle(preview);
    pendingStampPlacementMetrics = {
      width: preview.offsetWidth / displayScaleX,
      height: preview.offsetHeight / displayScaleY,
      fontSize: Number.parseFloat(computedStyle.fontSize) / fontDisplayScale,
      lineWidth: Math.max(1, Number.parseFloat(computedStyle.borderTopWidth) / fontDisplayScale),
      centerX,
      centerY,
    };
  } else {
    pendingStampPlacementMetrics = { centerX, centerY };
  }
  stampPositionMode.value = false;
  await applyTextToImage(`${t("stampMode")} - ${selectedStampPreset.value?.label ?? stampPositionLabel.value}`);
  clearStampSelection();
  activePanelTool.value = textPanelAfterPlacement();
}

function cancelStampPosition() {
  clearStampSelection();
  stampPositionMode.value = false;
  activePanelTool.value = "text";
}

async function confirmTextPosition() {
  if (!canEditTextPosition.value) return;
  textPositionMode.value = false;
  await applyTextToImage(t("text"));
  activePanelTool.value = textPanelAfterPlacement();
}

function cancelTextPosition() {
  textPositionMode.value = false;
  activePanelTool.value = "text";
}

function ensureTextHint() {
  if (!textState.content.trim()) textState.content = t("textPlaceholder");
}

async function loadComparisonBeforeImage() {
  const originalEntry = previewHistory.value[0];
  if (!originalEntry) return null;
  if (comparisonBeforeImage.value && comparisonBeforeDataUrl === originalEntry.dataUrl) {
    return comparisonBeforeImage.value;
  }
  const originalImage = new Image();
  originalImage.decoding = "async";
  originalImage.src = originalEntry.dataUrl;
  await originalImage.decode();
  comparisonBeforeImage.value = originalImage;
  comparisonBeforeDataUrl = originalEntry.dataUrl;
  return originalImage;
}

function finishComparisonDrag(pointerId?: number) {
  if (!comparisonPointer || (pointerId !== undefined && comparisonPointer.pointerId !== pointerId)) return;
  const { captureTarget, pointerId: activePointerId } = comparisonPointer;
  if (captureTarget.hasPointerCapture(activePointerId)) {
    try {
      captureTarget.releasePointerCapture(activePointerId);
    } catch {
      // The browser may already have released capture.
    }
  }
  comparisonPointer = null;
}

function exitComparisonMode(restoreSuspendedTool = true) {
  if (!comparisonMode.value && suspendedToolForComparison === null && suspendedPanelForComparison === null) return;
  if (comparisonLabelFrame != null) {
    window.cancelAnimationFrame(comparisonLabelFrame);
    comparisonLabelFrame = null;
  }
  comparisonLabelsVisible.value = false;
  comparisonMode.value = false;
  finishComparisonDrag();
  if (restoreSuspendedTool && activeTool.value === null) {
    if (imagePanMode.value) {
      suspendedToolForPan = suspendedToolForComparison;
      suspendedPanelForPan = suspendedPanelForComparison;
    } else {
      activeTool.value = suspendedToolForComparison;
      activePanelTool.value = suspendedPanelForComparison;
    }
  }
  suspendedToolForComparison = null;
  suspendedPanelForComparison = null;
  void nextTick(render);
}

async function toggleComparisonMode() {
  if (comparisonMode.value) {
    exitComparisonMode();
    return;
  }
  if (!canCompareImages.value) return;
  if (imagePanMode.value) toggleImagePanMode();
  const originalImage = await loadComparisonBeforeImage();
  if (!originalImage || !canCompareImages.value) return;
  suspendedToolForComparison = activeTool.value;
  suspendedPanelForComparison = activePanelTool.value;
  activeTool.value = null;
  activePanelTool.value = null;
  comparisonPosition.value = 50;
  comparisonLabelsVisible.value = false;
  comparisonMode.value = true;
  await nextTick();
  render();
  comparisonLabelFrame = window.requestAnimationFrame(() => {
    comparisonLabelFrame = window.requestAnimationFrame(() => {
      comparisonLabelFrame = null;
      if (comparisonMode.value) comparisonLabelsVisible.value = true;
    });
  });
}

function setComparisonPositionFromPointer(event: PointerEvent) {
  const stage = stageRef.value;
  if (!stage) return;
  const bounds = stage.getBoundingClientRect();
  comparisonPosition.value = clamp(((event.clientX - bounds.left) / Math.max(1, bounds.width)) * 100, 0, 100);
  render();
}

function startComparisonDrag(event: PointerEvent) {
  if (event.button !== 0) return;
  const captureTarget = event.currentTarget as HTMLElement;
  captureTarget.setPointerCapture(event.pointerId);
  comparisonPointer = { pointerId: event.pointerId, captureTarget };
  setComparisonPositionFromPointer(event);
  event.preventDefault();
  event.stopPropagation();
}

function moveComparisonDivider(event: PointerEvent) {
  if (!comparisonPointer || comparisonPointer.pointerId !== event.pointerId) return;
  setComparisonPositionFromPointer(event);
  event.preventDefault();
}

function onComparisonDividerKeydown(event: KeyboardEvent) {
  if (event.key === "ArrowLeft" || event.key === "ArrowDown") comparisonPosition.value = clamp(comparisonPosition.value - 2, 0, 100);
  else if (event.key === "ArrowRight" || event.key === "ArrowUp") comparisonPosition.value = clamp(comparisonPosition.value + 2, 0, 100);
  else if (event.key === "Home") comparisonPosition.value = 0;
  else if (event.key === "End") comparisonPosition.value = 100;
  else return;
  event.preventDefault();
  render();
}

function showTool(tool: ToolId) {
  if (comparisonMode.value) exitComparisonMode();
  if (workflowAwaitingCrop.value && tool !== "crop") cancelWorkflowPlayback();
  if (imagePanMode.value) {
    imagePanMode.value = false;
    finishImagePan();
    suspendedToolForPan = null;
    suspendedPanelForPan = null;
  }
  const previousTool = activeTool.value;
  if (previousTool === "crop" && tool !== "crop") resetCropPreview();
  if (previousTool === "border" && tool !== "border") resetBorderPreview();
  if (previousTool === "shadow" && tool !== "shadow") resetShadowPreview();
  if (previousTool === "resize" && tool !== "resize") resetResizeBox();
  if (previousTool === "filters" && tool !== "filters") resetFilterPreview();
  if (previousTool === "adjustments" && tool !== "adjustments") resetAdjustmentsPreview();
  if (previousTool === "text" && tool !== "text") resetTextPreview();
  if (isMobileViewport.value && previousTool === tool && activePanelTool.value == null) {
    activePanelTool.value = panelToolFor(tool);
    if (activePanelTool.value) positionFloatingPanel(false);
    void nextTick(render);
    return;
  }
  const deselectingDesktopCrop = !isMobileViewport.value && tool === "crop" && previousTool === "crop";
  activeTool.value = tool === "resize" ? "resize" : tool === "crop" ? deselectingDesktopCrop ? null : "crop" : activeTool.value === tool ? null : tool;
  if (deselectingDesktopCrop) activePanelTool.value = null;
  if (activeTool.value === "resize") {
    resizeBox.x = 0;
    resizeBox.y = 0;
    resizeBox.width = imageSize.width;
    resizeBox.height = imageSize.height;
    committedResizeBox.x = resizeBox.x;
    committedResizeBox.y = resizeBox.y;
    committedResizeBox.width = resizeBox.width;
    committedResizeBox.height = resizeBox.height;
    resizeConstrainedAspect.value = resizeBox.width / Math.max(1, resizeBox.height);
    resizePanelMode.value = "resize";
    resizeRotateDegrees.value = committedResizeRotateDegrees.value;
    resizeFlipHorizontal.value = committedResizeFlipHorizontal.value;
    resizeFlipVertical.value = committedResizeFlipVertical.value;
  }
  if (activeTool.value === "shadow") {
    settings.shadowDarkness = committedShadowState.shadowDarkness;
    settings.shadowSize = committedShadowState.shadowSize;
    settings.shadowColor = committedShadowState.shadowColor;
    settings.shadowDirection = committedShadowState.shadowDirection;
  }
  if (activeTool.value === "border" && previousTool !== "border" && hasCommittedShadowEffect()) {
    borderShadowWarningOpen.value = true;
  }
  if ((fullscreenMode.value || isMobileViewport.value) && activeTool.value) {
    activePanelTool.value = panelToolFor(activeTool.value);
  }
  if (isMobileViewport.value && previousTool === tool && activeTool.value !== tool) {
    activePanelTool.value = null;
  }
  if (activeTool.value && activeTool.value !== "resize") positionFloatingPanel(false);
  void nextTick(render);
}

function centerImageInView() {
  autoCenterEnabled.value = !autoCenterEnabled.value;
  if (autoCenterEnabled.value) {
    if (imagePanMode.value) toggleImagePanMode();
    imagePan.x = 0;
    imagePan.y = 0;
  }
  void nextTick(render);
}

function toggleImagePanMode() {
  if (!hasImage.value) return;
  const nextMode = !imagePanMode.value;
  if (nextMode && comparisonMode.value) exitComparisonMode();
  imagePanMode.value = nextMode;
  if (nextMode) {
    autoCenterEnabled.value = false;
    suspendedToolForPan = activeTool.value;
    suspendedPanelForPan = activePanelTool.value;
    activeTool.value = null;
    activePanelTool.value = null;
  } else {
    finishImagePan();
    if (activeTool.value === null && suspendedToolForPan !== null) {
      activeTool.value = suspendedToolForPan;
      activePanelTool.value = suspendedPanelForPan;
    }
    suspendedToolForPan = null;
    suspendedPanelForPan = null;
  }
  void nextTick(render);
}

function startImagePan(event: PointerEvent) {
  if (!imagePanMode.value || !canPanImage.value || event.button !== 0) return;
  const target = event.target as HTMLElement | null;
  if (target?.closest(".view-overlay, .fullscreen-mode-rail, .stamp-position-overlay, .comparison-overlay")) return;
  const captureTarget = event.currentTarget as HTMLElement;
  captureTarget.setPointerCapture(event.pointerId);
  imagePanPointer = {
    pointerId: event.pointerId,
    captureTarget,
    startX: event.clientX,
    startY: event.clientY,
    panX: imagePan.x,
    panY: imagePan.y,
  };
  imagePanActive.value = true;
  event.preventDefault();
  event.stopPropagation();
}

function moveImagePan(event: PointerEvent) {
  if (!imagePanPointer || event.pointerId !== imagePanPointer.pointerId) return;
  imagePan.x = imagePanPointer.panX + event.clientX - imagePanPointer.startX;
  imagePan.y = imagePanPointer.panY + event.clientY - imagePanPointer.startY;
  render();
}

function finishImagePan(pointerId?: number) {
  if (!imagePanPointer || (pointerId !== undefined && imagePanPointer.pointerId !== pointerId)) return;
  const { captureTarget, pointerId: activePointerId } = imagePanPointer;
  imagePanPointer = null;
  imagePanActive.value = false;
  if (captureTarget.hasPointerCapture?.(activePointerId)) captureTarget.releasePointerCapture(activePointerId);
}

function endImagePan(event: PointerEvent) {
  finishImagePan(event.pointerId);
}

function resizeMinSize() {
  return 1;
}

function resizeMaxSize() {
  return 99999;
}

function resetResizeBox() {
  if (!hasImage.value) return;
  resizeBox.x = committedResizeBox.x;
  resizeBox.y = committedResizeBox.y;
  resizeBox.width = committedResizeBox.width;
  resizeBox.height = committedResizeBox.height;
  resizeConstrainedAspect.value = resizeBox.width / Math.max(1, resizeBox.height);
  resizePanelMode.value = "resize";
  resizeRotateDegrees.value = committedResizeRotateDegrees.value;
  resizeFlipHorizontal.value = committedResizeFlipHorizontal.value;
  resizeFlipVertical.value = committedResizeFlipVertical.value;
  render();
}

function rotateResize(delta: number) {
  if (!hasImage.value) return;
  resizeRotateDegrees.value = ((Math.round(resizeRotateDegrees.value) + delta) % 360 + 360) % 360;
  if (resizeRotateDegrees.value > 180) resizeRotateDegrees.value -= 360;
  render();
}

function flipResizeVertical() {
  if (!hasImage.value) return;
  resizeFlipVertical.value = !resizeFlipVertical.value;
  render();
}

function flipResizeHorizontal() {
  if (!hasImage.value) return;
  resizeFlipHorizontal.value = !resizeFlipHorizontal.value;
  render();
}

function setResizeWidth(nextWidth: number) {
  if (!hasImage.value) return;
  if (!Number.isFinite(nextWidth) || nextWidth <= 0) return;
  const minSize = resizeMinSize();
  const maxSize = resizeMaxSize();
  const width = Math.max(minSize, Math.round(nextWidth || 0));
  if (resizeMode.value === "constrain") {
    const aspect = Math.max(Number.EPSILON, resizeConstrainedAspect.value);
    resizeBox.width = clamp(width, minSize, maxSize);
    resizeBox.height = clamp(Math.round(resizeBox.width / aspect), minSize, maxSize);
  } else {
    resizeBox.width = clamp(width, minSize, maxSize);
  }
}

function setResizeHeight(nextHeight: number) {
  if (!hasImage.value) return;
  if (!Number.isFinite(nextHeight) || nextHeight <= 0) return;
  const minSize = resizeMinSize();
  const maxSize = resizeMaxSize();
  const height = Math.max(minSize, Math.round(nextHeight || 0));
  if (resizeMode.value === "constrain") {
    const aspect = Math.max(Number.EPSILON, resizeConstrainedAspect.value);
    resizeBox.height = clamp(height, minSize, maxSize);
    resizeBox.width = clamp(Math.round(resizeBox.height * aspect), minSize, maxSize);
  } else {
    resizeBox.height = clamp(height, minSize, maxSize);
  }
}

function commitResizeSettings() {
  void applyResizeToImage();
}

function getPreviewBaseScale(width: number, height: number) {
  const stage = stageRef.value;
  const rect = stage?.getBoundingClientRect();
  const cssWidth = canvasMetrics.cssWidth || Math.max(320, Math.floor(rect?.width ?? 320));
  const cssHeight = canvasMetrics.cssHeight || Math.max(320, Math.floor(rect?.height ?? 320));
  const horizontalPadding = 56;
  const verticalPadding = 144;
  return Math.min((cssWidth - horizontalPadding) / width, (cssHeight - verticalPadding) / height);
}

async function applyCropToImage() {
  if (!image.value || crop.width < 1 || crop.height < 1) return;
  const cropPanelWasOpen = activePanelTool.value === "crop";
  const shouldShowCropTutorialTools = cropTutorialStep.value === "scissors";
  if (cropTutorialStep.value !== "hidden" && !shouldShowCropTutorialTools) closeCropTutorial();
  const preservedBorderState = { ...committedBorderState };
  const preservedBorderLayers = committedBorderLayers.map((layer) => ({ ...layer }));
  const source = createBorderlessSourceCanvas(image.value, imageSize.width, imageSize.height, getBorderLayerContentInset(preservedBorderLayers));
  const output = document.createElement("canvas");
  const isMasking = cropFunctionMode.value === "masking";
  const nextCropMaskMode = cropMaskMode.value;
  const nextCropMaskColor = cropMaskColor.value;
  const nextCropMaskTransparency = cropMaskTransparency.value;
  const nextCropMaskBlurIntensity = cropMaskBlurIntensity.value;
  const isCircleSelection = !isMasking && cropMode.value === "circle";
  const isCustomShapeSelection = !isMasking && (cropMode.value === "heart" || cropMode.value === "star");
  const isShapeSelection = isCircleSelection || isCustomShapeSelection;
  const isLockedShapeSelection = isShapeSelection && cropCircleAspectLocked.value;
  const isRoundedMaskSelection = isMasking && cropMaskShape.value === "circle";
  const oldBorderInset = !isMasking ? getBorderLayerTotalThickness(preservedBorderLayers) : 0;
  const sourceContentInset = !isMasking ? getBorderLayerContentInset(preservedBorderLayers) : 0;
  const sourceContentWidth = !isMasking ? source.width : imageSize.width;
  const sourceContentHeight = !isMasking ? source.height : imageSize.height;
  const requestedCropX = Math.round(crop.x);
  const requestedCropY = Math.round(crop.y);
  const requestedCropWidth = Math.round(crop.width);
  const requestedCropHeight = Math.round(crop.height);
  const cropSide = Math.round(Math.min(requestedCropWidth, requestedCropHeight));
  const selectedCropWidth = isLockedShapeSelection ? cropSide : requestedCropWidth;
  const selectedCropHeight = isLockedShapeSelection ? cropSide : requestedCropHeight;
  const contentCropX = !isMasking ? clamp(requestedCropX - sourceContentInset, 0, sourceContentWidth) : requestedCropX;
  const contentCropY = !isMasking ? clamp(requestedCropY - sourceContentInset, 0, sourceContentHeight) : requestedCropY;
  const contentCropRight = !isMasking ? clamp(requestedCropX + selectedCropWidth - sourceContentInset, 0, sourceContentWidth) : requestedCropX + selectedCropWidth;
  const contentCropBottom = !isMasking ? clamp(requestedCropY + selectedCropHeight - sourceContentInset, 0, sourceContentHeight) : requestedCropY + selectedCropHeight;
  const sourceCropWidth = Math.max(1, Math.round(contentCropRight - contentCropX));
  const sourceCropHeight = Math.max(1, Math.round(contentCropBottom - contentCropY));
  const borderPadding = !isMasking ? Math.ceil(oldBorderInset) : 0;
  const contentX = !isMasking ? borderPadding : 0;
  const contentY = !isMasking ? borderPadding : 0;
  const nextCommittedCornerRadiusX = isMasking ? committedCornerRadiusX.value : isCircleSelection ? Math.round(sourceCropWidth / 2) : 0;
  const nextCommittedCornerRadiusY = isMasking ? committedCornerRadiusY.value : isCircleSelection ? Math.round(sourceCropHeight / 2) : 0;
  const nextCommittedCornerRadius = Math.min(nextCommittedCornerRadiusX, nextCommittedCornerRadiusY);
  const cropHistoryLabel = isMasking
    ? cropMaskMode.value === "blur" ? `${t("blur")} - ${t("imageMasking")}` : `${t("overlayColor")} - ${t("imageMasking")}`
    : isCircleSelection ? t("circleCut") : cropMode.value === "heart" ? t("heartCut") : cropMode.value === "star" ? t("starCut") : t("crop");
  output.width = isMasking ? imageSize.width : sourceCropWidth + borderPadding * 2;
  output.height = isMasking ? imageSize.height : sourceCropHeight + borderPadding * 2;
  const ctx = output.getContext("2d");
  if (!ctx) return;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  if (!isMasking) {
    if (isShapeSelection) {
      ctx.save();
      ctx.beginPath();
      addCropShapePath(ctx, cropMode.value, contentX, contentY, sourceCropWidth, sourceCropHeight);
      ctx.clip();
      ctx.drawImage(source, contentCropX, contentCropY, sourceCropWidth, sourceCropHeight, contentX, contentY, sourceCropWidth, sourceCropHeight);
      ctx.restore();
    } else {
      ctx.drawImage(source, contentCropX, contentCropY, sourceCropWidth, sourceCropHeight, contentX, contentY, sourceCropWidth, sourceCropHeight);
    }
  } else {
    ctx.drawImage(image.value, 0, 0, output.width, output.height);
    ctx.save();
    ctx.beginPath();
    if (isRoundedMaskSelection) {
      ctx.ellipse(
        Math.round(crop.x) + sourceCropWidth / 2,
        Math.round(crop.y) + sourceCropHeight / 2,
        sourceCropWidth / 2,
        sourceCropHeight / 2,
        0,
        0,
        Math.PI * 2,
      );
    } else {
      ctx.rect(Math.round(crop.x), Math.round(crop.y), sourceCropWidth, sourceCropHeight);
    }
    ctx.clip();
  if (cropMaskMode.value === "blur") {
    const blurPx = getMaskBlurRadius(cropMaskBlurIntensity.value);
    if (canvasFilterSupported.value) {
      ctx.filter = `blur(${blurPx}px)`;
      ctx.drawImage(image.value, 0, 0, output.width, output.height);
    } else {
      const softwareScale = Math.min(1, 1600 / Math.max(output.width, output.height));
      const blurCanvas = document.createElement("canvas");
      blurCanvas.width = Math.max(1, Math.round(output.width * softwareScale));
      blurCanvas.height = Math.max(1, Math.round(output.height * softwareScale));
      const blurCtx = blurCanvas.getContext("2d");
      if (blurCtx) {
        blurCtx.drawImage(image.value, 0, 0, output.width, output.height, 0, 0, blurCanvas.width, blurCanvas.height);
        blurImageInPlace(blurCtx, blurCanvas.width, blurCanvas.height, blurPx * softwareScale);
        ctx.drawImage(blurCanvas, 0, 0, blurCanvas.width, blurCanvas.height, 0, 0, output.width, output.height);
      }
    }
  } else {
      ctx.fillStyle = hexToRgba(cropMaskColor.value, 1 - cropMaskTransparency.value / 100);
      ctx.fillRect(Math.round(crop.x), Math.round(crop.y), sourceCropWidth, sourceCropHeight);
    }
    ctx.restore();
  }
  if (!isMasking) {
    drawBorderLayers(
      ctx,
      contentX,
      contentY,
      sourceCropWidth,
      sourceCropHeight,
      { x: nextCommittedCornerRadiusX, y: nextCommittedCornerRadiusY },
      cropMode.value === "circle" && !cropCircleAspectLocked.value ? "ellipse" : "round",
      preservedBorderLayers,
    );
  }
  const dataUrl = output.toDataURL("image/png");
  const nextImage = new Image();
  nextImage.decoding = "async";
  nextImage.src = dataUrl;
  await nextImage.decode();
  const cropViewportAnchor = !isMasking
    ? {
        x: canvasMetrics.imageX + (requestedCropX + selectedCropWidth / 2) * canvasMetrics.scale,
        y: canvasMetrics.imageY + (requestedCropY + selectedCropHeight / 2) * canvasMetrics.scale,
      }
    : undefined;
  setCurrentImage(nextImage, dataUrl, `${imageName.value.replace(/\.[^.]+$/, "") || "cutmypic"}-${isMasking ? "masked" : "cropped"}.png`, {
    resetCornerRadius: false,
    preserveZoom: true,
    preservePreviewScale: true,
    preserveCropMode: true,
    viewportAnchor: cropViewportAnchor,
  });
  committedCropMaskBlurIntensity.value = nextCropMaskBlurIntensity;
  committedCropMaskTransparency.value = nextCropMaskTransparency;
  committedCropMaskColor.value = nextCropMaskColor;
  if (isMasking) cropFunctionMode.value = "trimmer";
  restoreBorderState(preservedBorderState);
  restoreBorderLayers(preservedBorderLayers);
  committedCornerRadius.value = nextCommittedCornerRadius;
  committedCornerRadiusX.value = nextCommittedCornerRadiusX;
  committedCornerRadiusY.value = nextCommittedCornerRadiusY;
  if (!isMasking) {
    committedImageShape.value =
      cropMode.value === "circle" || cropMode.value === "heart" || cropMode.value === "star" ? cropMode.value : "rect";
  }
  settings.radius = nextCommittedCornerRadius;
  applyCropMode();
  committedCrop.x = crop.x;
  committedCrop.y = crop.y;
  committedCrop.width = crop.width;
  committedCrop.height = crop.height;
  activeTool.value = "crop";
  committedResizeBox.x = 0;
  committedResizeBox.y = 0;
  committedResizeBox.width = imageSize.width;
  committedResizeBox.height = imageSize.height;
  cropMaskMode.value = nextCropMaskMode;
  cropMaskBlurIntensity.value = nextCropMaskBlurIntensity;
  cropMaskTransparency.value = nextCropMaskTransparency;
  cropMaskColor.value = nextCropMaskColor;
  pushHistoryEntry({
    kind: "crop",
    workflowStep: isMasking
      ? undefined
      : {
          kind: "crop",
          mode: cropMode.value,
          selectedPresetId: selectedCropPresetId.value,
          aspect: selectedCropWidth / Math.max(1, selectedCropHeight),
        },
    label: cropHistoryLabel,
    isMaskEdit: isMasking,
    dataUrl,
    name: `${imageName.value.replace(/\.[^.]+$/, "") || "cutmypic"}-${isMasking ? "masked" : "cropped"}.png`,
    width: imageSize.width,
    height: imageSize.height,
    zoom: settings.zoom,
    previewSnapshot: capturePreviewSnapshot(),
    committedCornerRadius: committedCornerRadius.value,
    committedCornerRadiusX: committedCornerRadiusX.value,
    committedCornerRadiusY: committedCornerRadiusY.value,
    committedFilter: committedFilter.value,
    committedAdjustments: { ...committedAdjustments },
    committedText: { ...committedText },
    committedBorderState: { ...committedBorderState },
    committedBorderLayers: committedBorderLayers.map((layer) => ({ ...layer })),
    committedShadowState: { ...committedShadowState },
    committedCropMaskColor: isMasking && nextCropMaskMode === "color" ? nextCropMaskColor : undefined,
  });
  await nextTick();
  render();
  // Applying a pending crop should not change the panel visibility. In
  // particular, the mobile apply button can be used while the crop panel is
  // closed, so keep it closed after the image is committed.
  if (!cropPanelWasOpen) activePanelTool.value = null;
  if (workflowAwaitingCrop.value && !isMasking) await runPendingWorkflowSteps();
  else if (shouldShowCropTutorialTools) await showCropTutorialTools();
}

async function applyRoundedCornersToImage() {
  if (!image.value || radius.value < 1) return;
  const preservedBorderState = { ...committedBorderState };
  const preservedBorderLayers = committedBorderLayers.map((layer) => ({ ...layer }));
  const oldBorderInset = getBorderLayerTotalThickness(preservedBorderLayers);
  const sourceContentInset = getBorderLayerContentInset(preservedBorderLayers);
  const source = createBorderlessSourceCanvas(image.value, imageSize.width, imageSize.height, sourceContentInset);
  const borderPadding = Math.ceil(oldBorderInset);
  const contentWidth = source.width;
  const contentHeight = source.height;
  const output = document.createElement("canvas");
  output.width = Math.round(contentWidth + borderPadding * 2);
  output.height = Math.round(contentHeight + borderPadding * 2);
  const ctx = output.getContext("2d");
  if (!ctx) return;

  const cornerRadius = Math.min(radius.value, contentWidth / 2, contentHeight / 2);
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(borderPadding, borderPadding, contentWidth, contentHeight, cornerRadius);
  ctx.clip();
  ctx.drawImage(source, 0, 0, contentWidth, contentHeight, borderPadding, borderPadding, contentWidth, contentHeight);
  ctx.restore();
  drawBorderLayers(ctx, borderPadding, borderPadding, contentWidth, contentHeight, cornerRadius, "round", preservedBorderLayers);

  const dataUrl = output.toDataURL("image/png");
  const nextImage = new Image();
  nextImage.decoding = "async";
  nextImage.src = dataUrl;
  await nextImage.decode();
  committedCornerRadius.value = cornerRadius;
  committedCornerRadiusX.value = cornerRadius;
  committedCornerRadiusY.value = cornerRadius;
  setCurrentImage(nextImage, dataUrl, `${imageName.value.replace(/\.[^.]+$/, "") || "cutmypic"}-rounded.png`, {
    resetCornerRadius: false,
    preserveZoom: true,
    preservePreviewScale: true,
  });
  restoreBorderState(preservedBorderState);
  restoreBorderLayers(preservedBorderLayers);
  pushHistoryEntry({
    kind: "corners",
    workflowStep: {
      kind: "corners",
      radiusRatio: cornerRadius / Math.max(1, Math.min(contentWidth, contentHeight)),
    },
    dataUrl,
    name: `${imageName.value.replace(/\.[^.]+$/, "") || "cutmypic"}-rounded.png`,
    width: imageSize.width,
    height: imageSize.height,
    zoom: settings.zoom,
    previewSnapshot: capturePreviewSnapshot(),
    committedCornerRadius: committedCornerRadius.value,
    committedCornerRadiusX: committedCornerRadiusX.value,
    committedCornerRadiusY: committedCornerRadiusY.value,
    committedFilter: committedFilter.value,
    committedAdjustments: { ...committedAdjustments },
    committedText: { ...committedText },
    committedBorderState: { ...committedBorderState },
    committedBorderLayers: committedBorderLayers.map((layer) => ({ ...layer })),
    committedShadowState: { ...committedShadowState },
  });
  activeTool.value = "corners";
  settings.radius = cornerRadius;
  await nextTick();
  render();
}

async function applyBorderToImage() {
  if (!image.value || settings.borderThickness < 1) return;
  await finalizeBorderApply();
}

function hasCommittedShadowEffect() {
  return committedShadowState.shadowSize > 0 || committedShadowState.shadowDarkness > 0;
}

async function finalizeBorderApply() {
  const source = image.value;
  if (!source) return;
  const sourceMinDimension = Math.max(1, Math.min(imageSize.width, imageSize.height));
  const appliedBorderState = {
    borderThickness: settings.borderThickness,
    borderColor: settings.borderColor,
    borderUseGradient: settings.borderUseGradient,
    borderGradientType: settings.borderGradientType,
    borderGradientAngle: settings.borderGradientAngle,
    borderGradientStart: settings.borderGradientStart,
    borderGradientEnd: settings.borderGradientEnd,
  };
  const nextBorderLayers = [...committedBorderLayers.map((layer) => ({ ...layer })), { ...appliedBorderState }];
  const borderPadding = Math.ceil(settings.borderThickness);
  const output = document.createElement("canvas");
  output.width = imageSize.width + borderPadding * 2;
  output.height = imageSize.height + borderPadding * 2;
  const ctx = output.getContext("2d");
  if (!ctx) return;

  ctx.clearRect(0, 0, output.width, output.height);
  const borderOverlay = document.createElement("canvas");
  borderOverlay.width = output.width;
  borderOverlay.height = output.height;
  const borderOverlayCtx = borderOverlay.getContext("2d");
  if (!borderOverlayCtx) return;
  const useAppleMobileBorderGeometry = isMobileViewport.value && isAppleMobileDevice();
  const drawCommittedBorder = (targetCtx: CanvasRenderingContext2D) => {
    if (useAppleMobileBorderGeometry && committedImageShape.value !== "heart" && committedImageShape.value !== "star") {
      const isCircleBorder = committedImageShape.value === "circle";
      drawBorder(
        targetCtx,
        borderPadding,
        borderPadding,
        imageSize.width,
        imageSize.height,
        isCircleBorder
          ? { x: imageSize.width / 2, y: imageSize.height / 2 }
          : { x: committedCornerRadiusX.value, y: committedCornerRadiusY.value },
        settings.borderThickness,
        isCircleBorder ? "ellipse" : "round",
        appliedBorderState,
      );
    } else if (usesAlphaSilhouetteBorder()) {
      drawAlphaBorder(
        targetCtx,
        source,
        borderPadding,
        borderPadding,
        imageSize.width,
        imageSize.height,
        settings.borderThickness,
        appliedBorderState,
      );
    } else {
      drawBorder(
        targetCtx,
        borderPadding,
        borderPadding,
        imageSize.width,
        imageSize.height,
        0,
        settings.borderThickness,
        "round",
        appliedBorderState,
      );
    }
  };
  drawCommittedBorder(borderOverlayCtx);
  if (useAppleMobileBorderGeometry) {
    drawCommittedBorder(ctx);
  } else {
    ctx.drawImage(borderOverlay, 0, 0);
  }
  ctx.drawImage(source, borderPadding, borderPadding, imageSize.width, imageSize.height);
  repositionProtectedOverlayMask(output.width, output.height, borderPadding, borderPadding);
  mergeProtectedOverlayMask(borderOverlay);

  const dataUrl = output.toDataURL("image/png");
  const nextImage = new Image();
  nextImage.decoding = "async";
  nextImage.src = dataUrl;
  await nextImage.decode();
  setCurrentImage(nextImage, dataUrl, `${imageName.value.replace(/\.[^.]+$/, "") || "cutmypic"}-bordered.png`, {
    resetCornerRadius: false,
    preserveZoom: true,
    preservePreviewScale: true,
  });
  if (isMobileViewport.value) {
    previewBaseScale.value = Math.min(previewBaseScale.value, getPreviewBaseScale(imageSize.width, imageSize.height));
  }
  settings.borderThickness = appliedBorderState.borderThickness;
  settings.borderColor = appliedBorderState.borderColor;
  settings.borderUseGradient = appliedBorderState.borderUseGradient;
  settings.borderGradientType = appliedBorderState.borderGradientType;
  settings.borderGradientAngle = appliedBorderState.borderGradientAngle;
  settings.borderGradientStart = appliedBorderState.borderGradientStart;
  settings.borderGradientEnd = appliedBorderState.borderGradientEnd;
  committedBorderState.borderThickness = appliedBorderState.borderThickness;
  committedBorderState.borderColor = appliedBorderState.borderColor;
  committedBorderState.borderUseGradient = appliedBorderState.borderUseGradient;
  committedBorderState.borderGradientType = appliedBorderState.borderGradientType;
  committedBorderState.borderGradientAngle = appliedBorderState.borderGradientAngle;
  committedBorderState.borderGradientStart = appliedBorderState.borderGradientStart;
  committedBorderState.borderGradientEnd = appliedBorderState.borderGradientEnd;
  restoreBorderLayers(nextBorderLayers);
  pushHistoryEntry({
    kind: "border",
    workflowStep: {
      kind: "border",
      thicknessRatio: appliedBorderState.borderThickness / sourceMinDimension,
      state: {
        borderColor: appliedBorderState.borderColor,
        borderUseGradient: appliedBorderState.borderUseGradient,
        borderGradientType: appliedBorderState.borderGradientType,
        borderGradientAngle: appliedBorderState.borderGradientAngle,
        borderGradientStart: appliedBorderState.borderGradientStart,
        borderGradientEnd: appliedBorderState.borderGradientEnd,
      },
    },
    dataUrl,
    name: `${imageName.value.replace(/\.[^.]+$/, "") || "cutmypic"}-bordered.png`,
    width: output.width,
    height: output.height,
    zoom: settings.zoom,
    previewSnapshot: capturePreviewSnapshot(),
    committedCornerRadius: committedCornerRadius.value,
    committedCornerRadiusX: committedCornerRadiusX.value,
    committedCornerRadiusY: committedCornerRadiusY.value,
    committedFilter: committedFilter.value,
    committedAdjustments: { ...committedAdjustments },
    committedText: { ...committedText },
    committedBorderState: { ...committedBorderState },
    committedBorderLayers: committedBorderLayers.map((layer) => ({ ...layer })),
    committedShadowState: { ...committedShadowState },
  });
  activeTool.value = "border";
  await nextTick();
  render();
}


async function applyShadowToImage() {
  if (!image.value) return;
  const sourceMinDimension = Math.max(1, Math.min(imageSize.width, imageSize.height));
  const darkness = Math.max(0, Math.round(settings.shadowDarkness));
  const size = Math.max(0, Math.round(settings.shadowSize));
  const direction = Number.isFinite(settings.shadowDirection) ? settings.shadowDirection : 45;
  const color = settings.shadowColor;
  const padding = Math.max(0, Math.ceil(size * 2.5));
  const radians = (direction * Math.PI) / 180;
  const offsetX = Math.round(Math.cos(radians) * size * 0.35);
  const offsetY = Math.round(Math.sin(radians) * size * 0.35);
  const output = document.createElement("canvas");
  output.width = imageSize.width + padding * 2 + Math.abs(offsetX);
  output.height = imageSize.height + padding * 2 + Math.abs(offsetY);
  const ctx = output.getContext("2d");
  if (!ctx) return;

  const drawX = padding + Math.max(0, offsetX);
  const drawY = padding + Math.max(0, offsetY);
  drawShadowedImage(ctx, image.value, drawX, drawY, imageSize.width, imageSize.height, {
    darkness,
    size,
    color,
    direction: settings.shadowDirection,
  });
  repositionProtectedOverlayMask(output.width, output.height, drawX, drawY);

  const dataUrl = output.toDataURL("image/png");
  const nextImage = new Image();
  nextImage.decoding = "async";
  nextImage.src = dataUrl;
  await nextImage.decode();
  committedShadowState.contentWidth = imageSize.width;
  committedShadowState.contentHeight = imageSize.height;
  committedShadowState.contentX = drawX;
  committedShadowState.contentY = drawY;
  setCurrentImage(nextImage, dataUrl, `${imageName.value.replace(/\.[^.]+$/, "") || "cutmypic"}-shadow.png`, {
    resetCornerRadius: false,
    preserveZoom: true,
    preservePreviewScale: true,
  });
  previewBaseScale.value = Math.min(previewBaseScale.value, getPreviewBaseScale(imageSize.width, imageSize.height));
  settings.shadowDarkness = darkness;
  settings.shadowSize = size;
  settings.shadowColor = color;
  settings.shadowDirection = direction;
  committedShadowState.shadowDarkness = darkness;
  committedShadowState.shadowSize = size;
  committedShadowState.shadowColor = color;
  committedShadowState.shadowDirection = direction;
  pushHistoryEntry({
    kind: "shadow",
    workflowStep: {
      kind: "shadow",
      darkness,
      sizeRatio: size / sourceMinDimension,
      color,
      direction,
    },
    dataUrl,
    name: `${imageName.value.replace(/\.[^.]+$/, "") || "cutmypic"}-shadow.png`,
    width: imageSize.width,
    height: imageSize.height,
    zoom: settings.zoom,
    previewSnapshot: capturePreviewSnapshot(),
    committedCornerRadius: committedCornerRadius.value,
    committedCornerRadiusX: committedCornerRadiusX.value,
    committedCornerRadiusY: committedCornerRadiusY.value,
    committedFilter: committedFilter.value,
    committedAdjustments: { ...committedAdjustments },
    committedText: { ...committedText },
    committedBorderState: { ...committedBorderState },
    committedBorderLayers: committedBorderLayers.map((layer) => ({ ...layer })),
    committedShadowState: { ...committedShadowState },
  });
  activeTool.value = "shadow";
  await nextTick();
  render();
}

function getFilterCssValue(filterId: FilterId) {
  switch (filterId) {
    case "bw":
      return "grayscale(1) contrast(1.08)";
    case "sepia":
      return "sepia(1) contrast(1.02) saturate(1.08)";
    case "oldfashioned":
      return "sepia(0.72) saturate(1.32) contrast(1.08) hue-rotate(-12deg)";
    case "vintage":
      return "sepia(0.42) saturate(1.24) contrast(0.92) brightness(1.06)";
    case "cool":
      return "hue-rotate(18deg) saturate(1.18) contrast(1.04)";
    case "warm":
      return "hue-rotate(-14deg) saturate(1.18) contrast(1.04)";
    case "cinematic":
      return "contrast(1.08) saturate(0.88) brightness(0.98) sepia(0.12)";
    case "matte":
      return "contrast(0.9) brightness(1.08) saturate(0.84)";
    case "cyanotype":
      return "sepia(0.85) saturate(1.6) hue-rotate(185deg) contrast(1.05)";
    case "glow":
      return "sepia(0.2) saturate(1.35) contrast(1.02) brightness(1.08) hue-rotate(-8deg)";
    case "greenify":
      return "sepia(1) saturate(1.7) hue-rotate(52deg) brightness(0.98) contrast(1.04)";
    case "redify":
      return "sepia(1) saturate(1.55) hue-rotate(-32deg) brightness(0.99) contrast(1.05)";
    case "bluify":
      return "sepia(1) saturate(1.8) hue-rotate(158deg) brightness(0.98) contrast(1.06)";
    case "purplify":
      return "sepia(1) saturate(1.95) hue-rotate(210deg) brightness(0.99) contrast(1.06)";
    case "brownify":
      return "sepia(0.96) saturate(1.22) hue-rotate(-8deg) brightness(0.94) contrast(1.04)";
    default:
      return "none";
  }
}

function getAdjustmentCssValue() {
  const contrast = settings.contrast / 100;
  const brightness = settings.brightness / 100;
  const saturation = settings.saturation / 100;
  const balance = settings.colorBalance;
  return `contrast(${contrast}) brightness(${brightness}) saturate(${saturation}) hue-rotate(${balance}deg)`;
}

function getSharpenSoftenStrength() {
  return Math.max(
    -100,
    Math.min(
      100,
      Math.round(settings.sharpen) -
        Math.round(committedAdjustments.sharpen) -
        (Math.round(settings.soften) - Math.round(committedAdjustments.soften)),
    ),
  );
}

function applySharpenSoftenInPlace(ctx: CanvasRenderingContext2D, width: number, height: number, strength: number) {
  const amount = Math.max(-100, Math.min(100, Math.round(strength)));
  if (!amount) return;
  const source = ctx.getImageData(0, 0, width, height);
  const target = ctx.createImageData(width, height);
  const src = source.data;
  const dst = target.data;
  const blend = amount > 0 ? (amount / 100) * 4 : (Math.abs(amount) / 100) * 0.95;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let blurR = 0;
      let blurG = 0;
      let blurB = 0;
      for (let ky = -1; ky <= 1; ky += 1) {
        for (let kx = -1; kx <= 1; kx += 1) {
          const sampleX = Math.min(width - 1, Math.max(0, x + kx));
          const sampleY = Math.min(height - 1, Math.max(0, y + ky));
          const sampleIndex = (sampleY * width + sampleX) * 4;
          blurR += src[sampleIndex];
          blurG += src[sampleIndex + 1];
          blurB += src[sampleIndex + 2];
        }
      }
      blurR /= 9;
      blurG /= 9;
      blurB /= 9;
      const index = (y * width + x) * 4;
      const originalR = src[index];
      const originalG = src[index + 1];
      const originalB = src[index + 2];
      if (amount > 0) {
        dst[index] = Math.max(0, Math.min(255, Math.round(originalR + (originalR - blurR) * blend)));
        dst[index + 1] = Math.max(0, Math.min(255, Math.round(originalG + (originalG - blurG) * blend)));
        dst[index + 2] = Math.max(0, Math.min(255, Math.round(originalB + (originalB - blurB) * blend)));
      } else {
        dst[index] = Math.round(originalR + (blurR - originalR) * blend);
        dst[index + 1] = Math.round(originalG + (blurG - originalG) * blend);
        dst[index + 2] = Math.round(originalB + (blurB - originalB) * blend);
      }
      dst[index + 3] = src[index + 3];
    }
  }
  ctx.putImageData(target, 0, 0);
}

function applyTransparencyInPlace(ctx: CanvasRenderingContext2D, width: number, height: number, transparency: number) {
  const amount = clamp(transparency, 0, 100) / 100;
  if (amount <= 0) return;
  const imageData = ctx.getImageData(0, 0, width, height);
  const pixels = imageData.data;
  const opacity = 1 - amount;
  for (let index = 3; index < pixels.length; index += 4) {
    pixels[index] = Math.round(pixels[index] * opacity);
  }
  ctx.putImageData(imageData, 0, 0);
}

function blurImageInPlace(ctx: CanvasRenderingContext2D, width: number, height: number, radius: number) {
  const amount = Math.max(0, Math.round(radius));
  if (!amount) return;
  const source = ctx.getImageData(0, 0, width, height);
  const target = ctx.createImageData(width, height);
  const src = source.data;
  const dst = target.data;
  const horizontal = new Uint8ClampedArray(src.length);
  const kernelSize = amount * 2 + 1;

  for (let y = 0; y < height; y += 1) {
    let red = 0;
    let green = 0;
    let blue = 0;
    let alpha = 0;
    for (let sampleOffset = -amount; sampleOffset <= amount; sampleOffset += 1) {
      const sampleX = clamp(sampleOffset, 0, width - 1);
      const sampleIndex = (y * width + sampleX) * 4;
      red += src[sampleIndex];
      green += src[sampleIndex + 1];
      blue += src[sampleIndex + 2];
      alpha += src[sampleIndex + 3];
    }
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      horizontal[index] = Math.round(red / kernelSize);
      horizontal[index + 1] = Math.round(green / kernelSize);
      horizontal[index + 2] = Math.round(blue / kernelSize);
      horizontal[index + 3] = Math.round(alpha / kernelSize);

      const removeX = clamp(x - amount, 0, width - 1);
      const addX = clamp(x + amount + 1, 0, width - 1);
      const removeIndex = (y * width + removeX) * 4;
      const addIndex = (y * width + addX) * 4;
      red += src[addIndex] - src[removeIndex];
      green += src[addIndex + 1] - src[removeIndex + 1];
      blue += src[addIndex + 2] - src[removeIndex + 2];
      alpha += src[addIndex + 3] - src[removeIndex + 3];
    }
  }

  for (let x = 0; x < width; x += 1) {
    let red = 0;
    let green = 0;
    let blue = 0;
    let alpha = 0;
    for (let sampleOffset = -amount; sampleOffset <= amount; sampleOffset += 1) {
      const sampleY = clamp(sampleOffset, 0, height - 1);
      const sampleIndex = (sampleY * width + x) * 4;
      red += horizontal[sampleIndex];
      green += horizontal[sampleIndex + 1];
      blue += horizontal[sampleIndex + 2];
      alpha += horizontal[sampleIndex + 3];
    }
    for (let y = 0; y < height; y += 1) {
      const index = (y * width + x) * 4;
      dst[index] = Math.round(red / kernelSize);
      dst[index + 1] = Math.round(green / kernelSize);
      dst[index + 2] = Math.round(blue / kernelSize);
      dst[index + 3] = Math.round(alpha / kernelSize);

      const removeY = clamp(y - amount, 0, height - 1);
      const addY = clamp(y + amount + 1, 0, height - 1);
      const removeIndex = (removeY * width + x) * 4;
      const addIndex = (addY * width + x) * 4;
      red += horizontal[addIndex] - horizontal[removeIndex];
      green += horizontal[addIndex + 1] - horizontal[removeIndex + 1];
      blue += horizontal[addIndex + 2] - horizontal[removeIndex + 2];
      alpha += horizontal[addIndex + 3] - horizontal[removeIndex + 3];
    }
  }
  ctx.putImageData(target, 0, 0);
}

function drawAdjustedImagePreservingCommittedBorder(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  filterValue: string,
  sourceWidth: number,
  sourceHeight: number,
  drawWidth: number,
  drawHeight: number,
  angleDegrees = 0,
  flipHorizontal = false,
  flipVertical = false,
  preserveCommittedBorder = true,
  sharpenSoftenStrength = 0,
  colorReplaceState: ColorReplaceState | null = null,
  transparency = 0,
  transparencyIncludeBorder = false,
) {
  const sharpenStrength = sharpenSoftenStrength;
  const processingWidth = sharpenStrength ? Math.max(1, Math.round(sourceWidth)) : Math.max(1, Math.round(drawWidth));
  const processingHeight = sharpenStrength ? Math.max(1, Math.round(sourceHeight)) : Math.max(1, Math.round(drawHeight));
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = processingWidth;
  tempCanvas.height = processingHeight;
  const tempCtx = tempCanvas.getContext("2d");
  if (!tempCtx) return;
  drawFilteredImagePreservingCommittedBorder(
    tempCtx,
    source,
    filterValue,
    sourceWidth,
    sourceHeight,
    processingWidth,
    processingHeight,
    preserveCommittedBorder,
    colorReplaceState,
  );
  if (sharpenStrength) {
    applySharpenSoftenInPlace(tempCtx, tempCanvas.width, tempCanvas.height, sharpenStrength);
    if (preserveCommittedBorder) {
      restoreProtectedOverlayPixels(tempCtx, source, sourceWidth, sourceHeight, processingWidth, processingHeight);
    }
  }
  if (transparency > 0) {
    applyTransparencyInPlace(tempCtx, tempCanvas.width, tempCanvas.height, transparency);
    if (preserveCommittedBorder && !transparencyIncludeBorder && getCommittedBorderProtectionThickness() > 0) {
      restoreProtectedOverlayPixels(tempCtx, source, sourceWidth, sourceHeight, processingWidth, processingHeight);
    }
  }
  let displayCanvas = tempCanvas;
  if (processingWidth !== Math.round(drawWidth) || processingHeight !== Math.round(drawHeight)) {
    displayCanvas = document.createElement("canvas");
    displayCanvas.width = Math.max(1, Math.round(drawWidth));
    displayCanvas.height = Math.max(1, Math.round(drawHeight));
    displayCanvas.getContext("2d")?.drawImage(tempCanvas, 0, 0, displayCanvas.width, displayCanvas.height);
  }
  if (angleDegrees || flipHorizontal || flipVertical) {
    const transformedCanvas = document.createElement("canvas");
    const transformedBounds = getResizeTransformBounds(drawWidth, drawHeight, angleDegrees);
    transformedCanvas.width = Math.max(1, Math.round(transformedBounds.width));
    transformedCanvas.height = Math.max(1, Math.round(transformedBounds.height));
    const transformedCtx = transformedCanvas.getContext("2d");
    if (transformedCtx) {
      transformedCtx.clearRect(0, 0, transformedCanvas.width, transformedCanvas.height);
      drawTransformedImage(
        transformedCtx,
        displayCanvas,
        displayCanvas.width,
        displayCanvas.height,
        displayCanvas.width,
        displayCanvas.height,
        angleDegrees,
        flipHorizontal,
        flipVertical,
      );
      ctx.drawImage(transformedCanvas, 0, 0);
      return;
    }
  }
  ctx.drawImage(displayCanvas, 0, 0, Math.max(1, Math.round(drawWidth)), Math.max(1, Math.round(drawHeight)));
}

function getPreviewCssValue() {
  const filterValue = activeTool.value === "filters" && filterPending.value ? getFilterCssValue(settings.filter) : "none";
  const adjustmentValue =
    activeTool.value === "adjustments" && adjustmentsPending.value
      ? getAdjustmentCssValue()
      : "none";
  return `${filterValue === "none" ? "" : `${filterValue} `}${adjustmentValue === "none" ? "" : adjustmentValue}`.trim() || "none";
}

function getPreviewColorReplaceState() {
  return activeTool.value === "filters" && filterPending.value && settings.filter === "colorreplace"
    ? { ...currentColorReplaceState.value }
    : null;
}

function getCommittedCssValue() {
  return "none";
}

function mergeProtectedOverlayMask(overlay: HTMLCanvasElement) {
  const nextMask = document.createElement("canvas");
  nextMask.width = overlay.width;
  nextMask.height = overlay.height;
  const ctx = nextMask.getContext("2d");
  if (!ctx) return;
  if (protectedOverlayMask) ctx.drawImage(protectedOverlayMask, 0, 0, nextMask.width, nextMask.height);
  ctx.drawImage(overlay, 0, 0);
  protectedOverlayMask = nextMask;
}

function repositionProtectedOverlayMask(width: number, height: number, offsetX: number, offsetY: number) {
  if (!protectedOverlayMask) return;
  const nextMask = document.createElement("canvas");
  nextMask.width = Math.max(1, Math.round(width));
  nextMask.height = Math.max(1, Math.round(height));
  nextMask.getContext("2d")?.drawImage(protectedOverlayMask, offsetX, offsetY);
  protectedOverlayMask = nextMask;
}

async function restoreProtectedOverlayMask(dataUrl?: string) {
  if (!dataUrl) {
    protectedOverlayMask = null;
    return;
  }
  const maskImage = new Image();
  maskImage.decoding = "async";
  maskImage.src = dataUrl;
  await maskImage.decode();
  const mask = document.createElement("canvas");
  mask.width = maskImage.naturalWidth;
  mask.height = maskImage.naturalHeight;
  mask.getContext("2d")?.drawImage(maskImage, 0, 0);
  protectedOverlayMask = mask;
}

function restoreProtectedOverlayPixels(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  drawWidth: number,
  drawHeight: number,
) {
  if (!protectedOverlayMask) return;
  const overlay = document.createElement("canvas");
  overlay.width = Math.max(1, Math.round(drawWidth));
  overlay.height = Math.max(1, Math.round(drawHeight));
  const overlayCtx = overlay.getContext("2d");
  if (!overlayCtx) return;
  overlayCtx.drawImage(source, 0, 0, sourceWidth, sourceHeight, 0, 0, overlay.width, overlay.height);
  overlayCtx.globalCompositeOperation = "destination-in";
  overlayCtx.drawImage(protectedOverlayMask, 0, 0, protectedOverlayMask.width, protectedOverlayMask.height, 0, 0, overlay.width, overlay.height);
  overlayCtx.globalCompositeOperation = "source-over";
  ctx.drawImage(overlay, 0, 0, drawWidth, drawHeight);
}

function parseColorReplaceHex(hex: string) {
  const normalized = hex.replace("#", "");
  const value = Number.parseInt(normalized, 16);
  if (normalized.length !== 6 || Number.isNaN(value)) return { r: 0, g: 0, b: 0 };
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function applyColorReplaceInPlace(ctx: CanvasRenderingContext2D, width: number, height: number, state: ColorReplaceState) {
  const imageData = ctx.getImageData(0, 0, width, height);
  const pixels = imageData.data;
  const target = parseColorReplaceHex(state.targetColor);
  const replacement = parseColorReplaceHex(state.replacementColor);
  const normalizedStrength = clamp(state.strength, 0, 100) / 100;
  const normalizedSmoothness = clamp(state.smoothness ?? 0, 0, 100) / 100;
  const maximumColorDistance = Math.sqrt(255 * 255 * 3);
  const colorTolerance = normalizedStrength * maximumColorDistance;
  const colorFeather = normalizedSmoothness * maximumColorDistance * 0.15;
  const alphaTolerance = normalizedStrength * 255;
  const alphaFeather = normalizedSmoothness * 255 * 0.15;

  const featheredMatch = (distance: number, tolerance: number, feather: number) => {
    if (distance <= tolerance) return 1;
    if (feather <= 0 || distance >= tolerance + feather) return 0;
    const progress = (distance - tolerance) / feather;
    const easedProgress = progress * progress * (3 - 2 * progress);
    return 1 - easedProgress;
  };

  for (let index = 0; index < pixels.length; index += 4) {
    const alpha = pixels[index + 3];
    let blend = 0;
    if (state.targetTransparent) {
      blend = featheredMatch(alpha, alphaTolerance, alphaFeather);
    } else if (alpha > 0) {
      const redDistance = pixels[index] - target.r;
      const greenDistance = pixels[index + 1] - target.g;
      const blueDistance = pixels[index + 2] - target.b;
      const colorDistance = Math.sqrt(redDistance * redDistance + greenDistance * greenDistance + blueDistance * blueDistance);
      blend = featheredMatch(colorDistance, colorTolerance, colorFeather);
    }
    if (blend <= 0) continue;

    if (state.replacementTransparent) {
      const nextAlpha = Math.round(alpha * (1 - blend));
      pixels[index + 3] = nextAlpha;
      if (nextAlpha === 0) {
        pixels[index] = 0;
        pixels[index + 1] = 0;
        pixels[index + 2] = 0;
      }
    } else {
      pixels[index] = Math.round(pixels[index] + (replacement.r - pixels[index]) * blend);
      pixels[index + 1] = Math.round(pixels[index + 1] + (replacement.g - pixels[index + 1]) * blend);
      pixels[index + 2] = Math.round(pixels[index + 2] + (replacement.b - pixels[index + 2]) * blend);
      pixels[index + 3] = state.targetTransparent ? Math.round(alpha + (255 - alpha) * blend) : alpha;
    }
  }
  ctx.putImageData(imageData, 0, 0);
}

function drawFilteredImagePreservingCommittedBorder(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  filterValue: string,
  sourceWidth: number,
  sourceHeight: number,
  drawWidth: number,
  drawHeight: number,
  preserveCommittedBorder = true,
  colorReplaceState: ColorReplaceState | null = null,
) {
  const normalizedFilter = filterValue.trim() || "none";
  const borderThickness = preserveCommittedBorder ? getCommittedBorderProtectionThickness() : 0;
  const scaleX = drawWidth / Math.max(1, sourceWidth);
  const scaleY = drawHeight / Math.max(1, sourceHeight);
  const scaledBorderThickness = borderThickness * Math.min(scaleX, scaleY);
  const scaledBorderRadiusX = preserveCommittedBorder ? committedCornerRadiusX.value * scaleX : 0;
  const scaledBorderRadiusY = preserveCommittedBorder ? committedCornerRadiusY.value * scaleY : 0;
  if (!colorReplaceState) {
    ctx.drawImage(source, 0, 0, sourceWidth, sourceHeight, 0, 0, drawWidth, drawHeight);
  }
  if (normalizedFilter === "none" && !colorReplaceState) return;

  let softwareFilteredCanvas: HTMLCanvasElement | null = null;
  if (!canvasFilterSupported.value || colorReplaceState) {
    softwareFilteredCanvas = document.createElement("canvas");
    softwareFilteredCanvas.width = Math.max(1, Math.round(drawWidth));
    softwareFilteredCanvas.height = Math.max(1, Math.round(drawHeight));
    const softwareCtx = softwareFilteredCanvas.getContext("2d");
    if (softwareCtx) {
      softwareCtx.drawImage(source, 0, 0, sourceWidth, sourceHeight, 0, 0, softwareFilteredCanvas.width, softwareFilteredCanvas.height);
      if (normalizedFilter !== "none") {
        applyCssColorFilterInPlace(softwareCtx, softwareFilteredCanvas.width, softwareFilteredCanvas.height, normalizedFilter);
      }
      if (colorReplaceState) {
        applyColorReplaceInPlace(softwareCtx, softwareFilteredCanvas.width, softwareFilteredCanvas.height, colorReplaceState);
      }
    } else {
      softwareFilteredCanvas = null;
    }
  }

  // A filter should affect the pixels that are already present, not create a
  // new geometric mask. In particular, circle crops are represented by
  // transparent pixels outside the circle; clipping the filtered pass to
  // persisted corner radii can reintroduce a circle when no border is being
  // protected. Preserve the source alpha directly in that case.
  if (borderThickness <= 0 && scaledBorderRadiusX <= 0 && scaledBorderRadiusY <= 0) {
    if (colorReplaceState && softwareFilteredCanvas) {
      ctx.drawImage(softwareFilteredCanvas, 0, 0, drawWidth, drawHeight);
      restoreProtectedOverlayPixels(ctx, source, sourceWidth, sourceHeight, drawWidth, drawHeight);
      return;
    }
    ctx.save();
    ctx.globalCompositeOperation = "source-atop";
    if (softwareFilteredCanvas) ctx.drawImage(softwareFilteredCanvas, 0, 0, drawWidth, drawHeight);
    else {
      ctx.filter = normalizedFilter;
      ctx.drawImage(source, 0, 0, sourceWidth, sourceHeight, 0, 0, drawWidth, drawHeight);
    }
    ctx.filter = "none";
    ctx.restore();
    return;
  }

  ctx.save();
  ctx.beginPath();
  if (scaledBorderThickness > 0) {
    const inset = scaledBorderThickness;
    addRoundedRectPath(
      ctx,
      inset,
      inset,
      Math.max(0, drawWidth - inset * 2),
      Math.max(0, drawHeight - inset * 2),
      Math.max(0, scaledBorderRadiusX),
      Math.max(0, scaledBorderRadiusY),
    );
  } else {
    addRoundedRectPath(ctx, 0, 0, drawWidth, drawHeight, Math.max(0, scaledBorderRadiusX), Math.max(0, scaledBorderRadiusY));
  }
  ctx.clip();
  if (softwareFilteredCanvas) ctx.drawImage(softwareFilteredCanvas, 0, 0, drawWidth, drawHeight);
  else {
    ctx.filter = normalizedFilter;
    ctx.drawImage(source, 0, 0, sourceWidth, sourceHeight, 0, 0, drawWidth, drawHeight);
  }
  ctx.filter = "none";
  ctx.restore();
  restoreProtectedOverlayPixels(ctx, source, sourceWidth, sourceHeight, drawWidth, drawHeight);
}

function applyCssColorFilterInPlace(ctx: CanvasRenderingContext2D, width: number, height: number, filterValue: string) {
  const operations = [...filterValue.matchAll(/([a-z-]+)\(([^)]+)\)/g)].map((match) => ({
    name: match[1],
    value: Number.parseFloat(match[2]),
  }));
  if (!operations.length) return;
  const imageData = ctx.getImageData(0, 0, width, height);
  const pixels = imageData.data;
  const limit = (value: number) => Math.max(0, Math.min(255, value));
  for (let index = 0; index < pixels.length; index += 4) {
    let red = pixels[index];
    let green = pixels[index + 1];
    let blue = pixels[index + 2];
    for (const operation of operations) {
      const amount = Number.isFinite(operation.value) ? operation.value : 0;
      if (operation.name === "brightness") {
        red *= amount; green *= amount; blue *= amount;
      } else if (operation.name === "contrast") {
        red = (red - 128) * amount + 128; green = (green - 128) * amount + 128; blue = (blue - 128) * amount + 128;
      } else if (operation.name === "grayscale") {
        const gray = red * 0.2126 + green * 0.7152 + blue * 0.0722;
        red += (gray - red) * amount; green += (gray - green) * amount; blue += (gray - blue) * amount;
      } else if (operation.name === "sepia") {
        const nextRed = red * 0.393 + green * 0.769 + blue * 0.189;
        const nextGreen = red * 0.349 + green * 0.686 + blue * 0.168;
        const nextBlue = red * 0.272 + green * 0.534 + blue * 0.131;
        red += (nextRed - red) * amount; green += (nextGreen - green) * amount; blue += (nextBlue - blue) * amount;
      } else if (operation.name === "saturate") {
        const nextRed = (0.213 + 0.787 * amount) * red + (0.715 - 0.715 * amount) * green + (0.072 - 0.072 * amount) * blue;
        const nextGreen = (0.213 - 0.213 * amount) * red + (0.715 + 0.285 * amount) * green + (0.072 - 0.072 * amount) * blue;
        const nextBlue = (0.213 - 0.213 * amount) * red + (0.715 - 0.715 * amount) * green + (0.072 + 0.928 * amount) * blue;
        red = nextRed; green = nextGreen; blue = nextBlue;
      } else if (operation.name === "hue-rotate") {
        const angle = amount * Math.PI / 180;
        const cosine = Math.cos(angle);
        const sine = Math.sin(angle);
        const nextRed = (0.213 + cosine * 0.787 - sine * 0.213) * red + (0.715 - cosine * 0.715 - sine * 0.715) * green + (0.072 - cosine * 0.072 + sine * 0.928) * blue;
        const nextGreen = (0.213 - cosine * 0.213 + sine * 0.143) * red + (0.715 + cosine * 0.285 + sine * 0.14) * green + (0.072 - cosine * 0.072 - sine * 0.283) * blue;
        const nextBlue = (0.213 - cosine * 0.213 - sine * 0.787) * red + (0.715 - cosine * 0.715 + sine * 0.715) * green + (0.072 + cosine * 0.928 + sine * 0.072) * blue;
        red = nextRed; green = nextGreen; blue = nextBlue;
      }
      red = limit(red); green = limit(green); blue = limit(blue);
    }
    pixels[index] = Math.round(red);
    pixels[index + 1] = Math.round(green);
    pixels[index + 2] = Math.round(blue);
  }
  ctx.putImageData(imageData, 0, 0);
}

async function applyFilterToImage() {
  if (!image.value || activeFilter.value === "none") return;
  const appliedFilter = activeFilter.value;
  const appliedColorReplace = { ...currentColorReplaceState.value };
  const preservedBorderState = { ...committedBorderState };
  const preservedBorderLayers = committedBorderLayers.map((layer) => ({ ...layer }));
  const output = document.createElement("canvas");
  output.width = imageSize.width;
  output.height = imageSize.height;
  const ctx = output.getContext("2d");
  if (!ctx) return;

  drawFilteredImagePreservingCommittedBorder(
    ctx,
    image.value,
    getPreviewCssValue(),
    imageSize.width,
    imageSize.height,
    output.width,
    output.height,
    true,
    appliedFilter === "colorreplace" ? { ...currentColorReplaceState.value } : null,
  );

  const dataUrl = output.toDataURL("image/png");
  const nextImage = new Image();
  nextImage.decoding = "async";
  nextImage.src = dataUrl;
  await nextImage.decode();
  committedFilter.value = appliedFilter;
  if (appliedFilter === "colorreplace") Object.assign(committedColorReplace, appliedColorReplace);
  setCurrentImage(nextImage, dataUrl, `${imageName.value.replace(/\.[^.]+$/, "") || "cutmypic"}-${activeFilter.value}.png`, {
    resetCornerRadius: false,
    preserveZoom: true,
    preservePreviewScale: true,
  });
  Object.assign(committedBorderState, preservedBorderState);
  restoreBorderLayers(preservedBorderLayers);
  committedFilter.value = appliedFilter;
  if (appliedFilter === "colorreplace") {
    Object.assign(committedColorReplace, appliedColorReplace);
    settings.colorReplaceTarget = appliedColorReplace.targetColor;
    settings.colorReplaceReplacement = appliedColorReplace.replacementColor;
    settings.colorReplaceStrength = appliedColorReplace.strength;
    settings.colorReplaceSmoothness = appliedColorReplace.smoothness;
    settings.colorReplaceTargetTransparent = appliedColorReplace.targetTransparent;
    settings.colorReplaceReplacementTransparent = appliedColorReplace.replacementTransparent;
  }
  pushHistoryEntry({
    kind: "filter",
    workflowStep: {
      kind: "filter",
      filter: appliedFilter,
      colorReplace: appliedFilter === "colorreplace" ? appliedColorReplace : undefined,
    },
    label: `${t("filters")} - ${t(filterTranslationKeys[appliedFilter])}`,
    dataUrl,
    name: `${imageName.value.replace(/\.[^.]+$/, "") || "cutmypic"}-${appliedFilter}.png`,
    width: imageSize.width,
    height: imageSize.height,
    zoom: settings.zoom,
    previewSnapshot: capturePreviewSnapshot(),
    committedCornerRadius: committedCornerRadius.value,
    committedCornerRadiusX: committedCornerRadiusX.value,
    committedCornerRadiusY: committedCornerRadiusY.value,
    committedFilter: committedFilter.value,
    committedColorReplace: { ...committedColorReplace },
    committedAdjustments: { ...committedAdjustments },
    committedText: { ...committedText },
    committedBorderState: { ...committedBorderState },
    committedBorderLayers: committedBorderLayers.map((layer) => ({ ...layer })),
    committedShadowState: { ...committedShadowState },
  });
  suppressFilterHistory = true;
  settings.filter = appliedFilter;
  suppressFilterHistory = false;
  activeTool.value = "filters";
  await nextTick();
  render();
}

function drawPriceTagStamp(
  ctx: CanvasRenderingContext2D,
  text: string,
  centerX: number,
  centerY: number,
  size: number,
  preset: StampPreset,
  placementMetrics?: { width?: number; height?: number; fontSize?: number; lineWidth?: number },
) {
  const width = placementMetrics?.width ?? Math.max(size * 2.35, 118);
  const height = placementMetrics?.height ?? Math.max(size * 0.9, 44);
  const notch = height * 0.5;
  const radius = Math.min(12, height * 0.22);
  const left = centerX - width / 2;
  const top = centerY - height / 2;
  ctx.save();
  ctx.shadowColor = "rgb(0 0 0 / 0.28)";
  ctx.shadowBlur = 10;
  ctx.shadowOffsetY = 4;
  ctx.beginPath();
  ctx.moveTo(left + notch, top);
  ctx.lineTo(left + width - radius, top);
  ctx.quadraticCurveTo(left + width, top, left + width, top + radius);
  ctx.lineTo(left + width, top + height - radius);
  ctx.quadraticCurveTo(left + width, top + height, left + width - radius, top + height);
  ctx.lineTo(left + notch, top + height);
  ctx.lineTo(left, top + height / 2);
  ctx.closePath();
  ctx.fillStyle = preset.backgroundColor ?? preset.color;
  ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.lineWidth = placementMetrics?.lineWidth ?? Math.max(2, size * 0.045);
  ctx.strokeStyle = preset.borderColor ?? "rgb(255 255 255 / 0.82)";
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(left + notch * 0.72, top + height / 2, Math.max(4, height * 0.09), 0, Math.PI * 2);
  ctx.fillStyle = "rgb(255 255 255 / 0.9)";
  ctx.fill();
  const labelFontSize = placementMetrics?.fontSize ?? Math.max(13, Math.round(size * 0.34));
  ctx.font = `900 ${labelFontSize}px Inter, ui-sans-serif, system-ui, sans-serif`;
  ctx.fillStyle = preset.textColor ?? "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, left + notch + (width - notch) / 2, top + height / 2);
  ctx.restore();
}

function drawStampContent(
  ctx: CanvasRenderingContext2D,
  text: string,
  centerX: number,
  centerY: number,
  size: number,
  preset: StampPreset | null,
  placementMetrics?: { width?: number; height?: number; fontSize?: number; lineWidth?: number },
) {
  if (preset?.kind === "price-tag") {
    drawPriceTagStamp(ctx, text, centerX, centerY, size, preset, placementMetrics);
    return;
  }
  ctx.save();
  ctx.font = `${Math.max(10, Math.round(size))}px Inter, ui-sans-serif, system-ui, sans-serif`;
  ctx.fillStyle = textState.color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.shadowColor = "rgb(0 0 0 / 0.35)";
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 2;
  ctx.fillText(text, centerX, centerY);
  ctx.restore();
}

async function applyTextToImage(historyLabel?: string) {
  if (!image.value) return;
  if (!textState.content.trim() && !(textPanelMode.value === "stamp" && selectedStampPreset.value)) return;
  const appliedPanelMode = textPanelMode.value;
  const appliedStampLabel = selectedStampLabel.value;
  const sourceMinDimension = Math.max(1, Math.min(imageSize.width, imageSize.height));
  const nextText = {
    content: textState.content,
    color: textState.color,
    size: textState.size,
    rotation: textState.rotation,
    x: textState.x,
    y: textState.y,
    shadowSize: textState.shadowSize,
    shadowDarkness: textState.shadowDarkness,
    shadowColor: textState.shadowColor,
  };
  const output = document.createElement("canvas");
  output.width = imageSize.width;
  output.height = imageSize.height;
  const ctx = output.getContext("2d");
  if (!ctx) return;
  const textOverlay = document.createElement("canvas");
  textOverlay.width = output.width;
  textOverlay.height = output.height;
  const overlayCtx = textOverlay.getContext("2d");
  if (!overlayCtx) return;

  ctx.drawImage(image.value, 0, 0, output.width, output.height);
  const fontSize = Math.max(10, Math.round(nextText.size));
  const margin = Math.max(12, Math.round(fontSize * 0.45));
  const centerX = clamp((nextText.x / 100) * output.width, margin, output.width - margin);
  const box = measureTextBox(fontSize, nextText.content.trim(), Math.max(40, output.width - margin * 2));
  const topY = clamp((nextText.y / 100) * output.height, margin, output.height - margin) - box.height + box.ascent;
  if (textPanelMode.value === "stamp") {
    const placementMetrics = pendingStampPlacementMetrics;
    const stampCenterX = placementMetrics?.centerX ?? centerX;
    const stampCenterY = placementMetrics?.centerY ?? topY + box.height / 2;
    overlayCtx.save();
    overlayCtx.translate(stampCenterX, stampCenterY);
    overlayCtx.rotate((nextText.rotation * Math.PI) / 180);
    drawStampContent(overlayCtx, nextText.content.trim(), 0, 0, fontSize, selectedStampPreset.value, placementMetrics ?? undefined);
    overlayCtx.restore();
    pendingStampPlacementMetrics = null;
  } else {
    overlayCtx.save();
    overlayCtx.font = `400 ${fontSize}px Inter, ui-sans-serif, system-ui, sans-serif`;
    overlayCtx.fillStyle = nextText.color;
    overlayCtx.textAlign = "center";
    overlayCtx.textBaseline = "top";
    overlayCtx.shadowColor = hexToRgba(nextText.shadowColor, nextText.shadowDarkness / 100);
    overlayCtx.shadowBlur = nextText.shadowSize;
    overlayCtx.shadowOffsetY = 2;
    overlayCtx.translate(centerX + textCommitOffsetX(), topY + box.height / 2);
    overlayCtx.rotate((nextText.rotation * Math.PI) / 180);
    drawCenteredWrappedText(overlayCtx, nextText.content.trim(), 0, -box.height / 2, fontSize, Math.max(40, output.width - margin * 2));
    overlayCtx.restore();
  }
  ctx.drawImage(textOverlay, 0, 0);
  mergeProtectedOverlayMask(textOverlay);

  const dataUrl = output.toDataURL("image/png");
  const nextImage = new Image();
  nextImage.decoding = "async";
  nextImage.src = dataUrl;
  await nextImage.decode();
  committedText.content = nextText.content;
  committedText.color = nextText.color;
  committedText.size = nextText.size;
  committedText.rotation = nextText.rotation;
  committedText.x = nextText.x;
  committedText.y = nextText.y;
  committedText.shadowSize = nextText.shadowSize;
  committedText.shadowDarkness = nextText.shadowDarkness;
  committedText.shadowColor = nextText.shadowColor;
  clearTextInputs();
  setCurrentImage(nextImage, dataUrl, `${imageName.value.replace(/\.[^.]+$/, "") || "cutmypic"}-text.png`, {
    resetCornerRadius: false,
    preserveZoom: true,
    preservePreviewScale: true,
    preserveText: true,
  });
  pushHistoryEntry({
    kind: "text",
    workflowStep: {
      kind: "text",
      panelMode: appliedPanelMode,
      stampLabel: appliedStampLabel,
      content: nextText.content,
      color: nextText.color,
      sizeRatio: nextText.size / sourceMinDimension,
      rotation: nextText.rotation,
      x: nextText.x,
      y: nextText.y,
      shadowSizeRatio: nextText.shadowSize / sourceMinDimension,
      shadowDarkness: nextText.shadowDarkness,
      shadowColor: nextText.shadowColor,
    },
    label: historyLabel,
    dataUrl,
    name: `${imageName.value.replace(/\.[^.]+$/, "") || "cutmypic"}-text.png`,
    width: imageSize.width,
    height: imageSize.height,
    zoom: settings.zoom,
    previewSnapshot: capturePreviewSnapshot(),
    committedCornerRadius: committedCornerRadius.value,
    committedCornerRadiusX: committedCornerRadiusX.value,
    committedCornerRadiusY: committedCornerRadiusY.value,
    committedFilter: committedFilter.value,
    committedAdjustments: { ...committedAdjustments },
    committedText: { ...committedText },
    committedBorderState: { ...committedBorderState },
    committedBorderLayers: committedBorderLayers.map((layer) => ({ ...layer })),
    committedShadowState: { ...committedShadowState },
  });
  activeTool.value = "text";
  await nextTick();
  render();
}

function adjustmentHistoryLabel(state: {
  contrast: number;
  brightness: number;
  saturation: number;
  colorBalance: number;
  sharpen: number;
  soften: number;
  transparency: number;
  transparencyIncludeBorder: boolean;
}) {
  const changedAdjustments = [
    state.contrast !== 100 ? t("contrast") : null,
    state.brightness !== 100 ? t("brightness") : null,
    state.colorBalance !== 0 ? t("colorBalance") : null,
    state.saturation !== 100 ? t("saturation") : null,
    state.sharpen !== 0 ? t("sharpen") : null,
    state.soften !== 0 ? t("soften") : null,
    state.transparency !== 0 ? t("transparency") : null,
  ].filter((label): label is string => label !== null);

  return changedAdjustments.length
    ? `${t("adjustments")} - ${changedAdjustments.join(", ")}`
    : t("adjustments");
}

async function applyAdjustmentsToImage() {
  if (!image.value) return;
  const appliedAdjustmentState = {
    contrast: settings.contrast,
    brightness: settings.brightness,
    saturation: settings.saturation,
    colorBalance: settings.colorBalance,
    sharpen: settings.sharpen,
    soften: settings.soften,
    transparency: settings.transparency,
    transparencyIncludeBorder: settings.transparencyIncludeBorder,
  };
  const appliedSharpenSoftenStrength = getSharpenSoftenStrength();
  const outputName = `${imageName.value.replace(/\.[^.]+$/, "") || "cutmypic"}-adjusted.png`;
  const preservedBorderState = { ...committedBorderState };
  const preservedBorderLayers = committedBorderLayers.map((layer) => ({ ...layer }));
  const output = document.createElement("canvas");
  output.width = imageSize.width;
  output.height = imageSize.height;
  const ctx = output.getContext("2d");
  if (!ctx) return;

  drawAdjustedImagePreservingCommittedBorder(
    ctx,
    image.value,
    getAdjustmentCssValue(),
    imageSize.width,
    imageSize.height,
    output.width,
    output.height,
    0,
    false,
    false,
    true,
    appliedSharpenSoftenStrength,
    null,
    appliedAdjustmentState.transparency,
    appliedAdjustmentState.transparencyIncludeBorder,
  );

  const dataUrl = output.toDataURL("image/png");
  const nextImage = new Image();
  nextImage.decoding = "async";
  nextImage.src = dataUrl;
  await nextImage.decode();
  replaceCurrentImagePixels(nextImage, dataUrl, outputName, output);
  Object.assign(committedBorderState, preservedBorderState);
  restoreBorderLayers(preservedBorderLayers);
  settings.contrast = 100;
  settings.brightness = 100;
  settings.saturation = 100;
  settings.colorBalance = 0;
  settings.sharpen = 0;
  settings.soften = 0;
  settings.transparency = 0;
  settings.transparencyIncludeBorder = false;
  committedAdjustments.contrast = 100;
  committedAdjustments.brightness = 100;
  committedAdjustments.saturation = 100;
  committedAdjustments.colorBalance = 0;
  committedAdjustments.sharpen = 0;
  committedAdjustments.soften = 0;
  committedAdjustments.transparency = 0;
  committedAdjustments.transparencyIncludeBorder = false;
  pushHistoryEntry({
    kind: "adjustments",
    label: adjustmentHistoryLabel(appliedAdjustmentState),
    workflowStep: { kind: "adjustments", ...appliedAdjustmentState },
    dataUrl,
    name: outputName,
    width: imageSize.width,
    height: imageSize.height,
    zoom: settings.zoom,
    previewSnapshot: capturePreviewSnapshot(),
    committedCornerRadius: committedCornerRadius.value,
    committedCornerRadiusX: committedCornerRadiusX.value,
    committedCornerRadiusY: committedCornerRadiusY.value,
    committedFilter: committedFilter.value,
    committedAdjustments: { ...committedAdjustments },
    committedText: { ...committedText },
    committedBorderState: { ...committedBorderState },
    committedBorderLayers: committedBorderLayers.map((layer) => ({ ...layer })),
    committedShadowState: { ...committedShadowState },
  });
  activeTool.value = "adjustments";
  await nextTick();
  render();
}

function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  centerX: number,
  topY: number,
  maxWidth: number,
  lineHeight: number,
) {
  const words = text.split(/\s+/).filter(Boolean);
  if (!words.length) return;
  const lines: string[] = [];
  let current = words[0];
  for (let i = 1; i < words.length; i += 1) {
    const testLine = `${current} ${words[i]}`;
    if (ctx.measureText(testLine).width <= maxWidth) current = testLine;
    else {
      lines.push(current);
      current = words[i];
    }
  }
  lines.push(current);

  lines.forEach((line, index) => {
    ctx.fillText(line, centerX, topY + index * lineHeight);
  });
}

function drawCenteredWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  centerX: number,
  topY: number,
  fontSize: number,
  maxWidth: number,
) {
  const { lines, lineHeight } = measureTextBox(fontSize, text, maxWidth);
  if (!lines.length) return;
  const startY = topY;
  lines.forEach((line, index) => {
    ctx.fillText(line, centerX, startY + index * lineHeight);
  });
}

function drawTextOverlay(
  ctx: CanvasRenderingContext2D,
  originX: number,
  originY: number,
  width: number,
  height: number,
  scale = 1,
) {
  const text = textState.content.trim();
  if (!text && !(textPanelMode.value === "stamp" && selectedStampPreset.value)) return;
  const fontSize = Math.max(10, Math.round(textState.size * scale));
  const margin = Math.max(12, Math.round(fontSize * 0.45));
  ctx.save();
  ctx.font = `400 ${fontSize}px Inter, ui-sans-serif, system-ui, sans-serif`;
  ctx.fillStyle = textState.color;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.shadowColor = hexToRgba(textState.shadowColor, textState.shadowDarkness / 100);
  ctx.shadowBlur = textState.shadowSize * scale;
  ctx.shadowOffsetY = 2;
  const centerX = originX + clamp((textState.x / 100) * width, margin, width - margin);
  const box = measureTextBox(fontSize, text, Math.max(40, width - margin * 2));
  const topY = originY + clamp((textState.y / 100) * height, margin, height - margin) - box.height + box.ascent;
  ctx.translate(centerX, topY + box.height / 2);
  ctx.rotate((textState.rotation * Math.PI) / 180);
  if (textPanelMode.value === "stamp") drawStampContent(ctx, text, 0, 0, fontSize, selectedStampPreset.value);
  else drawCenteredWrappedText(ctx, text, 0, -box.height / 2, fontSize, Math.max(40, width - margin * 2));
  ctx.restore();
}

async function applyResizeToImage() {
  if (!image.value) return;
  const sourceWidth = imageSize.width;
  const sourceHeight = imageSize.height;
  const nextWidth = Math.max(1, Math.round(resizeBox.width));
  const nextHeight = Math.max(1, Math.round(resizeBox.height));
  const outputBounds = getResizeTransformBounds(nextWidth, nextHeight, resizeRotateDegrees.value);
  const output = document.createElement("canvas");
  output.width = outputBounds.width;
  output.height = outputBounds.height;
  const ctx = output.getContext("2d");
  if (!ctx) return;

  drawAdjustedImagePreservingCommittedBorder(
    ctx,
    image.value,
    "none",
    imageSize.width,
    imageSize.height,
    nextWidth,
    nextHeight,
    resizeRotateDegrees.value,
    resizeFlipHorizontal.value,
    resizeFlipVertical.value,
    false,
  );

  const dataUrl = output.toDataURL("image/png");
  const nextImage = new Image();
  nextImage.decoding = "async";
  nextImage.src = dataUrl;
  await nextImage.decode();
  setCurrentImage(nextImage, dataUrl, `${imageName.value.replace(/\.[^.]+$/, "") || "cutmypic"}-resized.png`, { resetCornerRadius: false, preservePreviewScale: true });
  const scaleX = nextWidth / Math.max(1, sourceWidth);
  const scaleY = nextHeight / Math.max(1, sourceHeight);
  committedCornerRadiusX.value = Math.max(0, committedCornerRadiusX.value * scaleX);
  committedCornerRadiusY.value = Math.max(0, committedCornerRadiusY.value * scaleY);
  committedCornerRadius.value = Math.min(committedCornerRadiusX.value, committedCornerRadiusY.value);
  settings.radius = committedCornerRadius.value;
  committedResizeRotateDegrees.value = resizeRotateDegrees.value;
  committedResizeFlipHorizontal.value = resizeFlipHorizontal.value;
  committedResizeFlipVertical.value = resizeFlipVertical.value;
  pushHistoryEntry({
    kind: "resize",
    workflowStep: {
      kind: "resize",
      width: nextWidth,
      height: nextHeight,
      rotation: resizeRotateDegrees.value,
      flipHorizontal: resizeFlipHorizontal.value,
      flipVertical: resizeFlipVertical.value,
    },
    dataUrl,
    name: `${imageName.value.replace(/\.[^.]+$/, "") || "cutmypic"}-resized.png`,
    width: output.width,
    height: output.height,
    zoom: settings.zoom,
    previewSnapshot: capturePreviewSnapshot(),
    committedCornerRadius: committedCornerRadius.value,
    committedCornerRadiusX: committedCornerRadiusX.value,
    committedCornerRadiusY: committedCornerRadiusY.value,
    committedFilter: committedFilter.value,
    committedAdjustments: { ...committedAdjustments },
    committedText: { ...committedText },
    committedBorderState: { ...committedBorderState },
    committedBorderLayers: committedBorderLayers.map((layer) => ({ ...layer })),
    committedShadowState: { ...committedShadowState },
    committedResizeRotateDegrees: committedResizeRotateDegrees.value,
    committedResizeFlipHorizontal: committedResizeFlipHorizontal.value,
    committedResizeFlipVertical: committedResizeFlipVertical.value,
  });
  committedResizeBox.x = 0;
  committedResizeBox.y = 0;
  committedResizeBox.width = outputBounds.width;
  committedResizeBox.height = outputBounds.height;
  resizeBox.x = 0;
  resizeBox.y = 0;
  resizeBox.width = outputBounds.width;
  resizeBox.height = outputBounds.height;
  resizePanelMode.value = "resize";
  resizeRotateDegrees.value = 0;
  resizeFlipHorizontal.value = false;
  resizeFlipVertical.value = false;
  activeTool.value = "resize";
  await nextTick();
  render();
}

function startResizeDrag(event: PointerEvent, mode: DragMode) {
  if (!hasImage.value) return;
  const target = event.currentTarget as HTMLElement;
  target.setPointerCapture(event.pointerId);
  resizePointer = {
    pointerId: event.pointerId,
    captureTarget: target,
    mode,
    startX: event.clientX,
    startY: event.clientY,
    startBox: { ...resizeBox },
  };
}

function onResizePointerMove(event: PointerEvent) {
  if (!resizePointer || event.pointerId !== resizePointer.pointerId) return;
  const dx = (event.clientX - resizePointer.startX) / canvasMetrics.scale;
  const dy = (event.clientY - resizePointer.startY) / canvasMetrics.scale;
  const next = resizeResizeBox(resizePointer.startBox, resizePointer.mode, dx, dy);
  resizeBox.x = next.x;
  resizeBox.y = next.y;
  resizeBox.width = next.width;
  resizeBox.height = next.height;
}

function endResizeDrag(event: PointerEvent) {
  finishResizeDrag(event.pointerId);
}

function finishResizeDrag(pointerId?: number) {
  if (!resizePointer || (pointerId !== undefined && resizePointer.pointerId !== pointerId)) return;
  const { captureTarget, pointerId: activePointerId } = resizePointer;
  resizePointer = null;
  if (captureTarget.hasPointerCapture?.(activePointerId)) {
    captureTarget.releasePointerCapture(activePointerId);
  }
}

function resizeResizeBox(start: Crop, mode: DragMode, dx: number, dy: number): Crop {
  const minSize = 1;
  const maxSize = resizeMaxSize();
  let x = start.x;
  let y = start.y;
  let width = start.width;
  let height = start.height;
  const aspect = start.width / Math.max(1, start.height);

  if (mode === "move") {
    x = Math.round(start.x + dx);
    y = Math.round(start.y + dy);
    return { x, y, width, height };
  }

  const freeform = resizeMode.value === "freeform";
  if (mode.includes("e")) width = clamp(start.width + dx, minSize, maxSize);
  if (mode.includes("s")) height = clamp(start.height + dy, minSize, maxSize);
  if (mode.includes("w")) {
    const right = start.x + start.width;
    x = Math.round(start.x + dx);
    width = right - x;
  }
  if (mode.includes("n")) {
    const bottom = start.y + start.height;
    y = Math.round(start.y + dy);
    height = bottom - y;
  }

  if (!freeform) {
    if (mode === "e" || mode === "w") height = width / aspect;
    else if (mode === "n" || mode === "s") width = height * aspect;
    else {
      const nextAspectWidth = height * aspect;
      const nextAspectHeight = width / aspect;
      if (Math.abs(nextAspectWidth - width) < Math.abs(nextAspectHeight - height)) {
        width = nextAspectWidth;
      } else {
        height = nextAspectHeight;
      }
    }
  }

  if (mode.includes("w")) x = start.x + start.width - width;
  if (mode.includes("n")) y = start.y + start.height - height;
  return {
    x: Math.round(clamp(x, -maxSize, maxSize)),
    y: Math.round(clamp(y, -maxSize, maxSize)),
    width: Math.round(clamp(width, minSize, maxSize)),
    height: Math.round(clamp(height, minSize, maxSize)),
  };
}

async function loadFile(file: File) {
  const supportedMimeTypes = new Set(["image/png", "image/jpeg", "image/webp", "image/gif"]);
  const hasSupportedExtension = /\.(?:png|jpe?g|webp|gif)$/i.test(file.name);
  if (!supportedMimeTypes.has(file.type.toLowerCase()) && !hasSupportedExtension) {
    showUnsupportedFileNotice();
    return;
  }

  const requestId = ++imageLoadRequestId;
  imageLoading.value = true;

  try {
    cancelWorkflowPlayback();
    exitComparisonMode(false);
    comparisonBeforeImage.value = null;
    comparisonBeforeDataUrl = "";

    const url = URL.createObjectURL(file);
    const nextImage = new Image();
    nextImage.decoding = "async";
    nextImage.src = url;
    try {
      await nextImage.decode();
    } catch {
      URL.revokeObjectURL(url);
      showUnsupportedFileNotice();
      return;
    }
    if (requestId !== imageLoadRequestId) {
      URL.revokeObjectURL(url);
      return;
    }

    dismissUnsupportedFileNotice();

    protectedOverlayMask = null;
    autoCenterEnabled.value = true;
    imagePan.x = 0;
    imagePan.y = 0;
    setCurrentImage(nextImage, url, file.name, { activateCropOnMobile: true });
    triggerDropFlash();
    setDefaultCropGeometry();
    resizeBox.x = 0;
    resizeBox.y = 0;
    resizeBox.width = imageSize.width;
    resizeBox.height = imageSize.height;
    committedResizeBox.x = 0;
    committedResizeBox.y = 0;
    committedResizeBox.width = imageSize.width;
    committedResizeBox.height = imageSize.height;
    settings.radius = 0;
    activeTool.value = "crop";
    const entry = captureImageHistoryEntry("original");
    previewHistory.value = entry ? [entry] : [];

    await nextTick();
    render();
    await nextTick();
    await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
    void startCropTutorial();
  } finally {
    if (requestId === imageLoadRequestId) imageLoading.value = false;
  }
}

function onFileInput(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) void loadFile(file);
  input.value = "";
}

function onDrop(event: DragEvent) {
  event.preventDefault();
  isDraggingFile.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file) void loadFile(file);
}

function dismissUnsupportedFileNotice() {
  unsupportedFileNoticeVisible.value = false;
  if (unsupportedFileNoticeTimer !== null) {
    window.clearTimeout(unsupportedFileNoticeTimer);
    unsupportedFileNoticeTimer = null;
  }
}

function showUnsupportedFileNotice() {
  dismissUnsupportedFileNotice();
  unsupportedFileNoticeVisible.value = true;
  unsupportedFileNoticeTimer = window.setTimeout(() => {
    unsupportedFileNoticeVisible.value = false;
    unsupportedFileNoticeTimer = null;
  }, 5000);
}

function resetCrop() {
  if (!image.value) return;
  cropMode.value = "free";
  selectedCropPresetId.value = "common-free";
  cropMaskShape.value = "free";
  freeCropAspectLocked.value = false;
  freeCropLockedAspect.value = crop.width / Math.max(1, crop.height);
  cropCircleAspectLocked.value = true;
  maskRoundedAspectLocked.value = true;
  setDefaultCropGeometry();
  settings.radius = 0;
}

function fitCropToImage() {
  crop.x = 0;
  crop.y = 0;
  crop.width = imageSize.width;
  crop.height = imageSize.height;
}

function setSquareCropGeometry() {
  if (!hasImage.value) return;
  const side = Math.min(crop.width, crop.height, imageSize.width, imageSize.height);
  const centerX = crop.x + crop.width / 2;
  const centerY = crop.y + crop.height / 2;
  Object.assign(
    crop,
    fitCropRectToImage(
      {
        x: centerX - side / 2,
        y: centerY - side / 2,
        width: side,
        height: side,
      },
      1,
    ),
  );
}

function setSquareCrop() {
  cropMode.value = "square";
  setSquareCropGeometry();
}

function setCircleCrop() {
  cropMode.value = "circle";
  cropCircleAspectLocked.value = true;
  setSquareCropGeometry();
}

function setHeartCrop() {
  cropMode.value = "heart";
  cropCircleAspectLocked.value = true;
  setSquareCropGeometry();
}

function setStarCrop() {
  cropMode.value = "star";
  cropCircleAspectLocked.value = true;
  setSquareCropGeometry();
}

function setWidescreenCrop() {
  cropMode.value = "widescreen";
  setAspectCrop(16 / 9);
}

function setAspectCrop(aspect: number) {
  if (!hasImage.value) return;
  const width = Math.min(imageSize.width - crop.x, Math.round(crop.height * aspect));
  crop.width = width;
  crop.height = Math.round(width / aspect);
  crop.y = clamp(crop.y, 0, imageSize.height - crop.height);
}

function setPortraitCrop() {
  cropMode.value = "portrait";
  setAspectCrop(4 / 5);
}

function setStoryCrop() {
  cropMode.value = "story";
  setAspectCrop(9 / 16);
}

function setPinCrop() {
  cropMode.value = "pin";
  setAspectCrop(2 / 3);
}

function setCinemaCrop() {
  cropMode.value = "cinema";
  setAspectCrop(1.91);
}

function setClassicCrop() {
  cropMode.value = "classic";
  setAspectCrop(3 / 4);
}

function setWideCrop() {
  cropMode.value = "wide";
  setAspectCrop(2.35);
}

function setHeaderCrop() {
  cropMode.value = "header";
  setAspectCrop(3);
}

function setMaskShape(shape: "free" | "circle") {
  cropMaskShape.value = shape;
  if (shape === "circle") {
    maskRoundedAspectLocked.value = true;
    Object.assign(crop, fitCropRectToImage({ ...crop }, 1));
  }
}

function setCropFunctionMode(mode: "trimmer" | "masking") {
  cropFunctionMode.value = mode;
  if (mode === "masking") cropMaskShape.value = "free";
}

function setMaskMode(mode: "blur" | "color") {
  cropMaskMode.value = mode;
}

function toggleCircleAspectLock() {
  if (cropFunctionMode.value === "masking") {
    maskRoundedAspectLocked.value = !maskRoundedAspectLocked.value;
    if (maskRoundedAspectLocked.value) setSquareCropGeometry();
  } else if (cropMode.value === "free") {
    freeCropAspectLocked.value = !freeCropAspectLocked.value;
    if (freeCropAspectLocked.value) {
      freeCropLockedAspect.value = Math.max(0.01, crop.width / Math.max(1, crop.height));
    }
  } else {
    cropCircleAspectLocked.value = !cropCircleAspectLocked.value;
    if (cropCircleAspectLocked.value) setSquareCropGeometry();
  }
  void nextTick(render);
}

function selectCropPreset(item: { id: string; action: () => void }) {
  selectedCropPresetId.value = item.id;
  item.action();
}

function activeCropAspectLock() {
  if (cropFunctionMode.value === "masking") return cropMaskShape.value === "circle" && maskRoundedAspectLocked.value ? 1 : null;
  if (workflowAwaitingCrop.value && workflowCropLockedAspect.value) return workflowCropLockedAspect.value;
  if (cropMode.value === "free" && freeCropAspectLocked.value) return freeCropLockedAspect.value;
  const isLockableShape = cropMode.value === "circle" || cropMode.value === "heart" || cropMode.value === "star";
  return cropMode.value === "square" || (isLockableShape && cropCircleAspectLocked.value)
    ? 1
    : cropMode.value === "widescreen"
      ? 16 / 9
      : cropMode.value === "portrait"
        ? 4 / 5
        : cropMode.value === "story"
          ? 9 / 16
          : cropMode.value === "pin"
            ? 2 / 3
            : cropMode.value === "cinema"
              ? 1.91
              : cropMode.value === "classic"
                ? 3 / 4
                : cropMode.value === "wide"
                  ? 2.35
                  : cropMode.value === "header"
                    ? 3
                    : null;
}

const cropPresetGroups = [
  {
    titleKey: "cropCommon",
    items: [
      { id: "common-free", labelKey: "freeCut", mode: "free", shape: "free", action: resetCrop },
      { id: "common-square", labelKey: "squareCut", mode: "square", shape: "square", action: setSquareCrop },
      { id: "common-widescreen", labelKey: "widescreenCut", mode: "widescreen", shape: "widescreen", action: setWidescreenCrop },
      { id: "common-circle", labelKey: "circleCut", mode: "circle", shape: "circle", action: setCircleCrop },
      { id: "common-heart", labelKey: "heartCut", mode: "heart", shape: "heart", action: setHeartCrop },
      { id: "common-star", labelKey: "starCut", mode: "star", shape: "star", action: setStarCrop },
    ],
  },
  {
    titleKey: "cropSocial",
    items: [
      { id: "social-instagram-post", labelKey: "instagramPost", icon: "IG", mode: "portrait", brand: "instagram", brandColor: "#E4405F", brandBorder: "#FCAF45", ratio: "4:5", size: "1080 × 1350", hintKey: "portraitFeedFormat", action: setPortraitCrop },
      { id: "social-story-reel", labelKey: "storyReel", icon: "IG", mode: "story", brand: "instagram", brandColor: "#E4405F", brandBorder: "#FCAF45", ratio: "9:16", size: "1080 × 1920", hintKey: "fullscreenVertical", action: setStoryCrop },
      { id: "social-tiktok-douyin", labelKey: "tiktokDouyin", icon: "TT", mode: "story", brand: "tiktok", brandColor: "#000000", brandBorder: "#25F4EE", ratio: "9:16", size: "1080 × 1920", hintKey: "verticalVideoCover", action: setStoryCrop },
      { id: "social-pinterest-pin", labelKey: "pinterestPin", icon: "P", mode: "pin", brand: "pinterest", brandColor: "#BD081C", brandBorder: "#ffffff", ratio: "2:3", size: "1000 × 1500", hintKey: "tallDiscoveryPin", action: setPinCrop },
      { id: "social-youtube-thumbnail", labelKey: "youtubeThumbnail", icon: "YT", mode: "widescreen", brand: "youtube", brandColor: "#FF0000", brandBorder: "#ffffff", ratio: "16:9", size: "1280 × 720", hintKey: "videoThumbnail", action: setWidescreenCrop },
      { id: "social-facebook-feed", labelKey: "facebookFeed", icon: "f", mode: "square", brand: "facebook", brandColor: "#1877F2", brandBorder: "#ffffff", ratio: "1:1", size: "1080 × 1080", hintKey: "squarePostFormat", action: setSquareCrop },
      { id: "social-facebook-cover", labelKey: "facebookCover", icon: "f", mode: "cinema", brand: "facebook", brandColor: "#1877F2", brandBorder: "#ffffff", ratio: "1.91:1", size: "1200 × 628", hintKey: "coverLinkPreview", action: setCinemaCrop },
      { id: "social-x-post", labelKey: "xPost", icon: "X", mode: "widescreen", brand: "x", brandColor: "#000000", brandBorder: "#ffffff", ratio: "16:9", size: "1600 × 900", hintKey: "landscapePostImage", action: setWidescreenCrop },
      { id: "social-x-header", labelKey: "xHeader", icon: "X", mode: "header", brand: "x", brandColor: "#000000", brandBorder: "#ffffff", ratio: "3:1", size: "1500 × 500", hintKey: "profileBanner", action: setHeaderCrop },
      { id: "social-linkedin-cover", labelKey: "linkedinCover", icon: "in", mode: "cinema", brandColor: "#0A66C2", brandBorder: "#ffffff", ratio: "1.91:1", size: "1200 × 628", hintKey: "wideSocialPreview", action: setCinemaCrop },
      { id: "social-xiaohongshu-note", labelKey: "xiaohongshuNote", icon: "RED", mode: "classic", brand: "xiaohongshu", brandColor: "#FF2442", brandBorder: "#ffffff", ratio: "3:4", size: "1080 × 1440", hintKey: "redNotePortraitNote", action: setClassicCrop },
      { id: "social-wechat-cover", labelKey: "wechatCover", icon: "WX", mode: "wide", brand: "wechat", brandColor: "#07C160", brandBorder: "#ffffff", ratio: "2.35:1", size: "900 × 383", hintKey: "articleCoverStyle", action: setWideCrop },
      { id: "social-weibo-feed", labelKey: "weiboFeed", icon: "WB", mode: "square", brand: "weibo", brandColor: "#E6162D", brandBorder: "#F5CA31", ratio: "1:1", size: "1080 × 1080", hintKey: "squareFeedImage", action: setSquareCrop },
    ],
  },
] as const;

function parseTemplateSize(size: string) {
  const match = size.match(/(\d+)\s*[×x]\s*(\d+)/i);
  if (!match) return null;
  return { width: Number(match[1]), height: Number(match[2]) };
}

function isTemplateTooLarge(item: (typeof cropPresetGroups)[number]["items"][number]) {
  if (!hasImage.value) return false;
  if (!("size" in item)) return false;
  const dimensions = parseTemplateSize(item.size);
  if (!dimensions || !imageSize.width || !imageSize.height) return false;
  return imageSize.width < dimensions.width || imageSize.height < dimensions.height;
}

function showWorkflowStatus(message: string, duration = 4500) {
  if (workflowStatusTimer != null) window.clearTimeout(workflowStatusTimer);
  workflowStatusMessage.value = message;
  if (duration > 0) {
    workflowStatusTimer = window.setTimeout(() => {
      workflowStatusMessage.value = "";
      workflowStatusTimer = null;
    }, duration);
  }
}

function cancelWorkflowPlayback() {
  if (workflowResizeWarningResolver) workflowResizeWarningResolver(false);
  workflowResizeWarningResolver = null;
  workflowResizeWarning.value = null;
  workflowApplying.value = false;
  workflowAwaitingCrop.value = false;
  workflowCropLockedAspect.value = null;
  activeWorkflowName.value = "";
  pendingWorkflowSteps.value = [];
  if (workflowStatusTimer != null) window.clearTimeout(workflowStatusTimer);
  workflowStatusTimer = null;
  workflowStatusMessage.value = "";
}

function requestWorkflowResizeConfirmation(step: Extract<WorkflowStep, { kind: "resize" }>) {
  if (step.width <= imageSize.width && step.height <= imageSize.height) return Promise.resolve(true);
  workflowResizeWarning.value = {
    sourceWidth: imageSize.width,
    sourceHeight: imageSize.height,
    targetWidth: step.width,
    targetHeight: step.height,
  };
  return new Promise<boolean>((resolve) => {
    workflowResizeWarningResolver = resolve;
  });
}

function resolveWorkflowResizeWarning(proceed: boolean) {
  const resolve = workflowResizeWarningResolver;
  workflowResizeWarningResolver = null;
  workflowResizeWarning.value = null;
  resolve?.(proceed);
  if (!proceed) cancelWorkflowPlayback();
}

async function applyWorkflowStep(step: WorkflowStep, resizeAlreadyConfirmed = false) {
  if (!image.value || step.kind === "crop") return;
  const minDimension = Math.max(1, Math.min(imageSize.width, imageSize.height));
  if (step.kind === "corners") {
    activeTool.value = "corners";
    settings.radius = Math.max(1, Math.round(step.radiusRatio * minDimension));
    await applyRoundedCornersToImage();
    return;
  }
  if (step.kind === "border") {
    activeTool.value = "border";
    settings.borderThickness = Math.max(1, Math.round(step.thicknessRatio * minDimension));
    settings.borderColor = step.state.borderColor;
    settings.borderUseGradient = step.state.borderUseGradient;
    settings.borderGradientType = step.state.borderGradientType;
    settings.borderGradientAngle = step.state.borderGradientAngle;
    settings.borderGradientStart = step.state.borderGradientStart;
    settings.borderGradientEnd = step.state.borderGradientEnd;
    await applyBorderToImage();
    return;
  }
  if (step.kind === "shadow") {
    activeTool.value = "shadow";
    settings.shadowDarkness = step.darkness;
    settings.shadowSize = Math.max(0, Math.round(step.sizeRatio * minDimension));
    settings.shadowColor = step.color;
    settings.shadowDirection = step.direction;
    await applyShadowToImage();
    return;
  }
  if (step.kind === "filter") {
    activeTool.value = "filters";
    settings.filter = step.filter;
    if (step.filter === "colorreplace" && step.colorReplace) {
      settings.colorReplaceTarget = step.colorReplace.targetColor;
      settings.colorReplaceReplacement = step.colorReplace.replacementColor;
      settings.colorReplaceStrength = step.colorReplace.strength;
      settings.colorReplaceSmoothness = step.colorReplace.smoothness ?? 0;
      settings.colorReplaceTargetTransparent = step.colorReplace.targetTransparent;
      settings.colorReplaceReplacementTransparent = step.colorReplace.replacementTransparent;
    }
    await nextTick();
    await applyFilterToImage();
    return;
  }
  if (step.kind === "adjustments") {
    activeTool.value = "adjustments";
    settings.contrast = step.contrast;
    settings.brightness = step.brightness;
    settings.saturation = step.saturation;
    settings.colorBalance = step.colorBalance;
    settings.sharpen = step.sharpen;
    settings.soften = step.soften;
    settings.transparency = step.transparency ?? 0;
    settings.transparencyIncludeBorder = step.transparencyIncludeBorder ?? false;
    await nextTick();
    await applyAdjustmentsToImage();
    return;
  }
  if (step.kind === "resize") {
    const shouldResize = resizeAlreadyConfirmed || await requestWorkflowResizeConfirmation(step);
    if (!shouldResize || !workflowApplying.value) return;
    activeTool.value = "resize";
    resizeMode.value = "constrain";
    resizeConstrainedAspect.value = step.width / Math.max(1, step.height);
    resizeBox.x = 0;
    resizeBox.y = 0;
    resizeBox.width = step.width;
    resizeBox.height = step.height;
    resizeRotateDegrees.value = step.rotation;
    resizeFlipHorizontal.value = step.flipHorizontal;
    resizeFlipVertical.value = step.flipVertical;
    await applyResizeToImage();
    return;
  }
  activeTool.value = "text";
  textPanelMode.value = step.panelMode;
  selectedStampLabel.value = step.stampLabel;
  const preset = stampPresets.find((item) => item.label === step.stampLabel);
  textState.content = step.content || preset?.content || "";
  textState.color = step.color;
  textState.size = Math.max(10, Math.round(step.sizeRatio * minDimension));
  textState.rotation = step.rotation;
  textState.x = step.x;
  textState.y = step.y;
  textState.shadowSize = Math.max(0, Math.round(step.shadowSizeRatio * minDimension));
  textState.shadowDarkness = step.shadowDarkness;
  textState.shadowColor = step.shadowColor;
  await nextTick();
  await applyTextToImage(step.panelMode === "stamp" ? `${t("stampMode")} - ${step.stampLabel || t("stampMode")}` : t("text"));
  selectedStampLabel.value = "";
}

async function runPendingWorkflowSteps() {
  const workflowNameToReport = activeWorkflowName.value;
  workflowApplying.value = true;
  workflowAwaitingCrop.value = false;
  workflowCropLockedAspect.value = null;
  showWorkflowStatus(ui("applying", { name: workflowNameToReport }), 0);
  try {
    const finalStep = pendingWorkflowSteps.value[pendingWorkflowSteps.value.length - 1];
    const finalResizeStep = finalStep?.kind === "resize" ? finalStep : null;
    if (finalResizeStep) {
      const shouldResize = await requestWorkflowResizeConfirmation(finalResizeStep);
      if (!shouldResize || !workflowApplying.value) return;
    }
    while (workflowApplying.value && pendingWorkflowSteps.value.length) {
      const step = pendingWorkflowSteps.value.shift();
      if (step) await applyWorkflowStep(step, step === finalResizeStep);
    }
    if (!workflowApplying.value) return;
    if (autoCenterEnabled.value) {
      imagePan.x = 0;
      imagePan.y = 0;
    }
    previewBaseScale.value = getPreviewBaseScale(imageSize.width, imageSize.height);
    settings.zoom = oneToOneZoomLevel.value;
    await nextTick();
    render();
    showWorkflowStatus(ui("applied", { name: workflowNameToReport, width: imageSize.width, height: imageSize.height }));
  } catch (error) {
    console.warn("Cut My Pic could not finish applying the workflow.", error);
    showWorkflowStatus(ui("failed", { name: workflowNameToReport }));
  } finally {
    workflowApplying.value = false;
    activeWorkflowName.value = "";
    pendingWorkflowSteps.value = [];
  }
}

function positionWorkflowCrop(step: Extract<WorkflowStep, { kind: "crop" }>, resizeAspect: number | null = null) {
  cropFunctionMode.value = "trimmer";
  cropMode.value = step.mode;
  selectedCropPresetId.value = step.selectedPresetId || "common-free";
  cropCircleAspectLocked.value = step.mode === "circle" || step.mode === "heart" || step.mode === "star";
  const shapeAspect = step.mode === "circle" || step.mode === "heart" || step.mode === "star" || step.mode === "square" ? 1 : null;
  const aspect = Math.max(0.01, shapeAspect ?? resizeAspect ?? step.aspect);
  freeCropAspectLocked.value = step.mode === "free" && resizeAspect !== null;
  freeCropLockedAspect.value = aspect;
  let width = imageSize.width;
  let height = width / aspect;
  if (height > imageSize.height) {
    height = imageSize.height;
    width = height * aspect;
  }
  crop.x = Math.round((imageSize.width - width) / 2);
  crop.y = Math.round((imageSize.height - height) / 2);
  crop.width = Math.max(1, Math.round(width));
  crop.height = Math.max(1, Math.round(height));
}

function formatWorkflowAspectRatio(width: number, height: number) {
  const greatestCommonDivisor = (left: number, right: number): number => right === 0 ? left : greatestCommonDivisor(right, left % right);
  const divisor = greatestCommonDivisor(Math.max(1, Math.round(width)), Math.max(1, Math.round(height)));
  const ratioWidth = Math.round(width) / divisor;
  const ratioHeight = Math.round(height) / divisor;
  if (ratioWidth <= 20 && ratioHeight <= 20) return `${ratioWidth}:${ratioHeight}`;
  return `${(width / Math.max(1, height)).toFixed(2).replace(/\.00$/, "")}:1`;
}

async function loadWorkflow(workflow: SavedWorkflow) {
  if (!hasImage.value || workflowApplying.value) return;
  closeCropTutorial();
  cancelWorkflowPlayback();
  closeHistoryPanel();
  workflowLibraryOpen.value = false;
  activeWorkflowName.value = workflow.name;
  const steps = workflow.steps.map(cloneWorkflowStep);
  const cropStep = steps.find((step): step is Extract<WorkflowStep, { kind: "crop" }> => step.kind === "crop");
  const resizeSteps = steps.filter((step): step is Extract<WorkflowStep, { kind: "resize" }> => step.kind === "resize");
  const resizeStep = resizeSteps[resizeSteps.length - 1];
  const nonCropSteps = steps.filter((step) => step.kind !== "crop" && step !== resizeStep);
  pendingWorkflowSteps.value = resizeStep ? [...nonCropSteps, resizeStep] : nonCropSteps;
  if (cropStep) {
    workflowAwaitingCrop.value = true;
    const shapeAspect = cropStep.mode === "circle" || cropStep.mode === "heart" || cropStep.mode === "star" || cropStep.mode === "square" ? 1 : null;
    workflowCropLockedAspect.value = shapeAspect ?? (resizeStep ? resizeStep.width / Math.max(1, resizeStep.height) : null);
    activeTool.value = "crop";
    activePanelTool.value = fullscreenMode.value || isMobileViewport.value ? "crop" : null;
    positionWorkflowCrop(cropStep, workflowCropLockedAspect.value);
    await nextTick();
    positionWorkflowCrop(cropStep, workflowCropLockedAspect.value);
    if (resizeStep && workflowCropLockedAspect.value) {
      const ratio = shapeAspect ? "1:1" : formatWorkflowAspectRatio(resizeStep.width, resizeStep.height);
      showWorkflowStatus(ui("cropLocked", { ratio, width: resizeStep.width, height: resizeStep.height }), 0);
    } else {
      showWorkflowStatus(ui("positionCrop", { name: workflow.name }), 0);
    }
    render();
    return;
  }
  await runPendingWorkflowSteps();
}

watch(cropMode, (nextMode) => {
  if (!hasImage.value || activeTool.value !== "crop") return;
  if (nextMode === "free") resetCrop();
  else if (nextMode === "square" || nextMode === "circle" || nextMode === "heart" || nextMode === "star") {
    if (nextMode !== "square") cropCircleAspectLocked.value = true;
    setSquareCropGeometry();
  }
  else if (nextMode === "widescreen") setWidescreenCrop();
  else if (nextMode === "portrait") setPortraitCrop();
  else if (nextMode === "story") setStoryCrop();
  else if (nextMode === "pin") setPinCrop();
  else if (nextMode === "cinema") setCinemaCrop();
  else if (nextMode === "classic") setClassicCrop();
  else if (nextMode === "wide") setWideCrop();
  else if (nextMode === "header") setHeaderCrop();
  void nextTick(render);
});

watch(
  [cropMode, cropCircleAspectLocked, () => crop.width, () => crop.height],
  ([nextMode, isLocked, width, height]) => {
    const isLockableShape = nextMode === "circle" || nextMode === "heart" || nextMode === "star";
    if (!hasImage.value || !isLockableShape || !isLocked) return;
    if (Math.abs(Number(width) - Number(height)) < 0.01) return;
    setSquareCropGeometry();
    void nextTick(render);
  },
);

watch(cropFunctionMode, (nextMode) => {
  if (!hasImage.value) return;
  if (nextMode === "masking") cropMaskShape.value = "free";
  void nextTick(render);
});

function render() {
  const canvas = canvasRef.value;
  const source = imagePreviewSource.value ?? image.value;
  const stage = stageRef.value;
  if (!canvas || !stage) return;

  const rect = stage.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  const cssWidth = Math.max(320, Math.floor(rect.width));
  const cssHeight = Math.max(320, Math.floor(rect.height));

  canvas.width = Math.floor(cssWidth * dpr);
  canvas.height = Math.floor(cssHeight * dpr);
  canvas.style.width = `${cssWidth}px`;
  canvas.style.height = `${cssHeight}px`;

  canvasMetrics.cssWidth = cssWidth;
  canvasMetrics.cssHeight = cssHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssWidth, cssHeight);
  drawViewingBackground(ctx, cssWidth, cssHeight);

  if (!source) {
    return;
  }

  const previewInset = 6;
  let scale = Math.max(0.03, previewBaseScale.value * settings.zoom);
  if (isMobileViewport.value && activeTool.value === "border" && borderPending.value && settings.borderThickness > 0) {
    const borderedWidth = imageSize.width + settings.borderThickness * 2;
    const borderedHeight = imageSize.height + settings.borderThickness * 2;
    const borderFitScale = Math.min(
      Math.max(0.03, (cssWidth - previewInset * 2) / Math.max(1, borderedWidth)),
      Math.max(0.03, (cssHeight - previewInset * 2) / Math.max(1, borderedHeight)),
    );
    scale = Math.min(scale, borderFitScale);
  }
  let drawWidth = imageSize.width * scale;
  let drawHeight = imageSize.height * scale;
  let baseX = (cssWidth - drawWidth) / 2;
  const availableHeight = Math.max(0, cssHeight - previewInset * 2);
  let baseY = isMobileViewport.value
    ? Math.max(8, (cssHeight - drawHeight) * 0.12)
    : previewInset + (availableHeight - drawHeight) / 2;
  let drawX = baseX;
  let drawY = baseY;
  let previewScale = scale;
  let visibleDrawWidth = drawWidth;
  let visibleDrawHeight = drawHeight;

  if (activeTool.value === "resize" && resizeBox.width && resizeBox.height) {
    drawWidth = resizeBox.width * scale;
    drawHeight = resizeBox.height * scale;
    const transformedBounds = getResizeTransformBounds(drawWidth, drawHeight, resizeRotateDegrees.value);
    const centerX = baseX + (resizeBox.x + resizeBox.width / 2) * scale;
    const centerY = baseY + (resizeBox.y + resizeBox.height / 2) * scale;
    drawX = centerX - transformedBounds.width / 2;
    drawY = centerY - transformedBounds.height / 2;
    visibleDrawWidth = transformedBounds.width;
    visibleDrawHeight = transformedBounds.height;
    previewScale = scale;
  }

  if (autoCenterEnabled.value) {
    imagePan.x = 0;
    imagePan.y = 0;
  } else {
    const horizontalMinimumVisible = Math.min(48, Math.max(16, visibleDrawWidth / 2));
    const verticalMinimumVisible = Math.min(48, Math.max(16, visibleDrawHeight / 2));
    imagePan.x = clamp(
      imagePan.x,
      horizontalMinimumVisible - (drawX + visibleDrawWidth),
      cssWidth - horizontalMinimumVisible - drawX,
    );
    imagePan.y = clamp(
      imagePan.y,
      verticalMinimumVisible - (drawY + visibleDrawHeight),
      cssHeight - verticalMinimumVisible - drawY,
    );
  }
  baseX += imagePan.x;
  baseY += imagePan.y;
  drawX += imagePan.x;
  drawY += imagePan.y;

  canvasMetrics.imageX = activeTool.value === "resize" ? baseX : drawX;
  canvasMetrics.imageY = activeTool.value === "resize" ? baseY : drawY;
  canvasMetrics.imageWidth = drawWidth;
  canvasMetrics.imageHeight = drawHeight;
  canvasMetrics.scale = previewScale;

  if (comparisonMode.value && comparisonBeforeImage.value) {
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(source, 0, 0, imageSize.width, imageSize.height, drawX, drawY, drawWidth, drawHeight);

    const originalEntry = previewHistory.value[0];
    if (originalEntry) {
    // Match the original's height to the edited image, but preserve its own
    // aspect ratio. Using the edited width here would stretch the original
    // whenever a crop changed the image's aspect ratio.
    const originalHeight = drawHeight;
    const originalWidth = originalHeight * (originalEntry.width / originalEntry.height);
    const originalX = drawX + (drawWidth - originalWidth) / 2;
    const originalY = drawY;
      ctx.beginPath();
      ctx.rect(0, 0, cssWidth * (comparisonPosition.value / 100), cssHeight);
      ctx.clip();
      ctx.drawImage(
        comparisonBeforeImage.value,
        0,
        0,
        originalEntry.width,
        originalEntry.height,
        originalX,
        originalY,
        originalWidth,
        originalHeight,
      );
    }
    ctx.restore();
    return;
  }

  ctx.save();
  const previewFilterValue = getPreviewCssValue();
  const previewSharpenSoftenStrength =
    activeTool.value === "adjustments" && adjustmentsPending.value
      ? getSharpenSoftenStrength()
      : 0;
  if (activeTool.value === "shadow" && shadowPending.value && (settings.shadowDarkness > 0 || settings.shadowSize > 0)) {
    const shadowPreview = createShadowPreviewCanvas(source, drawWidth, drawHeight, {
      darkness: settings.shadowDarkness,
      size: settings.shadowSize * canvasMetrics.scale,
      color: settings.shadowColor,
      direction: settings.shadowDirection,
    });
    const offsetX = (shadowPreview.width - drawWidth) / 2;
    const offsetY = (shadowPreview.height - drawHeight) / 2;
    ctx.drawImage(shadowPreview, drawX - offsetX, drawY - offsetY);
  } else if (
    activeTool.value === "crop" &&
    cropFunctionMode.value === "trimmer" &&
    committedBorderLayers.length > 0 &&
    committedImageShape.value === "rect"
  ) {
    const sourceInset = getBorderLayerContentInset();
    const borderlessSource = createBorderlessSourceCanvas(source, imageSize.width, imageSize.height, sourceInset);
    ctx.translate(drawX + sourceInset * previewScale, drawY + sourceInset * previewScale);
    drawAdjustedImagePreservingCommittedBorder(
      ctx,
      borderlessSource,
      previewFilterValue,
      borderlessSource.width,
      borderlessSource.height,
      borderlessSource.width * previewScale,
      borderlessSource.height * previewScale,
      0,
      false,
      false,
      false,
      previewSharpenSoftenStrength,
    );
    const scaledBorderLayers = committedBorderLayers.map((layer) => ({
      ...layer,
      borderThickness: layer.borderThickness * previewScale,
    }));
    drawBorderLayers(
      ctx,
      0,
      0,
      borderlessSource.width * previewScale,
      borderlessSource.height * previewScale,
      {
        x: Math.max(committedCornerRadiusX.value * previewScale, 0),
        y: Math.max(committedCornerRadiusY.value * previewScale, 0),
      },
      "round",
      scaledBorderLayers,
    );
  } else {
    ctx.translate(drawX, drawY);
    drawAdjustedImagePreservingCommittedBorder(
      ctx,
      source,
      previewFilterValue,
      imageSize.width,
      imageSize.height,
      drawWidth,
      drawHeight,
      resizeRotateDegrees.value,
      resizeFlipHorizontal.value,
      resizeFlipVertical.value,
      true,
      previewSharpenSoftenStrength,
      getPreviewColorReplaceState(),
      activeTool.value === "adjustments" && adjustmentsPending.value ? settings.transparency : 0,
      settings.transparencyIncludeBorder,
    );
  }
  ctx.filter = "none";
  ctx.restore();

  if (textPending.value && !textPositionMode.value && !stampPositionMode.value) {
    drawTextOverlay(ctx, canvasMetrics.imageX, canvasMetrics.imageY, canvasMetrics.imageWidth, canvasMetrics.imageHeight, canvasMetrics.scale);
  }

  if (cropControlsVisible.value) {
    drawCropMaskPreview(ctx);
    if (cropFunctionMode.value === "trimmer") drawCropScrim(ctx);
  } else if (cornerControlsVisible.value) {
    drawRoundedOutline(ctx);
  }

  if (borderPending.value && settings.borderThickness > 0) {
    const previewBorderThickness = Math.max(1, Math.ceil(settings.borderThickness * canvasMetrics.scale));
    if (usesAlphaSilhouetteBorder()) {
      drawAlphaBorder(
        ctx,
        source,
        canvasMetrics.imageX,
        canvasMetrics.imageY,
        canvasMetrics.imageWidth,
        canvasMetrics.imageHeight,
        previewBorderThickness,
        settings,
      );
    } else {
      drawBorder(
        ctx,
        canvasMetrics.imageX,
        canvasMetrics.imageY,
        canvasMetrics.imageWidth,
        canvasMetrics.imageHeight,
        0,
        previewBorderThickness,
        "round",
        settings,
      );
    }
  }
}

function drawCheckerboard(ctx: CanvasRenderingContext2D, width: number, height: number) {
  const tile = 18;
  const styles = theme.value === "dark" ? ["#13151a", "#191c23"] : ["#eef2f5", "#f8fafc"];
  for (let y = 0; y < height; y += tile) {
    for (let x = 0; x < width; x += tile) {
      ctx.fillStyle = styles[(x / tile + y / tile) % 2];
      ctx.fillRect(x, y, tile, tile);
    }
  }
}

function drawViewingBackground(ctx: CanvasRenderingContext2D, width: number, height: number) {
  if (viewBackgroundMode.value === "color") {
    ctx.fillStyle = viewBackgroundColor.value;
    ctx.fillRect(0, 0, width, height);
    return;
  }
  drawCheckerboard(ctx, width, height);
}

function addCropShapePath(
  ctx: CanvasRenderingContext2D,
  shape: string,
  x: number,
  y: number,
  width: number,
  height: number,
) {
  if (shape === "circle") {
    ctx.ellipse(x + width / 2, y + height / 2, width / 2, height / 2, 0, 0, Math.PI * 2);
    return;
  }

  if (shape === "heart") {
    const heartMinY = 0.11259386;
    const heartX = (value: number) => x + ((value - 0.05) / 0.9) * width;
    const heartY = (value: number) => y + ((value - heartMinY) / (0.94 - heartMinY)) * height;
    ctx.moveTo(heartX(0.5), heartY(0.94));
    ctx.bezierCurveTo(heartX(0.43), heartY(0.88), heartX(0.05), heartY(0.62), heartX(0.05), heartY(0.35));
    ctx.bezierCurveTo(heartX(0.05), heartY(0.12), heartX(0.32), heartY(0.02), heartX(0.5), heartY(0.22));
    ctx.bezierCurveTo(heartX(0.68), heartY(0.02), heartX(0.95), heartY(0.12), heartX(0.95), heartY(0.35));
    ctx.bezierCurveTo(heartX(0.95), heartY(0.62), heartX(0.57), heartY(0.88), heartX(0.5), heartY(0.94));
    ctx.closePath();
    return;
  }

  if (shape === "star") {
    const points = Array.from({ length: 10 }, (_, point) => {
      const radius = point % 2 === 0 ? 1 : 0.45;
      const angle = -Math.PI / 2 + point * Math.PI / 5;
      return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
    });
    const minX = Math.min(...points.map((point) => point.x));
    const maxX = Math.max(...points.map((point) => point.x));
    const minY = Math.min(...points.map((point) => point.y));
    const maxY = Math.max(...points.map((point) => point.y));
    points.forEach((point, index) => {
      const pointX = x + ((point.x - minX) / (maxX - minX)) * width;
      const pointY = y + ((point.y - minY) / (maxY - minY)) * height;
      if (index === 0) ctx.moveTo(pointX, pointY);
      else ctx.lineTo(pointX, pointY);
    });
    ctx.closePath();
    return;
  }

  ctx.rect(x, y, width, height);
}

function drawCropScrim(ctx: CanvasRenderingContext2D) {
  const x = canvasMetrics.imageX + crop.x * canvasMetrics.scale;
  const y = canvasMetrics.imageY + crop.y * canvasMetrics.scale;
  const w = crop.width * canvasMetrics.scale;
  const h = crop.height * canvasMetrics.scale;
  const cropRadius = activeTool.value === "crop" && cropMode.value === "circle" ? Math.min(w, h) / 2 : activeTool.value === "crop" ? 0 : renderedCornerRadius.value * canvasMetrics.scale;

  ctx.save();
  ctx.fillStyle = "rgb(127 29 29 / 0.62)";
  ctx.beginPath();
  ctx.rect(canvasMetrics.imageX, canvasMetrics.imageY, canvasMetrics.imageWidth, canvasMetrics.imageHeight);
  if (cropMode.value === "circle" || cropMode.value === "heart" || cropMode.value === "star") {
    addCropShapePath(ctx, cropMode.value, x, y, w, h);
  } else {
    ctx.roundRect(x, y, w, h, Math.max(0, cropRadius));
  }
  ctx.fill("evenodd");
  ctx.restore();
  if (borderPending.value && settings.borderThickness > 0) {
    const previewBorderThickness = Math.max(1, Math.ceil(settings.borderThickness * canvasMetrics.scale));
    drawBorder(ctx, x, y, w, h, cropRadius, previewBorderThickness);
  }
}

function drawCropMaskPreview(ctx: CanvasRenderingContext2D) {
  if (activeTool.value !== "crop" || cropFunctionMode.value !== "masking" || !image.value) return;
  const x = canvasMetrics.imageX + crop.x * canvasMetrics.scale;
  const y = canvasMetrics.imageY + crop.y * canvasMetrics.scale;
  const w = crop.width * canvasMetrics.scale;
  const h = crop.height * canvasMetrics.scale;
  if (w <= 0 || h <= 0) return;

  ctx.save();
  ctx.beginPath();
  if (cropMaskShape.value === "circle") {
    ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
  } else {
    ctx.rect(x, y, w, h);
  }
  ctx.clip();

  if (cropMaskMode.value === "blur") {
    const sourceWidth = Math.max(1, Math.round(crop.width));
    const sourceHeight = Math.max(1, Math.round(crop.height));
    const blurCanvas = document.createElement("canvas");
    const softwarePreview = !canvasFilterSupported.value;
    blurCanvas.width = softwarePreview ? Math.max(1, Math.round(w)) : sourceWidth;
    blurCanvas.height = softwarePreview ? Math.max(1, Math.round(h)) : sourceHeight;
    const blurCtx = blurCanvas.getContext("2d");
    if (blurCtx) {
      const blurPx = getMaskBlurRadius(cropMaskBlurIntensity.value);
      if (!softwarePreview) blurCtx.filter = `blur(${blurPx}px)`;
      blurCtx.drawImage(image.value, crop.x, crop.y, sourceWidth, sourceHeight, 0, 0, blurCanvas.width, blurCanvas.height);
      if (softwarePreview) {
        const previewScale = Math.min(blurCanvas.width / sourceWidth, blurCanvas.height / sourceHeight);
        blurImageInPlace(blurCtx, blurCanvas.width, blurCanvas.height, blurPx * previewScale);
      }
      ctx.filter = "none";
      ctx.drawImage(blurCanvas, 0, 0, blurCanvas.width, blurCanvas.height, x, y, w, h);
    }
  } else {
    ctx.filter = "none";
      ctx.fillStyle = hexToRgba(cropMaskColor.value, 1 - cropMaskTransparency.value / 100);
    ctx.fillRect(x, y, w, h);
  }

  ctx.restore();
}

function drawRoundedOutline(ctx: CanvasRenderingContext2D) {
  ctx.save();
  ctx.beginPath();
  ctx.rect(canvasMetrics.imageX, canvasMetrics.imageY, canvasMetrics.imageWidth, canvasMetrics.imageHeight);
  addRoundedRectPath(
    ctx,
    canvasMetrics.imageX,
    canvasMetrics.imageY,
    canvasMetrics.imageWidth,
    canvasMetrics.imageHeight,
    Math.max(0, renderedCornerRadii.value.x * canvasMetrics.scale),
    Math.max(0, renderedCornerRadii.value.y * canvasMetrics.scale),
  );
  if (cornerPending.value) {
    ctx.fillStyle = "rgb(239 68 68 / 0.32)";
    ctx.fill("evenodd");
  }
  ctx.strokeStyle = "rgb(255 255 255 / 0.72)";
  ctx.lineWidth = 2;
  ctx.setLineDash([2, 5]);
  addRoundedRectPath(
    ctx,
    canvasMetrics.imageX,
    canvasMetrics.imageY,
    canvasMetrics.imageWidth,
    canvasMetrics.imageHeight,
    Math.max(0, renderedCornerRadii.value.x * canvasMetrics.scale),
    Math.max(0, renderedCornerRadii.value.y * canvasMetrics.scale),
  );
  ctx.stroke();
  ctx.restore();
}

type BorderRenderState = {
  borderColor: string;
  borderUseGradient: boolean;
  borderGradientType: "linear" | "edge";
  borderGradientAngle: number;
  borderGradientStart: string;
  borderGradientEnd: string;
};

function createBorderStyle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  state: BorderRenderState = settings,
) {
  if (!state.borderUseGradient) return state.borderColor;
  const angle = (state.borderGradientAngle * Math.PI) / 180;
  const centerX = x + width / 2;
  const centerY = y + height / 2;
  const radius = Math.sqrt(width * width + height * height) / 2;
  const offsetX = Math.cos(angle) * radius;
  const offsetY = Math.sin(angle) * radius;
  const gradient = ctx.createLinearGradient(centerX - offsetX, centerY - offsetY, centerX + offsetX, centerY + offsetY);
  gradient.addColorStop(0, state.borderGradientStart);
  gradient.addColorStop(1, state.borderGradientEnd);
  return gradient;
}

function interpolateHexColor(start: string, end: string, progress: number) {
  const parse = (hex: string) => {
    const normalized = hex.replace("#", "");
    const value = Number.parseInt(normalized.length === 3 ? normalized.split("").map((part) => part + part).join("") : normalized, 16);
    return Number.isNaN(value) ? [0, 0, 0] : [(value >> 16) & 255, (value >> 8) & 255, value & 255];
  };
  const from = parse(start);
  const to = parse(end);
  const amount = clamp(progress, 0, 1);
  const channels = from.map((channel, index) => Math.round(channel + (to[index] - channel) * amount));
  return `rgb(${channels[0]} ${channels[1]} ${channels[2]})`;
}

function addRoundedRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radiusX: number, radiusY = radiusX) {
  const rx = Math.max(0, Math.min(radiusX, width / 2));
  const ry = Math.max(0, Math.min(radiusY, height / 2));
  if (rx <= 0 || ry <= 0) {
    ctx.rect(x, y, width, height);
    return;
  }
  ctx.moveTo(x + rx, y);
  ctx.lineTo(x + width - rx, y);
  ctx.ellipse(x + width - rx, y + ry, rx, ry, 0, -Math.PI / 2, 0);
  ctx.lineTo(x + width, y + height - ry);
  ctx.ellipse(x + width - rx, y + height - ry, rx, ry, 0, 0, Math.PI / 2);
  ctx.lineTo(x + rx, y + height);
  ctx.ellipse(x + rx, y + height - ry, rx, ry, 0, Math.PI / 2, Math.PI);
  ctx.lineTo(x, y + ry);
  ctx.ellipse(x + rx, y + ry, rx, ry, 0, Math.PI, Math.PI * 1.5);
  ctx.closePath();
}

function expandBorderRadius(radius: number, borderThickness: number) {
  return radius > 0 ? radius + Math.max(0, borderThickness) : 0;
}

function drawBorder(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  borderRadius: number | { x: number; y: number },
  borderThickness: number,
  borderShape: "round" | "ellipse" = "round",
  state: BorderRenderState = settings,
) {
  if (borderThickness <= 0) return;
  if (state.borderUseGradient && state.borderGradientType === "edge") {
    const steps = Math.max(8, Math.min(96, Math.ceil(borderThickness * 2)));
    for (let index = 0; index < steps; index += 1) {
      const progress = index / Math.max(1, steps - 1);
      const remainingThickness = borderThickness * (1 - index / steps);
      drawBorder(ctx, x, y, width, height, borderRadius, remainingThickness, borderShape, {
        ...state,
        borderUseGradient: false,
        borderColor: interpolateHexColor(state.borderGradientStart, state.borderGradientEnd, progress),
      });
    }
    return;
  }
  const seamOverlap = 0.5;
  const borderRadiusX = typeof borderRadius === "number" ? borderRadius : borderRadius.x;
  const borderRadiusY = typeof borderRadius === "number" ? borderRadius : borderRadius.y;
  const outerX = x - borderThickness - seamOverlap;
  const outerY = y - borderThickness - seamOverlap;
  const outerWidth = Math.max(0, width + borderThickness * 2 + seamOverlap * 2);
  const outerHeight = Math.max(0, height + borderThickness * 2 + seamOverlap * 2);
  const innerX = x + seamOverlap;
  const innerY = y + seamOverlap;
  const innerWidth = Math.max(0, width - seamOverlap * 2);
  const innerHeight = Math.max(0, height - seamOverlap * 2);
  const outerRadiusX = expandBorderRadius(borderRadiusX, borderThickness);
  const outerRadiusY = expandBorderRadius(borderRadiusY, borderThickness);
  const innerRadiusX = Math.max(0, borderRadiusX);
  const innerRadiusY = Math.max(0, borderRadiusY);
  ctx.save();
  ctx.fillStyle = createBorderStyle(ctx, outerX, outerY, outerWidth, outerHeight, state);
  ctx.beginPath();
  if (borderShape === "ellipse") {
    ctx.ellipse(outerX + outerWidth / 2, outerY + outerHeight / 2, outerWidth / 2, outerHeight / 2, 0, 0, Math.PI * 2);
    ctx.ellipse(innerX + innerWidth / 2, innerY + innerHeight / 2, innerWidth / 2, innerHeight / 2, 0, 0, Math.PI * 2);
  } else {
    addRoundedRectPath(ctx, outerX, outerY, outerWidth, outerHeight, outerRadiusX, outerRadiusY);
    addRoundedRectPath(ctx, innerX, innerY, innerWidth, innerHeight, innerRadiusX, innerRadiusY);
  }
  ctx.fill("evenodd");
  ctx.restore();
}

function drawAlphaBorder(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  x: number,
  y: number,
  width: number,
  height: number,
  borderThickness: number,
  state: BorderRenderState = settings,
) {
  if (borderThickness <= 0 || width <= 0 || height <= 0) return;
  if (state.borderUseGradient && state.borderGradientType === "edge") {
    const steps = Math.max(8, Math.min(96, Math.ceil(borderThickness * 2)));
    for (let index = 0; index < steps; index += 1) {
      const progress = index / Math.max(1, steps - 1);
      const remainingThickness = borderThickness * (1 - index / steps);
      drawAlphaBorder(ctx, source, x, y, width, height, remainingThickness, {
        ...state,
        borderUseGradient: false,
        borderColor: interpolateHexColor(state.borderGradientStart, state.borderGradientEnd, progress),
      });
    }
    return;
  }
  if (committedImageShape.value === "circle") {
    drawBorder(ctx, x, y, width, height, { x: width / 2, y: height / 2 }, borderThickness, "ellipse", state);
    return;
  }
  const maskCanvas = createAlphaMaskCanvas(source, width, height);
  const borderCanvas = document.createElement("canvas");
  const seamOverlap = 0.5;
  borderCanvas.width = Math.max(1, Math.ceil(width + borderThickness * 2 + seamOverlap * 2));
  borderCanvas.height = Math.max(1, Math.ceil(height + borderThickness * 2 + seamOverlap * 2));
  const borderCtx = borderCanvas.getContext("2d");
  if (!borderCtx) return;
  borderCtx.imageSmoothingEnabled = true;
  borderCtx.imageSmoothingQuality = "high";

  const offset = borderThickness + seamOverlap;
  // Let the border sit slightly beneath antialiased shape pixels so no transparent seam can show through.
  const overlap = Math.min(2.5, Math.max(1.25, borderThickness * 0.22));
  const maxOffset = Math.max(1, Math.ceil(borderThickness));
  for (let dy = -maxOffset; dy <= maxOffset; dy += 1) {
    for (let dx = -maxOffset; dx <= maxOffset; dx += 1) {
      if (dx * dx + dy * dy > maxOffset * maxOffset) continue;
      borderCtx.drawImage(maskCanvas, offset + dx, offset + dy, width, height);
    }
  }

  borderCtx.globalCompositeOperation = "source-in";
  borderCtx.fillStyle = createBorderStyle(borderCtx, offset, offset, width, height, state);
  borderCtx.fillRect(0, 0, borderCanvas.width, borderCanvas.height);
  borderCtx.globalCompositeOperation = "destination-out";
  borderCtx.drawImage(maskCanvas, offset + overlap, offset + overlap, Math.max(0, width - overlap * 2), Math.max(0, height - overlap * 2));
  borderCtx.globalCompositeOperation = "source-over";
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(borderCanvas, x - borderThickness - seamOverlap, y - borderThickness - seamOverlap);
}

function createAlphaMaskCanvas(source: CanvasImageSource, width: number, height: number) {
  const maskCanvas = document.createElement("canvas");
  maskCanvas.width = Math.max(1, Math.ceil(width));
  maskCanvas.height = Math.max(1, Math.ceil(height));
  const maskCtx = maskCanvas.getContext("2d");
  if (!maskCtx) return maskCanvas;
  maskCtx.imageSmoothingEnabled = true;
  maskCtx.imageSmoothingQuality = "high";
  maskCtx.drawImage(source, 0, 0, width, height);
  // Preserve the fractional alpha generated by clipped shapes; thresholding it creates stepped borders.
  return maskCanvas;
}

function shouldUseAlphaBorder() {
  return committedCornerRadiusX.value > 0 || committedCornerRadiusY.value > 0;
}

function usesAlphaSilhouetteBorder() {
  return committedImageShape.value !== "rect" || shouldUseAlphaBorder();
}

function getShadowContentRect() {
  if (!committedShadowState.shadowSize && !committedShadowState.shadowDarkness) return null;
  const width = committedShadowState.contentWidth || imageSize.width;
  const height = committedShadowState.contentHeight || imageSize.height;
  if (width <= 0 || height <= 0) return null;
  return {
    x: committedShadowState.contentX || 0,
    y: committedShadowState.contentY || 0,
    width,
    height,
  };
}

function getCommittedBorderProtectionThickness() {
  return getBorderLayerTotalThickness();
}

function getBorderLayerTotalThickness(layers: BorderLayer[] = committedBorderLayers) {
  return layers.reduce((totalThickness, layer) => totalThickness + Math.max(0, layer.borderThickness), 0);
}

function getBorderLayerContentInset(layers: BorderLayer[] = committedBorderLayers) {
  const totalThickness = getBorderLayerTotalThickness(layers);
  if (totalThickness <= 0) return 0;
  return Math.ceil(totalThickness) + 2;
}

function createBorderlessSourceCanvas(source: CanvasImageSource, sourceWidth: number, sourceHeight: number, inset: number) {
  const output = document.createElement("canvas");
  output.width = Math.max(1, Math.round(sourceWidth - inset * 2));
  output.height = Math.max(1, Math.round(sourceHeight - inset * 2));
  const ctx = output.getContext("2d");
  if (!ctx) return output;
  ctx.drawImage(source, inset, inset, output.width, output.height, 0, 0, output.width, output.height);
  return output;
}

function drawBorderLayers(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  borderRadius: number | { x: number; y: number },
  borderShape: "round" | "ellipse" = "round",
  layers: BorderLayer[] = committedBorderLayers,
) {
  let layerOffset = 0;
  layers.forEach((layer) => {
    drawBorder(
      ctx,
      x - layerOffset,
      y - layerOffset,
      width + layerOffset * 2,
      height + layerOffset * 2,
      typeof borderRadius === "number"
        ? expandBorderRadius(borderRadius, layerOffset)
        : {
            x: expandBorderRadius(borderRadius.x, layerOffset),
            y: expandBorderRadius(borderRadius.y, layerOffset),
          },
      layer.borderThickness,
      borderShape,
      layer,
    );
    layerOffset += Math.max(0, layer.borderThickness);
  });
}

function restoreBorderLayers(layers: BorderLayer[]) {
  committedBorderLayers.splice(0, committedBorderLayers.length, ...layers.map((layer) => ({ ...layer })));
}

function restoreBorderState(state: BorderLayer) {
  committedBorderState.borderThickness = state.borderThickness;
  committedBorderState.borderColor = state.borderColor;
  committedBorderState.borderUseGradient = state.borderUseGradient;
  committedBorderState.borderGradientType = normalizeBorderGradientType(state.borderGradientType);
  committedBorderState.borderGradientAngle = state.borderGradientAngle;
  committedBorderState.borderGradientStart = state.borderGradientStart;
  committedBorderState.borderGradientEnd = state.borderGradientEnd;
  settings.borderThickness = state.borderThickness;
  settings.borderColor = state.borderColor;
  settings.borderUseGradient = state.borderUseGradient;
  settings.borderGradientType = normalizeBorderGradientType(state.borderGradientType);
  settings.borderGradientAngle = state.borderGradientAngle;
  settings.borderGradientStart = state.borderGradientStart;
  settings.borderGradientEnd = state.borderGradientEnd;
}

function hexToRgba(hex: string, alpha: number) {
  const normalized = hex.replace("#", "");
  if (normalized.length !== 6) return `rgb(0 0 0 / ${alpha})`;
  const value = Number.parseInt(normalized, 16);
  if (Number.isNaN(value)) return `rgb(0 0 0 / ${alpha})`;
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgb(${r} ${g} ${b} / ${alpha})`;
}

function drawShadowedImage(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  x: number,
  y: number,
  width: number,
  height: number,
  options: { darkness: number; size: number; color: string; direction: number },
) {
  if (options.darkness <= 0 || options.size <= 0) {
    ctx.drawImage(source, x, y, width, height);
    return;
  }

  const alpha = clamp(options.darkness / 100, 0, 1);
  const blur = Math.max(0, options.size);
  const radians = ((options.direction || 45) * Math.PI) / 180;
  const offsetX = Math.round(Math.cos(radians) * blur * 0.35);
  const offsetY = Math.round(Math.sin(radians) * blur * 0.35);

  ctx.save();
  ctx.shadowColor = hexToRgba(options.color, alpha);
  ctx.shadowBlur = blur;
  ctx.shadowOffsetX = offsetX;
  ctx.shadowOffsetY = offsetY;
  ctx.drawImage(source, x, y, width, height);
  ctx.restore();
}

function createShadowPreviewCanvas(
  source: CanvasImageSource,
  width: number,
  height: number,
  options: { darkness: number; size: number; color: string; direction: number },
) {
  const blur = Math.max(0, options.size);
  const padding = Math.max(0, Math.ceil(blur * 2.5));
  const radians = ((options.direction || 45) * Math.PI) / 180;
  const offsetX = Math.round(Math.cos(radians) * blur * 0.35);
  const offsetY = Math.round(Math.sin(radians) * blur * 0.35);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.ceil(width + padding * 2 + Math.abs(offsetX)));
  canvas.height = Math.max(1, Math.ceil(height + padding * 2 + Math.abs(offsetY)));
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  drawShadowedImage(ctx, source, padding + Math.max(0, offsetX), padding + Math.max(0, offsetY), width, height, options);
  return canvas;
}

function startCropDrag(event: PointerEvent, mode: DragMode) {
  if (!hasImage.value) return;
  if (cropTutorialStep.value === "handles") {
    cropTutorialHandleDragStarted = true;
    cropTutorialStep.value = "dragging";
  }
  const target = event.currentTarget as HTMLElement;
  target.setPointerCapture(event.pointerId);
  activePointer = {
    pointerId: event.pointerId,
    captureTarget: target,
    mode,
    startX: event.clientX,
    startY: event.clientY,
    startCrop: { ...crop },
  };
}

function onPointerMove(event: PointerEvent) {
  if (!activePointer || event.pointerId !== activePointer.pointerId) return;

  const dx = (event.clientX - activePointer.startX) / canvasMetrics.scale;
  const dy = (event.clientY - activePointer.startY) / canvasMetrics.scale;
  const next = resizeCrop(activePointer.startCrop, activePointer.mode, dx, dy);
  crop.x = next.x;
  crop.y = next.y;
  crop.width = next.width;
  crop.height = next.height;
}

function endCropDrag(event: PointerEvent) {
  finishCropDrag(event.pointerId);
}

function finishCropDrag(pointerId?: number) {
  if (!activePointer || (pointerId !== undefined && activePointer.pointerId !== pointerId)) return;
  const { captureTarget, pointerId: activePointerId } = activePointer;
  activePointer = null;
  if (captureTarget.hasPointerCapture?.(activePointerId)) {
    captureTarget.releasePointerCapture(activePointerId);
  }
  if (cropTutorialHandleDragStarted) {
    cropTutorialHandleDragStarted = false;
    void showCropTutorialScissors();
  }
}

function onEditorPointerRelease(event: PointerEvent) {
  hideZoomTooltip();
  finishCropDrag(event.pointerId);
  finishResizeDrag(event.pointerId);
  finishImagePan(event.pointerId);
  finishComparisonDrag(event.pointerId);
}

function onEditorLostPointerCapture(event: PointerEvent) {
  hideZoomTooltip();
  finishCropDrag(event.pointerId);
  finishResizeDrag(event.pointerId);
  finishImagePan(event.pointerId);
  finishComparisonDrag(event.pointerId);
}

function cancelEditorDragOnWindowDeactivate() {
  hideZoomTooltip();
  finishCropDrag();
  finishResizeDrag();
  finishImagePan();
  finishComparisonDrag();
}

function cancelEditorDragWhenHidden() {
  if (!document.hidden) return;
  hideZoomTooltip();
  finishCropDrag();
  finishResizeDrag();
  finishImagePan();
  finishComparisonDrag();
  flushAutosave();
}

function resizeCrop(start: Crop, mode: DragMode, dx: number, dy: number): Crop {
  const minSize = Math.min(10, imageSize.width, imageSize.height);
  let x = start.x;
  let y = start.y;
  let width = start.width;
  let height = start.height;
  const lockedAspect = activeCropAspectLock();

  if (mode === "move") {
    x = clamp(start.x + dx, 0, imageSize.width - width);
    y = clamp(start.y + dy, 0, imageSize.height - height);
    return { x, y, width, height };
  }

  if (lockedAspect) {
    const right = start.x + start.width;
    const bottom = start.y + start.height;
    const useHorizontal = mode.includes("e") || mode.includes("w");
    const useVertical = mode.includes("n") || mode.includes("s");

    if (mode === "e" || mode === "w") {
      width = clamp(mode === "e" ? start.width + dx : start.width - dx, minSize, imageSize.width);
      height = width / lockedAspect;
    } else if (mode === "n" || mode === "s") {
      height = clamp(mode === "s" ? start.height + dy : start.height - dy, minSize, imageSize.height);
      width = height * lockedAspect;
    } else if (useHorizontal && useVertical) {
      const targetWidthFromDx = mode.includes("e") ? start.width + dx : start.width - dx;
      const targetHeightFromDy = mode.includes("s") ? start.height + dy : start.height - dy;
      if (Math.abs(targetWidthFromDx / lockedAspect - targetHeightFromDy) < Math.abs(targetHeightFromDy * lockedAspect - targetWidthFromDx)) {
        width = clamp(targetWidthFromDx, minSize, imageSize.width);
        height = width / lockedAspect;
      } else {
        height = clamp(targetHeightFromDy, minSize, imageSize.height);
        width = height * lockedAspect;
      }
    }

    if (mode.includes("w")) x = right - width;
    if (mode.includes("n")) y = bottom - height;
  } else {
    if (mode.includes("e")) width = clamp(start.width + dx, minSize, imageSize.width - x);
    if (mode.includes("s")) height = clamp(start.height + dy, minSize, imageSize.height - y);
    if (mode.includes("w")) {
      const right = start.x + start.width;
      x = clamp(start.x + dx, 0, right - minSize);
      width = right - x;
    }
    if (mode.includes("n")) {
      const bottom = start.y + start.height;
      y = clamp(start.y + dy, 0, bottom - minSize);
      height = bottom - y;
    }
  }

  return fitCropRectToImage({ x, y, width, height }, lockedAspect);
}

function startFloatingDrag(event: PointerEvent) {
  const target = event.currentTarget as HTMLElement;
  const interactiveTarget = event.target as HTMLElement | null;
  const panelRect = target.getBoundingClientRect();
  if (event.clientY >= panelRect.bottom - 18 && event.clientX >= panelRect.right - 28) return;
  if (
    interactiveTarget?.closest("button, input, select, textarea, label, option, a") &&
    !interactiveTarget.closest(".floating-close-button")
  ) {
    return;
  }
  target.setPointerCapture(event.pointerId);
  floatingPointer = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    panelX: floatingPanel.x,
    panelY: floatingPanel.y,
  };
}

function onFloatingMove(event: PointerEvent) {
  if (!floatingPointer || event.pointerId !== floatingPointer.pointerId) return;
  const stage = stageRef.value?.getBoundingClientRect();
  const panel = (event.currentTarget as HTMLElement | null)?.getBoundingClientRect();
  const panelWidth = panel?.width ?? 420;
  const panelHeight = panel?.height ?? 190;
  const minX = stage ? stage.left + 12 : 24;
  const minY = stage ? stage.top + 12 : 24;
  const maxX = stage ? stage.right - panelWidth - 12 : window.innerWidth - panelWidth - 12;
  const maxY = stage ? stage.bottom - panelHeight - 12 : window.innerHeight - panelHeight - 12;
  floatingPanel.x = clamp(floatingPointer.panelX + event.clientX - floatingPointer.startX, minX, Math.max(minX, maxX));
  floatingPanel.y = clamp(floatingPointer.panelY + event.clientY - floatingPointer.startY, minY, Math.max(minY, maxY));
}

function endFloatingDrag(event: PointerEvent) {
  if (floatingPointer?.pointerId === event.pointerId) floatingPointer = null;
}

function startHistoryDrag(event: PointerEvent) {
  const target = event.currentTarget as HTMLElement;
  const interactiveTarget = event.target as HTMLElement | null;
  if (
    interactiveTarget?.closest("button, input, select, textarea, label, option, a") &&
    !interactiveTarget.closest(".floating-close-button")
  ) {
    return;
  }
  target.setPointerCapture(event.pointerId);
  historyPointer = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    panelX: historyPanelPosition.x,
    panelY: historyPanelPosition.y,
  };
}

function onHistoryMove(event: PointerEvent) {
  if (!historyPointer || event.pointerId !== historyPointer.pointerId) return;
  const stage = stageRef.value?.getBoundingClientRect();
  const panel = (event.currentTarget as HTMLElement | null)?.getBoundingClientRect();
  const panelWidth = panel?.width ?? 360;
  const panelHeight = panel?.height ?? 340;
  const minX = stage ? stage.left + 12 : 24;
  const minY = stage ? stage.top + 12 : 24;
  const maxX = stage ? stage.right - panelWidth - 12 : window.innerWidth - panelWidth - 12;
  const maxY = stage ? stage.bottom - panelHeight - 12 : window.innerHeight - panelHeight - 12;
  historyPanelPosition.x = clamp(historyPointer.panelX + event.clientX - historyPointer.startX, minX, Math.max(minX, maxX));
  historyPanelPosition.y = clamp(historyPointer.panelY + event.clientY - historyPointer.startY, minY, Math.max(minY, maxY));
}

function endHistoryDrag(event: PointerEvent) {
  if (historyPointer?.pointerId === event.pointerId) historyPointer = null;
}

function updateFloatingPanelBounds() {
  const stage = stageRef.value?.getBoundingClientRect();
  if (!stage) {
    floatingPanelBounds.maxHeight = 0;
    return;
  }

  const inset = 12;
  floatingPanelBounds.maxHeight = Math.max(0, Math.floor(stage.height - inset * 2));

  const clampPanelPosition = (position: { x: number; y: number }, selector: string) => {
    if (!position.x && !position.y) return;
    const panel = document.querySelector<HTMLElement>(selector)?.getBoundingClientRect();
    const panelWidth = panel?.width ?? 400;
    const panelHeight = Math.min(panel?.height ?? 150, floatingPanelBounds.maxHeight);
    position.x = clamp(position.x, stage.left + inset, Math.max(stage.left + inset, stage.right - panelWidth - inset));
    position.y = clamp(position.y, stage.top + inset, Math.max(stage.top + inset, stage.bottom - panelHeight - inset));
  };

  clampPanelPosition(floatingPanel, ".floating-panel:not(.history-panel)");
  clampPanelPosition(historyPanelPosition, ".history-panel");
}

function positionFloatingPanel(force = false) {
  updateFloatingPanelBounds();
  if (!force && (floatingPanel.x || floatingPanel.y)) return;
  const stage = stageRef.value?.getBoundingClientRect();
  floatingPanel.x = stage ? Math.round(stage.left + 12) : 24;
  floatingPanel.y = stage ? Math.round(stage.top + 12) : 24;
}

function positionHistoryPanel(force = false) {
  updateFloatingPanelBounds();
  if (!force && (historyPanelPosition.x || historyPanelPosition.y)) return;
  const stage = stageRef.value?.getBoundingClientRect();
  historyPanelPosition.x = stage ? Math.round(stage.left + 12) : 24;
  historyPanelPosition.y = stage ? Math.round(stage.top + 12) : 24;
}

function selectedExportOption() {
  return exportFormatOptions.find((option) => option.id === exportFormat.value) ?? exportFormatOptions[0];
}

function exportDialogTitle() {
  return t("pngExport").replace(/PNG/giu, selectedExportOption().label);
}

function buildExportCanvas(format: ExportFormat = exportFormat.value): HTMLCanvasElement | null {
  const source = image.value;
  if (!source || imageSize.width < 1 || imageSize.height < 1) return null;

  const output = document.createElement("canvas");
  output.width = Math.max(1, Math.round(imageSize.width));
  output.height = Math.max(1, Math.round(imageSize.height));
  const ctx = output.getContext("2d");
  if (!ctx) return null;

  if (format === "jpg") {
    // JPEG cannot retain transparency, so flatten it onto the user's chosen color.
    ctx.fillStyle = exportJpegBackgroundColor.value;
    ctx.fillRect(0, 0, output.width, output.height);
  } else {
    ctx.clearRect(0, 0, output.width, output.height);
  }
  // Applied edits are baked into the current image; pending tool previews are not.
  ctx.drawImage(source, 0, 0, output.width, output.height);
  return output;
}

function canvasToExportBlob(canvas: HTMLCanvasElement, format: ExportFormat = exportFormat.value): Promise<Blob | null> {
  const option = exportFormatOptions.find((candidate) => candidate.id === format) ?? exportFormatOptions[0];
  const quality = format === "png" ? undefined : exportQuality.value / 100;
  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob: Blob | null) => resolve(blob), option.mimeType, quality);
  });
}

function buildExportBlob(format: ExportFormat = exportFormat.value): Promise<Blob | null> {
  const output = buildExportCanvas(format);
  if (!output) return Promise.resolve(null);
  return canvasToExportBlob(output, format);
}

function hasUnappliedChanges() {
  return false;
}

async function savePendingChangesBeforeExport() {
  return;
}

function exportPng() {
  if (comparisonMode.value) exitComparisonMode();
  exportDialogOpen.value = true;
}

async function confirmSaveBeforeExport() {
  exportSavePromptOpen.value = false;
  await savePendingChangesBeforeExport();
  await downloadExportedImage(true);
}

function declineSaveBeforeExport() {
  exportSavePromptOpen.value = false;
  exportDialogOpen.value = true;
}

function exportFileName() {
  return `CutMyPic.${selectedExportOption().extension}`;
}

async function triggerDownloadFromCanvas(canvas: HTMLCanvasElement) {
  const blob = await canvasToExportBlob(canvas);
  if (!blob) return false;
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = exportFileName();
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
  scheduleSaveToolOffer();
  return true;
}

function isAppleMobileDevice() {
  if (!isBrowser) return false;
  return /iPad|iPhone|iPod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isApplePhoneDevice() {
  return isBrowser && /iPhone|iPod/i.test(navigator.userAgent);
}

async function openAppleMobileSaveSheet(canvas: HTMLCanvasElement) {
  if (!isAppleMobileDevice() || typeof navigator.share !== "function") return false;
  const blob = await canvasToExportBlob(canvas);
  if (!blob) return false;
  const file = new File([blob], exportFileName(), { type: blob.type });
  if (typeof navigator.canShare === "function" && !navigator.canShare({ files: [file] })) return false;
  try {
    await navigator.share({ title: "Cut My Pic", text: exportFileName(), files: [file] });
    exportDialogOpen.value = false;
    scheduleSaveToolOffer();
  } catch (error) {
    if (!(error instanceof DOMException) || error.name !== "AbortError") return false;
  }
  return true;
}

async function openAppleMobileExportPreview(canvas: HTMLCanvasElement) {
  if (!isApplePhoneDevice()) return false;
  const blob = await canvasToExportBlob(canvas);
  if (!blob) return false;
  const objectUrl = URL.createObjectURL(blob);
  const previewWindow = window.open(objectUrl, "_blank");
  if (previewWindow) {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    exportDialogOpen.value = false;
    scheduleSaveToolOffer();
    return true;
  }
  URL.revokeObjectURL(objectUrl);
  return false;
}

async function downloadExportedImage(force = false) {
  if (!force && hasUnappliedChanges()) {
    exportSavePromptOpen.value = true;
    exportDialogOpen.value = false;
    return;
  }
  const output = buildExportCanvas();
  if (!output) return;
  if (isApplePhoneDevice()) {
    const canAttemptNativeShare =
      typeof navigator.share === "function" &&
      (typeof navigator.canShare !== "function" ||
        navigator.canShare({ files: [new File([], exportFileName(), { type: selectedExportOption().mimeType })] }));
    if (canAttemptNativeShare) {
      if (await openAppleMobileSaveSheet(output)) return;
    }
    if (await openAppleMobileExportPreview(output)) {
      return;
    }
  } else if (
    isAppleMobileDevice() &&
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function" &&
    (await openAppleMobileSaveSheet(output))
  ) {
    return;
  }
  await triggerDownloadFromCanvas(output);
  exportDialogOpen.value = false;
}

async function shareExportedImage() {
  const blob = await buildExportBlob();
  if (!blob) return;
  const file = new File([blob], exportFileName(), { type: blob.type });
  const canShareFiles = typeof navigator.canShare === "function" ? navigator.canShare({ files: [file] }) : false;
  if (navigator.share && canShareFiles) {
    await navigator.share({
      title: "Cut My Pic",
      text: exportFileName(),
      files: [file],
    });
    exportDialogOpen.value = false;
    return;
  }
  const objectUrl = URL.createObjectURL(blob);
  window.open(objectUrl, "_blank", "noopener,noreferrer");
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
}

async function copyExportedImage() {
  const blob = await buildExportBlob("png");
  if (!blob) return;
  try {
    if (navigator.clipboard && "ClipboardItem" in window) {
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      showClipboardNotice();
      return;
    }
  } catch {
    // Fall through to opening the image locally.
  }
  const objectUrl = URL.createObjectURL(blob);
  window.open(objectUrl, "_blank", "noopener,noreferrer");
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
}

function openShareUrl(url: string) {
  window.open(url, "_blank", "noopener,noreferrer");
}

function showClipboardNotice(openingNow = false, duration = 4000) {
  shareClipboardNoticeOpen.value = true;
  shareOpeningNowOpen.value = openingNow;
  if (clipboardNoticeTimer != null) window.clearTimeout(clipboardNoticeTimer);
  clipboardNoticeTimer = window.setTimeout(() => {
    shareClipboardNoticeOpen.value = false;
    shareOpeningNowOpen.value = false;
    clipboardNoticeTimer = null;
  }, duration);
}

async function copyExportedImageToClipboard() {
  const blob = await buildExportBlob("png");
  if (!blob) return false;
  if (!navigator.clipboard || typeof ClipboardItem === "undefined") return false;
  await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
  return true;
}

async function shareToNetwork(url: string) {
  const copied = await copyExportedImageToClipboard().catch(() => false);
  if (copied) {
    showClipboardNotice(true, 4500);
  }
  if (shareOpenTimer != null) window.clearTimeout(shareOpenTimer);
  shareOpenTimer = window.setTimeout(() => {
    openShareUrl(url);
    shareOpeningNowOpen.value = false;
    shareOpenTimer = null;
  }, 3000);
}

function currentShareText() {
  const baseName = imageName.value.replace(/\.[^.]+$/, "") || "cutmypic";
  return `${baseName} — Cut My Pic`;
}

function currentShareUrl() {
  return window.location.href;
}

function currentSiteShareUrl() {
  const url = new URL(window.location.href);
  url.search = "";
  url.hash = "";
  return url.toString();
}

async function copySiteShareUrl(url: string) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch {
      // Fall through to the selection-based copy method for older browsers.
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = url;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  return copied;
}

function showShareSiteStatus(message: string) {
  shareSiteStatusMessage.value = message;
  if (shareSiteStatusTimer !== null) window.clearTimeout(shareSiteStatusTimer);
  shareSiteStatusTimer = window.setTimeout(() => {
    shareSiteStatusMessage.value = "";
    shareSiteStatusTimer = null;
  }, 3200);
}

async function shareSite() {
  mobileToolMenuOpen.value = false;
  showTopSettings.value = false;
  showLanguageMenu.value = false;
  const copy = shareSiteCopy[language.value];
  const url = currentSiteShareUrl();

  if (typeof navigator.share === "function") {
    try {
      await navigator.share({ title: "CutMyPic", text: copy.message, url });
      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
    }
  }

  if (await copySiteShareUrl(url)) showShareSiteStatus(copy.copied);
}

function shareBadgeStyle(key: keyof typeof shareBrandColors) {
  const color = shareBrandColors[key];
  return {
    backgroundColor: color.bg,
    color: color.fg,
    borderColor: color.border,
  };
}

function showHoverHint(text: string, event: MouseEvent | FocusEvent) {
  if (!text) return;
  hoverHintVariant.value = "default";
  if (hoverHintTimer !== null) window.clearTimeout(hoverHintTimer);
  const target = event.currentTarget as HTMLElement | null;
  const rect = target?.getBoundingClientRect();
  hoverHintTimer = window.setTimeout(() => {
    hoverHint.value = text;
    hoverHintVisible.value = true;
    hoverHintStyle.value = rect
      ? {
          left: `${Math.round(rect.left + rect.width / 2)}px`,
          top: `${Math.round(rect.top - 10)}px`,
          transform: "translate(-50%, -100%)",
        }
      : {};
  }, 1000);
}

function showWarningHoverHint(text: string, event: MouseEvent | FocusEvent) {
  if (!text) return;
  hoverHintVariant.value = "warning";
  if (hoverHintTimer !== null) window.clearTimeout(hoverHintTimer);
  const target = event.currentTarget as HTMLElement | null;
  const rect = target?.getBoundingClientRect();
  hoverHintTimer = window.setTimeout(() => {
    hoverHint.value = text;
    hoverHintVisible.value = true;
    hoverHintStyle.value = rect
      ? {
          left: `${Math.round(rect.left + rect.width / 2)}px`,
          top: `${Math.round(rect.top - 10)}px`,
          transform: "translate(-50%, -100%)",
        }
      : {};
  }, 300);
}

function hideHoverHint() {
  if (hoverHintTimer !== null) {
    window.clearTimeout(hoverHintTimer);
    hoverHintTimer = null;
  }
  hoverHintVisible.value = false;
  hoverHint.value = "";
  hoverHintVariant.value = "default";
}

function startFullscreenRailDrag(event: PointerEvent) {
  if (!fullscreenMode.value) return;
  if (event.button !== 0) return;
  const target = event.currentTarget as HTMLElement | null;
  target?.setPointerCapture(event.pointerId);
  fullscreenRailPointer = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    railX: fullscreenRailPosition.x,
    railY: fullscreenRailPosition.y,
  };
}

function onFullscreenRailMove(event: PointerEvent) {
  if (!fullscreenRailPointer || event.pointerId !== fullscreenRailPointer.pointerId) return;
  const stage = stageRef.value;
  const rect = stage?.getBoundingClientRect();
  if (!rect) return;
  const railWidth = 80;
  const railHeight = 392;
  const minX = 12;
  const minY = 12;
  const maxX = Math.max(minX, rect.width - railWidth - 12);
  const maxY = Math.max(minY, rect.height - railHeight - 12);
  fullscreenRailPosition.x = clamp(fullscreenRailPointer.railX + event.clientX - fullscreenRailPointer.startX, minX, maxX);
  fullscreenRailPosition.y = clamp(fullscreenRailPointer.railY + event.clientY - fullscreenRailPointer.startY, minY, maxY);
}

function endFullscreenRailDrag(event: PointerEvent) {
  if (fullscreenRailPointer?.pointerId !== event.pointerId) return;
  fullscreenRailPointer = null;
}

function railToolHint(toolId: ToolId) {
  const labelKey = toolLabelKeys[toolId];
  return labelKey ? t(labelKey) : "";
}

function resetFullscreenRailPosition() {
  fullscreenRailPosition.x = 12;
  fullscreenRailPosition.y = 96.5;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function getMaskBlurRadius(blurPercent: number) {
  const normalized = clamp(blurPercent / 100, 0, 1);
  return Math.max(0, Math.round(Math.pow(normalized, 1.45) * 42));
}

watch(theme, (nextTheme) => {
  if (!isBrowser) return;
  document.documentElement.classList.toggle("dark", nextTheme === "dark");
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", nextTheme === "dark" ? "#0f172a" : "#f4f7fa");
  window.localStorage.setItem("cutmypic-theme", nextTheme);
  render();
}, { immediate: true });

watch([viewBackgroundMode, viewBackgroundColor], ([nextMode, nextColor]) => {
  if (!isBrowser) return;
  window.localStorage.setItem("cutmypic-view-background-mode", nextMode);
  window.localStorage.setItem("cutmypic-view-background-color", nextColor);
  render();
});

watch(activeTool, (nextTool) => {
  if (activePanelTool.value && nextTool) {
    activePanelTool.value = panelToolFor(nextTool);
  }
  render();
});

watch([exportDialogOpen, exportFormat, exportQuality, exportJpegBackgroundColor], ([nextOpen]) => {
  if (nextOpen) {
    const canvas = buildExportCanvas();
    const option = selectedExportOption();
    exportPreviewUrl.value = canvas
      ? canvas.toDataURL(option.mimeType, exportFormat.value === "png" ? undefined : exportQuality.value / 100)
      : imageUrl.value;
  } else {
    exportPreviewUrl.value = "";
  }
});

watch(
  [
    () => settings.zoom,
    () => settings.radius,
    () => settings.filter,
    () => settings.colorReplaceTarget,
    () => settings.colorReplaceReplacement,
    () => settings.colorReplaceStrength,
    () => settings.colorReplaceSmoothness,
    () => settings.colorReplaceTargetTransparent,
    () => settings.colorReplaceReplacementTransparent,
    () => settings.contrast,
    () => settings.brightness,
    () => settings.saturation,
    () => settings.colorBalance,
    () => settings.sharpen,
    () => settings.soften,
    () => settings.transparency,
    () => settings.transparencyIncludeBorder,
    () => settings.borderThickness,
    () => settings.borderColor,
    () => settings.borderUseGradient,
    () => settings.borderGradientType,
    () => settings.borderGradientAngle,
    () => settings.borderGradientStart,
    () => settings.borderGradientEnd,
    () => settings.shadowDarkness,
    () => settings.shadowSize,
    () => settings.shadowColor,
    () => settings.shadowDirection,
    () => cropFunctionMode.value,
    () => cropMaskShape.value,
    () => cropMaskMode.value,
    () => cropMaskBlurIntensity.value,
    () => cropMaskTransparency.value,
    () => cropMaskColor.value,
    () => textState.content,
    () => textState.color,
    () => textState.size,
    () => textState.rotation,
    () => textState.x,
    () => textState.y,
    () => textState.shadowSize,
    () => textState.shadowDarkness,
    () => textState.shadowColor,
    () => crop.x,
    () => crop.y,
    () => crop.width,
    () => crop.height,
    () => resizeBox.x,
    () => resizeBox.y,
    () => resizeBox.width,
    () => resizeBox.height,
    () => resizeMode.value,
  ],
  render,
);

watch(
  () => settings.filter,
  (nextFilter, previousFilter) => {
    if (isRestoringPreview || !hasImage.value) return;
    if (suppressFilterHistory) return;
    if (activeTool.value !== "filters") return;
    if (nextFilter === previousFilter) return;
    if (nextFilter === "none" && previousFilter === "none") return;
    render();
  },
);

watch(
  () => settings.borderColor,
  (nextColor) => {
    if (settings.borderUseGradient) {
      settings.borderGradientStart = nextColor;
      settings.borderGradientEnd = lightenHexColor(nextColor);
    }
  },
);

watch(
  () => settings.borderUseGradient,
  (nextEnabled) => {
    if (nextEnabled) syncBorderGradientDefaults();
  },
);

watch(maxRadius, (nextMax) => {
  if (settings.radius > nextMax) settings.radius = nextMax;
});

watch(language, () => {
  if (!isBrowser) return;
  applyDocumentLanguage(language.value);
  window.history.replaceState(
    {},
    "",
    isManualPage.value
      ? getLocalizedManualUrl(language.value)
      : isFaqPage.value
        ? getLocalizedFaqUrl(language.value)
        : isGuidePage.value
          ? getLocalizedGuideUrl(language.value)
          : getLocalizedUrl(language.value),
  );
  routePath.value = window.location.pathname;
  syncSeoLinks();
  updateSeoMetadata();
}, { immediate: true });

let hasTrackedInitialPageView = false;
watch(routePath, () => {
  if (!hasTrackedInitialPageView) {
    hasTrackedInitialPageView = true;
    void nextTick(() => render());
    return;
  }
  trackPageView();
  void nextTick(() => render());
  if (!isManualPage.value && !isFaqPage.value) {
    positionFloatingPanel(true);
  }
});

watch(activePanelTool, (nextPanel, previousPanel) => {
  if (!isMobileViewport.value) return;
  if (![nextPanel, previousPanel].some((panel) => panel === "crop" || panel === "corners")) return;
  void nextTick(render);
});

watch(
  [
    () => previewHistory.value.length,
    () => redoHistory.value.length,
    settings,
    crop,
    resizeBox,
    textState,
    activeTool,
    activePanelTool,
    cropMode,
    cropFunctionMode,
    cropMaskShape,
    cropMaskMode,
    resizeMode,
    resizePanelMode,
    selectedCropPresetId,
    selectedStampLabel,
    textPanelMode,
  ],
  () => scheduleAutosave(),
  { deep: true },
);

function syncRouteState() {
  routePath.value = window.location.pathname;
}

onMounted(async () => {
  preloadLogos();
  updateViewportFlags();
  appInstalled.value = detectInstalledApp();
  const filterProbeCanvas = document.createElement("canvas");
  filterProbeCanvas.width = 1;
  filterProbeCanvas.height = 1;
  const filterProbe = filterProbeCanvas.getContext("2d");
  if (filterProbe && !(isMobileViewport.value && isAppleMobileDevice())) {
    try {
      filterProbe.fillStyle = "rgb(255 0 0)";
      filterProbe.filter = "grayscale(1)";
      filterProbe.fillRect(0, 0, 1, 1);
      const [red, green, blue] = filterProbe.getImageData(0, 0, 1, 1).data;
      canvasFilterSupported.value = Math.abs(red - green) < 3 && Math.abs(green - blue) < 3;
    } catch {
      canvasFilterSupported.value = false;
    }
  } else {
    canvasFilterSupported.value = false;
  }
  positionFloatingPanel(true);
  if (isManualPage.value || isFaqPage.value) triggerDropFlash();
  if (window.sessionStorage.getItem("cutmypic-open-shortcuts") === "1") {
    window.sessionStorage.removeItem("cutmypic-open-shortcuts");
    shortcutHelpOpen.value = true;
  }
  if (stageRef.value) {
    resizeObserver = new ResizeObserver(() => {
      render();
      updateFloatingPanelBounds();
    });
    resizeObserver.observe(stageRef.value);
  }
  window.addEventListener("resize", onWindowResize);
  window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  window.addEventListener("appinstalled", handleAppInstalled);
  window.addEventListener("keydown", onDocumentKeydown);
  window.addEventListener("popstate", syncRouteState);
  window.addEventListener("pointerup", onEditorPointerRelease, true);
  window.addEventListener("pointercancel", onEditorPointerRelease, true);
  window.addEventListener("lostpointercapture", onEditorLostPointerCapture, true);
  window.addEventListener("blur", cancelEditorDragOnWindowDeactivate);
  window.addEventListener("pagehide", flushAutosave);
  document.addEventListener("fullscreenchange", onBrowserFullscreenChange);
  document.addEventListener("webkitfullscreenchange", onBrowserFullscreenChange);
  document.addEventListener("visibilitychange", cancelEditorDragWhenHidden);
  render();
  await loadSavedWorkflows();
  await restoreAutosavedProject();
  autosaveReady = true;
  if (isManualPage.value && window.location.hash) {
    const sectionId = decodeURIComponent(window.location.hash.slice(1));
    await nextTick();
    manualJumpSelection.value = sectionId;
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "auto", block: "start" });
  }
});

onBeforeUnmount(() => {
  setMobileTextPositionScreenLock(false);
  flushAutosave();
  resizeObserver?.disconnect();
  window.removeEventListener("resize", onWindowResize);
  window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  window.removeEventListener("appinstalled", handleAppInstalled);
  window.removeEventListener("keydown", onDocumentKeydown);
  window.removeEventListener("popstate", syncRouteState);
  window.removeEventListener("pointerup", onEditorPointerRelease, true);
  window.removeEventListener("pointercancel", onEditorPointerRelease, true);
  window.removeEventListener("lostpointercapture", onEditorLostPointerCapture, true);
  window.removeEventListener("blur", cancelEditorDragOnWindowDeactivate);
  window.removeEventListener("pagehide", flushAutosave);
  document.removeEventListener("fullscreenchange", onBrowserFullscreenChange);
  document.removeEventListener("webkitfullscreenchange", onBrowserFullscreenChange);
  document.removeEventListener("visibilitychange", cancelEditorDragWhenHidden);
  window.removeEventListener("pointerdown", closeFinalCropTutorialOnInteraction, true);
  if (imageUrl.value) URL.revokeObjectURL(imageUrl.value);
  if (shareOpenTimer != null) window.clearTimeout(shareOpenTimer);
  if (clipboardNoticeTimer != null) window.clearTimeout(clipboardNoticeTimer);
  if (shareSiteStatusTimer != null) window.clearTimeout(shareSiteStatusTimer);
  if (saveToolOfferTimer != null) window.clearTimeout(saveToolOfferTimer);
  if (autosaveTimer != null) window.clearTimeout(autosaveTimer);
  if (autosaveNoticeTimer != null) window.clearTimeout(autosaveNoticeTimer);
  if (workflowStatusTimer != null) window.clearTimeout(workflowStatusTimer);
  if (comparisonLabelFrame != null) window.cancelAnimationFrame(comparisonLabelFrame);
  if (dropFlashTimer !== null) window.clearTimeout(dropFlashTimer);
  if (unsupportedFileNoticeTimer !== null) window.clearTimeout(unsupportedFileNoticeTimer);
  if (hoverHintTimer !== null) window.clearTimeout(hoverHintTimer);
});
</script>

<template>
  <main
    class="app-shell min-h-screen bg-surface text-ink transition-colors"
    :class="{ 'is-fullscreen': fullscreenMode, 'is-manual': isManualPage || isFaqPage }"
    @dragover.prevent="isDraggingFile = true"
    @dragleave.prevent="isDraggingFile = false"
    @drop="onDrop"
  >
    <label class="sr-only" for="image-file-input">{{ t("chooseImage") }}</label>
    <input
      id="image-file-input"
      ref="fileInputRef"
      class="sr-only"
      type="file"
      accept="image/png,image/jpeg,image/webp,image/gif"
      :aria-label="t('chooseImage')"
      @change="onFileInput"
    />

    <aside v-if="!fullscreenMode" class="left-rail">
      <div class="rail-header">
        <button class="brand-mark" type="button" :aria-label="t('revert')" @click="resetApp">
          <img :src="logoSrc" alt="Cut My Pic" />
        </button>
      </div>

      <div class="rail-scroll">
      <div class="rail-group">
        <template v-for="tool in tools" :key="tool.id">
          <div v-if="tool.id === 'crop'" class="crop-row">
            <span class="tool-row-label" :class="{ 'is-active': hasImage && activeTool === tool.id }">{{ t(tool.labelKey) }}</span>
            <div class="tool-row-buttons">
              <button
                class="square-tool-button is-mode"
                :class="{ 'is-active': hasImage && activeTool === tool.id }"
                type="button"
                :aria-label="t(tool.labelKey)"
                :disabled="!hasImage"
                @click="showTool(tool.id)"
              >
                <component :is="tool.icon" :size="18" stroke-width="2.1" />
              </button>
              <button
                class="square-tool-button is-gear"
                :class="{ 'is-active': hasImage && activeTool === tool.id }"
                type="button"
                :disabled="!hasImage || activeTool !== tool.id"
                :aria-label="t('settings')"
                @click="openToolPanel('crop')"
              >
                <Settings2 :size="18" stroke-width="2.1" />
              </button>
              <button
                data-crop-tutorial-commit="rail"
                class="square-tool-button is-danger"
                :class="{ 'is-pending': cropPending }"
                type="button"
                :disabled="!hasImage || activeTool !== tool.id || activePanelTool !== null || !cropPending"
                :aria-label="t('crop')"
                @click="applyCropToImage"
              >
                <Scissors :size="16" stroke-width="2.2" />
              </button>
            </div>
            <div v-if="activeTool === 'crop' && activePanelTool !== 'crop'" class="radio-stack mt-1">
              <label class="radio-row crop-shape-row">
                <input v-model="cropMode" type="radio" value="free" :disabled="!hasImage" @change="selectedCropPresetId = 'common-free'" />
                <span>{{ t("freeSelect") }}</span>
              </label>
              <label class="radio-row crop-shape-row">
                <input v-model="cropMode" type="radio" value="circle" :disabled="!hasImage" @change="selectedCropPresetId = 'common-circle'; setCircleCrop()" />
                <span class="crop-shape-icon is-circle" aria-hidden="true"></span>
                <span>{{ t("circleSelect") }}</span>
              </label>
              <label class="radio-row crop-shape-row">
                <input v-model="cropMode" type="radio" value="heart" :disabled="!hasImage" @change="selectedCropPresetId = 'common-heart'; setHeartCrop()" />
                <span class="crop-shape-icon is-heart" aria-hidden="true"></span>
                <span>{{ t("heartSelect") }}</span>
              </label>
            </div>
          </div>
          <div v-else-if="tool.id === 'corners'" class="crop-row">
            <span class="tool-row-label" :class="{ 'is-active': hasImage && activeTool === tool.id }">{{ t(tool.labelKey) }}</span>
            <div class="tool-row-buttons">
              <button
                class="square-tool-button is-mode"
                :class="{ 'is-active': hasImage && activeTool === tool.id }"
                type="button"
                :aria-label="t(tool.labelKey)"
                :disabled="!hasImage"
                @click="showTool(tool.id)"
              >
                <component :is="tool.icon" :size="18" stroke-width="2.1" />
              </button>
              <button
                class="square-tool-button is-gear"
                :class="{ 'is-active': hasImage && activeTool === tool.id }"
                type="button"
                :disabled="!hasImage || activeTool !== tool.id"
                :aria-label="t('settings')"
                @click="openToolPanel('corners')"
              >
                <Settings2 :size="18" stroke-width="2.1" />
              </button>
              <button
                class="square-tool-button is-danger"
                :class="{ 'is-pending': cornerPending }"
                type="button"
                :disabled="!hasImage || activeTool !== tool.id || activePanelTool !== null || !cornerPending"
                :aria-label="t('corners')"
                @click="applyRoundedCornersToImage"
              >
                <Scissors :size="16" stroke-width="2.2" />
              </button>
            </div>
          </div>
          <div v-if="tool.id === 'corners' && activeTool === 'corners' && activePanelTool !== 'corners'" class="inline-radius-row">
            <div class="inline-radius-header">
              <span class="inline-radius-label">{{ t("cornerRadius") }}</span>
              <span class="inline-radius-value">{{ radius.toFixed(1).replace(/\.0$/, "") }} px</span>
            </div>
            <input v-model.number="settings.radius" class="inline-radius-slider" type="range" min="0" :max="maxRadius || 200" step="0.5" :disabled="!hasImage" />
          </div>
          <div v-else-if="tool.id === 'border'" class="crop-row">
            <span class="tool-row-label" :class="{ 'is-active': hasImage && activeTool === tool.id }">{{ t(tool.labelKey) }}</span>
            <div class="tool-row-buttons is-two">
              <button
                class="square-tool-button is-mode"
                :class="{ 'is-active': hasImage && activeTool === tool.id }"
                type="button"
                :aria-label="t(tool.labelKey)"
                :disabled="!hasImage"
                @click="showTool(tool.id)"
              >
                <component :is="tool.icon" :size="18" stroke-width="2.1" />
              </button>
              <button
                class="square-tool-button is-gear"
                :class="{ 'is-active': hasImage && activeTool === tool.id }"
                type="button"
                :disabled="!hasImage || activeTool !== tool.id"
                :aria-label="t('settings')"
                @click="openToolPanel('border')"
              >
                <Settings2 :size="18" stroke-width="2.1" />
              </button>
              <button
                class="square-tool-button is-danger"
                :class="{ 'is-pending': borderPending }"
                type="button"
                :disabled="!hasImage || activeTool !== tool.id || activePanelTool !== null || !borderPending"
                :aria-label="t('border')"
                @click="applyBorderToImage"
              >
                <Check :size="16" stroke-width="2.2" />
              </button>
          </div>
          </div>
          <div v-if="tool.id === 'border' && activeTool === 'border' && activePanelTool !== 'border'" class="inline-radius-row">
            <div class="inline-radius-header">
              <span class="inline-radius-label">{{ t("borderThickness") }}</span>
              <span class="inline-radius-value">{{ Math.round(settings.borderThickness) }} px</span>
            </div>
            <input v-model.number="settings.borderThickness" class="inline-radius-slider" type="range" min="0" :max="Math.max(80, Math.floor(Math.min(crop.width, crop.height) / 4))" step="1" :disabled="!hasImage" />
            <label class="color-row stacked">
              <span>{{ t("borderColor") }}</span>
              <input v-model="settings.borderColor" type="color" :disabled="!hasImage || settings.borderUseGradient" />
            </label>
          </div>
          <div v-else-if="tool.id === 'shadow'" class="crop-row">
            <span class="tool-row-label" :class="{ 'is-active': hasImage && activeTool === tool.id }">{{ t(tool.labelKey) }}</span>
            <div class="tool-row-buttons is-two">
              <button
                class="square-tool-button is-mode"
                :class="{ 'is-active': hasImage && activeTool === tool.id }"
                type="button"
                :aria-label="t(tool.labelKey)"
                :disabled="!hasImage"
                @click="showTool(tool.id)"
              >
                <component :is="tool.icon" :size="18" stroke-width="2.1" />
              </button>
              <button
                class="square-tool-button is-gear"
                :class="{ 'is-active': hasImage && activeTool === tool.id }"
                type="button"
                :disabled="!hasImage || activeTool !== tool.id"
                :aria-label="t('settings')"
                @click="openToolPanel('shadow')"
              >
                <Settings2 :size="18" stroke-width="2.1" />
              </button>
              <button
                class="square-tool-button is-danger"
                :class="{ 'is-pending': shadowPending }"
                type="button"
                :disabled="!hasImage || activeTool !== tool.id || activePanelTool !== null || !shadowPending"
                :aria-label="t('shadow')"
                @click="applyShadowToImage"
              >
                <Check :size="16" stroke-width="2.2" />
              </button>
            </div>
            <div v-if="activeTool === 'shadow' && activePanelTool !== 'shadow'" class="inline-radius-row">
              <div class="inline-radius-header">
                <span class="inline-radius-label">{{ t("shadowDarkness") }}</span>
                <span class="inline-radius-value">{{ Math.round(settings.shadowDarkness) }}%</span>
              </div>
              <input v-model.number="settings.shadowDarkness" class="inline-radius-slider" type="range" min="0" max="100" step="1" :disabled="!hasImage" />
              <div class="inline-radius-header mt-3">
                <span class="inline-radius-label">{{ t("shadowSize") }}</span>
                <span class="inline-radius-value">{{ Math.round(settings.shadowSize) }} px</span>
              </div>
              <input v-model.number="settings.shadowSize" class="inline-radius-slider" type="range" min="0" max="80" step="1" :disabled="!hasImage" />
            </div>
          </div>
          <div v-else-if="tool.id === 'resize'" class="crop-row">
            <span class="tool-row-label" :class="{ 'is-active': hasImage && activeTool === tool.id }">{{ t(tool.labelKey) }}</span>
            <div class="tool-row-buttons is-two">
              <button
                class="square-tool-button is-mode"
                :class="{ 'is-active': hasImage && activeTool === tool.id }"
                type="button"
                :aria-label="t(tool.labelKey)"
                :disabled="!hasImage"
                @click="showTool(tool.id)"
              >
                <component :is="tool.icon" :size="18" stroke-width="2.1" />
              </button>
              <button
                class="square-tool-button is-gear"
                :class="{ 'is-active': hasImage && activeTool === tool.id }"
                type="button"
                :disabled="!hasImage || activeTool !== tool.id"
                :aria-label="t('settings')"
                @click="openToolPanel('resize')"
              >
                <Settings2 :size="18" stroke-width="2.1" />
              </button>
              <button
                class="square-tool-button is-danger"
                :class="{ 'is-pending': resizePending }"
                type="button"
                :disabled="!hasImage || activeTool !== tool.id || activePanelTool !== null || !resizePending"
                :aria-label="t('resize')"
                @click="applyResizeToImage"
              >
                <Check :size="16" stroke-width="2.2" />
              </button>
            </div>
          </div>
          <div v-if="tool.id === 'resize' && activeTool === 'resize' && activePanelTool !== 'resize'" class="inline-radius-row">
            <div class="radio-stack">
              <label class="radio-row">
                <input v-model="resizeMode" type="radio" value="constrain" />
                <span>{{ t("constrainProportions") }}</span>
              </label>
              <label class="radio-row">
                <input v-model="resizeMode" type="radio" value="freeform" />
                <span>{{ t("freeform") }}</span>
              </label>
            </div>
          </div>
          <div v-else-if="tool.id === 'adjustments'" class="crop-row">
            <span class="tool-row-label" :class="{ 'is-active': hasImage && activeTool === tool.id }">{{ t("adjustments") }}</span>
            <div class="tool-row-buttons is-two">
              <button
                class="square-tool-button is-mode"
                :class="{ 'is-active': hasImage && activeTool === tool.id }"
                type="button"
                :aria-label="t('adjustments')"
                :disabled="!hasImage"
                @click="showTool(tool.id)"
              >
                <SlidersVertical :size="18" stroke-width="2.1" />
              </button>
              <button
                class="square-tool-button is-gear"
                :class="{ 'is-active': hasImage && activeTool === tool.id }"
                type="button"
                :disabled="!hasImage || activeTool !== tool.id"
                :aria-label="`${t('adjustments')} ${t('settings')}`"
                @click="openToolPanel('adjustments')"
              >
                <Settings2 :size="18" stroke-width="2.1" />
              </button>
              <button
                class="square-tool-button is-danger"
                :class="{ 'is-pending': adjustmentsPending }"
                type="button"
                :disabled="!hasImage || activeTool !== tool.id || activePanelTool !== null || !adjustmentsPending"
                :aria-label="t('adjustments')"
                @click="applyAdjustmentsToImage"
              >
                <Check :size="16" stroke-width="2.2" />
              </button>
            </div>
          </div>
          <div v-else-if="tool.id === 'text'" class="crop-row">
            <span class="tool-row-label" :class="{ 'is-active': hasImage && activeTool === tool.id }">{{ t(tool.labelKey) }}</span>
            <div class="tool-row-buttons is-two">
              <button
                class="square-tool-button is-mode"
                :class="{ 'is-active': hasImage && activeTool === tool.id }"
                type="button"
                :aria-label="t(tool.labelKey)"
                :disabled="!hasImage"
                @click="showTool(tool.id)"
              >
                <component :is="tool.icon" :size="18" stroke-width="2.1" />
              </button>
              <button
                class="square-tool-button is-gear"
                :class="{ 'is-active': hasImage && activeTool === tool.id }"
                type="button"
                :disabled="!hasImage || activeTool !== tool.id"
                :aria-label="t('settings')"
                @click="openToolPanel('text')"
              >
                <Settings2 :size="18" stroke-width="2.1" />
              </button>
              <button
                class="square-tool-button is-danger"
                :class="{ 'is-pending': textPending }"
                type="button"
                :disabled="!hasImage || activeTool !== tool.id || activePanelTool !== null || !textPending"
                :aria-label="t('text')"
                @click="() => applyTextToImage()"
              >
                <Check :size="16" stroke-width="2.2" />
              </button>
            </div>
          </div>
          <div v-if="tool.id === 'text' && activeTool === 'text' && activePanelTool !== 'text'" class="inline-radius-row">
            <label class="stacked-select">
              <span>{{ t("textField") }}</span>
              <input v-model="textState.content" type="text" :placeholder="t('textPlaceholder')" :disabled="!hasImage" />
            </label>
            <label class="stacked-select mt-3">
              <div class="inline-radius-header">
                <span class="inline-radius-label">{{ t("size") }}</span>
                <span class="inline-radius-value">{{ Math.round(textState.size) }} px</span>
              </div>
              <input v-model.number="textState.size" class="inline-radius-slider" type="range" min="10" :max="textSizeMaximum" step="1" :disabled="!hasImage" />
            </label>
            <label class="stacked-select mt-3">
              <div class="inline-radius-header">
                <span class="inline-radius-label">{{ t("freeRotate") }}</span>
                <span class="inline-radius-value">{{ Math.round(textState.rotation) }}°</span>
              </div>
              <input v-model.number="textState.rotation" class="inline-radius-slider" type="range" min="-180" max="180" step="1" :disabled="!hasImage" />
            </label>
            <button
              v-if="textState.content.trim()"
              class="action-button is-apply text-position-action rail-text-position-action mt-3"
              type="button"
              :disabled="!canEditTextPosition"
              @click="beginTextPosition"
            >
              <Move :size="16" stroke-width="2.4" />
              <span>{{ t("positionText") }}</span>
            </button>
          </div>
          <div v-else-if="tool.id === 'filters'" class="crop-row">
            <span class="tool-row-label" :class="{ 'is-active': hasImage && activeTool === tool.id }">{{ t("filters") }}</span>
            <div class="tool-row-buttons is-two">
              <button
                class="square-tool-button is-mode"
                :class="{ 'is-active': hasImage && activeTool === tool.id }"
                type="button"
                :aria-label="t('filters')"
                :disabled="!hasImage"
                @click="showTool(tool.id)"
              >
                <Palette :size="18" stroke-width="2.1" />
              </button>
              <button
                class="square-tool-button is-gear"
                :class="{ 'is-active': hasImage && activeTool === tool.id }"
                type="button"
                :disabled="!hasImage || activeTool !== tool.id"
                :aria-label="`${t('filters')} ${t('settings')}`"
                @click="openToolPanel('filters')"
              >
                <Settings2 :size="18" stroke-width="2.1" />
              </button>
              <button
                class="square-tool-button is-danger"
                :class="{ 'is-pending': filterPending }"
                type="button"
                :disabled="!hasImage || activeTool !== tool.id || activePanelTool !== null || !filterPending"
                :aria-label="t('filters')"
                @click="applyFilterToImage"
              >
                <Check :size="16" stroke-width="2.2" />
              </button>
            </div>
          </div>
          <div v-if="tool.id === 'filters' && activeTool === 'filters' && activePanelTool !== 'filters'" class="inline-radius-row">
            <label class="stacked-select">
              <span>{{ t("filterPreset") }}</span>
            <select v-model="settings.filter" data-crop-tutorial-filter-selector :disabled="!hasImage">
              <option value="none">{{ t("none") }}</option>
              <option value="colorreplace">{{ t("colorReplace") }}</option>
              <option value="bw">{{ t("bw") }}</option>
              <option value="sepia">{{ t("sepia") }}</option>
              <option value="oldfashioned">{{ t("oldFashioned") }}</option>
              <option value="vintage">{{ t("vintage") }}</option>
              <option value="greenify">{{ t("greenify") }}</option>
              <option value="redify">{{ t("redify") }}</option>
              <option value="bluify">{{ t("bluify") }}</option>
              <option value="purplify">{{ t("purplify") }}</option>
              <option value="brownify">{{ t("brownify") }}</option>
              <option value="cool">{{ t("coolTone") }}</option>
              <option value="warm">{{ t("warmTone") }}</option>
              <option value="cinematic">{{ t("cinematic") }}</option>
              <option value="matte">{{ t("matte") }}</option>
              <option value="cyanotype">{{ t("cyanotype") }}</option>
              <option value="glow">{{ t("warmGlow") }}</option>
              </select>
            </label>
            <div v-if="settings.filter === 'colorreplace'" class="color-replace-controls">
              <div class="color-replace-pair">
                <div
                  class="color-row stacked color-replace-color"
                  :class="{ 'is-active': !settings.colorReplaceTargetTransparent }"
                  role="button"
                  :tabindex="hasImage ? 0 : -1"
                  :aria-pressed="!settings.colorReplaceTargetTransparent"
                  :aria-disabled="!hasImage"
                  @click="settings.colorReplaceTargetTransparent = false"
                  @keydown.enter.prevent="settings.colorReplaceTargetTransparent = false"
                  @keydown.space.prevent="settings.colorReplaceTargetTransparent = false"
                >
                  <span>{{ t("targetColor") }}</span>
                  <input v-model="settings.colorReplaceTarget" type="color" :disabled="!hasImage" :aria-label="t('targetColor')" @click="settings.colorReplaceTargetTransparent = false" />
                </div>
                <button
                  class="color-replace-transparent"
                  :class="{ 'is-active': settings.colorReplaceTargetTransparent }"
                  type="button"
                  :disabled="!hasImage"
                  :aria-label="`${t('targetColor')}: ${t('transparent')}`"
                  :aria-pressed="settings.colorReplaceTargetTransparent"
                  :title="t('transparent')"
                  @mouseenter="showHoverHint(t('transparent'), $event)"
                  @mouseleave="hideHoverHint"
                  @focus="showHoverHint(t('transparent'), $event)"
                  @blur="hideHoverHint"
                  @click="settings.colorReplaceTargetTransparent = !settings.colorReplaceTargetTransparent"
                ></button>
              </div>
              <div class="color-replace-pair">
                <div
                  class="color-row stacked color-replace-color"
                  :class="{ 'is-active': !settings.colorReplaceReplacementTransparent }"
                  role="button"
                  :tabindex="hasImage ? 0 : -1"
                  :aria-pressed="!settings.colorReplaceReplacementTransparent"
                  :aria-disabled="!hasImage"
                  @click="settings.colorReplaceReplacementTransparent = false"
                  @keydown.enter.prevent="settings.colorReplaceReplacementTransparent = false"
                  @keydown.space.prevent="settings.colorReplaceReplacementTransparent = false"
                >
                  <span>{{ t("replacementColor") }}</span>
                  <input v-model="settings.colorReplaceReplacement" type="color" :disabled="!hasImage" :aria-label="t('replacementColor')" @click="settings.colorReplaceReplacementTransparent = false" />
                </div>
                <button
                  class="color-replace-transparent"
                  :class="{ 'is-active': settings.colorReplaceReplacementTransparent }"
                  type="button"
                  :disabled="!hasImage"
                  :aria-label="`${t('replacementColor')}: ${t('transparent')}`"
                  :aria-pressed="settings.colorReplaceReplacementTransparent"
                  :title="t('transparent')"
                  @mouseenter="showHoverHint(t('transparent'), $event)"
                  @mouseleave="hideHoverHint"
                  @focus="showHoverHint(t('transparent'), $event)"
                  @blur="hideHoverHint"
                  @click="settings.colorReplaceReplacementTransparent = !settings.colorReplaceReplacementTransparent"
                ></button>
              </div>
              <label class="stacked-select">
                <div class="control-header mb-1">
                  <span>{{ t("strength") }}</span>
                  <span class="value-pill is-active-mode">{{ Math.round(settings.colorReplaceStrength) }}%</span>
                </div>
                <input v-model.number="settings.colorReplaceStrength" class="range" type="range" min="0" max="100" step="1" :disabled="!hasImage" />
              </label>
              <label class="stacked-select">
                <div class="control-header mb-1">
                  <span>{{ t("smoothness") }}</span>
                  <span class="value-pill is-active-mode">{{ Math.round(settings.colorReplaceSmoothness) }}%</span>
                </div>
                <input v-model.number="settings.colorReplaceSmoothness" class="range" type="range" min="0" max="100" step="1" :disabled="!hasImage" />
              </label>
            </div>
          </div>
          <div v-else-if="tool.id === 'adjustments' && activeTool === 'adjustments' && activePanelTool !== 'adjustments'" class="inline-radius-row">
            <div class="inline-radius-header">
              <span class="inline-radius-label">{{ t("contrast") }}</span>
              <span class="inline-radius-value">{{ Math.round(settings.contrast) }}%</span>
            </div>
            <input v-model.number="settings.contrast" class="inline-radius-slider" type="range" min="0" max="200" step="1" :disabled="!hasImage" />
            <div class="inline-radius-header mt-3">
              <span class="inline-radius-label">{{ t("brightness") }}</span>
              <span class="inline-radius-value">{{ Math.round(settings.brightness) }}%</span>
            </div>
            <input v-model.number="settings.brightness" class="inline-radius-slider" type="range" min="0" max="200" step="1" :disabled="!hasImage" />
            <div class="inline-radius-header mt-3">
              <span class="inline-radius-label">{{ t("transparency") }}</span>
              <span class="inline-radius-value">{{ Math.round(settings.transparency) }}%</span>
            </div>
            <input v-model.number="settings.transparency" class="inline-radius-slider" type="range" min="0" max="100" step="1" :disabled="!hasImage" />
            <label class="toggle-row mt-3">
              <input v-model="settings.transparencyIncludeBorder" type="checkbox" class="accent-[rgb(var(--accent))]" :disabled="!hasImage || committedBorderLayers.length === 0" />
              <span>{{ t("includeBorder") }}</span>
            </label>
          </div>
        </template>
      </div>
      </div>

      <div class="rail-footer">
        <button
          class="rail-button is-export"
          :class="{ 'is-active': hasImage && activeTool === 'export' }"
          type="button"
          :disabled="!hasImage"
          :aria-label="t('export')"
          :title="`${t('export')} (${shortcutDisplayMap.export})`"
          @click="onToolClick('export')"
        >
          <span class="rail-icon"><Download :size="18" stroke-width="2.1" /></span>
          <span class="rail-label">{{ t('export') }}</span>
        </button>
        <button class="rail-button compact is-share-site" type="button" :aria-label="shareSiteCopy[language].label" @click="shareSite">
          <span class="rail-icon"><Share2 :size="18" stroke-width="2.1" /></span>
          <span class="rail-label">{{ shareSiteCopy[language].label }}</span>
        </button>
        <a class="rail-button compact" href="https://paypal.me/cutmypic" target="_blank" rel="noreferrer noopener">
          <span class="rail-icon"><Heart :size="18" stroke-width="2.1" /></span>
          <span class="rail-label">{{ t("donate") }}</span>
        </a>
      </div>
    </aside>

    <section class="workspace-shell">
      <div class="top-tabs-stack">
      <header class="top-tabs">
        <button v-if="fullscreenMode || isMobileViewport" class="top-brand-button" type="button" :aria-label="t('revert')" @click="resetApp">
          <img :src="logoSrc" alt="Cut My Pic" />
        </button>
        <div class="top-dimensions">
          <div class="size-readouts current-size-readout" aria-live="polite">
            <span>{{ workspaceHeaderCopy[language].currentSize }}</span>
            <strong>{{ uploadSize }}</strong>
          </div>
          <div class="size-readouts current-size-readout" aria-live="polite">
            <span>{{ workspaceHeaderCopy[language].selectedSize }}</span>
            <strong>{{ currentSize }}</strong>
          </div>
          <div class="size-readouts current-size-readout zoom-level-readout" aria-live="polite">
            <span>{{ workspaceHeaderCopy[language].zoomLevel }}</span>
            <strong>{{ zoomLevelReadout }}</strong>
          </div>
        </div>
        <div class="top-utility">
          <button class="theme-button top-gear-button" :class="{ 'is-active': showTopSettings }" type="button" :aria-label="t('settings')" @click="toggleTopSettings">
            <Cog :size="16" stroke-width="2.1" />
          </button>
          <Transition name="mobile-settings-sheet">
          <div v-show="showTopSettings" class="top-settings-popover">
            <div class="mobile-settings-header">
              <h2>{{ t("settings") }}</h2>
            </div>
            <div class="language-switcher" :aria-label="t('language')">
              <span class="language-switcher-label">
                <span class="language-flag" :aria-label="t('language')">{{ activeLanguageFlag }}</span>
                <span>{{ t("language") }}</span>
              </span>
              <button class="language-switcher-trigger" type="button" :aria-expanded="showLanguageMenu" :aria-label="t('language')" @click="showLanguageMenu = !showLanguageMenu">
                <span>{{ languageOptionLabel(languageOptions.find((option) => option.code === languageSelection) ?? languageOptions[0]) }}</span>
                <Menu :size="16" stroke-width="2.1" />
              </button>
              <div v-if="showLanguageMenu" class="language-switcher-menu" role="listbox">
                <button
                  v-for="option in languageOptions"
                  :key="option.code"
                  type="button"
                  class="language-switcher-option"
                  :class="{ 'is-selected': languageSelection === option.code }"
                  :aria-selected="languageSelection === option.code"
                  @click="setLanguageOverride(option.code)"
                >
                  {{ languageOptionLabel(option) }}
                </button>
              </div>
            </div>
            <div class="help-link-stack">
              <button v-if="!appInstalled && !isMobileViewport" class="manual-settings-button settings-save-tool-button" type="button" @click="openSaveTool">
                <Bookmark :size="16" stroke-width="2.1" aria-hidden="true" />
                <span>{{ saveToolActionLabel }}</span>
              </button>
              <a class="manual-settings-button" :href="getLocalizedManualUrl(language)" @click.prevent="openManualPage">
                {{ t("manualButton") }}
              </a>
              <a class="manual-settings-button" :href="getLocalizedGuideUrl(language)" @click.prevent="openGuidePage">
                {{ howItWorksLabelByLanguage[language] }}
              </a>
              <a class="manual-settings-button" :href="getLocalizedFaqUrl(language)" @click.prevent="openFaqPage">
                FAQ
              </a>
              <button class="manual-settings-button" type="button" @click="openShortcutHelp">
                {{ t("keyboardShortcuts") }}
              </button>
            </div>
            <div class="settings-bottom-row">
              <label class="settings-tutorial-toggle">
                <input v-model="cropTutorialEnabled" type="checkbox" />
                <span>{{ t("showTutorial") }}</span>
              </label>
              <div class="theme-switcher" :aria-label="t('theme')">
                <button class="theme-button" :class="{ 'is-active': theme === 'light' }" type="button" :aria-label="t('useLightMode')" @click="theme = 'light'">
                  <Sun :size="16" stroke-width="2.1" />
                </button>
                <button class="theme-button" :class="{ 'is-active': theme === 'dark' }" type="button" :aria-label="t('useDarkMode')" @click="theme = 'dark'">
                  <Moon :size="16" stroke-width="2.1" />
                </button>
              </div>
            </div>
            <section v-if="isMobileViewport" class="mobile-support-share-section" :aria-label="supportShareLabelByLanguage[language]">
              <h3>{{ supportShareLabelByLanguage[language] }}</h3>
              <div class="mobile-support-share-actions">
                <button v-if="!appInstalled" class="manual-settings-button settings-save-tool-button" type="button" @click="openSaveTool">
                  <Bookmark :size="18" stroke-width="2.1" aria-hidden="true" />
                  <span>{{ saveToolActionLabel }}</span>
                </button>
                <button class="manual-settings-button settings-share-site-button" type="button" @click="shareSite">
                  <Share2 :size="18" stroke-width="2.1" aria-hidden="true" />
                  <span>{{ shareSiteCopy[language].label }}</span>
                </button>
                <a class="manual-settings-button settings-donate-button" href="https://paypal.me/cutmypic" target="_blank" rel="noreferrer noopener">
                  <Heart :size="18" stroke-width="2.1" aria-hidden="true" />
                  <span>{{ t("donate") }}</span>
                </a>
              </div>
            </section>
          </div>
          </Transition>
        </div>
      </header>

      <Teleport to="body">
        <div v-if="isMobileViewport && hasImage && !showTopSettings" class="mobile-top-history-actions">
          <button
            ref="mobileHistoryButtonRef"
            class="theme-button top-gear-button has-badge is-history mobile-top-history-button"
            :class="{ 'is-active': historyPanelOpen }"
            type="button"
            :aria-label="t('editHistory')"
            @click="toggleMobileHistoryPanel"
          >
            <span class="history-icon" aria-hidden="true">⌘</span>
            <span class="history-badge">{{ historyStepCount }}</span>
          </button>
          <button class="theme-button top-gear-button is-danger mobile-clear-image-button" type="button" :aria-label="clearImageCopy[language]" @click="resetApp">
            <Trash2 :size="16" stroke-width="2.1" />
          </button>
        </div>
      </Teleport>

      </div>

      <div v-if="isGuidePage" class="manual-page">
        <div class="manual-shell how-it-works-shell">
          <button class="close-button floating-close-button" type="button" :aria-label="t('closePanel')" @click="openEditorPage">×</button>
          <div class="manual-hero">
            <img class="manual-logo" :src="logoSrc" alt="Cut My Pic" />
            <h1 class="manual-title">{{ howItWorksLabelByLanguage[language] }}</h1>
            <p class="manual-intro">{{ howItWorksIntroByLanguage[language] }}</p>
          </div>
          <div class="manual-content how-it-works-content">
            <section v-for="(step, index) in guideSteps" :key="step.title" class="manual-section how-it-works-step">
              <h2 class="manual-section-title"><span>{{ localizedGuideStepTitle(index) }}</span></h2>
              <img class="how-it-works-image" :src="step.image" :alt="step.alt" loading="lazy" />
              <p class="manual-paragraph" v-html="formatGuideText(step.summary)"></p>
              <p v-for="detail in step.details" :key="detail" class="manual-paragraph" v-html="formatGuideText(detail)"></p>
              <a class="guide-manual-link" :href="`${getLocalizedManualUrl(language)}#${['crop', 'filters', 'border', 'shadow', 'text', 'export'][index]}`" @click.prevent="openManualSection(['crop', 'filters', 'border', 'shadow', 'text', 'export'][index])">{{ t("manualButton") }}: {{ localizedGuideStepTitle(index).replace(/^\d+\.\s*/, "") }}</a>
            </section>
          </div>
          <nav class="manual-related-links" :aria-label="t('manualOverview')">
            <a :href="getLocalizedUrl(language)" @click.prevent="openEditorPage">{{ getManualCopy(language).back }}</a>
            <a :href="getLocalizedManualUrl(language)" @click.prevent="openManualPage">{{ t("manualButton") }}</a>
            <a :href="getLocalizedFaqUrl(language)" @click.prevent="openFaqPage">FAQ</a>
          </nav>
        </div>
      </div>
      <div v-else-if="isManualPage" class="manual-page">
        <div class="manual-shell" :class="{ 'has-drop-flash': dropFlashVisible }">
          <button class="close-button floating-close-button" type="button" :aria-label="t('closePanel')" @click="openEditorPage">
            ×
          </button>
          <div class="manual-hero">
            <img class="manual-logo" :src="logoSrc" alt="Cut My Pic" />
            <h1 class="manual-title">{{ getManualCopy(language).title }}</h1>
            <p class="manual-intro" v-html="formatManualParagraph(localizedSeoCopy[language].manualDescription, language)"></p>
          </div>

          <label class="manual-jump">
            <span>{{ getManualCopy(language).jump }}</span>
            <select v-model="manualJumpSelection" @change="scrollToManualSection(manualJumpSelection)">
              <option v-for="section in getManualContent(language)" :key="section.id" :value="section.id">
                {{ getManualSectionTitle(section) }}
              </option>
            </select>
          </label>

          <div class="manual-content">
            <section v-for="section in getManualContent(language)" :id="section.id" :key="section.id" class="manual-section">
              <h2 v-if="section.id !== 'intro'" class="manual-section-title">
                <span class="manual-section-icon" aria-hidden="true">
                  <component :is="manualSections.find((entry) => entry.id === section.id)?.icon ?? Target" :size="18" stroke-width="2.1" />
                </span>
                <span>{{ getManualSectionTitle(section) }}</span>
              </h2>
              <p v-for="paragraph in section.body" :key="paragraph" class="manual-paragraph" v-html="formatManualParagraph(paragraph)"></p>
            </section>
          </div>

          <nav class="manual-related-links" :aria-label="t('manualOverview')">
            <a :href="getLocalizedUrl(language)" @click.prevent="openEditorPage">{{ getManualCopy(language).back }}</a>
            <a :href="getLocalizedGuideUrl(language)" @click.prevent="openGuidePage">{{ howItWorksLabelByLanguage[language] }}</a>
            <a :href="getLocalizedFaqUrl(language)" @click.prevent="openFaqPage">{{ localizedFaqPageCopy.title }}</a>
          </nav>

        </div>
      </div>
      <div v-else-if="isFaqPage" class="manual-page">
        <div class="manual-shell manual-faq-shell" :class="{ 'has-drop-flash': dropFlashVisible }">
          <button class="close-button floating-close-button" type="button" :aria-label="t('closePanel')" @click="openEditorPage">
            ×
          </button>
          <div class="manual-hero">
            <img class="manual-logo" :src="logoSrc" alt="Cut My Pic" />
            <h1 class="manual-title">{{ localizedFaqPageCopy.title }}</h1>
            <p class="manual-intro">{{ localizedFaqPageCopy.intro }}</p>
          </div>

          <div class="manual-content">
            <section v-for="section in localizedFaqSectionsWithEntries" :key="section.title" class="manual-section">
              <h2 class="manual-section-title">
                <span class="manual-section-icon" aria-hidden="true">
                  <HelpCircle :size="18" stroke-width="2.1" />
                </span>
                <span>{{ section.title }}</span>
              </h2>
              <div class="manual-section-body">
                <article v-for="item in section.entries" :key="item.question" class="manual-faq-entry">
                  <h3 class="manual-faq-question">{{ item.question }}</h3>
                  <p v-if="item.answerHtml" class="manual-paragraph" v-html="item.answerHtml"></p>
                  <p v-else class="manual-paragraph">{{ item.answer }}</p>
                </article>
              </div>
            </section>
          </div>

          <nav class="manual-related-links" :aria-label="t('manualOverview')">
            <a :href="getLocalizedUrl(language)" @click.prevent="openEditorPage">{{ localizedFaqPageCopy.back }}</a>
            <a :href="getLocalizedManualUrl(language)" @click.prevent="openManualPage">{{ t("manualButton") }}</a>
            <a :href="getLocalizedGuideUrl(language)" @click.prevent="openGuidePage">{{ howItWorksLabelByLanguage[language] }}</a>
          </nav>
        </div>
      </div>
      <div v-else class="work-card">
        <h1 class="sr-only">{{ t("seoTitle") }}</h1>
        <div class="top-utility-tray-wrap">
          <div class="top-utility-tray" :class="{ 'is-visible': hasImage }">
            <button class="theme-button top-gear-button is-back" type="button" :disabled="!hasImage || previewHistory.length < 2" :aria-label="t('undo')" @mouseenter="showHoverHint(`${t('undo')} (Cmd+Z)`, $event)" @mouseleave="hideHoverHint" @focus="showHoverHint(`${t('undo')} (Cmd+Z)`, $event)" @blur="hideHoverHint" @click="stepBackPreview">
              <Undo2 :size="16" stroke-width="2.1" />
            </button>
            <button ref="historyButtonRef" class="theme-button top-gear-button has-badge is-history" :class="{ 'is-active': historyPanelOpen }" type="button" :disabled="!hasImage" :aria-label="t('editHistory')" @mouseenter="showHoverHint(`${t('editHistory')} (${shortcutDisplayMap.history})`, $event)" @mouseleave="hideHoverHint" @focus="showHoverHint(`${t('editHistory')} (${shortcutDisplayMap.history})`, $event)" @blur="hideHoverHint" @click="openHistoryPanel">
              <span class="history-icon" aria-hidden="true">⌘</span>
              <span class="history-badge">{{ historyStepCount }}</span>
            </button>
            <button class="theme-button top-gear-button is-danger clear-image-button" type="button" :disabled="!hasImage" :aria-label="clearImageCopy[language]" @mouseenter="showHoverHint(clearImageCopy[language], $event)" @mouseleave="hideHoverHint" @focus="showHoverHint(clearImageCopy[language], $event)" @blur="hideHoverHint" @click="resetApp">
              <Trash2 :size="16" stroke-width="2.1" />
            </button>
          </div>
        </div>
        <div class="view-background-tray-wrap">
          <div class="view-background-tray">
            <button
              class="theme-button view-background-checkerboard"
              :class="{ 'is-active': viewBackgroundMode === 'checkerboard' }"
              type="button"
              :aria-label="`${t('viewBackground')}: ${t('checkerboard')}`"
              :aria-pressed="viewBackgroundMode === 'checkerboard'"
              :title="t('checkerboard')"
              @mouseenter="showHoverHint(t('checkerboard'), $event)"
              @mouseleave="hideHoverHint"
              @focus="showHoverHint(t('checkerboard'), $event)"
              @blur="hideHoverHint"
              @click="viewBackgroundMode = 'checkerboard'"
            ></button>
            <input
              v-model="viewBackgroundColor"
              class="view-background-color"
              :class="{ 'is-active': viewBackgroundMode === 'color' }"
              type="color"
              :aria-label="t('viewBackground')"
              :title="t('viewBackground')"
              @mouseenter="showHoverHint(t('viewBackground'), $event)"
              @mouseleave="hideHoverHint"
              @focus="showHoverHint(t('viewBackground'), $event)"
              @blur="hideHoverHint"
              @input="viewBackgroundMode = 'color'"
            />
          </div>
        </div>
          <div
            ref="stageRef"
            class="stage relative flex-1 overflow-hidden bg-panel"
            :class="{ 'has-drop-flash': dropFlashVisible, 'ring-2 ring-accent': isDraggingFile, 'is-pannable': imagePanMode && canPanImage, 'is-panning': imagePanActive }"
            :style="stageCursorStyle"
            @pointerdown.capture="startImagePan"
            @pointermove="moveImagePan"
            @pointerup="endImagePan"
            @pointercancel="endImagePan"
          >
          <Transition name="autosave-notice">
            <div v-if="unsupportedFileNoticeVisible" class="autosave-recovery-notice unsupported-file-notice" role="alert" aria-live="assertive">
              <AlertCircle :size="18" :stroke-width="2.4" aria-hidden="true" />
              <span>{{ unsupportedFileTypeCopy[language] }}</span>
              <button class="autosave-recovery-dismiss" type="button" :aria-label="t('closePanel')" @click="dismissUnsupportedFileNotice">
                <span aria-hidden="true">×</span>
              </button>
            </div>
          </Transition>
          <Transition name="autosave-notice">
            <div v-if="autosaveRecoveryNoticeOpen" class="autosave-recovery-notice" role="status" aria-live="polite">
              <Check :size="18" :stroke-width="2.6" aria-hidden="true" />
              <span>{{ autosaveRecoveredMessage }}</span>
              <button class="autosave-recovery-dismiss" type="button" :aria-label="t('closePanel')" @click="autosaveRecoveryNoticeOpen = false">
                <span aria-hidden="true">×</span>
              </button>
            </div>
          </Transition>
          <Transition name="autosave-notice">
            <div v-if="workflowStatusMessage" class="workflow-run-status" role="status" aria-live="polite">
              <span v-if="workflowApplying" class="workflow-run-spinner" aria-hidden="true"></span>
              <Scissors v-else-if="workflowAwaitingCrop" :size="17" :stroke-width="2.4" aria-hidden="true" />
              <Check v-else :size="17" :stroke-width="2.5" aria-hidden="true" />
              <span>{{ workflowStatusMessage }}</span>
              <button v-if="workflowApplying || workflowAwaitingCrop" class="workflow-run-cancel" type="button" @click="cancelWorkflowPlayback">
                Cancel
              </button>
            </div>
          </Transition>
          <Transition name="autosave-notice">
            <div v-if="shareSiteStatusMessage" class="autosave-recovery-notice share-site-notice" role="status" aria-live="polite">
              <Share2 :size="18" :stroke-width="2.4" aria-hidden="true" />
              <span>{{ shareSiteStatusMessage }}</span>
            </div>
          </Transition>
          <canvas ref="canvasRef" class="absolute inset-0 h-full w-full"></canvas>
          <Transition name="mobile-image-loading">
            <div
              v-if="isMobileViewport && imageLoading"
              class="mobile-image-loading-overlay"
              role="status"
              aria-live="polite"
              :aria-label="t('loadingImage')"
            >
              <span class="mobile-image-loading-spinner" aria-hidden="true"></span>
              <span class="sr-only">{{ t("loadingImage") }}</span>
            </div>
          </Transition>
          <div class="comparison-labels" :class="{ 'is-active': comparisonLabelsVisible }" :aria-hidden="!comparisonLabelsVisible">
            <span class="comparison-side-label is-before">{{ comparisonCopy.before }}</span>
            <span class="comparison-side-label is-after">{{ comparisonCopy.after }}</span>
          </div>
          <div v-if="comparisonMode" class="comparison-overlay" aria-hidden="false">
            <button
              class="comparison-divider"
              type="button"
              role="slider"
              :style="comparisonDividerStyle"
              :aria-label="comparisonCopy.compare"
              aria-valuemin="0"
              aria-valuemax="100"
              :aria-valuenow="Math.round(comparisonPosition)"
              @pointerdown="startComparisonDrag"
              @pointermove="moveComparisonDivider"
              @pointerup="finishComparisonDrag($event.pointerId)"
              @pointercancel="finishComparisonDrag($event.pointerId)"
              @keydown="onComparisonDividerKeydown"
            >
              <span class="comparison-divider-handle" aria-hidden="true">
                <ArrowLeftRight :size="16" :stroke-width="2.4" />
              </span>
            </button>
          </div>
          <button
            v-if="circleAspectLockVisible"
            class="aspect-lock-toggle"
            :class="{ 'is-unlocked': !circleAspectLocked }"
            type="button"
            :style="circleAspectLockStyle"
            :aria-label="circleAspectLocked ? ui('unlockAspect') : ui('lockAspect')"
            @click.stop="toggleCircleAspectLock"
          >
            <component :is="circleAspectLocked ? Lock : Unlock" :size="15" stroke-width="2.3" />
          </button>
          <div
            v-if="hasImage && !comparisonMode && textPanelMode === 'text' && textPositionMode"
            class="stamp-position-overlay text-position-screen-lock"
          >
            <div class="text-position-anchor" :style="{ left: `${textPositionPoint.left}px`, top: `${textPositionPoint.top}px` }">
              <button
                class="text-preview-chip"
                type="button"
                :aria-label="dragItemLabel(textState.content)"
                @pointerdown.prevent.stop="beginTextPositionDrag"
                @pointermove.prevent.stop="onTextPositionDrag"
                @pointerup.prevent.stop="endTextPositionDrag"
                @pointercancel.prevent.stop="endTextPositionDrag"
              >
                <span
                  class="text-preview-placement"
                  :style="textPreviewStyle"
                >{{ textState.content }}</span>
              </button>
            </div>
            <button class="stamp-place-button text-place-button" type="button" :disabled="!canEditTextPosition" :style="textPlaceButtonStyle" @pointerdown.stop @pointerup.stop @click.stop="confirmTextPosition">
              <Move :size="15" stroke-width="2.4" />
              <span>{{ t("place") }}</span>
            </button>
            <button class="stamp-cancel-button theme-button top-gear-button is-danger" type="button" :aria-label="t('closePanel')" :style="textCancelButtonStyle" @pointerdown.prevent.stop="cancelTextPosition" @pointerup.stop @click.stop>
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <div
            v-if="hasImage && !comparisonMode && textPanelMode === 'stamp' && stampPositionMode"
            class="stamp-position-overlay"
          >
            <div class="stamp-position-anchor" :style="{ left: `${stampPositionPoint.left}px`, top: `${stampPositionPoint.top}px` }">
              <button
                class="stamp-preview-chip"
                type="button"
                :aria-label="dragItemLabel(stampPositionLabel)"
              >
                <span
                  v-if="selectedStampPreset?.kind === 'emoji'"
                  class="stamp-preview-glyph"
                  :style="{ color: textState.color, fontSize: `${placementFontSize}px`, transform: `rotate(${textState.rotation}deg)` }"
                  @pointerdown.prevent.stop="beginStampPositionDrag"
                  @pointermove.prevent.stop="onStampPositionDrag"
                  @pointerup.prevent.stop="endStampPositionDrag"
                  @pointercancel.prevent.stop="endStampPositionDrag"
                >{{ textState.content }}</span>
                <span
                  v-else
                  ref="stampPreviewRef"
                  class="stamp-preview-price-tag"
                  :style="{
                    '--stamp-bg': selectedStampPreset?.backgroundColor,
                    '--stamp-fg': selectedStampPreset?.textColor,
                    '--stamp-border': selectedStampPreset?.borderColor,
                    '--stamp-preview-min-width': `${118 * (textState.size / Math.max(1, selectedStampPreset?.size ?? 54))}px`,
                    '--stamp-preview-min-height': `${44 * (textState.size / Math.max(1, selectedStampPreset?.size ?? 54))}px`,
                    '--stamp-preview-pad-y': `${5 * (textState.size / Math.max(1, selectedStampPreset?.size ?? 54))}px`,
                    '--stamp-preview-pad-right': `${10 * (textState.size / Math.max(1, selectedStampPreset?.size ?? 54))}px`,
                    '--stamp-preview-pad-left': `${30 * (textState.size / Math.max(1, selectedStampPreset?.size ?? 54))}px`,
                    '--stamp-preview-radius': `${8 * (textState.size / Math.max(1, selectedStampPreset?.size ?? 54))}px`,
                    '--stamp-preview-notch': `${28 * (textState.size / Math.max(1, selectedStampPreset?.size ?? 54))}px`,
                    '--stamp-preview-hole-left': `${16 * (textState.size / Math.max(1, selectedStampPreset?.size ?? 54))}px`,
                    '--stamp-preview-hole-size': `${8 * (textState.size / Math.max(1, selectedStampPreset?.size ?? 54))}px`,
                    fontSize: `${Math.max(13, Math.round(textState.size * canvasMetrics.scale * 0.34))}px`,
                    transform: `rotate(${textState.rotation}deg)`,
                  }"
                  @pointerdown.prevent.stop="beginStampPositionDrag"
                  @pointermove.prevent.stop="onStampPositionDrag"
                  @pointerup.prevent.stop="endStampPositionDrag"
                  @pointercancel.prevent.stop="endStampPositionDrag"
                >{{ textState.content }}</span>
              </button>
            </div>
            <button class="stamp-place-button" type="button" :disabled="!canEditStampPosition" :style="stampPlaceButtonStyle" @pointerdown.stop @pointerup.stop @click.stop="confirmStampPosition">
              Place stamp
            </button>
            <button class="stamp-cancel-button theme-button top-gear-button is-danger" type="button" :aria-label="t('closePanel')" :style="stampCancelButtonStyle" @pointerdown.prevent.stop="cancelStampPosition" @pointerup.stop @click.stop>
              <span aria-hidden="true">×</span>
            </button>
          </div>

          <div class="view-overlay" :class="{ 'is-empty': !hasImage, 'has-pan': canPanImage, 'is-mobile-zoom-open': mobileZoomOpen }">
            <label class="view-overlay-label" for="view-zoom-slider">{{ t("zoom") }}</label>
            <div class="view-overlay-slider-wrap">
              <input
                id="view-zoom-slider"
                v-model.number="settings.zoom"
                class="view-overlay-slider"
                type="range"
                :min="zoomSliderMin"
                :max="zoomSliderMax"
                step="0.01"
                :disabled="!hasImage"
                :class="{ 'is-mobile-zoom-open': mobileZoomOpen }"
                @pointerdown="showZoomTooltip"
                @pointerup="hideZoomTooltip"
                @pointercancel="hideZoomTooltip"
                @change="hideZoomTooltip"
                @blur="hideZoomTooltip"
              />
              <output v-show="zoomTooltipVisible && hasImage" class="zoom-drag-tooltip" :style="zoomTooltipStyle">
                {{ zoomLevelReadout }}
              </output>
            </div>
            <button
              class="view-overlay-center view-overlay-compare"
              :class="{ 'is-active': comparisonMode }"
              type="button"
              :disabled="!canCompareImages"
              :aria-pressed="comparisonMode"
              :aria-label="comparisonCopy.compare"
              @mouseenter="showHoverHint(comparisonCopy.compare, $event)"
              @mouseleave="hideHoverHint"
              @focus="showHoverHint(comparisonCopy.compare, $event)"
              @blur="hideHoverHint"
              @click="toggleComparisonMode"
            >
              <Columns2 :size="15" stroke-width="2.1" />
            </button>
            <button
              class="view-overlay-center view-overlay-auto-center"
              :class="{ 'is-auto-center-off': !autoCenterEnabled }"
              type="button"
              :disabled="!hasImage"
              :aria-pressed="autoCenterEnabled"
              :aria-label="autoCenterEnabled ? workspacePositionCopy[language].autoCenterOn : workspacePositionCopy[language].autoCenterOff"
              @mouseenter="showHoverHint(autoCenterEnabled ? workspacePositionCopy[language].autoCenterOn : workspacePositionCopy[language].autoCenterOff, $event)"
              @mouseleave="hideHoverHint"
              @focus="showHoverHint(autoCenterEnabled ? workspacePositionCopy[language].autoCenterOn : workspacePositionCopy[language].autoCenterOff, $event)"
              @blur="hideHoverHint"
              @click="centerImageInView"
            >
              <Target :size="15" stroke-width="2.1" />
            </button>
            <button
              class="view-overlay-center view-overlay-pan"
              :class="{ 'is-active': imagePanMode }"
              type="button"
              :disabled="!hasImage"
              :aria-pressed="imagePanMode"
              :aria-label="workspacePositionCopy[language].moveImage"
              @mouseenter="showHoverHint(workspacePositionCopy[language].moveImage, $event)"
              @mouseleave="hideHoverHint"
              @focus="showHoverHint(workspacePositionCopy[language].moveImage, $event)"
              @blur="hideHoverHint"
              @click="toggleImagePanMode"
            >
              <Move :size="15" stroke-width="2.1" />
            </button>
            <button
              class="view-overlay-fullscreen"
              type="button"
              :disabled="!hasImage"
              :aria-label="fullscreenMode ? t('exitFullscreen') : t('enterFullscreen')"
              @mouseenter="showHoverHint(fullscreenMode ? t('exitFullscreen') : t('enterFullscreen'), $event)"
              @mouseleave="hideHoverHint"
              @focus="showHoverHint(fullscreenMode ? t('exitFullscreen') : t('enterFullscreen'), $event)"
              @blur="hideHoverHint"
              @click="toggleFullscreenMode"
            >
              <component :is="fullscreenMode ? Minimize2 : Maximize2" :size="15" stroke-width="2.1" />
            </button>
            <Teleport to="body">
              <button v-if="!historyPanelOpen && !mobileViewBackgroundOpen && (mobileApplyAvailable || mobileBackAvailable)" class="mobile-apply-corner-button" type="button" :aria-label="mobileBackAvailable ? t('undo') : activeTool === 'crop' ? t('freeCut') : t('apply')" @click="applyCurrentToolAction">
                <Undo2 v-if="mobileBackAvailable" :size="22" stroke-width="2.1" />
                <Scissors v-else-if="activeTool === 'crop'" :size="22" stroke-width="2.1" />
                <Check v-else :size="22" stroke-width="2.4" />
              </button>
              <TransitionGroup name="mobile-quick-action" tag="div" class="mobile-quick-actions" :class="{ 'is-open': mobileQuickActionsOpen }">
                <button v-if="mobileQuickActionsOpen" key="download" class="mobile-quick-action" type="button" :aria-label="t('downloadPng')" @click="chooseMobileQuickAction('download')">
                  <Download :size="21" stroke-width="2.1" />
                </button>
                <button v-if="mobileQuickActionsOpen" key="background" class="mobile-quick-action" type="button" :aria-label="t('viewBackground')" :aria-pressed="mobileViewBackgroundOpen" @click="chooseMobileQuickAction('background')">
                  <Palette :size="21" stroke-width="2.1" />
                </button>
                <button v-if="mobileQuickActionsOpen" key="zoom" class="mobile-quick-action" type="button" :aria-label="t('zoom')" :aria-pressed="mobileZoomOpen" @click="chooseMobileQuickAction('zoom')">
                  <ZoomIn :size="21" stroke-width="2.1" />
                </button>
                <button v-if="mobileQuickActionsOpen" key="modes" class="mobile-quick-action" type="button" :aria-label="t('tools')" @click="chooseMobileQuickAction('modes')">
                  <Settings2 :size="21" stroke-width="2.1" />
                </button>
              </TransitionGroup>
              <button v-if="!historyPanelOpen && !mobileViewBackgroundOpen" class="view-overlay-mobile-menu" type="button" :disabled="!hasImage" :aria-label="mobileQuickActionsOpen ? t('closePanel') : t('tools')" :aria-pressed="mobileQuickActionsOpen" @click="toggleMobileQuickActions">
                <Menu :size="22" stroke-width="2.1" />
              </button>
            </Teleport>
          </div>

          <div
            v-if="fullscreenMode"
            class="fullscreen-mode-rail"
            :style="{ left: `${fullscreenRailPosition.x}px`, top: `${fullscreenRailPosition.y}px` }"
          >
            <button
              class="fullscreen-mode-rail-handle"
              type="button"
              :aria-label="t('moveLayer')"
              @pointerdown="startFullscreenRailDrag"
              @pointermove="onFullscreenRailMove"
              @pointerup="endFullscreenRailDrag"
              @pointercancel="endFullscreenRailDrag"
            >
              <span class="fullscreen-mode-rail-handle-bar"></span>
            </button>
            <button
              v-for="tool in tools"
              :key="tool.id"
              class="fullscreen-mode-button"
              :class="{ 'is-active': hasImage && activeTool === tool.id, 'is-export': tool.id === 'export' }"
              type="button"
              :disabled="!hasImage"
              :aria-label="t(tool.labelKey)"
              @mouseenter="showHoverHint(railToolHint(tool.id) || t(tool.labelKey), $event)"
              @mouseleave="hideHoverHint"
              @focus="showHoverHint(railToolHint(tool.id) || t(tool.labelKey), $event)"
              @blur="hideHoverHint"
              @click="onToolClick(tool.id)"
            >
              <component :is="tool.icon" :size="18" stroke-width="2.1" />
            </button>
          </div>

          <Transition name="mobile-mode-sheet">
          <div v-if="mobileToolMenuOpen" class="mobile-tool-sheet" role="dialog" aria-modal="true" :aria-label="t('tools')">
            <button class="mobile-tool-sheet-close" type="button" :aria-label="t('closePanel')" @click="mobileToolMenuOpen = false">×</button>
            <button
              v-for="tool in tools"
              :key="tool.id"
              class="mobile-tool-sheet-button"
              :class="{ 'is-active': hasImage && activeTool === tool.id, 'is-export': tool.id === 'export' }"
              type="button"
              :disabled="!hasImage"
              :aria-label="t(tool.labelKey)"
              @click="onToolClick(tool.id)"
            >
              <span class="mobile-tool-sheet-icon"><component :is="tool.icon" :size="18" stroke-width="2.1" /></span>
              <span class="mobile-tool-sheet-label">{{ t(tool.labelKey) }}</span>
            </button>
          </div>
          </Transition>

          <Teleport to="body">
            <Transition name="mobile-mode-sheet">
              <section
                v-if="isMobileViewport && mobileViewBackgroundOpen"
                class="mobile-view-background-sheet"
                role="dialog"
                aria-modal="true"
                :aria-label="t('viewBackground')"
              >
                <header class="mobile-view-background-header">
                  <h2>{{ t("viewBackground") }}</h2>
                  <button class="mobile-view-background-close" type="button" :aria-label="t('closePanel')" @click="mobileViewBackgroundOpen = false">×</button>
                </header>
                <div class="mobile-view-background-options">
                  <button
                    class="mobile-view-background-option"
                    :class="{ 'is-active': viewBackgroundMode === 'checkerboard' }"
                    type="button"
                    :aria-pressed="viewBackgroundMode === 'checkerboard'"
                    @click="viewBackgroundMode = 'checkerboard'"
                  >
                    <span class="mobile-view-background-checkerboard" aria-hidden="true"></span>
                    <span>{{ t("checkerboard") }}</span>
                  </button>
                  <label class="mobile-view-background-option" :class="{ 'is-active': viewBackgroundMode === 'color' }">
                    <input
                      v-model="viewBackgroundColor"
                      class="mobile-view-background-color"
                      type="color"
                      :aria-label="t('viewBackground')"
                      @click="viewBackgroundMode = 'color'"
                      @input="viewBackgroundMode = 'color'"
                    />
                    <span>{{ viewBackgroundColor.toUpperCase() }}</span>
                  </label>
                </div>
              </section>
            </Transition>
          </Teleport>

          <Teleport to="body">
            <Transition name="save-tool-offer">
              <aside v-if="saveToolOfferVisible" class="save-tool-offer" role="dialog" aria-modal="false" aria-labelledby="save-tool-offer-title">
                <button class="save-tool-offer-close" type="button" :aria-label="t('closePanel')" @click="dismissSaveToolOffer">×</button>
                <span class="save-tool-offer-icon" aria-hidden="true"><Bookmark :size="20" stroke-width="2.1" /></span>
                <div class="save-tool-offer-copy">
                  <strong id="save-tool-offer-title">{{ currentSaveToolCopy.postTitle }}</strong>
                  <span>{{ currentSaveToolCopy.postBody }}</span>
                </div>
                <div class="save-tool-offer-actions">
                  <button class="save-tool-offer-primary" type="button" @click="openSaveTool">{{ saveToolActionLabel }}</button>
                  <button class="save-tool-offer-secondary" type="button" @click="dismissSaveToolOffer">{{ currentSaveToolCopy.notNow }}</button>
                </div>
              </aside>
            </Transition>
          </Teleport>

          <Teleport to="body">
            <div v-if="saveToolDialogOpen" class="export-dialog-backdrop save-tool-dialog-backdrop" @click.self="saveToolDialogOpen = false">
              <section class="export-dialog export-confirm save-tool-dialog" role="dialog" aria-modal="true" aria-labelledby="save-tool-dialog-title">
                <button class="close-button floating-close-button" type="button" :aria-label="t('closePanel')" @click="saveToolDialogOpen = false">×</button>
                <span class="save-tool-dialog-icon" aria-hidden="true"><Bookmark :size="25" stroke-width="2.1" /></span>
                <h2 id="save-tool-dialog-title" class="section-title">{{ currentSaveToolCopy.title }}</h2>
                <p class="save-tool-dialog-intro">{{ currentSaveToolCopy.intro }}</p>
                <ol class="save-tool-instructions">
                  <li v-for="instruction in saveToolInstructions" :key="instruction">{{ instruction }}</li>
                </ol>
                <button class="export-dialog-button" type="button" @click="saveToolDialogOpen = false">{{ t("tutorialOk") }}</button>
              </section>
            </div>
          </Teleport>

          <div v-if="exportSavePromptOpen" class="export-dialog-backdrop" @click.self="declineSaveBeforeExport">
            <section class="export-dialog export-confirm" role="dialog" aria-modal="true" :aria-label="t('downloadNewImage')">
              <h2 class="section-title">{{ t("saveLastChanges") }}</h2>
              <div class="export-confirm-actions">
                <button class="export-dialog-button" type="button" @click="confirmSaveBeforeExport">
                  {{ t("yes") }}
                </button>
                <button class="export-dialog-button is-secondary" type="button" @click="declineSaveBeforeExport">
                  {{ t("no") }}
                </button>
              </div>
            </section>
          </div>

          <div v-if="exportDialogOpen" class="export-dialog-backdrop" @click.self="exportDialogOpen = false">
            <section class="export-dialog" role="dialog" aria-modal="true" :aria-label="exportDialogTitle()">
              <img class="export-dialog-logo" :src="logoSrc" alt="Cut My Pic" />
              <button class="close-button floating-close-button export-close-button" type="button" :aria-label="t('closePanel')" @click="exportDialogOpen = false">×</button>
              <div class="export-dialog-body">
                <div class="export-preview-section">
                  <div class="export-preview-wrap">
                    <img v-if="exportPreviewUrl" class="export-preview-image" :src="exportPreviewUrl" alt="" />
                  </div>
                  <div v-if="exportHasUnappliedChanges" class="export-unapplied-warning" role="status">
                    <span class="export-unapplied-warning-icon" aria-hidden="true">!</span>
                    <span>{{ t("unappliedChangesWarning") }}</span>
                  </div>
                </div>
                <div class="export-dialog-scroll">
                  <div class="control-header export-format-header">
                    <h2 class="section-title">{{ exportDialogTitle() }}</h2>
                    <div class="export-format-options" role="radiogroup" :aria-label="exportDialogTitle()">
                      <label v-for="option in exportFormatOptions" :key="option.id" class="export-format-option">
                        <input v-model="exportFormat" type="radio" name="export-format" :value="option.id" />
                        <span>{{ option.label }}</span>
                      </label>
                    </div>
                  </div>
                  <div v-if="exportFormat !== 'png'" class="export-quality-control">
                    <div class="control-header">
                      <label class="section-title" for="export-quality">{{ t("quality") }}</label>
                      <span class="value-pill">{{ exportQuality }}%</span>
                    </div>
                    <input
                      id="export-quality"
                      v-model.number="exportQuality"
                      class="range"
                      type="range"
                      min="10"
                      max="100"
                      step="1"
                      :aria-label="t('quality')"
                    />
                  </div>
                  <label v-if="exportFormat === 'jpg'" class="export-jpg-color-control">
                    <span class="section-title">{{ t("transparentAreaColor") }}</span>
                    <span class="export-jpg-color-picker">
                      <input
                        v-model="exportJpegBackgroundColor"
                        type="color"
                        :aria-label="t('transparentAreaColor')"
                      />
                      <span>{{ exportJpegBackgroundColor.toUpperCase() }}</span>
                    </span>
                  </label>
                  <button class="export-dialog-button" type="button" :disabled="!hasImage" @click="downloadExportedImage()">
                    {{ t("downloadPng") }}
                  </button>
                  <button class="export-dialog-button is-secondary" type="button" :disabled="!hasImage" @click="copyExportedImage()">
                    {{ t("copyImage") }}
                  </button>
                  <div v-if="shareClipboardNoticeOpen" class="export-inline-clipboard-status" role="status" aria-live="polite">
                    <Check class="export-inline-clipboard-icon" :size="18" :stroke-width="2.6" aria-hidden="true" />
                    <div class="export-inline-clipboard-copy">
                      <span>{{ t("shareCopiedMessage") }}</span>
                      <span v-if="shareOpeningNowOpen" class="share-opening-status is-inline">
                        <span class="share-opening-spinner" aria-hidden="true"></span>
                        <span class="share-opening-text">{{ t("openingNow") }}</span>
                      </span>
                    </div>
                  </div>
                  <div class="share-section">
                    <p class="share-section-title">{{ t("shareTo") }}</p>
                    <div class="share-grid">
                      <button class="share-link-button" type="button" :disabled="!hasImage" @click="shareToNetwork(currentShareUrl())">
                        <span class="share-icon-badge" :style="shareBadgeStyle('share')">↗</span>
                        {{ t("shareImage") }}
                      </button>
                      <button class="share-link-button" type="button" :disabled="!hasImage" @click="shareToNetwork(`https://twitter.com/intent/tweet?text=${encodeURIComponent(currentShareText())}&url=${encodeURIComponent(currentShareUrl())}`)">
                        <span class="share-icon-badge" :style="shareBadgeStyle('x')">X</span>
                        X
                      </button>
                      <button class="share-link-button" type="button" :disabled="!hasImage" @click="shareToNetwork(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentShareUrl())}`)">
                        <span class="share-icon-badge" :style="shareBadgeStyle('facebook')">f</span>
                        Facebook
                      </button>
                      <button class="share-link-button" type="button" :disabled="!hasImage" @click="shareToNetwork(`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(currentShareUrl())}`)">
                        <span class="share-icon-badge" :style="shareBadgeStyle('line')">L</span>
                        LINE
                      </button>
                      <button class="share-link-button" type="button" :disabled="!hasImage" @click="shareToNetwork(`https://wa.me/?text=${encodeURIComponent(`${currentShareText()} ${currentShareUrl()}`)}`)">
                        <span class="share-icon-badge" :style="shareBadgeStyle('whatsapp')">WA</span>
                        WhatsApp
                      </button>
                      <button class="share-link-button" type="button" :disabled="!hasImage" @click="shareToNetwork(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentShareUrl())}`)">
                        <span class="share-icon-badge" :style="shareBadgeStyle('linkedin')">in</span>
                        LinkedIn
                      </button>
                      <button class="share-link-button" type="button" :disabled="!hasImage" @click="shareToNetwork('https://www.weibo.com')">
                        <span class="share-icon-badge" :style="shareBadgeStyle('weibo')">微</span>
                        {{ t("openWeibo") }}
                      </button>
                      <button class="share-link-button" type="button" :disabled="!hasImage" @click="shareToNetwork('https://www.weixin.qq.com')">
                        <span class="share-icon-badge" :style="shareBadgeStyle('wechat')">微</span>
                        {{ t("openWeChat") }}
                      </button>
                      <button class="share-link-button" type="button" :disabled="!hasImage" @click="shareToNetwork('https://www.douyin.com')">
                        <span class="share-icon-badge" :style="shareBadgeStyle('douyin')">抖</span>
                        {{ t("openDouyin") }}
                      </button>
                      <button class="share-link-button" type="button" :disabled="!hasImage" @click="shareToNetwork('https://www.xiaohongshu.com')">
                        <span class="share-icon-badge" :style="shareBadgeStyle('xiaohongshu')">小</span>
                        {{ t("openXiaohongshu") }}
                      </button>
                      <button class="share-link-button" type="button" :disabled="!hasImage" @click="shareToNetwork('https://im.qq.com')">
                        <span class="share-icon-badge" :style="shareBadgeStyle('qq')">QQ</span>
                        {{ t("openQQ") }}
                      </button>
                      <button class="share-link-button" type="button" :disabled="!hasImage" @click="shareToNetwork('https://sharechat.com')">
                        <span class="share-icon-badge" :style="shareBadgeStyle('sharechat')">SC</span>
                        ShareChat
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div v-if="borderShadowWarningOpen" class="export-dialog-backdrop" @click.self="borderShadowWarningOpen = false">
            <section class="export-dialog export-confirm border-shadow-warning-dialog" role="dialog" aria-modal="true" :aria-label="ui('borderWarningTitle')">
              <h2 class="section-title export-confirm-title">{{ ui("borderWarningTitle") }}</h2>
              <p class="export-dialog-note export-confirm-message">
                {{ ui("borderWarningBody") }}
              </p>
              <div class="export-confirm-actions">
                <button class="export-dialog-button" type="button" @click="borderShadowWarningOpen = false">
                  {{ t("tutorialOk") }}
                </button>
              </div>
            </section>
          </div>

          <div v-if="shortcutHelpOpen" class="export-dialog-backdrop" @click.self="shortcutHelpOpen = false">
            <section class="export-dialog shortcut-dialog" role="dialog" aria-modal="true" :aria-label="t('keyboardShortcuts')">
              <button class="close-button floating-close-button" type="button" :aria-label="t('closePanel')" @click="shortcutHelpOpen = false">×</button>
              <div class="export-dialog-body">
                <div class="export-dialog-scroll">
                  <div class="control-header">
                    <h2 class="section-title">{{ t("keyboardShortcuts") }}</h2>
                  </div>
                  <div class="shortcut-list">
                    <div class="shortcut-row"><span>Cmd/Ctrl+Z</span><span>{{ t("undo") }}</span></div>
                    <div class="shortcut-row"><span>Cmd/Ctrl+Shift+Z</span><span>{{ t("redo") }}</span></div>
                    <div class="shortcut-row"><span>Cmd/Ctrl+S</span><span>{{ t("exportShortcut") }}</span></div>
                    <div class="shortcut-row"><span>Cmd/Ctrl+Shift+H</span><span>{{ t("historyShortcut") }}</span></div>
                    <div class="shortcut-row"><span>Cmd/Ctrl+Enter</span><span>{{ t("applyCurrentEdit") }}</span></div>
                    <div class="shortcut-row"><span>1-8</span><span>{{ t("switchTools") }}</span></div>
                    <div class="shortcut-row"><span>Esc</span><span>{{ t("closePanels") }}</span></div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <Teleport to="body" :disabled="!isMobileViewport">
            <Transition name="mobile-history-sheet">
            <section
              v-if="historyPanelOpen"
              class="floating-panel history-panel"
              :style="historyPanelStyle"
              @pointerdown="startHistoryDrag"
              @pointermove="onHistoryMove"
              @pointerup="endHistoryDrag"
              @pointercancel="endHistoryDrag"
            >
            <button class="close-button floating-close-button" type="button" :aria-label="t('closePanel')" @pointerdown.stop @click.stop="closeHistoryPanel">×</button>
            <div class="history-dialog floating-body">
              <div class="floating-panel-header control-header history-dialog-header">
                <div class="floating-panel-header-main">
                  <div class="history-dialog-header-copy">
                    <div class="history-dialog-title-row">
                      <h2 class="section-title">{{ t("editHistory") }}</h2>
                      <span class="section-meta history-step-count">{{ ui("stepCount", { count: historyStepCount }) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="workflowLibraryOpen" class="workflow-library" @pointerdown.stop>
                <div v-if="!savedWorkflows.length" class="workflow-library-empty">
                  <FolderOpen :size="28" :stroke-width="1.7" aria-hidden="true" />
                  <strong>{{ ui("noSavedWorkflows") }}</strong>
                  <span>{{ ui("workflowHelp") }}</span>
                </div>
                <article v-for="workflow in savedWorkflows" :key="workflow.id" class="workflow-library-item">
                  <div class="workflow-library-copy">
                    <strong>{{ workflow.name }}</strong>
                    <span>{{ workflow.steps.length }} {{ ui(workflow.steps.length === 1 ? "reusableStep" : "reusableSteps") }}</span>
                    <small>{{ workflowSummary(workflow) }}</small>
                  </div>
                  <div class="workflow-library-actions">
                    <button class="workflow-apply-button" type="button" :disabled="workflowApplying" @click="loadWorkflow(workflow)">
                      <Play :size="15" fill="currentColor" aria-hidden="true" />
                      {{ t("apply") }}
                    </button>
                    <button class="workflow-delete-button" type="button" :aria-label="ui('deleteWorkflow')" @click="deleteSavedWorkflow(workflow.id)">
                      <Trash2 :size="15" aria-hidden="true" />
                    </button>
                  </div>
                </article>
              </div>
              <div v-else class="history-list">
                <div v-for="(item, itemIndex) in historyEntries" :key="`${item.index}-${item.kind}`" class="history-step-group">
                  <div class="history-step-control">
                    <button
                      class="history-item"
                      :class="{ 'is-original': item.isOriginal, 'is-hovered': item.isHovered, 'is-after-hovered': item.isAfterHovered, 'is-redo': item.isRedo }"
                      type="button"
                      :disabled="!hasImage"
                      :title="item.isRedo ? '' : t('returnToPoint')"
                      @mouseenter="item.isRedo ? setHistoryHover(null) : setHistoryHover(item.index)"
                      @mouseleave="setHistoryHover(null)"
                      @click="item.isRedo ? redoToHistoryStep(item.index) : restoreHistoryStep(item.index)"
                    >
                      <span class="history-step-number">#{{ item.index }}</span>
                      <span class="history-step-label">
                        <span class="history-step-label-text">{{ item.label }}</span>
                        <span v-if="item.swatchColor" class="history-color-swatch" :style="{ backgroundColor: item.swatchColor }" aria-hidden="true"></span>
                      </span>
                    </button>
                    <button
                      v-if="!item.isOriginal"
                      class="history-step-delete"
                      type="button"
                      :aria-label="t('deleteThisStep')"
                      :title="t('deleteThisStep')"
                      @pointerdown.stop
                      @click.stop="deleteHistoryEntry(item.index, item.isRedo)"
                    >
                      ×
                    </button>
                  </div>
                  <span
                    v-if="itemIndex < historyEntries.length - 1"
                    class="history-step-arrow"
                    :class="{ 'is-warning': historyEntries[itemIndex + 1]?.isAfterHovered, 'is-redo': historyEntries[itemIndex + 1]?.isRedo }"
                    aria-hidden="true"
                  >
                    ↓
                  </span>
                </div>
              </div>
              <div class="history-workflow-footer" @pointerdown.stop>
                <button class="history-save-workflow-button" type="button" :disabled="!reusableWorkflowSteps.length" @click="openWorkflowSaveDialog">
                  <Save :size="16" :stroke-width="2.2" aria-hidden="true" />
                  {{ ui("saveWorkflow") }}
                </button>
                <button
                  class="history-load-workflow-button"
                  :class="{ 'is-active': workflowLibraryOpen }"
                  type="button"
                  @click="workflowLibraryOpen = !workflowLibraryOpen"
                >
                  <FolderOpen :size="15" :stroke-width="2.2" aria-hidden="true" />
                  <span>{{ ui("loadWorkflow") }}</span>
                  <span v-if="savedWorkflows.length" class="workflow-count-badge">{{ savedWorkflows.length }}</span>
                </button>
                <span v-if="!isMobileViewport">{{ reusableWorkflowSteps.length }} {{ ui(reusableWorkflowSteps.length === 1 ? "reusableStep" : "reusableSteps") }}</span>
              </div>
            </div>
            </section>
            </Transition>
          </Teleport>

          <div v-if="workflowSaveDialogOpen" class="export-dialog-backdrop workflow-save-backdrop" @click.self="workflowSaveDialogOpen = false">
            <section class="workflow-save-dialog" role="dialog" aria-modal="true" aria-labelledby="workflow-save-title">
              <button class="close-button floating-close-button" type="button" :aria-label="t('closePanel')" @click="workflowSaveDialogOpen = false">×</button>
              <div class="workflow-save-heading">
                <span class="workflow-save-icon"><Save :size="19" :stroke-width="2.2" aria-hidden="true" /></span>
                <div>
                  <h2 id="workflow-save-title" class="section-title">{{ ui("saveWorkflow") }}</h2>
                  <p>{{ ui("reuseEdits") }}</p>
                </div>
              </div>
              <label class="workflow-name-field">
                <span>{{ ui("workflowName") }}</span>
                <input v-model="workflowName" type="text" maxlength="60" autofocus @keydown.enter="saveCurrentWorkflow" />
              </label>
              <div class="workflow-save-steps">
                <span v-for="(step, index) in reusableWorkflowSteps" :key="`${step.kind}-${index}`">{{ workflowStepLabel(step) }}</span>
              </div>
              <p class="workflow-save-note">
                {{ ui("cropPositionNote") }}
              </p>
              <div class="workflow-save-actions">
                <button class="export-dialog-button is-secondary" type="button" @click="workflowSaveDialogOpen = false">{{ t("no") }}</button>
                <button class="export-dialog-button" type="button" :disabled="!workflowName.trim()" @click="saveCurrentWorkflow">{{ ui("saveWorkflow") }}</button>
              </div>
            </section>
          </div>

          <div v-if="workflowResizeWarning" class="export-dialog-backdrop workflow-save-backdrop" @click.self="resolveWorkflowResizeWarning(false)">
            <section class="workflow-save-dialog workflow-resize-warning-dialog" role="alertdialog" aria-modal="true" aria-labelledby="workflow-resize-warning-title">
              <div class="workflow-save-heading">
                <span class="workflow-resize-warning-icon"><AlertCircle :size="21" :stroke-width="2.3" aria-hidden="true" /></span>
                <div>
                  <h2 id="workflow-resize-warning-title" class="section-title">{{ ui("enlargementTitle") }}</h2>
                  <p>{{ ui("enlargementIntro") }}</p>
                </div>
              </div>
              <div class="workflow-resize-comparison" :aria-label="`${ui('currentImage')} / ${ui('workflowTarget')}`">
                <div>
                  <span>{{ ui("currentImage") }}</span>
                  <strong>{{ workflowResizeWarning.sourceWidth }} × {{ workflowResizeWarning.sourceHeight }} px</strong>
                </div>
                <span class="workflow-resize-arrow" aria-hidden="true">→</span>
                <div class="is-target">
                  <span>{{ ui("workflowTarget") }}</span>
                  <strong>{{ workflowResizeWarning.targetWidth }} × {{ workflowResizeWarning.targetHeight }} px</strong>
                </div>
              </div>
              <p class="workflow-resize-warning-copy">
                {{ ui("enlargementBody") }}
              </p>
              <div class="workflow-save-actions">
                <button class="export-dialog-button is-secondary" type="button" @click="resolveWorkflowResizeWarning(false)">{{ ui("cancelWorkflow") }}</button>
                <button class="export-dialog-button workflow-resize-continue" type="button" @click="resolveWorkflowResizeWarning(true)">{{ ui("continueAnyway") }}</button>
              </div>
            </section>
          </div>

          <div
            v-if="hasImage && activeTool === 'resize' && resizePanelMode === 'resize'"
            class="resize-layer-box absolute touch-none"
            :style="resizeStyle"
            @pointermove="onResizePointerMove"
            @pointerup="endResizeDrag"
            @pointercancel="endResizeDrag"
          >
            <button class="layer-move" type="button" :aria-label="t('resize')" @pointerdown="startResizeDrag($event, 'move')" @mouseenter="setStageCursor('move')" @mouseleave="setStageCursor(null)"></button>
            <button class="transform-handle handle-n" type="button" :aria-label="t('resizeTop')" @pointerdown="startResizeDrag($event, 'n')" @mouseenter="setStageCursor('ns-resize')" @mouseleave="setStageCursor(null)"></button>
            <button class="transform-handle handle-e" type="button" :aria-label="t('resizeRight')" @pointerdown="startResizeDrag($event, 'e')" @mouseenter="setStageCursor('ew-resize')" @mouseleave="setStageCursor(null)"></button>
            <button class="transform-handle handle-s" type="button" :aria-label="t('resizeBottom')" @pointerdown="startResizeDrag($event, 's')" @mouseenter="setStageCursor('ns-resize')" @mouseleave="setStageCursor(null)"></button>
            <button class="transform-handle handle-w" type="button" :aria-label="t('resizeLeft')" @pointerdown="startResizeDrag($event, 'w')" @mouseenter="setStageCursor('ew-resize')" @mouseleave="setStageCursor(null)"></button>
            <button class="transform-handle handle-ne" type="button" :aria-label="t('resizeTopRight')" @pointerdown="startResizeDrag($event, 'ne')" @mouseenter="setStageCursor('nesw-resize')" @mouseleave="setStageCursor(null)"></button>
            <button class="transform-handle handle-nw" type="button" :aria-label="t('resizeTopLeft')" @pointerdown="startResizeDrag($event, 'nw')" @mouseenter="setStageCursor('nwse-resize')" @mouseleave="setStageCursor(null)"></button>
            <button class="transform-handle handle-se" type="button" :aria-label="t('resizeBottomRight')" @pointerdown="startResizeDrag($event, 'se')" @mouseenter="setStageCursor('nwse-resize')" @mouseleave="setStageCursor(null)"></button>
            <button class="transform-handle handle-sw" type="button" :aria-label="t('resizeBottomLeft')" @pointerdown="startResizeDrag($event, 'sw')" @mouseenter="setStageCursor('nesw-resize')" @mouseleave="setStageCursor(null)"></button>
          </div>

          <div
            v-if="cropControlsVisible"
            class="crop-box absolute touch-none"
            :class="{ 'is-masking': cropFunctionMode === 'masking' }"
            :style="cropStyle"
            @pointermove="onPointerMove"
            @pointerup="endCropDrag"
            @pointercancel="endCropDrag"
          >
            <button class="crop-move" type="button" :aria-label="t('moveCrop')" @pointerdown="startCropDrag($event, 'move')" @mouseenter="setStageCursor('move')" @mouseleave="setStageCursor(null)"></button>
            <button class="handle handle-n" type="button" :aria-label="t('resizeTop')" @pointerdown="startCropDrag($event, 'n')" @mouseenter="setStageCursor('ns-resize')" @mouseleave="setStageCursor(null)"></button>
            <button class="handle handle-e" type="button" :aria-label="t('resizeRight')" @pointerdown="startCropDrag($event, 'e')" @mouseenter="setStageCursor('ew-resize')" @mouseleave="setStageCursor(null)"></button>
            <button class="handle handle-s" type="button" :aria-label="t('resizeBottom')" @pointerdown="startCropDrag($event, 's')" @mouseenter="setStageCursor('ns-resize')" @mouseleave="setStageCursor(null)"></button>
            <button class="handle handle-w" type="button" :aria-label="t('resizeLeft')" @pointerdown="startCropDrag($event, 'w')" @mouseenter="setStageCursor('ew-resize')" @mouseleave="setStageCursor(null)"></button>
            <button class="handle handle-ne" type="button" :aria-label="t('resizeTopRight')" @pointerdown="startCropDrag($event, 'ne')" @mouseenter="setStageCursor('nesw-resize')" @mouseleave="setStageCursor(null)"></button>
            <button ref="cropTutorialHandleRef" class="handle handle-nw" type="button" :aria-label="t('resizeTopLeft')" @pointerdown="startCropDrag($event, 'nw')" @mouseenter="setStageCursor('nwse-resize')" @mouseleave="setStageCursor(null)"></button>
            <button class="handle handle-se" type="button" :aria-label="t('resizeBottomRight')" @pointerdown="startCropDrag($event, 'se')" @mouseenter="setStageCursor('nwse-resize')" @mouseleave="setStageCursor(null)"></button>
            <button class="handle handle-sw" type="button" :aria-label="t('resizeBottomLeft')" @pointerdown="startCropDrag($event, 'sw')" @mouseenter="setStageCursor('nesw-resize')" @mouseleave="setStageCursor(null)"></button>
          </div>

          <button
            v-if="!hasImage"
            class="drop-zone-button absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center cursor-default"
            type="button"
            @click="openPicker()"
          >
            <span class="drop-image-illustration">
              <span class="drop-image-card" aria-hidden="true">
                <ImageIcon :size="30" stroke-width="1.7" />
                <span class="drop-image-sun"></span>
                <span class="drop-image-mountain drop-image-mountain-back"></span>
                <span class="drop-image-mountain drop-image-mountain-front"></span>
              </span>
              <img class="drop-image-arrow" :src="dropArrow" alt="" aria-hidden="true" />
              <span class="drop-copy-tooltip" role="tooltip">
                <span>{{ t("seoDescription") }}</span>
                <span>
                  {{ t("localBrowserPrefix") }} <span class="underline decoration-current decoration-1 underline-offset-2">{{ t("localBrowserYours") }}</span>
                  <span v-html="t('localBrowserSuffix')"></span>
                </span>
              </span>
            </span>
            <span class="drop-message-wrap">
              <h2 class="max-w-md text-xl font-semibold tracking-normal sm:text-3xl">{{ isMobileViewport ? t("tapToLoadImage") : t("dropImage") }}</h2>
            </span>
            <span class="accepted-formats">{{ acceptedFormatsLabelByLanguage[language] }} {{ t("formats") }}</span>
          </button>
        </div>
      </div>

      <section
        v-if="activePanelTool"
        class="floating-panel"
        :class="{ 'is-stamp-mode': activePanelTool === 'text' && textPanelMode === 'stamp', 'is-crop-panel': activePanelTool === 'crop', 'is-resize-panel': activePanelTool === 'resize', 'is-text-panel': activePanelTool === 'text', 'is-corners-panel': activePanelTool === 'corners', 'is-adjustments-panel': activePanelTool === 'adjustments' }"
        :style="floatingStyle"
        @pointerdown="startFloatingDrag"
        @pointermove="onFloatingMove"
        @pointerup="endFloatingDrag"
        @pointercancel="endFloatingDrag"
      >
        <div v-if="activePanelTool === 'image'" class="floating-body">
          <button class="close-button floating-close-button" type="button" :aria-label="t('closePanel')" @pointerdown.stop @click.stop="activePanelTool = null">×</button>
          <div class="info-card">
            <div class="floating-panel-header control-header mb-0">
              <div class="floating-panel-header-main">
                <h3 class="section-title">{{ t("image") }}</h3>
                <span class="value-pill">{{ imageDimensions }}</span>
              </div>
            </div>
            <dl class="info-list">
              <div>
                <dt>{{ t("source") }}</dt>
                <dd>{{ imageName || t("noFileSelected") }}</dd>
              </div>
              <div>
                <dt>{{ t("uploadSize") }}</dt>
                <dd>{{ uploadSize }}</dd>
              </div>
              <div>
                <dt>{{ t("currentSize") }}</dt>
                <dd>{{ currentSize }}</dd>
              </div>
            </dl>
          </div>
        </div>

        <div v-else-if="activePanelTool === 'corners'" class="floating-body">
          <button v-if="!isMobileViewport" class="close-button floating-close-button" type="button" :aria-label="t('closePanel')" @pointerdown.stop @click.stop="activePanelTool = null">×</button>
          <div class="floating-panel-header control-header">
            <div class="floating-panel-header-main">
              <h2 class="section-title">{{ t("cornerRadius") }}</h2>
              <span class="value-pill is-active-mode">{{ Math.round(radius) }} px</span>
              <button v-if="isMobileViewport" class="close-button mobile-corners-close-button" type="button" :aria-label="t('closePanel')" @pointerdown.stop @click.stop="activePanelTool = null">×</button>
            </div>
          </div>
          <div class="grid grid-cols-4 gap-2">
            <button v-for="preset in cornerRadiusPresets" :key="preset" class="preset-button" type="button" :disabled="!hasImage" @click="setCornerRadiusPreset(preset)">
              {{ preset }} px
            </button>
          </div>
            <input v-model.number="settings.radius" class="range" type="range" min="0" :max="maxRadius || 200" step="0.5" :disabled="!hasImage" />
          <div class="floating-panel-footer">
            <button class="action-button is-apply is-icon-only" type="button" :disabled="!hasImage || !cornerPending" :aria-label="t('apply')" :title="t('apply')" @mouseenter="showHoverHint(t('apply'), $event)" @mouseleave="hideHoverHint" @focus="showHoverHint(t('apply'), $event)" @blur="hideHoverHint" @click="applyRoundedCornersToImage">
              <Scissors :size="16" stroke-width="2.2" />
            </button>
            <button class="action-button is-revert" type="button" :disabled="!hasImage || activeTool !== 'corners'" @click="settings.radius = committedCornerRadius">
              {{ t("revert") }}
            </button>
          </div>
        </div>

        <div v-else-if="activePanelTool === 'border'" class="floating-body">
          <button class="close-button floating-close-button" type="button" :aria-label="t('closePanel')" @pointerdown.stop @click.stop="activePanelTool = null">×</button>
          <div class="floating-panel-header control-header">
            <div class="floating-panel-header-main">
              <h2 class="section-title">{{ t("border") }}</h2>
              <span class="value-pill is-active-mode">{{ Math.round(settings.borderThickness) }} px</span>
            </div>
          </div>
          <input v-model.number="settings.borderThickness" class="range" type="range" min="0" :max="Math.max(80, Math.floor(Math.min(crop.width, crop.height) / 4))" step="1" :disabled="!hasImage" />
          <label class="color-row stacked">
            <span>{{ t("borderColor") }}</span>
            <input v-model="settings.borderColor" type="color" :disabled="!hasImage || settings.borderUseGradient" />
          </label>
          <label class="toggle-row">
            <input v-model="settings.borderUseGradient" type="checkbox" class="accent-[rgb(var(--accent))]" :disabled="!hasImage" />
            <span>{{ t("gradientBorder") }}</span>
          </label>
          <div v-if="settings.borderUseGradient" class="grid grid-cols-2 gap-2">
            <div class="col-span-2 radio-stack">
              <label class="radio-row">
                <input v-model="settings.borderGradientType" type="radio" value="linear" :disabled="!hasImage" />
                <span>{{ t("linearGradient") }}</span>
              </label>
              <label class="radio-row">
                <input v-model="settings.borderGradientType" type="radio" value="edge" :disabled="!hasImage" />
                <span>{{ t("edgeGradient") }}</span>
              </label>
            </div>
            <div v-if="settings.borderGradientType === 'linear'" class="col-span-2">
              <div class="control-header">
                <h2 class="section-title">{{ t("gradientAngle") }}</h2>
                <span class="value-pill is-active-mode">{{ Math.round(settings.borderGradientAngle) }}°</span>
              </div>
              <input v-model.number="settings.borderGradientAngle" class="range" type="range" min="0" max="360" step="1" :disabled="!hasImage" />
            </div>
            <div class="gradient-color-row col-span-2">
              <label class="color-row stacked">
                <span>{{ t("gradientStart") }}</span>
                <input v-model="settings.borderGradientStart" type="color" :disabled="!hasImage" />
              </label>
              <button
                class="gradient-swap-button"
                type="button"
                :disabled="!hasImage"
                :aria-label="t('swapGradientColors')"
                @mouseenter="showHoverHint(t('swapGradientColors'), $event)"
                @mouseleave="hideHoverHint"
                @focus="showHoverHint(t('swapGradientColors'), $event)"
                @blur="hideHoverHint"
                @click="swapBorderGradientColors"
              >
                <ArrowLeftRight :size="16" stroke-width="2.2" />
              </button>
              <label class="color-row stacked">
                <span>{{ t("gradientEnd") }}</span>
                <input v-model="settings.borderGradientEnd" type="color" :disabled="!hasImage" />
              </label>
            </div>
          </div>
          <div class="floating-panel-footer">
            <button class="action-button is-apply" type="button" :disabled="!hasImage || !borderPending" @click="applyBorderToImage">
              {{ t("apply") }}
            </button>
            <button class="action-button is-revert" type="button" :disabled="!hasImage || activeTool !== 'border'" @click="resetBorderPreview">
              {{ t("revert") }}
            </button>
          </div>
        </div>

        <div v-else-if="activePanelTool === 'adjustments'" class="floating-body">
          <button v-if="!isMobileViewport" class="close-button floating-close-button" type="button" :aria-label="t('closePanel')" @pointerdown.stop @click.stop="activePanelTool = null">×</button>
          <div class="floating-panel-header control-header">
            <div class="floating-panel-header-main">
              <h2 class="section-title">{{ t("adjustments") }}</h2>
              <button v-if="isMobileViewport" class="close-button mobile-adjustments-close-button" type="button" :aria-label="t('closePanel')" @pointerdown.stop @click.stop="activePanelTool = null">×</button>
            </div>
          </div>
          <label class="stacked-select">
            <div class="control-header mb-1">
              <span>{{ t("contrast") }}</span>
              <span class="value-pill is-active-mode">{{ Math.round(settings.contrast) }}%</span>
            </div>
            <input v-model.number="settings.contrast" class="range" type="range" min="0" max="200" step="1" :disabled="!hasImage" />
          </label>
          <label class="stacked-select mt-3">
            <div class="control-header mb-1">
              <span>{{ t("brightness") }}</span>
              <span class="value-pill is-active-mode">{{ Math.round(settings.brightness) }}%</span>
            </div>
            <input v-model.number="settings.brightness" class="range" type="range" min="0" max="200" step="1" :disabled="!hasImage" />
          </label>
          <label class="stacked-select mt-3">
            <div class="control-header mb-1">
              <span>{{ t("colorBalance") }}</span>
              <span class="value-pill is-active-mode">{{ Math.round(settings.colorBalance) }}°</span>
            </div>
            <input v-model.number="settings.colorBalance" class="range" type="range" min="-180" max="180" step="1" :disabled="!hasImage" />
          </label>
          <label class="stacked-select mt-3">
            <div class="control-header mb-1">
              <span>{{ t("saturation") }}</span>
              <span class="value-pill is-active-mode">{{ Math.round(settings.saturation) }}%</span>
            </div>
            <input v-model.number="settings.saturation" class="range" type="range" min="0" max="200" step="1" :disabled="!hasImage" />
          </label>
          <label class="stacked-select mt-3">
            <div class="control-header mb-1">
              <span>{{ t("sharpen") }}</span>
              <span class="value-pill is-active-mode">{{ Math.round(settings.sharpen) }}%</span>
            </div>
            <input v-model.number="settings.sharpen" class="range" type="range" min="0" max="100" step="1" :disabled="!hasImage" />
          </label>
          <label class="stacked-select mt-3">
            <div class="control-header mb-1">
              <span>{{ t("soften") }}</span>
              <span class="value-pill is-active-mode">{{ Math.round(settings.soften) }}%</span>
            </div>
            <input v-model.number="settings.soften" class="range" type="range" min="0" max="100" step="1" :disabled="!hasImage" />
          </label>
          <label class="stacked-select mt-3">
            <div class="control-header mb-1">
              <span>{{ t("transparency") }}</span>
              <span class="value-pill is-active-mode">{{ Math.round(settings.transparency) }}%</span>
            </div>
            <input v-model.number="settings.transparency" class="range" type="range" min="0" max="100" step="1" :disabled="!hasImage" />
          </label>
          <label class="toggle-row mt-3">
            <input v-model="settings.transparencyIncludeBorder" type="checkbox" class="accent-[rgb(var(--accent))]" :disabled="!hasImage || committedBorderLayers.length === 0" />
            <span>{{ t("includeBorder") }}</span>
          </label>
          <div class="floating-panel-footer">
            <button class="action-button is-apply" type="button" :disabled="!hasImage || !adjustmentsPending" @click="applyAdjustmentsToImage">
              {{ t("apply") }}
            </button>
            <button class="action-button is-revert" type="button" :disabled="!hasImage || activeTool !== 'adjustments'" @click="resetAdjustmentsPreview">
              {{ t("revert") }}
            </button>
          </div>
        </div>

        <div v-else-if="activePanelTool === 'text'" class="floating-body" :class="{ 'is-stamp-mode': textPanelMode === 'stamp' }">
          <button v-if="!isMobileViewport" class="close-button floating-close-button" type="button" :aria-label="t('closePanel')" @pointerdown.stop @click.stop="activePanelTool = null">×</button>
          <div class="floating-panel-header control-header">
            <div class="floating-panel-header-main">
              <h2 class="section-title">{{ t("text") }}</h2>
              <span class="value-pill is-active-mode">{{ Math.round(textState.size) }} px</span>
              <div v-if="isMobileViewport" class="mobile-text-mode-switch" role="tablist" :aria-label="t('text')">
                <button
                  class="mobile-text-mode-button"
                  :class="{ 'is-active': textPanelMode === 'text' }"
                  type="button"
                  role="tab"
                  :aria-label="t('textMode')"
                  :aria-selected="textPanelMode === 'text'"
                  :disabled="!hasImage"
                  @click="activateTextPanelMode('text')"
                >
                  <Type :size="17" stroke-width="2.2" aria-hidden="true" />
                </button>
                <button
                  class="mobile-text-mode-button"
                  :class="{ 'is-active': textPanelMode === 'stamp' }"
                  type="button"
                  role="tab"
                  :aria-label="t('stampMode')"
                  :aria-selected="textPanelMode === 'stamp'"
                  :disabled="!hasImage"
                  @click="activateTextPanelMode('stamp')"
                >
                  <Stamp :size="17" stroke-width="2.2" aria-hidden="true" />
                </button>
              </div>
              <button v-if="isMobileViewport" class="close-button mobile-text-close-button" type="button" :aria-label="t('closePanel')" @pointerdown.stop @click.stop="activePanelTool = null">×</button>
            </div>
          </div>
          <div class="panel-file-tabs">
            <div class="panel-file-tab-row" role="tablist" :aria-label="t('text')">
            <button
              class="panel-file-tab"
              :class="{ 'is-active': textPanelMode === 'text' }"
              type="button"
              role="tab"
              :aria-selected="textPanelMode === 'text'"
              :disabled="!hasImage"
              @click="activateTextPanelMode('text')"
            >
              {{ t("textMode") }}
            </button>
            <button
              class="panel-file-tab"
              :class="{ 'is-active': textPanelMode === 'stamp' }"
              type="button"
              role="tab"
              :aria-selected="textPanelMode === 'stamp'"
              :disabled="!hasImage"
              @click="activateTextPanelMode('stamp')"
            >
              {{ t("stampMode") }}
            </button>
            </div>
            <div class="panel-file-tab-content">
              <template v-if="textPanelMode === 'text'">
            <label class="stacked-select">
              <span>{{ t("textField") }}</span>
              <input v-model="textState.content" type="text" :placeholder="t('textPlaceholder')" :disabled="!hasImage" />
            </label>
            <button
              v-if="isMobileViewport"
              class="action-button is-apply text-position-action rail-text-position-action mobile-text-position-action"
              type="button"
              :disabled="!canEditTextPosition"
              @click="beginTextPosition"
            >
              <Move :size="16" stroke-width="2.4" />
              <span>{{ t("positionText") }}</span>
            </button>
            <label class="stacked-select">
              <div class="control-header mb-1">
                <span>{{ t("size") }}</span>
                <span class="value-pill is-active-mode">{{ Math.round(textState.size) }} px</span>
              </div>
              <input v-model.number="textState.size" class="range" type="range" min="10" :max="textSizeMaximum" step="1" :disabled="!hasImage" />
            </label>
            <label class="stacked-select mt-3">
              <div class="control-header mb-1">
                <span>{{ t("freeRotate") }}</span>
                <span class="value-pill is-active-mode">{{ Math.round(textState.rotation) }}°</span>
              </div>
              <input v-model.number="textState.rotation" class="range" type="range" min="-180" max="180" step="1" :disabled="!hasImage" />
            </label>
            <label class="color-row stacked mt-3">
              <span>{{ t("color") }}</span>
              <input v-model="textState.color" type="color" :disabled="!hasImage" />
            </label>
            <label class="stacked-select mt-3">
              <div class="control-header mb-1">
                <span>{{ t("dropShadowSize") }}</span>
                <span class="value-pill is-active-mode">{{ Math.round(textState.shadowSize) }} px</span>
              </div>
              <input v-model.number="textState.shadowSize" class="range" type="range" min="0" max="40" step="1" :disabled="!hasImage" />
            </label>
            <label class="stacked-select mt-3">
              <div class="control-header mb-1">
                <span>{{ t("dropShadowIntensity") }}</span>
                <span class="value-pill is-active-mode">{{ Math.round(textState.shadowDarkness) }}%</span>
              </div>
              <input v-model.number="textState.shadowDarkness" class="range" type="range" min="0" max="100" step="1" :disabled="!hasImage" />
            </label>
            <label class="color-row stacked mt-3">
              <span>{{ t("dropShadowColor") }}</span>
              <input v-model="textState.shadowColor" type="color" :disabled="!hasImage" />
            </label>
              </template>
              <template v-else>
            <label class="stacked-select mt-3">
              <div class="control-header mb-1">
                <span>{{ t("size") }}</span>
                <span class="value-pill is-active-mode">{{ Math.round(textState.size) }} px</span>
              </div>
              <input v-model.number="textState.size" class="range" type="range" min="20" max="240" step="1" :disabled="!hasImage" />
            </label>
            <label class="stacked-select mt-3">
              <div class="control-header mb-1">
                <span>{{ t("freeRotate") }}</span>
                <span class="value-pill is-active-mode">{{ Math.round(textState.rotation) }}°</span>
              </div>
              <input v-model.number="textState.rotation" class="range" type="range" min="-180" max="180" step="1" :disabled="!hasImage" />
            </label>
            <div class="stamp-section mt-4">
              <div class="stamp-category-header">
                <span>{{ t("decorations") }}</span>
              </div>
              <div class="stamp-grid">
                <button
                  v-for="preset in decorationStampPresets"
                  :key="preset.label"
                  class="stamp-button"
                  type="button"
                  :class="{ 'is-active': selectedStampPreset?.label === preset.label }"
                  :disabled="!hasImage"
                  @click="applyStampPreset(preset)"
                >
                  <span class="stamp-button-label">{{ preset.label }}</span>
                  <span class="stamp-button-preview" :style="{ color: preset.color }">{{ preset.content }}</span>
                </button>
              </div>
              <div class="stamp-category-header">
                <span>{{ t("animals") }}</span>
              </div>
              <div class="stamp-grid">
                <button
                  v-for="preset in animalStampPresets"
                  :key="preset.label"
                  class="stamp-button"
                  type="button"
                  :class="{ 'is-active': selectedStampPreset?.label === preset.label }"
                  :disabled="!hasImage"
                  @click="applyStampPreset(preset)"
                >
                  <span class="stamp-button-label">{{ preset.label }}</span>
                  <span class="stamp-button-preview" :style="{ color: preset.color }">{{ preset.content }}</span>
                </button>
              </div>
              <div class="stamp-category-header">
                <span>{{ t("labels") }}</span>
              </div>
              <div class="stamp-grid">
                <button
                  v-for="preset in labelStampPresets"
                  :key="preset.label"
                  class="stamp-button"
                  type="button"
                  :class="{ 'is-active': selectedStampPreset?.label === preset.label }"
                  :disabled="!hasImage"
                  @click="applyStampPreset(preset)"
                >
                  <span class="stamp-button-label">{{ preset.label }}</span>
                  <span class="stamp-label-preview" :style="{ '--stamp-bg': preset.backgroundColor, '--stamp-fg': preset.textColor, '--stamp-border': preset.borderColor }">
                    {{ preset.content }}
                  </span>
                </button>
              </div>
            </div>
              </template>
            </div>
          </div>
          <div v-if="!isMobileViewport" class="floating-panel-footer">
            <button class="action-button is-apply text-position-action" type="button" :disabled="!canEditTextPosition" @click="beginTextPosition">
              <Move :size="16" stroke-width="2.4" />
              <span>{{ t("positionText") }}</span>
            </button>
          </div>
        </div>

        <div v-else-if="activePanelTool === 'resize'" class="floating-body">
          <button v-if="!isMobileViewport" class="close-button floating-close-button" type="button" :aria-label="t('closePanel')" @pointerdown.stop @click.stop="activePanelTool = null">×</button>
          <div class="floating-panel-header control-header">
            <div class="floating-panel-header-main">
              <h2 class="section-title">{{ t("resize") }}</h2>
              <span class="value-pill is-active-mode">{{ Math.round(resizeBox.width) }} x {{ Math.round(resizeBox.height) }} px</span>
              <div v-if="isMobileViewport" class="mobile-resize-mode-switch" role="tablist" :aria-label="t('resize')">
                <button
                  class="mobile-resize-mode-button"
                  :class="{ 'is-active': resizePanelMode === 'resize' }"
                  type="button"
                  role="tab"
                  :aria-label="t('resizeOnly')"
                  :aria-selected="resizePanelMode === 'resize'"
                  :disabled="!hasImage"
                  @click="resizePanelMode = 'resize'"
                >
                  <Maximize2 :size="17" stroke-width="2.2" aria-hidden="true" />
                </button>
                <button
                  class="mobile-resize-mode-button"
                  :class="{ 'is-active': resizePanelMode === 'rotate' }"
                  type="button"
                  role="tab"
                  :aria-label="t('rotateFlip')"
                  :aria-selected="resizePanelMode === 'rotate'"
                  :disabled="!hasImage"
                  @click="resizePanelMode = 'rotate'"
                >
                  <RotateCw :size="17" stroke-width="2.2" aria-hidden="true" />
                </button>
              </div>
              <button v-if="isMobileViewport" class="close-button mobile-resize-close-button" type="button" :aria-label="t('closePanel')" @pointerdown.stop @click.stop="activePanelTool = null">×</button>
            </div>
          </div>
          <div class="panel-file-tabs">
            <div class="panel-file-tab-row" role="tablist" :aria-label="t('resize')">
            <button
              class="panel-file-tab"
              :class="{ 'is-active': resizePanelMode === 'resize' }"
              type="button"
              role="tab"
              :aria-selected="resizePanelMode === 'resize'"
              :disabled="!hasImage"
              @click="resizePanelMode = 'resize'"
            >
              {{ t("resizeOnly") }}
            </button>
            <button
              class="panel-file-tab"
              :class="{ 'is-active': resizePanelMode === 'rotate' }"
              type="button"
              role="tab"
              :aria-selected="resizePanelMode === 'rotate'"
              :disabled="!hasImage"
              @click="resizePanelMode = 'rotate'"
            >
              {{ t("rotateFlip") }}
            </button>
            </div>
            <div class="panel-file-tab-content">
              <div v-if="isMobileViewport" class="mobile-resize-dimensions-readout" aria-live="polite">
                <span>{{ t("currentSize") }}</span>
                <strong>{{ Math.round(resizeBox.width) }} × {{ Math.round(resizeBox.height) }} px</strong>
              </div>
              <div v-if="resizePanelMode === 'resize'" class="radio-stack">
            <label class="radio-row">
              <input v-model="resizeMode" type="radio" value="constrain" />
              <span>{{ t("constrainProportions") }}</span>
            </label>
            <label class="radio-row">
              <input v-model="resizeMode" type="radio" value="freeform" />
              <span>{{ t("freeform") }}</span>
            </label>
          </div>
              <div v-if="resizePanelMode === 'resize'" class="resize-dimensions">
            <label class="dimension-field">
              <span>{{ t("width") }}</span>
              <input
                :value="Math.round(resizeBox.width)"
                type="number"
                :min="resizeMinSize()"
                :max="resizeMaxSize()"
                :step="resizeMode === 'constrain' ? 1 : 1"
                :disabled="!hasImage"
                @input="setResizeWidth(($event.target as HTMLInputElement).valueAsNumber)"
              />
            </label>
            <label class="dimension-field">
              <span>{{ t("height") }}</span>
              <input
                :value="Math.round(resizeBox.height)"
                type="number"
                :min="resizeMinSize()"
                :max="resizeMaxSize()"
                :step="resizeMode === 'constrain' ? 1 : 1"
                :disabled="!hasImage"
                @input="setResizeHeight(($event.target as HTMLInputElement).valueAsNumber)"
              />
            </label>
          </div>
              <p v-if="resizePanelMode === 'resize'" class="resize-help">{{ t("resizeHelp") }}</p>
              <div v-if="resizePanelMode === 'rotate'" class="resize-rotate-section">
            <p class="section-meta resize-rotate-label">{{ t("rotate") }}</p>
            <div class="resize-rotate-buttons rotate-icon-row">
              <button class="action-button is-secondary rotate-icon-button" type="button" :disabled="!hasImage" @click="rotateResize(-90)">
                <RotateCcw :size="16" stroke-width="2.3" />
                <span class="sr-only">{{ t("rotateCounterClockwise") }}</span>
              </button>
              <button class="action-button is-secondary rotate-icon-button" type="button" :disabled="!hasImage" @click="rotateResize(90)">
                <RotateCw :size="16" stroke-width="2.3" />
                <span class="sr-only">{{ t("rotateClockwise") }}</span>
              </button>
            </div>
            <label class="stacked-select mt-3">
              <div class="control-header mb-1">
                <span class="section-meta resize-rotate-inline-label">{{ t("freeRotate") }}</span>
                <span class="value-pill is-active-mode">{{ Math.round(resizeRotateDegrees) }}°</span>
              </div>
              <input v-model.number="resizeRotateDegrees" class="range" type="range" min="-180" max="180" step="1" :disabled="!hasImage" />
            </label>
            <p class="section-meta resize-rotate-label resize-rotate-flip-label">{{ t("flip") }}</p>
            <div class="resize-rotate-buttons resize-rotate-flip-row mt-4">
                  <button
                    class="action-button is-secondary flip-button"
                    type="button"
                    :disabled="!hasImage"
                    :aria-label="t('flipVertical')"
                    @mouseenter="showHoverHint(t('flipVertical'), $event)"
                    @mouseleave="hideHoverHint"
                    @focus="showHoverHint(t('flipVertical'), $event)"
                    @blur="hideHoverHint"
                    @click="flipResizeVertical"
                  >
                    <FlipVertical2 :size="20" :stroke-width="2.2" aria-hidden="true" />
                  </button>
                  <button
                    class="action-button is-secondary flip-button"
                    type="button"
                    :disabled="!hasImage"
                    :aria-label="t('flipHorizontal')"
                    @mouseenter="showHoverHint(t('flipHorizontal'), $event)"
                    @mouseleave="hideHoverHint"
                    @focus="showHoverHint(t('flipHorizontal'), $event)"
                    @blur="hideHoverHint"
                    @click="flipResizeHorizontal"
                  >
                    <FlipHorizontal2 :size="20" :stroke-width="2.2" aria-hidden="true" />
                  </button>
            </div>
              </div>
            </div>
          </div>
          <div class="floating-panel-footer">
            <button class="action-button is-apply" type="button" :disabled="!hasImage || !resizePending" @click="applyResizeToImage">
              {{ t("apply") }}
            </button>
            <button class="action-button is-revert" type="button" :disabled="!hasImage" @click="resetResizeBox">
              {{ t("revert") }}
            </button>
          </div>
        </div>

        <div v-else-if="activePanelTool === 'filters'" class="floating-body">
          <button class="close-button floating-close-button" type="button" :aria-label="t('closePanel')" @pointerdown.stop @click.stop="activePanelTool = null">×</button>
          <div class="floating-panel-header control-header">
            <div class="floating-panel-header-main">
              <h2 class="section-title">{{ t("filters") }}</h2>
            </div>
          </div>
          <label class="stacked-select">
            <span>{{ t("filterPreset") }}</span>
            <select v-model="settings.filter" data-crop-tutorial-filter-selector :disabled="!hasImage">
              <option value="none">{{ t("none") }}</option>
              <option value="colorreplace">{{ t("colorReplace") }}</option>
              <option value="bw">{{ t("bw") }}</option>
              <option value="sepia">{{ t("sepia") }}</option>
              <option value="oldfashioned">{{ t("oldFashioned") }}</option>
              <option value="vintage">{{ t("vintage") }}</option>
              <option value="greenify">{{ t("greenify") }}</option>
              <option value="redify">{{ t("redify") }}</option>
              <option value="bluify">{{ t("bluify") }}</option>
              <option value="purplify">{{ t("purplify") }}</option>
              <option value="brownify">{{ t("brownify") }}</option>
              <option value="cool">{{ t("coolTone") }}</option>
              <option value="warm">{{ t("warmTone") }}</option>
              <option value="cinematic">{{ t("cinematic") }}</option>
              <option value="matte">{{ t("matte") }}</option>
              <option value="cyanotype">{{ t("cyanotype") }}</option>
              <option value="glow">{{ t("warmGlow") }}</option>
            </select>
          </label>
          <div v-if="settings.filter === 'colorreplace'" class="color-replace-controls">
            <div class="color-replace-pair">
              <div
                class="color-row stacked color-replace-color"
                :class="{ 'is-active': !settings.colorReplaceTargetTransparent }"
                role="button"
                :tabindex="hasImage ? 0 : -1"
                :aria-pressed="!settings.colorReplaceTargetTransparent"
                :aria-disabled="!hasImage"
                @click="settings.colorReplaceTargetTransparent = false"
                @keydown.enter.prevent="settings.colorReplaceTargetTransparent = false"
                @keydown.space.prevent="settings.colorReplaceTargetTransparent = false"
              >
                <span>{{ t("targetColor") }}</span>
                <input v-model="settings.colorReplaceTarget" type="color" :disabled="!hasImage" :aria-label="t('targetColor')" @click="settings.colorReplaceTargetTransparent = false" />
              </div>
              <button
                class="color-replace-transparent"
                :class="{ 'is-active': settings.colorReplaceTargetTransparent }"
                type="button"
                :disabled="!hasImage"
                :aria-label="`${t('targetColor')}: ${t('transparent')}`"
                :aria-pressed="settings.colorReplaceTargetTransparent"
                :title="t('transparent')"
                @mouseenter="showHoverHint(t('transparent'), $event)"
                @mouseleave="hideHoverHint"
                @focus="showHoverHint(t('transparent'), $event)"
                @blur="hideHoverHint"
                @click="settings.colorReplaceTargetTransparent = !settings.colorReplaceTargetTransparent"
              ></button>
            </div>
            <div class="color-replace-pair">
              <div
                class="color-row stacked color-replace-color"
                :class="{ 'is-active': !settings.colorReplaceReplacementTransparent }"
                role="button"
                :tabindex="hasImage ? 0 : -1"
                :aria-pressed="!settings.colorReplaceReplacementTransparent"
                :aria-disabled="!hasImage"
                @click="settings.colorReplaceReplacementTransparent = false"
                @keydown.enter.prevent="settings.colorReplaceReplacementTransparent = false"
                @keydown.space.prevent="settings.colorReplaceReplacementTransparent = false"
              >
                <span>{{ t("replacementColor") }}</span>
                <input v-model="settings.colorReplaceReplacement" type="color" :disabled="!hasImage" :aria-label="t('replacementColor')" @click="settings.colorReplaceReplacementTransparent = false" />
              </div>
              <button
                class="color-replace-transparent"
                :class="{ 'is-active': settings.colorReplaceReplacementTransparent }"
                type="button"
                :disabled="!hasImage"
                :aria-label="`${t('replacementColor')}: ${t('transparent')}`"
                :aria-pressed="settings.colorReplaceReplacementTransparent"
                :title="t('transparent')"
                @mouseenter="showHoverHint(t('transparent'), $event)"
                @mouseleave="hideHoverHint"
                @focus="showHoverHint(t('transparent'), $event)"
                @blur="hideHoverHint"
                @click="settings.colorReplaceReplacementTransparent = !settings.colorReplaceReplacementTransparent"
              ></button>
            </div>
            <label class="stacked-select">
              <div class="control-header mb-1">
                <span>{{ t("strength") }}</span>
                <span class="value-pill is-active-mode">{{ Math.round(settings.colorReplaceStrength) }}%</span>
              </div>
              <input v-model.number="settings.colorReplaceStrength" class="range" type="range" min="0" max="100" step="1" :disabled="!hasImage" />
            </label>
            <label class="stacked-select">
              <div class="control-header mb-1">
                <span>{{ t("smoothness") }}</span>
                <span class="value-pill is-active-mode">{{ Math.round(settings.colorReplaceSmoothness) }}%</span>
              </div>
              <input v-model.number="settings.colorReplaceSmoothness" class="range" type="range" min="0" max="100" step="1" :disabled="!hasImage" />
            </label>
          </div>
          <div class="floating-panel-footer">
            <button class="action-button is-apply" type="button" :disabled="!hasImage || !filterPending" @click="applyFilterToImage">
              {{ t("apply") }}
            </button>
            <button class="action-button is-revert" type="button" :disabled="!hasImage || activeTool !== 'filters'" @click="resetFilterPreview">
              {{ t("revert") }}
            </button>
          </div>
        </div>

          <div v-else-if="activePanelTool === 'shadow'" class="floating-body">
          <button class="close-button floating-close-button" type="button" :aria-label="t('closePanel')" @pointerdown.stop @click.stop="activePanelTool = null">×</button>
          <div class="floating-panel-header control-header">
            <div class="floating-panel-header-main">
              <h2 class="section-title">{{ t("shadow") }}</h2>
            </div>
          </div>
          <div class="control-stack">
            <div class="control-header mb-0">
              <span class="section-meta">{{ t("shadowDarkness") }}</span>
              <span class="value-pill is-active-mode">{{ Math.round(settings.shadowDarkness) }}%</span>
            </div>
            <input v-model.number="settings.shadowDarkness" class="range" type="range" min="0" max="100" step="1" :disabled="!hasImage || activeTool !== 'shadow'" />
          </div>
          <div class="control-stack">
            <div class="control-header mb-0">
              <span class="section-meta">{{ t("shadowSize") }}</span>
              <span class="value-pill is-active-mode">{{ Math.round(settings.shadowSize) }} px</span>
            </div>
            <input v-model.number="settings.shadowSize" class="range" type="range" min="0" max="80" step="1" :disabled="!hasImage || activeTool !== 'shadow'" />
          </div>
          <label class="color-row stacked">
            <span>{{ t("shadowColor") }}</span>
            <input v-model="settings.shadowColor" type="color" :disabled="!hasImage || activeTool !== 'shadow'" />
          </label>
          <div class="control-stack">
            <div class="control-header mb-0">
              <span class="section-meta">{{ t("shadowDirection") }}</span>
              <span class="value-pill is-active-mode">{{ Math.round(settings.shadowDirection) }}°</span>
            </div>
            <input v-model.number="settings.shadowDirection" class="range" type="range" min="0" max="360" step="1" :disabled="!hasImage || activeTool !== 'shadow'" />
          </div>
          <div class="floating-panel-footer">
            <button class="action-button is-apply" type="button" :disabled="!hasImage || !shadowPending" @click="applyShadowToImage">
              {{ t("apply") }}
            </button>
            <button class="action-button is-revert" type="button" :disabled="!hasImage || activeTool !== 'shadow'" @click="resetShadowPreview">
              {{ t("revert") }}
            </button>
          </div>
        </div>

        <div v-else-if="activePanelTool === 'crop'" class="floating-body">
          <button v-if="!isMobileViewport" class="close-button floating-close-button" type="button" :aria-label="t('closePanel')" @pointerdown.stop @click.stop="activePanelTool = null">×</button>
          <div class="floating-panel-header control-header">
            <div class="floating-panel-header-main">
              <h2 class="section-title">{{ t("crop") }}</h2>
              <div v-if="isMobileViewport" class="mobile-crop-mode-switch" role="tablist" :aria-label="t('crop')">
                <button
                  class="mobile-crop-mode-button"
                  :class="{ 'is-active': cropFunctionMode === 'trimmer' }"
                  type="button"
                  role="tab"
                  :aria-selected="cropFunctionMode === 'trimmer'"
                  :disabled="!hasImage"
                  @click="setCropFunctionMode('trimmer')"
                >
                  {{ t("cropShort") }}
                </button>
                <button
                  class="mobile-crop-mode-button"
                  :class="{ 'is-active': cropFunctionMode === 'masking' }"
                  type="button"
                  role="tab"
                  :aria-selected="cropFunctionMode === 'masking'"
                  :disabled="!hasImage"
                  @click="setCropFunctionMode('masking')"
                >
                  {{ t("maskShort") }}
                </button>
              </div>
              <button v-if="isMobileViewport" class="close-button mobile-crop-close-button" type="button" :aria-label="t('closePanel')" @pointerdown.stop @click.stop="activePanelTool = null">×</button>
            </div>
          </div>
          <div class="panel-file-tabs">
            <div class="panel-file-tab-row" role="tablist" :aria-label="t('crop')">
            <button
              class="panel-file-tab"
              :class="{ 'is-active': cropFunctionMode === 'trimmer' }"
              type="button"
              role="tab"
              :aria-selected="cropFunctionMode === 'trimmer'"
              :disabled="!hasImage"
              @click="setCropFunctionMode('trimmer')"
            >
              {{ t("imageTrimmer") }}
            </button>
            <button
              class="panel-file-tab"
              :class="{ 'is-active': cropFunctionMode === 'masking' }"
              type="button"
              role="tab"
              :aria-selected="cropFunctionMode === 'masking'"
              :disabled="!hasImage"
              @click="setCropFunctionMode('masking')"
            >
              {{ t("imageMasking") }}
            </button>
            </div>
            <div class="panel-file-tab-content">
              <p class="section-meta">{{ cropFunctionMode === 'trimmer' ? t("imageTrimmer") : t("imageMasking") }}</p>
              <div v-if="cropFunctionMode === 'trimmer'" class="crop-preset-groups">
            <div class="radio-stack mask-radio-row crop-category-radio-row" role="radiogroup" :aria-label="t('imageTrimmer')">
              <label
                v-for="group in cropPresetGroups"
                :key="group.titleKey"
                class="radio-option"
              >
                <input
                  :checked="cropTrimmerTab === group.titleKey"
                  type="radio"
                  name="crop-trimmer-category"
                  :value="group.titleKey"
                  :disabled="!hasImage"
                  @change="cropTrimmerTab = group.titleKey"
                />
                <span>{{ t(group.titleKey) }}</span>
              </label>
            </div>
            <section v-for="group in cropPresetGroups.filter((presetGroup) => presetGroup.titleKey === cropTrimmerTab)" :key="group.titleKey" class="crop-preset-group">
              <div class="crop-preset-grid" :class="{ 'is-social-template-grid': group.titleKey === 'cropSocial' }">
                <button
                  v-for="item in group.items"
                  :key="item.id"
                  class="crop-preset-button preset-button"
                  :class="{ 'is-social-template': group.titleKey === 'cropSocial', 'is-active': selectedCropPresetId === item.id }"
                  type="button"
                  :disabled="!hasImage"
                  :aria-pressed="selectedCropPresetId === item.id"
                  @click="selectCropPreset(item)"
                >
                  <span
                    class="crop-preset-icon"
                    :class="[
                      'shape' in item ? `is-${item.shape}` : '',
                      { 'is-brand-icon': 'brand' in item && item.brand in socialBrandIcons, 'has-brand-color': 'brandColor' in item },
                    ]"
                    :style="'brandColor' in item ? { '--brand-bg': item.brandColor, '--brand-border': item.brandBorder } : undefined"
                    aria-hidden="true"
                  >
                    <svg
                      v-if="'brand' in item && item.brand in socialBrandIcons"
                      viewBox="0 0 24 24"
                      focusable="false"
                    >
                      <path :d="socialBrandIcons[item.brand as keyof typeof socialBrandIcons].path" />
                    </svg>
                    <span v-else-if="'icon' in item">{{ item.icon }}</span>
                  </span>
                  <span class="crop-preset-copy">
                    <span class="crop-preset-label">{{ t(item.labelKey) }}</span>
                    <span v-if="'ratio' in item" class="crop-template-meta">
                      {{ item.ratio }} · {{ item.size }}
                    </span>
                  </span>
                  <span
                    v-if="group.titleKey === 'cropSocial' && isTemplateTooLarge(item)"
                    class="crop-preset-warning"
                    role="img"
                    :aria-label="t('socialTemplateSizeWarning')"
                    @mouseenter="showWarningHoverHint(t('socialTemplateSizeWarning'), $event)"
                    @mouseleave="hideHoverHint"
                    @focus="showWarningHoverHint(t('socialTemplateSizeWarning'), $event)"
                    @blur="hideHoverHint"
                  >
                    <AlertCircle :size="15" stroke-width="2.6" />
                  </span>
                </button>
              </div>
            </section>
              </div>
              <div v-else class="crop-mask-panel">
            <div class="radio-stack mask-radio-row">
              <label class="radio-option">
                <input :checked="cropMaskShape === 'free'" type="radio" value="free" :disabled="!hasImage" @change="setMaskShape('free')" />
                <span>{{ t("freeSelectLabel") }}</span>
              </label>
              <label class="radio-option">
                <input :checked="cropMaskShape === 'circle'" type="radio" value="circle" :disabled="!hasImage" @change="setMaskShape('circle')" />
                <span>{{ t("roundedSelect") }}</span>
              </label>
            </div>
            <p class="section-meta mask-type-label">{{ t("maskType") }}</p>
            <div class="radio-stack mask-radio-row">
              <label class="radio-option">
                <input :checked="cropMaskMode === 'blur'" type="radio" value="blur" :disabled="!hasImage" @change="setMaskMode('blur')" />
                <span>{{ t("blur") }}</span>
              </label>
              <label class="radio-option">
                <input :checked="cropMaskMode === 'color'" type="radio" value="color" :disabled="!hasImage" @change="setMaskMode('color')" />
                <span>{{ t("overlayColor") }}</span>
              </label>
            </div>
            <div class="mask-settings-spacer">
              <label v-if="cropMaskMode === 'blur'" class="stacked-select">
                <div class="control-header mb-1">
                  <span>{{ t("intensity") }}</span>
                  <span class="value-pill">{{ Math.round(cropMaskBlurIntensity) }}%</span>
                </div>
                <input v-model.number="cropMaskBlurIntensity" class="range" type="range" min="0" max="100" step="1" :disabled="!hasImage" />
              </label>
              <label v-else class="stacked-select">
                <div class="control-header mb-1">
                  <span>{{ t("opacity") }}</span>
                  <span class="value-pill">{{ Math.round(cropMaskTransparency) }}%</span>
                </div>
                <input v-model.number="cropMaskTransparency" class="range" type="range" min="0" max="100" step="1" :disabled="!hasImage" />
              </label>
            </div>
            <label v-if="cropMaskMode === 'color'" class="inline-field">
              <span>{{ t("color") }}</span>
              <input v-model="cropMaskColor" type="color" :disabled="!hasImage" />
            </label>
              </div>
            </div>
          </div>
          <div class="floating-panel-footer">
            <button
              ref="cropTutorialPanelCommitRef"
              class="action-button is-apply is-icon-only"
              type="button"
              :disabled="!hasImage || !cropPending"
              :aria-label="t('apply')"
              :title="t('apply')"
              @mouseenter="showHoverHint(t('apply'), $event)"
              @mouseleave="hideHoverHint"
              @focus="showHoverHint(t('apply'), $event)"
              @blur="hideHoverHint"
              @click="applyCropToImage"
            >
              <Scissors v-if="cropFunctionMode === 'trimmer'" :size="16" stroke-width="2.2" />
              <span v-else>{{ t("apply") }}</span>
            </button>
            <button class="action-button is-revert" type="button" :disabled="!hasImage || activeTool !== 'crop'" @click="resetCropPreview">
              {{ t("revert") }}
            </button>
          </div>
        </div>

      </section>
      <div
        v-if="cropTutorialStep === 'handles' || cropTutorialStep === 'scissors' || cropTutorialStep === 'tools'"
        class="crop-tutorial-layer"
        aria-live="polite"
      >
        <div
          class="crop-tutorial-pointer"
          :class="{ 'faces-left': cropTutorialPointerFacesLeft }"
          :style="cropTutorialPointerStyle"
          aria-hidden="true"
        >
          <ArrowDownRight :size="42" stroke-width="3" />
        </div>
        <aside class="crop-tutorial-card" :style="cropTutorialCardStyle">
          <button class="crop-tutorial-close" type="button" :aria-label="t('closePanel')" @click="closeCropTutorial">×</button>
          <div class="crop-tutorial-meta">
            <span class="crop-tutorial-kicker">{{ t("tutorialQuickTip") }}</span>
            <span class="crop-tutorial-counter">{{ cropTutorialStep === 'handles' ? '1/3' : cropTutorialStep === 'scissors' ? '2/3' : '3/3' }}</span>
          </div>
          <strong v-if="cropTutorialStep === 'handles'">{{ t("tutorialLetsGetCutting") }}</strong>
          <strong v-else-if="cropTutorialStep === 'scissors'">{{ t("tutorialMakeCut") }}</strong>
          <strong v-else>{{ t("tutorialKeepCreating") }}</strong>
          <p v-if="cropTutorialStep === 'handles'">{{ t("tutorialDragHandles") }}</p>
          <p v-else-if="cropTutorialStep === 'scissors'">{{ t("tutorialClickScissors") }}</p>
          <p v-else>{{ t("tutorialTryTools") }}</p>
          <button v-if="cropTutorialStep !== 'tools'" class="crop-tutorial-dismiss" type="button" @click="dismissCropTutorialForever">{{ t("tutorialDontShowAgain") }}</button>
          <button v-else class="crop-tutorial-ok" type="button" @click="closeCropTutorial">{{ t("tutorialOk") }}</button>
        </aside>
      </div>
    </section>
    <div v-if="hoverHintVisible" class="hover-hint" :class="{ 'is-warning': hoverHintVariant === 'warning' }" :style="hoverHintStyle">{{ hoverHint }}</div>
  </main>
</template>
